import { jsx as jsx$1, Fragment, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import React__default, { useState, useEffect, forwardRef, useRef, useLayoutEffect, useCallback, useContext, useMemo, createContext, Component, Fragment as Fragment$1, cloneElement, createRef, createElement, useSyncExternalStore, isValidElement } from 'react';
import * as ReactDOM from 'react-dom';
import ReactDOM__default, { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { StyleSheet as StyleSheet$1, View, Text, Canvas, PDFViewer, Document, Page, PDFDownloadLink } from '@react-pdf/renderer';

// packages/react/context/src/createContext.tsx
function createContext2(rootComponentName, defaultContext) {
  const Context = React.createContext(defaultContext);
  const Provider = (props) => {
    const { children, ...context } = props;
    const value = React.useMemo(() => context, Object.values(context));
    return /* @__PURE__ */ jsx$1(Context.Provider, { value, children });
  };
  Provider.displayName = rootComponentName + "Provider";
  function useContext2(consumerName) {
    const context = React.useContext(Context);
    if (context) return context;
    if (defaultContext !== void 0) return defaultContext;
    throw new Error(`\`${consumerName}\` must be used within \`${rootComponentName}\``);
  }
  return [Provider, useContext2];
}
function createContextScope(scopeName, createContextScopeDeps = []) {
  let defaultContexts = [];
  function createContext3(rootComponentName, defaultContext) {
    const BaseContext = React.createContext(defaultContext);
    const index = defaultContexts.length;
    defaultContexts = [...defaultContexts, defaultContext];
    const Provider = (props) => {
      const { scope, children, ...context } = props;
      const Context = scope?.[scopeName]?.[index] || BaseContext;
      const value = React.useMemo(() => context, Object.values(context));
      return /* @__PURE__ */ jsx$1(Context.Provider, { value, children });
    };
    Provider.displayName = rootComponentName + "Provider";
    function useContext2(consumerName, scope) {
      const Context = scope?.[scopeName]?.[index] || BaseContext;
      const context = React.useContext(Context);
      if (context) return context;
      if (defaultContext !== void 0) return defaultContext;
      throw new Error(`\`${consumerName}\` must be used within \`${rootComponentName}\``);
    }
    return [Provider, useContext2];
  }
  const createScope = () => {
    const scopeContexts = defaultContexts.map((defaultContext) => {
      return React.createContext(defaultContext);
    });
    return function useScope(scope) {
      const contexts = scope?.[scopeName] || scopeContexts;
      return React.useMemo(
        () => ({ [`__scope${scopeName}`]: { ...scope, [scopeName]: contexts } }),
        [scope, contexts]
      );
    };
  };
  createScope.scopeName = scopeName;
  return [createContext3, composeContextScopes(createScope, ...createContextScopeDeps)];
}
function composeContextScopes(...scopes) {
  const baseScope = scopes[0];
  if (scopes.length === 1) return baseScope;
  const createScope = () => {
    const scopeHooks = scopes.map((createScope2) => ({
      useScope: createScope2(),
      scopeName: createScope2.scopeName
    }));
    return function useComposedScopes(overrideScopes) {
      const nextScopes = scopeHooks.reduce((nextScopes2, { useScope, scopeName }) => {
        const scopeProps = useScope(overrideScopes);
        const currentScope = scopeProps[`__scope${scopeName}`];
        return { ...nextScopes2, ...currentScope };
      }, {});
      return React.useMemo(() => ({ [`__scope${baseScope.scopeName}`]: nextScopes }), [nextScopes]);
    };
  };
  createScope.scopeName = baseScope.scopeName;
  return createScope;
}

// packages/react/compose-refs/src/composeRefs.tsx
function setRef(ref, value) {
  if (typeof ref === "function") {
    return ref(value);
  } else if (ref !== null && ref !== void 0) {
    ref.current = value;
  }
}
function composeRefs(...refs) {
  return (node) => {
    let hasCleanup = false;
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup == "function") {
        hasCleanup = true;
      }
      return cleanup;
    });
    if (hasCleanup) {
      return () => {
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i];
          if (typeof cleanup == "function") {
            cleanup();
          } else {
            setRef(refs[i], null);
          }
        }
      };
    }
  };
}
function useComposedRefs(...refs) {
  return React.useCallback(composeRefs(...refs), refs);
}

// packages/core/primitive/src/primitive.tsx
function composeEventHandlers(originalEventHandler, ourEventHandler, { checkForDefaultPrevented = true } = {}) {
  return function handleEvent(event) {
    originalEventHandler?.(event);
    if (checkForDefaultPrevented === false || !event.defaultPrevented) {
      return ourEventHandler?.(event);
    }
  };
}

// packages/react/use-layout-effect/src/useLayoutEffect.tsx
var useLayoutEffect2 = Boolean(globalThis?.document) ? React.useLayoutEffect : () => {
};

// packages/react/id/src/id.tsx
var useReactId$1 = React["useId".toString()] || (() => void 0);
var count$3 = 0;
function useId$1(deterministicId) {
  const [id, setId] = React.useState(useReactId$1());
  useLayoutEffect2(() => {
    setId((reactId) => reactId ?? String(count$3++));
  }, [deterministicId]);
  return deterministicId || (id ? `radix-${id}` : "");
}

// packages/react/use-callback-ref/src/useCallbackRef.tsx
function useCallbackRef$1(callback) {
  const callbackRef = React.useRef(callback);
  React.useEffect(() => {
    callbackRef.current = callback;
  });
  return React.useMemo(() => (...args) => callbackRef.current?.(...args), []);
}

// packages/react/use-controllable-state/src/useControllableState.tsx
function useControllableState({
  prop,
  defaultProp,
  onChange = () => {
  }
}) {
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({ defaultProp, onChange });
  const isControlled = prop !== void 0;
  const value = isControlled ? prop : uncontrolledProp;
  const handleChange = useCallbackRef$1(onChange);
  const setValue = React.useCallback(
    (nextValue) => {
      if (isControlled) {
        const setter = nextValue;
        const value2 = typeof nextValue === "function" ? setter(prop) : nextValue;
        if (value2 !== prop) handleChange(value2);
      } else {
        setUncontrolledProp(nextValue);
      }
    },
    [isControlled, prop, setUncontrolledProp, handleChange]
  );
  return [value, setValue];
}
function useUncontrolledState({
  defaultProp,
  onChange
}) {
  const uncontrolledState = React.useState(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = React.useRef(value);
  const handleChange = useCallbackRef$1(onChange);
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      handleChange(value);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef, handleChange]);
  return uncontrolledState;
}

// packages/react/slot/src/slot.tsx
var Slot = React.forwardRef((props, forwardedRef) => {
  const { children, ...slotProps } = props;
  const childrenArray = React.Children.toArray(children);
  const slottable = childrenArray.find(isSlottable);
  if (slottable) {
    const newElement = slottable.props.children;
    const newChildren = childrenArray.map((child) => {
      if (child === slottable) {
        if (React.Children.count(newElement) > 1) return React.Children.only(null);
        return React.isValidElement(newElement) ? newElement.props.children : null;
      } else {
        return child;
      }
    });
    return /* @__PURE__ */ jsx$1(SlotClone, { ...slotProps, ref: forwardedRef, children: React.isValidElement(newElement) ? React.cloneElement(newElement, void 0, newChildren) : null });
  }
  return /* @__PURE__ */ jsx$1(SlotClone, { ...slotProps, ref: forwardedRef, children });
});
Slot.displayName = "Slot";
var SlotClone = React.forwardRef((props, forwardedRef) => {
  const { children, ...slotProps } = props;
  if (React.isValidElement(children)) {
    const childrenRef = getElementRef$1(children);
    const props2 = mergeProps(slotProps, children.props);
    if (children.type !== React.Fragment) {
      props2.ref = forwardedRef ? composeRefs(forwardedRef, childrenRef) : childrenRef;
    }
    return React.cloneElement(children, props2);
  }
  return React.Children.count(children) > 1 ? React.Children.only(null) : null;
});
SlotClone.displayName = "SlotClone";
var Slottable = ({ children }) => {
  return /* @__PURE__ */ jsx$1(Fragment, { children });
};
function isSlottable(child) {
  return React.isValidElement(child) && child.type === Slottable;
}
function mergeProps(slotProps, childProps) {
  const overrideProps = { ...childProps };
  for (const propName in childProps) {
    const slotPropValue = slotProps[propName];
    const childPropValue = childProps[propName];
    const isHandler = /^on[A-Z]/.test(propName);
    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args) => {
          childPropValue(...args);
          slotPropValue(...args);
        };
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue;
      }
    } else if (propName === "style") {
      overrideProps[propName] = { ...slotPropValue, ...childPropValue };
    } else if (propName === "className") {
      overrideProps[propName] = [slotPropValue, childPropValue].filter(Boolean).join(" ");
    }
  }
  return { ...slotProps, ...overrideProps };
}
function getElementRef$1(element) {
  let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
  let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.ref;
  }
  getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
  mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.props.ref;
  }
  return element.props.ref || element.ref;
}

// packages/react/primitive/src/primitive.tsx
var NODES = [
  "a",
  "button",
  "div",
  "form",
  "h2",
  "h3",
  "img",
  "input",
  "label",
  "li",
  "nav",
  "ol",
  "p",
  "span",
  "svg",
  "ul"
];
var Primitive = NODES.reduce((primitive, node) => {
  const Node = React.forwardRef((props, forwardedRef) => {
    const { asChild, ...primitiveProps } = props;
    const Comp = asChild ? Slot : node;
    if (typeof window !== "undefined") {
      window[Symbol.for("radix-ui")] = true;
    }
    return /* @__PURE__ */ jsx$1(Comp, { ...primitiveProps, ref: forwardedRef });
  });
  Node.displayName = `Primitive.${node}`;
  return { ...primitive, [node]: Node };
}, {});
function dispatchDiscreteCustomEvent(target, event) {
  if (target) ReactDOM.flushSync(() => target.dispatchEvent(event));
}

// packages/react/use-escape-keydown/src/useEscapeKeydown.tsx
function useEscapeKeydown(onEscapeKeyDownProp, ownerDocument = globalThis?.document) {
  const onEscapeKeyDown = useCallbackRef$1(onEscapeKeyDownProp);
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onEscapeKeyDown(event);
      }
    };
    ownerDocument.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => ownerDocument.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [onEscapeKeyDown, ownerDocument]);
}

var DISMISSABLE_LAYER_NAME = "DismissableLayer";
var CONTEXT_UPDATE = "dismissableLayer.update";
var POINTER_DOWN_OUTSIDE = "dismissableLayer.pointerDownOutside";
var FOCUS_OUTSIDE = "dismissableLayer.focusOutside";
var originalBodyPointerEvents;
var DismissableLayerContext = React.createContext({
  layers: /* @__PURE__ */ new Set(),
  layersWithOutsidePointerEventsDisabled: /* @__PURE__ */ new Set(),
  branches: /* @__PURE__ */ new Set()
});
var DismissableLayer = React.forwardRef(
  (props, forwardedRef) => {
    const {
      disableOutsidePointerEvents = false,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      onDismiss,
      ...layerProps
    } = props;
    const context = React.useContext(DismissableLayerContext);
    const [node, setNode] = React.useState(null);
    const ownerDocument = node?.ownerDocument ?? globalThis?.document;
    const [, force] = React.useState({});
    const composedRefs = useComposedRefs(forwardedRef, (node2) => setNode(node2));
    const layers = Array.from(context.layers);
    const [highestLayerWithOutsidePointerEventsDisabled] = [...context.layersWithOutsidePointerEventsDisabled].slice(-1);
    const highestLayerWithOutsidePointerEventsDisabledIndex = layers.indexOf(highestLayerWithOutsidePointerEventsDisabled);
    const index = node ? layers.indexOf(node) : -1;
    const isBodyPointerEventsDisabled = context.layersWithOutsidePointerEventsDisabled.size > 0;
    const isPointerEventsEnabled = index >= highestLayerWithOutsidePointerEventsDisabledIndex;
    const pointerDownOutside = usePointerDownOutside((event) => {
      const target = event.target;
      const isPointerDownOnBranch = [...context.branches].some((branch) => branch.contains(target));
      if (!isPointerEventsEnabled || isPointerDownOnBranch) return;
      onPointerDownOutside?.(event);
      onInteractOutside?.(event);
      if (!event.defaultPrevented) onDismiss?.();
    }, ownerDocument);
    const focusOutside = useFocusOutside((event) => {
      const target = event.target;
      const isFocusInBranch = [...context.branches].some((branch) => branch.contains(target));
      if (isFocusInBranch) return;
      onFocusOutside?.(event);
      onInteractOutside?.(event);
      if (!event.defaultPrevented) onDismiss?.();
    }, ownerDocument);
    useEscapeKeydown((event) => {
      const isHighestLayer = index === context.layers.size - 1;
      if (!isHighestLayer) return;
      onEscapeKeyDown?.(event);
      if (!event.defaultPrevented && onDismiss) {
        event.preventDefault();
        onDismiss();
      }
    }, ownerDocument);
    React.useEffect(() => {
      if (!node) return;
      if (disableOutsidePointerEvents) {
        if (context.layersWithOutsidePointerEventsDisabled.size === 0) {
          originalBodyPointerEvents = ownerDocument.body.style.pointerEvents;
          ownerDocument.body.style.pointerEvents = "none";
        }
        context.layersWithOutsidePointerEventsDisabled.add(node);
      }
      context.layers.add(node);
      dispatchUpdate();
      return () => {
        if (disableOutsidePointerEvents && context.layersWithOutsidePointerEventsDisabled.size === 1) {
          ownerDocument.body.style.pointerEvents = originalBodyPointerEvents;
        }
      };
    }, [node, ownerDocument, disableOutsidePointerEvents, context]);
    React.useEffect(() => {
      return () => {
        if (!node) return;
        context.layers.delete(node);
        context.layersWithOutsidePointerEventsDisabled.delete(node);
        dispatchUpdate();
      };
    }, [node, context]);
    React.useEffect(() => {
      const handleUpdate = () => force({});
      document.addEventListener(CONTEXT_UPDATE, handleUpdate);
      return () => document.removeEventListener(CONTEXT_UPDATE, handleUpdate);
    }, []);
    return /* @__PURE__ */ jsx$1(
      Primitive.div,
      {
        ...layerProps,
        ref: composedRefs,
        style: {
          pointerEvents: isBodyPointerEventsDisabled ? isPointerEventsEnabled ? "auto" : "none" : void 0,
          ...props.style
        },
        onFocusCapture: composeEventHandlers(props.onFocusCapture, focusOutside.onFocusCapture),
        onBlurCapture: composeEventHandlers(props.onBlurCapture, focusOutside.onBlurCapture),
        onPointerDownCapture: composeEventHandlers(
          props.onPointerDownCapture,
          pointerDownOutside.onPointerDownCapture
        )
      }
    );
  }
);
DismissableLayer.displayName = DISMISSABLE_LAYER_NAME;
var BRANCH_NAME = "DismissableLayerBranch";
var DismissableLayerBranch = React.forwardRef((props, forwardedRef) => {
  const context = React.useContext(DismissableLayerContext);
  const ref = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, ref);
  React.useEffect(() => {
    const node = ref.current;
    if (node) {
      context.branches.add(node);
      return () => {
        context.branches.delete(node);
      };
    }
  }, [context.branches]);
  return /* @__PURE__ */ jsx$1(Primitive.div, { ...props, ref: composedRefs });
});
DismissableLayerBranch.displayName = BRANCH_NAME;
function usePointerDownOutside(onPointerDownOutside, ownerDocument = globalThis?.document) {
  const handlePointerDownOutside = useCallbackRef$1(onPointerDownOutside);
  const isPointerInsideReactTreeRef = React.useRef(false);
  const handleClickRef = React.useRef(() => {
  });
  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (event.target && !isPointerInsideReactTreeRef.current) {
        let handleAndDispatchPointerDownOutsideEvent2 = function() {
          handleAndDispatchCustomEvent(
            POINTER_DOWN_OUTSIDE,
            handlePointerDownOutside,
            eventDetail,
            { discrete: true }
          );
        };
        const eventDetail = { originalEvent: event };
        if (event.pointerType === "touch") {
          ownerDocument.removeEventListener("click", handleClickRef.current);
          handleClickRef.current = handleAndDispatchPointerDownOutsideEvent2;
          ownerDocument.addEventListener("click", handleClickRef.current, { once: true });
        } else {
          handleAndDispatchPointerDownOutsideEvent2();
        }
      } else {
        ownerDocument.removeEventListener("click", handleClickRef.current);
      }
      isPointerInsideReactTreeRef.current = false;
    };
    const timerId = window.setTimeout(() => {
      ownerDocument.addEventListener("pointerdown", handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timerId);
      ownerDocument.removeEventListener("pointerdown", handlePointerDown);
      ownerDocument.removeEventListener("click", handleClickRef.current);
    };
  }, [ownerDocument, handlePointerDownOutside]);
  return {
    // ensures we check React component tree (not just DOM tree)
    onPointerDownCapture: () => isPointerInsideReactTreeRef.current = true
  };
}
function useFocusOutside(onFocusOutside, ownerDocument = globalThis?.document) {
  const handleFocusOutside = useCallbackRef$1(onFocusOutside);
  const isFocusInsideReactTreeRef = React.useRef(false);
  React.useEffect(() => {
    const handleFocus = (event) => {
      if (event.target && !isFocusInsideReactTreeRef.current) {
        const eventDetail = { originalEvent: event };
        handleAndDispatchCustomEvent(FOCUS_OUTSIDE, handleFocusOutside, eventDetail, {
          discrete: false
        });
      }
    };
    ownerDocument.addEventListener("focusin", handleFocus);
    return () => ownerDocument.removeEventListener("focusin", handleFocus);
  }, [ownerDocument, handleFocusOutside]);
  return {
    onFocusCapture: () => isFocusInsideReactTreeRef.current = true,
    onBlurCapture: () => isFocusInsideReactTreeRef.current = false
  };
}
function dispatchUpdate() {
  const event = new CustomEvent(CONTEXT_UPDATE);
  document.dispatchEvent(event);
}
function handleAndDispatchCustomEvent(name, handler, detail, { discrete }) {
  const target = detail.originalEvent.target;
  const event = new CustomEvent(name, { bubbles: false, cancelable: true, detail });
  if (handler) target.addEventListener(name, handler, { once: true });
  if (discrete) {
    dispatchDiscreteCustomEvent(target, event);
  } else {
    target.dispatchEvent(event);
  }
}

var AUTOFOCUS_ON_MOUNT = "focusScope.autoFocusOnMount";
var AUTOFOCUS_ON_UNMOUNT = "focusScope.autoFocusOnUnmount";
var EVENT_OPTIONS$1 = { bubbles: false, cancelable: true };
var FOCUS_SCOPE_NAME = "FocusScope";
var FocusScope = React.forwardRef((props, forwardedRef) => {
  const {
    loop = false,
    trapped = false,
    onMountAutoFocus: onMountAutoFocusProp,
    onUnmountAutoFocus: onUnmountAutoFocusProp,
    ...scopeProps
  } = props;
  const [container, setContainer] = React.useState(null);
  const onMountAutoFocus = useCallbackRef$1(onMountAutoFocusProp);
  const onUnmountAutoFocus = useCallbackRef$1(onUnmountAutoFocusProp);
  const lastFocusedElementRef = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, (node) => setContainer(node));
  const focusScope = React.useRef({
    paused: false,
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    }
  }).current;
  React.useEffect(() => {
    if (trapped) {
      let handleFocusIn2 = function(event) {
        if (focusScope.paused || !container) return;
        const target = event.target;
        if (container.contains(target)) {
          lastFocusedElementRef.current = target;
        } else {
          focus(lastFocusedElementRef.current, { select: true });
        }
      }, handleFocusOut2 = function(event) {
        if (focusScope.paused || !container) return;
        const relatedTarget = event.relatedTarget;
        if (relatedTarget === null) return;
        if (!container.contains(relatedTarget)) {
          focus(lastFocusedElementRef.current, { select: true });
        }
      }, handleMutations2 = function(mutations) {
        const focusedElement = document.activeElement;
        if (focusedElement !== document.body) return;
        for (const mutation of mutations) {
          if (mutation.removedNodes.length > 0) focus(container);
        }
      };
      document.addEventListener("focusin", handleFocusIn2);
      document.addEventListener("focusout", handleFocusOut2);
      const mutationObserver = new MutationObserver(handleMutations2);
      if (container) mutationObserver.observe(container, { childList: true, subtree: true });
      return () => {
        document.removeEventListener("focusin", handleFocusIn2);
        document.removeEventListener("focusout", handleFocusOut2);
        mutationObserver.disconnect();
      };
    }
  }, [trapped, container, focusScope.paused]);
  React.useEffect(() => {
    if (container) {
      focusScopesStack.add(focusScope);
      const previouslyFocusedElement = document.activeElement;
      const hasFocusedCandidate = container.contains(previouslyFocusedElement);
      if (!hasFocusedCandidate) {
        const mountEvent = new CustomEvent(AUTOFOCUS_ON_MOUNT, EVENT_OPTIONS$1);
        container.addEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
        container.dispatchEvent(mountEvent);
        if (!mountEvent.defaultPrevented) {
          focusFirst$2(removeLinks(getTabbableCandidates(container)), { select: true });
          if (document.activeElement === previouslyFocusedElement) {
            focus(container);
          }
        }
      }
      return () => {
        container.removeEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
        setTimeout(() => {
          const unmountEvent = new CustomEvent(AUTOFOCUS_ON_UNMOUNT, EVENT_OPTIONS$1);
          container.addEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          container.dispatchEvent(unmountEvent);
          if (!unmountEvent.defaultPrevented) {
            focus(previouslyFocusedElement ?? document.body, { select: true });
          }
          container.removeEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          focusScopesStack.remove(focusScope);
        }, 0);
      };
    }
  }, [container, onMountAutoFocus, onUnmountAutoFocus, focusScope]);
  const handleKeyDown = React.useCallback(
    (event) => {
      if (!loop && !trapped) return;
      if (focusScope.paused) return;
      const isTabKey = event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey;
      const focusedElement = document.activeElement;
      if (isTabKey && focusedElement) {
        const container2 = event.currentTarget;
        const [first, last] = getTabbableEdges(container2);
        const hasTabbableElementsInside = first && last;
        if (!hasTabbableElementsInside) {
          if (focusedElement === container2) event.preventDefault();
        } else {
          if (!event.shiftKey && focusedElement === last) {
            event.preventDefault();
            if (loop) focus(first, { select: true });
          } else if (event.shiftKey && focusedElement === first) {
            event.preventDefault();
            if (loop) focus(last, { select: true });
          }
        }
      }
    },
    [loop, trapped, focusScope.paused]
  );
  return /* @__PURE__ */ jsx$1(Primitive.div, { tabIndex: -1, ...scopeProps, ref: composedRefs, onKeyDown: handleKeyDown });
});
FocusScope.displayName = FOCUS_SCOPE_NAME;
function focusFirst$2(candidates, { select = false } = {}) {
  const previouslyFocusedElement = document.activeElement;
  for (const candidate of candidates) {
    focus(candidate, { select });
    if (document.activeElement !== previouslyFocusedElement) return;
  }
}
function getTabbableEdges(container) {
  const candidates = getTabbableCandidates(container);
  const first = findVisible(candidates, container);
  const last = findVisible(candidates.reverse(), container);
  return [first, last];
}
function getTabbableCandidates(container) {
  const nodes = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const isHiddenInput = node.tagName === "INPUT" && node.type === "hidden";
      if (node.disabled || node.hidden || isHiddenInput) return NodeFilter.FILTER_SKIP;
      return node.tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    }
  });
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}
function findVisible(elements, container) {
  for (const element of elements) {
    if (!isHidden(element, { upTo: container })) return element;
  }
}
function isHidden(node, { upTo }) {
  if (getComputedStyle(node).visibility === "hidden") return true;
  while (node) {
    if (upTo !== void 0 && node === upTo) return false;
    if (getComputedStyle(node).display === "none") return true;
    node = node.parentElement;
  }
  return false;
}
function isSelectableInput(element) {
  return element instanceof HTMLInputElement && "select" in element;
}
function focus(element, { select = false } = {}) {
  if (element && element.focus) {
    const previouslyFocusedElement = document.activeElement;
    element.focus({ preventScroll: true });
    if (element !== previouslyFocusedElement && isSelectableInput(element) && select)
      element.select();
  }
}
var focusScopesStack = createFocusScopesStack();
function createFocusScopesStack() {
  let stack = [];
  return {
    add(focusScope) {
      const activeFocusScope = stack[0];
      if (focusScope !== activeFocusScope) {
        activeFocusScope?.pause();
      }
      stack = arrayRemove(stack, focusScope);
      stack.unshift(focusScope);
    },
    remove(focusScope) {
      stack = arrayRemove(stack, focusScope);
      stack[0]?.resume();
    }
  };
}
function arrayRemove(array, item) {
  const updatedArray = [...array];
  const index = updatedArray.indexOf(item);
  if (index !== -1) {
    updatedArray.splice(index, 1);
  }
  return updatedArray;
}
function removeLinks(items) {
  return items.filter((item) => item.tagName !== "A");
}

var PORTAL_NAME$6 = "Portal";
var Portal$4 = React.forwardRef((props, forwardedRef) => {
  const { container: containerProp, ...portalProps } = props;
  const [mounted, setMounted] = React.useState(false);
  useLayoutEffect2(() => setMounted(true), []);
  const container = containerProp || mounted && globalThis?.document?.body;
  return container ? ReactDOM__default.createPortal(/* @__PURE__ */ jsx$1(Primitive.div, { ...portalProps, ref: forwardedRef }), container) : null;
});
Portal$4.displayName = PORTAL_NAME$6;

function useStateMachine(initialState, machine) {
  return React.useReducer((state, event) => {
    const nextState = machine[state][event];
    return nextState ?? state;
  }, initialState);
}

// packages/react/presence/src/Presence.tsx
var Presence = (props) => {
  const { present, children } = props;
  const presence = usePresence(present);
  const child = typeof children === "function" ? children({ present: presence.isPresent }) : React.Children.only(children);
  const ref = useComposedRefs(presence.ref, getElementRef(child));
  const forceMount = typeof children === "function";
  return forceMount || presence.isPresent ? React.cloneElement(child, { ref }) : null;
};
Presence.displayName = "Presence";
function usePresence(present) {
  const [node, setNode] = React.useState();
  const stylesRef = React.useRef({});
  const prevPresentRef = React.useRef(present);
  const prevAnimationNameRef = React.useRef("none");
  const initialState = present ? "mounted" : "unmounted";
  const [state, send] = useStateMachine(initialState, {
    mounted: {
      UNMOUNT: "unmounted",
      ANIMATION_OUT: "unmountSuspended"
    },
    unmountSuspended: {
      MOUNT: "mounted",
      ANIMATION_END: "unmounted"
    },
    unmounted: {
      MOUNT: "mounted"
    }
  });
  React.useEffect(() => {
    const currentAnimationName = getAnimationName(stylesRef.current);
    prevAnimationNameRef.current = state === "mounted" ? currentAnimationName : "none";
  }, [state]);
  useLayoutEffect2(() => {
    const styles = stylesRef.current;
    const wasPresent = prevPresentRef.current;
    const hasPresentChanged = wasPresent !== present;
    if (hasPresentChanged) {
      const prevAnimationName = prevAnimationNameRef.current;
      const currentAnimationName = getAnimationName(styles);
      if (present) {
        send("MOUNT");
      } else if (currentAnimationName === "none" || styles?.display === "none") {
        send("UNMOUNT");
      } else {
        const isAnimating = prevAnimationName !== currentAnimationName;
        if (wasPresent && isAnimating) {
          send("ANIMATION_OUT");
        } else {
          send("UNMOUNT");
        }
      }
      prevPresentRef.current = present;
    }
  }, [present, send]);
  useLayoutEffect2(() => {
    if (node) {
      let timeoutId;
      const ownerWindow = node.ownerDocument.defaultView ?? window;
      const handleAnimationEnd = (event) => {
        const currentAnimationName = getAnimationName(stylesRef.current);
        const isCurrentAnimation = currentAnimationName.includes(event.animationName);
        if (event.target === node && isCurrentAnimation) {
          send("ANIMATION_END");
          if (!prevPresentRef.current) {
            const currentFillMode = node.style.animationFillMode;
            node.style.animationFillMode = "forwards";
            timeoutId = ownerWindow.setTimeout(() => {
              if (node.style.animationFillMode === "forwards") {
                node.style.animationFillMode = currentFillMode;
              }
            });
          }
        }
      };
      const handleAnimationStart = (event) => {
        if (event.target === node) {
          prevAnimationNameRef.current = getAnimationName(stylesRef.current);
        }
      };
      node.addEventListener("animationstart", handleAnimationStart);
      node.addEventListener("animationcancel", handleAnimationEnd);
      node.addEventListener("animationend", handleAnimationEnd);
      return () => {
        ownerWindow.clearTimeout(timeoutId);
        node.removeEventListener("animationstart", handleAnimationStart);
        node.removeEventListener("animationcancel", handleAnimationEnd);
        node.removeEventListener("animationend", handleAnimationEnd);
      };
    } else {
      send("ANIMATION_END");
    }
  }, [node, send]);
  return {
    isPresent: ["mounted", "unmountSuspended"].includes(state),
    ref: React.useCallback((node2) => {
      if (node2) stylesRef.current = getComputedStyle(node2);
      setNode(node2);
    }, [])
  };
}
function getAnimationName(styles) {
  return styles?.animationName || "none";
}
function getElementRef(element) {
  let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
  let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.ref;
  }
  getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
  mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.props.ref;
  }
  return element.props.ref || element.ref;
}

var count$2 = 0;
function useFocusGuards() {
  React.useEffect(() => {
    const edgeGuards = document.querySelectorAll("[data-radix-focus-guard]");
    document.body.insertAdjacentElement("afterbegin", edgeGuards[0] ?? createFocusGuard());
    document.body.insertAdjacentElement("beforeend", edgeGuards[1] ?? createFocusGuard());
    count$2++;
    return () => {
      if (count$2 === 1) {
        document.querySelectorAll("[data-radix-focus-guard]").forEach((node) => node.remove());
      }
      count$2--;
    };
  }, []);
}
function createFocusGuard() {
  const element = document.createElement("span");
  element.setAttribute("data-radix-focus-guard", "");
  element.tabIndex = 0;
  element.style.outline = "none";
  element.style.opacity = "0";
  element.style.position = "fixed";
  element.style.pointerEvents = "none";
  return element;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray$1(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var zeroRightClassName = 'right-scroll-bar-position';
var fullWidthClassName = 'width-before-scroll-bar';
var noScrollbarsClassName = 'with-scroll-bars-hidden';
/**
 * Name of a CSS variable containing the amount of "hidden" scrollbar
 * ! might be undefined ! use will fallback!
 */
var removedBarSizeVariable = '--removed-body-scroll-bar-size';

/**
 * Assigns a value for a given ref, no matter of the ref format
 * @param {RefObject} ref - a callback function or ref object
 * @param value - a new value
 *
 * @see https://github.com/theKashey/use-callback-ref#assignref
 * @example
 * const refObject = useRef();
 * const refFn = (ref) => {....}
 *
 * assignRef(refObject, "refValue");
 * assignRef(refFn, "refValue");
 */
function assignRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
    return ref;
}

/**
 * creates a MutableRef with ref change callback
 * @param initialValue - initial ref value
 * @param {Function} callback - a callback to run when value changes
 *
 * @example
 * const ref = useCallbackRef(0, (newValue, oldValue) => console.log(oldValue, '->', newValue);
 * ref.current = 1;
 * // prints 0 -> 1
 *
 * @see https://reactjs.org/docs/hooks-reference.html#useref
 * @see https://github.com/theKashey/use-callback-ref#usecallbackref---to-replace-reactuseref
 * @returns {MutableRefObject}
 */
function useCallbackRef(initialValue, callback) {
    var ref = useState(function () { return ({
        // value
        value: initialValue,
        // last callback
        callback: callback,
        // "memoized" public interface
        facade: {
            get current() {
                return ref.value;
            },
            set current(value) {
                var last = ref.value;
                if (last !== value) {
                    ref.value = value;
                    ref.callback(value, last);
                }
            },
        },
    }); })[0];
    // update callback
    ref.callback = callback;
    return ref.facade;
}

var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;
var currentValues = new WeakMap();
/**
 * Merges two or more refs together providing a single interface to set their value
 * @param {RefObject|Ref} refs
 * @returns {MutableRefObject} - a new ref, which translates all changes to {refs}
 *
 * @see {@link mergeRefs} a version without buit-in memoization
 * @see https://github.com/theKashey/use-callback-ref#usemergerefs
 * @example
 * const Component = React.forwardRef((props, ref) => {
 *   const ownRef = useRef();
 *   const domRef = useMergeRefs([ref, ownRef]); // 👈 merge together
 *   return <div ref={domRef}>...</div>
 * }
 */
function useMergeRefs(refs, defaultValue) {
    var callbackRef = useCallbackRef(null, function (newValue) {
        return refs.forEach(function (ref) { return assignRef(ref, newValue); });
    });
    // handle refs changes - added or removed
    useIsomorphicLayoutEffect(function () {
        var oldValue = currentValues.get(callbackRef);
        if (oldValue) {
            var prevRefs_1 = new Set(oldValue);
            var nextRefs_1 = new Set(refs);
            var current_1 = callbackRef.current;
            prevRefs_1.forEach(function (ref) {
                if (!nextRefs_1.has(ref)) {
                    assignRef(ref, null);
                }
            });
            nextRefs_1.forEach(function (ref) {
                if (!prevRefs_1.has(ref)) {
                    assignRef(ref, current_1);
                }
            });
        }
        currentValues.set(callbackRef, refs);
    }, [refs]);
    return callbackRef;
}

var global$1 = (typeof global !== "undefined" ? global :
  typeof self !== "undefined" ? self :
  typeof window !== "undefined" ? window : {});

var env = {};

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance = global$1.performance || {};
performance.now        ||
  performance.mozNow     ||
  performance.msNow      ||
  performance.oNow       ||
  performance.webkitNow  ||
  function(){ return (new Date()).getTime() };

var browser$1 = {
  env: env};

function ItoI(a) {
    return a;
}
function innerCreateMedium(defaults, middleware) {
    if (middleware === void 0) { middleware = ItoI; }
    var buffer = [];
    var assigned = false;
    var medium = {
        read: function () {
            if (assigned) {
                throw new Error('Sidecar: could not `read` from an `assigned` medium. `read` could be used only with `useMedium`.');
            }
            if (buffer.length) {
                return buffer[buffer.length - 1];
            }
            return defaults;
        },
        useMedium: function (data) {
            var item = middleware(data, assigned);
            buffer.push(item);
            return function () {
                buffer = buffer.filter(function (x) { return x !== item; });
            };
        },
        assignSyncMedium: function (cb) {
            assigned = true;
            while (buffer.length) {
                var cbs = buffer;
                buffer = [];
                cbs.forEach(cb);
            }
            buffer = {
                push: function (x) { return cb(x); },
                filter: function () { return buffer; },
            };
        },
        assignMedium: function (cb) {
            assigned = true;
            var pendingQueue = [];
            if (buffer.length) {
                var cbs = buffer;
                buffer = [];
                cbs.forEach(cb);
                pendingQueue = buffer;
            }
            var executeQueue = function () {
                var cbs = pendingQueue;
                pendingQueue = [];
                cbs.forEach(cb);
            };
            var cycle = function () { return Promise.resolve().then(executeQueue); };
            cycle();
            buffer = {
                push: function (x) {
                    pendingQueue.push(x);
                    cycle();
                },
                filter: function (filter) {
                    pendingQueue = pendingQueue.filter(filter);
                    return buffer;
                },
            };
        },
    };
    return medium;
}
// eslint-disable-next-line @typescript-eslint/ban-types
function createSidecarMedium(options) {
    if (options === void 0) { options = {}; }
    var medium = innerCreateMedium(null);
    medium.options = __assign({ async: true, ssr: false }, options);
    return medium;
}

var SideCar$1 = function (_a) {
    var sideCar = _a.sideCar, rest = __rest(_a, ["sideCar"]);
    if (!sideCar) {
        throw new Error('Sidecar: please provide `sideCar` property to import the right car');
    }
    var Target = sideCar.read();
    if (!Target) {
        throw new Error('Sidecar medium not found');
    }
    return React.createElement(Target, __assign({}, rest));
};
SideCar$1.isSideCarExport = true;
function exportSidecar(medium, exported) {
    medium.useMedium(exported);
    return SideCar$1;
}

var effectCar = createSidecarMedium();

var nothing = function () {
    return;
};
/**
 * Removes scrollbar from the page and contain the scroll within the Lock
 */
var RemoveScroll = React.forwardRef(function (props, parentRef) {
    var ref = React.useRef(null);
    var _a = React.useState({
        onScrollCapture: nothing,
        onWheelCapture: nothing,
        onTouchMoveCapture: nothing,
    }), callbacks = _a[0], setCallbacks = _a[1];
    var forwardProps = props.forwardProps, children = props.children, className = props.className, removeScrollBar = props.removeScrollBar, enabled = props.enabled, shards = props.shards, sideCar = props.sideCar, noIsolation = props.noIsolation, inert = props.inert, allowPinchZoom = props.allowPinchZoom, _b = props.as, Container = _b === void 0 ? 'div' : _b, gapMode = props.gapMode, rest = __rest(props, ["forwardProps", "children", "className", "removeScrollBar", "enabled", "shards", "sideCar", "noIsolation", "inert", "allowPinchZoom", "as", "gapMode"]);
    var SideCar = sideCar;
    var containerRef = useMergeRefs([ref, parentRef]);
    var containerProps = __assign(__assign({}, rest), callbacks);
    return (React.createElement(React.Fragment, null,
        enabled && (React.createElement(SideCar, { sideCar: effectCar, removeScrollBar: removeScrollBar, shards: shards, noIsolation: noIsolation, inert: inert, setCallbacks: setCallbacks, allowPinchZoom: !!allowPinchZoom, lockRef: ref, gapMode: gapMode })),
        forwardProps ? (React.cloneElement(React.Children.only(children), __assign(__assign({}, containerProps), { ref: containerRef }))) : (React.createElement(Container, __assign({}, containerProps, { className: className, ref: containerRef }), children))));
});
RemoveScroll.defaultProps = {
    enabled: true,
    removeScrollBar: true,
    inert: false,
};
RemoveScroll.classNames = {
    fullWidth: fullWidthClassName,
    zeroRight: zeroRightClassName,
};

var getNonce = function () {
    if (typeof __webpack_nonce__ !== 'undefined') {
        return __webpack_nonce__;
    }
    return undefined;
};

function makeStyleTag() {
    if (!document)
        return null;
    var tag = document.createElement('style');
    tag.type = 'text/css';
    var nonce = getNonce();
    if (nonce) {
        tag.setAttribute('nonce', nonce);
    }
    return tag;
}
function injectStyles(tag, css) {
    // @ts-ignore
    if (tag.styleSheet) {
        // @ts-ignore
        tag.styleSheet.cssText = css;
    }
    else {
        tag.appendChild(document.createTextNode(css));
    }
}
function insertStyleTag(tag) {
    var head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(tag);
}
var stylesheetSingleton = function () {
    var counter = 0;
    var stylesheet = null;
    return {
        add: function (style) {
            if (counter == 0) {
                if ((stylesheet = makeStyleTag())) {
                    injectStyles(stylesheet, style);
                    insertStyleTag(stylesheet);
                }
            }
            counter++;
        },
        remove: function () {
            counter--;
            if (!counter && stylesheet) {
                stylesheet.parentNode && stylesheet.parentNode.removeChild(stylesheet);
                stylesheet = null;
            }
        },
    };
};

/**
 * creates a hook to control style singleton
 * @see {@link styleSingleton} for a safer component version
 * @example
 * ```tsx
 * const useStyle = styleHookSingleton();
 * ///
 * useStyle('body { overflow: hidden}');
 */
var styleHookSingleton = function () {
    var sheet = stylesheetSingleton();
    return function (styles, isDynamic) {
        React.useEffect(function () {
            sheet.add(styles);
            return function () {
                sheet.remove();
            };
        }, [styles && isDynamic]);
    };
};

/**
 * create a Component to add styles on demand
 * - styles are added when first instance is mounted
 * - styles are removed when the last instance is unmounted
 * - changing styles in runtime does nothing unless dynamic is set. But with multiple components that can lead to the undefined behavior
 */
var styleSingleton = function () {
    var useStyle = styleHookSingleton();
    var Sheet = function (_a) {
        var styles = _a.styles, dynamic = _a.dynamic;
        useStyle(styles, dynamic);
        return null;
    };
    return Sheet;
};

var zeroGap = {
    left: 0,
    top: 0,
    right: 0,
    gap: 0,
};
var parse$2 = function (x) { return parseInt(x || '', 10) || 0; };
var getOffset = function (gapMode) {
    var cs = window.getComputedStyle(document.body);
    var left = cs[gapMode === 'padding' ? 'paddingLeft' : 'marginLeft'];
    var top = cs[gapMode === 'padding' ? 'paddingTop' : 'marginTop'];
    var right = cs[gapMode === 'padding' ? 'paddingRight' : 'marginRight'];
    return [parse$2(left), parse$2(top), parse$2(right)];
};
var getGapWidth = function (gapMode) {
    if (gapMode === void 0) { gapMode = 'margin'; }
    if (typeof window === 'undefined') {
        return zeroGap;
    }
    var offsets = getOffset(gapMode);
    var documentWidth = document.documentElement.clientWidth;
    var windowWidth = window.innerWidth;
    return {
        left: offsets[0],
        top: offsets[1],
        right: offsets[2],
        gap: Math.max(0, windowWidth - documentWidth + offsets[2] - offsets[0]),
    };
};

var Style = styleSingleton();
var lockAttribute = 'data-scroll-locked';
// important tip - once we measure scrollBar width and remove them
// we could not repeat this operation
// thus we are using style-singleton - only the first "yet correct" style will be applied.
var getStyles = function (_a, allowRelative, gapMode, important) {
    var left = _a.left, top = _a.top, right = _a.right, gap = _a.gap;
    if (gapMode === void 0) { gapMode = 'margin'; }
    return "\n  .".concat(noScrollbarsClassName, " {\n   overflow: hidden ").concat(important, ";\n   padding-right: ").concat(gap, "px ").concat(important, ";\n  }\n  body[").concat(lockAttribute, "] {\n    overflow: hidden ").concat(important, ";\n    overscroll-behavior: contain;\n    ").concat([
        allowRelative && "position: relative ".concat(important, ";"),
        gapMode === 'margin' &&
            "\n    padding-left: ".concat(left, "px;\n    padding-top: ").concat(top, "px;\n    padding-right: ").concat(right, "px;\n    margin-left:0;\n    margin-top:0;\n    margin-right: ").concat(gap, "px ").concat(important, ";\n    "),
        gapMode === 'padding' && "padding-right: ".concat(gap, "px ").concat(important, ";"),
    ]
        .filter(Boolean)
        .join(''), "\n  }\n  \n  .").concat(zeroRightClassName, " {\n    right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " {\n    margin-right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(zeroRightClassName, " .").concat(zeroRightClassName, " {\n    right: 0 ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " .").concat(fullWidthClassName, " {\n    margin-right: 0 ").concat(important, ";\n  }\n  \n  body[").concat(lockAttribute, "] {\n    ").concat(removedBarSizeVariable, ": ").concat(gap, "px;\n  }\n");
};
var getCurrentUseCounter = function () {
    var counter = parseInt(document.body.getAttribute(lockAttribute) || '0', 10);
    return isFinite(counter) ? counter : 0;
};
var useLockAttribute = function () {
    React.useEffect(function () {
        document.body.setAttribute(lockAttribute, (getCurrentUseCounter() + 1).toString());
        return function () {
            var newCounter = getCurrentUseCounter() - 1;
            if (newCounter <= 0) {
                document.body.removeAttribute(lockAttribute);
            }
            else {
                document.body.setAttribute(lockAttribute, newCounter.toString());
            }
        };
    }, []);
};
/**
 * Removes page scrollbar and blocks page scroll when mounted
 */
var RemoveScrollBar = function (_a) {
    var noRelative = _a.noRelative, noImportant = _a.noImportant, _b = _a.gapMode, gapMode = _b === void 0 ? 'margin' : _b;
    useLockAttribute();
    /*
     gap will be measured on every component mount
     however it will be used only by the "first" invocation
     due to singleton nature of <Style
     */
    var gap = React.useMemo(function () { return getGapWidth(gapMode); }, [gapMode]);
    return React.createElement(Style, { styles: getStyles(gap, !noRelative, gapMode, !noImportant ? '!important' : '') });
};

var passiveSupported$1 = false;
if (typeof window !== 'undefined') {
    try {
        var options$1 = Object.defineProperty({}, 'passive', {
            get: function () {
                passiveSupported$1 = true;
                return true;
            },
        });
        // @ts-ignore
        window.addEventListener('test', options$1, options$1);
        // @ts-ignore
        window.removeEventListener('test', options$1, options$1);
    }
    catch (err) {
        passiveSupported$1 = false;
    }
}
var nonPassive = passiveSupported$1 ? { passive: false } : false;

var alwaysContainsScroll = function (node) {
    // textarea will always _contain_ scroll inside self. It only can be hidden
    return node.tagName === 'TEXTAREA';
};
var elementCanBeScrolled = function (node, overflow) {
    if (!(node instanceof Element)) {
        return false;
    }
    var styles = window.getComputedStyle(node);
    return (
    // not-not-scrollable
    styles[overflow] !== 'hidden' &&
        // contains scroll inside self
        !(styles.overflowY === styles.overflowX && !alwaysContainsScroll(node) && styles[overflow] === 'visible'));
};
var elementCouldBeVScrolled = function (node) { return elementCanBeScrolled(node, 'overflowY'); };
var elementCouldBeHScrolled = function (node) { return elementCanBeScrolled(node, 'overflowX'); };
var locationCouldBeScrolled = function (axis, node) {
    var ownerDocument = node.ownerDocument;
    var current = node;
    do {
        // Skip over shadow root
        if (typeof ShadowRoot !== 'undefined' && current instanceof ShadowRoot) {
            current = current.host;
        }
        var isScrollable = elementCouldBeScrolled(axis, current);
        if (isScrollable) {
            var _a = getScrollVariables(axis, current), scrollHeight = _a[1], clientHeight = _a[2];
            if (scrollHeight > clientHeight) {
                return true;
            }
        }
        current = current.parentNode;
    } while (current && current !== ownerDocument.body);
    return false;
};
var getVScrollVariables = function (_a) {
    var scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, clientHeight = _a.clientHeight;
    return [
        scrollTop,
        scrollHeight,
        clientHeight,
    ];
};
var getHScrollVariables = function (_a) {
    var scrollLeft = _a.scrollLeft, scrollWidth = _a.scrollWidth, clientWidth = _a.clientWidth;
    return [
        scrollLeft,
        scrollWidth,
        clientWidth,
    ];
};
var elementCouldBeScrolled = function (axis, node) {
    return axis === 'v' ? elementCouldBeVScrolled(node) : elementCouldBeHScrolled(node);
};
var getScrollVariables = function (axis, node) {
    return axis === 'v' ? getVScrollVariables(node) : getHScrollVariables(node);
};
var getDirectionFactor = function (axis, direction) {
    /**
     * If the element's direction is rtl (right-to-left), then scrollLeft is 0 when the scrollbar is at its rightmost position,
     * and then increasingly negative as you scroll towards the end of the content.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollLeft
     */
    return axis === 'h' && direction === 'rtl' ? -1 : 1;
};
var handleScroll = function (axis, endTarget, event, sourceDelta, noOverscroll) {
    var directionFactor = getDirectionFactor(axis, window.getComputedStyle(endTarget).direction);
    var delta = directionFactor * sourceDelta;
    // find scrollable target
    var target = event.target;
    var targetInLock = endTarget.contains(target);
    var shouldCancelScroll = false;
    var isDeltaPositive = delta > 0;
    var availableScroll = 0;
    var availableScrollTop = 0;
    do {
        var _a = getScrollVariables(axis, target), position = _a[0], scroll_1 = _a[1], capacity = _a[2];
        var elementScroll = scroll_1 - capacity - directionFactor * position;
        if (position || elementScroll) {
            if (elementCouldBeScrolled(axis, target)) {
                availableScroll += elementScroll;
                availableScrollTop += position;
            }
        }
        if (target instanceof ShadowRoot) {
            target = target.host;
        }
        else {
            target = target.parentNode;
        }
    } while (
    // portaled content
    (!targetInLock && target !== document.body) ||
        // self content
        (targetInLock && (endTarget.contains(target) || endTarget === target)));
    // handle epsilon around 0 (non standard zoom levels)
    if (isDeltaPositive &&
        ((Math.abs(availableScroll) < 1) || (false))) {
        shouldCancelScroll = true;
    }
    else if (!isDeltaPositive &&
        ((Math.abs(availableScrollTop) < 1) || (false))) {
        shouldCancelScroll = true;
    }
    return shouldCancelScroll;
};

var getTouchXY = function (event) {
    return 'changedTouches' in event ? [event.changedTouches[0].clientX, event.changedTouches[0].clientY] : [0, 0];
};
var getDeltaXY = function (event) { return [event.deltaX, event.deltaY]; };
var extractRef = function (ref) {
    return ref && 'current' in ref ? ref.current : ref;
};
var deltaCompare = function (x, y) { return x[0] === y[0] && x[1] === y[1]; };
var generateStyle = function (id) { return "\n  .block-interactivity-".concat(id, " {pointer-events: none;}\n  .allow-interactivity-").concat(id, " {pointer-events: all;}\n"); };
var idCounter = 0;
var lockStack = [];
function RemoveScrollSideCar(props) {
    var shouldPreventQueue = React.useRef([]);
    var touchStartRef = React.useRef([0, 0]);
    var activeAxis = React.useRef();
    var id = React.useState(idCounter++)[0];
    var Style = React.useState(styleSingleton)[0];
    var lastProps = React.useRef(props);
    React.useEffect(function () {
        lastProps.current = props;
    }, [props]);
    React.useEffect(function () {
        if (props.inert) {
            document.body.classList.add("block-interactivity-".concat(id));
            var allow_1 = __spreadArray$1([props.lockRef.current], (props.shards || []).map(extractRef), true).filter(Boolean);
            allow_1.forEach(function (el) { return el.classList.add("allow-interactivity-".concat(id)); });
            return function () {
                document.body.classList.remove("block-interactivity-".concat(id));
                allow_1.forEach(function (el) { return el.classList.remove("allow-interactivity-".concat(id)); });
            };
        }
        return;
    }, [props.inert, props.lockRef.current, props.shards]);
    var shouldCancelEvent = React.useCallback(function (event, parent) {
        if (('touches' in event && event.touches.length === 2) || (event.type === 'wheel' && event.ctrlKey)) {
            return !lastProps.current.allowPinchZoom;
        }
        var touch = getTouchXY(event);
        var touchStart = touchStartRef.current;
        var deltaX = 'deltaX' in event ? event.deltaX : touchStart[0] - touch[0];
        var deltaY = 'deltaY' in event ? event.deltaY : touchStart[1] - touch[1];
        var currentAxis;
        var target = event.target;
        var moveDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'h' : 'v';
        // allow horizontal touch move on Range inputs. They will not cause any scroll
        if ('touches' in event && moveDirection === 'h' && target.type === 'range') {
            return false;
        }
        var canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
        if (!canBeScrolledInMainDirection) {
            return true;
        }
        if (canBeScrolledInMainDirection) {
            currentAxis = moveDirection;
        }
        else {
            currentAxis = moveDirection === 'v' ? 'h' : 'v';
            canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
            // other axis might be not scrollable
        }
        if (!canBeScrolledInMainDirection) {
            return false;
        }
        if (!activeAxis.current && 'changedTouches' in event && (deltaX || deltaY)) {
            activeAxis.current = currentAxis;
        }
        if (!currentAxis) {
            return true;
        }
        var cancelingAxis = activeAxis.current || currentAxis;
        return handleScroll(cancelingAxis, parent, event, cancelingAxis === 'h' ? deltaX : deltaY);
    }, []);
    var shouldPrevent = React.useCallback(function (_event) {
        var event = _event;
        if (!lockStack.length || lockStack[lockStack.length - 1] !== Style) {
            // not the last active
            return;
        }
        var delta = 'deltaY' in event ? getDeltaXY(event) : getTouchXY(event);
        var sourceEvent = shouldPreventQueue.current.filter(function (e) { return e.name === event.type && (e.target === event.target || event.target === e.shadowParent) && deltaCompare(e.delta, delta); })[0];
        // self event, and should be canceled
        if (sourceEvent && sourceEvent.should) {
            if (event.cancelable) {
                event.preventDefault();
            }
            return;
        }
        // outside or shard event
        if (!sourceEvent) {
            var shardNodes = (lastProps.current.shards || [])
                .map(extractRef)
                .filter(Boolean)
                .filter(function (node) { return node.contains(event.target); });
            var shouldStop = shardNodes.length > 0 ? shouldCancelEvent(event, shardNodes[0]) : !lastProps.current.noIsolation;
            if (shouldStop) {
                if (event.cancelable) {
                    event.preventDefault();
                }
            }
        }
    }, []);
    var shouldCancel = React.useCallback(function (name, delta, target, should) {
        var event = { name: name, delta: delta, target: target, should: should, shadowParent: getOutermostShadowParent(target) };
        shouldPreventQueue.current.push(event);
        setTimeout(function () {
            shouldPreventQueue.current = shouldPreventQueue.current.filter(function (e) { return e !== event; });
        }, 1);
    }, []);
    var scrollTouchStart = React.useCallback(function (event) {
        touchStartRef.current = getTouchXY(event);
        activeAxis.current = undefined;
    }, []);
    var scrollWheel = React.useCallback(function (event) {
        shouldCancel(event.type, getDeltaXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    var scrollTouchMove = React.useCallback(function (event) {
        shouldCancel(event.type, getTouchXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    React.useEffect(function () {
        lockStack.push(Style);
        props.setCallbacks({
            onScrollCapture: scrollWheel,
            onWheelCapture: scrollWheel,
            onTouchMoveCapture: scrollTouchMove,
        });
        document.addEventListener('wheel', shouldPrevent, nonPassive);
        document.addEventListener('touchmove', shouldPrevent, nonPassive);
        document.addEventListener('touchstart', scrollTouchStart, nonPassive);
        return function () {
            lockStack = lockStack.filter(function (inst) { return inst !== Style; });
            document.removeEventListener('wheel', shouldPrevent, nonPassive);
            document.removeEventListener('touchmove', shouldPrevent, nonPassive);
            document.removeEventListener('touchstart', scrollTouchStart, nonPassive);
        };
    }, []);
    var removeScrollBar = props.removeScrollBar, inert = props.inert;
    return (React.createElement(React.Fragment, null,
        inert ? React.createElement(Style, { styles: generateStyle(id) }) : null,
        removeScrollBar ? React.createElement(RemoveScrollBar, { gapMode: props.gapMode }) : null));
}
function getOutermostShadowParent(node) {
    var shadowParent = null;
    while (node !== null) {
        if (node instanceof ShadowRoot) {
            shadowParent = node.host;
            node = node.host;
        }
        node = node.parentNode;
    }
    return shadowParent;
}

var SideCar = exportSidecar(effectCar, RemoveScrollSideCar);

var ReactRemoveScroll = React.forwardRef(function (props, ref) { return (React.createElement(RemoveScroll, __assign({}, props, { ref: ref, sideCar: SideCar }))); });
ReactRemoveScroll.classNames = RemoveScroll.classNames;

var getDefaultParent = function (originalTarget) {
    if (typeof document === 'undefined') {
        return null;
    }
    var sampleTarget = Array.isArray(originalTarget) ? originalTarget[0] : originalTarget;
    return sampleTarget.ownerDocument.body;
};
var counterMap = new WeakMap();
var uncontrolledNodes = new WeakMap();
var markerMap = {};
var lockCount = 0;
var unwrapHost = function (node) {
    return node && (node.host || unwrapHost(node.parentNode));
};
var correctTargets = function (parent, targets) {
    return targets
        .map(function (target) {
        if (parent.contains(target)) {
            return target;
        }
        var correctedTarget = unwrapHost(target);
        if (correctedTarget && parent.contains(correctedTarget)) {
            return correctedTarget;
        }
        console.error('aria-hidden', target, 'in not contained inside', parent, '. Doing nothing');
        return null;
    })
        .filter(function (x) { return Boolean(x); });
};
/**
 * Marks everything except given node(or nodes) as aria-hidden
 * @param {Element | Element[]} originalTarget - elements to keep on the page
 * @param [parentNode] - top element, defaults to document.body
 * @param {String} [markerName] - a special attribute to mark every node
 * @param {String} [controlAttribute] - html Attribute to control
 * @return {Undo} undo command
 */
var applyAttributeToOthers = function (originalTarget, parentNode, markerName, controlAttribute) {
    var targets = correctTargets(parentNode, Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    if (!markerMap[markerName]) {
        markerMap[markerName] = new WeakMap();
    }
    var markerCounter = markerMap[markerName];
    var hiddenNodes = [];
    var elementsToKeep = new Set();
    var elementsToStop = new Set(targets);
    var keep = function (el) {
        if (!el || elementsToKeep.has(el)) {
            return;
        }
        elementsToKeep.add(el);
        keep(el.parentNode);
    };
    targets.forEach(keep);
    var deep = function (parent) {
        if (!parent || elementsToStop.has(parent)) {
            return;
        }
        Array.prototype.forEach.call(parent.children, function (node) {
            if (elementsToKeep.has(node)) {
                deep(node);
            }
            else {
                try {
                    var attr = node.getAttribute(controlAttribute);
                    var alreadyHidden = attr !== null && attr !== 'false';
                    var counterValue = (counterMap.get(node) || 0) + 1;
                    var markerValue = (markerCounter.get(node) || 0) + 1;
                    counterMap.set(node, counterValue);
                    markerCounter.set(node, markerValue);
                    hiddenNodes.push(node);
                    if (counterValue === 1 && alreadyHidden) {
                        uncontrolledNodes.set(node, true);
                    }
                    if (markerValue === 1) {
                        node.setAttribute(markerName, 'true');
                    }
                    if (!alreadyHidden) {
                        node.setAttribute(controlAttribute, 'true');
                    }
                }
                catch (e) {
                    console.error('aria-hidden: cannot operate on ', node, e);
                }
            }
        });
    };
    deep(parentNode);
    elementsToKeep.clear();
    lockCount++;
    return function () {
        hiddenNodes.forEach(function (node) {
            var counterValue = counterMap.get(node) - 1;
            var markerValue = markerCounter.get(node) - 1;
            counterMap.set(node, counterValue);
            markerCounter.set(node, markerValue);
            if (!counterValue) {
                if (!uncontrolledNodes.has(node)) {
                    node.removeAttribute(controlAttribute);
                }
                uncontrolledNodes.delete(node);
            }
            if (!markerValue) {
                node.removeAttribute(markerName);
            }
        });
        lockCount--;
        if (!lockCount) {
            // clear
            counterMap = new WeakMap();
            counterMap = new WeakMap();
            uncontrolledNodes = new WeakMap();
            markerMap = {};
        }
    };
};
/**
 * Marks everything except given node(or nodes) as aria-hidden
 * @param {Element | Element[]} originalTarget - elements to keep on the page
 * @param [parentNode] - top element, defaults to document.body
 * @param {String} [markerName] - a special attribute to mark every node
 * @return {Undo} undo command
 */
var hideOthers = function (originalTarget, parentNode, markerName) {
    if (markerName === void 0) { markerName = 'data-aria-hidden'; }
    var targets = Array.from(Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    var activeParentNode = getDefaultParent(originalTarget);
    if (!activeParentNode) {
        return function () { return null; };
    }
    // we should not hide ariaLive elements - https://github.com/theKashey/aria-hidden/issues/10
    targets.push.apply(targets, Array.from(activeParentNode.querySelectorAll('[aria-live]')));
    return applyAttributeToOthers(targets, activeParentNode, markerName, 'aria-hidden');
};

var DIALOG_NAME = "Dialog";
var [createDialogContext, createDialogScope] = createContextScope(DIALOG_NAME);
var [DialogProvider, useDialogContext] = createDialogContext(DIALOG_NAME);
var Dialog = (props) => {
  const {
    __scopeDialog,
    children,
    open: openProp,
    defaultOpen,
    onOpenChange,
    modal = true
  } = props;
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx$1(
    DialogProvider,
    {
      scope: __scopeDialog,
      triggerRef,
      contentRef,
      contentId: useId$1(),
      titleId: useId$1(),
      descriptionId: useId$1(),
      open,
      onOpenChange: setOpen,
      onOpenToggle: React.useCallback(() => setOpen((prevOpen) => !prevOpen), [setOpen]),
      modal,
      children
    }
  );
};
Dialog.displayName = DIALOG_NAME;
var TRIGGER_NAME$5 = "DialogTrigger";
var DialogTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, ...triggerProps } = props;
    const context = useDialogContext(TRIGGER_NAME$5, __scopeDialog);
    const composedTriggerRef = useComposedRefs(forwardedRef, context.triggerRef);
    return /* @__PURE__ */ jsx$1(
      Primitive.button,
      {
        type: "button",
        "aria-haspopup": "dialog",
        "aria-expanded": context.open,
        "aria-controls": context.contentId,
        "data-state": getState(context.open),
        ...triggerProps,
        ref: composedTriggerRef,
        onClick: composeEventHandlers(props.onClick, context.onOpenToggle)
      }
    );
  }
);
DialogTrigger.displayName = TRIGGER_NAME$5;
var PORTAL_NAME$5 = "DialogPortal";
var [PortalProvider$2, usePortalContext$2] = createDialogContext(PORTAL_NAME$5, {
  forceMount: void 0
});
var DialogPortal = (props) => {
  const { __scopeDialog, forceMount, children, container } = props;
  const context = useDialogContext(PORTAL_NAME$5, __scopeDialog);
  return /* @__PURE__ */ jsx$1(PortalProvider$2, { scope: __scopeDialog, forceMount, children: React.Children.map(children, (child) => /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(Portal$4, { asChild: true, container, children: child }) })) });
};
DialogPortal.displayName = PORTAL_NAME$5;
var OVERLAY_NAME$1 = "DialogOverlay";
var DialogOverlay = React.forwardRef(
  (props, forwardedRef) => {
    const portalContext = usePortalContext$2(OVERLAY_NAME$1, props.__scopeDialog);
    const { forceMount = portalContext.forceMount, ...overlayProps } = props;
    const context = useDialogContext(OVERLAY_NAME$1, props.__scopeDialog);
    return context.modal ? /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(DialogOverlayImpl, { ...overlayProps, ref: forwardedRef }) }) : null;
  }
);
DialogOverlay.displayName = OVERLAY_NAME$1;
var DialogOverlayImpl = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, ...overlayProps } = props;
    const context = useDialogContext(OVERLAY_NAME$1, __scopeDialog);
    return (
      // Make sure `Content` is scrollable even when it doesn't live inside `RemoveScroll`
      // ie. when `Overlay` and `Content` are siblings
      /* @__PURE__ */ jsx$1(ReactRemoveScroll, { as: Slot, allowPinchZoom: true, shards: [context.contentRef], children: /* @__PURE__ */ jsx$1(
        Primitive.div,
        {
          "data-state": getState(context.open),
          ...overlayProps,
          ref: forwardedRef,
          style: { pointerEvents: "auto", ...overlayProps.style }
        }
      ) })
    );
  }
);
var CONTENT_NAME$7 = "DialogContent";
var DialogContent = React.forwardRef(
  (props, forwardedRef) => {
    const portalContext = usePortalContext$2(CONTENT_NAME$7, props.__scopeDialog);
    const { forceMount = portalContext.forceMount, ...contentProps } = props;
    const context = useDialogContext(CONTENT_NAME$7, props.__scopeDialog);
    return /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: context.modal ? /* @__PURE__ */ jsx$1(DialogContentModal, { ...contentProps, ref: forwardedRef }) : /* @__PURE__ */ jsx$1(DialogContentNonModal, { ...contentProps, ref: forwardedRef }) });
  }
);
DialogContent.displayName = CONTENT_NAME$7;
var DialogContentModal = React.forwardRef(
  (props, forwardedRef) => {
    const context = useDialogContext(CONTENT_NAME$7, props.__scopeDialog);
    const contentRef = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, context.contentRef, contentRef);
    React.useEffect(() => {
      const content = contentRef.current;
      if (content) return hideOthers(content);
    }, []);
    return /* @__PURE__ */ jsx$1(
      DialogContentImpl,
      {
        ...props,
        ref: composedRefs,
        trapFocus: context.open,
        disableOutsidePointerEvents: true,
        onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
          event.preventDefault();
          context.triggerRef.current?.focus();
        }),
        onPointerDownOutside: composeEventHandlers(props.onPointerDownOutside, (event) => {
          const originalEvent = event.detail.originalEvent;
          const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
          const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
          if (isRightClick) event.preventDefault();
        }),
        onFocusOutside: composeEventHandlers(
          props.onFocusOutside,
          (event) => event.preventDefault()
        )
      }
    );
  }
);
var DialogContentNonModal = React.forwardRef(
  (props, forwardedRef) => {
    const context = useDialogContext(CONTENT_NAME$7, props.__scopeDialog);
    const hasInteractedOutsideRef = React.useRef(false);
    const hasPointerDownOutsideRef = React.useRef(false);
    return /* @__PURE__ */ jsx$1(
      DialogContentImpl,
      {
        ...props,
        ref: forwardedRef,
        trapFocus: false,
        disableOutsidePointerEvents: false,
        onCloseAutoFocus: (event) => {
          props.onCloseAutoFocus?.(event);
          if (!event.defaultPrevented) {
            if (!hasInteractedOutsideRef.current) context.triggerRef.current?.focus();
            event.preventDefault();
          }
          hasInteractedOutsideRef.current = false;
          hasPointerDownOutsideRef.current = false;
        },
        onInteractOutside: (event) => {
          props.onInteractOutside?.(event);
          if (!event.defaultPrevented) {
            hasInteractedOutsideRef.current = true;
            if (event.detail.originalEvent.type === "pointerdown") {
              hasPointerDownOutsideRef.current = true;
            }
          }
          const target = event.target;
          const targetIsTrigger = context.triggerRef.current?.contains(target);
          if (targetIsTrigger) event.preventDefault();
          if (event.detail.originalEvent.type === "focusin" && hasPointerDownOutsideRef.current) {
            event.preventDefault();
          }
        }
      }
    );
  }
);
var DialogContentImpl = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, trapFocus, onOpenAutoFocus, onCloseAutoFocus, ...contentProps } = props;
    const context = useDialogContext(CONTENT_NAME$7, __scopeDialog);
    const contentRef = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, contentRef);
    useFocusGuards();
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx$1(
        FocusScope,
        {
          asChild: true,
          loop: true,
          trapped: trapFocus,
          onMountAutoFocus: onOpenAutoFocus,
          onUnmountAutoFocus: onCloseAutoFocus,
          children: /* @__PURE__ */ jsx$1(
            DismissableLayer,
            {
              role: "dialog",
              id: context.contentId,
              "aria-describedby": context.descriptionId,
              "aria-labelledby": context.titleId,
              "data-state": getState(context.open),
              ...contentProps,
              ref: composedRefs,
              onDismiss: () => context.onOpenChange(false)
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx$1(TitleWarning, { titleId: context.titleId }),
        /* @__PURE__ */ jsx$1(DescriptionWarning$1, { contentRef, descriptionId: context.descriptionId })
      ] })
    ] });
  }
);
var TITLE_NAME$1 = "DialogTitle";
var DialogTitle = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, ...titleProps } = props;
    const context = useDialogContext(TITLE_NAME$1, __scopeDialog);
    return /* @__PURE__ */ jsx$1(Primitive.h2, { id: context.titleId, ...titleProps, ref: forwardedRef });
  }
);
DialogTitle.displayName = TITLE_NAME$1;
var DESCRIPTION_NAME$1 = "DialogDescription";
var DialogDescription = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, ...descriptionProps } = props;
    const context = useDialogContext(DESCRIPTION_NAME$1, __scopeDialog);
    return /* @__PURE__ */ jsx$1(Primitive.p, { id: context.descriptionId, ...descriptionProps, ref: forwardedRef });
  }
);
DialogDescription.displayName = DESCRIPTION_NAME$1;
var CLOSE_NAME = "DialogClose";
var DialogClose = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDialog, ...closeProps } = props;
    const context = useDialogContext(CLOSE_NAME, __scopeDialog);
    return /* @__PURE__ */ jsx$1(
      Primitive.button,
      {
        type: "button",
        ...closeProps,
        ref: forwardedRef,
        onClick: composeEventHandlers(props.onClick, () => context.onOpenChange(false))
      }
    );
  }
);
DialogClose.displayName = CLOSE_NAME;
function getState(open) {
  return open ? "open" : "closed";
}
var TITLE_WARNING_NAME = "DialogTitleWarning";
var [WarningProvider, useWarningContext] = createContext2(TITLE_WARNING_NAME, {
  contentName: CONTENT_NAME$7,
  titleName: TITLE_NAME$1,
  docsSlug: "dialog"
});
var TitleWarning = ({ titleId }) => {
  const titleWarningContext = useWarningContext(TITLE_WARNING_NAME);
  const MESSAGE = `\`${titleWarningContext.contentName}\` requires a \`${titleWarningContext.titleName}\` for the component to be accessible for screen reader users.

If you want to hide the \`${titleWarningContext.titleName}\`, you can wrap it with our VisuallyHidden component.

For more information, see https://radix-ui.com/primitives/docs/components/${titleWarningContext.docsSlug}`;
  React.useEffect(() => {
    if (titleId) {
      const hasTitle = document.getElementById(titleId);
      if (!hasTitle) console.error(MESSAGE);
    }
  }, [MESSAGE, titleId]);
  return null;
};
var DESCRIPTION_WARNING_NAME = "DialogDescriptionWarning";
var DescriptionWarning$1 = ({ contentRef, descriptionId }) => {
  const descriptionWarningContext = useWarningContext(DESCRIPTION_WARNING_NAME);
  const MESSAGE = `Warning: Missing \`Description\` or \`aria-describedby={undefined}\` for {${descriptionWarningContext.contentName}}.`;
  React.useEffect(() => {
    const describedById = contentRef.current?.getAttribute("aria-describedby");
    if (descriptionId && describedById) {
      const hasDescription = document.getElementById(descriptionId);
      if (!hasDescription) console.warn(MESSAGE);
    }
  }, [MESSAGE, contentRef, descriptionId]);
  return null;
};
var Root$3 = Dialog;
var Trigger$4 = DialogTrigger;
var Portal$3 = DialogPortal;
var Overlay = DialogOverlay;
var Content$2 = DialogContent;
var Title = DialogTitle;
var Description = DialogDescription;
var Close = DialogClose;

var ROOT_NAME = "AlertDialog";
var [createAlertDialogContext, createAlertDialogScope] = createContextScope(ROOT_NAME, [
  createDialogScope
]);
var useDialogScope = createDialogScope();
var AlertDialog$2 = (props) => {
  const { __scopeAlertDialog, ...alertDialogProps } = props;
  const dialogScope = useDialogScope(__scopeAlertDialog);
  return /* @__PURE__ */ jsx$1(Root$3, { ...dialogScope, ...alertDialogProps, modal: true });
};
AlertDialog$2.displayName = ROOT_NAME;
var TRIGGER_NAME$4 = "AlertDialogTrigger";
var AlertDialogTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, ...triggerProps } = props;
    const dialogScope = useDialogScope(__scopeAlertDialog);
    return /* @__PURE__ */ jsx$1(Trigger$4, { ...dialogScope, ...triggerProps, ref: forwardedRef });
  }
);
AlertDialogTrigger.displayName = TRIGGER_NAME$4;
var PORTAL_NAME$4 = "AlertDialogPortal";
var AlertDialogPortal = (props) => {
  const { __scopeAlertDialog, ...portalProps } = props;
  const dialogScope = useDialogScope(__scopeAlertDialog);
  return /* @__PURE__ */ jsx$1(Portal$3, { ...dialogScope, ...portalProps });
};
AlertDialogPortal.displayName = PORTAL_NAME$4;
var OVERLAY_NAME = "AlertDialogOverlay";
var AlertDialogOverlay = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, ...overlayProps } = props;
    const dialogScope = useDialogScope(__scopeAlertDialog);
    return /* @__PURE__ */ jsx$1(Overlay, { ...dialogScope, ...overlayProps, ref: forwardedRef });
  }
);
AlertDialogOverlay.displayName = OVERLAY_NAME;
var CONTENT_NAME$6 = "AlertDialogContent";
var [AlertDialogContentProvider, useAlertDialogContentContext] = createAlertDialogContext(CONTENT_NAME$6);
var AlertDialogContent = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, children, ...contentProps } = props;
    const dialogScope = useDialogScope(__scopeAlertDialog);
    const contentRef = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, contentRef);
    const cancelRef = React.useRef(null);
    return /* @__PURE__ */ jsx$1(
      WarningProvider,
      {
        contentName: CONTENT_NAME$6,
        titleName: TITLE_NAME,
        docsSlug: "alert-dialog",
        children: /* @__PURE__ */ jsx$1(AlertDialogContentProvider, { scope: __scopeAlertDialog, cancelRef, children: /* @__PURE__ */ jsxs(
          Content$2,
          {
            role: "alertdialog",
            ...dialogScope,
            ...contentProps,
            ref: composedRefs,
            onOpenAutoFocus: composeEventHandlers(contentProps.onOpenAutoFocus, (event) => {
              event.preventDefault();
              cancelRef.current?.focus({ preventScroll: true });
            }),
            onPointerDownOutside: (event) => event.preventDefault(),
            onInteractOutside: (event) => event.preventDefault(),
            children: [
              /* @__PURE__ */ jsx$1(Slottable, { children }),
              /* @__PURE__ */ jsx$1(DescriptionWarning, { contentRef })
            ]
          }
        ) })
      }
    );
  }
);
AlertDialogContent.displayName = CONTENT_NAME$6;
var TITLE_NAME = "AlertDialogTitle";
var AlertDialogTitle = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, ...titleProps } = props;
    const dialogScope = useDialogScope(__scopeAlertDialog);
    return /* @__PURE__ */ jsx$1(Title, { ...dialogScope, ...titleProps, ref: forwardedRef });
  }
);
AlertDialogTitle.displayName = TITLE_NAME;
var DESCRIPTION_NAME = "AlertDialogDescription";
var AlertDialogDescription = React.forwardRef((props, forwardedRef) => {
  const { __scopeAlertDialog, ...descriptionProps } = props;
  const dialogScope = useDialogScope(__scopeAlertDialog);
  return /* @__PURE__ */ jsx$1(Description, { ...dialogScope, ...descriptionProps, ref: forwardedRef });
});
AlertDialogDescription.displayName = DESCRIPTION_NAME;
var ACTION_NAME = "AlertDialogAction";
var AlertDialogAction = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, ...actionProps } = props;
    const dialogScope = useDialogScope(__scopeAlertDialog);
    return /* @__PURE__ */ jsx$1(Close, { ...dialogScope, ...actionProps, ref: forwardedRef });
  }
);
AlertDialogAction.displayName = ACTION_NAME;
var CANCEL_NAME = "AlertDialogCancel";
var AlertDialogCancel = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAlertDialog, ...cancelProps } = props;
    const { cancelRef } = useAlertDialogContentContext(CANCEL_NAME, __scopeAlertDialog);
    const dialogScope = useDialogScope(__scopeAlertDialog);
    const ref = useComposedRefs(forwardedRef, cancelRef);
    return /* @__PURE__ */ jsx$1(Close, { ...dialogScope, ...cancelProps, ref });
  }
);
AlertDialogCancel.displayName = CANCEL_NAME;
var DescriptionWarning = ({ contentRef }) => {
  const MESSAGE = `\`${CONTENT_NAME$6}\` requires a description for the component to be accessible for screen reader users.

You can add a description to the \`${CONTENT_NAME$6}\` by passing a \`${DESCRIPTION_NAME}\` component as a child, which also benefits sighted users by adding visible context to the dialog.

Alternatively, you can use your own component as a description by assigning it an \`id\` and passing the same value to the \`aria-describedby\` prop in \`${CONTENT_NAME$6}\`. If the description is confusing or duplicative for sighted users, you can use the \`@radix-ui/react-visually-hidden\` primitive as a wrapper around your description component.

For more information, see https://radix-ui.com/primitives/docs/components/alert-dialog`;
  React.useEffect(() => {
    const hasDescription = document.getElementById(
      contentRef.current?.getAttribute("aria-describedby")
    );
    if (!hasDescription) console.warn(MESSAGE);
  }, [MESSAGE, contentRef]);
  return null;
};
var Root2$4 = AlertDialog$2;
var Trigger2 = AlertDialogTrigger;
var Portal2$2 = AlertDialogPortal;
var Overlay2 = AlertDialogOverlay;
var Content2$4 = AlertDialogContent;
var Action = AlertDialogAction;
var Cancel = AlertDialogCancel;
var Title2 = AlertDialogTitle;
var Description2 = AlertDialogDescription;

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
  if (n.__esModule) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			if (this instanceof a) {
        return Reflect.construct(f, arguments, this.constructor);
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

var hasRequiredClassnames;

function requireClassnames () {
	if (hasRequiredClassnames) return classnames.exports;
	hasRequiredClassnames = 1;
	(function (module) {
		/* global define */

		(function () {

			var hasOwn = {}.hasOwnProperty;

			function classNames () {
				var classes = '';

				for (var i = 0; i < arguments.length; i++) {
					var arg = arguments[i];
					if (arg) {
						classes = appendClass(classes, parseValue(arg));
					}
				}

				return classes;
			}

			function parseValue (arg) {
				if (typeof arg === 'string' || typeof arg === 'number') {
					return arg;
				}

				if (typeof arg !== 'object') {
					return '';
				}

				if (Array.isArray(arg)) {
					return classNames.apply(null, arg);
				}

				if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
					return arg.toString();
				}

				var classes = '';

				for (var key in arg) {
					if (hasOwn.call(arg, key) && arg[key]) {
						classes = appendClass(classes, key);
					}
				}

				return classes;
			}

			function appendClass (value, newClass) {
				if (!newClass) {
					return value;
				}
			
				if (value) {
					return value + ' ' + newClass;
				}
			
				return value + newClass;
			}

			if (module.exports) {
				classNames.default = classNames;
				module.exports = classNames;
			} else {
				window.classNames = classNames;
			}
		}()); 
	} (classnames));
	return classnames.exports;
}

var classnamesExports = requireClassnames();
var classNames$1 = /*@__PURE__*/getDefaultExportFromCjs(classnamesExports);

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$_ = ".Button-module_button__AKKkh {\n  border-radius: var(--button-border-radius);\n  background: var(--button-primary-bg-color);\n  border: 1px solid var(--button-primary-border-color);\n  color: var(--button-color-text);\n  height: calc(var(--input-height-mobile) - 2px);\n  min-width: calc(var(--input-height-mobile) - 2px);\n  line-height: calc(var(--input-height-mobile) - 2px);\n  font-size: var(--font-size-m);\n  display: inline-flex;\n  justify-content: center;\n  vertical-align: middle;\n  align-items: center;\n  padding: 0;\n  transition: all 0.3s ease;\n}\n\n.Button-module_button__AKKkh.Button-module_small-height__UTYYv {\n  height: 24px;\n}\n\n@media (width >= 580px) {\n  .Button-module_button__AKKkh {\n    font-size: var(--font-size-s);\n    height: calc(var(--input-height-desktop) - 2px);\n    min-width: calc(var(--input-height-desktop) - 2px);\n    line-height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n.Button-module_full__-TxDz {\n  display: flex;\n  width: 100%;\n  padding: 0 8px;\n}\n\n.Button-module_full__-TxDz:not(:last-child) {\n  margin-bottom: 16px;\n}\n\n.Button-module_reversed__C-UJu {\n  flex-direction: row-reverse;\n}\n\n.Button-module_icon-only__SJwej svg {\n  margin: 0;\n}\n\n.Button-module_includes-icon__bW4yF {\n  justify-content: space-between;\n}\n\n.Button-module_includes-icon__bW4yF svg {\n  margin-left: 8px;\n  margin-right: 0;\n}\n\n.Button-module_includes-icon__bW4yF.Button-module_reversed__C-UJu svg {\n  margin-right: 8px;\n  margin-left: 0;\n}\n\n.Button-module_icon-only__SJwej {\n  width: calc(var(--input-height-mobile) - 2px);\n}\n\n@media (width >= 580px) {\n  .Button-module_icon-only__SJwej {\n    width: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n.Button-module_value__adDsB {\n  padding-left: 12px;\n  padding-right: 12px;\n  display: inline-block;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.Button-module_no-horizontal-padding__hcjhG.Button-module_includes-icon__bW4yF svg {\n  margin: 0 8px 0 0;\n}\n\n.Button-module_no-horizontal-padding__hcjhG.Button-module_icon-only__SJwej {\n  width: fit-content;\n  min-width: fit-content;\n}\n\n.Button-module_no-horizontal-padding__hcjhG .Button-module_value__adDsB {\n  padding-left: 0;\n  padding-right: 0;\n}\n\n.Button-module_includes-icon__bW4yF .Button-module_value__adDsB {\n  padding-left: 8px;\n  padding-right: 12px;\n}\n\n.Button-module_includes-icon__bW4yF.Button-module_reversed__C-UJu .Button-module_value__adDsB {\n  padding-left: 12px;\n  padding-right: 8px;\n}\n\n.Button-module_button__AKKkh:hover {\n  cursor: pointer;\n}\n\n.Button-module_button__AKKkh:disabled {\n  cursor: not-allowed;\n}\n\n/* primary */\n.Button-module_button__AKKkh.Button-module_primary__feVRD:disabled {\n  background-color: var(--button-primary-bg-color-disabled);\n  border-color: var(--button-primary-border-color-disabled);\n}\n\n.Button-module_button__AKKkh.Button-module_primary__feVRD:hover {\n  background-color: var(--button-primary-bg-color-hover);\n  border-color: var(--button-primary-border-color-bover);\n}\n\n.Button-module_button__AKKkh.Button-module_primary__feVRD:active:not(:disabled) {\n  background-color: var(--button-primary-bg-color-active);\n}\n\n/* secondary */\n.Button-module_button__AKKkh.Button-module_secondary__DVXFL {\n  background-color: var(--button-secondary-bg-color);\n  color: var(--button-secondary-text-color);\n  border-color: var(--button-secondary-border-color);\n}\n\n.Button-module_button__AKKkh.Button-module_secondary__DVXFL:disabled {\n  background-color: var(--button-secondary-bg-color-disabled);\n  color: var(--button-secondary-text-color-disabled);\n  border-color: var(--button-secondary-border-color-disabled);\n}\n\n.Button-module_button__AKKkh.Button-module_secondary__DVXFL:hover {\n  background-color: var(--button-secondary-bg-color-hover);\n  color: var(--button-secondary-text-color-hover);\n  border-color: var(--button-secondary-border-color-hover);\n}\n\n.Button-module_button__AKKkh.Button-module_secondary__DVXFL:active:not(:disabled) {\n  background-color: var(--button-secondary-bg-color-active);\n}\n\n/* tertiary */\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV {\n  background-color: var(--button-tertiary-bg-color);\n  border-color: var(--button-tertiary-border-color);\n  color: var(--button-tertiary-text-color);\n}\n\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV:disabled {\n  background-color: var(--button-tertiary-bg-color-disabled);\n  color: var(--button-tertiary-text-color-disabled);\n  border-color: var(--button-tertiary-border-color-disabled);\n}\n\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV:hover {\n  background-color: var(--button-tertiary-bg-color-hover);\n  border-color: var(--button-tertiary-bg-color-hover);\n  color: var(--button-tertiary-text-color-hover);\n}\n\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV:active:not(:disabled) {\n  background-color: var(--button-tertiary-bg-color-active);\n}\n\n/* with border always shown */\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV.Button-module_with-border__28tKG,\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV.Button-module_with-border__28tKG:hover {\n  border-color: var(--button-secondary-border-color);\n}\n\n/* tertiary not on white */\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV.Button-module_not-on-white__R3dit:disabled {\n  background-color: var(--button-tertiary-bg-color-disabled);\n  color: var(--button-tertiary-text-color-disabled);\n  border-color: var(--button-tertiary-border-color-disabled);\n}\n\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV.Button-module_not-on-white__R3dit:hover {\n  background-color: var(--button-secondary-bg-color-active);\n}\n\n.Button-module_button__AKKkh.Button-module_tertiary__JOJNV.Button-module_not-on-white__R3dit:active:not(:disabled) {\n  background-color: var(--button-tertiary-bg-color-active);\n}\n\n/* ghost */\n.Button-module_button__AKKkh.Button-module_ghost__-dX71 {\n  color: var(--button-tertiary-text-color);\n  border: none;\n  background: none;\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71:disabled {\n  color: var(--button-tertiary-text-color-disabled);\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71:hover {\n  color: var(--button-tertiary-text-color-hover);\n  background: none;\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71:active:not(:disabled) {\n  background-color: transparent;\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71.Button-module_no-horizontal-padding__hcjhG:not(.Button-module_icon-only__SJwej) svg {\n  margin-left: 0;\n  margin-right: 6px;\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71.Button-module_no-horizontal-padding__hcjhG:not(.Button-module_icon-only__SJwej).Button-module_reversed__C-UJu svg {\n  margin-left: 6px;\n  margin-right: 0;\n}\n\n.Button-module_button__AKKkh.Button-module_ghost__-dX71.Button-module_no-horizontal-padding__hcjhG .Button-module_value__adDsB {\n  padding-right: 0;\n  padding-left: 0;\n}\n\n.Button-module_loading-icon__2-qa- {\n  width: 1.5rem;\n  height: 1.5rem;\n  stroke-width: 4;\n  animation: Button-module_spinning__hzuq9 2.5s linear infinite;\n}\n\n@keyframes Button-module_spinning__hzuq9 {\n  from {\n    transform: rotate(0deg);\n  }\n\n  to {\n    transform: rotate(360deg);\n  }\n}\n";
var styles$Y = {"button":"Button-module_button__AKKkh","small-height":"Button-module_small-height__UTYYv","full":"Button-module_full__-TxDz","reversed":"Button-module_reversed__C-UJu","icon-only":"Button-module_icon-only__SJwej","includes-icon":"Button-module_includes-icon__bW4yF","value":"Button-module_value__adDsB","no-horizontal-padding":"Button-module_no-horizontal-padding__hcjhG","primary":"Button-module_primary__feVRD","secondary":"Button-module_secondary__DVXFL","tertiary":"Button-module_tertiary__JOJNV","with-border":"Button-module_with-border__28tKG","not-on-white":"Button-module_not-on-white__R3dit","ghost":"Button-module_ghost__-dX71","loading-icon":"Button-module_loading-icon__2-qa-"};
styleInject(css_248z$_);

var SvgAccessIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32.86 39.39h9.27v4.15' }), jsx$1("circle", { cx: 27.3, cy: 39.39, r: 5.43, fill: 'none' }), jsx$1("rect", { width: 38, height: 28.64, x: 13, y: 25.07, fill: 'none', rx: 6, ry: 6 }), jsx$1("path", { fill: 'none', d: 'M41.27 25.07v-9.36c0-3-2.43-5.43-5.43-5.43h-7.68c-3 0-5.43 2.43-5.43 5.43v9.36' })] }))); };

var SvgAddIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M16 32h32M32 16v32' }) }))); };

var SvgAllMeetingsIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 31.76, height: 32, x: 11, y: 16, fill: 'none', rx: 6.17, ry: 6.17 }), jsx$1("path", { fill: 'none', d: 'm42.76 25.46 6.81-6.29c1.31-1.21 3.43-.27 3.43 1.52V43.3c0 1.8-2.12 2.74-3.43 1.52l-6.81-6.29M22.21 27.35v9.3c0 1.7 1.93 2.67 3.28 1.65l6.15-4.65c1.09-.83 1.09-2.48 0-3.3l-6.15-4.65c-1.35-1.02-3.28-.05-3.28 1.65' })] }))); };

var SvgArrowDownIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M46 26 32 40 18 26.03' }) }))); };

var SvgArrowHeadDownIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M32 15v34m14-14L32 49 18 35.03' }) }))); };

var SvgArrowHeadLeftIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M50 32H16m14 14L16 32l13.97-14' }) }))); };

var SvgArrowHeadRightIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M14 32h34M34 18l14 14-13.97 14' }) }))); };

var SvgArrowHeadUpIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M32 48V14M18 28l14-14 14 13.97' }) }))); };

var SvgArrowLeftIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M38 46 24 32l13.97-14' }) }))); };

var SvgArrowRightIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'm26 18 14 14-13.97 14' }) }))); };

var SvgArrowUpIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'm18 38 14-14 14 13.97' }) }))); };

var SvgBasedataIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm33.81 12.46 15.96 8.81c1.64.9 1.64 3.19 0 4.1l-15.96 8.81c-1.12.62-2.5.62-3.63 0l-15.96-8.81a2.323 2.323 0 0 1 0-4.1l15.96-8.81c1.12-.62 2.5-.62 3.63 0' }), jsx$1("path", { fill: 'none', d: 'm49.77 34.05-15.96 8.81c-1.12.62-2.5.62-3.63 0l-15.96-8.81' }), jsx$1("path", { fill: 'none', d: 'm49.77 25.37-15.96 8.81c-1.12.62-2.5.62-3.63 0l-15.96-8.81m35.55 17.36-15.96 8.81c-1.12.62-2.5.62-3.63 0l-15.96-8.81' })] }))); };

var SvgBirthdayIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M20 24.73h24c3.31 0 6 2.69 6 6V51H14V30.73c0-3.31 2.69-6 6-6m12 0v-3.79m3.08-3c0 1.65-1.38 2.99-3.08 2.99s-3.08-1.34-3.08-2.99S32 11 32 11s3.08 5.29 3.08 6.94' }), jsx$1("path", { fill: 'none', d: 'M50 33.41H30.63c-1.7 0-3.08 1.34-3.08 2.99v5.58c0 2-1.54 3.79-3.58 3.89-2.18.11-3.99-1.58-3.99-3.68V36.4c0-1.65-1.38-2.99-3.08-2.99h-2.91' })] }))); };

var SvgBulkEditIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M45.92 29.92V40.4c0 3.19-2.57 5.78-5.73 5.78H24.5c-3.16 0-5.73-2.59-5.73-5.78V24.57c0-3.19 2.57-5.78 5.73-5.78h11.09' }), jsx$1("path", { fill: 'none', d: 'M18.77 24.61h-1.7c-2.25 0-4.07 1.84-4.07 4.11v17.5c0 3.19 2.57 5.78 5.73 5.78h17.35c2.25 0 4.07-1.84 4.07-4.11v-1.71M52.37 15.7 41.93 26.23l-5.36 2.34 2.32-5.41 10.44-10.53c.84-.85 2.2-.85 3.04 0h0c.84.85.84 2.22 0 3.07m-4.52-1.56 3.04 3.07' })] }))); };

var SvgCalendarIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 39.89, height: 35.17, x: 12.06, y: 14.42, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M17.21 28.03h6.29v6.29h-6.29zm11.65 0h6.29v6.29h-6.29zm11.64 0h6.29v6.29H40.5zm-23.29 10.7h6.29v6.29h-6.29zm11.65 0h6.29v6.29h-6.29zm11.64 0h6.29v6.29H40.5zm-28.44-15.6h39.88' })] }))); };

var SvgCertificatesIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M34.03 48.84H15.95c-1.63 0-2.95-1.32-2.95-2.94v-28a2.94 2.94 0 0 1 2.95-2.94h32.1c1.63 0 2.95 1.32 2.95 2.94v28a2.94 2.94 0 0 1-2.95 2.94h-5.92M19.84 21.79h18.24m-18.24 6.04h14.19' }), jsx$1("path", { fill: 'none', d: 'M42.13 40.8h-8.11v14.16l4.05-2.57 4.05 2.57V40.8z' })] }))); };

var SvgChangeViewIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M15.1 50c-1.71 0-3.1-1.4-3.1-3.13V17.12c0-1.73 1.39-3.13 3.1-3.13h33.79c1.71 0 3.1 1.4 3.1 3.13v29.75c0 1.73-1.39 3.13-3.1 3.13zm4.5-6.72h18' }) }))); };

var SvgCheckIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M14 33.43 25.71 45 50 21' }) }))); };

var SvgClassbookIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M14.44 48.06V17.54c0-3.31 2.69-6 6-6h26.11c1.66 0 3 1.34 3 3v29.13' }), jsx$1("path", { fill: 'none', d: 'M49.56 52.45H18.84a4.39 4.39 0 0 1-4.39-4.39h0a4.39 4.39 0 0 1 4.39-4.39h30.72' }), jsx$1("path", { fill: 'none', d: 'M47.93 43.67c-.4.73-1.05 2.18-1.11 4.11-.07 2.24.71 3.92 1.11 4.67' }), jsx$1("circle", { cx: 32, cy: 22.24, r: 5.35, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M28.72 30.63c-2.07-1.17-6.36 1.17-6.36 4.17s2.25 3.11 2.25 3.11H39.4s2.25-.11 2.25-3.11-4.29-5.34-6.36-4.17c0 0-1.78 1.22-3.28 1.22s-3.28-1.22-3.28-1.22z' })] }))); };

var SvgClockIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M32 17.85v14.11l6.38 6.37' })] }))); };

var SvgCloseIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'm20 20 24 24m0-24L20 44' }) }))); };

var SvgControlIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M39.01 13.22h6.73c2.9 0 5.26 2.31 5.26 5.15v30.48C51 51.7 48.65 54 45.74 54H18.26c-2.9 0-5.26-2.31-5.26-5.15V18.37c0-2.85 2.35-5.15 5.26-5.15h6.7' }), jsx$1("path", { fill: 'none', d: 'm22.5 35.83 6.18 5.98 12.82-12.4m-2.49-14.47v1.65H24.99v-1.65c0-1.94 1.57-3.51 3.51-3.51h0c0-1.9 1.57-3.43 3.51-3.43s3.51 1.54 3.51 3.43h0c1.94 0 3.51 1.57 3.51 3.51z' })] }))); };

var SvgCoursesIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M15.15 44.1c-1.1 0-2-.9-2-2V21.42c0-3.31 2.69-6 6-6h25.69c3.31 0 6 2.69 6 6V42.1c0 1.1-.9 2-2 2zm3.18-20.9h18.09m-18.09 6.01H32.4M18.33 42.4h5.39M10 44.1h44m-32.02 0-2.62 8.47m22.66-8.47 2.62 8.47' }) }))); };

var SvgDashboardIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M22.04 52.04v-10c0-1.66 1.34-3 3-3H29c1.66 0 3 1.34 3 3v10h14c3.31 0 6-2.69 6-6V26.16c0-1.01-.51-1.95-1.35-2.51l-17-11.19c-1-.66-2.3-.66-3.3 0l-17 11.19c-.84.55-1.35 1.5-1.35 2.51v19.88c0 3.31 2.69 6 6 6z' }) }))); };

var SvgDeleteIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M46.43 17.81v29.24a5.75 5.75 0 0 1-5.75 5.75H23.31a5.75 5.75 0 0 1-5.75-5.75V17.81m10.29 8.93v15.2m8.3-15.2v15.2m13.71-24.13H14.14' }), jsx$1("path", { fill: 'none', d: 'M38.87 17.81v-3.73A2.87 2.87 0 0 0 36 11.21h-7.98a2.87 2.87 0 0 0-2.87 2.87v3.73' })] }))); };

var SvgDeputateIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 38.89, height: 38.89, x: 12.56, y: 12.56, fill: 'none', rx: 6, ry: 6 }), jsx$1("path", { fill: 'none', d: 'M30.31 23.67h14.52m-14.52 4.14h10.37' }), jsx$1("circle", { cx: 22.02, cy: 25.74, r: 2.85, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M30.31 36.19h14.52m-14.52 4.14h10.37' }), jsx$1("circle", { cx: 22.02, cy: 38.26, r: 2.85, fill: 'none' })] }))); };

var SvgDotsHorizontalIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fill: 'currentcolor', className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 32, r: 3 }), jsx$1("circle", { cx: 19.5, cy: 32, r: 3 }), jsx$1("circle", { cx: 44.5, cy: 32, r: 3 })] }))); };

var SvgDotsVerticalIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fill: 'currentcolor', className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 32, r: 3 }), jsx$1("circle", { cx: 32, cy: 19.5, r: 3 }), jsx$1("circle", { cx: 32, cy: 44.5, r: 3 })] }))); };

var SvgDoubleExclamationmarkIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 25.44, cy: 44.19, r: 1.5, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M25.44 18.31v18.88' }), jsx$1("circle", { cx: 38.56, cy: 44.19, r: 1.5, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M38.56 18.31v18.88' })] }))); };

var SvgDoublearrowLeftIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm33.22 46-14-14 13.97-14' }), jsx$1("path", { fill: 'none', d: 'm44.78 46-14-14 13.97-14' })] }))); };

var SvgDoublearrowRightIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm30.78 18 14 14-13.97 14' }), jsx$1("path", { fill: 'none', d: 'm19.22 18 14 14-13.97 14' })] }))); };

var SvgDuplicateIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M28.06 14.37c-3.16 0-5.73 2.59-5.73 5.78v15.83c0 3.19 2.57 5.78 5.73 5.78h15.69c3.16 0 5.73-2.59 5.73-5.78V20.37c0-3.31-2.69-6-6-6z' }), jsx$1("path", { fill: 'none', d: 'M41.67 41.76v3.76c0 2.27-1.82 4.11-4.07 4.11H20.25c-3.16 0-5.73-2.59-5.73-5.78v-17.5c0-2.27 1.82-4.11 4.07-4.11h3.75' })] }))); };

var SvgEditIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M47.26 22.77 26.25 43.96l-10.79 4.71 4.67-10.88L41.14 16.6a4.31 4.31 0 0 1 6.12 0h0a4.39 4.39 0 0 1 0 6.17m-8.9-3.34 6.12 6.17' }) }))); };

var SvgEpochenIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 41.36, height: 16.33, x: 13.56, y: 23.83, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M9.08 40.17V23.83' })] }))); };

var SvgExclamationmarkIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 44.19, r: 1.5, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M32 18.31v18.88' })] }))); };

var SvgFachstundenIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 15, height: 15, x: 12.76, y: 24.5, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M8.85 39.5v-15' }), jsx$1("rect", { width: 15, height: 15, x: 40.15, y: 24.5, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M36.24 39.5v-15' })] }))); };

var SvgFactorIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M24.34 16.21h-4.5c-2.44 0-4.41 1.97-4.41 4.41v19.92c0 2.44-1.97 4.41-4.41 4.41H9m12.96-16.74H9m26.69 1.03 10 10m0-10-10 10m-4.83 8.55c-2.82-3.92-4.48-8.72-4.48-13.92s1.66-10 4.48-13.91m19.66 27.83C53.34 43.87 55 39.07 55 33.87s-1.66-10-4.48-13.91' }) }))); };

var SvgFileIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M49.08 26.96v20.15c0 3.31-2.69 6-6 6H20.91c-3.31 0-6-2.69-6-6V16.89c0-3.31 2.69-6 6-6h12.1z' }), jsx$1("path", { fill: 'none', d: 'M49.08 26.96H36.85c-2.12 0-3.84-1.72-3.84-3.84V10.89' })] }))); };

var SvgFilterIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M47.58 18.34H16.42M38.3 45.66H25.7m16.59-13.5H21.71' }) }))); };

var SvgForumIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 31.19, cy: 33.32, r: 6.44, fill: 'none' }), jsx$1("circle", { cx: 15.71, cy: 23.3, r: 3.22, fill: 'none' }), jsx$1("circle", { cx: 46.2, cy: 14.8, r: 3.22, fill: 'none' }), jsx$1("circle", { cx: 48.29, cy: 30.68, r: 3.22, fill: 'none' }), jsx$1("circle", { cx: 41.1, cy: 49.2, r: 3.22, fill: 'none' }), jsx$1("circle", { cx: 15.71, cy: 40.25, r: 3.22, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'm18.44 24.99 7.13 5.22M44.32 17.4 35.2 28.29m9.92 2.91-7.56 1.22m1.93 13.99-5.1-7.5m-15.87-.22 6.69-2.98' })] }))); };

var SvgFullscreenActiveIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M15.28 25.61h10.33V15.28m-12.05-1.72 12.05 12.05m23.11 0H38.39V15.28m12.05-1.72L38.39 25.61M15.28 38.39h10.33v10.33m-12.05 1.72 12.05-12.05m23.11 0H38.39v10.33m12.05 1.72L38.39 38.39' }) }))); };

var SvgFullscreenIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M24.89 14.56H14.56v10.33m12.05 1.72L14.56 14.56m24.55 0h10.33v10.33m-12.05 1.72 12.05-12.05M24.89 49.44H14.56V39.11m12.05-1.72L14.56 49.44m24.55 0h10.33V39.11m-12.05-1.72 12.05 12.05' }) }))); };

var SvgGenerateIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M26.11 32.94a21.77 21.77 0 0 0-10.02-10.02c4.36-2.12 7.89-5.66 10.02-10.02 2.12 4.36 5.66 7.89 10.02 10.02a21.77 21.77 0 0 0-10.02 10.02M37.89 44.3a13.57 13.57 0 0 0-6.26-6.26c2.73-1.33 4.93-3.53 6.26-6.26 1.33 2.73 3.53 4.93 6.26 6.26a13.57 13.57 0 0 0-6.26 6.26' }), jsx$1("path", { fill: 'currentcolor', d: 'M30.16 53.09a6.5 6.5 0 0 0-3-3 6.5 6.5 0 0 0 3-3 6.5 6.5 0 0 0 3 3 6.5 6.5 0 0 0-3 3M44.9 29.84a6.5 6.5 0 0 0-3-3 6.5 6.5 0 0 0 3-3 6.5 6.5 0 0 0 3 3 6.5 6.5 0 0 0-3 3' }), jsx$1("path", { fill: 'none', d: 'M20.44 46.15a8.16 8.16 0 0 0-3.76-3.76c1.64-.8 2.96-2.12 3.76-3.76.8 1.64 2.12 2.96 3.76 3.76-1.64.8-2.96 2.12-3.76 3.76' })] }))); };

var SvgGradeGroupsIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M8.89 41.34c-.94 0-1.79-.18-2.56-.54-.76-.36-1.37-.87-1.82-1.55s-.68-1.5-.68-2.46c0-.85.17-1.54.5-2.07s.78-.95 1.34-1.25 1.18-.53 1.86-.68 1.36-.27 2.05-.36c.9-.12 1.63-.2 2.2-.27.56-.06.97-.17 1.23-.31.26-.15.39-.4.39-.77V31q0-1.425-.78-2.22c-.52-.53-1.3-.79-2.35-.79s-1.94.24-2.56.71c-.62.48-1.05.98-1.3 1.52l-2.16-.77c.39-.9.9-1.6 1.55-2.11a5.7 5.7 0 0 1 2.12-1.06c.77-.2 1.53-.3 2.27-.3.48 0 1.02.06 1.64.17q.93.165 1.8.69c.58.35 1.07.87 1.45 1.57.39.7.58 1.64.58 2.82v9.76h-2.28v-2.01h-.11c-.15.32-.41.67-.77 1.03-.36.37-.84.68-1.44.94q-.9.39-2.19.39zm.35-2.04c.9 0 1.66-.18 2.28-.53s1.09-.81 1.41-1.37.48-1.15.48-1.77v-2.08c-.1.12-.31.22-.63.31s-.7.17-1.12.24-.83.13-1.22.17l-.96.12c-.59.08-1.14.2-1.65.37s-.92.42-1.23.76-.47.8-.47 1.37c0 .79.29 1.39.88 1.79s1.34.6 2.24.6zm16.71 1.73V21.27h2.28v7.29h.19c.17-.26.4-.59.7-.99q.45-.6 1.29-1.08c.84-.48 1.33-.48 2.29-.48 1.25 0 2.35.31 3.3.94.95.62 1.69 1.51 2.23 2.65.53 1.14.8 2.49.8 4.05s-.27 2.93-.8 4.08-1.27 2.04-2.22 2.66c-.95.63-2.04.94-3.27.94-.95 0-1.71-.16-2.29-.48s-1.01-.68-1.32-1.09-.55-.75-.71-1.02h-.27v2.28h-2.2zm2.24-7.41c0 1.12.16 2.1.49 2.96.33.85.81 1.52 1.44 2s1.4.72 2.32.72 1.75-.25 2.39-.76 1.12-1.19 1.45-2.05.49-1.82.49-2.87-.16-1.98-.48-2.82-.8-1.51-1.44-2-1.44-.74-2.41-.74-1.7.23-2.33.7-1.11 1.12-1.43 1.95q-.48 1.245-.48 2.91zm26.15 7.72c-1.39 0-2.59-.33-3.59-.98-1-.66-1.77-1.56-2.31-2.71s-.81-2.47-.81-3.95.28-2.83.83-3.99c.56-1.15 1.33-2.06 2.33-2.71s2.17-.98 3.51-.98c1.04 0 1.98.19 2.82.58a5.4 5.4 0 0 1 2.05 1.62c.53.69.87 1.51.99 2.43h-2.28c-.17-.68-.56-1.27-1.15-1.8-.6-.52-1.39-.79-2.4-.79-.89 0-1.66.23-2.33.69s-1.18 1.11-1.55 1.94-.55 1.81-.55 2.93.18 2.14.54 2.99.88 1.51 1.54 1.98c.67.47 1.45.7 2.35.7.59 0 1.13-.1 1.61-.31s.89-.5 1.23-.89c.33-.39.57-.85.71-1.39h2.28a5.4 5.4 0 0 1-.95 2.36c-.51.7-1.17 1.25-2 1.66s-1.79.61-2.88.61z' }), jsx$1("path", { fill: 'none', d: 'M27.55 15.96 14.09 48.04M50.9 15.96 37.44 48.04' })] }))); };

var SvgGridIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fill: 'currentcolor', className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 14.18, cy: 20.12, r: 1.98 }), jsx$1("circle", { cx: 26.06, cy: 20.12, r: 1.98 }), jsx$1("circle", { cx: 37.94, cy: 20.12, r: 1.98 }), jsx$1("circle", { cx: 49.82, cy: 20.12, r: 1.98 }), jsx$1("circle", { cx: 14.18, cy: 32, r: 1.98 }), jsx$1("circle", { cx: 26.06, cy: 32, r: 1.98 }), jsx$1("circle", { cx: 37.94, cy: 32, r: 1.98 }), jsx$1("circle", { cx: 49.82, cy: 32, r: 1.98 }), jsx$1("circle", { cx: 14.18, cy: 43.88, r: 1.98 }), jsx$1("circle", { cx: 26.06, cy: 43.88, r: 1.98 }), jsx$1("circle", { cx: 37.94, cy: 43.88, r: 1.98 }), jsx$1("circle", { cx: 49.82, cy: 43.88, r: 1.98 })] }))); };

var SvgGroupsIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 24.01, cy: 27.5, r: 5.5, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M10.78 46.83c1.12-5.99 6.38-10.53 12.7-10.53s11.58 4.54 12.7 10.53' }), jsx$1("circle", { cx: 41.05, cy: 22.67, r: 5.5, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M33.75 33.38c1.97-1.21 4.29-1.91 6.77-1.91 6.32 0 11.58 4.54 12.7 10.53m-21.14-7.39c.52-.45 1.08-.86 1.67-1.23' })] }))); };

var SvgHandIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M30.04 30.44 17.39 17.79c-1.45-1.44-3.83-1.42-5.25.02-1.4 1.43-1.4 3.77.02 5.2l19.01 19.01-10.74-.09a3.743 3.743 0 0 0-3.77 3.78h0a3.737 3.737 0 0 0 3.74 3.69h16.39c2.5 0 4.96-.58 7.19-1.71h0c3.38-1.7 6.17-4.46 7.65-7.94 2.56-6 1.13-12.76-3.3-17.19l-4.67-4.67-2.37-2.37c-1.25-1.19-3.15-1.26-4.36-.28-1.38 1.12-1.57 3.39-.22 4.85' }), jsx$1("path", { fill: 'none', d: 'm34.56 26.39-6.44-6.44c-1.23-1.49-3.36-1.62-4.55-.6-1.29 1.12-1.36 3.51.26 4.89' }), jsx$1("path", { fill: 'none', d: 'm38.78 22.17-4.87-4.87c-1.15-1.22-3.09-1.27-4.26-.14-1.21 1.16-1.21 3.17.05 4.35' })] }))); };

var SvgHomeIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 31.12, r: 10, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M32 26.69v4.41l3.04.02' }), jsx$1("path", { fill: 'none', d: 'M46 53.37c3.31 0 6-2.69 6-6V27.49c0-1.01-.51-1.95-1.35-2.51l-17-13.86c-1-.66-2.3-.66-3.3 0l-17 13.86c-.84.55-1.35 1.5-1.35 2.51v19.88c0 3.31 2.69 6 6 6z' })] }))); };

var SvgImportIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M14.36 27.06v16.65c0 2.92 2.37 5.29 5.29 5.29h24.69c2.92 0 5.29-2.37 5.29-5.29V27.06M32 15v21.46' }), jsx$1("path", { fill: 'none', d: 'm39.2 29.26-7.2 7.2-7.2-7.19' })] }))); };

var SvgImportantIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 41.92, r: 1.22, fill: '#0e0e2c' }), jsx$1("path", { fill: 'none', d: 'M32 20.86v15.18' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgImproveIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M45.6 31.93v10.83c0 3.31-2.69 6-6 6H17.88c-3.31 0-6-2.69-6-6V22.37c0-3.31 2.69-6 6-6h13.83m9.03-1.13h11.38v10.5' }), jsx$1("path", { fill: 'none', d: 'M52.12 15.24 37.53 29.83l-5.69-5.7-8.85 8.86' })] }))); };

var SvgInfoIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 21.23, r: 2, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M32 44.22V28.86' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgInvisibleIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M40.4 31.1a8.11 8.11 0 0 1-8.06 9.01c-4.48 0-8.11-3.63-8.11-8.11a8.109 8.109 0 0 1 13.85-5.73m8.36-1.65c4.48 3.22 6.86 6.97 7.11 7.38' }), jsx$1("path", { fill: 'none', d: 'M10.44 32c.35-.57 7.82-12.3 21.89-12.11 3.79.05 7.08.95 9.87 2.25M22.26 42.07C14.74 38.76 10.77 32.53 10.44 32m43.12 0c-.35.57-7.82 12.3-21.89 12.11-1.49-.02-2.91-.17-4.25-.43m22.24-29L15.01 49.32' })] }))); };

var SvgLessonIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M15.1 50c-1.71 0-3.1-1.4-3.1-3.13V17.12c0-1.73 1.39-3.13 3.1-3.13h33.79c1.71 0 3.1 1.4 3.1 3.13v29.75c0 1.73-1.39 3.13-3.1 3.13zm4.5-6.72h18m-18-5.22H32' }) }))); };

var SvgLinkIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M27.96 36.04c3.18 3.18 8.35 3.18 11.53 0l9.23-9.23c3.18-3.18 3.18-8.35 0-11.53s-8.35-3.18-11.53 0L32 20.47' }), jsx$1("path", { fill: 'none', d: 'M36.04 27.96c-3.18-3.18-8.35-3.18-11.53 0l-9.23 9.23c-3.18 3.18-3.18 8.35 0 11.53s8.35 3.18 11.53 0l5.77-5.77' })] }))); };

var SvgListIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 14.35, cy: 17.68, r: 1.69, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M51.33 17.68H21.11' }), jsx$1("circle", { cx: 14.35, cy: 32, r: 1.69, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M51.33 32H21.11' }), jsx$1("circle", { cx: 14.35, cy: 46.32, r: 1.69, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M51.33 46.32H21.11' })] }))); };

var SvgLockIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32 40.47v4.77' }), jsx$1("circle", { cx: 32, cy: 37.01, r: 3.47, fill: 'none' }), jsx$1("rect", { width: 34.73, height: 28.64, x: 14.64, y: 25.07, fill: 'none', rx: 6, ry: 6 }), jsx$1("path", { fill: 'none', d: 'M39.32 25.07V17.6c0-4.04-3.28-7.32-7.32-7.32h0c-4.04 0-7.32 3.28-7.32 7.32v7.47' })] }))); };

var SvgLogOutIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M36.91 14.48H20.37a5.26 5.26 0 0 0-5.26 5.26v24.53c0 2.9 2.35 5.26 5.26 5.26h16.54M27.57 32h21.32m-7.16-7.15L48.89 32l-7.14 7.15' }) }))); };

var SvgMailIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 41.89, height: 28.22, x: 11.06, y: 17.89, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'm11.06 24.44 19.23 13.41c1.03.72 2.4.72 3.43 0l19.23-13.41' })] }))); };

var SvgMandatoryIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 27.17, cy: 41.92, r: 1.22, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M27.17 20.86v15.18' }), jsx$1("circle", { cx: 36.83, cy: 41.92, r: 1.22, fill: 'currentcolor' }), jsx$1("path", { fill: 'none', d: 'M36.83 20.86v15.18' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgMaterialArchiveIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M16.83 23.63v22.59c0 1.66 1.34 3 3 3h24.33c1.66 0 3-1.34 3-3V23.66m-9.43 6.86H26.27' }), jsx$1("rect", { width: 35.28, height: 8.89, x: 14.36, y: 14.78, fill: 'none', rx: 3, ry: 3 })] }))); };

var SvgMediathekIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 40, height: 30, x: 12, y: 17, fill: 'none', rx: 6, ry: 6 }), jsx$1("path", { fill: 'none', d: 'M26.88 27.35v9.3c0 1.7 1.93 2.67 3.28 1.65l6.15-4.65c1.09-.83 1.09-2.48 0-3.3l-6.15-4.65c-1.35-1.02-3.28-.05-3.28 1.65' })] }))); };

var SvgMeetingActiveIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 31.76, height: 32, x: 11, y: 16, fill: 'none', rx: 6.17, ry: 6.17 }), jsx$1("path", { fill: 'none', d: 'm42.76 25.46 6.81-6.29c1.31-1.21 3.43-.27 3.43 1.52V43.3c0 1.8-2.12 2.74-3.43 1.52l-6.81-6.29' }), jsx$1("circle", { cx: 26.88, cy: 32, r: 6, fill: '#e4574e', stroke: 'none' })] }))); };

var SvgMeetingDefaultIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 31.76, height: 32, x: 11, y: 16, fill: 'none', rx: 6.17, ry: 6.17 }), jsx$1("path", { fill: 'none', d: 'm42.76 25.46 6.81-6.29c1.31-1.21 3.43-.27 3.43 1.52V43.3c0 1.8-2.12 2.74-3.43 1.52l-6.81-6.29' })] }))); };

var SvgMenuIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M48.8 33.85H17.64M35 20.02H17.64M41.1 47.35H17.64' }) }))); };

var SvgMessagesActiveIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M34 24.96H19m26 8.32H19m1.33 15.8v-5.74h-3.17c-3.31 0-6-2.69-6-6V19.92c0-3.31 2.69-6 6-6h29.67c3.31 0 6 2.69 6 6v17.42c0 3.31-2.69 6-6 6H31.5l-9.6 6.56a.998.998 0 0 1-1.56-.83z' }), jsx$1("circle", { cx: 51.36, cy: 15.98, r: 6, fill: '#e4574e', stroke: 'none' })] }))); };

var SvgMessagesDefaultIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M34 24.96H19m26 8.32H19m1.33 15.8v-5.74h-3.17c-3.31 0-6-2.69-6-6V19.92c0-3.31 2.69-6 6-6h29.67c3.31 0 6 2.69 6 6v17.42c0 3.31-2.69 6-6 6H31.5l-9.6 6.56a.998.998 0 0 1-1.56-.83z' }) }))); };

var SvgMinusIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M16 32h32' }) }))); };

var SvgMobilePhoneIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 42.44, height: 28.67, x: 10.78, y: 17.67, fill: 'none', rx: 6, ry: 6, transform: 'rotate(90 32 32)' }), jsx$1("circle", { cx: 32, cy: 46.89, r: 2, fill: 'none' })] }))); };

var SvgNextcloudIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 32, r: 11.61, fill: 'none' }), jsx$1("circle", { cx: 49.86, cy: 32, r: 5.36, fill: 'none' }), jsx$1("circle", { cx: 14.14, cy: 32, r: 5.36, fill: 'none' })] }))); };

var SvgNoDataIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M39.78 47H15.03l6.1-13.57a2.83 2.83 0 0 1 2.66-1.85h20.69c1.97 0 3.34 1.96 2.66 3.81l-4.7 9.76A2.83 2.83 0 0 1 39.78 47' }), jsx$1("path", { fill: 'none', d: 'M42.77 31.58v-2.23c0-1.56-1.27-2.83-2.83-2.83H26.31c-.76 0-1.37-.61-1.37-1.37h0c0-.76-.61-1.37-1.37-1.37h-8.33c-1.56 0-2.83 1.27-2.83 2.83v17.77c0 1.45 1.17 2.62 2.62 2.62h0m32.59-19.9h3.97m-5.58-4.52 3.22-3.22m-7.13 1.61V17' })] }))); };

var SvgNoTasksIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M25.44 13.22c-.12.08-1.21.85-1.26 2.26-.06 1.54 1.18 2.42 1.26 2.47.18.15.98.86 1.11 2.05.16 1.56-1 2.59-1.11 2.68m6.77-9.46c-.12.08-1.21.85-1.26 2.26-.06 1.54 1.18 2.42 1.26 2.47.18.15.98.86 1.11 2.05.16 1.56-1 2.59-1.11 2.68m6.77-9.46c-.12.08-1.21.85-1.26 2.26-.06 1.54 1.18 2.42 1.26 2.47.18.15.98.86 1.11 2.05.16 1.56-1 2.59-1.11 2.68m8.63 9.25s4.11-3.13 5.83-1.29-.08 7.92-6.77 11.21m-33.7 8.93H51.3' }), jsx$1("path", { fill: 'none', d: 'M16.67 26.97h30.94v9.8c0 7.73-6.27 14-14 14h-2.94c-7.73 0-14-6.27-14-14z' })] }))); };

var SvgNotificationActiveIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M44.27 32.08c0 3.57.79 7.1 2.32 10.33l2.6 5.5c.44.94-.24 2.02-1.28 2.02H16.1c-1.04 0-1.72-1.08-1.28-2.02l2.6-5.5c1.53-3.23 2.32-6.76 2.32-10.33h0v-8.57c0-5.21 4.22-9.44 9.44-9.44h5.66c5.21 0 9.44 4.22 9.44 9.44v8.57zm-8.44 17.85a3.83 3.83 0 0 1-7.66 0' }), jsx$1("path", { fill: 'none', d: 'M29.36 14.07c0-1.46 1.18-2.64 2.64-2.64s2.64 1.18 2.64 2.64' }), jsx$1("circle", { cx: 42.94, cy: 18.75, r: 6, fill: '#e4574e', stroke: 'none' })] }))); };

var SvgNotificationDefaultIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M44.27 32.08c0 3.57.79 7.1 2.32 10.33l2.6 5.5c.44.94-.24 2.02-1.28 2.02H16.1c-1.04 0-1.72-1.08-1.28-2.02l2.6-5.5c1.53-3.23 2.32-6.76 2.32-10.33h0v-8.57c0-5.21 4.22-9.44 9.44-9.44h5.66c5.21 0 9.44 4.22 9.44 9.44v8.57zm-8.44 17.85a3.83 3.83 0 0 1-7.66 0' }), jsx$1("path", { fill: 'none', d: 'M29.36 14.07c0-1.46 1.18-2.64 2.64-2.64s2.64 1.18 2.64 2.64' })] }))); };

var SvgOptimizingIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32 43.96V20.04m-9.85 9.85L32 20.04l9.85 9.83' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgPdfIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M49.08 44.57v2.54c0 3.31-2.69 6-6 6H20.91c-3.31 0-6-2.69-6-6V16.89c0-3.31 2.69-6 6-6h15.86L49.08 23.2v7.89' }), jsx$1("path", { fill: 'none', d: 'M48.28 23.19h-8.56c-1.63 0-2.94-1.32-2.94-2.94v-8.7' }), jsx$1("rect", { width: 29.13, height: 13.49, x: 23.36, y: 31.08, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M27.82 42.19v-8.73h3.27c.67 0 1.23.13 1.69.38s.8.59 1.04 1.03.35.93.35 1.49-.12 1.06-.35 1.49c-.24.43-.58.78-1.04 1.02-.46.25-1.03.37-1.7.37h-2.17v-1.3h1.96c.39 0 .71-.07.96-.2s.44-.32.56-.56.18-.51.18-.82-.06-.58-.18-.82-.31-.42-.56-.55-.57-.2-.97-.2h-1.45v7.41h-1.58zm10.68 0h-2.96v-8.73h3.02c.87 0 1.61.17 2.23.52q.93.525 1.44 1.5c.34.65.5 1.43.5 2.34s-.17 1.69-.51 2.34-.82 1.15-1.45 1.5-1.39.52-2.28.52zm-1.38-1.37h1.3c.61 0 1.11-.11 1.52-.33s.71-.56.92-1c.2-.44.31-1 .31-1.67s-.1-1.22-.31-1.66c-.2-.44-.51-.77-.91-.99s-.89-.33-1.49-.33h-1.35v5.99zm7.11 1.37v-8.73h5.59v1.33h-4.01v2.37h3.63v1.33h-3.63v3.71h-1.58z' })] }))); };

var SvgPinIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M27.49 25.54 35.03 18c.63-.63.99-1.47 1.01-2.36h0c.02-.89.38-1.73 1.01-2.36h0a3.17 3.17 0 0 1 4.47 0l9.2 9.2a3.17 3.17 0 0 1 0 4.47h0c-.63.63-1.47.99-2.36 1.01h0c-.89.02-1.73.38-2.36 1.01l-7.54 7.54a5 5 0 0 0-1.41 4.29h0c.24 1.57-.28 3.17-1.41 4.29l-4.72 4.72-16.73-16.73 4.72-4.72a5 5 0 0 1 4.29-1.41h0c1.57.24 3.17-.28 4.29-1.41m-6.58 14.28-8.56 11.83 11.83-8.56' }) }))); };

var SvgPrintIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M17.33 43.8h-4.17c-1.66 0-3-1.34-3-3V26.16c0-1.66 1.34-3 3-3h37.67c1.66 0 3 1.34 3 3V40.8c0 1.66-1.34 3-3 3h-4.17' }), jsx$1("rect", { width: 29.33, height: 16.94, x: 17.33, y: 33.11, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M17.33 23.17v-3.22c0-3.31 2.69-6 6-6h17.33c3.31 0 6 2.69 6 6v3.22' })] }))); };

var SvgProfileIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32.53, cy: 27.04, r: 7.21, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M19.3 48.73C20.42 42.74 25.68 38.2 32 38.2s11.58 4.54 12.7 10.53' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgQuestionmarkCircleIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M24.75 26.59v-.52c0-4 3.25-7.25 7.25-7.25s7.25 3.25 7.25 7.25-3.87 6.59-3.87 6.59-3.5 2.3-3.5 6.15v.38' }), jsx$1("circle", { cx: 32, cy: 45.68, r: 1.5, fill: 'currentcolor' })] }))); };

var SvgQuestionmarkIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M19.78 23.51v-.88c0-6.75 5.47-12.22 12.22-12.22s12.22 5.47 12.22 12.22-6.52 11.12-6.52 11.12-5.9 3.87-5.9 10.38v.64' }), jsx$1("circle", { cx: 32, cy: 52.1, r: 1.5, fill: 'currentcolor' })] }))); };

var SvgRoomIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'm38.51 51-18.73-4V17l18.73-4zm-18.73-4h-7.2m25.93-29.78h5.22v29.64h7.69M34.17 33.27h-2.06' }) }))); };

var SvgScenarioIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M49.08 23.19v23.92c0 3.31-2.69 6-6 6H20.91c-3.31 0-6-2.69-6-6V16.89c0-3.31 2.69-6 6-6h15.86L49.08 23.2z' }), jsx$1("path", { fill: 'none', d: 'M49.08 23.19h-9.36c-1.63 0-2.94-1.32-2.94-2.94v-9.36m2.77 28.63c-.83 3.4-3.89 5.93-7.55 5.93-4.29 0-7.77-3.48-7.77-7.77 0-3.62 2.48-6.66 5.82-7.52' }), jsx$1("path", { fill: 'none', d: 'M41.55 35.89h-7.77v-7.77c4.29 0 7.77 3.48 7.77 7.77' })] }))); };

var SvgSearchIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 27.38, cy: 27.38, r: 11.38, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'm35.42 35.42 12.37 12.37' })] }))); };

var SvgSendIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M15.78 29.91 46.8 15.87c.84-.38 1.7.48 1.32 1.32L34.08 48.21l-4.71-13.36-13.6-4.94zm4.52 7.02-1.52 1.52m8.52 5.48-2.9 2.91m.35-7.35-6.41 6.4m11.04-11.04 18.64-18.64' }) }))); };

var SvgSettingsIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm50.91 26.29-1.34 3.63a6 6 0 0 0 0 4.14l1.34 3.64c1.35 3.65-1.09 7.6-4.96 8.03l-3.53.39a5.98 5.98 0 0 0-4.1 2.32l-1.56 2.04c-2.4 3.14-7.13 3.14-9.53 0l-1.56-2.04a5.98 5.98 0 0 0-4.1-2.32l-3.52-.39c-3.87-.43-6.31-4.38-4.96-8.03l1.34-3.63c.49-1.34.49-2.81 0-4.14l-1.34-3.64c-1.35-3.65 1.09-7.6 4.96-8.03l3.53-.39a5.98 5.98 0 0 0 4.1-2.32l1.56-2.04c2.4-3.14 7.13-3.14 9.53 0l1.56 2.04c.99 1.3 2.47 2.14 4.1 2.32l3.52.39c3.87.43 6.31 4.38 4.96 8.03' }), jsx$1("circle", { cx: 32, cy: 32, r: 7.72, fill: 'none' })] }))); };

var SvgShareAlternateIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 48.07, cy: 14.54, r: 3.46, fill: 'none' }), jsx$1("circle", { cx: 48.07, cy: 49.46, r: 3.46, fill: 'none' }), jsx$1("circle", { cx: 15.93, cy: 32, r: 3.46, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'm19.08 30.58 25.84-14.62M19.08 33.42l25.84 14.62' })] }))); };

var SvgShareIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M20.85 27.09h-3.37c-1.66 0-3 1.34-3 3v13.54c0 2.9 2.35 5.26 5.26 5.26h24.53c2.9 0 5.26-2.35 5.26-5.26V30.09c0-1.66-1.34-3-3-3h-3.37M32 36.43V15.11m-7.15 7.16L32 15.11l7.15 7.14' }) }))); };

var SvgSpinnerIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M30.59 46.19V53m0-42v6.81m10.5 32.38-3.4-5.9m11.09-1.79-5.9-3.41M51.59 32h-6.81m-26.47 7.09-5.9 3.41m36.37-21-5.9 3.41M23.5 44.29l-3.41 5.9m21-36.38-3.4 5.9' }) }))); };

var SvgSubstitutionsIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M23.24 36.96V25.03c0-2.2 1.78-3.98 3.98-3.98h9.16m4.38 5.99v11.93c0 2.2-1.78 3.98-3.98 3.98h-9.16' }), jsx$1("path", { fill: 'none', d: 'm18.85 32 4.38 4.96L27.61 32m17.54 0-4.38-4.96L36.39 32' }), jsx$1("circle", { cx: 32, cy: 32, r: 21, fill: 'none' })] }))); };

var SvgSwitchIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M29.83 39.33h21.32m-8.78-8.78 8.78 8.78-8.76 8.78m-8.22-23.44H12.85m8.78 8.78-8.78-8.78 8.76-8.78' }) }))); };

var SvgTemplatesIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M17.88 34.45H32m-14.12 6.01h10.07m23.99 3.97V25.18a3.72 3.72 0 0 0-3.72-3.72H31.99c-1.12 0-2.15-.63-2.67-1.63l-1.33-2.58c-.51-1-1.54-1.63-2.67-1.63H15.04c-1.66 0-3 1.34-3 3v26.31c0 1.9 1.54 3.44 3.44 3.44h32.51c2.18 0 3.94-1.76 3.94-3.94z' }) }))); };

var SvgTestIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm21.84 27.62 3.67 3.62 7.62-7.52' }), jsx$1("circle", { cx: 27.48, cy: 27.48, r: 11.38, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M35.53 35.53 47.9 47.9' })] }))); };

var SvgThreemaIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32 34.09v2.38' }), jsx$1("circle", { cx: 32, cy: 32.59, r: 1.5, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M32 21.91c2.02 0 3.65 1.64 3.65 3.65v1.92h-7.3v-1.92c0-2.02 1.64-3.65 3.65-3.65M15.82 41.2s-1.32 4.84-3.42 7.05c-.38.4-.11 1.05.44 1.05 3.11.03 8.81-.92 10.58-3.37' }), jsx$1("path", { fill: 'none', d: 'M15.82 41.21c-2.81-2.79-4.48-6.33-4.48-10.17 0-9.03 9.25-16.35 20.67-16.35s20.67 7.32 20.67 16.35-9.25 16.35-20.67 16.35c-3.06 0-5.96-.53-8.57-1.47' }), jsx$1("rect", { width: 15.5, height: 12.6, x: 24.25, y: 27.48, fill: 'none', rx: 3, ry: 3 })] }))); };

var SvgTimetableIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 39.89, height: 35.17, x: 12.06, y: 16.03, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M17.21 24.44h17.93m-17.93 4.91h6.29v6.29h-6.29zm11.65 0h6.29v6.29h-6.29zm11.64 0h6.29v6.29H40.5zM17.21 40.54h6.29v6.29h-6.29zm11.65 0h6.29v6.29h-6.29zm11.64 0h6.29v6.29H40.5zM25.38 12.81v6.44m13.24-6.44v6.44' })] }))); };

var SvgUndoIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M16.61 23.52h23.07c4.26 0 7.7 3.45 7.7 7.7v17.72' }), jsx$1("path", { fill: 'none', d: 'm26.2 15.05-9.59 8.48 9.59 8.48' })] }))); };

var SvgUnlockIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32 40.47v4.77' }), jsx$1("circle", { cx: 32, cy: 37.01, r: 3.47, fill: 'none' }), jsx$1("rect", { width: 34.73, height: 28.64, x: 14.64, y: 25.07, fill: 'none', rx: 6, ry: 6 }), jsx$1("path", { fill: 'none', d: 'M37.52 12.8A7.3 7.3 0 0 0 32 10.28h0c-4.04 0-7.32 3.28-7.32 7.32v7.47' })] }))); };

var SvgUnpinIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'm31.94 48.8-1.02 1.02-16.73-16.73 4.72-4.72a5 5 0 0 1 4.29-1.41h0c1.57.24 3.17-.28 4.29-1.41L32 21.04m4.04-5.4c.02-.89.38-1.73 1.01-2.36h0a3.17 3.17 0 0 1 4.47 0l9.2 9.2a3.17 3.17 0 0 1 0 4.47h0c-.63.63-1.47.99-2.36 1.01h0c-.89.02-1.73.38-2.36 1.01l-7.54 7.54a5 5 0 0 0-1.41 4.29h0c.16 1.02-.01 2.06-.46 2.96m-15.68-3.94-8.56 11.83 11.83-8.56M32 8.12v47.76' }) }))); };

var SvgUpdateIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M17.36 50.09v-8.8h9.05m20.23-27.38v8.8h-9.05' }), jsx$1("path", { fill: 'none', d: 'M14.9 34.88q-.24-1.41-.24-2.88c0-9.57 7.76-17.33 17.33-17.33 6.15 0 11.56 3.21 14.63 8.04m2.52 6.66c.13.86.2 1.73.2 2.63 0 9.57-7.76 17.33-17.33 17.33-6.15 0-11.56-3.21-14.63-8.04' })] }))); };

var SvgUploadIcon = function (props) { return (jsx$1("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: jsx$1("path", { fill: 'none', d: 'M14.48 27.09v16.54c0 2.9 2.35 5.26 5.26 5.26h24.53c2.9 0 5.26-2.35 5.26-5.26V27.09M32 36.43V15.11m-7.15 7.16L32 15.11l7.15 7.14' }) }))); };

var SvgVisibleIcon = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-icon', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M39.59 31.19c.03.27.04.53.04.81 0 4.03-3.27 7.31-7.31 7.31s-7.31-3.27-7.31-7.31 3.27-7.31 7.31-7.31c2.02 0 3.84.82 5.17 2.14' }), jsx$1("path", { fill: 'none', d: 'M10.44 32c.35-.57 7.82-12.3 21.89-12.11.42 0 .84.02 1.25.05 3.79.24 7.44 1.5 10.71 3.43l.72.42c1.13.66 2.23 1.39 3.23 2.24 3.33 2.82 5.11 5.63 5.32 5.98' }), jsx$1("path", { fill: 'none', d: 'M53.56 32c-.35.57-7.82 12.3-21.89 12.11h-.04c-2.12-.03-4.23-.43-6.26-1.07l-1.18-.37c-1.74-.54-3.44-1.26-4.99-2.21-5.54-3.4-8.47-8.01-8.75-8.46' })] }))); };

var getContainerDimensions = function (ref) {
    var _a, _b, _c, _d;
    return {
        width: (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) !== null && _b !== void 0 ? _b : 0,
        height: (_d = (_c = ref.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().height) !== null && _d !== void 0 ? _d : 0,
    };
};
var getWindowDimensions = function () {
    var width = window.innerWidth, height = window.innerHeight;
    return {
        width: width,
        height: height,
    };
};
function useContainerDimensions(ref) {
    var _a = useState({ width: 0, height: 0 }), dimensions = _a[0], setDimensions = _a[1];
    useEffect(function () {
        function handleResize() {
            setDimensions(getContainerDimensions(ref));
        }
        if (ref.current) {
            setDimensions(getContainerDimensions(ref));
        }
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, [ref]);
    return dimensions;
}
function useWindowDimensions$1() {
    var _a = useState(getWindowDimensions()), dimensions = _a[0], setDimensions = _a[1];
    useEffect(function () {
        function handleResize() {
            setDimensions(getWindowDimensions());
        }
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    return dimensions;
}

var Button = function (_a, ref) {
    var _b;
    var children = _a.children, _c = _a.hierarchy, hierarchy = _c === void 0 ? 'primary' : _c, _d = _a.isLoading, isLoading = _d === void 0 ? false : _d, _e = _a.type, type = _e === void 0 ? 'button' : _e, icon = _a.icon, _f = _a.height, height = _f === void 0 ? 'm' : _f, disabled = _a.disabled, _g = _a.fullWidth, fullWidth = _g === void 0 ? false : _g, _h = _a.iconPosition, iconPosition = _h === void 0 ? 'left' : _h, _j = _a.alwaysShowBorder, alwaysShowBorder = _j === void 0 ? false : _j, _k = _a.horizontalPadding, horizontalPadding = _k === void 0 ? true : _k, _l = _a.tabIndex, tabIndex = _l === void 0 ? 0 : _l, style = _a.style, onClick = _a.onClick, className = _a.className;
    var thisRef = useRef(null);
    var width = useContainerDimensions(thisRef).width;
    var classes = classNames$1(styles$Y.button, (_b = {},
        _b[styles$Y.primary] = hierarchy === 'primary',
        _b[styles$Y.secondary] = hierarchy === 'secondary',
        _b[styles$Y.tertiary] = hierarchy === 'tertiary' || hierarchy === 'tertiary-not-on-white',
        _b[styles$Y['not-on-white']] = hierarchy === 'tertiary-not-on-white',
        _b[styles$Y['includes-icon']] = icon && children && !isLoading,
        _b[styles$Y['icon-only']] = (icon && !children) || isLoading,
        _b[styles$Y.full] = fullWidth,
        _b[styles$Y.reversed] = iconPosition === 'right',
        _b[styles$Y['with-border']] = alwaysShowBorder,
        _b[styles$Y['ghost']] = hierarchy === 'ghost',
        _b[styles$Y['no-horizontal-padding']] = hierarchy === 'ghost' || !horizontalPadding,
        _b[styles$Y['small-height']] = height === 's',
        _b), className);
    return (jsxs("button", { ref: ref !== null && ref !== void 0 ? ref : thisRef, className: classes, style: __assign(__assign({}, style), (isLoading ? { width: width } : undefined)), type: type, disabled: disabled, onClick: !isLoading ? onClick : undefined, tabIndex: tabIndex, children: [isLoading && jsx$1(SvgSpinnerIcon, { className: styles$Y['loading-icon'] }), !isLoading && (jsxs(Fragment, { children: [icon && icon, children && jsx$1("div", { className: styles$Y.value, children: children })] }))] }));
};
var Button$1 = forwardRef(Button);

var css_248z$Z = ".AlertDialog-module_trigger__8TIAY {\n  width: 100%;\n}\n\n.AlertDialog-module_overlay__b2fWp {\n  background-color: rgb(0 0 0 / 50%);\n  position: fixed;\n  inset: 0;\n  z-index: var(--z-index-overlay);\n}\n\n.AlertDialog-module_content__Zpncs {\n  background-color: white;\n  border-radius: 8px;\n  box-shadow: 0 12px 34px rgb(15 15 47 / 6%);\n  position: fixed;\n  bottom: 4px;\n  left: 50%;\n  transform: translate(-50%, 0);\n  width: calc(100% - 8px);\n  max-width: 500px;\n  max-height: 85vh;\n  padding: 18px;\n  z-index: var(--z-index-modal);\n  font-size: var(--font-size-m);\n  overflow-y: auto;\n}\n\n@media (width >= 580px) {\n  .AlertDialog-module_content__Zpncs {\n    top: 50%;\n    bottom: auto;\n    transform: translate(-50%, -50%);\n    width: 90vw;\n    padding: 30px;\n    border-radius: 16px;\n  }\n}\n\n.AlertDialog-module_title__tsWqs {\n  margin-bottom: var(--spacing-6);\n}\n\n.AlertDialog-module_children__-mLCP {\n  margin-bottom: var(--spacing-6);\n}\n\n.AlertDialog-module_actions__tnxPn {\n  display: flex;\n  justify-content: flex-end;\n  gap: var(--spacing-4);\n}\n\n@media (prefers-reduced-motion: no-preference) {\n  .AlertDialog-module_overlay__b2fWp {\n    animation: AlertDialog-module_overlay-show__1SI9q 150ms cubic-bezier(0.16, 1, 0.3, 1);\n  }\n\n  .AlertDialog-module_content__Zpncs {\n    animation: AlertDialog-module_content-show__RtGXw 150ms cubic-bezier(0.16, 1, 0.3, 1);\n  }\n}\n\n@keyframes AlertDialog-module_overlay-show__1SI9q {\n  0% {\n    opacity: 0;\n  }\n\n  100% {\n    opacity: 1;\n  }\n}\n\n@keyframes AlertDialog-module_content-show__RtGXw {\n  0% {\n    opacity: 0;\n    transform: translate(-50%, -48%) scale(0.96);\n  }\n\n  100% {\n    opacity: 1;\n    transform: translate(-50%, -50%) scale(1);\n  }\n}\n";
var styles$X = {"trigger":"AlertDialog-module_trigger__8TIAY","overlay":"AlertDialog-module_overlay__b2fWp","content":"AlertDialog-module_content__Zpncs","title":"AlertDialog-module_title__tsWqs","children":"AlertDialog-module_children__-mLCP","actions":"AlertDialog-module_actions__tnxPn"};
styleInject(css_248z$Z);

/**
 *
 * Alert Dialog:
 * A modal dialog that interrupts the user with important content and expects a response.
 *
 * Uses radix-ui as base component:
 * https://www.radix-ui.com/docs/primitives/components/alert-dialog
 *
 * @param {React.ReactNode} children - Alert Dialog Content
 * @param {React.ReactNode} trigger - Trigger element to open Alert - usually a Button
 * @param {string} confirmText - Confirm button text
 * @param {string} cancelText - Cancel button text
 * @param {string} title - Alert Dialog Headline
 * @param {function} onConfirm - Callback when confirm button is clicked
 * @param className
 * @param ref
 */
var AlertDialog = function (_a, ref) {
    var trigger = _a.trigger, _b = _a.usePortal, usePortal = _b === void 0 ? true : _b, confirmText = _a.confirmText, cancelText = _a.cancelText, title = _a.title, onConfirm = _a.onConfirm, onCancel = _a.onCancel, className = _a.className, children = _a.children;
    var classes = classNames$1(styles$X.content, className);
    return (jsxs(Root2$4, { children: [jsx$1(Trigger2, { className: styles$X.trigger, asChild: true, children: jsx$1("div", { children: trigger }) }), usePortal ? (jsxs(Portal2$2, { children: [jsx$1(Overlay2, { className: styles$X.overlay }), jsxs(Content2$4, { ref: ref, className: classes, onEscapeKeyDown: function () { return onCancel && onCancel(); }, children: [jsx$1(Title2, { className: styles$X.title, children: title }), jsx$1(Description2, { className: styles$X.children, asChild: true, children: children }), jsxs("footer", { className: styles$X.actions, children: [jsx$1(Cancel, { asChild: true, children: jsx$1("span", { children: jsx$1(Button$1, { hierarchy: 'tertiary', onClick: onCancel, children: cancelText }) }) }), jsx$1(Action, { className: styles$X.confirm, asChild: true, children: jsx$1("span", { children: jsx$1(Button$1, { hierarchy: 'primary', onClick: onConfirm, children: confirmText }) }) })] })] })] })) : (jsxs(Fragment, { children: [jsx$1(Overlay2, { className: styles$X.overlay }), jsxs(Content2$4, { ref: ref, className: classes, onEscapeKeyDown: function () { return onCancel && onCancel(); }, children: [jsx$1(Title2, { className: styles$X.title, children: title }), jsx$1(Description2, { className: styles$X.children, asChild: true, children: children }), jsxs("footer", { className: styles$X.actions, children: [jsx$1(Cancel, { asChild: true, children: jsx$1("span", { children: jsx$1(Button$1, { hierarchy: 'tertiary', children: cancelText }) }) }), jsx$1(Action, { className: styles$X.confirm, asChild: true, children: jsx$1("span", { children: jsx$1(Button$1, { hierarchy: 'primary', onClick: onConfirm, children: confirmText }) }) })] })] })] }))] }));
};
var AlertDialog$1 = forwardRef(AlertDialog);

var css_248z$Y = "/* stylelint-disable -- too much hassle for disabled rules with nested Dropdown/AlertDialog  */\n.ButtonGroup-module_btn-group__3q7TF {\n  display: inline-flex;\n  position: relative;\n  border-radius: var(--button-border-radius);\n}\n\n/* buttons */\n.ButtonGroup-module_btn-group__3q7TF > [type='button'],\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:hover {\n  border: 1px solid var(--btn-group-border-color);\n  border-radius: 0;\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:first-child {\n  border-top-left-radius: var(--button-border-radius);\n  border-bottom-left-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:last-child {\n  border-top-right-radius: var(--button-border-radius);\n  border-bottom-right-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:not(:first-child) {\n  border-left: none;\n}\n\n/* dropdown trigger buttons */\n.ButtonGroup-module_btn-group__3q7TF > span[type='button'] {\n  height: calc(var(--input-height-desktop) - 2px);\n  border: 1px solid var(--btn-group-border-color);\n  border-left: none;\n  overflow: hidden;\n}\n\n.ButtonGroup-module_btn-group__3q7TF > span[type='button']:first-child {\n  border-left: 1px solid var(--btn-group-border-color);\n  border-top-left-radius: var(--button-border-radius);\n  border-bottom-left-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > span[type='button']:last-child {\n  border-top-right-radius: var(--button-border-radius);\n  border-bottom-right-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > span[type='button'] > button {\n  border: none;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n}\n\n.ButtonGroup-module_btn-group__3q7TF > span[type='button'][disabled] {\n  border-color: var(--input-border-color-disabled);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > span[type='button'] > button:hover {\n  border: none;\n  background-color: var(--color-primary-lighter);\n}\n\n/* alert dialog trigger */\n.ButtonGroup-module_btn-group__3q7TF > div[class*='_trigger'] {\n  width: fit-content;\n  height: calc(var(--input-height-desktop) - 2px);\n  border: 1px solid var(--btn-group-border-color);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > div[class*='_trigger']:first-child {\n  border-top-left-radius: var(--button-border-radius);\n  border-bottom-left-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > div[class*='_trigger']:last-child {\n  border-top-right-radius: var(--button-border-radius);\n  border-bottom-right-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > div[class*='_trigger']:not(:first-child) {\n  border-left: none;\n}\n\n.ButtonGroup-module_btn-group__3q7TF > div[class*='_trigger'] > [type='button'] {\n  height: calc(var(--input-height-desktop) - 4px);\n}\n\n/* small buttons */\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > button[type='button'] {\n  height: 24px;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > button[type='button'][class*='primary'] > div {\n  height: 24px;\n  line-height: 24px;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > button[type='button'][class*='icon-only'] {\n  min-width: 24px;\n  max-width: 24px;\n  width: 24px;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > button[type='button'] > svg {\n  width: 16px;\n  height: 16px;\n  stroke-width: 4;\n}\n\n/* small with dropdown trigger */\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > span[type='button'] {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  min-width: 24px;\n  width: 24px;\n  min-height: 24px;\n  height: 24px;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > span[type='button'] > button {\n  border: none;\n  min-width: 24px;\n  width: 24px;\n  min-height: 24px;\n  height: 24px;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > span[type='button'] > button:hover {\n  background-color: var(--color-primary-lighter);\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_small__k3-0w > span[type='button'] > button > svg {\n  width: 16px;\n  height: 16px;\n  stroke-width: 4;\n}\n\n/* loader + tooltip */\n.ButtonGroup-module_loader__f52Fs {\n  position: absolute;\n  z-index: var(--z-index-toast);\n}\n\n.ButtonGroup-module_loader__f52Fs[class*='lazy-loader'] {\n  background-color: rgb(255 255 255 / 80%);\n  border: 1px solid var(--btn-group-border-color);\n  border-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_tooltip__yJjEQ {\n  width: fit-content;\n}\n\n/* disabled buttons */\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv {\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-border-color-disabled);\n  cursor: not-allowed;\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:disabled {\n  border-color: var(--input-border-color-disabled);\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button'],\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button']:hover,\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button']:disabled,\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button']:disabled:hover {\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-border-color-disabled);\n  pointer-events: none;\n}\n\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button'] > svg,\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > div[class*='_trigger'] svg,\n.ButtonGroup-module_btn-group__3q7TF.ButtonGroup-module_disabled__cO9yv > [type='button'] > [type='button'] {\n  color: var(--input-border-color-disabled);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:disabled:first-child {\n  border-top-left-radius: var(--button-border-radius);\n  border-bottom-left-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:disabled:last-child {\n  border-top-right-radius: var(--button-border-radius);\n  border-bottom-right-radius: var(--button-border-radius);\n}\n\n.ButtonGroup-module_btn-group__3q7TF > [type='button']:disabled:not(:first-child) {\n  border-left: none;\n}\n";
var styles$W = {"btn-group":"ButtonGroup-module_btn-group__3q7TF","small":"ButtonGroup-module_small__k3-0w","loader":"ButtonGroup-module_loader__f52Fs","tooltip":"ButtonGroup-module_tooltip__yJjEQ","disabled":"ButtonGroup-module_disabled__cO9yv"};
styleInject(css_248z$Y);

var css_248z$X = ".LazyLoader-module_lazy-loader__D490g {\n  width: fit-content;\n  height: fit-content;\n  padding: 16px;\n  background-color: var(--color-white);\n  box-shadow: var(--box-shadow);\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  transition: none;\n  animation: none;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_embedded__tXlyX {\n  border-radius: 0;\n  box-shadow: none;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_transparent__l5PI6 {\n  background-color: transparent;\n  box-shadow: none;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_no-shadow__PJG-U {\n  box-shadow: none;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_no-padding__0SKoG {\n  padding: 0;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_border-s__DFQT8 {\n  border-radius: var(--border-radius-s);\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_border-m__0x3e- {\n  border-radius: var(--border-radius-m);\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_border-l__AowoS {\n  border-radius: var(--border-radius-l);\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_icon-left__1cCx4 {\n  align-items: start;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_icon-center__wfFLH {\n  align-items: center;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_icon-right__akgf6 {\n  align-items: end;\n}\n\n.LazyLoader-module_lazy-loader__D490g svg {\n  transform-origin: center;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_size-s__APcTS svg {\n  height: 16px;\n  width: 16px;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_size-m__GoJtj svg {\n  height: 24px;\n  width: 24px;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_size-l__I41hE svg {\n  height: 36px;\n  width: 36px;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_size-xxl__VZHfH svg {\n  height: 48px;\n  width: 48px;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_full-width__M3-fY {\n  width: 100%;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_full-height__kV7kG {\n  height: 100%;\n}\n\n.LazyLoader-module_lazy-loader__D490g.LazyLoader-module_positioned__QOg4s {\n  position: absolute;\n}\n\n.LazyLoader-module_lazy-loader__D490g .LazyLoader-module_spinning__NDm-q {\n  animation: LazyLoader-module_spinning__NDm-q 1.4s linear infinite;\n}\n\n@keyframes LazyLoader-module_spinning__NDm-q {\n  from {\n    transform: rotate(0deg);\n  }\n\n  to {\n    transform: rotate(360deg);\n  }\n}\n";
var styles$V = {"lazy-loader":"LazyLoader-module_lazy-loader__D490g","embedded":"LazyLoader-module_embedded__tXlyX","transparent":"LazyLoader-module_transparent__l5PI6","no-shadow":"LazyLoader-module_no-shadow__PJG-U","no-padding":"LazyLoader-module_no-padding__0SKoG","border-s":"LazyLoader-module_border-s__DFQT8","border-m":"LazyLoader-module_border-m__0x3e-","border-l":"LazyLoader-module_border-l__AowoS","icon-left":"LazyLoader-module_icon-left__1cCx4","icon-center":"LazyLoader-module_icon-center__wfFLH","icon-right":"LazyLoader-module_icon-right__akgf6","size-s":"LazyLoader-module_size-s__APcTS","size-m":"LazyLoader-module_size-m__GoJtj","size-l":"LazyLoader-module_size-l__I41hE","size-xxl":"LazyLoader-module_size-xxl__VZHfH","full-width":"LazyLoader-module_full-width__M3-fY","full-height":"LazyLoader-module_full-height__kV7kG","positioned":"LazyLoader-module_positioned__QOg4s","spinning":"LazyLoader-module_spinning__NDm-q"};
styleInject(css_248z$X);

var LazyLoader = function (_a, ref) {
    var _b;
    var _c = _a.embedded, embedded = _c === void 0 ? false : _c, _d = _a.transparent, transparent = _d === void 0 ? false : _d, _e = _a.noShadow, noShadow = _e === void 0 ? false : _e, _f = _a.noPadding, noPadding = _f === void 0 ? false : _f, _g = _a.size, size = _g === void 0 ? 'l' : _g, _h = _a.borderRadius, borderRadius = _h === void 0 ? 'l' : _h, _j = _a.iconPosition, iconPosition = _j === void 0 ? 'center' : _j, _k = _a.fullWidth, fullWidth = _k === void 0 ? true : _k, _l = _a.forceWidth, forceWidth = _l === void 0 ? '' : _l, _m = _a.fullHeight, fullHeight = _m === void 0 ? true : _m, _o = _a.forceHeight, forceHeight = _o === void 0 ? '' : _o, _p = _a.position, position = _p === void 0 ? null : _p, className = _a.className;
    var classes = classNames$1(styles$V['lazy-loader'], (_b = {},
        _b[styles$V['embedded']] = embedded,
        _b[styles$V['transparent']] = transparent,
        _b[styles$V['no-shadow']] = noShadow,
        _b[styles$V['no-padding']] = noPadding,
        _b[styles$V['size-' + size]] = size,
        _b[styles$V['border-' + borderRadius]] = !embedded,
        _b[styles$V['icon-' + iconPosition]] = iconPosition,
        _b[styles$V['full-width']] = fullWidth && !forceWidth,
        _b[styles$V['full-height']] = fullHeight && !forceHeight,
        _b[styles$V['positioned']] = position,
        _b), className);
    var width = forceWidth ? forceWidth : undefined;
    var height = forceHeight ? forceHeight : undefined;
    var top = position && position.top ? position.top : undefined;
    var right = position && position.right ? position.right : undefined;
    var bottom = position && position.bottom ? position.bottom : undefined;
    var left = position && position.left ? position.left : undefined;
    return (jsx$1("div", { ref: ref, className: classes, style: {
            width: width,
            height: height,
            top: top,
            right: right,
            bottom: bottom,
            left: left,
        }, children: jsx$1(SvgSpinnerIcon, { className: styles$V.spinning }) }));
};
var LazyLoader$1 = forwardRef(LazyLoader);

/**
 * Custom positioning reference element.
 * @see https://floating-ui.com/docs/virtual-elements
 */

const sides = ['top', 'right', 'bottom', 'left'];
const min$2 = Math.min;
const max$2 = Math.max;
const round = Math.round;
const floor = Math.floor;
const createCoords = v => ({
  x: v,
  y: v
});
const oppositeSideMap = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
const oppositeAlignmentMap = {
  start: 'end',
  end: 'start'
};
function clamp(start, value, end) {
  return max$2(start, min$2(value, end));
}
function evaluate(value, param) {
  return typeof value === 'function' ? value(param) : value;
}
function getSide(placement) {
  return placement.split('-')[0];
}
function getAlignment(placement) {
  return placement.split('-')[1];
}
function getOppositeAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}
function getAxisLength(axis) {
  return axis === 'y' ? 'height' : 'width';
}
function getSideAxis(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'y' : 'x';
}
function getAlignmentAxis(placement) {
  return getOppositeAxis(getSideAxis(placement));
}
function getAlignmentSides(placement, rects, rtl) {
  if (rtl === void 0) {
    rtl = false;
  }
  const alignment = getAlignment(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const length = getAxisLength(alignmentAxis);
  let mainAlignmentSide = alignmentAxis === 'x' ? alignment === (rtl ? 'end' : 'start') ? 'right' : 'left' : alignment === 'start' ? 'bottom' : 'top';
  if (rects.reference[length] > rects.floating[length]) {
    mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
  }
  return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
}
function getExpandedPlacements(placement) {
  const oppositePlacement = getOppositePlacement(placement);
  return [getOppositeAlignmentPlacement(placement), oppositePlacement, getOppositeAlignmentPlacement(oppositePlacement)];
}
function getOppositeAlignmentPlacement(placement) {
  return placement.replace(/start|end/g, alignment => oppositeAlignmentMap[alignment]);
}
function getSideList(side, isStart, rtl) {
  const lr = ['left', 'right'];
  const rl = ['right', 'left'];
  const tb = ['top', 'bottom'];
  const bt = ['bottom', 'top'];
  switch (side) {
    case 'top':
    case 'bottom':
      if (rtl) return isStart ? rl : lr;
      return isStart ? lr : rl;
    case 'left':
    case 'right':
      return isStart ? tb : bt;
    default:
      return [];
  }
}
function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
  const alignment = getAlignment(placement);
  let list = getSideList(getSide(placement), direction === 'start', rtl);
  if (alignment) {
    list = list.map(side => side + "-" + alignment);
    if (flipAlignment) {
      list = list.concat(list.map(getOppositeAlignmentPlacement));
    }
  }
  return list;
}
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, side => oppositeSideMap[side]);
}
function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}
function getPaddingObject(padding) {
  return typeof padding !== 'number' ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}
function rectToClientRect(rect) {
  const {
    x,
    y,
    width,
    height
  } = rect;
  return {
    width,
    height,
    top: y,
    left: x,
    right: x + width,
    bottom: y + height,
    x,
    y
  };
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const sideAxis = getSideAxis(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const alignLength = getAxisLength(alignmentAxis);
  const side = getSide(placement);
  const isVertical = sideAxis === 'y';
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const validMiddleware = middleware.filter(Boolean);
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < validMiddleware.length; i++) {
    const {
      name,
      fn
    } = validMiddleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

/**
 * Resolves with an object of overflow side offsets that determine how much the
 * element is overflowing a given clipping boundary on each side.
 * - positive = overflowing the boundary by that number of pixels
 * - negative = how many pixels left before it will overflow
 * - 0 = lies flush with the boundary
 * @see https://floating-ui.com/docs/detectOverflow
 */
async function detectOverflow(state, options) {
  var _await$platform$isEle;
  if (options === void 0) {
    options = {};
  }
  const {
    x,
    y,
    platform,
    rects,
    elements,
    strategy
  } = state;
  const {
    boundary = 'clippingAncestors',
    rootBoundary = 'viewport',
    elementContext = 'floating',
    altBoundary = false,
    padding = 0
  } = evaluate(options, state);
  const paddingObject = getPaddingObject(padding);
  const altContext = elementContext === 'floating' ? 'reference' : 'floating';
  const element = elements[altBoundary ? altContext : elementContext];
  const clippingClientRect = rectToClientRect(await platform.getClippingRect({
    element: ((_await$platform$isEle = await (platform.isElement == null ? void 0 : platform.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || (await (platform.getDocumentElement == null ? void 0 : platform.getDocumentElement(elements.floating))),
    boundary,
    rootBoundary,
    strategy
  }));
  const rect = elementContext === 'floating' ? {
    x,
    y,
    width: rects.floating.width,
    height: rects.floating.height
  } : rects.reference;
  const offsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(elements.floating));
  const offsetScale = (await (platform.isElement == null ? void 0 : platform.isElement(offsetParent))) ? (await (platform.getScale == null ? void 0 : platform.getScale(offsetParent))) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  };
  const elementClientRect = rectToClientRect(platform.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform.convertOffsetParentRelativeRectToViewportRelativeRect({
    elements,
    rect,
    offsetParent,
    strategy
  }) : rect);
  return {
    top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
    bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
    left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
    right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
  };
}

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$3 = options => ({
  name: 'arrow',
  options,
  async fn(state) {
    const {
      x,
      y,
      placement,
      rects,
      platform,
      elements,
      middlewareData
    } = state;
    // Since `element` is required, we don't Partial<> the type.
    const {
      element,
      padding = 0
    } = evaluate(options, state) || {};
    if (element == null) {
      return {};
    }
    const paddingObject = getPaddingObject(padding);
    const coords = {
      x,
      y
    };
    const axis = getAlignmentAxis(placement);
    const length = getAxisLength(axis);
    const arrowDimensions = await platform.getDimensions(element);
    const isYAxis = axis === 'y';
    const minProp = isYAxis ? 'top' : 'left';
    const maxProp = isYAxis ? 'bottom' : 'right';
    const clientProp = isYAxis ? 'clientHeight' : 'clientWidth';
    const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
    const startDiff = coords[axis] - rects.reference[axis];
    const arrowOffsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(element));
    let clientSize = arrowOffsetParent ? arrowOffsetParent[clientProp] : 0;

    // DOM platform can return `window` as the `offsetParent`.
    if (!clientSize || !(await (platform.isElement == null ? void 0 : platform.isElement(arrowOffsetParent)))) {
      clientSize = elements.floating[clientProp] || rects.floating[length];
    }
    const centerToReference = endDiff / 2 - startDiff / 2;

    // If the padding is large enough that it causes the arrow to no longer be
    // centered, modify the padding so that it is centered.
    const largestPossiblePadding = clientSize / 2 - arrowDimensions[length] / 2 - 1;
    const minPadding = min$2(paddingObject[minProp], largestPossiblePadding);
    const maxPadding = min$2(paddingObject[maxProp], largestPossiblePadding);

    // Make sure the arrow doesn't overflow the floating element if the center
    // point is outside the floating element's bounds.
    const min$1 = minPadding;
    const max = clientSize - arrowDimensions[length] - maxPadding;
    const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
    const offset = clamp(min$1, center, max);

    // If the reference is small enough that the arrow's padding causes it to
    // to point to nothing for an aligned placement, adjust the offset of the
    // floating element itself. To ensure `shift()` continues to take action,
    // a single reset is performed when this is true.
    const shouldAddOffset = !middlewareData.arrow && getAlignment(placement) != null && center !== offset && rects.reference[length] / 2 - (center < min$1 ? minPadding : maxPadding) - arrowDimensions[length] / 2 < 0;
    const alignmentOffset = shouldAddOffset ? center < min$1 ? center - min$1 : center - max : 0;
    return {
      [axis]: coords[axis] + alignmentOffset,
      data: {
        [axis]: offset,
        centerOffset: center - offset - alignmentOffset,
        ...(shouldAddOffset && {
          alignmentOffset
        })
      },
      reset: shouldAddOffset
    };
  }
});

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip$2 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'flip',
    options,
    async fn(state) {
      var _middlewareData$arrow, _middlewareData$flip;
      const {
        placement,
        middlewareData,
        rects,
        initialPlacement,
        platform,
        elements
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = true,
        fallbackPlacements: specifiedFallbackPlacements,
        fallbackStrategy = 'bestFit',
        fallbackAxisSideDirection = 'none',
        flipAlignment = true,
        ...detectOverflowOptions
      } = evaluate(options, state);

      // If a reset by the arrow was caused due to an alignment offset being
      // added, we should skip any logic now since `flip()` has already done its
      // work.
      // https://github.com/floating-ui/floating-ui/issues/2549#issuecomment-1719601643
      if ((_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      const side = getSide(placement);
      const initialSideAxis = getSideAxis(initialPlacement);
      const isBasePlacement = getSide(initialPlacement) === initialPlacement;
      const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
      const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
      const hasFallbackAxisSideDirection = fallbackAxisSideDirection !== 'none';
      if (!specifiedFallbackPlacements && hasFallbackAxisSideDirection) {
        fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
      }
      const placements = [initialPlacement, ...fallbackPlacements];
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const overflows = [];
      let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
      if (checkMainAxis) {
        overflows.push(overflow[side]);
      }
      if (checkCrossAxis) {
        const sides = getAlignmentSides(placement, rects, rtl);
        overflows.push(overflow[sides[0]], overflow[sides[1]]);
      }
      overflowsData = [...overflowsData, {
        placement,
        overflows
      }];

      // One or more sides is overflowing.
      if (!overflows.every(side => side <= 0)) {
        var _middlewareData$flip2, _overflowsData$filter;
        const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
        const nextPlacement = placements[nextIndex];
        if (nextPlacement) {
          // Try next placement and re-run the lifecycle.
          return {
            data: {
              index: nextIndex,
              overflows: overflowsData
            },
            reset: {
              placement: nextPlacement
            }
          };
        }

        // First, find the candidates that fit on the mainAxis side of overflow,
        // then find the placement that fits the best on the main crossAxis side.
        let resetPlacement = (_overflowsData$filter = overflowsData.filter(d => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;

        // Otherwise fallback.
        if (!resetPlacement) {
          switch (fallbackStrategy) {
            case 'bestFit':
              {
                var _overflowsData$filter2;
                const placement = (_overflowsData$filter2 = overflowsData.filter(d => {
                  if (hasFallbackAxisSideDirection) {
                    const currentSideAxis = getSideAxis(d.placement);
                    return currentSideAxis === initialSideAxis ||
                    // Create a bias to the `y` side axis due to horizontal
                    // reading directions favoring greater width.
                    currentSideAxis === 'y';
                  }
                  return true;
                }).map(d => [d.placement, d.overflows.filter(overflow => overflow > 0).reduce((acc, overflow) => acc + overflow, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$filter2[0];
                if (placement) {
                  resetPlacement = placement;
                }
                break;
              }
            case 'initialPlacement':
              resetPlacement = initialPlacement;
              break;
          }
        }
        if (placement !== resetPlacement) {
          return {
            reset: {
              placement: resetPlacement
            }
          };
        }
      }
      return {};
    }
  };
};

function getSideOffsets(overflow, rect) {
  return {
    top: overflow.top - rect.height,
    right: overflow.right - rect.width,
    bottom: overflow.bottom - rect.height,
    left: overflow.left - rect.width
  };
}
function isAnySideFullyClipped(overflow) {
  return sides.some(side => overflow[side] >= 0);
}
/**
 * Provides data to hide the floating element in applicable situations, such as
 * when it is not in the same clipping context as the reference element.
 * @see https://floating-ui.com/docs/hide
 */
const hide$2 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'hide',
    options,
    async fn(state) {
      const {
        rects
      } = state;
      const {
        strategy = 'referenceHidden',
        ...detectOverflowOptions
      } = evaluate(options, state);
      switch (strategy) {
        case 'referenceHidden':
          {
            const overflow = await detectOverflow(state, {
              ...detectOverflowOptions,
              elementContext: 'reference'
            });
            const offsets = getSideOffsets(overflow, rects.reference);
            return {
              data: {
                referenceHiddenOffsets: offsets,
                referenceHidden: isAnySideFullyClipped(offsets)
              }
            };
          }
        case 'escaped':
          {
            const overflow = await detectOverflow(state, {
              ...detectOverflowOptions,
              altBoundary: true
            });
            const offsets = getSideOffsets(overflow, rects.floating);
            return {
              data: {
                escapedOffsets: offsets,
                escaped: isAnySideFullyClipped(offsets)
              }
            };
          }
        default:
          {
            return {};
          }
      }
    }
  };
};

// For type backwards-compatibility, the `OffsetOptions` type was also
// Derivable.

async function convertValueToCoords(state, options) {
  const {
    placement,
    platform,
    elements
  } = state;
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getSideAxis(placement) === 'y';
  const mainAxisMulti = ['left', 'top'].includes(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = evaluate(options, state);

  // eslint-disable-next-line prefer-const
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === 'number' ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: rawValue.mainAxis || 0,
    crossAxis: rawValue.crossAxis || 0,
    alignmentAxis: rawValue.alignmentAxis
  };
  if (alignment && typeof alignmentAxis === 'number') {
    crossAxis = alignment === 'end' ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset$2 = function (options) {
  if (options === void 0) {
    options = 0;
  }
  return {
    name: 'offset',
    options,
    async fn(state) {
      var _middlewareData$offse, _middlewareData$arrow;
      const {
        x,
        y,
        placement,
        middlewareData
      } = state;
      const diffCoords = await convertValueToCoords(state, options);

      // If the placement is the same and the arrow caused an alignment offset
      // then we don't need to change the positioning coordinates.
      if (placement === ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse.placement) && (_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: {
          ...diffCoords,
          placement
        }
      };
    }
  };
};

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift$2 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'shift',
    options,
    async fn(state) {
      const {
        x,
        y,
        placement
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = false,
        limiter = {
          fn: _ref => {
            let {
              x,
              y
            } = _ref;
            return {
              x,
              y
            };
          }
        },
        ...detectOverflowOptions
      } = evaluate(options, state);
      const coords = {
        x,
        y
      };
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const crossAxis = getSideAxis(getSide(placement));
      const mainAxis = getOppositeAxis(crossAxis);
      let mainAxisCoord = coords[mainAxis];
      let crossAxisCoord = coords[crossAxis];
      if (checkMainAxis) {
        const minSide = mainAxis === 'y' ? 'top' : 'left';
        const maxSide = mainAxis === 'y' ? 'bottom' : 'right';
        const min = mainAxisCoord + overflow[minSide];
        const max = mainAxisCoord - overflow[maxSide];
        mainAxisCoord = clamp(min, mainAxisCoord, max);
      }
      if (checkCrossAxis) {
        const minSide = crossAxis === 'y' ? 'top' : 'left';
        const maxSide = crossAxis === 'y' ? 'bottom' : 'right';
        const min = crossAxisCoord + overflow[minSide];
        const max = crossAxisCoord - overflow[maxSide];
        crossAxisCoord = clamp(min, crossAxisCoord, max);
      }
      const limitedCoords = limiter.fn({
        ...state,
        [mainAxis]: mainAxisCoord,
        [crossAxis]: crossAxisCoord
      });
      return {
        ...limitedCoords,
        data: {
          x: limitedCoords.x - x,
          y: limitedCoords.y - y,
          enabled: {
            [mainAxis]: checkMainAxis,
            [crossAxis]: checkCrossAxis
          }
        }
      };
    }
  };
};
/**
 * Built-in `limiter` that will stop `shift()` at a certain point.
 */
const limitShift$2 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    options,
    fn(state) {
      const {
        x,
        y,
        placement,
        rects,
        middlewareData
      } = state;
      const {
        offset = 0,
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = true
      } = evaluate(options, state);
      const coords = {
        x,
        y
      };
      const crossAxis = getSideAxis(placement);
      const mainAxis = getOppositeAxis(crossAxis);
      let mainAxisCoord = coords[mainAxis];
      let crossAxisCoord = coords[crossAxis];
      const rawOffset = evaluate(offset, state);
      const computedOffset = typeof rawOffset === 'number' ? {
        mainAxis: rawOffset,
        crossAxis: 0
      } : {
        mainAxis: 0,
        crossAxis: 0,
        ...rawOffset
      };
      if (checkMainAxis) {
        const len = mainAxis === 'y' ? 'height' : 'width';
        const limitMin = rects.reference[mainAxis] - rects.floating[len] + computedOffset.mainAxis;
        const limitMax = rects.reference[mainAxis] + rects.reference[len] - computedOffset.mainAxis;
        if (mainAxisCoord < limitMin) {
          mainAxisCoord = limitMin;
        } else if (mainAxisCoord > limitMax) {
          mainAxisCoord = limitMax;
        }
      }
      if (checkCrossAxis) {
        var _middlewareData$offse, _middlewareData$offse2;
        const len = mainAxis === 'y' ? 'width' : 'height';
        const isOriginSide = ['top', 'left'].includes(getSide(placement));
        const limitMin = rects.reference[crossAxis] - rects.floating[len] + (isOriginSide ? ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse[crossAxis]) || 0 : 0) + (isOriginSide ? 0 : computedOffset.crossAxis);
        const limitMax = rects.reference[crossAxis] + rects.reference[len] + (isOriginSide ? 0 : ((_middlewareData$offse2 = middlewareData.offset) == null ? void 0 : _middlewareData$offse2[crossAxis]) || 0) - (isOriginSide ? computedOffset.crossAxis : 0);
        if (crossAxisCoord < limitMin) {
          crossAxisCoord = limitMin;
        } else if (crossAxisCoord > limitMax) {
          crossAxisCoord = limitMax;
        }
      }
      return {
        [mainAxis]: mainAxisCoord,
        [crossAxis]: crossAxisCoord
      };
    }
  };
};

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size$2 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'size',
    options,
    async fn(state) {
      var _state$middlewareData, _state$middlewareData2;
      const {
        placement,
        rects,
        platform,
        elements
      } = state;
      const {
        apply = () => {},
        ...detectOverflowOptions
      } = evaluate(options, state);
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const side = getSide(placement);
      const alignment = getAlignment(placement);
      const isYAxis = getSideAxis(placement) === 'y';
      const {
        width,
        height
      } = rects.floating;
      let heightSide;
      let widthSide;
      if (side === 'top' || side === 'bottom') {
        heightSide = side;
        widthSide = alignment === ((await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating))) ? 'start' : 'end') ? 'left' : 'right';
      } else {
        widthSide = side;
        heightSide = alignment === 'end' ? 'top' : 'bottom';
      }
      const maximumClippingHeight = height - overflow.top - overflow.bottom;
      const maximumClippingWidth = width - overflow.left - overflow.right;
      const overflowAvailableHeight = min$2(height - overflow[heightSide], maximumClippingHeight);
      const overflowAvailableWidth = min$2(width - overflow[widthSide], maximumClippingWidth);
      const noShift = !state.middlewareData.shift;
      let availableHeight = overflowAvailableHeight;
      let availableWidth = overflowAvailableWidth;
      if ((_state$middlewareData = state.middlewareData.shift) != null && _state$middlewareData.enabled.x) {
        availableWidth = maximumClippingWidth;
      }
      if ((_state$middlewareData2 = state.middlewareData.shift) != null && _state$middlewareData2.enabled.y) {
        availableHeight = maximumClippingHeight;
      }
      if (noShift && !alignment) {
        const xMin = max$2(overflow.left, 0);
        const xMax = max$2(overflow.right, 0);
        const yMin = max$2(overflow.top, 0);
        const yMax = max$2(overflow.bottom, 0);
        if (isYAxis) {
          availableWidth = width - 2 * (xMin !== 0 || xMax !== 0 ? xMin + xMax : max$2(overflow.left, overflow.right));
        } else {
          availableHeight = height - 2 * (yMin !== 0 || yMax !== 0 ? yMin + yMax : max$2(overflow.top, overflow.bottom));
        }
      }
      await apply({
        ...state,
        availableWidth,
        availableHeight
      });
      const nextDimensions = await platform.getDimensions(elements.floating);
      if (width !== nextDimensions.width || height !== nextDimensions.height) {
        return {
          reset: {
            rects: true
          }
        };
      }
      return {};
    }
  };
};

function hasWindow() {
  return typeof window !== 'undefined';
}
function getNodeName(node) {
  if (isNode(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  // Mocked nodes in testing environments may not be instances of Node. By
  // returning `#document` an infinite loop won't occur.
  // https://github.com/floating-ui/floating-ui/issues/2317
  return '#document';
}
function getWindow(node) {
  var _node$ownerDocument;
  return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
  var _ref;
  return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof Node || value instanceof getWindow(value).Node;
}
function isElement(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof Element || value instanceof getWindow(value).Element;
}
function isHTMLElement(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isShadowRoot(value) {
  if (!hasWindow() || typeof ShadowRoot === 'undefined') {
    return false;
  }
  return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle$1(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !['inline', 'contents'].includes(display);
}
function isTableElement(element) {
  return ['table', 'td', 'th'].includes(getNodeName(element));
}
function isTopLayer(element) {
  return [':popover-open', ':modal'].some(selector => {
    try {
      return element.matches(selector);
    } catch (e) {
      return false;
    }
  });
}
function isContainingBlock(elementOrCss) {
  const webkit = isWebKit();
  const css = isElement(elementOrCss) ? getComputedStyle$1(elementOrCss) : elementOrCss;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  // https://drafts.csswg.org/css-transforms-2/#individual-transforms
  return ['transform', 'translate', 'scale', 'rotate', 'perspective'].some(value => css[value] ? css[value] !== 'none' : false) || (css.containerType ? css.containerType !== 'normal' : false) || !webkit && (css.backdropFilter ? css.backdropFilter !== 'none' : false) || !webkit && (css.filter ? css.filter !== 'none' : false) || ['transform', 'translate', 'scale', 'rotate', 'perspective', 'filter'].some(value => (css.willChange || '').includes(value)) || ['paint', 'layout', 'strict', 'content'].some(value => (css.contain || '').includes(value));
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else if (isTopLayer(currentNode)) {
      return null;
    }
    currentNode = getParentNode(currentNode);
  }
  return null;
}
function isWebKit() {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('-webkit-backdrop-filter', 'none');
}
function isLastTraversableNode(node) {
  return ['html', 'body', '#document'].includes(getNodeName(node));
}
function getComputedStyle$1(element) {
  return getWindow(element).getComputedStyle(element);
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.scrollX,
    scrollTop: element.scrollY
  };
}
function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument ? node.ownerDocument.body : node.body;
  }
  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list, traverseIframes) {
  var _node$ownerDocument2;
  if (list === void 0) {
    list = [];
  }
  if (traverseIframes === void 0) {
    traverseIframes = true;
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
  const win = getWindow(scrollableAncestor);
  if (isBody) {
    const frameElement = getFrameElement(win);
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], frameElement && traverseIframes ? getOverflowAncestors(frameElement) : []);
  }
  return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
}
function getFrameElement(win) {
  return win.parent && Object.getPrototypeOf(win.parent) ? win.frameElement : null;
}

function getCssDimensions(element) {
  const css = getComputedStyle$1(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    $: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement(domElement)) {
    return createCoords(1);
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    $
  } = getCssDimensions(domElement);
  let x = ($ ? round(rect.width) : rect.width) / width;
  let y = ($ ? round(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = /*#__PURE__*/createCoords(0);
function getVisualOffsets(element) {
  const win = getWindow(element);
  if (!isWebKit() || !win.visualViewport) {
    return noOffsets;
  }
  return {
    x: win.visualViewport.offsetLeft,
    y: win.visualViewport.offsetTop
  };
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow(element)) {
    return false;
  }
  return isFixed;
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = createCoords(1);
  if (includeScale) {
    if (offsetParent) {
      if (isElement(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow(domElement);
    const offsetWin = offsetParent && isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
    let currentWin = win;
    let currentIFrame = getFrameElement(currentWin);
    while (currentIFrame && offsetParent && offsetWin !== currentWin) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle$1(currentIFrame);
      const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += left;
      y += top;
      currentWin = getWindow(currentIFrame);
      currentIFrame = getFrameElement(currentWin);
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

// If <html> has a CSS width greater than the viewport, then this will be
// incorrect for RTL.
function getWindowScrollBarX(element, rect) {
  const leftScroll = getNodeScroll(element).scrollLeft;
  if (!rect) {
    return getBoundingClientRect(getDocumentElement(element)).left + leftScroll;
  }
  return rect.left + leftScroll;
}

function getHTMLOffset(documentElement, scroll, ignoreScrollbarX) {
  if (ignoreScrollbarX === void 0) {
    ignoreScrollbarX = false;
  }
  const htmlRect = documentElement.getBoundingClientRect();
  const x = htmlRect.left + scroll.scrollLeft - (ignoreScrollbarX ? 0 :
  // RTL <body> scrollbar.
  getWindowScrollBarX(documentElement, htmlRect));
  const y = htmlRect.top + scroll.scrollTop;
  return {
    x,
    y
  };
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    elements,
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isFixed = strategy === 'fixed';
  const documentElement = getDocumentElement(offsetParent);
  const topLayer = elements ? isTopLayer(elements.floating) : false;
  if (offsetParent === documentElement || topLayer && isFixed) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = createCoords(1);
  const offsets = createCoords(0);
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll, true) : createCoords(0);
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x + htmlOffset.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y + htmlOffset.y
  };
}

function getClientRects(element) {
  return Array.from(element.getClientRects());
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max$2(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max$2(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle$1(body).direction === 'rtl') {
    x += max$2(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

function getViewportRect(element, strategy) {
  const win = getWindow(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isWebKit();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement(element) ? getScale(element) : createCoords(1);
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y,
      width: clippingAncestor.width,
      height: clippingAncestor.height
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle$1(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element, [], false).filter(el => isElement(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle$1(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle$1(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && ['absolute', 'fixed'].includes(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? isTopLayer(element) ? [] : getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max$2(rect.top, accRect.top);
    accRect.right = min$2(rect.right, accRect.right);
    accRect.bottom = min$2(rect.bottom, accRect.bottom);
    accRect.left = max$2(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}

function getDimensions(element) {
  const {
    width,
    height
  } = getCssDimensions(element);
  return {
    width,
    height
  };
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      // If the <body> scrollbar appears on the left (e.g. RTL systems). Use
      // Firefox with layout.scrollbar.side = 3 in about:config to test this.
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll) : createCoords(0);
  const x = rect.left + scroll.scrollLeft - offsets.x - htmlOffset.x;
  const y = rect.top + scroll.scrollTop - offsets.y - htmlOffset.y;
  return {
    x,
    y,
    width: rect.width,
    height: rect.height
  };
}

function isStaticPositioned(element) {
  return getComputedStyle$1(element).position === 'static';
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement(element) || getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  let rawOffsetParent = element.offsetParent;

  // Firefox returns the <html> element as the offsetParent if it's non-static,
  // while Chrome and Safari return the <body> element. The <body> element must
  // be used to perform the correct calculations even if the <html> element is
  // non-static.
  if (getDocumentElement(element) === rawOffsetParent) {
    rawOffsetParent = rawOffsetParent.ownerDocument.body;
  }
  return rawOffsetParent;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const win = getWindow(element);
  if (isTopLayer(element)) {
    return win;
  }
  if (!isHTMLElement(element)) {
    let svgOffsetParent = getParentNode(element);
    while (svgOffsetParent && !isLastTraversableNode(svgOffsetParent)) {
      if (isElement(svgOffsetParent) && !isStaticPositioned(svgOffsetParent)) {
        return svgOffsetParent;
      }
      svgOffsetParent = getParentNode(svgOffsetParent);
    }
    return win;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && isStaticPositioned(offsetParent)) {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && isLastTraversableNode(offsetParent) && isStaticPositioned(offsetParent) && !isContainingBlock(offsetParent)) {
    return win;
  }
  return offsetParent || getContainingBlock(element) || win;
}

const getElementRects = async function (data) {
  const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
  const getDimensionsFn = this.getDimensions;
  const floatingDimensions = await getDimensionsFn(data.floating);
  return {
    reference: getRectRelativeToOffsetParent(data.reference, await getOffsetParentFn(data.floating), data.strategy),
    floating: {
      x: 0,
      y: 0,
      width: floatingDimensions.width,
      height: floatingDimensions.height
    }
  };
};

function isRTL(element) {
  return getComputedStyle$1(element).direction === 'rtl';
}

const platform = {
  convertOffsetParentRelativeRectToViewportRelativeRect,
  getDocumentElement,
  getClippingRect,
  getOffsetParent,
  getElementRects,
  getClientRects,
  getDimensions,
  getScale,
  isElement,
  isRTL
};

function rectsAreEqual(a, b) {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

// https://samthor.au/2021/observing-dom/
function observeMove(element, onMove) {
  let io = null;
  let timeoutId;
  const root = getDocumentElement(element);
  function cleanup() {
    var _io;
    clearTimeout(timeoutId);
    (_io = io) == null || _io.disconnect();
    io = null;
  }
  function refresh(skip, threshold) {
    if (skip === void 0) {
      skip = false;
    }
    if (threshold === void 0) {
      threshold = 1;
    }
    cleanup();
    const elementRectForRootMargin = element.getBoundingClientRect();
    const {
      left,
      top,
      width,
      height
    } = elementRectForRootMargin;
    if (!skip) {
      onMove();
    }
    if (!width || !height) {
      return;
    }
    const insetTop = floor(top);
    const insetRight = floor(root.clientWidth - (left + width));
    const insetBottom = floor(root.clientHeight - (top + height));
    const insetLeft = floor(left);
    const rootMargin = -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px";
    const options = {
      rootMargin,
      threshold: max$2(0, min$2(1, threshold)) || 1
    };
    let isFirstUpdate = true;
    function handleObserve(entries) {
      const ratio = entries[0].intersectionRatio;
      if (ratio !== threshold) {
        if (!isFirstUpdate) {
          return refresh();
        }
        if (!ratio) {
          // If the reference is clipped, the ratio is 0. Throttle the refresh
          // to prevent an infinite loop of updates.
          timeoutId = setTimeout(() => {
            refresh(false, 1e-7);
          }, 1000);
        } else {
          refresh(false, ratio);
        }
      }
      if (ratio === 1 && !rectsAreEqual(elementRectForRootMargin, element.getBoundingClientRect())) {
        // It's possible that even though the ratio is reported as 1, the
        // element is not actually fully within the IntersectionObserver's root
        // area anymore. This can happen under performance constraints. This may
        // be a bug in the browser's IntersectionObserver implementation. To
        // work around this, we compare the element's bounding rect now with
        // what it was at the time we created the IntersectionObserver. If they
        // are not equal then the element moved, so we refresh.
        refresh();
      }
      isFirstUpdate = false;
    }

    // Older browsers don't support a `document` as the root and will throw an
    // error.
    try {
      io = new IntersectionObserver(handleObserve, {
        ...options,
        // Handle <iframe>s
        root: root.ownerDocument
      });
    } catch (e) {
      io = new IntersectionObserver(handleObserve, options);
    }
    io.observe(element);
  }
  refresh(true);
  return cleanup;
}

/**
 * Automatically updates the position of the floating element when necessary.
 * Should only be called when the floating element is mounted on the DOM or
 * visible on the screen.
 * @returns cleanup function that should be invoked when the floating element is
 * removed from the DOM or hidden from the screen.
 * @see https://floating-ui.com/docs/autoUpdate
 */
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll = true,
    ancestorResize = true,
    elementResize = typeof ResizeObserver === 'function',
    layoutShift = typeof IntersectionObserver === 'function',
    animationFrame = false
  } = options;
  const referenceEl = unwrapElement(reference);
  const ancestors = ancestorScroll || ancestorResize ? [...(referenceEl ? getOverflowAncestors(referenceEl) : []), ...getOverflowAncestors(floating)] : [];
  ancestors.forEach(ancestor => {
    ancestorScroll && ancestor.addEventListener('scroll', update, {
      passive: true
    });
    ancestorResize && ancestor.addEventListener('resize', update);
  });
  const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update) : null;
  let reobserveFrame = -1;
  let resizeObserver = null;
  if (elementResize) {
    resizeObserver = new ResizeObserver(_ref => {
      let [firstEntry] = _ref;
      if (firstEntry && firstEntry.target === referenceEl && resizeObserver) {
        // Prevent update loops when using the `size` middleware.
        // https://github.com/floating-ui/floating-ui/issues/1740
        resizeObserver.unobserve(floating);
        cancelAnimationFrame(reobserveFrame);
        reobserveFrame = requestAnimationFrame(() => {
          var _resizeObserver;
          (_resizeObserver = resizeObserver) == null || _resizeObserver.observe(floating);
        });
      }
      update();
    });
    if (referenceEl && !animationFrame) {
      resizeObserver.observe(referenceEl);
    }
    resizeObserver.observe(floating);
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && !rectsAreEqual(prevRefRect, nextRefRect)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    var _resizeObserver2;
    ancestors.forEach(ancestor => {
      ancestorScroll && ancestor.removeEventListener('scroll', update);
      ancestorResize && ancestor.removeEventListener('resize', update);
    });
    cleanupIo == null || cleanupIo();
    (_resizeObserver2 = resizeObserver) == null || _resizeObserver2.disconnect();
    resizeObserver = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset$1 = offset$2;

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift$1 = shift$2;

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip$1 = flip$2;

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size$1 = size$2;

/**
 * Provides data to hide the floating element in applicable situations, such as
 * when it is not in the same clipping context as the reference element.
 * @see https://floating-ui.com/docs/hide
 */
const hide$1 = hide$2;

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$2 = arrow$3;

/**
 * Built-in `limiter` that will stop `shift()` at a certain point.
 */
const limitShift$1 = limitShift$2;

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

var index$2 = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

// Fork of `fast-deep-equal` that only does the comparisons we need and compares
// functions
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'function' && a.toString() === b.toString()) {
    return true;
  }
  let length;
  let i;
  let keys;
  if (a && b && typeof a === 'object') {
    if (Array.isArray(a)) {
      length = a.length;
      if (length !== b.length) return false;
      for (i = length; i-- !== 0;) {
        if (!deepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = length; i-- !== 0;) {
      if (!{}.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }
    for (i = length; i-- !== 0;) {
      const key = keys[i];
      if (key === '_owner' && a.$$typeof) {
        continue;
      }
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return a !== a && b !== b;
}

function getDPR(element) {
  if (typeof window === 'undefined') {
    return 1;
  }
  const win = element.ownerDocument.defaultView || window;
  return win.devicePixelRatio || 1;
}

function roundByDPR(element, value) {
  const dpr = getDPR(element);
  return Math.round(value * dpr) / dpr;
}

function useLatestRef(value) {
  const ref = React.useRef(value);
  index$2(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Provides data to position a floating element.
 * @see https://floating-ui.com/docs/useFloating
 */
function useFloating$1(options) {
  if (options === void 0) {
    options = {};
  }
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform,
    elements: {
      reference: externalReference,
      floating: externalFloating
    } = {},
    transform = true,
    whileElementsMounted,
    open
  } = options;
  const [data, setData] = React.useState({
    x: 0,
    y: 0,
    strategy,
    placement,
    middlewareData: {},
    isPositioned: false
  });
  const [latestMiddleware, setLatestMiddleware] = React.useState(middleware);
  if (!deepEqual(latestMiddleware, middleware)) {
    setLatestMiddleware(middleware);
  }
  const [_reference, _setReference] = React.useState(null);
  const [_floating, _setFloating] = React.useState(null);
  const setReference = React.useCallback(node => {
    if (node !== referenceRef.current) {
      referenceRef.current = node;
      _setReference(node);
    }
  }, []);
  const setFloating = React.useCallback(node => {
    if (node !== floatingRef.current) {
      floatingRef.current = node;
      _setFloating(node);
    }
  }, []);
  const referenceEl = externalReference || _reference;
  const floatingEl = externalFloating || _floating;
  const referenceRef = React.useRef(null);
  const floatingRef = React.useRef(null);
  const dataRef = React.useRef(data);
  const hasWhileElementsMounted = whileElementsMounted != null;
  const whileElementsMountedRef = useLatestRef(whileElementsMounted);
  const platformRef = useLatestRef(platform);
  const openRef = useLatestRef(open);
  const update = React.useCallback(() => {
    if (!referenceRef.current || !floatingRef.current) {
      return;
    }
    const config = {
      placement,
      strategy,
      middleware: latestMiddleware
    };
    if (platformRef.current) {
      config.platform = platformRef.current;
    }
    computePosition(referenceRef.current, floatingRef.current, config).then(data => {
      const fullData = {
        ...data,
        // The floating element's position may be recomputed while it's closed
        // but still mounted (such as when transitioning out). To ensure
        // `isPositioned` will be `false` initially on the next open, avoid
        // setting it to `true` when `open === false` (must be specified).
        isPositioned: openRef.current !== false
      };
      if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
        dataRef.current = fullData;
        ReactDOM.flushSync(() => {
          setData(fullData);
        });
      }
    });
  }, [latestMiddleware, placement, strategy, platformRef, openRef]);
  index$2(() => {
    if (open === false && dataRef.current.isPositioned) {
      dataRef.current.isPositioned = false;
      setData(data => ({
        ...data,
        isPositioned: false
      }));
    }
  }, [open]);
  const isMountedRef = React.useRef(false);
  index$2(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  index$2(() => {
    if (referenceEl) referenceRef.current = referenceEl;
    if (floatingEl) floatingRef.current = floatingEl;
    if (referenceEl && floatingEl) {
      if (whileElementsMountedRef.current) {
        return whileElementsMountedRef.current(referenceEl, floatingEl, update);
      }
      update();
    }
  }, [referenceEl, floatingEl, update, whileElementsMountedRef, hasWhileElementsMounted]);
  const refs = React.useMemo(() => ({
    reference: referenceRef,
    floating: floatingRef,
    setReference,
    setFloating
  }), [setReference, setFloating]);
  const elements = React.useMemo(() => ({
    reference: referenceEl,
    floating: floatingEl
  }), [referenceEl, floatingEl]);
  const floatingStyles = React.useMemo(() => {
    const initialStyles = {
      position: strategy,
      left: 0,
      top: 0
    };
    if (!elements.floating) {
      return initialStyles;
    }
    const x = roundByDPR(elements.floating, data.x);
    const y = roundByDPR(elements.floating, data.y);
    if (transform) {
      return {
        ...initialStyles,
        transform: "translate(" + x + "px, " + y + "px)",
        ...(getDPR(elements.floating) >= 1.5 && {
          willChange: 'transform'
        })
      };
    }
    return {
      position: strategy,
      left: x,
      top: y
    };
  }, [strategy, transform, elements.floating, data.x, data.y]);
  return React.useMemo(() => ({
    ...data,
    update,
    refs,
    elements,
    floatingStyles
  }), [data, update, refs, elements, floatingStyles]);
}

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * This wraps the core `arrow` middleware to allow React refs as the element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$1 = options => {
  function isRef(value) {
    return {}.hasOwnProperty.call(value, 'current');
  }
  return {
    name: 'arrow',
    options,
    fn(state) {
      const {
        element,
        padding
      } = typeof options === 'function' ? options(state) : options;
      if (element && isRef(element)) {
        if (element.current != null) {
          return arrow$2({
            element: element.current,
            padding
          }).fn(state);
        }
        return {};
      }
      if (element) {
        return arrow$2({
          element,
          padding
        }).fn(state);
      }
      return {};
    }
  };
};

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset = (options, deps) => ({
  ...offset$1(options),
  options: [options, deps]
});

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift = (options, deps) => ({
  ...shift$1(options),
  options: [options, deps]
});

/**
 * Built-in `limiter` that will stop `shift()` at a certain point.
 */
const limitShift = (options, deps) => ({
  ...limitShift$1(options),
  options: [options, deps]
});

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip = (options, deps) => ({
  ...flip$1(options),
  options: [options, deps]
});

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size = (options, deps) => ({
  ...size$1(options),
  options: [options, deps]
});

/**
 * Provides data to hide the floating element in applicable situations, such as
 * when it is not in the same clipping context as the reference element.
 * @see https://floating-ui.com/docs/hide
 */
const hide = (options, deps) => ({
  ...hide$1(options),
  options: [options, deps]
});

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * This wraps the core `arrow` middleware to allow React refs as the element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow = (options, deps) => ({
  ...arrow$1(options),
  options: [options, deps]
});

// packages/react/arrow/src/arrow.tsx
var NAME$1 = "Arrow";
var Arrow$1 = React.forwardRef((props, forwardedRef) => {
  const { children, width = 10, height = 5, ...arrowProps } = props;
  return /* @__PURE__ */ jsx$1(
    Primitive.svg,
    {
      ...arrowProps,
      ref: forwardedRef,
      width,
      height,
      viewBox: "0 0 30 10",
      preserveAspectRatio: "none",
      children: props.asChild ? children : /* @__PURE__ */ jsx$1("polygon", { points: "0,0 30,0 15,10" })
    }
  );
});
Arrow$1.displayName = NAME$1;
var Root$2 = Arrow$1;

// packages/react/use-size/src/useSize.tsx
function useSize(element) {
  const [size, setSize] = React.useState(void 0);
  useLayoutEffect2(() => {
    if (element) {
      setSize({ width: element.offsetWidth, height: element.offsetHeight });
      const resizeObserver = new ResizeObserver((entries) => {
        if (!Array.isArray(entries)) {
          return;
        }
        if (!entries.length) {
          return;
        }
        const entry = entries[0];
        let width;
        let height;
        if ("borderBoxSize" in entry) {
          const borderSizeEntry = entry["borderBoxSize"];
          const borderSize = Array.isArray(borderSizeEntry) ? borderSizeEntry[0] : borderSizeEntry;
          width = borderSize["inlineSize"];
          height = borderSize["blockSize"];
        } else {
          width = element.offsetWidth;
          height = element.offsetHeight;
        }
        setSize({ width, height });
      });
      resizeObserver.observe(element, { box: "border-box" });
      return () => resizeObserver.unobserve(element);
    } else {
      setSize(void 0);
    }
  }, [element]);
  return size;
}

var POPPER_NAME = "Popper";
var [createPopperContext, createPopperScope] = createContextScope(POPPER_NAME);
var [PopperProvider, usePopperContext] = createPopperContext(POPPER_NAME);
var Popper = (props) => {
  const { __scopePopper, children } = props;
  const [anchor, setAnchor] = React.useState(null);
  return /* @__PURE__ */ jsx$1(PopperProvider, { scope: __scopePopper, anchor, onAnchorChange: setAnchor, children });
};
Popper.displayName = POPPER_NAME;
var ANCHOR_NAME$1 = "PopperAnchor";
var PopperAnchor = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopePopper, virtualRef, ...anchorProps } = props;
    const context = usePopperContext(ANCHOR_NAME$1, __scopePopper);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    React.useEffect(() => {
      context.onAnchorChange(virtualRef?.current || ref.current);
    });
    return virtualRef ? null : /* @__PURE__ */ jsx$1(Primitive.div, { ...anchorProps, ref: composedRefs });
  }
);
PopperAnchor.displayName = ANCHOR_NAME$1;
var CONTENT_NAME$5 = "PopperContent";
var [PopperContentProvider, useContentContext] = createPopperContext(CONTENT_NAME$5);
var PopperContent = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopePopper,
      side = "bottom",
      sideOffset = 0,
      align = "center",
      alignOffset = 0,
      arrowPadding = 0,
      avoidCollisions = true,
      collisionBoundary = [],
      collisionPadding: collisionPaddingProp = 0,
      sticky = "partial",
      hideWhenDetached = false,
      updatePositionStrategy = "optimized",
      onPlaced,
      ...contentProps
    } = props;
    const context = usePopperContext(CONTENT_NAME$5, __scopePopper);
    const [content, setContent] = React.useState(null);
    const composedRefs = useComposedRefs(forwardedRef, (node) => setContent(node));
    const [arrow$1, setArrow] = React.useState(null);
    const arrowSize = useSize(arrow$1);
    const arrowWidth = arrowSize?.width ?? 0;
    const arrowHeight = arrowSize?.height ?? 0;
    const desiredPlacement = side + (align !== "center" ? "-" + align : "");
    const collisionPadding = typeof collisionPaddingProp === "number" ? collisionPaddingProp : { top: 0, right: 0, bottom: 0, left: 0, ...collisionPaddingProp };
    const boundary = Array.isArray(collisionBoundary) ? collisionBoundary : [collisionBoundary];
    const hasExplicitBoundaries = boundary.length > 0;
    const detectOverflowOptions = {
      padding: collisionPadding,
      boundary: boundary.filter(isNotNull),
      // with `strategy: 'fixed'`, this is the only way to get it to respect boundaries
      altBoundary: hasExplicitBoundaries
    };
    const { refs, floatingStyles, placement, isPositioned, middlewareData } = useFloating$1({
      // default to `fixed` strategy so users don't have to pick and we also avoid focus scroll issues
      strategy: "fixed",
      placement: desiredPlacement,
      whileElementsMounted: (...args) => {
        const cleanup = autoUpdate(...args, {
          animationFrame: updatePositionStrategy === "always"
        });
        return cleanup;
      },
      elements: {
        reference: context.anchor
      },
      middleware: [
        offset({ mainAxis: sideOffset + arrowHeight, alignmentAxis: alignOffset }),
        avoidCollisions && shift({
          mainAxis: true,
          crossAxis: false,
          limiter: sticky === "partial" ? limitShift() : void 0,
          ...detectOverflowOptions
        }),
        avoidCollisions && flip({ ...detectOverflowOptions }),
        size({
          ...detectOverflowOptions,
          apply: ({ elements, rects, availableWidth, availableHeight }) => {
            const { width: anchorWidth, height: anchorHeight } = rects.reference;
            const contentStyle = elements.floating.style;
            contentStyle.setProperty("--radix-popper-available-width", `${availableWidth}px`);
            contentStyle.setProperty("--radix-popper-available-height", `${availableHeight}px`);
            contentStyle.setProperty("--radix-popper-anchor-width", `${anchorWidth}px`);
            contentStyle.setProperty("--radix-popper-anchor-height", `${anchorHeight}px`);
          }
        }),
        arrow$1 && arrow({ element: arrow$1, padding: arrowPadding }),
        transformOrigin({ arrowWidth, arrowHeight }),
        hideWhenDetached && hide({ strategy: "referenceHidden", ...detectOverflowOptions })
      ]
    });
    const [placedSide, placedAlign] = getSideAndAlignFromPlacement(placement);
    const handlePlaced = useCallbackRef$1(onPlaced);
    useLayoutEffect2(() => {
      if (isPositioned) {
        handlePlaced?.();
      }
    }, [isPositioned, handlePlaced]);
    const arrowX = middlewareData.arrow?.x;
    const arrowY = middlewareData.arrow?.y;
    const cannotCenterArrow = middlewareData.arrow?.centerOffset !== 0;
    const [contentZIndex, setContentZIndex] = React.useState();
    useLayoutEffect2(() => {
      if (content) setContentZIndex(window.getComputedStyle(content).zIndex);
    }, [content]);
    return /* @__PURE__ */ jsx$1(
      "div",
      {
        ref: refs.setFloating,
        "data-radix-popper-content-wrapper": "",
        style: {
          ...floatingStyles,
          transform: isPositioned ? floatingStyles.transform : "translate(0, -200%)",
          // keep off the page when measuring
          minWidth: "max-content",
          zIndex: contentZIndex,
          ["--radix-popper-transform-origin"]: [
            middlewareData.transformOrigin?.x,
            middlewareData.transformOrigin?.y
          ].join(" "),
          // hide the content if using the hide middleware and should be hidden
          // set visibility to hidden and disable pointer events so the UI behaves
          // as if the PopperContent isn't there at all
          ...middlewareData.hide?.referenceHidden && {
            visibility: "hidden",
            pointerEvents: "none"
          }
        },
        dir: props.dir,
        children: /* @__PURE__ */ jsx$1(
          PopperContentProvider,
          {
            scope: __scopePopper,
            placedSide,
            onArrowChange: setArrow,
            arrowX,
            arrowY,
            shouldHideArrow: cannotCenterArrow,
            children: /* @__PURE__ */ jsx$1(
              Primitive.div,
              {
                "data-side": placedSide,
                "data-align": placedAlign,
                ...contentProps,
                ref: composedRefs,
                style: {
                  ...contentProps.style,
                  // if the PopperContent hasn't been placed yet (not all measurements done)
                  // we prevent animations so that users's animation don't kick in too early referring wrong sides
                  animation: !isPositioned ? "none" : void 0
                }
              }
            )
          }
        )
      }
    );
  }
);
PopperContent.displayName = CONTENT_NAME$5;
var ARROW_NAME$4 = "PopperArrow";
var OPPOSITE_SIDE = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right"
};
var PopperArrow = React.forwardRef(function PopperArrow2(props, forwardedRef) {
  const { __scopePopper, ...arrowProps } = props;
  const contentContext = useContentContext(ARROW_NAME$4, __scopePopper);
  const baseSide = OPPOSITE_SIDE[contentContext.placedSide];
  return (
    // we have to use an extra wrapper because `ResizeObserver` (used by `useSize`)
    // doesn't report size as we'd expect on SVG elements.
    // it reports their bounding box which is effectively the largest path inside the SVG.
    /* @__PURE__ */ jsx$1(
      "span",
      {
        ref: contentContext.onArrowChange,
        style: {
          position: "absolute",
          left: contentContext.arrowX,
          top: contentContext.arrowY,
          [baseSide]: 0,
          transformOrigin: {
            top: "",
            right: "0 0",
            bottom: "center 0",
            left: "100% 0"
          }[contentContext.placedSide],
          transform: {
            top: "translateY(100%)",
            right: "translateY(50%) rotate(90deg) translateX(-50%)",
            bottom: `rotate(180deg)`,
            left: "translateY(50%) rotate(-90deg) translateX(50%)"
          }[contentContext.placedSide],
          visibility: contentContext.shouldHideArrow ? "hidden" : void 0
        },
        children: /* @__PURE__ */ jsx$1(
          Root$2,
          {
            ...arrowProps,
            ref: forwardedRef,
            style: {
              ...arrowProps.style,
              // ensures the element can be measured correctly (mostly for if SVG)
              display: "block"
            }
          }
        )
      }
    )
  );
});
PopperArrow.displayName = ARROW_NAME$4;
function isNotNull(value) {
  return value !== null;
}
var transformOrigin = (options) => ({
  name: "transformOrigin",
  options,
  fn(data) {
    const { placement, rects, middlewareData } = data;
    const cannotCenterArrow = middlewareData.arrow?.centerOffset !== 0;
    const isArrowHidden = cannotCenterArrow;
    const arrowWidth = isArrowHidden ? 0 : options.arrowWidth;
    const arrowHeight = isArrowHidden ? 0 : options.arrowHeight;
    const [placedSide, placedAlign] = getSideAndAlignFromPlacement(placement);
    const noArrowAlign = { start: "0%", center: "50%", end: "100%" }[placedAlign];
    const arrowXCenter = (middlewareData.arrow?.x ?? 0) + arrowWidth / 2;
    const arrowYCenter = (middlewareData.arrow?.y ?? 0) + arrowHeight / 2;
    let x = "";
    let y = "";
    if (placedSide === "bottom") {
      x = isArrowHidden ? noArrowAlign : `${arrowXCenter}px`;
      y = `${-arrowHeight}px`;
    } else if (placedSide === "top") {
      x = isArrowHidden ? noArrowAlign : `${arrowXCenter}px`;
      y = `${rects.floating.height + arrowHeight}px`;
    } else if (placedSide === "right") {
      x = `${-arrowHeight}px`;
      y = isArrowHidden ? noArrowAlign : `${arrowYCenter}px`;
    } else if (placedSide === "left") {
      x = `${rects.floating.width + arrowHeight}px`;
      y = isArrowHidden ? noArrowAlign : `${arrowYCenter}px`;
    }
    return { data: { x, y } };
  }
});
function getSideAndAlignFromPlacement(placement) {
  const [side, align = "center"] = placement.split("-");
  return [side, align];
}
var Root2$3 = Popper;
var Anchor = PopperAnchor;
var Content$1 = PopperContent;
var Arrow = PopperArrow;

// packages/react/visually-hidden/src/visually-hidden.tsx
var NAME = "VisuallyHidden";
var VisuallyHidden = React.forwardRef(
  (props, forwardedRef) => {
    return /* @__PURE__ */ jsx$1(
      Primitive.span,
      {
        ...props,
        ref: forwardedRef,
        style: {
          // See: https://github.com/twbs/bootstrap/blob/main/scss/mixins/_visually-hidden.scss
          position: "absolute",
          border: 0,
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          wordWrap: "normal",
          ...props.style
        }
      }
    );
  }
);
VisuallyHidden.displayName = NAME;
var Root$1 = VisuallyHidden;

var [createTooltipContext, createTooltipScope] = createContextScope("Tooltip", [
  createPopperScope
]);
var usePopperScope$1 = createPopperScope();
var PROVIDER_NAME = "TooltipProvider";
var DEFAULT_DELAY_DURATION = 700;
var TOOLTIP_OPEN = "tooltip.open";
var [TooltipProviderContextProvider, useTooltipProviderContext] = createTooltipContext(PROVIDER_NAME);
var TooltipProvider = (props) => {
  const {
    __scopeTooltip,
    delayDuration = DEFAULT_DELAY_DURATION,
    skipDelayDuration = 300,
    disableHoverableContent = false,
    children
  } = props;
  const [isOpenDelayed, setIsOpenDelayed] = React.useState(true);
  const isPointerInTransitRef = React.useRef(false);
  const skipDelayTimerRef = React.useRef(0);
  React.useEffect(() => {
    const skipDelayTimer = skipDelayTimerRef.current;
    return () => window.clearTimeout(skipDelayTimer);
  }, []);
  return /* @__PURE__ */ jsx$1(
    TooltipProviderContextProvider,
    {
      scope: __scopeTooltip,
      isOpenDelayed,
      delayDuration,
      onOpen: React.useCallback(() => {
        window.clearTimeout(skipDelayTimerRef.current);
        setIsOpenDelayed(false);
      }, []),
      onClose: React.useCallback(() => {
        window.clearTimeout(skipDelayTimerRef.current);
        skipDelayTimerRef.current = window.setTimeout(
          () => setIsOpenDelayed(true),
          skipDelayDuration
        );
      }, [skipDelayDuration]),
      isPointerInTransitRef,
      onPointerInTransitChange: React.useCallback((inTransit) => {
        isPointerInTransitRef.current = inTransit;
      }, []),
      disableHoverableContent,
      children
    }
  );
};
TooltipProvider.displayName = PROVIDER_NAME;
var TOOLTIP_NAME = "Tooltip";
var [TooltipContextProvider, useTooltipContext] = createTooltipContext(TOOLTIP_NAME);
var Tooltip$2 = (props) => {
  const {
    __scopeTooltip,
    children,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    disableHoverableContent: disableHoverableContentProp,
    delayDuration: delayDurationProp
  } = props;
  const providerContext = useTooltipProviderContext(TOOLTIP_NAME, props.__scopeTooltip);
  const popperScope = usePopperScope$1(__scopeTooltip);
  const [trigger, setTrigger] = React.useState(null);
  const contentId = useId$1();
  const openTimerRef = React.useRef(0);
  const disableHoverableContent = disableHoverableContentProp ?? providerContext.disableHoverableContent;
  const delayDuration = delayDurationProp ?? providerContext.delayDuration;
  const wasOpenDelayedRef = React.useRef(false);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: (open2) => {
      if (open2) {
        providerContext.onOpen();
        document.dispatchEvent(new CustomEvent(TOOLTIP_OPEN));
      } else {
        providerContext.onClose();
      }
      onOpenChange?.(open2);
    }
  });
  const stateAttribute = React.useMemo(() => {
    return open ? wasOpenDelayedRef.current ? "delayed-open" : "instant-open" : "closed";
  }, [open]);
  const handleOpen = React.useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = 0;
    wasOpenDelayedRef.current = false;
    setOpen(true);
  }, [setOpen]);
  const handleClose = React.useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = 0;
    setOpen(false);
  }, [setOpen]);
  const handleDelayedOpen = React.useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(() => {
      wasOpenDelayedRef.current = true;
      setOpen(true);
      openTimerRef.current = 0;
    }, delayDuration);
  }, [delayDuration, setOpen]);
  React.useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
        openTimerRef.current = 0;
      }
    };
  }, []);
  return /* @__PURE__ */ jsx$1(Root2$3, { ...popperScope, children: /* @__PURE__ */ jsx$1(
    TooltipContextProvider,
    {
      scope: __scopeTooltip,
      contentId,
      open,
      stateAttribute,
      trigger,
      onTriggerChange: setTrigger,
      onTriggerEnter: React.useCallback(() => {
        if (providerContext.isOpenDelayed) handleDelayedOpen();
        else handleOpen();
      }, [providerContext.isOpenDelayed, handleDelayedOpen, handleOpen]),
      onTriggerLeave: React.useCallback(() => {
        if (disableHoverableContent) {
          handleClose();
        } else {
          window.clearTimeout(openTimerRef.current);
          openTimerRef.current = 0;
        }
      }, [handleClose, disableHoverableContent]),
      onOpen: handleOpen,
      onClose: handleClose,
      disableHoverableContent,
      children
    }
  ) });
};
Tooltip$2.displayName = TOOLTIP_NAME;
var TRIGGER_NAME$3 = "TooltipTrigger";
var TooltipTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeTooltip, ...triggerProps } = props;
    const context = useTooltipContext(TRIGGER_NAME$3, __scopeTooltip);
    const providerContext = useTooltipProviderContext(TRIGGER_NAME$3, __scopeTooltip);
    const popperScope = usePopperScope$1(__scopeTooltip);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref, context.onTriggerChange);
    const isPointerDownRef = React.useRef(false);
    const hasPointerMoveOpenedRef = React.useRef(false);
    const handlePointerUp = React.useCallback(() => isPointerDownRef.current = false, []);
    React.useEffect(() => {
      return () => document.removeEventListener("pointerup", handlePointerUp);
    }, [handlePointerUp]);
    return /* @__PURE__ */ jsx$1(Anchor, { asChild: true, ...popperScope, children: /* @__PURE__ */ jsx$1(
      Primitive.button,
      {
        "aria-describedby": context.open ? context.contentId : void 0,
        "data-state": context.stateAttribute,
        ...triggerProps,
        ref: composedRefs,
        onPointerMove: composeEventHandlers(props.onPointerMove, (event) => {
          if (event.pointerType === "touch") return;
          if (!hasPointerMoveOpenedRef.current && !providerContext.isPointerInTransitRef.current) {
            context.onTriggerEnter();
            hasPointerMoveOpenedRef.current = true;
          }
        }),
        onPointerLeave: composeEventHandlers(props.onPointerLeave, () => {
          context.onTriggerLeave();
          hasPointerMoveOpenedRef.current = false;
        }),
        onPointerDown: composeEventHandlers(props.onPointerDown, () => {
          isPointerDownRef.current = true;
          document.addEventListener("pointerup", handlePointerUp, { once: true });
        }),
        onFocus: composeEventHandlers(props.onFocus, () => {
          if (!isPointerDownRef.current) context.onOpen();
        }),
        onBlur: composeEventHandlers(props.onBlur, context.onClose),
        onClick: composeEventHandlers(props.onClick, context.onClose)
      }
    ) });
  }
);
TooltipTrigger.displayName = TRIGGER_NAME$3;
var PORTAL_NAME$3 = "TooltipPortal";
var [PortalProvider$1, usePortalContext$1] = createTooltipContext(PORTAL_NAME$3, {
  forceMount: void 0
});
var TooltipPortal = (props) => {
  const { __scopeTooltip, forceMount, children, container } = props;
  const context = useTooltipContext(PORTAL_NAME$3, __scopeTooltip);
  return /* @__PURE__ */ jsx$1(PortalProvider$1, { scope: __scopeTooltip, forceMount, children: /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(Portal$4, { asChild: true, container, children }) }) });
};
TooltipPortal.displayName = PORTAL_NAME$3;
var CONTENT_NAME$4 = "TooltipContent";
var TooltipContent$1 = React.forwardRef(
  (props, forwardedRef) => {
    const portalContext = usePortalContext$1(CONTENT_NAME$4, props.__scopeTooltip);
    const { forceMount = portalContext.forceMount, side = "top", ...contentProps } = props;
    const context = useTooltipContext(CONTENT_NAME$4, props.__scopeTooltip);
    return /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: context.disableHoverableContent ? /* @__PURE__ */ jsx$1(TooltipContentImpl, { side, ...contentProps, ref: forwardedRef }) : /* @__PURE__ */ jsx$1(TooltipContentHoverable, { side, ...contentProps, ref: forwardedRef }) });
  }
);
var TooltipContentHoverable = React.forwardRef((props, forwardedRef) => {
  const context = useTooltipContext(CONTENT_NAME$4, props.__scopeTooltip);
  const providerContext = useTooltipProviderContext(CONTENT_NAME$4, props.__scopeTooltip);
  const ref = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, ref);
  const [pointerGraceArea, setPointerGraceArea] = React.useState(null);
  const { trigger, onClose } = context;
  const content = ref.current;
  const { onPointerInTransitChange } = providerContext;
  const handleRemoveGraceArea = React.useCallback(() => {
    setPointerGraceArea(null);
    onPointerInTransitChange(false);
  }, [onPointerInTransitChange]);
  const handleCreateGraceArea = React.useCallback(
    (event, hoverTarget) => {
      const currentTarget = event.currentTarget;
      const exitPoint = { x: event.clientX, y: event.clientY };
      const exitSide = getExitSideFromRect(exitPoint, currentTarget.getBoundingClientRect());
      const paddedExitPoints = getPaddedExitPoints(exitPoint, exitSide);
      const hoverTargetPoints = getPointsFromRect(hoverTarget.getBoundingClientRect());
      const graceArea = getHull([...paddedExitPoints, ...hoverTargetPoints]);
      setPointerGraceArea(graceArea);
      onPointerInTransitChange(true);
    },
    [onPointerInTransitChange]
  );
  React.useEffect(() => {
    return () => handleRemoveGraceArea();
  }, [handleRemoveGraceArea]);
  React.useEffect(() => {
    if (trigger && content) {
      const handleTriggerLeave = (event) => handleCreateGraceArea(event, content);
      const handleContentLeave = (event) => handleCreateGraceArea(event, trigger);
      trigger.addEventListener("pointerleave", handleTriggerLeave);
      content.addEventListener("pointerleave", handleContentLeave);
      return () => {
        trigger.removeEventListener("pointerleave", handleTriggerLeave);
        content.removeEventListener("pointerleave", handleContentLeave);
      };
    }
  }, [trigger, content, handleCreateGraceArea, handleRemoveGraceArea]);
  React.useEffect(() => {
    if (pointerGraceArea) {
      const handleTrackPointerGrace = (event) => {
        const target = event.target;
        const pointerPosition = { x: event.clientX, y: event.clientY };
        const hasEnteredTarget = trigger?.contains(target) || content?.contains(target);
        const isPointerOutsideGraceArea = !isPointInPolygon$1(pointerPosition, pointerGraceArea);
        if (hasEnteredTarget) {
          handleRemoveGraceArea();
        } else if (isPointerOutsideGraceArea) {
          handleRemoveGraceArea();
          onClose();
        }
      };
      document.addEventListener("pointermove", handleTrackPointerGrace);
      return () => document.removeEventListener("pointermove", handleTrackPointerGrace);
    }
  }, [trigger, content, pointerGraceArea, onClose, handleRemoveGraceArea]);
  return /* @__PURE__ */ jsx$1(TooltipContentImpl, { ...props, ref: composedRefs });
});
var [VisuallyHiddenContentContextProvider, useVisuallyHiddenContentContext] = createTooltipContext(TOOLTIP_NAME, { isInside: false });
var TooltipContentImpl = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopeTooltip,
      children,
      "aria-label": ariaLabel,
      onEscapeKeyDown,
      onPointerDownOutside,
      ...contentProps
    } = props;
    const context = useTooltipContext(CONTENT_NAME$4, __scopeTooltip);
    const popperScope = usePopperScope$1(__scopeTooltip);
    const { onClose } = context;
    React.useEffect(() => {
      document.addEventListener(TOOLTIP_OPEN, onClose);
      return () => document.removeEventListener(TOOLTIP_OPEN, onClose);
    }, [onClose]);
    React.useEffect(() => {
      if (context.trigger) {
        const handleScroll = (event) => {
          const target = event.target;
          if (target?.contains(context.trigger)) onClose();
        };
        window.addEventListener("scroll", handleScroll, { capture: true });
        return () => window.removeEventListener("scroll", handleScroll, { capture: true });
      }
    }, [context.trigger, onClose]);
    return /* @__PURE__ */ jsx$1(
      DismissableLayer,
      {
        asChild: true,
        disableOutsidePointerEvents: false,
        onEscapeKeyDown,
        onPointerDownOutside,
        onFocusOutside: (event) => event.preventDefault(),
        onDismiss: onClose,
        children: /* @__PURE__ */ jsxs(
          Content$1,
          {
            "data-state": context.stateAttribute,
            ...popperScope,
            ...contentProps,
            ref: forwardedRef,
            style: {
              ...contentProps.style,
              // re-namespace exposed content custom properties
              ...{
                "--radix-tooltip-content-transform-origin": "var(--radix-popper-transform-origin)",
                "--radix-tooltip-content-available-width": "var(--radix-popper-available-width)",
                "--radix-tooltip-content-available-height": "var(--radix-popper-available-height)",
                "--radix-tooltip-trigger-width": "var(--radix-popper-anchor-width)",
                "--radix-tooltip-trigger-height": "var(--radix-popper-anchor-height)"
              }
            },
            children: [
              /* @__PURE__ */ jsx$1(Slottable, { children }),
              /* @__PURE__ */ jsx$1(VisuallyHiddenContentContextProvider, { scope: __scopeTooltip, isInside: true, children: /* @__PURE__ */ jsx$1(Root$1, { id: context.contentId, role: "tooltip", children: ariaLabel || children }) })
            ]
          }
        )
      }
    );
  }
);
TooltipContent$1.displayName = CONTENT_NAME$4;
var ARROW_NAME$3 = "TooltipArrow";
var TooltipArrow = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeTooltip, ...arrowProps } = props;
    const popperScope = usePopperScope$1(__scopeTooltip);
    const visuallyHiddenContentContext = useVisuallyHiddenContentContext(
      ARROW_NAME$3,
      __scopeTooltip
    );
    return visuallyHiddenContentContext.isInside ? null : /* @__PURE__ */ jsx$1(Arrow, { ...popperScope, ...arrowProps, ref: forwardedRef });
  }
);
TooltipArrow.displayName = ARROW_NAME$3;
function getExitSideFromRect(point, rect) {
  const top = Math.abs(rect.top - point.y);
  const bottom = Math.abs(rect.bottom - point.y);
  const right = Math.abs(rect.right - point.x);
  const left = Math.abs(rect.left - point.x);
  switch (Math.min(top, bottom, right, left)) {
    case left:
      return "left";
    case right:
      return "right";
    case top:
      return "top";
    case bottom:
      return "bottom";
    default:
      throw new Error("unreachable");
  }
}
function getPaddedExitPoints(exitPoint, exitSide, padding = 5) {
  const paddedExitPoints = [];
  switch (exitSide) {
    case "top":
      paddedExitPoints.push(
        { x: exitPoint.x - padding, y: exitPoint.y + padding },
        { x: exitPoint.x + padding, y: exitPoint.y + padding }
      );
      break;
    case "bottom":
      paddedExitPoints.push(
        { x: exitPoint.x - padding, y: exitPoint.y - padding },
        { x: exitPoint.x + padding, y: exitPoint.y - padding }
      );
      break;
    case "left":
      paddedExitPoints.push(
        { x: exitPoint.x + padding, y: exitPoint.y - padding },
        { x: exitPoint.x + padding, y: exitPoint.y + padding }
      );
      break;
    case "right":
      paddedExitPoints.push(
        { x: exitPoint.x - padding, y: exitPoint.y - padding },
        { x: exitPoint.x - padding, y: exitPoint.y + padding }
      );
      break;
  }
  return paddedExitPoints;
}
function getPointsFromRect(rect) {
  const { top, right, bottom, left } = rect;
  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom }
  ];
}
function isPointInPolygon$1(point, polygon) {
  const { x, y } = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect = yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
function getHull(points) {
  const newPoints = points.slice();
  newPoints.sort((a, b) => {
    if (a.x < b.x) return -1;
    else if (a.x > b.x) return 1;
    else if (a.y < b.y) return -1;
    else if (a.y > b.y) return 1;
    else return 0;
  });
  return getHullPresorted(newPoints);
}
function getHullPresorted(points) {
  if (points.length <= 1) return points.slice();
  const upperHull = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    while (upperHull.length >= 2) {
      const q = upperHull[upperHull.length - 1];
      const r = upperHull[upperHull.length - 2];
      if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x)) upperHull.pop();
      else break;
    }
    upperHull.push(p);
  }
  upperHull.pop();
  const lowerHull = [];
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    while (lowerHull.length >= 2) {
      const q = lowerHull[lowerHull.length - 1];
      const r = lowerHull[lowerHull.length - 2];
      if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x)) lowerHull.pop();
      else break;
    }
    lowerHull.push(p);
  }
  lowerHull.pop();
  if (upperHull.length === 1 && lowerHull.length === 1 && upperHull[0].x === lowerHull[0].x && upperHull[0].y === lowerHull[0].y) {
    return upperHull;
  } else {
    return upperHull.concat(lowerHull);
  }
}
var Provider = TooltipProvider;
var Root3$1 = Tooltip$2;
var Trigger$3 = TooltipTrigger;
var Portal$2 = TooltipPortal;
var Content2$3 = TooltipContent$1;
var Arrow2$1 = TooltipArrow;

var css_248z$W = ".Tooltip-module_trigger__GqTfV {\n  width: fit-content;\n  max-width: 100%;\n  text-overflow: ellipsis;\n  overflow: hidden;\n}\n\n.Tooltip-module_content__wCATw {\n  border-radius: 4px;\n  padding: 15px;\n  font-size: var(--font-size-s);\n  color: var(--tooltip-font-color);\n  background-color: var(--tooltip-background-color);\n  box-shadow: 0 12px 34px rgb(15 15 47 / 6%);\n  will-change: transform, opacity;\n  z-index: 100;\n  white-space: pre-line;\n}\n\n.Tooltip-module_content__wCATw > div:first-child > p:first-child {\n  padding-top: 10px;\n}\n\n.Tooltip-module_arrow__Mq0TT {\n  fill: var(--tooltip-background-color);\n}\n\n@media (prefers-reduced-motion: no-preference) {\n  .Tooltip-module_content__wCATw {\n    animation-duration: 400ms;\n    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);\n    animation-fill-mode: forwards;\n    animation-name: Tooltip-module_slide-down-and-fade__nVfBL;\n  }\n}\n\n@keyframes Tooltip-module_slide-down-and-fade__nVfBL {\n  0% {\n    opacity: 0;\n    transform: translateY(-2px);\n  }\n\n  100% {\n    opacity: 1;\n    transform: translateY(0);\n  }\n}\n";
var styles$U = {"trigger":"Tooltip-module_trigger__GqTfV","content":"Tooltip-module_content__wCATw","arrow":"Tooltip-module_arrow__Mq0TT"};
styleInject(css_248z$W);

/*
https://stackoverflow.com/a/56490656
 */
var TooltipContent = forwardRef(
// eslint-disable-next-line react/prop-types
function (_a, ref) {
    var content = _a.content, arrowOffset = _a.arrowOffset, className = _a.className;
    var classes = classNames$1(styles$U.content, className);
    return (jsxs(Content2$3, { ref: ref, className: classes, children: [content, jsx$1(Arrow2$1, { className: styles$U.arrow, offset: arrowOffset })] }));
});
TooltipContent.displayName = 'TooltipContent';

/**
 *
 * Tooltip:
 * A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.
 *
 * Uses radix-ui as base component:
 * https://www.radix-ui.com/docs/primitives/components/tooltip
 *
 * @param children
 * @param content
 * @param {number} delayDuration The duration from when the mouse enters a tooltip trigger until the tooltip opens.
 * @param {number} skipDelayDuration How much time a user has to enter another trigger without incurring a delay again.
 * @param {number} arrowOffset The distance in pixels to render from the Tooltip.Content edge.
 *
 * @param className
 * @param {boolean} usePortal
 */
var Tooltip = function (_a, ref) {
    var children = _a.children, content = _a.content, _b = _a.delayDuration, delayDuration = _b === void 0 ? 0 : _b, _c = _a.skipDelayDuration, skipDelayDuration = _c === void 0 ? 0 : _c, _d = _a.arrowOffset, arrowOffset = _d === void 0 ? 10 : _d, triggerClass = _a.triggerClass, triggerStyle = _a.triggerStyle, usePortal = _a.usePortal, className = _a.className;
    var classes = classNames$1(styles$U.tooltip, className);
    var triggerClasses = classNames$1(styles$U.trigger, triggerClass);
    return (jsx$1(Provider, { skipDelayDuration: skipDelayDuration, children: jsxs(Root3$1, { delayDuration: delayDuration, children: [jsx$1(Trigger$3, { className: triggerClasses, style: triggerStyle, asChild: true, children: jsx$1("div", { children: children }) }), usePortal ? (jsx$1(Portal$2, { children: jsx$1(TooltipContent, { ref: ref, className: classes, content: content, arrowOffset: arrowOffset }) })) : (jsx$1(TooltipContent, { content: content, arrowOffset: arrowOffset }))] }) }));
};
var Tooltip$1 = forwardRef(Tooltip);

var ButtonGroup = function (_a, ref) {
    var _b;
    var isSmall = _a.isSmall, disabled = _a.disabled, loading = _a.loading, tooltip = _a.tooltip, children = _a.children, className = _a.className, other = __rest(_a, ["isSmall", "disabled", "loading", "tooltip", "children", "className"]);
    var classes = classNames$1(styles$W['btn-group'], (_b = {}, _b[styles$W.small] = isSmall, _b[styles$W.disabled] = disabled, _b), className);
    return tooltip ? (jsx$1(Tooltip$1, { triggerClass: styles$W.tooltip, content: tooltip, children: jsxs("div", __assign({ className: classes }, other, { ref: ref, children: [loading && (jsx$1(LazyLoader$1, { className: styles$W.loader, embedded: true, size: 'm', noPadding: true })), children] })) })) : (jsxs("div", __assign({ className: classes }, other, { ref: ref, children: [loading && (jsx$1(LazyLoader$1, { className: styles$W.loader, embedded: true, size: 'm', noPadding: true })), children] })));
};
var ButtonGroup$1 = forwardRef(ButtonGroup);

var css_248z$V = ".Badge-module_badge-root__E0Hnf {\n  position: relative;\n  display: inline-flex;\n  vertical-align: middle;\n  flex-shrink: 0;\n}\n\n.Badge-module_badge__KLFHM {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: var(--font-size-xs);\n  min-width: var(--badge-height);\n  max-width: 32px;\n  line-height: 1;\n  z-index: 1;\n  color: var(--color-white);\n  background-color: var(--badge-bg-color);\n  border-radius: calc(var(--badge-height) * 0.5);\n  padding: 0 6px;\n  position: absolute;\n  overflow: hidden;\n  transform: scale(1) translate(50%, -50%);\n  transform-origin: 100% 0;\n  height: var(--badge-height);\n  border: 1px solid var(--badge-border-color);\n  transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;\n}\n\n.Badge-module_top-right__8Fi6f {\n  top: 0;\n  right: 0;\n  transform: scale(1) translate(50%, -50%);\n  transform-origin: 100% 0;\n}\n\n.Badge-module_top-left__BA3jV {\n  top: 0;\n  left: 0;\n  transform: scale(1) translate(-50%, -50%);\n  transform-origin: 0 0;\n}\n\n.Badge-module_bottom-right__tMkSK {\n  bottom: 0;\n  right: 0;\n  transform: scale(1) translate(50%, 50%);\n  transform-origin: 100% 100%;\n}\n\n.Badge-module_bottom-left__t4-kh {\n  bottom: 0;\n  left: 0;\n  transform: scale(1) translate(-50%, 50%);\n  transform-origin: 0 100%;\n}\n\n.Badge-module_hidden__Kvogk {\n  transform: scale(0);\n}\n";
var styles$T = {"badge-root":"Badge-module_badge-root__E0Hnf","badge":"Badge-module_badge__KLFHM","top-right":"Badge-module_top-right__8Fi6f","top-left":"Badge-module_top-left__BA3jV","bottom-right":"Badge-module_bottom-right__tMkSK","bottom-left":"Badge-module_bottom-left__t4-kh","hidden":"Badge-module_hidden__Kvogk"};
styleInject(css_248z$V);

var Badge = function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var children = _a.children, content = _a.content, className = _a.className, vertical = _a.vertical, horizontal = _a.horizontal, _g = _a.hidden, hidden = _g === void 0 ? false : _g, color = _a.color;
    var badgeRootClass = classNames$1(styles$T['badge-root'], className);
    var badgeClass = classNames$1(styles$T.badge, (_b = {},
        _b[styles$T['top-right']] = (vertical === 'top' || !vertical) &&
            (horizontal === 'right' || !horizontal),
        _b), (_c = {},
        _c[styles$T['top-left']] = (vertical === 'top' || !vertical) && horizontal === 'left',
        _c), (_d = {},
        _d[styles$T['bottom-right']] = vertical === 'bottom' && (horizontal === 'right' || !horizontal),
        _d), (_e = {},
        _e[styles$T['bottom-left']] = vertical === 'bottom' && horizontal === 'left',
        _e), (_f = {}, _f[styles$T.hidden] = hidden, _f));
    return (jsxs("span", { ref: ref, className: badgeRootClass, children: [children, jsx$1("span", __assign({ className: badgeClass }, (color && { style: { backgroundColor: color } }), { children: content }))] }));
};
var Badge$1 = forwardRef(Badge);

var css_248z$U = ".BadgeCard-module_badge-card-wrapper__8LJIs {\n  height: var(--badge-card-height);\n}\n\n.BadgeCard-module_badge-card__y9NDf {\n  height: 100%;\n  width: 100%;\n  font-size: var(--font-size-xs);\n  color: var(--color-white);\n  background-color: var(--badge-card-color);\n  padding: 5px 6px;\n  position: relative;\n}\n\n.BadgeCard-module_background-wrapper__-oXyB {\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  overflow: hidden;\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_has-hover__X3Toc {\n  cursor: pointer;\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_is-rounded__fwgzK {\n  border-radius: var(--badge-card-border-radius);\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_is-rounded__fwgzK .BadgeCard-module_badge-card__y9NDf {\n  border-radius: var(--badge-card-border-radius);\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_is-rounded__fwgzK .BadgeCard-module_background-wrapper__-oXyB {\n  border-radius: var(--badge-card-border-radius);\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_full-height__gkcBF {\n  height: 100%;\n}\n\n.BadgeCard-module_fixed-width-1__QvgAu {\n  width: var(--badge-card-width);\n}\n\n.BadgeCard-module_fixed-width-2__jWqkA {\n  width: calc(var(--badge-card-width) + var(--badge-card-grow-factor));\n}\n\n.BadgeCard-module_fixed-width-3__ZOCMI {\n  width: calc(var(--badge-card-width) + var(--badge-card-grow-factor) * 2);\n}\n\n.BadgeCard-module_fixed-width-4__tKdHm {\n  width: calc(var(--badge-card-width) + var(--badge-card-grow-factor) * 3);\n}\n\n.BadgeCard-module_fixed-width-5__EJgsP {\n  width: calc(var(--badge-card-width) + var(--badge-card-grow-factor) * 4);\n}\n\n.BadgeCard-module_fixed-width-6__zyoJt {\n  width: calc(var(--badge-card-width) + var(--badge-card-grow-factor) * 5);\n}\n\n.BadgeCard-module_custom-badge__MHEKi {\n  position: absolute;\n  top: -8px;\n  left: -10px;\n  padding: 0 4px;\n  height: 16px;\n  max-width: 30px;\n  border: 1px solid var(--color-white);\n  border-radius: 8px;\n  text-align: center;\n  overflow: hidden;\n}\n\n.BadgeCard-module_label__SljrT {\n  position: inherit;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  z-index: 2;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.BadgeCard-module_badge-card-wrapper__8LJIs.BadgeCard-module_is-loading__uZGZJ .BadgeCard-module_label__SljrT {\n  justify-content: center;\n}\n\n.BadgeCard-module_label__SljrT .BadgeCard-module_spinning__O9zkV {\n  width: 18px;\n  height: 18px;\n  animation: BadgeCard-module_spinning__O9zkV 2.5s linear infinite;\n}\n\n.BadgeCard-module_background-row__yHJGJ {\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: calc(var(--badge-card-width) / 2 * -1);\n  right: calc(var(--badge-card-width) / 2 * -1);\n}\n\n.BadgeCard-module_background-column__eJv5M {\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  transform: skew(-45deg);\n}\n\n@keyframes BadgeCard-module_spinning__O9zkV {\n  from {\n    transform: rotate(0deg);\n  }\n\n  to {\n    transform: rotate(360deg);\n  }\n}\n";
var styles$S = {"badge-card-wrapper":"BadgeCard-module_badge-card-wrapper__8LJIs","badge-card":"BadgeCard-module_badge-card__y9NDf","background-wrapper":"BadgeCard-module_background-wrapper__-oXyB","has-hover":"BadgeCard-module_has-hover__X3Toc","is-rounded":"BadgeCard-module_is-rounded__fwgzK","full-height":"BadgeCard-module_full-height__gkcBF","fixed-width-1":"BadgeCard-module_fixed-width-1__QvgAu","fixed-width-2":"BadgeCard-module_fixed-width-2__jWqkA","fixed-width-3":"BadgeCard-module_fixed-width-3__ZOCMI","fixed-width-4":"BadgeCard-module_fixed-width-4__tKdHm","fixed-width-5":"BadgeCard-module_fixed-width-5__EJgsP","fixed-width-6":"BadgeCard-module_fixed-width-6__zyoJt","custom-badge":"BadgeCard-module_custom-badge__MHEKi","label":"BadgeCard-module_label__SljrT","is-loading":"BadgeCard-module_is-loading__uZGZJ","spinning":"BadgeCard-module_spinning__O9zkV","background-row":"BadgeCard-module_background-row__yHJGJ","background-column":"BadgeCard-module_background-column__eJv5M"};
styleInject(css_248z$U);

var BadgeCard = function (_a, ref) {
    var _b;
    var label = _a.label, _c = _a.width, width = _c === void 0 ? 1 : _c, _d = _a.count, count = _d === void 0 ? 1 : _d, _e = _a.color, color = _e === void 0 ? 'var(--color-white)' : _e, _f = _a.badgeColor, badgeColor = _f === void 0 ? 'var(--color-white)' : _f, _g = _a.backgroundColor, backgroundColor = _g === void 0 ? 'var(--color-grey-light)' : _g, _h = _a.badgeBackgroundColor, badgeBackgroundColor = _h === void 0 ? 'var(--color-grey-light)' : _h, rows = _a.rows, _j = _a.showHover, showHover = _j === void 0 ? false : _j, _k = _a.showGap, showGap = _k === void 0 ? false : _k, _l = _a.gapSize, gapSize = _l === void 0 ? 3 : _l, _m = _a.showRounded, showRounded = _m === void 0 ? true : _m, _o = _a.fullHeight, fullHeight = _o === void 0 ? false : _o, _p = _a.isLoading, isLoading = _p === void 0 ? false : _p, className = _a.className, rest = __rest(_a, ["label", "width", "count", "color", "badgeColor", "backgroundColor", "badgeBackgroundColor", "rows", "showHover", "showGap", "gapSize", "showRounded", "fullHeight", "isLoading", "className"]);
    function getRowStyle(row, rowCount, index) {
        var fraction = 100 / rowCount;
        var top = fraction * index;
        var bottom;
        if (index + 1 === rowCount)
            bottom = 0;
        else if (index === 0)
            bottom = showGap ? 100 - fraction + gapSize : 100 - fraction;
        else
            bottom = showGap
                ? 100 - fraction * (index + 1) + gapSize
                : 100 - fraction * (index + 1);
        return {
            backgroundColor: row.rowColor,
            clipPath: "inset(".concat(top, "% 0 ").concat(bottom, "%)"),
        };
    }
    function getColumnStyle(color, colCount, index) {
        var fraction = 100 / colCount;
        var left, right;
        if (index === 0) {
            left = -fraction;
            right = 100 - fraction;
        }
        else if (index === colCount - 1) {
            left = 100 - fraction;
            right = -fraction;
        }
        else {
            left = fraction * index;
            right = 100 - fraction * (index + 1);
        }
        // prevent color bleeding
        left -= 1;
        right -= 1;
        return {
            backgroundColor: color,
            left: "".concat(left === 0 ? left : left + '%'),
            right: "".concat(right === 0 ? right : right + '%'),
        };
    }
    var wrapperClasses = classNames$1(styles$S['badge-card-wrapper'], (_b = {},
        _b[styles$S['fixed-width-' + width]] = width,
        _b[styles$S['has-hover']] = showHover && !isLoading,
        _b[styles$S['has-gap']] = showGap,
        _b[styles$S['is-rounded']] = showRounded,
        _b[styles$S['full-height']] = fullHeight,
        _b[styles$S['is-loading']] = isLoading,
        _b), className);
    return (jsx$1("div", __assign({ ref: ref, className: wrapperClasses }, rest, { children: jsxs("div", { className: styles$S['badge-card'], style: {
                color: color,
                backgroundColor: backgroundColor,
            }, children: [jsx$1("div", { className: styles$S.label, children: isLoading ? jsx$1(SvgSpinnerIcon, { className: styles$S.spinning }) : label }), rows && (jsx$1("div", { className: styles$S['background-wrapper'], style: { backgroundColor: backgroundColor }, children: rows.map(function (row, rowIndex) {
                        return (jsx$1("div", { className: styles$S['background-row'], style: getRowStyle(row, rows.length, rowIndex), title: row.label ? row.label : undefined, children: row.colColors &&
                                row.colColors.map(function (color, colIndex) {
                                    return (jsx$1("div", { className: styles$S['background-column'], style: getColumnStyle(color, row.colColors.length, colIndex) }, colIndex));
                                }) }, rowIndex));
                    }) })), count > 1 && (jsx$1("div", { className: styles$S['custom-badge'], style: { backgroundColor: badgeBackgroundColor, color: badgeColor }, children: count }))] }) })));
};
var BadgeCard$1 = forwardRef(BadgeCard);

var Breadcrumbs = function (_a) {
    var indexLink = _a.indexLink, indexTitle = _a.indexTitle;
    var params = useParams();
    return (jsx$1(Fragment, { children: params['*'] && (jsxs(Fragment, { children: [jsx$1(Link, { to: indexLink, children: indexTitle }), " /", jsx$1("strong", { children: " \u00DCberlega" })] })) }));
};

var css_248z$T = ".Burger-module_burger__30Kz0 {\n  position: fixed;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  top: 16px;\n  right: 16px;\n  height: 56px;\n  width: 56px;\n  z-index: var(--z-index-burger);\n  background-color: var(--color-white);\n  box-shadow: var(--box-shadow);\n  border-radius: 6px;\n}\n\n@media (width >= 580px) {\n  .Burger-module_burger__30Kz0 {\n    display: none;\n  }\n}\n";
var styles$R = {"burger":"Burger-module_burger__30Kz0"};
styleInject(css_248z$T);

var Burger = function (_a, ref) {
    var _b;
    var setExpandedSidebar = _a.setExpandedSidebar, expandedSidebar = _a.expandedSidebar, className = _a.className;
    var classes = classNames$1(styles$R.burger, (_b = {},
        _b[styles$R.extended] = expandedSidebar,
        _b), className);
    return (jsx$1("div", { ref: ref, className: classes, onClick: function () { return setExpandedSidebar(!expandedSidebar); }, children: expandedSidebar ? (jsx$1(SvgCloseIcon, { className: 'large' })) : (jsx$1(SvgDotsVerticalIcon, { className: 'large' })) }));
};
var Burger$1 = forwardRef(Burger);

var css_248z$S = ".Card-module_card__TXz08 {\n  width: 100%;\n  background-color: var(--color-white);\n  border-radius: var(--card-border-radius);\n  box-shadow: var(--card-box-shadow);\n  overflow: hidden;\n}\n\n.Card-module_card__TXz08 .Card-module_header-wrapper__QQzUU {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: var(--spacing-10);\n  margin-bottom: var(--spacing-4);\n}\n\n.Card-module_card__TXz08 .Card-module_content__mCsiE {\n  max-height: calc(100% - var(--spacing-10));\n}\n\n.Card-module_card__TXz08.Card-module_is-collapsible__G6mXU {\n  overflow: hidden;\n}\n\n.Card-module_card__TXz08.Card-module_is-collapsed__ZcSJD .Card-module_content__mCsiE {\n  max-height: 0;\n  padding-bottom: 0;\n}\n\n.Card-module_card__TXz08.Card-module_padding-s__LoqKU .Card-module_content__mCsiE {\n  padding: 0 var(--spacing-4) var(--spacing-3) var(--spacing-4);\n}\n\n.Card-module_card__TXz08.Card-module_padding-m__OGsRs .Card-module_content__mCsiE {\n  padding: 0 var(--spacing-6) var(--spacing-5) var(--spacing-6);\n}\n\n.Card-module_card__TXz08.Card-module_padding-l__oAOaz .Card-module_content__mCsiE {\n  padding: 0 var(--spacing-8) var(--spacing-7) var(--spacing-8);\n}\n\n.Card-module_card__TXz08.Card-module_padding-none__oMmnl .Card-module_content__mCsiE {\n  padding: 0;\n}\n\n.Card-module_card__TXz08 .Card-module_content__mCsiE:empty {\n  padding: 0;\n}\n\n.Card-module_card__TXz08 .Card-module_header__d0d-P {\n  width: 100%;\n  padding: 0 var(--spacing-6);\n}\n\n.Card-module_card__TXz08 .Card-module_header__d0d-P.Card-module_with-divider__OhspK {\n  border-bottom: 1px solid var(--color-grey-lighter);\n}\n\n.Card-module_card__TXz08.Card-module_fit-parent__IItQn {\n  height: 100%;\n}\n\n.Card-module_card__TXz08.Card-module_fit-parent__IItQn .Card-module_content__mCsiE {\n  height: 100%;\n}\n\n.Card-module_card__TXz08.Card-module_has-scroll__FoKik .Card-module_content__mCsiE {\n  overflow-y: auto;\n}\n\n.Card-module_card__TXz08.Card-module_no-header__Faodo .Card-module_content__mCsiE {\n  max-height: 100%;\n}\n\n.Card-module_card__TXz08.Card-module_no-header__Faodo.Card-module_padding-s__LoqKU .Card-module_content__mCsiE {\n  padding: var(--spacing-4);\n}\n\n.Card-module_card__TXz08.Card-module_no-header__Faodo.Card-module_padding-m__OGsRs .Card-module_content__mCsiE {\n  padding: var(--spacing-6);\n}\n\n.Card-module_card__TXz08.Card-module_no-header__Faodo.Card-module_padding-l__oAOaz .Card-module_content__mCsiE {\n  padding: var(--spacing-8);\n}\n\n.Card-module_card__TXz08.Card-module_no-header__Faodo.Card-module_padding-none__oMmnl .Card-module_content__mCsiE {\n  padding: 0;\n}\n\n.Card-module_card__TXz08.Card-module_no-margin__9SrtN .Card-module_header-wrapper__QQzUU {\n  margin: 0;\n}\n\n.Card-module_card__TXz08.Card-module_no-background__Cw3-w {\n  background-color: transparent;\n  box-shadow: none;\n}\n\n.Card-module_card__TXz08.Card-module_with-border__OF9-r {\n  border: 1px solid var(--color-grey-light);\n}\n";
var styles$Q = {"card":"Card-module_card__TXz08","header-wrapper":"Card-module_header-wrapper__QQzUU","content":"Card-module_content__mCsiE","is-collapsible":"Card-module_is-collapsible__G6mXU","is-collapsed":"Card-module_is-collapsed__ZcSJD","padding-s":"Card-module_padding-s__LoqKU","padding-m":"Card-module_padding-m__OGsRs","padding-l":"Card-module_padding-l__oAOaz","padding-none":"Card-module_padding-none__oMmnl","header":"Card-module_header__d0d-P","with-divider":"Card-module_with-divider__OhspK","fit-parent":"Card-module_fit-parent__IItQn","has-scroll":"Card-module_has-scroll__FoKik","no-header":"Card-module_no-header__Faodo","no-margin":"Card-module_no-margin__9SrtN","no-background":"Card-module_no-background__Cw3-w","with-border":"Card-module_with-border__OF9-r"};
styleInject(css_248z$S);

var css_248z$R = ".Chip-module_chip__F-Jro {\n  width: fit-content;\n  min-width: 25px;\n  border-radius: 100px;\n  height: var(--chip-height-m);\n  line-height: var(--chip-height-m);\n  text-align: center;\n  background-color: var(--chip-bg-color);\n  padding: 0 8px;\n  user-select: none;\n  transition: filter 0.3s;\n}\n\n.Chip-module_chip__F-Jro.Chip-module_is-small__JEv6j {\n  height: var(--chip-height-s);\n  line-height: var(--chip-height-s);\n  padding: 0 4px;\n  min-width: 30px;\n}\n\n.Chip-module_chip__F-Jro.Chip-module_active__UI5tC {\n  outline: 1px var(--chip-active-border-color) solid;\n  box-shadow: var(--chip-active-box-shadow);\n}\n\n.Chip-module_chip__F-Jro.Chip-module_active__UI5tC:hover {\n  filter: brightness(96%);\n}\n\n.Chip-module_chip__F-Jro .Chip-module_value__Sk9uf {\n  color: var(--chip-color);\n  font-size: var(--chip-font-size);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n";
var styles$P = {"chip":"Chip-module_chip__F-Jro","is-small":"Chip-module_is-small__JEv6j","active":"Chip-module_active__UI5tC","value":"Chip-module_value__Sk9uf"};
styleInject(css_248z$R);

var Chip = function (_a, ref) {
    var _b;
    var value = _a.value, title = _a.title, _c = _a.active, active = _c === void 0 ? false : _c, color = _a.color, _d = _a.bgColor, bgColor = _d === void 0 ? 'var(--chip-bg-color)' : _d, _e = _a.maxWidth, maxWidth = _e === void 0 ? '130px' : _e, _f = _a.isSmall, isSmall = _f === void 0 ? false : _f, className = _a.className;
    var chipClass = classNames$1(styles$P.chip, (_b = {},
        _b[styles$P.active] = active,
        _b[styles$P['is-small']] = isSmall,
        _b), className);
    return (jsx$1("div", { title: title, ref: ref, className: chipClass, style: {
            backgroundColor: bgColor !== null && bgColor !== void 0 ? bgColor : undefined,
            maxWidth: maxWidth !== null && maxWidth !== void 0 ? maxWidth : undefined,
        }, children: jsx$1("div", { className: styles$P.value, style: {
                color: color !== null && color !== void 0 ? color : undefined,
            }, children: value }) }));
};
var Chip$1 = forwardRef(Chip);

var css_248z$Q = ".Header-module_header__na9hy {\n  width: 100%;\n  height: var(--input-height-desktop);\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin-bottom: var(--spacing-7);\n  gap: var(--spacing-2);\n  user-select: none;\n}\n\n.Header-module_header__na9hy .Header-module_headline-wrapper__0m5Sr {\n  display: flex;\n  align-items: baseline;\n  gap: var(--spacing-4);\n  max-width: 100%;\n  min-width: 0;\n}\n\n.Header-module_header__na9hy .Header-module_headline__iEYao {\n  margin: 0;\n  font-size: var(--font-size-xl);\n  color: var(--color-grey-darkest);\n  max-width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.Header-module_header__na9hy .Header-module_subline__kGgxM {\n  margin-top: 2px;\n  color: var(--color-grey-light);\n  font-size: var(--font-size-m);\n  max-width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.Header-module_header__na9hy .Header-module_actions__8XO0- {\n  margin-left: auto;\n  align-self: center;\n  height: var(--spacing-7);\n  display: inline-flex;\n}\n\n.Header-module_header__na9hy .Header-module_actions__8XO0- button:not(:last-child) {\n  margin-right: var(--spacing-2);\n}\n\n.Header-module_header__na9hy.Header-module_big-gap__npmqE .Header-module_actions__8XO0- button:not(:last-child) {\n  margin-right: var(--spacing-4);\n}\n\n.Header-module_header__na9hy.Header-module_size-s__8pDxK .Header-module_headline__iEYao {\n  font-size: var(--font-size-xl);\n}\n\n.Header-module_header__na9hy.Header-module_size-m__C5VIH .Header-module_headline__iEYao {\n  font-size: var(--font-size-xxl);\n}\n\n.Header-module_header__na9hy.Header-module_size-l__vyuGK {\n  height: 55px;\n  margin-bottom: var(--spacing-8);\n}\n\n.Header-module_header__na9hy.Header-module_size-l__vyuGK .Header-module_headline__iEYao {\n  font-size: var(--font-size-xxxl);\n}\n\n.Header-module_header__na9hy.Header-module_no-margin__nYaTK {\n  margin: 0;\n}\n\n.Header-module_header__na9hy.Header-module_with-chip__EsH64 .Header-module_headline-wrapper__0m5Sr {\n  align-items: center;\n}\n";
var styles$O = {"header":"Header-module_header__na9hy","headline-wrapper":"Header-module_headline-wrapper__0m5Sr","headline":"Header-module_headline__iEYao","subline":"Header-module_subline__kGgxM","actions":"Header-module_actions__8XO0-","big-gap":"Header-module_big-gap__npmqE","size-s":"Header-module_size-s__8pDxK","size-m":"Header-module_size-m__C5VIH","size-l":"Header-module_size-l__vyuGK","no-margin":"Header-module_no-margin__nYaTK","with-chip":"Header-module_with-chip__EsH64"};
styleInject(css_248z$Q);

var Header = function (_a) {
    var _b;
    var headline = _a.headline, subHeadline = _a.subHeadline, _c = _a.size, size = _c === void 0 ? 'm' : _c, _d = _a.noMargin, noMargin = _d === void 0 ? false : _d, _e = _a.bigActionsGap, bigActionsGap = _e === void 0 ? false : _e, _f = _a.showSubHeadlineAsChip, showSubHeadlineAsChip = _f === void 0 ? false : _f, actions = _a.actions, className = _a.className;
    var classes = classNames$1(styles$O['header'], styles$O["size-".concat(size)], (_b = {},
        _b[styles$O['no-margin']] = noMargin,
        _b[styles$O['big-gap']] = bigActionsGap,
        _b[styles$O['with-chip']] = showSubHeadlineAsChip,
        _b), className);
    return (jsxs("div", { className: classes, children: [jsxs("div", { className: styles$O['headline-wrapper'], children: [jsx$1("div", { className: styles$O.headline, children: headline }), (subHeadline || subHeadline === 0) && (jsx$1(Fragment, { children: showSubHeadlineAsChip ? (jsx$1(Chip$1, { className: styles$O.subline, value: subHeadline.toString(), maxWidth: '100%' })) : (jsx$1("div", { className: styles$O.subline, children: subHeadline.toString() })) }))] }), actions && (jsx$1("div", { className: styles$O.actions, children: actions &&
                    actions.map(function (action, index) {
                        var _a;
                        return (jsx$1(Button$1, { className: action.className, disabled: action.disabled, icon: action.icon, hierarchy: (_a = action.hierarchy) !== null && _a !== void 0 ? _a : 'ghost', onClick: action.callback, type: action.isSubmit ? 'submit' : 'button', children: action.text }, index));
                    }) }))] }));
};

var Card = function (_a) {
    var _b, _c;
    var _d;
    var children = _a.children, maxHeight = _a.maxHeight, minHeight = _a.minHeight, fixedHeight = _a.fixedHeight, _e = _a.fitContent, fitContent = _e === void 0 ? false : _e, header = _a.header, _f = _a.contentPadding, contentPadding = _f === void 0 ? 'm' : _f, _g = _a.showHeaderMargin, showHeaderMargin = _g === void 0 ? false : _g, _h = _a.canScroll, canScroll = _h === void 0 ? false : _h, _j = _a.collapsibleOn, collapsibleOn = _j === void 0 ? 'mobile' : _j, _k = _a.disableCollapsible, disableCollapsible = _k === void 0 ? false : _k, _l = _a.isCollapsed, isCollapsed = _l === void 0 ? false : _l, _m = _a.hideBackground, hideBackground = _m === void 0 ? false : _m, _o = _a.showBorder, showBorder = _o === void 0 ? false : _o, onCollapse = _a.onCollapse, wrapperStyle = _a.wrapperStyle, wrapperClass = _a.wrapperClass, style = _a.style, className = _a.className;
    var _p = useState(), _isCollapsed = _p[0], setIsCollapsed = _p[1];
    var width = useWindowDimensions$1().width;
    var isMobile = width < 770;
    useEffect(function () {
        setIsCollapsed(isCollapsed);
    }, [isCollapsed]);
    var classes = classNames$1(styles$Q['card'], styles$Q['padding-' + contentPadding], (_b = {},
        _b[styles$Q['fit-parent']] = !fitContent,
        _b[styles$Q['no-header']] = !header,
        _b[styles$Q['no-margin']] = !showHeaderMargin,
        _b[styles$Q['has-scroll']] = canScroll,
        _b[styles$Q['is-collapsible']] = collapsibleOn !== 'never' || isMobile || _isCollapsed,
        _b[styles$Q['is-collapsed']] = _isCollapsed,
        _b[styles$Q['no-background']] = hideBackground,
        _b[styles$Q['with-border']] = showBorder,
        _b), wrapperClass);
    var headerClasses = classNames$1(styles$Q.header, (_c = {},
        _c[styles$Q['with-divider']] = header === null || header === void 0 ? void 0 : header.showDivider,
        _c));
    var contentClasses = classNames$1(styles$Q.content, {
        'has-scrollbar': canScroll,
    }, className);
    return (jsxs("div", { className: classes, style: __assign(__assign({}, wrapperStyle), { height: fixedHeight && !_isCollapsed ? fixedHeight : undefined, maxHeight: maxHeight
                ? "calc(".concat(maxHeight, " + var(--spacing-10))")
                : undefined, minHeight: minHeight !== null && minHeight !== void 0 ? minHeight : undefined }), children: [header && (jsx$1("div", { className: styles$Q['header-wrapper'], children: jsx$1("div", { className: headerClasses, children: jsx$1(Header, { headline: header.headline, subHeadline: header.subHeadline, size: 's', noMargin: true, bigActionsGap: header.bigActionsGap, showSubHeadlineAsChip: header.showSubHeadlineAsChip, actions: __spreadArray$1(__spreadArray$1([], (header.actions
                            ? (_d = header.actions.map(function (action) {
                                var _a, _b, _c, _d;
                                return {
                                    text: action.text,
                                    callback: action === null || action === void 0 ? void 0 : action.callback,
                                    icon: (_a = action.icon) !== null && _a !== void 0 ? _a : undefined,
                                    hierarchy: (_b = action.hierarchy) !== null && _b !== void 0 ? _b : 'ghost',
                                    disabled: (_c = action.disabled) !== null && _c !== void 0 ? _c : false,
                                    className: (_d = action.className) !== null && _d !== void 0 ? _d : undefined,
                                };
                            })) !== null && _d !== void 0 ? _d : []
                            : []), true), (collapsibleOn === 'always' ||
                            (collapsibleOn === 'mobile' && (isMobile || _isCollapsed))
                            ? [
                                {
                                    callback: function () {
                                        setIsCollapsed(!_isCollapsed);
                                        if (onCollapse)
                                            onCollapse();
                                    },
                                    icon: (jsx$1(SvgArrowUpIcon, { style: {
                                            transform: _isCollapsed
                                                ? 'rotate(-180deg)'
                                                : undefined,
                                        }, className: 'svg-icon' })),
                                    disabled: disableCollapsible,
                                },
                            ]
                            : []), true) }) }) })), !_isCollapsed && (jsx$1("div", { className: contentClasses, style: __assign(__assign({}, style), { maxHeight: maxHeight !== null && maxHeight !== void 0 ? maxHeight : undefined }), children: children }))] }));
};

var css_248z$P = ".Checkbox-module_checkbox-wrapper__-8K8o {\n  height: var(--input-height-mobile);\n  display: flex;\n  align-items: center;\n  position: relative;\n}\n\n.Checkbox-module_label__AQbya {\n  margin: 0 4px 0 8px;\n  font-size: var(--checkbox-text-size-mobile);\n  color: var(--input-text-color-default);\n  line-height: 1rem;\n  cursor: pointer;\n  user-select: none;\n  transition: 0.3s ease all;\n}\n\n.Checkbox-module_label__AQbya.Checkbox-module_has-ellipse__XQvbc {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.Checkbox-module_checkbox__PadWh {\n  /* Remove most native input styles */\n  appearance: none;\n\n  /* For iOS < 15 */\n  background: none;\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: 4px;\n\n  /* Not removed via appearance */\n  margin: 0;\n  font: inherit;\n  color: currentcolor;\n  width: var(--checkbox-input-size-mobile);\n  height: var(--checkbox-input-size-mobile);\n  transform: translateY(-0.075em);\n  transition: 0.3s ease all;\n  display: grid;\n  place-content: center;\n  cursor: pointer;\n}\n\n.Checkbox-module_checkbox__PadWh::before {\n  content: '';\n  width: var(--checkbox-input-size-mobile);\n  height: var(--checkbox-input-size-mobile);\n  clip-path: polygon(81% 20%, 44% 67%, 23% 50%, 16% 58%, 45% 82%, 89% 27%);\n  transform: scale(0);\n  transform-origin: center;\n  transition: 120ms transform ease-in-out;\n  box-shadow: inset 1em 1em #fff;\n}\n\n/* checked */\n.Checkbox-module_checkbox__PadWh:checked {\n  background-color: var(--input-bg-color-checked);\n  border-color: var(--input-border-color-checked);\n}\n\n.Checkbox-module_checkbox__PadWh:checked::before {\n  transform: scale(1);\n  background-color: #fff;\n}\n\n/* tooltip */\n.Checkbox-module_tooltip-icon__-AI9G {\n  width: 20px;\n  height: 20px;\n  margin-bottom: 4px;\n}\n\n/* disabled */\n.Checkbox-module_label__AQbya.Checkbox-module_disabled__K9Zqs {\n  color: var(--input-text-color-disabled);\n  cursor: not-allowed;\n}\n\n.Checkbox-module_tooltip-icon__-AI9G.Checkbox-module_disabled__K9Zqs {\n  color: var(--input-text-color-disabled);\n  transition: color 0.3s ease;\n}\n\n.Checkbox-module_checkbox__PadWh:disabled {\n  border-color: var(--input-border-color-disabled);\n  cursor: not-allowed;\n}\n\n.Checkbox-module_checkbox__PadWh:disabled:checked {\n  background-color: var(--input-text-color-disabled);\n}\n\n/* readonly */\n.Checkbox-module_checkbox-wrapper__-8K8o.Checkbox-module_readonly__6DfAF .Checkbox-module_checkbox__PadWh {\n  border-color: var(--input-border-color-readonly);\n  background-color: var(--input-bg-color-readonly);\n  cursor: initial;\n}\n\n.Checkbox-module_label__AQbya.Checkbox-module_readonly__6DfAF {\n  color: var(--input-text-color-readonly);\n  cursor: initial;\n}\n\n.Checkbox-module_tooltip-icon__-AI9G.Checkbox-module_readonly__6DfAF {\n  color: var(--input-text-color-readonly);\n  transition: color 0.3s ease;\n}\n\n/* error */\n.Checkbox-module_checkbox-wrapper__-8K8o.Checkbox-module_error__2dyN8 .Checkbox-module_checkbox__PadWh:not(:disabled) {\n  border-color: var(--input-border-color-error);\n}\n\n.Checkbox-module_checkbox-wrapper__-8K8o.Checkbox-module_error__2dyN8 .Checkbox-module_checkbox__PadWh:checked {\n  background-color: var(--input-bg-color-error);\n}\n\n.Checkbox-module_error-message__OJlNw {\n  color: var(--input-text-color-error);\n  position: absolute;\n  left: 0;\n  bottom: 0;\n  height: 1rem;\n}\n\n@media (width >= 580px) {\n  .Checkbox-module_checkbox-wrapper__-8K8o {\n    height: calc(var(--input-height-desktop) - 2px);\n    line-height: calc(var(--input-height-desktop) - 2px);\n  }\n\n  .Checkbox-module_label__AQbya {\n    font-size: var(--checkbox-text-size-desktop);\n  }\n\n  .Checkbox-module_checkbox__PadWh {\n    width: var(--checkbox-input-size-desktop);\n    height: var(--checkbox-input-size-desktop);\n  }\n\n  .Checkbox-module_checkbox__PadWh::before {\n    width: var(--checkbox-input-size-desktop);\n    height: var(--checkbox-input-size-desktop);\n  }\n}\n";
var styles$N = {"checkbox-wrapper":"Checkbox-module_checkbox-wrapper__-8K8o","label":"Checkbox-module_label__AQbya","has-ellipse":"Checkbox-module_has-ellipse__XQvbc","checkbox":"Checkbox-module_checkbox__PadWh","tooltip-icon":"Checkbox-module_tooltip-icon__-AI9G","disabled":"Checkbox-module_disabled__K9Zqs","readonly":"Checkbox-module_readonly__6DfAF","error":"Checkbox-module_error__2dyN8","error-message":"Checkbox-module_error-message__OJlNw"};
styleInject(css_248z$P);

/**
 *
 * Checkbox toggle:
 * A control that allows the user to toggle between checked and not checked.
 * Visually rendered as a regular checkbox.
 *
 * In Storybook the label can not be clicked because it handles a checkbox
 * onChange event in a weird way.
 *
 * @param {string} name
 * @param {boolean} disabled
 * @param {boolean} required
 * @param {boolean} checked
 * @param {string} error
 * @param {string} tooltipText
 * @param {string} label
 * @param {function} onChange
 * @param {function} onClick
 * @param {string} className
 * @param {CSSProperties} style
 * @param ref
 */
var Checkbox = function (_a, ref) {
    var _b, _c, _d;
    var name = _a.name, disabled = _a.disabled, readonly = _a.readonly, required = _a.required, checked = _a.checked, error = _a.error, tooltipText = _a.tooltipText, label = _a.label, _e = _a.preventLabelWrapping, preventLabelWrapping = _e === void 0 ? false : _e, _f = _a.tabIndex, tabIndex = _f === void 0 ? 0 : _f, onChange = _a.onChange, onClick = _a.onClick, className = _a.className, style = _a.style;
    var wrapperClass = classNames$1(styles$N['checkbox-wrapper'], (_b = {},
        _b[styles$N['error']] = error,
        _b[styles$N['readonly']] = readonly,
        _b), className);
    var labelClass = classNames$1(styles$N.label, (_c = {},
        _c[styles$N['has-ellipse']] = preventLabelWrapping,
        _c[styles$N['error']] = error,
        _c[styles$N['disabled']] = disabled,
        _c[styles$N['readonly']] = readonly,
        _c));
    var iconClass = classNames$1(styles$N['tooltip-icon'], (_d = {},
        _d[styles$N['disabled']] = disabled,
        _d[styles$N['readonly']] = readonly,
        _d));
    return (jsxs("div", { ref: ref, className: wrapperClass, style: style, children: [jsx$1("input", { type: 'checkbox', className: styles$N.checkbox, name: name, id: name, title: label !== null && label !== void 0 ? label : undefined, disabled: disabled, readOnly: readonly, required: required, checked: checked, onChange: function (e) {
                    !disabled && !readonly && onChange && onChange(e);
                }, onClick: function (e) {
                    !disabled && !readonly && onClick && onClick(e);
                }, tabIndex: tabIndex }), label && (jsx$1("label", { htmlFor: disabled || readonly ? undefined : name, className: labelClass, children: label })), tooltipText && (jsx$1(Tooltip$1, { usePortal: true, content: tooltipText, children: jsx$1(SvgInfoIcon, { className: iconClass }) })), error && jsx$1("small", { className: styles$N['error-message'], children: error })] }));
};
var Checkbox$1 = forwardRef(Checkbox);

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), true).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}

function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = true,
      o = false;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = true, n = r;
    } finally {
      try {
        if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}

function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}

function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}

var _excluded$6 = ["defaultInputValue", "defaultMenuIsOpen", "defaultValue", "inputValue", "menuIsOpen", "onChange", "onInputChange", "onMenuClose", "onMenuOpen", "value"];
function useStateManager(_ref) {
  var _ref$defaultInputValu = _ref.defaultInputValue,
    defaultInputValue = _ref$defaultInputValu === void 0 ? '' : _ref$defaultInputValu,
    _ref$defaultMenuIsOpe = _ref.defaultMenuIsOpen,
    defaultMenuIsOpen = _ref$defaultMenuIsOpe === void 0 ? false : _ref$defaultMenuIsOpe,
    _ref$defaultValue = _ref.defaultValue,
    defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue,
    propsInputValue = _ref.inputValue,
    propsMenuIsOpen = _ref.menuIsOpen,
    propsOnChange = _ref.onChange,
    propsOnInputChange = _ref.onInputChange,
    propsOnMenuClose = _ref.onMenuClose,
    propsOnMenuOpen = _ref.onMenuOpen,
    propsValue = _ref.value,
    restSelectProps = _objectWithoutProperties(_ref, _excluded$6);
  var _useState = useState(propsInputValue !== undefined ? propsInputValue : defaultInputValue),
    _useState2 = _slicedToArray(_useState, 2),
    stateInputValue = _useState2[0],
    setStateInputValue = _useState2[1];
  var _useState3 = useState(propsMenuIsOpen !== undefined ? propsMenuIsOpen : defaultMenuIsOpen),
    _useState4 = _slicedToArray(_useState3, 2),
    stateMenuIsOpen = _useState4[0],
    setStateMenuIsOpen = _useState4[1];
  var _useState5 = useState(propsValue !== undefined ? propsValue : defaultValue),
    _useState6 = _slicedToArray(_useState5, 2),
    stateValue = _useState6[0],
    setStateValue = _useState6[1];
  var onChange = useCallback(function (value, actionMeta) {
    if (typeof propsOnChange === 'function') {
      propsOnChange(value, actionMeta);
    }
    setStateValue(value);
  }, [propsOnChange]);
  var onInputChange = useCallback(function (value, actionMeta) {
    var newValue;
    if (typeof propsOnInputChange === 'function') {
      newValue = propsOnInputChange(value, actionMeta);
    }
    setStateInputValue(newValue !== undefined ? newValue : value);
  }, [propsOnInputChange]);
  var onMenuOpen = useCallback(function () {
    if (typeof propsOnMenuOpen === 'function') {
      propsOnMenuOpen();
    }
    setStateMenuIsOpen(true);
  }, [propsOnMenuOpen]);
  var onMenuClose = useCallback(function () {
    if (typeof propsOnMenuClose === 'function') {
      propsOnMenuClose();
    }
    setStateMenuIsOpen(false);
  }, [propsOnMenuClose]);
  var inputValue = propsInputValue !== undefined ? propsInputValue : stateInputValue;
  var menuIsOpen = propsMenuIsOpen !== undefined ? propsMenuIsOpen : stateMenuIsOpen;
  var value = propsValue !== undefined ? propsValue : stateValue;
  return _objectSpread2(_objectSpread2({}, restSelectProps), {}, {
    inputValue: inputValue,
    menuIsOpen: menuIsOpen,
    onChange: onChange,
    onInputChange: onInputChange,
    onMenuClose: onMenuClose,
    onMenuOpen: onMenuOpen,
    value: value
  });
}

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

function _classCallCheck(a, n) {
  if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function");
}

function _defineProperties(e, r) {
  for (var t = 0; t < r.length; t++) {
    var o = r[t];
    o.enumerable = o.enumerable || false, o.configurable = true, "value" in o && (o.writable = true), Object.defineProperty(e, toPropertyKey(o.key), o);
  }
}
function _createClass(e, r, t) {
  return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", {
    writable: false
  }), e;
}

function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
    return t.__proto__ = e, t;
  }, _setPrototypeOf(t, e);
}

function _inherits(t, e) {
  if ("function" != typeof e && null !== e) throw new TypeError("Super expression must either be null or a function");
  t.prototype = Object.create(e && e.prototype, {
    constructor: {
      value: t,
      writable: true,
      configurable: true
    }
  }), Object.defineProperty(t, "prototype", {
    writable: false
  }), e && _setPrototypeOf(t, e);
}

function _getPrototypeOf(t) {
  return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) {
    return t.__proto__ || Object.getPrototypeOf(t);
  }, _getPrototypeOf(t);
}

function _isNativeReflectConstruct() {
  try {
    var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
  } catch (t) {}
  return (_isNativeReflectConstruct = function _isNativeReflectConstruct() {
    return !!t;
  })();
}

function _assertThisInitialized(e) {
  if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  return e;
}

function _possibleConstructorReturn(t, e) {
  if (e && ("object" == _typeof(e) || "function" == typeof e)) return e;
  if (void 0 !== e) throw new TypeError("Derived constructors may only return object or undefined");
  return _assertThisInitialized(t);
}

function _createSuper(t) {
  var r = _isNativeReflectConstruct();
  return function () {
    var e,
      o = _getPrototypeOf(t);
    if (r) {
      var s = _getPrototypeOf(this).constructor;
      e = Reflect.construct(o, arguments, s);
    } else e = o.apply(this, arguments);
    return _possibleConstructorReturn(this, e);
  };
}

function _arrayWithoutHoles(r) {
  if (Array.isArray(r)) return _arrayLikeToArray(r);
}

function _iterableToArray(r) {
  if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(r) {
  return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread();
}

/*

Based off glamor's StyleSheet, thanks Sunil ❤️

high performance StyleSheet for css-in-js systems

- uses multiple style tags behind the scenes for millions of rules
- uses `insertRule` for appending in production for *much* faster performance

// usage

import { StyleSheet } from '@emotion/sheet'

let styleSheet = new StyleSheet({ key: '', container: document.head })

styleSheet.insert('#box { border: 1px solid red; }')
- appends a css rule into the stylesheet

styleSheet.flush()
- empties the stylesheet of all its contents

*/

function sheetForTag(tag) {
  if (tag.sheet) {
    return tag.sheet;
  } // this weirdness brought to you by firefox

  /* istanbul ignore next */


  for (var i = 0; i < document.styleSheets.length; i++) {
    if (document.styleSheets[i].ownerNode === tag) {
      return document.styleSheets[i];
    }
  } // this function should always return with a value
  // TS can't understand it though so we make it stop complaining here


  return undefined;
}

function createStyleElement(options) {
  var tag = document.createElement('style');
  tag.setAttribute('data-emotion', options.key);

  if (options.nonce !== undefined) {
    tag.setAttribute('nonce', options.nonce);
  }

  tag.appendChild(document.createTextNode(''));
  tag.setAttribute('data-s', '');
  return tag;
}

var StyleSheet = /*#__PURE__*/function () {
  // Using Node instead of HTMLElement since container may be a ShadowRoot
  function StyleSheet(options) {
    var _this = this;

    this._insertTag = function (tag) {
      var before;

      if (_this.tags.length === 0) {
        if (_this.insertionPoint) {
          before = _this.insertionPoint.nextSibling;
        } else if (_this.prepend) {
          before = _this.container.firstChild;
        } else {
          before = _this.before;
        }
      } else {
        before = _this.tags[_this.tags.length - 1].nextSibling;
      }

      _this.container.insertBefore(tag, before);

      _this.tags.push(tag);
    };

    this.isSpeedy = options.speedy === undefined ? true : options.speedy;
    this.tags = [];
    this.ctr = 0;
    this.nonce = options.nonce; // key is the value of the data-emotion attribute, it's used to identify different sheets

    this.key = options.key;
    this.container = options.container;
    this.prepend = options.prepend;
    this.insertionPoint = options.insertionPoint;
    this.before = null;
  }

  var _proto = StyleSheet.prototype;

  _proto.hydrate = function hydrate(nodes) {
    nodes.forEach(this._insertTag);
  };

  _proto.insert = function insert(rule) {
    // the max length is how many rules we have per style tag, it's 65000 in speedy mode
    // it's 1 in dev because we insert source maps that map a single rule to a location
    // and you can only have one source map per style tag
    if (this.ctr % (this.isSpeedy ? 65000 : 1) === 0) {
      this._insertTag(createStyleElement(this));
    }

    var tag = this.tags[this.tags.length - 1];

    if (this.isSpeedy) {
      var sheet = sheetForTag(tag);

      try {
        // this is the ultrafast version, works across browsers
        // the big drawback is that the css won't be editable in devtools
        sheet.insertRule(rule, sheet.cssRules.length);
      } catch (e) {
      }
    } else {
      tag.appendChild(document.createTextNode(rule));
    }

    this.ctr++;
  };

  _proto.flush = function flush() {
    this.tags.forEach(function (tag) {
      var _tag$parentNode;

      return (_tag$parentNode = tag.parentNode) == null ? void 0 : _tag$parentNode.removeChild(tag);
    });
    this.tags = [];
    this.ctr = 0;
  };

  return StyleSheet;
}();

var MS = '-ms-';
var MOZ = '-moz-';
var WEBKIT = '-webkit-';

var COMMENT = 'comm';
var RULESET = 'rule';
var DECLARATION = 'decl';
var IMPORT = '@import';
var KEYFRAMES = '@keyframes';
var LAYER = '@layer';

/**
 * @param {number}
 * @return {number}
 */
var abs = Math.abs;

/**
 * @param {number}
 * @return {string}
 */
var from = String.fromCharCode;

/**
 * @param {object}
 * @return {object}
 */
var assign = Object.assign;

/**
 * @param {string} value
 * @param {number} length
 * @return {number}
 */
function hash (value, length) {
	return charat(value, 0) ^ 45 ? (((((((length << 2) ^ charat(value, 0)) << 2) ^ charat(value, 1)) << 2) ^ charat(value, 2)) << 2) ^ charat(value, 3) : 0
}

/**
 * @param {string} value
 * @return {string}
 */
function trim (value) {
	return value.trim()
}

/**
 * @param {string} value
 * @param {RegExp} pattern
 * @return {string?}
 */
function match$3 (value, pattern) {
	return (value = pattern.exec(value)) ? value[0] : value
}

/**
 * @param {string} value
 * @param {(string|RegExp)} pattern
 * @param {string} replacement
 * @return {string}
 */
function replace (value, pattern, replacement) {
	return value.replace(pattern, replacement)
}

/**
 * @param {string} value
 * @param {string} search
 * @return {number}
 */
function indexof (value, search) {
	return value.indexOf(search)
}

/**
 * @param {string} value
 * @param {number} index
 * @return {number}
 */
function charat (value, index) {
	return value.charCodeAt(index) | 0
}

/**
 * @param {string} value
 * @param {number} begin
 * @param {number} end
 * @return {string}
 */
function substr (value, begin, end) {
	return value.slice(begin, end)
}

/**
 * @param {string} value
 * @return {number}
 */
function strlen (value) {
	return value.length
}

/**
 * @param {any[]} value
 * @return {number}
 */
function sizeof (value) {
	return value.length
}

/**
 * @param {any} value
 * @param {any[]} array
 * @return {any}
 */
function append (value, array) {
	return array.push(value), value
}

/**
 * @param {string[]} array
 * @param {function} callback
 * @return {string}
 */
function combine (array, callback) {
	return array.map(callback).join('')
}

var line = 1;
var column = 1;
var length = 0;
var position = 0;
var character = 0;
var characters = '';

/**
 * @param {string} value
 * @param {object | null} root
 * @param {object | null} parent
 * @param {string} type
 * @param {string[] | string} props
 * @param {object[] | string} children
 * @param {number} length
 */
function node (value, root, parent, type, props, children, length) {
	return {value: value, root: root, parent: parent, type: type, props: props, children: children, line: line, column: column, length: length, return: ''}
}

/**
 * @param {object} root
 * @param {object} props
 * @return {object}
 */
function copy (root, props) {
	return assign(node('', null, null, '', null, null, 0), root, {length: -root.length}, props)
}

/**
 * @return {number}
 */
function char () {
	return character
}

/**
 * @return {number}
 */
function prev () {
	character = position > 0 ? charat(characters, --position) : 0;

	if (column--, character === 10)
		column = 1, line--;

	return character
}

/**
 * @return {number}
 */
function next () {
	character = position < length ? charat(characters, position++) : 0;

	if (column++, character === 10)
		column = 1, line++;

	return character
}

/**
 * @return {number}
 */
function peek () {
	return charat(characters, position)
}

/**
 * @return {number}
 */
function caret () {
	return position
}

/**
 * @param {number} begin
 * @param {number} end
 * @return {string}
 */
function slice (begin, end) {
	return substr(characters, begin, end)
}

/**
 * @param {number} type
 * @return {number}
 */
function token (type) {
	switch (type) {
		// \0 \t \n \r \s whitespace token
		case 0: case 9: case 10: case 13: case 32:
			return 5
		// ! + , / > @ ~ isolate token
		case 33: case 43: case 44: case 47: case 62: case 64: case 126:
		// ; { } breakpoint token
		case 59: case 123: case 125:
			return 4
		// : accompanied token
		case 58:
			return 3
		// " ' ( [ opening delimit token
		case 34: case 39: case 40: case 91:
			return 2
		// ) ] closing delimit token
		case 41: case 93:
			return 1
	}

	return 0
}

/**
 * @param {string} value
 * @return {any[]}
 */
function alloc (value) {
	return line = column = 1, length = strlen(characters = value), position = 0, []
}

/**
 * @param {any} value
 * @return {any}
 */
function dealloc (value) {
	return characters = '', value
}

/**
 * @param {number} type
 * @return {string}
 */
function delimit (type) {
	return trim(slice(position - 1, delimiter(type === 91 ? type + 2 : type === 40 ? type + 1 : type)))
}

/**
 * @param {number} type
 * @return {string}
 */
function whitespace (type) {
	while (character = peek())
		if (character < 33)
			next();
		else
			break

	return token(type) > 2 || token(character) > 3 ? '' : ' '
}

/**
 * @param {number} index
 * @param {number} count
 * @return {string}
 */
function escaping (index, count) {
	while (--count && next())
		// not 0-9 A-F a-f
		if (character < 48 || character > 102 || (character > 57 && character < 65) || (character > 70 && character < 97))
			break

	return slice(index, caret() + (count < 6 && peek() == 32 && next() == 32))
}

/**
 * @param {number} type
 * @return {number}
 */
function delimiter (type) {
	while (next())
		switch (character) {
			// ] ) " '
			case type:
				return position
			// " '
			case 34: case 39:
				if (type !== 34 && type !== 39)
					delimiter(character);
				break
			// (
			case 40:
				if (type === 41)
					delimiter(type);
				break
			// \
			case 92:
				next();
				break
		}

	return position
}

/**
 * @param {number} type
 * @param {number} index
 * @return {number}
 */
function commenter (type, index) {
	while (next())
		// //
		if (type + character === 47 + 10)
			break
		// /*
		else if (type + character === 42 + 42 && peek() === 47)
			break

	return '/*' + slice(index, position - 1) + '*' + from(type === 47 ? type : next())
}

/**
 * @param {number} index
 * @return {string}
 */
function identifier (index) {
	while (!token(peek()))
		next();

	return slice(index, position)
}

/**
 * @param {string} value
 * @return {object[]}
 */
function compile (value) {
	return dealloc(parse$1('', null, null, null, [''], value = alloc(value), 0, [0], value))
}

/**
 * @param {string} value
 * @param {object} root
 * @param {object?} parent
 * @param {string[]} rule
 * @param {string[]} rules
 * @param {string[]} rulesets
 * @param {number[]} pseudo
 * @param {number[]} points
 * @param {string[]} declarations
 * @return {object}
 */
function parse$1 (value, root, parent, rule, rules, rulesets, pseudo, points, declarations) {
	var index = 0;
	var offset = 0;
	var length = pseudo;
	var atrule = 0;
	var property = 0;
	var previous = 0;
	var variable = 1;
	var scanning = 1;
	var ampersand = 1;
	var character = 0;
	var type = '';
	var props = rules;
	var children = rulesets;
	var reference = rule;
	var characters = type;

	while (scanning)
		switch (previous = character, character = next()) {
			// (
			case 40:
				if (previous != 108 && charat(characters, length - 1) == 58) {
					if (indexof(characters += replace(delimit(character), '&', '&\f'), '&\f') != -1)
						ampersand = -1;
					break
				}
			// " ' [
			case 34: case 39: case 91:
				characters += delimit(character);
				break
			// \t \n \r \s
			case 9: case 10: case 13: case 32:
				characters += whitespace(previous);
				break
			// \
			case 92:
				characters += escaping(caret() - 1, 7);
				continue
			// /
			case 47:
				switch (peek()) {
					case 42: case 47:
						append(comment(commenter(next(), caret()), root, parent), declarations);
						break
					default:
						characters += '/';
				}
				break
			// {
			case 123 * variable:
				points[index++] = strlen(characters) * ampersand;
			// } ; \0
			case 125 * variable: case 59: case 0:
				switch (character) {
					// \0 }
					case 0: case 125: scanning = 0;
					// ;
					case 59 + offset: if (ampersand == -1) characters = replace(characters, /\f/g, '');
						if (property > 0 && (strlen(characters) - length))
							append(property > 32 ? declaration(characters + ';', rule, parent, length - 1) : declaration(replace(characters, ' ', '') + ';', rule, parent, length - 2), declarations);
						break
					// @ ;
					case 59: characters += ';';
					// { rule/at-rule
					default:
						append(reference = ruleset(characters, root, parent, index, offset, rules, points, type, props = [], children = [], length), rulesets);

						if (character === 123)
							if (offset === 0)
								parse$1(characters, root, reference, reference, props, rulesets, length, points, children);
							else
								switch (atrule === 99 && charat(characters, 3) === 110 ? 100 : atrule) {
									// d l m s
									case 100: case 108: case 109: case 115:
										parse$1(value, reference, reference, rule && append(ruleset(value, reference, reference, 0, 0, rules, points, type, rules, props = [], length), children), rules, children, length, points, rule ? props : children);
										break
									default:
										parse$1(characters, reference, reference, reference, [''], children, 0, points, children);
								}
				}

				index = offset = property = 0, variable = ampersand = 1, type = characters = '', length = pseudo;
				break
			// :
			case 58:
				length = 1 + strlen(characters), property = previous;
			default:
				if (variable < 1)
					if (character == 123)
						--variable;
					else if (character == 125 && variable++ == 0 && prev() == 125)
						continue

				switch (characters += from(character), character * variable) {
					// &
					case 38:
						ampersand = offset > 0 ? 1 : (characters += '\f', -1);
						break
					// ,
					case 44:
						points[index++] = (strlen(characters) - 1) * ampersand, ampersand = 1;
						break
					// @
					case 64:
						// -
						if (peek() === 45)
							characters += delimit(next());

						atrule = peek(), offset = length = strlen(type = characters += identifier(caret())), character++;
						break
					// -
					case 45:
						if (previous === 45 && strlen(characters) == 2)
							variable = 0;
				}
		}

	return rulesets
}

/**
 * @param {string} value
 * @param {object} root
 * @param {object?} parent
 * @param {number} index
 * @param {number} offset
 * @param {string[]} rules
 * @param {number[]} points
 * @param {string} type
 * @param {string[]} props
 * @param {string[]} children
 * @param {number} length
 * @return {object}
 */
function ruleset (value, root, parent, index, offset, rules, points, type, props, children, length) {
	var post = offset - 1;
	var rule = offset === 0 ? rules : [''];
	var size = sizeof(rule);

	for (var i = 0, j = 0, k = 0; i < index; ++i)
		for (var x = 0, y = substr(value, post + 1, post = abs(j = points[i])), z = value; x < size; ++x)
			if (z = trim(j > 0 ? rule[x] + ' ' + y : replace(y, /&\f/g, rule[x])))
				props[k++] = z;

	return node(value, root, parent, offset === 0 ? RULESET : type, props, children, length)
}

/**
 * @param {number} value
 * @param {object} root
 * @param {object?} parent
 * @return {object}
 */
function comment (value, root, parent) {
	return node(value, root, parent, COMMENT, from(char()), substr(value, 2, -2), 0)
}

/**
 * @param {string} value
 * @param {object} root
 * @param {object?} parent
 * @param {number} length
 * @return {object}
 */
function declaration (value, root, parent, length) {
	return node(value, root, parent, DECLARATION, substr(value, 0, length), substr(value, length + 1, -1), length)
}

/**
 * @param {object[]} children
 * @param {function} callback
 * @return {string}
 */
function serialize (children, callback) {
	var output = '';
	var length = sizeof(children);

	for (var i = 0; i < length; i++)
		output += callback(children[i], i, children, callback) || '';

	return output
}

/**
 * @param {object} element
 * @param {number} index
 * @param {object[]} children
 * @param {function} callback
 * @return {string}
 */
function stringify (element, index, children, callback) {
	switch (element.type) {
		case LAYER: if (element.children.length) break
		case IMPORT: case DECLARATION: return element.return = element.return || element.value
		case COMMENT: return ''
		case KEYFRAMES: return element.return = element.value + '{' + serialize(element.children, callback) + '}'
		case RULESET: element.value = element.props.join(',');
	}

	return strlen(children = serialize(element.children, callback)) ? element.return = element.value + '{' + children + '}' : ''
}

/**
 * @param {function[]} collection
 * @return {function}
 */
function middleware (collection) {
	var length = sizeof(collection);

	return function (element, index, children, callback) {
		var output = '';

		for (var i = 0; i < length; i++)
			output += collection[i](element, index, children, callback) || '';

		return output
	}
}

/**
 * @param {function} callback
 * @return {function}
 */
function rulesheet (callback) {
	return function (element) {
		if (!element.root)
			if (element = element.return)
				callback(element);
	}
}

var weakMemoize = function weakMemoize(func) {
  var cache = new WeakMap();
  return function (arg) {
    if (cache.has(arg)) {
      // Use non-null assertion because we just checked that the cache `has` it
      // This allows us to remove `undefined` from the return value
      return cache.get(arg);
    }

    var ret = func(arg);
    cache.set(arg, ret);
    return ret;
  };
};

function memoize(fn) {
  var cache = Object.create(null);
  return function (arg) {
    if (cache[arg] === undefined) cache[arg] = fn(arg);
    return cache[arg];
  };
}

var isBrowser$3 = typeof document !== 'undefined';

var identifierWithPointTracking = function identifierWithPointTracking(begin, points, index) {
  var previous = 0;
  var character = 0;

  while (true) {
    previous = character;
    character = peek(); // &\f

    if (previous === 38 && character === 12) {
      points[index] = 1;
    }

    if (token(character)) {
      break;
    }

    next();
  }

  return slice(begin, position);
};

var toRules = function toRules(parsed, points) {
  // pretend we've started with a comma
  var index = -1;
  var character = 44;

  do {
    switch (token(character)) {
      case 0:
        // &\f
        if (character === 38 && peek() === 12) {
          // this is not 100% correct, we don't account for literal sequences here - like for example quoted strings
          // stylis inserts \f after & to know when & where it should replace this sequence with the context selector
          // and when it should just concatenate the outer and inner selectors
          // it's very unlikely for this sequence to actually appear in a different context, so we just leverage this fact here
          points[index] = 1;
        }

        parsed[index] += identifierWithPointTracking(position - 1, points, index);
        break;

      case 2:
        parsed[index] += delimit(character);
        break;

      case 4:
        // comma
        if (character === 44) {
          // colon
          parsed[++index] = peek() === 58 ? '&\f' : '';
          points[index] = parsed[index].length;
          break;
        }

      // fallthrough

      default:
        parsed[index] += from(character);
    }
  } while (character = next());

  return parsed;
};

var getRules = function getRules(value, points) {
  return dealloc(toRules(alloc(value), points));
}; // WeakSet would be more appropriate, but only WeakMap is supported in IE11


var fixedElements = /* #__PURE__ */new WeakMap();
var compat = function compat(element) {
  if (element.type !== 'rule' || !element.parent || // positive .length indicates that this rule contains pseudo
  // negative .length indicates that this rule has been already prefixed
  element.length < 1) {
    return;
  }

  var value = element.value;
  var parent = element.parent;
  var isImplicitRule = element.column === parent.column && element.line === parent.line;

  while (parent.type !== 'rule') {
    parent = parent.parent;
    if (!parent) return;
  } // short-circuit for the simplest case


  if (element.props.length === 1 && value.charCodeAt(0) !== 58
  /* colon */
  && !fixedElements.get(parent)) {
    return;
  } // if this is an implicitly inserted rule (the one eagerly inserted at the each new nested level)
  // then the props has already been manipulated beforehand as they that array is shared between it and its "rule parent"


  if (isImplicitRule) {
    return;
  }

  fixedElements.set(element, true);
  var points = [];
  var rules = getRules(value, points);
  var parentRules = parent.props;

  for (var i = 0, k = 0; i < rules.length; i++) {
    for (var j = 0; j < parentRules.length; j++, k++) {
      element.props[k] = points[i] ? rules[i].replace(/&\f/g, parentRules[j]) : parentRules[j] + " " + rules[i];
    }
  }
};
var removeLabel = function removeLabel(element) {
  if (element.type === 'decl') {
    var value = element.value;

    if ( // charcode for l
    value.charCodeAt(0) === 108 && // charcode for b
    value.charCodeAt(2) === 98) {
      // this ignores label
      element["return"] = '';
      element.value = '';
    }
  }
};

/* eslint-disable no-fallthrough */

function prefix(value, length) {
  switch (hash(value, length)) {
    // color-adjust
    case 5103:
      return WEBKIT + 'print-' + value + value;
    // animation, animation-(delay|direction|duration|fill-mode|iteration-count|name|play-state|timing-function)

    case 5737:
    case 4201:
    case 3177:
    case 3433:
    case 1641:
    case 4457:
    case 2921: // text-decoration, filter, clip-path, backface-visibility, column, box-decoration-break

    case 5572:
    case 6356:
    case 5844:
    case 3191:
    case 6645:
    case 3005: // mask, mask-image, mask-(mode|clip|size), mask-(repeat|origin), mask-position, mask-composite,

    case 6391:
    case 5879:
    case 5623:
    case 6135:
    case 4599:
    case 4855: // background-clip, columns, column-(count|fill|gap|rule|rule-color|rule-style|rule-width|span|width)

    case 4215:
    case 6389:
    case 5109:
    case 5365:
    case 5621:
    case 3829:
      return WEBKIT + value + value;
    // appearance, user-select, transform, hyphens, text-size-adjust

    case 5349:
    case 4246:
    case 4810:
    case 6968:
    case 2756:
      return WEBKIT + value + MOZ + value + MS + value + value;
    // flex, flex-direction

    case 6828:
    case 4268:
      return WEBKIT + value + MS + value + value;
    // order

    case 6165:
      return WEBKIT + value + MS + 'flex-' + value + value;
    // align-items

    case 5187:
      return WEBKIT + value + replace(value, /(\w+).+(:[^]+)/, WEBKIT + 'box-$1$2' + MS + 'flex-$1$2') + value;
    // align-self

    case 5443:
      return WEBKIT + value + MS + 'flex-item-' + replace(value, /flex-|-self/, '') + value;
    // align-content

    case 4675:
      return WEBKIT + value + MS + 'flex-line-pack' + replace(value, /align-content|flex-|-self/, '') + value;
    // flex-shrink

    case 5548:
      return WEBKIT + value + MS + replace(value, 'shrink', 'negative') + value;
    // flex-basis

    case 5292:
      return WEBKIT + value + MS + replace(value, 'basis', 'preferred-size') + value;
    // flex-grow

    case 6060:
      return WEBKIT + 'box-' + replace(value, '-grow', '') + WEBKIT + value + MS + replace(value, 'grow', 'positive') + value;
    // transition

    case 4554:
      return WEBKIT + replace(value, /([^-])(transform)/g, '$1' + WEBKIT + '$2') + value;
    // cursor

    case 6187:
      return replace(replace(replace(value, /(zoom-|grab)/, WEBKIT + '$1'), /(image-set)/, WEBKIT + '$1'), value, '') + value;
    // background, background-image

    case 5495:
    case 3959:
      return replace(value, /(image-set\([^]*)/, WEBKIT + '$1' + '$`$1');
    // justify-content

    case 4968:
      return replace(replace(value, /(.+:)(flex-)?(.*)/, WEBKIT + 'box-pack:$3' + MS + 'flex-pack:$3'), /s.+-b[^;]+/, 'justify') + WEBKIT + value + value;
    // (margin|padding)-inline-(start|end)

    case 4095:
    case 3583:
    case 4068:
    case 2532:
      return replace(value, /(.+)-inline(.+)/, WEBKIT + '$1$2') + value;
    // (min|max)?(width|height|inline-size|block-size)

    case 8116:
    case 7059:
    case 5753:
    case 5535:
    case 5445:
    case 5701:
    case 4933:
    case 4677:
    case 5533:
    case 5789:
    case 5021:
    case 4765:
      // stretch, max-content, min-content, fill-available
      if (strlen(value) - 1 - length > 6) switch (charat(value, length + 1)) {
        // (m)ax-content, (m)in-content
        case 109:
          // -
          if (charat(value, length + 4) !== 45) break;
        // (f)ill-available, (f)it-content

        case 102:
          return replace(value, /(.+:)(.+)-([^]+)/, '$1' + WEBKIT + '$2-$3' + '$1' + MOZ + (charat(value, length + 3) == 108 ? '$3' : '$2-$3')) + value;
        // (s)tretch

        case 115:
          return ~indexof(value, 'stretch') ? prefix(replace(value, 'stretch', 'fill-available'), length) + value : value;
      }
      break;
    // position: sticky

    case 4949:
      // (s)ticky?
      if (charat(value, length + 1) !== 115) break;
    // display: (flex|inline-flex)

    case 6444:
      switch (charat(value, strlen(value) - 3 - (~indexof(value, '!important') && 10))) {
        // stic(k)y
        case 107:
          return replace(value, ':', ':' + WEBKIT) + value;
        // (inline-)?fl(e)x

        case 101:
          return replace(value, /(.+:)([^;!]+)(;|!.+)?/, '$1' + WEBKIT + (charat(value, 14) === 45 ? 'inline-' : '') + 'box$3' + '$1' + WEBKIT + '$2$3' + '$1' + MS + '$2box$3') + value;
      }

      break;
    // writing-mode

    case 5936:
      switch (charat(value, length + 11)) {
        // vertical-l(r)
        case 114:
          return WEBKIT + value + MS + replace(value, /[svh]\w+-[tblr]{2}/, 'tb') + value;
        // vertical-r(l)

        case 108:
          return WEBKIT + value + MS + replace(value, /[svh]\w+-[tblr]{2}/, 'tb-rl') + value;
        // horizontal(-)tb

        case 45:
          return WEBKIT + value + MS + replace(value, /[svh]\w+-[tblr]{2}/, 'lr') + value;
      }

      return WEBKIT + value + MS + value + value;
  }

  return value;
}

var prefixer = function prefixer(element, index, children, callback) {
  if (element.length > -1) if (!element["return"]) switch (element.type) {
    case DECLARATION:
      element["return"] = prefix(element.value, element.length);
      break;

    case KEYFRAMES:
      return serialize([copy(element, {
        value: replace(element.value, '@', '@' + WEBKIT)
      })], callback);

    case RULESET:
      if (element.length) return combine(element.props, function (value) {
        switch (match$3(value, /(::plac\w+|:read-\w+)/)) {
          // :read-(only|write)
          case ':read-only':
          case ':read-write':
            return serialize([copy(element, {
              props: [replace(value, /:(read-\w+)/, ':' + MOZ + '$1')]
            })], callback);
          // :placeholder

          case '::placeholder':
            return serialize([copy(element, {
              props: [replace(value, /:(plac\w+)/, ':' + WEBKIT + 'input-$1')]
            }), copy(element, {
              props: [replace(value, /:(plac\w+)/, ':' + MOZ + '$1')]
            }), copy(element, {
              props: [replace(value, /:(plac\w+)/, MS + 'input-$1')]
            })], callback);
        }

        return '';
      });
  }
};

var getServerStylisCache = isBrowser$3 ? undefined : weakMemoize(function () {
  return memoize(function () {
    return {};
  });
});
var defaultStylisPlugins = [prefixer];

var createCache = function createCache(options) {
  var key = options.key;

  if (isBrowser$3 && key === 'css') {
    var ssrStyles = document.querySelectorAll("style[data-emotion]:not([data-s])"); // get SSRed styles out of the way of React's hydration
    // document.head is a safe place to move them to(though note document.head is not necessarily the last place they will be)
    // note this very very intentionally targets all style elements regardless of the key to ensure
    // that creating a cache works inside of render of a React component

    Array.prototype.forEach.call(ssrStyles, function (node) {
      // we want to only move elements which have a space in the data-emotion attribute value
      // because that indicates that it is an Emotion 11 server-side rendered style elements
      // while we will already ignore Emotion 11 client-side inserted styles because of the :not([data-s]) part in the selector
      // Emotion 10 client-side inserted styles did not have data-s (but importantly did not have a space in their data-emotion attributes)
      // so checking for the space ensures that loading Emotion 11 after Emotion 10 has inserted some styles
      // will not result in the Emotion 10 styles being destroyed
      var dataEmotionAttribute = node.getAttribute('data-emotion');

      if (dataEmotionAttribute.indexOf(' ') === -1) {
        return;
      }

      document.head.appendChild(node);
      node.setAttribute('data-s', '');
    });
  }

  var stylisPlugins = options.stylisPlugins || defaultStylisPlugins;

  var inserted = {};
  var container;
  var nodesToHydrate = [];

  if (isBrowser$3) {
    container = options.container || document.head;
    Array.prototype.forEach.call( // this means we will ignore elements which don't have a space in them which
    // means that the style elements we're looking at are only Emotion 11 server-rendered style elements
    document.querySelectorAll("style[data-emotion^=\"" + key + " \"]"), function (node) {
      var attrib = node.getAttribute("data-emotion").split(' ');

      for (var i = 1; i < attrib.length; i++) {
        inserted[attrib[i]] = true;
      }

      nodesToHydrate.push(node);
    });
  }

  var _insert;

  var omnipresentPlugins = [compat, removeLabel];

  if (!getServerStylisCache) {
    var currentSheet;
    var finalizingPlugins = [stringify, rulesheet(function (rule) {
      currentSheet.insert(rule);
    })];
    var serializer = middleware(omnipresentPlugins.concat(stylisPlugins, finalizingPlugins));

    var stylis = function stylis(styles) {
      return serialize(compile(styles), serializer);
    };

    _insert = function insert(selector, serialized, sheet, shouldCache) {
      currentSheet = sheet;

      stylis(selector ? selector + "{" + serialized.styles + "}" : serialized.styles);

      if (shouldCache) {
        cache.inserted[serialized.name] = true;
      }
    };
  } else {
    var _finalizingPlugins = [stringify];

    var _serializer = middleware(omnipresentPlugins.concat(stylisPlugins, _finalizingPlugins));

    var _stylis = function _stylis(styles) {
      return serialize(compile(styles), _serializer);
    };

    var serverStylisCache = getServerStylisCache(stylisPlugins)(key);

    var getRules = function getRules(selector, serialized) {
      var name = serialized.name;

      if (serverStylisCache[name] === undefined) {
        serverStylisCache[name] = _stylis(selector ? selector + "{" + serialized.styles + "}" : serialized.styles);
      }

      return serverStylisCache[name];
    };

    _insert = function _insert(selector, serialized, sheet, shouldCache) {
      var name = serialized.name;
      var rules = getRules(selector, serialized);

      if (cache.compat === undefined) {
        // in regular mode, we don't set the styles on the inserted cache
        // since we don't need to and that would be wasting memory
        // we return them so that they are rendered in a style tag
        if (shouldCache) {
          cache.inserted[name] = true;
        }

        return rules;
      } else {
        // in compat mode, we put the styles on the inserted cache so
        // that emotion-server can pull out the styles
        // except when we don't want to cache it which was in Global but now
        // is nowhere but we don't want to do a major right now
        // and just in case we're going to leave the case here
        // it's also not affecting client side bundle size
        // so it's really not a big deal
        if (shouldCache) {
          cache.inserted[name] = rules;
        } else {
          return rules;
        }
      }
    };
  }

  var cache = {
    key: key,
    sheet: new StyleSheet({
      key: key,
      container: container,
      nonce: options.nonce,
      speedy: options.speedy,
      prepend: options.prepend,
      insertionPoint: options.insertionPoint
    }),
    nonce: options.nonce,
    inserted: inserted,
    registered: {},
    insert: _insert
  };
  cache.sheet.hydrate(nodesToHydrate);
  return cache;
};

var reactIs$1 = {exports: {}};

var reactIs_production_min$1 = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min$1;

function requireReactIs_production_min$1 () {
	if (hasRequiredReactIs_production_min$1) return reactIs_production_min$1;
	hasRequiredReactIs_production_min$1 = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min$1.AsyncMode=l;reactIs_production_min$1.ConcurrentMode=m;reactIs_production_min$1.ContextConsumer=k;reactIs_production_min$1.ContextProvider=h;reactIs_production_min$1.Element=c;reactIs_production_min$1.ForwardRef=n;reactIs_production_min$1.Fragment=e;reactIs_production_min$1.Lazy=t;reactIs_production_min$1.Memo=r;reactIs_production_min$1.Portal=d;
	reactIs_production_min$1.Profiler=g;reactIs_production_min$1.StrictMode=f;reactIs_production_min$1.Suspense=p;reactIs_production_min$1.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min$1.isConcurrentMode=A;reactIs_production_min$1.isContextConsumer=function(a){return z(a)===k};reactIs_production_min$1.isContextProvider=function(a){return z(a)===h};reactIs_production_min$1.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min$1.isForwardRef=function(a){return z(a)===n};reactIs_production_min$1.isFragment=function(a){return z(a)===e};reactIs_production_min$1.isLazy=function(a){return z(a)===t};
	reactIs_production_min$1.isMemo=function(a){return z(a)===r};reactIs_production_min$1.isPortal=function(a){return z(a)===d};reactIs_production_min$1.isProfiler=function(a){return z(a)===g};reactIs_production_min$1.isStrictMode=function(a){return z(a)===f};reactIs_production_min$1.isSuspense=function(a){return z(a)===p};
	reactIs_production_min$1.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min$1.typeOf=z;
	return reactIs_production_min$1;
}

var reactIs_development$1 = {};

var hasRequiredReactIs_development$1;

function requireReactIs_development$1 () {
	if (hasRequiredReactIs_development$1) return reactIs_development$1;
	hasRequiredReactIs_development$1 = 1;



	if (browser$1.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development$1.AsyncMode = AsyncMode;
	reactIs_development$1.ConcurrentMode = ConcurrentMode;
	reactIs_development$1.ContextConsumer = ContextConsumer;
	reactIs_development$1.ContextProvider = ContextProvider;
	reactIs_development$1.Element = Element;
	reactIs_development$1.ForwardRef = ForwardRef;
	reactIs_development$1.Fragment = Fragment;
	reactIs_development$1.Lazy = Lazy;
	reactIs_development$1.Memo = Memo;
	reactIs_development$1.Portal = Portal;
	reactIs_development$1.Profiler = Profiler;
	reactIs_development$1.StrictMode = StrictMode;
	reactIs_development$1.Suspense = Suspense;
	reactIs_development$1.isAsyncMode = isAsyncMode;
	reactIs_development$1.isConcurrentMode = isConcurrentMode;
	reactIs_development$1.isContextConsumer = isContextConsumer;
	reactIs_development$1.isContextProvider = isContextProvider;
	reactIs_development$1.isElement = isElement;
	reactIs_development$1.isForwardRef = isForwardRef;
	reactIs_development$1.isFragment = isFragment;
	reactIs_development$1.isLazy = isLazy;
	reactIs_development$1.isMemo = isMemo;
	reactIs_development$1.isPortal = isPortal;
	reactIs_development$1.isProfiler = isProfiler;
	reactIs_development$1.isStrictMode = isStrictMode;
	reactIs_development$1.isSuspense = isSuspense;
	reactIs_development$1.isValidElementType = isValidElementType;
	reactIs_development$1.typeOf = typeOf;
	  })();
	}
	return reactIs_development$1;
}

var hasRequiredReactIs$1;

function requireReactIs$1 () {
	if (hasRequiredReactIs$1) return reactIs$1.exports;
	hasRequiredReactIs$1 = 1;

	if (browser$1.env.NODE_ENV === 'production') {
	  reactIs$1.exports = requireReactIs_production_min$1();
	} else {
	  reactIs$1.exports = requireReactIs_development$1();
	}
	return reactIs$1.exports;
}

var hoistNonReactStatics_cjs;
var hasRequiredHoistNonReactStatics_cjs;

function requireHoistNonReactStatics_cjs () {
	if (hasRequiredHoistNonReactStatics_cjs) return hoistNonReactStatics_cjs;
	hasRequiredHoistNonReactStatics_cjs = 1;

	var reactIs = requireReactIs$1();

	/**
	 * Copyright 2015, Yahoo! Inc.
	 * Copyrights licensed under the New BSD License. See the accompanying LICENSE file for terms.
	 */
	var REACT_STATICS = {
	  childContextTypes: true,
	  contextType: true,
	  contextTypes: true,
	  defaultProps: true,
	  displayName: true,
	  getDefaultProps: true,
	  getDerivedStateFromError: true,
	  getDerivedStateFromProps: true,
	  mixins: true,
	  propTypes: true,
	  type: true
	};
	var KNOWN_STATICS = {
	  name: true,
	  length: true,
	  prototype: true,
	  caller: true,
	  callee: true,
	  arguments: true,
	  arity: true
	};
	var FORWARD_REF_STATICS = {
	  '$$typeof': true,
	  render: true,
	  defaultProps: true,
	  displayName: true,
	  propTypes: true
	};
	var MEMO_STATICS = {
	  '$$typeof': true,
	  compare: true,
	  defaultProps: true,
	  displayName: true,
	  propTypes: true,
	  type: true
	};
	var TYPE_STATICS = {};
	TYPE_STATICS[reactIs.ForwardRef] = FORWARD_REF_STATICS;
	TYPE_STATICS[reactIs.Memo] = MEMO_STATICS;

	function getStatics(component) {
	  // React v16.11 and below
	  if (reactIs.isMemo(component)) {
	    return MEMO_STATICS;
	  } // React v16.12 and above


	  return TYPE_STATICS[component['$$typeof']] || REACT_STATICS;
	}

	var defineProperty = Object.defineProperty;
	var getOwnPropertyNames = Object.getOwnPropertyNames;
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
	var getPrototypeOf = Object.getPrototypeOf;
	var objectPrototype = Object.prototype;
	function hoistNonReactStatics(targetComponent, sourceComponent, blacklist) {
	  if (typeof sourceComponent !== 'string') {
	    // don't hoist over string (html) components
	    if (objectPrototype) {
	      var inheritedComponent = getPrototypeOf(sourceComponent);

	      if (inheritedComponent && inheritedComponent !== objectPrototype) {
	        hoistNonReactStatics(targetComponent, inheritedComponent, blacklist);
	      }
	    }

	    var keys = getOwnPropertyNames(sourceComponent);

	    if (getOwnPropertySymbols) {
	      keys = keys.concat(getOwnPropertySymbols(sourceComponent));
	    }

	    var targetStatics = getStatics(targetComponent);
	    var sourceStatics = getStatics(sourceComponent);

	    for (var i = 0; i < keys.length; ++i) {
	      var key = keys[i];

	      if (!KNOWN_STATICS[key] && !(blacklist && blacklist[key]) && !(sourceStatics && sourceStatics[key]) && !(targetStatics && targetStatics[key])) {
	        var descriptor = getOwnPropertyDescriptor(sourceComponent, key);

	        try {
	          // Avoid failures from read-only properties
	          defineProperty(targetComponent, key, descriptor);
	        } catch (e) {}
	      }
	    }
	  }

	  return targetComponent;
	}

	hoistNonReactStatics_cjs = hoistNonReactStatics;
	return hoistNonReactStatics_cjs;
}

requireHoistNonReactStatics_cjs();

var isBrowser$2 = typeof document !== 'undefined';

function getRegisteredStyles(registered, registeredStyles, classNames) {
  var rawClassName = '';
  classNames.split(' ').forEach(function (className) {
    if (registered[className] !== undefined) {
      registeredStyles.push(registered[className] + ";");
    } else if (className) {
      rawClassName += className + " ";
    }
  });
  return rawClassName;
}
var registerStyles = function registerStyles(cache, serialized, isStringTag) {
  var className = cache.key + "-" + serialized.name;

  if ( // we only need to add the styles to the registered cache if the
  // class name could be used further down
  // the tree but if it's a string tag, we know it won't
  // so we don't have to add it to registered cache.
  // this improves memory usage since we can avoid storing the whole style string
  (isStringTag === false || // we need to always store it if we're in compat mode and
  // in node since emotion-server relies on whether a style is in
  // the registered cache to know whether a style is global or not
  // also, note that this check will be dead code eliminated in the browser
  isBrowser$2 === false && cache.compat !== undefined) && cache.registered[className] === undefined) {
    cache.registered[className] = serialized.styles;
  }
};
var insertStyles = function insertStyles(cache, serialized, isStringTag) {
  registerStyles(cache, serialized, isStringTag);
  var className = cache.key + "-" + serialized.name;

  if (cache.inserted[serialized.name] === undefined) {
    var stylesForSSR = '';
    var current = serialized;

    do {
      var maybeStyles = cache.insert(serialized === current ? "." + className : '', current, cache.sheet, true);

      if (!isBrowser$2 && maybeStyles !== undefined) {
        stylesForSSR += maybeStyles;
      }

      current = current.next;
    } while (current !== undefined);

    if (!isBrowser$2 && stylesForSSR.length !== 0) {
      return stylesForSSR;
    }
  }
};

/* eslint-disable */
// Inspired by https://github.com/garycourt/murmurhash-js
// Ported from https://github.com/aappleby/smhasher/blob/61a0530f28277f2e850bfc39600ce61d02b518de/src/MurmurHash2.cpp#L37-L86
function murmur2(str) {
  // 'm' and 'r' are mixing constants generated offline.
  // They're not really 'magic', they just happen to work well.
  // const m = 0x5bd1e995;
  // const r = 24;
  // Initialize the hash
  var h = 0; // Mix 4 bytes at a time into the hash

  var k,
      i = 0,
      len = str.length;

  for (; len >= 4; ++i, len -= 4) {
    k = str.charCodeAt(i) & 0xff | (str.charCodeAt(++i) & 0xff) << 8 | (str.charCodeAt(++i) & 0xff) << 16 | (str.charCodeAt(++i) & 0xff) << 24;
    k =
    /* Math.imul(k, m): */
    (k & 0xffff) * 0x5bd1e995 + ((k >>> 16) * 0xe995 << 16);
    k ^=
    /* k >>> r: */
    k >>> 24;
    h =
    /* Math.imul(k, m): */
    (k & 0xffff) * 0x5bd1e995 + ((k >>> 16) * 0xe995 << 16) ^
    /* Math.imul(h, m): */
    (h & 0xffff) * 0x5bd1e995 + ((h >>> 16) * 0xe995 << 16);
  } // Handle the last few bytes of the input array


  switch (len) {
    case 3:
      h ^= (str.charCodeAt(i + 2) & 0xff) << 16;

    case 2:
      h ^= (str.charCodeAt(i + 1) & 0xff) << 8;

    case 1:
      h ^= str.charCodeAt(i) & 0xff;
      h =
      /* Math.imul(h, m): */
      (h & 0xffff) * 0x5bd1e995 + ((h >>> 16) * 0xe995 << 16);
  } // Do a few final mixes of the hash to ensure the last few
  // bytes are well-incorporated.


  h ^= h >>> 13;
  h =
  /* Math.imul(h, m): */
  (h & 0xffff) * 0x5bd1e995 + ((h >>> 16) * 0xe995 << 16);
  return ((h ^ h >>> 15) >>> 0).toString(36);
}

var unitlessKeys = {
  animationIterationCount: 1,
  aspectRatio: 1,
  borderImageOutset: 1,
  borderImageSlice: 1,
  borderImageWidth: 1,
  boxFlex: 1,
  boxFlexGroup: 1,
  boxOrdinalGroup: 1,
  columnCount: 1,
  columns: 1,
  flex: 1,
  flexGrow: 1,
  flexPositive: 1,
  flexShrink: 1,
  flexNegative: 1,
  flexOrder: 1,
  gridRow: 1,
  gridRowEnd: 1,
  gridRowSpan: 1,
  gridRowStart: 1,
  gridColumn: 1,
  gridColumnEnd: 1,
  gridColumnSpan: 1,
  gridColumnStart: 1,
  msGridRow: 1,
  msGridRowSpan: 1,
  msGridColumn: 1,
  msGridColumnSpan: 1,
  fontWeight: 1,
  lineHeight: 1,
  opacity: 1,
  order: 1,
  orphans: 1,
  scale: 1,
  tabSize: 1,
  widows: 1,
  zIndex: 1,
  zoom: 1,
  WebkitLineClamp: 1,
  // SVG-related properties
  fillOpacity: 1,
  floodOpacity: 1,
  stopOpacity: 1,
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeMiterlimit: 1,
  strokeOpacity: 1,
  strokeWidth: 1
};

var hyphenateRegex = /[A-Z]|^ms/g;
var animationRegex = /_EMO_([^_]+?)_([^]*?)_EMO_/g;

var isCustomProperty = function isCustomProperty(property) {
  return property.charCodeAt(1) === 45;
};

var isProcessableValue = function isProcessableValue(value) {
  return value != null && typeof value !== 'boolean';
};

var processStyleName = /* #__PURE__ */memoize(function (styleName) {
  return isCustomProperty(styleName) ? styleName : styleName.replace(hyphenateRegex, '-$&').toLowerCase();
});

var processStyleValue = function processStyleValue(key, value) {
  switch (key) {
    case 'animation':
    case 'animationName':
      {
        if (typeof value === 'string') {
          return value.replace(animationRegex, function (match, p1, p2) {
            cursor = {
              name: p1,
              styles: p2,
              next: cursor
            };
            return p1;
          });
        }
      }
  }

  if (unitlessKeys[key] !== 1 && !isCustomProperty(key) && typeof value === 'number' && value !== 0) {
    return value + 'px';
  }

  return value;
};

function handleInterpolation(mergedProps, registered, interpolation) {
  if (interpolation == null) {
    return '';
  }

  var componentSelector = interpolation;

  if (componentSelector.__emotion_styles !== undefined) {

    return componentSelector;
  }

  switch (typeof interpolation) {
    case 'boolean':
      {
        return '';
      }

    case 'object':
      {
        var keyframes = interpolation;

        if (keyframes.anim === 1) {
          cursor = {
            name: keyframes.name,
            styles: keyframes.styles,
            next: cursor
          };
          return keyframes.name;
        }

        var serializedStyles = interpolation;

        if (serializedStyles.styles !== undefined) {
          var next = serializedStyles.next;

          if (next !== undefined) {
            // not the most efficient thing ever but this is a pretty rare case
            // and there will be very few iterations of this generally
            while (next !== undefined) {
              cursor = {
                name: next.name,
                styles: next.styles,
                next: cursor
              };
              next = next.next;
            }
          }

          var styles = serializedStyles.styles + ";";
          return styles;
        }

        return createStringFromObject(mergedProps, registered, interpolation);
      }

    case 'function':
      {
        if (mergedProps !== undefined) {
          var previousCursor = cursor;
          var result = interpolation(mergedProps);
          cursor = previousCursor;
          return handleInterpolation(mergedProps, registered, result);
        }

        break;
      }
  } // finalize string values (regular strings and functions interpolated into css calls)


  var asString = interpolation;

  {
    return asString;
  }
}

function createStringFromObject(mergedProps, registered, obj) {
  var string = '';

  if (Array.isArray(obj)) {
    for (var i = 0; i < obj.length; i++) {
      string += handleInterpolation(mergedProps, registered, obj[i]) + ";";
    }
  } else {
    for (var key in obj) {
      var value = obj[key];

      if (typeof value !== 'object') {
        var asString = value;

        if (isProcessableValue(asString)) {
          string += processStyleName(key) + ":" + processStyleValue(key, asString) + ";";
        }
      } else {

        if (Array.isArray(value) && typeof value[0] === 'string' && (registered == null)) {
          for (var _i = 0; _i < value.length; _i++) {
            if (isProcessableValue(value[_i])) {
              string += processStyleName(key) + ":" + processStyleValue(key, value[_i]) + ";";
            }
          }
        } else {
          var interpolated = handleInterpolation(mergedProps, registered, value);

          switch (key) {
            case 'animation':
            case 'animationName':
              {
                string += processStyleName(key) + ":" + interpolated + ";";
                break;
              }

            default:
              {

                string += key + "{" + interpolated + "}";
              }
          }
        }
      }
    }
  }

  return string;
}

var labelPattern = /label:\s*([^\s;{]+)\s*(;|$)/g; // this is the cursor for keyframes
// keyframes are stored on the SerializedStyles object as a linked list

var cursor;
function serializeStyles(args, registered, mergedProps) {
  if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null && args[0].styles !== undefined) {
    return args[0];
  }

  var stringMode = true;
  var styles = '';
  cursor = undefined;
  var strings = args[0];

  if (strings == null || strings.raw === undefined) {
    stringMode = false;
    styles += handleInterpolation(mergedProps, registered, strings);
  } else {
    var asTemplateStringsArr = strings;

    styles += asTemplateStringsArr[0];
  } // we start at 1 since we've already handled the first arg


  for (var i = 1; i < args.length; i++) {
    styles += handleInterpolation(mergedProps, registered, args[i]);

    if (stringMode) {
      var templateStringsArr = strings;

      styles += templateStringsArr[i];
    }
  } // using a global regex with .exec is stateful so lastIndex has to be reset each time


  labelPattern.lastIndex = 0;
  var identifierName = '';
  var match; // https://esbench.com/bench/5b809c2cf2949800a0f61fb5

  while ((match = labelPattern.exec(styles)) !== null) {
    identifierName += '-' + match[1];
  }

  var name = murmur2(styles) + identifierName;

  return {
    name: name,
    styles: styles,
    next: cursor
  };
}

var isBrowser$1 = typeof document !== 'undefined';

var syncFallback = function syncFallback(create) {
  return create();
};

var useInsertionEffect$1 = React['useInsertion' + 'Effect'] ? React['useInsertion' + 'Effect'] : false;
var useInsertionEffectAlwaysWithSyncFallback = !isBrowser$1 ? syncFallback : useInsertionEffect$1 || syncFallback;

var isBrowser = typeof document !== 'undefined';

var EmotionCacheContext = /* #__PURE__ */React.createContext( // we're doing this to avoid preconstruct's dead code elimination in this one case
// because this module is primarily intended for the browser and node
// but it's also required in react native and similar environments sometimes
// and we could have a special build just for that
// but this is much easier and the native packages
// might use a different theme context in the future anyway
typeof HTMLElement !== 'undefined' ? /* #__PURE__ */createCache({
  key: 'css'
}) : null);

EmotionCacheContext.Provider;

var withEmotionCache = function withEmotionCache(func) {
  return /*#__PURE__*/forwardRef(function (props, ref) {
    // the cache will never be null in the browser
    var cache = useContext(EmotionCacheContext);
    return func(props, cache, ref);
  });
};

if (!isBrowser) {
  withEmotionCache = function withEmotionCache(func) {
    return function (props) {
      var cache = useContext(EmotionCacheContext);

      if (cache === null) {
        // yes, we're potentially creating this on every render
        // it doesn't actually matter though since it's only on the server
        // so there will only every be a single render
        // that could change in the future because of suspense and etc. but for now,
        // this works and i don't want to optimise for a future thing that we aren't sure about
        cache = createCache({
          key: 'css'
        });
        return /*#__PURE__*/React.createElement(EmotionCacheContext.Provider, {
          value: cache
        }, func(props, cache));
      } else {
        return func(props, cache);
      }
    };
  };
}

var ThemeContext = /* #__PURE__ */React.createContext({});

var hasOwn = {}.hasOwnProperty;

var typePropName = '__EMOTION_TYPE_PLEASE_DO_NOT_USE__';
var createEmotionProps = function createEmotionProps(type, props) {

  var newProps = {};

  for (var _key in props) {
    if (hasOwn.call(props, _key)) {
      newProps[_key] = props[_key];
    }
  }

  newProps[typePropName] = type; // Runtime labeling is an opt-in feature because:

  return newProps;
};

var Insertion = function Insertion(_ref) {
  var cache = _ref.cache,
      serialized = _ref.serialized,
      isStringTag = _ref.isStringTag;
  registerStyles(cache, serialized, isStringTag);
  var rules = useInsertionEffectAlwaysWithSyncFallback(function () {
    return insertStyles(cache, serialized, isStringTag);
  });

  if (!isBrowser && rules !== undefined) {
    var _ref2;

    var serializedNames = serialized.name;
    var next = serialized.next;

    while (next !== undefined) {
      serializedNames += ' ' + next.name;
      next = next.next;
    }

    return /*#__PURE__*/React.createElement("style", (_ref2 = {}, _ref2["data-emotion"] = cache.key + " " + serializedNames, _ref2.dangerouslySetInnerHTML = {
      __html: rules
    }, _ref2.nonce = cache.sheet.nonce, _ref2));
  }

  return null;
};

var Emotion = /* #__PURE__ */withEmotionCache(function (props, cache, ref) {
  var cssProp = props.css; // so that using `css` from `emotion` and passing the result to the css prop works
  // not passing the registered cache to serializeStyles because it would
  // make certain babel optimisations not possible

  if (typeof cssProp === 'string' && cache.registered[cssProp] !== undefined) {
    cssProp = cache.registered[cssProp];
  }

  var WrappedComponent = props[typePropName];
  var registeredStyles = [cssProp];
  var className = '';

  if (typeof props.className === 'string') {
    className = getRegisteredStyles(cache.registered, registeredStyles, props.className);
  } else if (props.className != null) {
    className = props.className + " ";
  }

  var serialized = serializeStyles(registeredStyles, undefined, React.useContext(ThemeContext));

  className += cache.key + "-" + serialized.name;
  var newProps = {};

  for (var _key2 in props) {
    if (hasOwn.call(props, _key2) && _key2 !== 'css' && _key2 !== typePropName && (true )) {
      newProps[_key2] = props[_key2];
    }
  }

  newProps.className = className;

  if (ref) {
    newProps.ref = ref;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Insertion, {
    cache: cache,
    serialized: serialized,
    isStringTag: typeof WrappedComponent === 'string'
  }), /*#__PURE__*/React.createElement(WrappedComponent, newProps));
});

var Emotion$1 = Emotion;

var jsx = function jsx(type, props) {
  // eslint-disable-next-line prefer-rest-params
  var args = arguments;

  if (props == null || !hasOwn.call(props, 'css')) {
    return React.createElement.apply(undefined, args);
  }

  var argsLength = args.length;
  var createElementArgArray = new Array(argsLength);
  createElementArgArray[0] = Emotion$1;
  createElementArgArray[1] = createEmotionProps(type, props);

  for (var i = 2; i < argsLength; i++) {
    createElementArgArray[i] = args[i];
  }

  return React.createElement.apply(null, createElementArgArray);
};

(function (_jsx) {
  var JSX;

  (function (_JSX) {})(JSX || (JSX = _jsx.JSX || (_jsx.JSX = {})));
})(jsx || (jsx = {}));

function css$2() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return serializeStyles(args);
}

function keyframes() {
  var insertable = css$2.apply(void 0, arguments);
  var name = "animation-" + insertable.name;
  return {
    name: name,
    styles: "@keyframes " + name + "{" + insertable.styles + "}",
    anim: 1,
    toString: function toString() {
      return "_EMO_" + this.name + "_" + this.styles + "_EMO_";
    }
  };
}

function _taggedTemplateLiteral(e, t) {
  return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, {
    raw: {
      value: Object.freeze(t)
    }
  }));
}

var isClient = typeof document !== 'undefined';

var index$1 = isClient ? useLayoutEffect : useEffect;

var _excluded$4 = ["className", "clearValue", "cx", "getStyles", "getClassNames", "getValue", "hasValue", "isMulti", "isRtl", "options", "selectOption", "selectProps", "setValue", "theme"];
// ==============================
// NO OP
// ==============================

var noop = function noop() {};

// ==============================
// Class Name Prefixer
// ==============================

/**
 String representation of component state for styling with class names.

 Expects an array of strings OR a string/object pair:
 - className(['comp', 'comp-arg', 'comp-arg-2'])
   @returns 'react-select__comp react-select__comp-arg react-select__comp-arg-2'
 - className('comp', { some: true, state: false })
   @returns 'react-select__comp react-select__comp--some'
*/
function applyPrefixToName(prefix, name) {
  if (!name) {
    return prefix;
  } else if (name[0] === '-') {
    return prefix + name;
  } else {
    return prefix + '__' + name;
  }
}
function classNames(prefix, state) {
  for (var _len = arguments.length, classNameList = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    classNameList[_key - 2] = arguments[_key];
  }
  var arr = [].concat(classNameList);
  if (state && prefix) {
    for (var key in state) {
      if (state.hasOwnProperty(key) && state[key]) {
        arr.push("".concat(applyPrefixToName(prefix, key)));
      }
    }
  }
  return arr.filter(function (i) {
    return i;
  }).map(function (i) {
    return String(i).trim();
  }).join(' ');
}
// ==============================
// Clean Value
// ==============================

var cleanValue = function cleanValue(value) {
  if (isArray(value)) return value.filter(Boolean);
  if (_typeof(value) === 'object' && value !== null) return [value];
  return [];
};

// ==============================
// Clean Common Props
// ==============================

var cleanCommonProps = function cleanCommonProps(props) {
  //className
  props.className;
    props.clearValue;
    props.cx;
    props.getStyles;
    props.getClassNames;
    props.getValue;
    props.hasValue;
    props.isMulti;
    props.isRtl;
    props.options;
    props.selectOption;
    props.selectProps;
    props.setValue;
    props.theme;
    var innerProps = _objectWithoutProperties(props, _excluded$4);
  return _objectSpread2({}, innerProps);
};

// ==============================
// Get Style Props
// ==============================

var getStyleProps = function getStyleProps(props, name, classNamesState) {
  var cx = props.cx,
    getStyles = props.getStyles,
    getClassNames = props.getClassNames,
    className = props.className;
  return {
    css: getStyles(name, props),
    className: cx(classNamesState !== null && classNamesState !== void 0 ? classNamesState : {}, getClassNames(name, props), className)
  };
};

// ==============================
// Scroll Helpers
// ==============================

function isDocumentElement(el) {
  return [document.documentElement, document.body, window].indexOf(el) > -1;
}

// Normalized Scroll Top
// ------------------------------

function normalizedHeight(el) {
  if (isDocumentElement(el)) {
    return window.innerHeight;
  }
  return el.clientHeight;
}

// Normalized scrollTo & scrollTop
// ------------------------------

function getScrollTop(el) {
  if (isDocumentElement(el)) {
    return window.pageYOffset;
  }
  return el.scrollTop;
}
function scrollTo(el, top) {
  // with a scroll distance, we perform scroll on the element
  if (isDocumentElement(el)) {
    window.scrollTo(0, top);
    return;
  }
  el.scrollTop = top;
}

// Get Scroll Parent
// ------------------------------

function getScrollParent(element) {
  var style = getComputedStyle(element);
  var excludeStaticParent = style.position === 'absolute';
  var overflowRx = /(auto|scroll)/;
  if (style.position === 'fixed') return document.documentElement;
  for (var parent = element; parent = parent.parentElement;) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === 'static') {
      continue;
    }
    if (overflowRx.test(style.overflow + style.overflowY + style.overflowX)) {
      return parent;
    }
  }
  return document.documentElement;
}

// Animated Scroll To
// ------------------------------

/**
  @param t: time (elapsed)
  @param b: initial value
  @param c: amount of change
  @param d: duration
*/
function easeOutCubic(t, b, c, d) {
  return c * ((t = t / d - 1) * t * t + 1) + b;
}
function animatedScrollTo(element, to) {
  var duration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 200;
  var callback = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  var start = getScrollTop(element);
  var change = to - start;
  var increment = 10;
  var currentTime = 0;
  function animateScroll() {
    currentTime += increment;
    var val = easeOutCubic(currentTime, start, change, duration);
    scrollTo(element, val);
    if (currentTime < duration) {
      window.requestAnimationFrame(animateScroll);
    } else {
      callback(element);
    }
  }
  animateScroll();
}

// Scroll Into View
// ------------------------------

function scrollIntoView(menuEl, focusedEl) {
  var menuRect = menuEl.getBoundingClientRect();
  var focusedRect = focusedEl.getBoundingClientRect();
  var overScroll = focusedEl.offsetHeight / 3;
  if (focusedRect.bottom + overScroll > menuRect.bottom) {
    scrollTo(menuEl, Math.min(focusedEl.offsetTop + focusedEl.clientHeight - menuEl.offsetHeight + overScroll, menuEl.scrollHeight));
  } else if (focusedRect.top - overScroll < menuRect.top) {
    scrollTo(menuEl, Math.max(focusedEl.offsetTop - overScroll, 0));
  }
}

// ==============================
// Get bounding client object
// ==============================

// cannot get keys using array notation with DOMRect
function getBoundingClientObj(element) {
  var rect = element.getBoundingClientRect();
  return {
    bottom: rect.bottom,
    height: rect.height,
    left: rect.left,
    right: rect.right,
    top: rect.top,
    width: rect.width
  };
}

// ==============================
// Touch Capability Detector
// ==============================

function isTouchCapable() {
  try {
    document.createEvent('TouchEvent');
    return true;
  } catch (e) {
    return false;
  }
}

// ==============================
// Mobile Device Detector
// ==============================

function isMobileDevice() {
  try {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  } catch (e) {
    return false;
  }
}

// ==============================
// Passive Event Detector
// ==============================

// https://github.com/rafgraph/detect-it/blob/main/src/index.ts#L19-L36
var passiveOptionAccessed = false;
var options = {
  get passive() {
    return passiveOptionAccessed = true;
  }
};
// check for SSR
var w$1 = typeof window !== 'undefined' ? window : {};
if (w$1.addEventListener && w$1.removeEventListener) {
  w$1.addEventListener('p', noop, options);
  w$1.removeEventListener('p', noop, false);
}
var supportsPassiveEvents = passiveOptionAccessed;
function notNullish(item) {
  return item != null;
}
function isArray(arg) {
  return Array.isArray(arg);
}
function valueTernary(isMulti, multiValue, singleValue) {
  return isMulti ? multiValue : singleValue;
}
function singleValueAsValue(singleValue) {
  return singleValue;
}
function multiValueAsValue(multiValue) {
  return multiValue;
}
var removeProps = function removeProps(propsObj) {
  for (var _len2 = arguments.length, properties = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    properties[_key2 - 1] = arguments[_key2];
  }
  var propsMap = Object.entries(propsObj).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 1),
      key = _ref2[0];
    return !properties.includes(key);
  });
  return propsMap.reduce(function (newProps, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      key = _ref4[0],
      val = _ref4[1];
    newProps[key] = val;
    return newProps;
  }, {});
};

var _excluded$3 = ["children", "innerProps"],
  _excluded2$1 = ["children", "innerProps"];
function getMenuPlacement(_ref) {
  var preferredMaxHeight = _ref.maxHeight,
    menuEl = _ref.menuEl,
    minHeight = _ref.minHeight,
    preferredPlacement = _ref.placement,
    shouldScroll = _ref.shouldScroll,
    isFixedPosition = _ref.isFixedPosition,
    controlHeight = _ref.controlHeight;
  var scrollParent = getScrollParent(menuEl);
  var defaultState = {
    placement: 'bottom',
    maxHeight: preferredMaxHeight
  };

  // something went wrong, return default state
  if (!menuEl || !menuEl.offsetParent) return defaultState;

  // we can't trust `scrollParent.scrollHeight` --> it may increase when
  // the menu is rendered
  var _scrollParent$getBoun = scrollParent.getBoundingClientRect(),
    scrollHeight = _scrollParent$getBoun.height;
  var _menuEl$getBoundingCl = menuEl.getBoundingClientRect(),
    menuBottom = _menuEl$getBoundingCl.bottom,
    menuHeight = _menuEl$getBoundingCl.height,
    menuTop = _menuEl$getBoundingCl.top;
  var _menuEl$offsetParent$ = menuEl.offsetParent.getBoundingClientRect(),
    containerTop = _menuEl$offsetParent$.top;
  var viewHeight = isFixedPosition ? window.innerHeight : normalizedHeight(scrollParent);
  var scrollTop = getScrollTop(scrollParent);
  var marginBottom = parseInt(getComputedStyle(menuEl).marginBottom, 10);
  var marginTop = parseInt(getComputedStyle(menuEl).marginTop, 10);
  var viewSpaceAbove = containerTop - marginTop;
  var viewSpaceBelow = viewHeight - menuTop;
  var scrollSpaceAbove = viewSpaceAbove + scrollTop;
  var scrollSpaceBelow = scrollHeight - scrollTop - menuTop;
  var scrollDown = menuBottom - viewHeight + scrollTop + marginBottom;
  var scrollUp = scrollTop + menuTop - marginTop;
  var scrollDuration = 160;
  switch (preferredPlacement) {
    case 'auto':
    case 'bottom':
      // 1: the menu will fit, do nothing
      if (viewSpaceBelow >= menuHeight) {
        return {
          placement: 'bottom',
          maxHeight: preferredMaxHeight
        };
      }

      // 2: the menu will fit, if scrolled
      if (scrollSpaceBelow >= menuHeight && !isFixedPosition) {
        if (shouldScroll) {
          animatedScrollTo(scrollParent, scrollDown, scrollDuration);
        }
        return {
          placement: 'bottom',
          maxHeight: preferredMaxHeight
        };
      }

      // 3: the menu will fit, if constrained
      if (!isFixedPosition && scrollSpaceBelow >= minHeight || isFixedPosition && viewSpaceBelow >= minHeight) {
        if (shouldScroll) {
          animatedScrollTo(scrollParent, scrollDown, scrollDuration);
        }

        // we want to provide as much of the menu as possible to the user,
        // so give them whatever is available below rather than the minHeight.
        var constrainedHeight = isFixedPosition ? viewSpaceBelow - marginBottom : scrollSpaceBelow - marginBottom;
        return {
          placement: 'bottom',
          maxHeight: constrainedHeight
        };
      }

      // 4. Forked beviour when there isn't enough space below

      // AUTO: flip the menu, render above
      if (preferredPlacement === 'auto' || isFixedPosition) {
        // may need to be constrained after flipping
        var _constrainedHeight = preferredMaxHeight;
        var spaceAbove = isFixedPosition ? viewSpaceAbove : scrollSpaceAbove;
        if (spaceAbove >= minHeight) {
          _constrainedHeight = Math.min(spaceAbove - marginBottom - controlHeight, preferredMaxHeight);
        }
        return {
          placement: 'top',
          maxHeight: _constrainedHeight
        };
      }

      // BOTTOM: allow browser to increase scrollable area and immediately set scroll
      if (preferredPlacement === 'bottom') {
        if (shouldScroll) {
          scrollTo(scrollParent, scrollDown);
        }
        return {
          placement: 'bottom',
          maxHeight: preferredMaxHeight
        };
      }
      break;
    case 'top':
      // 1: the menu will fit, do nothing
      if (viewSpaceAbove >= menuHeight) {
        return {
          placement: 'top',
          maxHeight: preferredMaxHeight
        };
      }

      // 2: the menu will fit, if scrolled
      if (scrollSpaceAbove >= menuHeight && !isFixedPosition) {
        if (shouldScroll) {
          animatedScrollTo(scrollParent, scrollUp, scrollDuration);
        }
        return {
          placement: 'top',
          maxHeight: preferredMaxHeight
        };
      }

      // 3: the menu will fit, if constrained
      if (!isFixedPosition && scrollSpaceAbove >= minHeight || isFixedPosition && viewSpaceAbove >= minHeight) {
        var _constrainedHeight2 = preferredMaxHeight;

        // we want to provide as much of the menu as possible to the user,
        // so give them whatever is available below rather than the minHeight.
        if (!isFixedPosition && scrollSpaceAbove >= minHeight || isFixedPosition && viewSpaceAbove >= minHeight) {
          _constrainedHeight2 = isFixedPosition ? viewSpaceAbove - marginTop : scrollSpaceAbove - marginTop;
        }
        if (shouldScroll) {
          animatedScrollTo(scrollParent, scrollUp, scrollDuration);
        }
        return {
          placement: 'top',
          maxHeight: _constrainedHeight2
        };
      }

      // 4. not enough space, the browser WILL NOT increase scrollable area when
      // absolutely positioned element rendered above the viewport (only below).
      // Flip the menu, render below
      return {
        placement: 'bottom',
        maxHeight: preferredMaxHeight
      };
    default:
      throw new Error("Invalid placement provided \"".concat(preferredPlacement, "\"."));
  }
  return defaultState;
}

// Menu Component
// ------------------------------

function alignToControl(placement) {
  var placementToCSSProp = {
    bottom: 'top',
    top: 'bottom'
  };
  return placement ? placementToCSSProp[placement] : 'bottom';
}
var coercePlacement = function coercePlacement(p) {
  return p === 'auto' ? 'bottom' : p;
};
var menuCSS = function menuCSS(_ref2, unstyled) {
  var _objectSpread2$1;
  var placement = _ref2.placement,
    _ref2$theme = _ref2.theme,
    borderRadius = _ref2$theme.borderRadius,
    spacing = _ref2$theme.spacing,
    colors = _ref2$theme.colors;
  return _objectSpread2((_objectSpread2$1 = {
    label: 'menu'
  }, _defineProperty(_objectSpread2$1, alignToControl(placement), '100%'), _defineProperty(_objectSpread2$1, "position", 'absolute'), _defineProperty(_objectSpread2$1, "width", '100%'), _defineProperty(_objectSpread2$1, "zIndex", 1), _objectSpread2$1), unstyled ? {} : {
    backgroundColor: colors.neutral0,
    borderRadius: borderRadius,
    boxShadow: '0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1)',
    marginBottom: spacing.menuGutter,
    marginTop: spacing.menuGutter
  });
};
var PortalPlacementContext = /*#__PURE__*/createContext(null);

// NOTE: internal only
var MenuPlacer = function MenuPlacer(props) {
  var children = props.children,
    minMenuHeight = props.minMenuHeight,
    maxMenuHeight = props.maxMenuHeight,
    menuPlacement = props.menuPlacement,
    menuPosition = props.menuPosition,
    menuShouldScrollIntoView = props.menuShouldScrollIntoView,
    theme = props.theme;
  var _ref3 = useContext(PortalPlacementContext) || {},
    setPortalPlacement = _ref3.setPortalPlacement;
  var ref = useRef(null);
  var _useState = useState(maxMenuHeight),
    _useState2 = _slicedToArray(_useState, 2),
    maxHeight = _useState2[0],
    setMaxHeight = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    placement = _useState4[0],
    setPlacement = _useState4[1];
  var controlHeight = theme.spacing.controlHeight;
  index$1(function () {
    var menuEl = ref.current;
    if (!menuEl) return;

    // DO NOT scroll if position is fixed
    var isFixedPosition = menuPosition === 'fixed';
    var shouldScroll = menuShouldScrollIntoView && !isFixedPosition;
    var state = getMenuPlacement({
      maxHeight: maxMenuHeight,
      menuEl: menuEl,
      minHeight: minMenuHeight,
      placement: menuPlacement,
      shouldScroll: shouldScroll,
      isFixedPosition: isFixedPosition,
      controlHeight: controlHeight
    });
    setMaxHeight(state.maxHeight);
    setPlacement(state.placement);
    setPortalPlacement === null || setPortalPlacement === void 0 ? void 0 : setPortalPlacement(state.placement);
  }, [maxMenuHeight, menuPlacement, menuPosition, menuShouldScrollIntoView, minMenuHeight, setPortalPlacement, controlHeight]);
  return children({
    ref: ref,
    placerProps: _objectSpread2(_objectSpread2({}, props), {}, {
      placement: placement || coercePlacement(menuPlacement),
      maxHeight: maxHeight
    })
  });
};
var Menu$1 = function Menu(props) {
  var children = props.children,
    innerRef = props.innerRef,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'menu', {
    menu: true
  }), {
    ref: innerRef
  }, innerProps), children);
};
var Menu$1$1 = Menu$1;

// ==============================
// Menu List
// ==============================

var menuListCSS = function menuListCSS(_ref4, unstyled) {
  var maxHeight = _ref4.maxHeight,
    baseUnit = _ref4.theme.spacing.baseUnit;
  return _objectSpread2({
    maxHeight: maxHeight,
    overflowY: 'auto',
    position: 'relative',
    // required for offset[Height, Top] > keyboard scroll
    WebkitOverflowScrolling: 'touch'
  }, unstyled ? {} : {
    paddingBottom: baseUnit,
    paddingTop: baseUnit
  });
};
var MenuList = function MenuList(props) {
  var children = props.children,
    innerProps = props.innerProps,
    innerRef = props.innerRef,
    isMulti = props.isMulti;
  return jsx("div", _extends({}, getStyleProps(props, 'menuList', {
    'menu-list': true,
    'menu-list--is-multi': isMulti
  }), {
    ref: innerRef
  }, innerProps), children);
};

// ==============================
// Menu Notices
// ==============================

var noticeCSS = function noticeCSS(_ref5, unstyled) {
  var _ref5$theme = _ref5.theme,
    baseUnit = _ref5$theme.spacing.baseUnit,
    colors = _ref5$theme.colors;
  return _objectSpread2({
    textAlign: 'center'
  }, unstyled ? {} : {
    color: colors.neutral40,
    padding: "".concat(baseUnit * 2, "px ").concat(baseUnit * 3, "px")
  });
};
var noOptionsMessageCSS = noticeCSS;
var loadingMessageCSS = noticeCSS;
var NoOptionsMessage = function NoOptionsMessage(_ref6) {
  var _ref6$children = _ref6.children,
    children = _ref6$children === void 0 ? 'No options' : _ref6$children,
    innerProps = _ref6.innerProps,
    restProps = _objectWithoutProperties(_ref6, _excluded$3);
  return jsx("div", _extends({}, getStyleProps(_objectSpread2(_objectSpread2({}, restProps), {}, {
    children: children,
    innerProps: innerProps
  }), 'noOptionsMessage', {
    'menu-notice': true,
    'menu-notice--no-options': true
  }), innerProps), children);
};
var LoadingMessage = function LoadingMessage(_ref7) {
  var _ref7$children = _ref7.children,
    children = _ref7$children === void 0 ? 'Loading...' : _ref7$children,
    innerProps = _ref7.innerProps,
    restProps = _objectWithoutProperties(_ref7, _excluded2$1);
  return jsx("div", _extends({}, getStyleProps(_objectSpread2(_objectSpread2({}, restProps), {}, {
    children: children,
    innerProps: innerProps
  }), 'loadingMessage', {
    'menu-notice': true,
    'menu-notice--loading': true
  }), innerProps), children);
};

// ==============================
// Menu Portal
// ==============================

var menuPortalCSS = function menuPortalCSS(_ref8) {
  var rect = _ref8.rect,
    offset = _ref8.offset,
    position = _ref8.position;
  return {
    left: rect.left,
    position: position,
    top: offset,
    width: rect.width,
    zIndex: 1
  };
};
var MenuPortal$1 = function MenuPortal(props) {
  var appendTo = props.appendTo,
    children = props.children,
    controlElement = props.controlElement,
    innerProps = props.innerProps,
    menuPlacement = props.menuPlacement,
    menuPosition = props.menuPosition;
  var menuPortalRef = useRef(null);
  var cleanupRef = useRef(null);
  var _useState5 = useState(coercePlacement(menuPlacement)),
    _useState6 = _slicedToArray(_useState5, 2),
    placement = _useState6[0],
    setPortalPlacement = _useState6[1];
  var portalPlacementContext = useMemo(function () {
    return {
      setPortalPlacement: setPortalPlacement
    };
  }, []);
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    computedPosition = _useState8[0],
    setComputedPosition = _useState8[1];
  var updateComputedPosition = useCallback(function () {
    if (!controlElement) return;
    var rect = getBoundingClientObj(controlElement);
    var scrollDistance = menuPosition === 'fixed' ? 0 : window.pageYOffset;
    var offset = rect[placement] + scrollDistance;
    if (offset !== (computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.offset) || rect.left !== (computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.rect.left) || rect.width !== (computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.rect.width)) {
      setComputedPosition({
        offset: offset,
        rect: rect
      });
    }
  }, [controlElement, menuPosition, placement, computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.offset, computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.rect.left, computedPosition === null || computedPosition === void 0 ? void 0 : computedPosition.rect.width]);
  index$1(function () {
    updateComputedPosition();
  }, [updateComputedPosition]);
  var runAutoUpdate = useCallback(function () {
    if (typeof cleanupRef.current === 'function') {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (controlElement && menuPortalRef.current) {
      cleanupRef.current = autoUpdate(controlElement, menuPortalRef.current, updateComputedPosition, {
        elementResize: 'ResizeObserver' in window
      });
    }
  }, [controlElement, updateComputedPosition]);
  index$1(function () {
    runAutoUpdate();
  }, [runAutoUpdate]);
  var setMenuPortalElement = useCallback(function (menuPortalElement) {
    menuPortalRef.current = menuPortalElement;
    runAutoUpdate();
  }, [runAutoUpdate]);

  // bail early if required elements aren't present
  if (!appendTo && menuPosition !== 'fixed' || !computedPosition) return null;

  // same wrapper element whether fixed or portalled
  var menuWrapper = jsx("div", _extends({
    ref: setMenuPortalElement
  }, getStyleProps(_objectSpread2(_objectSpread2({}, props), {}, {
    offset: computedPosition.offset,
    position: menuPosition,
    rect: computedPosition.rect
  }), 'menuPortal', {
    'menu-portal': true
  }), innerProps), children);
  return jsx(PortalPlacementContext.Provider, {
    value: portalPlacementContext
  }, appendTo ? /*#__PURE__*/createPortal(menuWrapper, appendTo) : menuWrapper);
};

// ==============================
// Root Container
// ==============================

var containerCSS = function containerCSS(_ref) {
  var isDisabled = _ref.isDisabled,
    isRtl = _ref.isRtl;
  return {
    label: 'container',
    direction: isRtl ? 'rtl' : undefined,
    pointerEvents: isDisabled ? 'none' : undefined,
    // cancel mouse events when disabled
    position: 'relative'
  };
};
var SelectContainer = function SelectContainer(props) {
  var children = props.children,
    innerProps = props.innerProps,
    isDisabled = props.isDisabled,
    isRtl = props.isRtl;
  return jsx("div", _extends({}, getStyleProps(props, 'container', {
    '--is-disabled': isDisabled,
    '--is-rtl': isRtl
  }), innerProps), children);
};

// ==============================
// Value Container
// ==============================

var valueContainerCSS = function valueContainerCSS(_ref2, unstyled) {
  var spacing = _ref2.theme.spacing,
    isMulti = _ref2.isMulti,
    hasValue = _ref2.hasValue,
    controlShouldRenderValue = _ref2.selectProps.controlShouldRenderValue;
  return _objectSpread2({
    alignItems: 'center',
    display: isMulti && hasValue && controlShouldRenderValue ? 'flex' : 'grid',
    flex: 1,
    flexWrap: 'wrap',
    WebkitOverflowScrolling: 'touch',
    position: 'relative',
    overflow: 'hidden'
  }, unstyled ? {} : {
    padding: "".concat(spacing.baseUnit / 2, "px ").concat(spacing.baseUnit * 2, "px")
  });
};
var ValueContainer = function ValueContainer(props) {
  var children = props.children,
    innerProps = props.innerProps,
    isMulti = props.isMulti,
    hasValue = props.hasValue;
  return jsx("div", _extends({}, getStyleProps(props, 'valueContainer', {
    'value-container': true,
    'value-container--is-multi': isMulti,
    'value-container--has-value': hasValue
  }), innerProps), children);
};

// ==============================
// Indicator Container
// ==============================

var indicatorsContainerCSS = function indicatorsContainerCSS() {
  return {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    flexShrink: 0
  };
};
var IndicatorsContainer = function IndicatorsContainer(props) {
  var children = props.children,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'indicatorsContainer', {
    indicators: true
  }), innerProps), children);
};

var _templateObject;
var _excluded$2 = ["size"],
  _excluded2 = ["innerProps", "isRtl", "size"];
function _EMOTION_STRINGIFIED_CSS_ERROR__$3() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }

// ==============================
// Dropdown & Clear Icons
// ==============================
var _ref2$2 = browser$1.env.NODE_ENV === "production" ? {
  name: "8mmkcg",
  styles: "display:inline-block;fill:currentColor;line-height:1;stroke:currentColor;stroke-width:0"
} : {
  name: "tj5bde-Svg",
  styles: "display:inline-block;fill:currentColor;line-height:1;stroke:currentColor;stroke-width:0;label:Svg;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$3
};
var Svg = function Svg(_ref) {
  var size = _ref.size,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return jsx("svg", _extends({
    height: size,
    width: size,
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    css: _ref2$2
  }, props));
};
var CrossIcon = function CrossIcon(props) {
  return jsx(Svg, _extends({
    size: 20
  }, props), jsx("path", {
    d: "M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
  }));
};
var DownChevron = function DownChevron(props) {
  return jsx(Svg, _extends({
    size: 20
  }, props), jsx("path", {
    d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
  }));
};

// ==============================
// Dropdown & Clear Buttons
// ==============================

var baseCSS = function baseCSS(_ref3, unstyled) {
  var isFocused = _ref3.isFocused,
    _ref3$theme = _ref3.theme,
    baseUnit = _ref3$theme.spacing.baseUnit,
    colors = _ref3$theme.colors;
  return _objectSpread2({
    label: 'indicatorContainer',
    display: 'flex',
    transition: 'color 150ms'
  }, unstyled ? {} : {
    color: isFocused ? colors.neutral60 : colors.neutral20,
    padding: baseUnit * 2,
    ':hover': {
      color: isFocused ? colors.neutral80 : colors.neutral40
    }
  });
};
var dropdownIndicatorCSS = baseCSS;
var DropdownIndicator = function DropdownIndicator(props) {
  var children = props.children,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'dropdownIndicator', {
    indicator: true,
    'dropdown-indicator': true
  }), innerProps), children || jsx(DownChevron, null));
};
var clearIndicatorCSS = baseCSS;
var ClearIndicator = function ClearIndicator(props) {
  var children = props.children,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'clearIndicator', {
    indicator: true,
    'clear-indicator': true
  }), innerProps), children || jsx(CrossIcon, null));
};

// ==============================
// Separator
// ==============================

var indicatorSeparatorCSS = function indicatorSeparatorCSS(_ref4, unstyled) {
  var isDisabled = _ref4.isDisabled,
    _ref4$theme = _ref4.theme,
    baseUnit = _ref4$theme.spacing.baseUnit,
    colors = _ref4$theme.colors;
  return _objectSpread2({
    label: 'indicatorSeparator',
    alignSelf: 'stretch',
    width: 1
  }, unstyled ? {} : {
    backgroundColor: isDisabled ? colors.neutral10 : colors.neutral20,
    marginBottom: baseUnit * 2,
    marginTop: baseUnit * 2
  });
};
var IndicatorSeparator = function IndicatorSeparator(props) {
  var innerProps = props.innerProps;
  return jsx("span", _extends({}, innerProps, getStyleProps(props, 'indicatorSeparator', {
    'indicator-separator': true
  })));
};

// ==============================
// Loading
// ==============================

var loadingDotAnimations = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  0%, 80%, 100% { opacity: 0; }\n  40% { opacity: 1; }\n"])));
var loadingIndicatorCSS = function loadingIndicatorCSS(_ref5, unstyled) {
  var isFocused = _ref5.isFocused,
    size = _ref5.size,
    _ref5$theme = _ref5.theme,
    colors = _ref5$theme.colors,
    baseUnit = _ref5$theme.spacing.baseUnit;
  return _objectSpread2({
    label: 'loadingIndicator',
    display: 'flex',
    transition: 'color 150ms',
    alignSelf: 'center',
    fontSize: size,
    lineHeight: 1,
    marginRight: size,
    textAlign: 'center',
    verticalAlign: 'middle'
  }, unstyled ? {} : {
    color: isFocused ? colors.neutral60 : colors.neutral20,
    padding: baseUnit * 2
  });
};
var LoadingDot = function LoadingDot(_ref6) {
  var delay = _ref6.delay,
    offset = _ref6.offset;
  return jsx("span", {
    css: /*#__PURE__*/css$2({
      animation: "".concat(loadingDotAnimations, " 1s ease-in-out ").concat(delay, "ms infinite;"),
      backgroundColor: 'currentColor',
      borderRadius: '1em',
      display: 'inline-block',
      marginLeft: offset ? '1em' : undefined,
      height: '1em',
      verticalAlign: 'top',
      width: '1em'
    }, browser$1.env.NODE_ENV === "production" ? "" : ";label:LoadingDot;", browser$1.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */")
  });
};
var LoadingIndicator = function LoadingIndicator(_ref7) {
  var innerProps = _ref7.innerProps,
    isRtl = _ref7.isRtl,
    _ref7$size = _ref7.size,
    size = _ref7$size === void 0 ? 4 : _ref7$size,
    restProps = _objectWithoutProperties(_ref7, _excluded2);
  return jsx("div", _extends({}, getStyleProps(_objectSpread2(_objectSpread2({}, restProps), {}, {
    innerProps: innerProps,
    isRtl: isRtl,
    size: size
  }), 'loadingIndicator', {
    indicator: true,
    'loading-indicator': true
  }), innerProps), jsx(LoadingDot, {
    delay: 0,
    offset: isRtl
  }), jsx(LoadingDot, {
    delay: 160,
    offset: true
  }), jsx(LoadingDot, {
    delay: 320,
    offset: !isRtl
  }));
};

var css$1 = function css(_ref, unstyled) {
  var isDisabled = _ref.isDisabled,
    isFocused = _ref.isFocused,
    _ref$theme = _ref.theme,
    colors = _ref$theme.colors,
    borderRadius = _ref$theme.borderRadius,
    spacing = _ref$theme.spacing;
  return _objectSpread2({
    label: 'control',
    alignItems: 'center',
    cursor: 'default',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    minHeight: spacing.controlHeight,
    outline: '0 !important',
    position: 'relative',
    transition: 'all 100ms'
  }, unstyled ? {} : {
    backgroundColor: isDisabled ? colors.neutral5 : colors.neutral0,
    borderColor: isDisabled ? colors.neutral10 : isFocused ? colors.primary : colors.neutral20,
    borderRadius: borderRadius,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: isFocused ? "0 0 0 1px ".concat(colors.primary) : undefined,
    '&:hover': {
      borderColor: isFocused ? colors.primary : colors.neutral30
    }
  });
};
var Control = function Control(props) {
  var children = props.children,
    isDisabled = props.isDisabled,
    isFocused = props.isFocused,
    innerRef = props.innerRef,
    innerProps = props.innerProps,
    menuIsOpen = props.menuIsOpen;
  return jsx("div", _extends({
    ref: innerRef
  }, getStyleProps(props, 'control', {
    control: true,
    'control--is-disabled': isDisabled,
    'control--is-focused': isFocused,
    'control--menu-is-open': menuIsOpen
  }), innerProps, {
    "aria-disabled": isDisabled || undefined
  }), children);
};
var Control$1 = Control;

var _excluded$1$1 = ["data"];
var groupCSS = function groupCSS(_ref, unstyled) {
  var spacing = _ref.theme.spacing;
  return unstyled ? {} : {
    paddingBottom: spacing.baseUnit * 2,
    paddingTop: spacing.baseUnit * 2
  };
};
var Group$1 = function Group(props) {
  var children = props.children,
    cx = props.cx,
    getStyles = props.getStyles,
    getClassNames = props.getClassNames,
    Heading = props.Heading,
    headingProps = props.headingProps,
    innerProps = props.innerProps,
    label = props.label,
    theme = props.theme,
    selectProps = props.selectProps;
  return jsx("div", _extends({}, getStyleProps(props, 'group', {
    group: true
  }), innerProps), jsx(Heading, _extends({}, headingProps, {
    selectProps: selectProps,
    theme: theme,
    getStyles: getStyles,
    getClassNames: getClassNames,
    cx: cx
  }), label), jsx("div", null, children));
};
var groupHeadingCSS = function groupHeadingCSS(_ref2, unstyled) {
  var _ref2$theme = _ref2.theme,
    colors = _ref2$theme.colors,
    spacing = _ref2$theme.spacing;
  return _objectSpread2({
    label: 'group',
    cursor: 'default',
    display: 'block'
  }, unstyled ? {} : {
    color: colors.neutral40,
    fontSize: '75%',
    fontWeight: 500,
    marginBottom: '0.25em',
    paddingLeft: spacing.baseUnit * 3,
    paddingRight: spacing.baseUnit * 3,
    textTransform: 'uppercase'
  });
};
var GroupHeading = function GroupHeading(props) {
  var _cleanCommonProps = cleanCommonProps(props);
    _cleanCommonProps.data;
    var innerProps = _objectWithoutProperties(_cleanCommonProps, _excluded$1$1);
  return jsx("div", _extends({}, getStyleProps(props, 'groupHeading', {
    'group-heading': true
  }), innerProps));
};
var Group$1$1 = Group$1;

var _excluded$5 = ["innerRef", "isDisabled", "isHidden", "inputClassName"];
var inputCSS = function inputCSS(_ref, unstyled) {
  var isDisabled = _ref.isDisabled,
    value = _ref.value,
    _ref$theme = _ref.theme,
    spacing = _ref$theme.spacing,
    colors = _ref$theme.colors;
  return _objectSpread2(_objectSpread2({
    visibility: isDisabled ? 'hidden' : 'visible',
    // force css to recompute when value change due to @emotion bug.
    // We can remove it whenever the bug is fixed.
    transform: value ? 'translateZ(0)' : ''
  }, containerStyle), unstyled ? {} : {
    margin: spacing.baseUnit / 2,
    paddingBottom: spacing.baseUnit / 2,
    paddingTop: spacing.baseUnit / 2,
    color: colors.neutral80
  });
};
var spacingStyle = {
  gridArea: '1 / 2',
  font: 'inherit',
  minWidth: '2px',
  border: 0,
  margin: 0,
  outline: 0,
  padding: 0
};
var containerStyle = {
  flex: '1 1 auto',
  display: 'inline-grid',
  gridArea: '1 / 1 / 2 / 3',
  gridTemplateColumns: '0 min-content',
  '&:after': _objectSpread2({
    content: 'attr(data-value) " "',
    visibility: 'hidden',
    whiteSpace: 'pre'
  }, spacingStyle)
};
var inputStyle = function inputStyle(isHidden) {
  return _objectSpread2({
    label: 'input',
    color: 'inherit',
    background: 0,
    opacity: isHidden ? 0 : 1,
    width: '100%'
  }, spacingStyle);
};
var Input$2 = function Input(props) {
  var cx = props.cx,
    value = props.value;
  var _cleanCommonProps = cleanCommonProps(props),
    innerRef = _cleanCommonProps.innerRef,
    isDisabled = _cleanCommonProps.isDisabled,
    isHidden = _cleanCommonProps.isHidden,
    inputClassName = _cleanCommonProps.inputClassName,
    innerProps = _objectWithoutProperties(_cleanCommonProps, _excluded$5);
  return jsx("div", _extends({}, getStyleProps(props, 'input', {
    'input-container': true
  }), {
    "data-value": value || ''
  }), jsx("input", _extends({
    className: cx({
      input: true
    }, inputClassName),
    ref: innerRef,
    style: inputStyle(isHidden),
    disabled: isDisabled
  }, innerProps)));
};
var Input$1$1 = Input$2;

var multiValueCSS = function multiValueCSS(_ref, unstyled) {
  var _ref$theme = _ref.theme,
    spacing = _ref$theme.spacing,
    borderRadius = _ref$theme.borderRadius,
    colors = _ref$theme.colors;
  return _objectSpread2({
    label: 'multiValue',
    display: 'flex',
    minWidth: 0
  }, unstyled ? {} : {
    backgroundColor: colors.neutral10,
    borderRadius: borderRadius / 2,
    margin: spacing.baseUnit / 2
  });
};
var multiValueLabelCSS = function multiValueLabelCSS(_ref2, unstyled) {
  var _ref2$theme = _ref2.theme,
    borderRadius = _ref2$theme.borderRadius,
    colors = _ref2$theme.colors,
    cropWithEllipsis = _ref2.cropWithEllipsis;
  return _objectSpread2({
    overflow: 'hidden',
    textOverflow: cropWithEllipsis || cropWithEllipsis === undefined ? 'ellipsis' : undefined,
    whiteSpace: 'nowrap'
  }, unstyled ? {} : {
    borderRadius: borderRadius / 2,
    color: colors.neutral80,
    fontSize: '85%',
    padding: 3,
    paddingLeft: 6
  });
};
var multiValueRemoveCSS = function multiValueRemoveCSS(_ref3, unstyled) {
  var _ref3$theme = _ref3.theme,
    spacing = _ref3$theme.spacing,
    borderRadius = _ref3$theme.borderRadius,
    colors = _ref3$theme.colors,
    isFocused = _ref3.isFocused;
  return _objectSpread2({
    alignItems: 'center',
    display: 'flex'
  }, unstyled ? {} : {
    borderRadius: borderRadius / 2,
    backgroundColor: isFocused ? colors.dangerLight : undefined,
    paddingLeft: spacing.baseUnit,
    paddingRight: spacing.baseUnit,
    ':hover': {
      backgroundColor: colors.dangerLight,
      color: colors.danger
    }
  });
};
var MultiValueGeneric = function MultiValueGeneric(_ref4) {
  var children = _ref4.children,
    innerProps = _ref4.innerProps;
  return jsx("div", innerProps, children);
};
var MultiValueContainer = MultiValueGeneric;
var MultiValueLabel = MultiValueGeneric;
function MultiValueRemove(_ref5) {
  var children = _ref5.children,
    innerProps = _ref5.innerProps;
  return jsx("div", _extends({
    role: "button"
  }, innerProps), children || jsx(CrossIcon, {
    size: 14
  }));
}
var MultiValue = function MultiValue(props) {
  var children = props.children,
    components = props.components,
    data = props.data,
    innerProps = props.innerProps,
    isDisabled = props.isDisabled,
    removeProps = props.removeProps,
    selectProps = props.selectProps;
  var Container = components.Container,
    Label = components.Label,
    Remove = components.Remove;
  return jsx(Container, {
    data: data,
    innerProps: _objectSpread2(_objectSpread2({}, getStyleProps(props, 'multiValue', {
      'multi-value': true,
      'multi-value--is-disabled': isDisabled
    })), innerProps),
    selectProps: selectProps
  }, jsx(Label, {
    data: data,
    innerProps: _objectSpread2({}, getStyleProps(props, 'multiValueLabel', {
      'multi-value__label': true
    })),
    selectProps: selectProps
  }, children), jsx(Remove, {
    data: data,
    innerProps: _objectSpread2(_objectSpread2({}, getStyleProps(props, 'multiValueRemove', {
      'multi-value__remove': true
    })), {}, {
      'aria-label': "Remove ".concat(children || 'option')
    }, removeProps),
    selectProps: selectProps
  }));
};
var MultiValue$1 = MultiValue;

var optionCSS = function optionCSS(_ref, unstyled) {
  var isDisabled = _ref.isDisabled,
    isFocused = _ref.isFocused,
    isSelected = _ref.isSelected,
    _ref$theme = _ref.theme,
    spacing = _ref$theme.spacing,
    colors = _ref$theme.colors;
  return _objectSpread2({
    label: 'option',
    cursor: 'default',
    display: 'block',
    fontSize: 'inherit',
    width: '100%',
    userSelect: 'none',
    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)'
  }, unstyled ? {} : {
    backgroundColor: isSelected ? colors.primary : isFocused ? colors.primary25 : 'transparent',
    color: isDisabled ? colors.neutral20 : isSelected ? colors.neutral0 : 'inherit',
    padding: "".concat(spacing.baseUnit * 2, "px ").concat(spacing.baseUnit * 3, "px"),
    // provide some affordance on touch devices
    ':active': {
      backgroundColor: !isDisabled ? isSelected ? colors.primary : colors.primary50 : undefined
    }
  });
};
var Option = function Option(props) {
  var children = props.children,
    isDisabled = props.isDisabled,
    isFocused = props.isFocused,
    isSelected = props.isSelected,
    innerRef = props.innerRef,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'option', {
    option: true,
    'option--is-disabled': isDisabled,
    'option--is-focused': isFocused,
    'option--is-selected': isSelected
  }), {
    ref: innerRef,
    "aria-disabled": isDisabled
  }, innerProps), children);
};
var Option$1 = Option;

var placeholderCSS = function placeholderCSS(_ref, unstyled) {
  var _ref$theme = _ref.theme,
    spacing = _ref$theme.spacing,
    colors = _ref$theme.colors;
  return _objectSpread2({
    label: 'placeholder',
    gridArea: '1 / 1 / 2 / 3'
  }, unstyled ? {} : {
    color: colors.neutral50,
    marginLeft: spacing.baseUnit / 2,
    marginRight: spacing.baseUnit / 2
  });
};
var Placeholder = function Placeholder(props) {
  var children = props.children,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'placeholder', {
    placeholder: true
  }), innerProps), children);
};
var Placeholder$1 = Placeholder;

var css = function css(_ref, unstyled) {
  var isDisabled = _ref.isDisabled,
    _ref$theme = _ref.theme,
    spacing = _ref$theme.spacing,
    colors = _ref$theme.colors;
  return _objectSpread2({
    label: 'singleValue',
    gridArea: '1 / 1 / 2 / 3',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }, unstyled ? {} : {
    color: isDisabled ? colors.neutral40 : colors.neutral80,
    marginLeft: spacing.baseUnit / 2,
    marginRight: spacing.baseUnit / 2
  });
};
var SingleValue = function SingleValue(props) {
  var children = props.children,
    isDisabled = props.isDisabled,
    innerProps = props.innerProps;
  return jsx("div", _extends({}, getStyleProps(props, 'singleValue', {
    'single-value': true,
    'single-value--is-disabled': isDisabled
  }), innerProps), children);
};
var SingleValue$1 = SingleValue;

var components = {
  ClearIndicator: ClearIndicator,
  Control: Control$1,
  DropdownIndicator: DropdownIndicator,
  DownChevron: DownChevron,
  CrossIcon: CrossIcon,
  Group: Group$1$1,
  GroupHeading: GroupHeading,
  IndicatorsContainer: IndicatorsContainer,
  IndicatorSeparator: IndicatorSeparator,
  Input: Input$1$1,
  LoadingIndicator: LoadingIndicator,
  Menu: Menu$1$1,
  MenuList: MenuList,
  MenuPortal: MenuPortal$1,
  LoadingMessage: LoadingMessage,
  NoOptionsMessage: NoOptionsMessage,
  MultiValue: MultiValue$1,
  MultiValueContainer: MultiValueContainer,
  MultiValueLabel: MultiValueLabel,
  MultiValueRemove: MultiValueRemove,
  Option: Option$1,
  Placeholder: Placeholder$1,
  SelectContainer: SelectContainer,
  SingleValue: SingleValue$1,
  ValueContainer: ValueContainer
};
var defaultComponents = function defaultComponents(props) {
  return _objectSpread2(_objectSpread2({}, components), props.components);
};

var safeIsNaN = Number.isNaN ||
    function ponyfill(value) {
        return typeof value === 'number' && value !== value;
    };
function isEqual$2(first, second) {
    if (first === second) {
        return true;
    }
    if (safeIsNaN(first) && safeIsNaN(second)) {
        return true;
    }
    return false;
}
function areInputsEqual(newInputs, lastInputs) {
    if (newInputs.length !== lastInputs.length) {
        return false;
    }
    for (var i = 0; i < newInputs.length; i++) {
        if (!isEqual$2(newInputs[i], lastInputs[i])) {
            return false;
        }
    }
    return true;
}

function memoizeOne(resultFn, isEqual) {
    if (isEqual === void 0) { isEqual = areInputsEqual; }
    var cache = null;
    function memoized() {
        var newArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            newArgs[_i] = arguments[_i];
        }
        if (cache && cache.lastThis === this && isEqual(newArgs, cache.lastArgs)) {
            return cache.lastResult;
        }
        var lastResult = resultFn.apply(this, newArgs);
        cache = {
            lastResult: lastResult,
            lastArgs: newArgs,
            lastThis: this,
        };
        return lastResult;
    }
    memoized.clear = function clear() {
        cache = null;
    };
    return memoized;
}

function _EMOTION_STRINGIFIED_CSS_ERROR__$2() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }

// Assistive text to describe visual elements. Hidden for sighted users.
var _ref = browser$1.env.NODE_ENV === "production" ? {
  name: "7pg0cj-a11yText",
  styles: "label:a11yText;z-index:9999;border:0;clip:rect(1px, 1px, 1px, 1px);height:1px;width:1px;position:absolute;overflow:hidden;padding:0;white-space:nowrap"
} : {
  name: "1f43avz-a11yText-A11yText",
  styles: "label:a11yText;z-index:9999;border:0;clip:rect(1px, 1px, 1px, 1px);height:1px;width:1px;position:absolute;overflow:hidden;padding:0;white-space:nowrap;label:A11yText;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIkExMXlUZXh0LnRzeCJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFPSSIsImZpbGUiOiJBMTF5VGV4dC50c3giLCJzb3VyY2VzQ29udGVudCI6WyIvKiogQGpzeCBqc3ggKi9cbmltcG9ydCB7IEpTWCB9IGZyb20gJ3JlYWN0JztcbmltcG9ydCB7IGpzeCB9IGZyb20gJ0BlbW90aW9uL3JlYWN0JztcblxuLy8gQXNzaXN0aXZlIHRleHQgdG8gZGVzY3JpYmUgdmlzdWFsIGVsZW1lbnRzLiBIaWRkZW4gZm9yIHNpZ2h0ZWQgdXNlcnMuXG5jb25zdCBBMTF5VGV4dCA9IChwcm9wczogSlNYLkludHJpbnNpY0VsZW1lbnRzWydzcGFuJ10pID0+IChcbiAgPHNwYW5cbiAgICBjc3M9e3tcbiAgICAgIGxhYmVsOiAnYTExeVRleHQnLFxuICAgICAgekluZGV4OiA5OTk5LFxuICAgICAgYm9yZGVyOiAwLFxuICAgICAgY2xpcDogJ3JlY3QoMXB4LCAxcHgsIDFweCwgMXB4KScsXG4gICAgICBoZWlnaHQ6IDEsXG4gICAgICB3aWR0aDogMSxcbiAgICAgIHBvc2l0aW9uOiAnYWJzb2x1dGUnLFxuICAgICAgb3ZlcmZsb3c6ICdoaWRkZW4nLFxuICAgICAgcGFkZGluZzogMCxcbiAgICAgIHdoaXRlU3BhY2U6ICdub3dyYXAnLFxuICAgIH19XG4gICAgey4uLnByb3BzfVxuICAvPlxuKTtcblxuZXhwb3J0IGRlZmF1bHQgQTExeVRleHQ7XG4iXX0= */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$2
};
var A11yText = function A11yText(props) {
  return jsx("span", _extends({
    css: _ref
  }, props));
};
var A11yText$1 = A11yText;

var defaultAriaLiveMessages = {
  guidance: function guidance(props) {
    var isSearchable = props.isSearchable,
      isMulti = props.isMulti,
      tabSelectsValue = props.tabSelectsValue,
      context = props.context,
      isInitialFocus = props.isInitialFocus;
    switch (context) {
      case 'menu':
        return "Use Up and Down to choose options, press Enter to select the currently focused option, press Escape to exit the menu".concat(tabSelectsValue ? ', press Tab to select the option and exit the menu' : '', ".");
      case 'input':
        return isInitialFocus ? "".concat(props['aria-label'] || 'Select', " is focused ").concat(isSearchable ? ',type to refine list' : '', ", press Down to open the menu, ").concat(isMulti ? ' press left to focus selected values' : '') : '';
      case 'value':
        return 'Use left and right to toggle between focused values, press Backspace to remove the currently focused value';
      default:
        return '';
    }
  },
  onChange: function onChange(props) {
    var action = props.action,
      _props$label = props.label,
      label = _props$label === void 0 ? '' : _props$label,
      labels = props.labels,
      isDisabled = props.isDisabled;
    switch (action) {
      case 'deselect-option':
      case 'pop-value':
      case 'remove-value':
        return "option ".concat(label, ", deselected.");
      case 'clear':
        return 'All selected options have been cleared.';
      case 'initial-input-focus':
        return "option".concat(labels.length > 1 ? 's' : '', " ").concat(labels.join(','), ", selected.");
      case 'select-option':
        return isDisabled ? "option ".concat(label, " is disabled. Select another option.") : "option ".concat(label, ", selected.");
      default:
        return '';
    }
  },
  onFocus: function onFocus(props) {
    var context = props.context,
      focused = props.focused,
      options = props.options,
      _props$label2 = props.label,
      label = _props$label2 === void 0 ? '' : _props$label2,
      selectValue = props.selectValue,
      isDisabled = props.isDisabled,
      isSelected = props.isSelected,
      isAppleDevice = props.isAppleDevice;
    var getArrayIndex = function getArrayIndex(arr, item) {
      return arr && arr.length ? "".concat(arr.indexOf(item) + 1, " of ").concat(arr.length) : '';
    };
    if (context === 'value' && selectValue) {
      return "value ".concat(label, " focused, ").concat(getArrayIndex(selectValue, focused), ".");
    }
    if (context === 'menu' && isAppleDevice) {
      var disabled = isDisabled ? ' disabled' : '';
      var status = "".concat(isSelected ? ' selected' : '').concat(disabled);
      return "".concat(label).concat(status, ", ").concat(getArrayIndex(options, focused), ".");
    }
    return '';
  },
  onFilter: function onFilter(props) {
    var inputValue = props.inputValue,
      resultsMessage = props.resultsMessage;
    return "".concat(resultsMessage).concat(inputValue ? ' for search term ' + inputValue : '', ".");
  }
};

var LiveRegion = function LiveRegion(props) {
  var ariaSelection = props.ariaSelection,
    focusedOption = props.focusedOption,
    focusedValue = props.focusedValue,
    focusableOptions = props.focusableOptions,
    isFocused = props.isFocused,
    selectValue = props.selectValue,
    selectProps = props.selectProps,
    id = props.id,
    isAppleDevice = props.isAppleDevice;
  var ariaLiveMessages = selectProps.ariaLiveMessages,
    getOptionLabel = selectProps.getOptionLabel,
    inputValue = selectProps.inputValue,
    isMulti = selectProps.isMulti,
    isOptionDisabled = selectProps.isOptionDisabled,
    isSearchable = selectProps.isSearchable,
    menuIsOpen = selectProps.menuIsOpen,
    options = selectProps.options,
    screenReaderStatus = selectProps.screenReaderStatus,
    tabSelectsValue = selectProps.tabSelectsValue,
    isLoading = selectProps.isLoading;
  var ariaLabel = selectProps['aria-label'];
  var ariaLive = selectProps['aria-live'];

  // Update aria live message configuration when prop changes
  var messages = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, defaultAriaLiveMessages), ariaLiveMessages || {});
  }, [ariaLiveMessages]);

  // Update aria live selected option when prop changes
  var ariaSelected = useMemo(function () {
    var message = '';
    if (ariaSelection && messages.onChange) {
      var option = ariaSelection.option,
        selectedOptions = ariaSelection.options,
        removedValue = ariaSelection.removedValue,
        removedValues = ariaSelection.removedValues,
        value = ariaSelection.value;
      // select-option when !isMulti does not return option so we assume selected option is value
      var asOption = function asOption(val) {
        return !Array.isArray(val) ? val : null;
      };

      // If there is just one item from the action then get its label
      var selected = removedValue || option || asOption(value);
      var label = selected ? getOptionLabel(selected) : '';

      // If there are multiple items from the action then return an array of labels
      var multiSelected = selectedOptions || removedValues || undefined;
      var labels = multiSelected ? multiSelected.map(getOptionLabel) : [];
      var onChangeProps = _objectSpread2({
        // multiSelected items are usually items that have already been selected
        // or set by the user as a default value so we assume they are not disabled
        isDisabled: selected && isOptionDisabled(selected, selectValue),
        label: label,
        labels: labels
      }, ariaSelection);
      message = messages.onChange(onChangeProps);
    }
    return message;
  }, [ariaSelection, messages, isOptionDisabled, selectValue, getOptionLabel]);
  var ariaFocused = useMemo(function () {
    var focusMsg = '';
    var focused = focusedOption || focusedValue;
    var isSelected = !!(focusedOption && selectValue && selectValue.includes(focusedOption));
    if (focused && messages.onFocus) {
      var onFocusProps = {
        focused: focused,
        label: getOptionLabel(focused),
        isDisabled: isOptionDisabled(focused, selectValue),
        isSelected: isSelected,
        options: focusableOptions,
        context: focused === focusedOption ? 'menu' : 'value',
        selectValue: selectValue,
        isAppleDevice: isAppleDevice
      };
      focusMsg = messages.onFocus(onFocusProps);
    }
    return focusMsg;
  }, [focusedOption, focusedValue, getOptionLabel, isOptionDisabled, messages, focusableOptions, selectValue, isAppleDevice]);
  var ariaResults = useMemo(function () {
    var resultsMsg = '';
    if (menuIsOpen && options.length && !isLoading && messages.onFilter) {
      var resultsMessage = screenReaderStatus({
        count: focusableOptions.length
      });
      resultsMsg = messages.onFilter({
        inputValue: inputValue,
        resultsMessage: resultsMessage
      });
    }
    return resultsMsg;
  }, [focusableOptions, inputValue, menuIsOpen, messages, options, screenReaderStatus, isLoading]);
  var isInitialFocus = (ariaSelection === null || ariaSelection === void 0 ? void 0 : ariaSelection.action) === 'initial-input-focus';
  var ariaGuidance = useMemo(function () {
    var guidanceMsg = '';
    if (messages.guidance) {
      var context = focusedValue ? 'value' : menuIsOpen ? 'menu' : 'input';
      guidanceMsg = messages.guidance({
        'aria-label': ariaLabel,
        context: context,
        isDisabled: focusedOption && isOptionDisabled(focusedOption, selectValue),
        isMulti: isMulti,
        isSearchable: isSearchable,
        tabSelectsValue: tabSelectsValue,
        isInitialFocus: isInitialFocus
      });
    }
    return guidanceMsg;
  }, [ariaLabel, focusedOption, focusedValue, isMulti, isOptionDisabled, isSearchable, menuIsOpen, messages, selectValue, tabSelectsValue, isInitialFocus]);
  var ScreenReaderText = jsx(Fragment$1, null, jsx("span", {
    id: "aria-selection"
  }, ariaSelected), jsx("span", {
    id: "aria-focused"
  }, ariaFocused), jsx("span", {
    id: "aria-results"
  }, ariaResults), jsx("span", {
    id: "aria-guidance"
  }, ariaGuidance));
  return jsx(Fragment$1, null, jsx(A11yText$1, {
    id: id
  }, isInitialFocus && ScreenReaderText), jsx(A11yText$1, {
    "aria-live": ariaLive,
    "aria-atomic": "false",
    "aria-relevant": "additions text",
    role: "log"
  }, isFocused && !isInitialFocus && ScreenReaderText));
};
var LiveRegion$1 = LiveRegion;

var diacritics = [{
  base: 'A',
  letters: "A\u24B6\uFF21\xC0\xC1\xC2\u1EA6\u1EA4\u1EAA\u1EA8\xC3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\xC4\u01DE\u1EA2\xC5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F"
}, {
  base: 'AA',
  letters: "\uA732"
}, {
  base: 'AE',
  letters: "\xC6\u01FC\u01E2"
}, {
  base: 'AO',
  letters: "\uA734"
}, {
  base: 'AU',
  letters: "\uA736"
}, {
  base: 'AV',
  letters: "\uA738\uA73A"
}, {
  base: 'AY',
  letters: "\uA73C"
}, {
  base: 'B',
  letters: "B\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181"
}, {
  base: 'C',
  letters: "C\u24B8\uFF23\u0106\u0108\u010A\u010C\xC7\u1E08\u0187\u023B\uA73E"
}, {
  base: 'D',
  letters: "D\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779"
}, {
  base: 'DZ',
  letters: "\u01F1\u01C4"
}, {
  base: 'Dz',
  letters: "\u01F2\u01C5"
}, {
  base: 'E',
  letters: "E\u24BA\uFF25\xC8\xC9\xCA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\xCB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E"
}, {
  base: 'F',
  letters: "F\u24BB\uFF26\u1E1E\u0191\uA77B"
}, {
  base: 'G',
  letters: "G\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E"
}, {
  base: 'H',
  letters: "H\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D"
}, {
  base: 'I',
  letters: "I\u24BE\uFF29\xCC\xCD\xCE\u0128\u012A\u012C\u0130\xCF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197"
}, {
  base: 'J',
  letters: "J\u24BF\uFF2A\u0134\u0248"
}, {
  base: 'K',
  letters: "K\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2"
}, {
  base: 'L',
  letters: "L\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780"
}, {
  base: 'LJ',
  letters: "\u01C7"
}, {
  base: 'Lj',
  letters: "\u01C8"
}, {
  base: 'M',
  letters: "M\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C"
}, {
  base: 'N',
  letters: "N\u24C3\uFF2E\u01F8\u0143\xD1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4"
}, {
  base: 'NJ',
  letters: "\u01CA"
}, {
  base: 'Nj',
  letters: "\u01CB"
}, {
  base: 'O',
  letters: "O\u24C4\uFF2F\xD2\xD3\xD4\u1ED2\u1ED0\u1ED6\u1ED4\xD5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\xD6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\xD8\u01FE\u0186\u019F\uA74A\uA74C"
}, {
  base: 'OI',
  letters: "\u01A2"
}, {
  base: 'OO',
  letters: "\uA74E"
}, {
  base: 'OU',
  letters: "\u0222"
}, {
  base: 'P',
  letters: "P\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754"
}, {
  base: 'Q',
  letters: "Q\u24C6\uFF31\uA756\uA758\u024A"
}, {
  base: 'R',
  letters: "R\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782"
}, {
  base: 'S',
  letters: "S\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784"
}, {
  base: 'T',
  letters: "T\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786"
}, {
  base: 'TZ',
  letters: "\uA728"
}, {
  base: 'U',
  letters: "U\u24CA\uFF35\xD9\xDA\xDB\u0168\u1E78\u016A\u1E7A\u016C\xDC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244"
}, {
  base: 'V',
  letters: "V\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245"
}, {
  base: 'VY',
  letters: "\uA760"
}, {
  base: 'W',
  letters: "W\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72"
}, {
  base: 'X',
  letters: "X\u24CD\uFF38\u1E8A\u1E8C"
}, {
  base: 'Y',
  letters: "Y\u24CE\uFF39\u1EF2\xDD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE"
}, {
  base: 'Z',
  letters: "Z\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762"
}, {
  base: 'a',
  letters: "a\u24D0\uFF41\u1E9A\xE0\xE1\xE2\u1EA7\u1EA5\u1EAB\u1EA9\xE3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\xE4\u01DF\u1EA3\xE5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250"
}, {
  base: 'aa',
  letters: "\uA733"
}, {
  base: 'ae',
  letters: "\xE6\u01FD\u01E3"
}, {
  base: 'ao',
  letters: "\uA735"
}, {
  base: 'au',
  letters: "\uA737"
}, {
  base: 'av',
  letters: "\uA739\uA73B"
}, {
  base: 'ay',
  letters: "\uA73D"
}, {
  base: 'b',
  letters: "b\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253"
}, {
  base: 'c',
  letters: "c\u24D2\uFF43\u0107\u0109\u010B\u010D\xE7\u1E09\u0188\u023C\uA73F\u2184"
}, {
  base: 'd',
  letters: "d\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A"
}, {
  base: 'dz',
  letters: "\u01F3\u01C6"
}, {
  base: 'e',
  letters: "e\u24D4\uFF45\xE8\xE9\xEA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\xEB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD"
}, {
  base: 'f',
  letters: "f\u24D5\uFF46\u1E1F\u0192\uA77C"
}, {
  base: 'g',
  letters: "g\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F"
}, {
  base: 'h',
  letters: "h\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265"
}, {
  base: 'hv',
  letters: "\u0195"
}, {
  base: 'i',
  letters: "i\u24D8\uFF49\xEC\xED\xEE\u0129\u012B\u012D\xEF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131"
}, {
  base: 'j',
  letters: "j\u24D9\uFF4A\u0135\u01F0\u0249"
}, {
  base: 'k',
  letters: "k\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3"
}, {
  base: 'l',
  letters: "l\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747"
}, {
  base: 'lj',
  letters: "\u01C9"
}, {
  base: 'm',
  letters: "m\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F"
}, {
  base: 'n',
  letters: "n\u24DD\uFF4E\u01F9\u0144\xF1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5"
}, {
  base: 'nj',
  letters: "\u01CC"
}, {
  base: 'o',
  letters: "o\u24DE\uFF4F\xF2\xF3\xF4\u1ED3\u1ED1\u1ED7\u1ED5\xF5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\xF6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\xF8\u01FF\u0254\uA74B\uA74D\u0275"
}, {
  base: 'oi',
  letters: "\u01A3"
}, {
  base: 'ou',
  letters: "\u0223"
}, {
  base: 'oo',
  letters: "\uA74F"
}, {
  base: 'p',
  letters: "p\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755"
}, {
  base: 'q',
  letters: "q\u24E0\uFF51\u024B\uA757\uA759"
}, {
  base: 'r',
  letters: "r\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783"
}, {
  base: 's',
  letters: "s\u24E2\uFF53\xDF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B"
}, {
  base: 't',
  letters: "t\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787"
}, {
  base: 'tz',
  letters: "\uA729"
}, {
  base: 'u',
  letters: "u\u24E4\uFF55\xF9\xFA\xFB\u0169\u1E79\u016B\u1E7B\u016D\xFC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289"
}, {
  base: 'v',
  letters: "v\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C"
}, {
  base: 'vy',
  letters: "\uA761"
}, {
  base: 'w',
  letters: "w\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73"
}, {
  base: 'x',
  letters: "x\u24E7\uFF58\u1E8B\u1E8D"
}, {
  base: 'y',
  letters: "y\u24E8\uFF59\u1EF3\xFD\u0177\u1EF9\u0233\u1E8F\xFF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF"
}, {
  base: 'z',
  letters: "z\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763"
}];
var anyDiacritic = new RegExp('[' + diacritics.map(function (d) {
  return d.letters;
}).join('') + ']', 'g');
var diacriticToBase = {};
for (var i = 0; i < diacritics.length; i++) {
  var diacritic = diacritics[i];
  for (var j = 0; j < diacritic.letters.length; j++) {
    diacriticToBase[diacritic.letters[j]] = diacritic.base;
  }
}
var stripDiacritics = function stripDiacritics(str) {
  return str.replace(anyDiacritic, function (match) {
    return diacriticToBase[match];
  });
};

var memoizedStripDiacriticsForInput = memoizeOne(stripDiacritics);
var trimString = function trimString(str) {
  return str.replace(/^\s+|\s+$/g, '');
};
var defaultStringify = function defaultStringify(option) {
  return "".concat(option.label, " ").concat(option.value);
};
var createFilter = function createFilter(config) {
  return function (option, rawInput) {
    // eslint-disable-next-line no-underscore-dangle
    if (option.data.__isNew__) return true;
    var _ignoreCase$ignoreAcc = _objectSpread2({
        ignoreCase: true,
        ignoreAccents: true,
        stringify: defaultStringify,
        trim: true,
        matchFrom: 'any'
      }, config),
      ignoreCase = _ignoreCase$ignoreAcc.ignoreCase,
      ignoreAccents = _ignoreCase$ignoreAcc.ignoreAccents,
      stringify = _ignoreCase$ignoreAcc.stringify,
      trim = _ignoreCase$ignoreAcc.trim,
      matchFrom = _ignoreCase$ignoreAcc.matchFrom;
    var input = trim ? trimString(rawInput) : rawInput;
    var candidate = trim ? trimString(stringify(option)) : stringify(option);
    if (ignoreCase) {
      input = input.toLowerCase();
      candidate = candidate.toLowerCase();
    }
    if (ignoreAccents) {
      input = memoizedStripDiacriticsForInput(input);
      candidate = stripDiacritics(candidate);
    }
    return matchFrom === 'start' ? candidate.substr(0, input.length) === input : candidate.indexOf(input) > -1;
  };
};

var _excluded$1 = ["innerRef"];
function DummyInput(_ref) {
  var innerRef = _ref.innerRef,
    props = _objectWithoutProperties(_ref, _excluded$1);
  // Remove animation props not meant for HTML elements
  var filteredProps = removeProps(props, 'onExited', 'in', 'enter', 'exit', 'appear');
  return jsx("input", _extends({
    ref: innerRef
  }, filteredProps, {
    css: /*#__PURE__*/css$2({
      label: 'dummyInput',
      // get rid of any default styles
      background: 0,
      border: 0,
      // important! this hides the flashing cursor
      caretColor: 'transparent',
      fontSize: 'inherit',
      gridArea: '1 / 1 / 2 / 3',
      outline: 0,
      padding: 0,
      // important! without `width` browsers won't allow focus
      width: 1,
      // remove cursor on desktop
      color: 'transparent',
      // remove cursor on mobile whilst maintaining "scroll into view" behaviour
      left: -100,
      opacity: 0,
      position: 'relative',
      transform: 'scale(.01)'
    }, browser$1.env.NODE_ENV === "production" ? "" : ";label:DummyInput;", browser$1.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIkR1bW15SW5wdXQudHN4Il0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQXlCTSIsImZpbGUiOiJEdW1teUlucHV0LnRzeCIsInNvdXJjZXNDb250ZW50IjpbIi8qKiBAanN4IGpzeCAqL1xuaW1wb3J0IHsgSlNYLCBSZWYgfSBmcm9tICdyZWFjdCc7XG5pbXBvcnQgeyBqc3ggfSBmcm9tICdAZW1vdGlvbi9yZWFjdCc7XG5pbXBvcnQgeyByZW1vdmVQcm9wcyB9IGZyb20gJy4uL3V0aWxzJztcblxuZXhwb3J0IGRlZmF1bHQgZnVuY3Rpb24gRHVtbXlJbnB1dCh7XG4gIGlubmVyUmVmLFxuICAuLi5wcm9wc1xufTogSlNYLkludHJpbnNpY0VsZW1lbnRzWydpbnB1dCddICYge1xuICByZWFkb25seSBpbm5lclJlZjogUmVmPEhUTUxJbnB1dEVsZW1lbnQ+O1xufSkge1xuICAvLyBSZW1vdmUgYW5pbWF0aW9uIHByb3BzIG5vdCBtZWFudCBmb3IgSFRNTCBlbGVtZW50c1xuICBjb25zdCBmaWx0ZXJlZFByb3BzID0gcmVtb3ZlUHJvcHMoXG4gICAgcHJvcHMsXG4gICAgJ29uRXhpdGVkJyxcbiAgICAnaW4nLFxuICAgICdlbnRlcicsXG4gICAgJ2V4aXQnLFxuICAgICdhcHBlYXInXG4gICk7XG5cbiAgcmV0dXJuIChcbiAgICA8aW5wdXRcbiAgICAgIHJlZj17aW5uZXJSZWZ9XG4gICAgICB7Li4uZmlsdGVyZWRQcm9wc31cbiAgICAgIGNzcz17e1xuICAgICAgICBsYWJlbDogJ2R1bW15SW5wdXQnLFxuICAgICAgICAvLyBnZXQgcmlkIG9mIGFueSBkZWZhdWx0IHN0eWxlc1xuICAgICAgICBiYWNrZ3JvdW5kOiAwLFxuICAgICAgICBib3JkZXI6IDAsXG4gICAgICAgIC8vIGltcG9ydGFudCEgdGhpcyBoaWRlcyB0aGUgZmxhc2hpbmcgY3Vyc29yXG4gICAgICAgIGNhcmV0Q29sb3I6ICd0cmFuc3BhcmVudCcsXG4gICAgICAgIGZvbnRTaXplOiAnaW5oZXJpdCcsXG4gICAgICAgIGdyaWRBcmVhOiAnMSAvIDEgLyAyIC8gMycsXG4gICAgICAgIG91dGxpbmU6IDAsXG4gICAgICAgIHBhZGRpbmc6IDAsXG4gICAgICAgIC8vIGltcG9ydGFudCEgd2l0aG91dCBgd2lkdGhgIGJyb3dzZXJzIHdvbid0IGFsbG93IGZvY3VzXG4gICAgICAgIHdpZHRoOiAxLFxuXG4gICAgICAgIC8vIHJlbW92ZSBjdXJzb3Igb24gZGVza3RvcFxuICAgICAgICBjb2xvcjogJ3RyYW5zcGFyZW50JyxcblxuICAgICAgICAvLyByZW1vdmUgY3Vyc29yIG9uIG1vYmlsZSB3aGlsc3QgbWFpbnRhaW5pbmcgXCJzY3JvbGwgaW50byB2aWV3XCIgYmVoYXZpb3VyXG4gICAgICAgIGxlZnQ6IC0xMDAsXG4gICAgICAgIG9wYWNpdHk6IDAsXG4gICAgICAgIHBvc2l0aW9uOiAncmVsYXRpdmUnLFxuICAgICAgICB0cmFuc2Zvcm06ICdzY2FsZSguMDEpJyxcbiAgICAgIH19XG4gICAgLz5cbiAgKTtcbn1cbiJdfQ== */")
  }));
}

var cancelScroll = function cancelScroll(event) {
  if (event.cancelable) event.preventDefault();
  event.stopPropagation();
};
function useScrollCapture(_ref) {
  var isEnabled = _ref.isEnabled,
    onBottomArrive = _ref.onBottomArrive,
    onBottomLeave = _ref.onBottomLeave,
    onTopArrive = _ref.onTopArrive,
    onTopLeave = _ref.onTopLeave;
  var isBottom = useRef(false);
  var isTop = useRef(false);
  var touchStart = useRef(0);
  var scrollTarget = useRef(null);
  var handleEventDelta = useCallback(function (event, delta) {
    if (scrollTarget.current === null) return;
    var _scrollTarget$current = scrollTarget.current,
      scrollTop = _scrollTarget$current.scrollTop,
      scrollHeight = _scrollTarget$current.scrollHeight,
      clientHeight = _scrollTarget$current.clientHeight;
    var target = scrollTarget.current;
    var isDeltaPositive = delta > 0;
    var availableScroll = scrollHeight - clientHeight - scrollTop;
    var shouldCancelScroll = false;

    // reset bottom/top flags
    if (availableScroll > delta && isBottom.current) {
      if (onBottomLeave) onBottomLeave(event);
      isBottom.current = false;
    }
    if (isDeltaPositive && isTop.current) {
      if (onTopLeave) onTopLeave(event);
      isTop.current = false;
    }

    // bottom limit
    if (isDeltaPositive && delta > availableScroll) {
      if (onBottomArrive && !isBottom.current) {
        onBottomArrive(event);
      }
      target.scrollTop = scrollHeight;
      shouldCancelScroll = true;
      isBottom.current = true;

      // top limit
    } else if (!isDeltaPositive && -delta > scrollTop) {
      if (onTopArrive && !isTop.current) {
        onTopArrive(event);
      }
      target.scrollTop = 0;
      shouldCancelScroll = true;
      isTop.current = true;
    }

    // cancel scroll
    if (shouldCancelScroll) {
      cancelScroll(event);
    }
  }, [onBottomArrive, onBottomLeave, onTopArrive, onTopLeave]);
  var onWheel = useCallback(function (event) {
    handleEventDelta(event, event.deltaY);
  }, [handleEventDelta]);
  var onTouchStart = useCallback(function (event) {
    // set touch start so we can calculate touchmove delta
    touchStart.current = event.changedTouches[0].clientY;
  }, []);
  var onTouchMove = useCallback(function (event) {
    var deltaY = touchStart.current - event.changedTouches[0].clientY;
    handleEventDelta(event, deltaY);
  }, [handleEventDelta]);
  var startListening = useCallback(function (el) {
    // bail early if no element is available to attach to
    if (!el) return;
    var notPassive = supportsPassiveEvents ? {
      passive: false
    } : false;
    el.addEventListener('wheel', onWheel, notPassive);
    el.addEventListener('touchstart', onTouchStart, notPassive);
    el.addEventListener('touchmove', onTouchMove, notPassive);
  }, [onTouchMove, onTouchStart, onWheel]);
  var stopListening = useCallback(function (el) {
    // bail early if no element is available to detach from
    if (!el) return;
    el.removeEventListener('wheel', onWheel, false);
    el.removeEventListener('touchstart', onTouchStart, false);
    el.removeEventListener('touchmove', onTouchMove, false);
  }, [onTouchMove, onTouchStart, onWheel]);
  useEffect(function () {
    if (!isEnabled) return;
    var element = scrollTarget.current;
    startListening(element);
    return function () {
      stopListening(element);
    };
  }, [isEnabled, startListening, stopListening]);
  return function (element) {
    scrollTarget.current = element;
  };
}

var STYLE_KEYS = ['boxSizing', 'height', 'overflow', 'paddingRight', 'position'];
var LOCK_STYLES = {
  boxSizing: 'border-box',
  // account for possible declaration `width: 100%;` on body
  overflow: 'hidden',
  position: 'relative',
  height: '100%'
};
function preventTouchMove(e) {
  if (e.cancelable) e.preventDefault();
}
function allowTouchMove(e) {
  e.stopPropagation();
}
function preventInertiaScroll() {
  var top = this.scrollTop;
  var totalScroll = this.scrollHeight;
  var currentScroll = top + this.offsetHeight;
  if (top === 0) {
    this.scrollTop = 1;
  } else if (currentScroll === totalScroll) {
    this.scrollTop = top - 1;
  }
}

// `ontouchstart` check works on most browsers
// `maxTouchPoints` works on IE10/11 and Surface
function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints;
}
var canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
var activeScrollLocks = 0;
var listenerOptions = {
  capture: false,
  passive: false
};
function useScrollLock(_ref) {
  var isEnabled = _ref.isEnabled,
    _ref$accountForScroll = _ref.accountForScrollbars,
    accountForScrollbars = _ref$accountForScroll === void 0 ? true : _ref$accountForScroll;
  var originalStyles = useRef({});
  var scrollTarget = useRef(null);
  var addScrollLock = useCallback(function (touchScrollTarget) {
    if (!canUseDOM) return;
    var target = document.body;
    var targetStyle = target && target.style;
    if (accountForScrollbars) {
      // store any styles already applied to the body
      STYLE_KEYS.forEach(function (key) {
        var val = targetStyle && targetStyle[key];
        originalStyles.current[key] = val;
      });
    }

    // apply the lock styles and padding if this is the first scroll lock
    if (accountForScrollbars && activeScrollLocks < 1) {
      var currentPadding = parseInt(originalStyles.current.paddingRight, 10) || 0;
      var clientWidth = document.body ? document.body.clientWidth : 0;
      var adjustedPadding = window.innerWidth - clientWidth + currentPadding || 0;
      Object.keys(LOCK_STYLES).forEach(function (key) {
        var val = LOCK_STYLES[key];
        if (targetStyle) {
          targetStyle[key] = val;
        }
      });
      if (targetStyle) {
        targetStyle.paddingRight = "".concat(adjustedPadding, "px");
      }
    }

    // account for touch devices
    if (target && isTouchDevice()) {
      // Mobile Safari ignores { overflow: hidden } declaration on the body.
      target.addEventListener('touchmove', preventTouchMove, listenerOptions);

      // Allow scroll on provided target
      if (touchScrollTarget) {
        touchScrollTarget.addEventListener('touchstart', preventInertiaScroll, listenerOptions);
        touchScrollTarget.addEventListener('touchmove', allowTouchMove, listenerOptions);
      }
    }

    // increment active scroll locks
    activeScrollLocks += 1;
  }, [accountForScrollbars]);
  var removeScrollLock = useCallback(function (touchScrollTarget) {
    if (!canUseDOM) return;
    var target = document.body;
    var targetStyle = target && target.style;

    // safely decrement active scroll locks
    activeScrollLocks = Math.max(activeScrollLocks - 1, 0);

    // reapply original body styles, if any
    if (accountForScrollbars && activeScrollLocks < 1) {
      STYLE_KEYS.forEach(function (key) {
        var val = originalStyles.current[key];
        if (targetStyle) {
          targetStyle[key] = val;
        }
      });
    }

    // remove touch listeners
    if (target && isTouchDevice()) {
      target.removeEventListener('touchmove', preventTouchMove, listenerOptions);
      if (touchScrollTarget) {
        touchScrollTarget.removeEventListener('touchstart', preventInertiaScroll, listenerOptions);
        touchScrollTarget.removeEventListener('touchmove', allowTouchMove, listenerOptions);
      }
    }
  }, [accountForScrollbars]);
  useEffect(function () {
    if (!isEnabled) return;
    var element = scrollTarget.current;
    addScrollLock(element);
    return function () {
      removeScrollLock(element);
    };
  }, [isEnabled, addScrollLock, removeScrollLock]);
  return function (element) {
    scrollTarget.current = element;
  };
}

function _EMOTION_STRINGIFIED_CSS_ERROR__$1() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var blurSelectInput = function blurSelectInput(event) {
  var element = event.target;
  return element.ownerDocument.activeElement && element.ownerDocument.activeElement.blur();
};
var _ref2$1 = browser$1.env.NODE_ENV === "production" ? {
  name: "1kfdb0e",
  styles: "position:fixed;left:0;bottom:0;right:0;top:0"
} : {
  name: "bp8cua-ScrollManager",
  styles: "position:fixed;left:0;bottom:0;right:0;top:0;label:ScrollManager;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$1
};
function ScrollManager(_ref) {
  var children = _ref.children,
    lockEnabled = _ref.lockEnabled,
    _ref$captureEnabled = _ref.captureEnabled,
    captureEnabled = _ref$captureEnabled === void 0 ? true : _ref$captureEnabled,
    onBottomArrive = _ref.onBottomArrive,
    onBottomLeave = _ref.onBottomLeave,
    onTopArrive = _ref.onTopArrive,
    onTopLeave = _ref.onTopLeave;
  var setScrollCaptureTarget = useScrollCapture({
    isEnabled: captureEnabled,
    onBottomArrive: onBottomArrive,
    onBottomLeave: onBottomLeave,
    onTopArrive: onTopArrive,
    onTopLeave: onTopLeave
  });
  var setScrollLockTarget = useScrollLock({
    isEnabled: lockEnabled
  });
  var targetRef = function targetRef(element) {
    setScrollCaptureTarget(element);
    setScrollLockTarget(element);
  };
  return jsx(Fragment$1, null, lockEnabled && jsx("div", {
    onClick: blurSelectInput,
    css: _ref2$1
  }), children(targetRef));
}

function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var _ref2 = browser$1.env.NODE_ENV === "production" ? {
  name: "1a0ro4n-requiredInput",
  styles: "label:requiredInput;opacity:0;pointer-events:none;position:absolute;bottom:0;left:0;right:0;width:100%"
} : {
  name: "5kkxb2-requiredInput-RequiredInput",
  styles: "label:requiredInput;opacity:0;pointer-events:none;position:absolute;bottom:0;left:0;right:0;width:100%;label:RequiredInput;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIlJlcXVpcmVkSW5wdXQudHN4Il0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQWNJIiwiZmlsZSI6IlJlcXVpcmVkSW5wdXQudHN4Iiwic291cmNlc0NvbnRlbnQiOlsiLyoqIEBqc3gganN4ICovXG5pbXBvcnQgeyBGb2N1c0V2ZW50SGFuZGxlciwgRnVuY3Rpb25Db21wb25lbnQgfSBmcm9tICdyZWFjdCc7XG5pbXBvcnQgeyBqc3ggfSBmcm9tICdAZW1vdGlvbi9yZWFjdCc7XG5cbmNvbnN0IFJlcXVpcmVkSW5wdXQ6IEZ1bmN0aW9uQ29tcG9uZW50PHtcbiAgcmVhZG9ubHkgbmFtZT86IHN0cmluZztcbiAgcmVhZG9ubHkgb25Gb2N1czogRm9jdXNFdmVudEhhbmRsZXI8SFRNTElucHV0RWxlbWVudD47XG59PiA9ICh7IG5hbWUsIG9uRm9jdXMgfSkgPT4gKFxuICA8aW5wdXRcbiAgICByZXF1aXJlZFxuICAgIG5hbWU9e25hbWV9XG4gICAgdGFiSW5kZXg9ey0xfVxuICAgIGFyaWEtaGlkZGVuPVwidHJ1ZVwiXG4gICAgb25Gb2N1cz17b25Gb2N1c31cbiAgICBjc3M9e3tcbiAgICAgIGxhYmVsOiAncmVxdWlyZWRJbnB1dCcsXG4gICAgICBvcGFjaXR5OiAwLFxuICAgICAgcG9pbnRlckV2ZW50czogJ25vbmUnLFxuICAgICAgcG9zaXRpb246ICdhYnNvbHV0ZScsXG4gICAgICBib3R0b206IDAsXG4gICAgICBsZWZ0OiAwLFxuICAgICAgcmlnaHQ6IDAsXG4gICAgICB3aWR0aDogJzEwMCUnLFxuICAgIH19XG4gICAgLy8gUHJldmVudCBgU3dpdGNoaW5nIGZyb20gdW5jb250cm9sbGVkIHRvIGNvbnRyb2xsZWRgIGVycm9yXG4gICAgdmFsdWU9XCJcIlxuICAgIG9uQ2hhbmdlPXsoKSA9PiB7fX1cbiAgLz5cbik7XG5cbmV4cG9ydCBkZWZhdWx0IFJlcXVpcmVkSW5wdXQ7XG4iXX0= */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var RequiredInput = function RequiredInput(_ref) {
  var name = _ref.name,
    onFocus = _ref.onFocus;
  return jsx("input", {
    required: true,
    name: name,
    tabIndex: -1,
    "aria-hidden": "true",
    onFocus: onFocus,
    css: _ref2
    // Prevent `Switching from uncontrolled to controlled` error
    ,
    value: "",
    onChange: function onChange() {}
  });
};
var RequiredInput$1 = RequiredInput;

/// <reference types="user-agent-data-types" />

function testPlatform(re) {
  var _window$navigator$use;
  return typeof window !== 'undefined' && window.navigator != null ? re.test(((_window$navigator$use = window.navigator['userAgentData']) === null || _window$navigator$use === void 0 ? void 0 : _window$navigator$use.platform) || window.navigator.platform) : false;
}
function isIPhone() {
  return testPlatform(/^iPhone/i);
}
function isMac() {
  return testPlatform(/^Mac/i);
}
function isIPad() {
  return testPlatform(/^iPad/i) ||
  // iPadOS 13 lies and says it's a Mac, but we can distinguish by detecting touch support.
  isMac() && navigator.maxTouchPoints > 1;
}
function isIOS() {
  return isIPhone() || isIPad();
}
function isAppleDevice() {
  return isMac() || isIOS();
}

var formatGroupLabel = function formatGroupLabel(group) {
  return group.label;
};
var getOptionLabel$1 = function getOptionLabel(option) {
  return option.label;
};
var getOptionValue$1 = function getOptionValue(option) {
  return option.value;
};
var isOptionDisabled = function isOptionDisabled(option) {
  return !!option.isDisabled;
};

var defaultStyles = {
  clearIndicator: clearIndicatorCSS,
  container: containerCSS,
  control: css$1,
  dropdownIndicator: dropdownIndicatorCSS,
  group: groupCSS,
  groupHeading: groupHeadingCSS,
  indicatorsContainer: indicatorsContainerCSS,
  indicatorSeparator: indicatorSeparatorCSS,
  input: inputCSS,
  loadingIndicator: loadingIndicatorCSS,
  loadingMessage: loadingMessageCSS,
  menu: menuCSS,
  menuList: menuListCSS,
  menuPortal: menuPortalCSS,
  multiValue: multiValueCSS,
  multiValueLabel: multiValueLabelCSS,
  multiValueRemove: multiValueRemoveCSS,
  noOptionsMessage: noOptionsMessageCSS,
  option: optionCSS,
  placeholder: placeholderCSS,
  singleValue: css,
  valueContainer: valueContainerCSS
};

var colors = {
  primary: '#2684FF',
  primary75: '#4C9AFF',
  primary50: '#B2D4FF',
  primary25: '#DEEBFF',
  danger: '#DE350B',
  dangerLight: '#FFBDAD',
  neutral0: 'hsl(0, 0%, 100%)',
  neutral5: 'hsl(0, 0%, 95%)',
  neutral10: 'hsl(0, 0%, 90%)',
  neutral20: 'hsl(0, 0%, 80%)',
  neutral30: 'hsl(0, 0%, 70%)',
  neutral40: 'hsl(0, 0%, 60%)',
  neutral50: 'hsl(0, 0%, 50%)',
  neutral60: 'hsl(0, 0%, 40%)',
  neutral70: 'hsl(0, 0%, 30%)',
  neutral80: 'hsl(0, 0%, 20%)',
  neutral90: 'hsl(0, 0%, 10%)'
};
var borderRadius = 4;
// Used to calculate consistent margin/padding on elements
var baseUnit = 4;
// The minimum height of the control
var controlHeight = 38;
// The amount of space between the control and menu */
var menuGutter = baseUnit * 2;
var spacing = {
  baseUnit: baseUnit,
  controlHeight: controlHeight,
  menuGutter: menuGutter
};
var defaultTheme = {
  borderRadius: borderRadius,
  colors: colors,
  spacing: spacing
};

var defaultProps = {
  'aria-live': 'polite',
  backspaceRemovesValue: true,
  blurInputOnSelect: isTouchCapable(),
  captureMenuScroll: !isTouchCapable(),
  classNames: {},
  closeMenuOnSelect: true,
  closeMenuOnScroll: false,
  components: {},
  controlShouldRenderValue: true,
  escapeClearsValue: false,
  filterOption: createFilter(),
  formatGroupLabel: formatGroupLabel,
  getOptionLabel: getOptionLabel$1,
  getOptionValue: getOptionValue$1,
  isDisabled: false,
  isLoading: false,
  isMulti: false,
  isRtl: false,
  isSearchable: true,
  isOptionDisabled: isOptionDisabled,
  loadingMessage: function loadingMessage() {
    return 'Loading...';
  },
  maxMenuHeight: 300,
  minMenuHeight: 140,
  menuIsOpen: false,
  menuPlacement: 'bottom',
  menuPosition: 'absolute',
  menuShouldBlockScroll: false,
  menuShouldScrollIntoView: !isMobileDevice(),
  noOptionsMessage: function noOptionsMessage() {
    return 'No options';
  },
  openMenuOnFocus: false,
  openMenuOnClick: true,
  options: [],
  pageSize: 5,
  placeholder: 'Select...',
  screenReaderStatus: function screenReaderStatus(_ref) {
    var count = _ref.count;
    return "".concat(count, " result").concat(count !== 1 ? 's' : '', " available");
  },
  styles: {},
  tabIndex: 0,
  tabSelectsValue: true,
  unstyled: false
};
function toCategorizedOption(props, option, selectValue, index) {
  var isDisabled = _isOptionDisabled(props, option, selectValue);
  var isSelected = _isOptionSelected(props, option, selectValue);
  var label = getOptionLabel(props, option);
  var value = getOptionValue(props, option);
  return {
    type: 'option',
    data: option,
    isDisabled: isDisabled,
    isSelected: isSelected,
    label: label,
    value: value,
    index: index
  };
}
function buildCategorizedOptions(props, selectValue) {
  return props.options.map(function (groupOrOption, groupOrOptionIndex) {
    if ('options' in groupOrOption) {
      var categorizedOptions = groupOrOption.options.map(function (option, optionIndex) {
        return toCategorizedOption(props, option, selectValue, optionIndex);
      }).filter(function (categorizedOption) {
        return isFocusable(props, categorizedOption);
      });
      return categorizedOptions.length > 0 ? {
        type: 'group',
        data: groupOrOption,
        options: categorizedOptions,
        index: groupOrOptionIndex
      } : undefined;
    }
    var categorizedOption = toCategorizedOption(props, groupOrOption, selectValue, groupOrOptionIndex);
    return isFocusable(props, categorizedOption) ? categorizedOption : undefined;
  }).filter(notNullish);
}
function buildFocusableOptionsFromCategorizedOptions(categorizedOptions) {
  return categorizedOptions.reduce(function (optionsAccumulator, categorizedOption) {
    if (categorizedOption.type === 'group') {
      optionsAccumulator.push.apply(optionsAccumulator, _toConsumableArray(categorizedOption.options.map(function (option) {
        return option.data;
      })));
    } else {
      optionsAccumulator.push(categorizedOption.data);
    }
    return optionsAccumulator;
  }, []);
}
function buildFocusableOptionsWithIds(categorizedOptions, optionId) {
  return categorizedOptions.reduce(function (optionsAccumulator, categorizedOption) {
    if (categorizedOption.type === 'group') {
      optionsAccumulator.push.apply(optionsAccumulator, _toConsumableArray(categorizedOption.options.map(function (option) {
        return {
          data: option.data,
          id: "".concat(optionId, "-").concat(categorizedOption.index, "-").concat(option.index)
        };
      })));
    } else {
      optionsAccumulator.push({
        data: categorizedOption.data,
        id: "".concat(optionId, "-").concat(categorizedOption.index)
      });
    }
    return optionsAccumulator;
  }, []);
}
function buildFocusableOptions(props, selectValue) {
  return buildFocusableOptionsFromCategorizedOptions(buildCategorizedOptions(props, selectValue));
}
function isFocusable(props, categorizedOption) {
  var _props$inputValue = props.inputValue,
    inputValue = _props$inputValue === void 0 ? '' : _props$inputValue;
  var data = categorizedOption.data,
    isSelected = categorizedOption.isSelected,
    label = categorizedOption.label,
    value = categorizedOption.value;
  return (!shouldHideSelectedOptions(props) || !isSelected) && _filterOption(props, {
    label: label,
    value: value,
    data: data
  }, inputValue);
}
function getNextFocusedValue(state, nextSelectValue) {
  var focusedValue = state.focusedValue,
    lastSelectValue = state.selectValue;
  var lastFocusedIndex = lastSelectValue.indexOf(focusedValue);
  if (lastFocusedIndex > -1) {
    var nextFocusedIndex = nextSelectValue.indexOf(focusedValue);
    if (nextFocusedIndex > -1) {
      // the focused value is still in the selectValue, return it
      return focusedValue;
    } else if (lastFocusedIndex < nextSelectValue.length) {
      // the focusedValue is not present in the next selectValue array by
      // reference, so return the new value at the same index
      return nextSelectValue[lastFocusedIndex];
    }
  }
  return null;
}
function getNextFocusedOption(state, options) {
  var lastFocusedOption = state.focusedOption;
  return lastFocusedOption && options.indexOf(lastFocusedOption) > -1 ? lastFocusedOption : options[0];
}
var getFocusedOptionId = function getFocusedOptionId(focusableOptionsWithIds, focusedOption) {
  var _focusableOptionsWith;
  var focusedOptionId = (_focusableOptionsWith = focusableOptionsWithIds.find(function (option) {
    return option.data === focusedOption;
  })) === null || _focusableOptionsWith === void 0 ? void 0 : _focusableOptionsWith.id;
  return focusedOptionId || null;
};
var getOptionLabel = function getOptionLabel(props, data) {
  return props.getOptionLabel(data);
};
var getOptionValue = function getOptionValue(props, data) {
  return props.getOptionValue(data);
};
function _isOptionDisabled(props, option, selectValue) {
  return typeof props.isOptionDisabled === 'function' ? props.isOptionDisabled(option, selectValue) : false;
}
function _isOptionSelected(props, option, selectValue) {
  if (selectValue.indexOf(option) > -1) return true;
  if (typeof props.isOptionSelected === 'function') {
    return props.isOptionSelected(option, selectValue);
  }
  var candidate = getOptionValue(props, option);
  return selectValue.some(function (i) {
    return getOptionValue(props, i) === candidate;
  });
}
function _filterOption(props, option, inputValue) {
  return props.filterOption ? props.filterOption(option, inputValue) : true;
}
var shouldHideSelectedOptions = function shouldHideSelectedOptions(props) {
  var hideSelectedOptions = props.hideSelectedOptions,
    isMulti = props.isMulti;
  if (hideSelectedOptions === undefined) return isMulti;
  return hideSelectedOptions;
};
var instanceId = 1;
var Select$2 = /*#__PURE__*/function (_Component) {
  _inherits(Select, _Component);
  var _super = _createSuper(Select);
  // Misc. Instance Properties
  // ------------------------------

  // TODO

  // Refs
  // ------------------------------

  // Lifecycle
  // ------------------------------

  function Select(_props) {
    var _this;
    _classCallCheck(this, Select);
    _this = _super.call(this, _props);
    _this.state = {
      ariaSelection: null,
      focusedOption: null,
      focusedOptionId: null,
      focusableOptionsWithIds: [],
      focusedValue: null,
      inputIsHidden: false,
      isFocused: false,
      selectValue: [],
      clearFocusValueOnUpdate: false,
      prevWasFocused: false,
      inputIsHiddenAfterUpdate: undefined,
      prevProps: undefined,
      instancePrefix: ''
    };
    _this.blockOptionHover = false;
    _this.isComposing = false;
    _this.commonProps = void 0;
    _this.initialTouchX = 0;
    _this.initialTouchY = 0;
    _this.openAfterFocus = false;
    _this.scrollToFocusedOptionOnUpdate = false;
    _this.userIsDragging = void 0;
    _this.isAppleDevice = isAppleDevice();
    _this.controlRef = null;
    _this.getControlRef = function (ref) {
      _this.controlRef = ref;
    };
    _this.focusedOptionRef = null;
    _this.getFocusedOptionRef = function (ref) {
      _this.focusedOptionRef = ref;
    };
    _this.menuListRef = null;
    _this.getMenuListRef = function (ref) {
      _this.menuListRef = ref;
    };
    _this.inputRef = null;
    _this.getInputRef = function (ref) {
      _this.inputRef = ref;
    };
    _this.focus = _this.focusInput;
    _this.blur = _this.blurInput;
    _this.onChange = function (newValue, actionMeta) {
      var _this$props = _this.props,
        onChange = _this$props.onChange,
        name = _this$props.name;
      actionMeta.name = name;
      _this.ariaOnChange(newValue, actionMeta);
      onChange(newValue, actionMeta);
    };
    _this.setValue = function (newValue, action, option) {
      var _this$props2 = _this.props,
        closeMenuOnSelect = _this$props2.closeMenuOnSelect,
        isMulti = _this$props2.isMulti,
        inputValue = _this$props2.inputValue;
      _this.onInputChange('', {
        action: 'set-value',
        prevInputValue: inputValue
      });
      if (closeMenuOnSelect) {
        _this.setState({
          inputIsHiddenAfterUpdate: !isMulti
        });
        _this.onMenuClose();
      }
      // when the select value should change, we should reset focusedValue
      _this.setState({
        clearFocusValueOnUpdate: true
      });
      _this.onChange(newValue, {
        action: action,
        option: option
      });
    };
    _this.selectOption = function (newValue) {
      var _this$props3 = _this.props,
        blurInputOnSelect = _this$props3.blurInputOnSelect,
        isMulti = _this$props3.isMulti,
        name = _this$props3.name;
      var selectValue = _this.state.selectValue;
      var deselected = isMulti && _this.isOptionSelected(newValue, selectValue);
      var isDisabled = _this.isOptionDisabled(newValue, selectValue);
      if (deselected) {
        var candidate = _this.getOptionValue(newValue);
        _this.setValue(multiValueAsValue(selectValue.filter(function (i) {
          return _this.getOptionValue(i) !== candidate;
        })), 'deselect-option', newValue);
      } else if (!isDisabled) {
        // Select option if option is not disabled
        if (isMulti) {
          _this.setValue(multiValueAsValue([].concat(_toConsumableArray(selectValue), [newValue])), 'select-option', newValue);
        } else {
          _this.setValue(singleValueAsValue(newValue), 'select-option');
        }
      } else {
        _this.ariaOnChange(singleValueAsValue(newValue), {
          action: 'select-option',
          option: newValue,
          name: name
        });
        return;
      }
      if (blurInputOnSelect) {
        _this.blurInput();
      }
    };
    _this.removeValue = function (removedValue) {
      var isMulti = _this.props.isMulti;
      var selectValue = _this.state.selectValue;
      var candidate = _this.getOptionValue(removedValue);
      var newValueArray = selectValue.filter(function (i) {
        return _this.getOptionValue(i) !== candidate;
      });
      var newValue = valueTernary(isMulti, newValueArray, newValueArray[0] || null);
      _this.onChange(newValue, {
        action: 'remove-value',
        removedValue: removedValue
      });
      _this.focusInput();
    };
    _this.clearValue = function () {
      var selectValue = _this.state.selectValue;
      _this.onChange(valueTernary(_this.props.isMulti, [], null), {
        action: 'clear',
        removedValues: selectValue
      });
    };
    _this.popValue = function () {
      var isMulti = _this.props.isMulti;
      var selectValue = _this.state.selectValue;
      var lastSelectedValue = selectValue[selectValue.length - 1];
      var newValueArray = selectValue.slice(0, selectValue.length - 1);
      var newValue = valueTernary(isMulti, newValueArray, newValueArray[0] || null);
      if (lastSelectedValue) {
        _this.onChange(newValue, {
          action: 'pop-value',
          removedValue: lastSelectedValue
        });
      }
    };
    _this.getFocusedOptionId = function (focusedOption) {
      return getFocusedOptionId(_this.state.focusableOptionsWithIds, focusedOption);
    };
    _this.getFocusableOptionsWithIds = function () {
      return buildFocusableOptionsWithIds(buildCategorizedOptions(_this.props, _this.state.selectValue), _this.getElementId('option'));
    };
    _this.getValue = function () {
      return _this.state.selectValue;
    };
    _this.cx = function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return classNames.apply(void 0, [_this.props.classNamePrefix].concat(args));
    };
    _this.getOptionLabel = function (data) {
      return getOptionLabel(_this.props, data);
    };
    _this.getOptionValue = function (data) {
      return getOptionValue(_this.props, data);
    };
    _this.getStyles = function (key, props) {
      var unstyled = _this.props.unstyled;
      var base = defaultStyles[key](props, unstyled);
      base.boxSizing = 'border-box';
      var custom = _this.props.styles[key];
      return custom ? custom(base, props) : base;
    };
    _this.getClassNames = function (key, props) {
      var _this$props$className, _this$props$className2;
      return (_this$props$className = (_this$props$className2 = _this.props.classNames)[key]) === null || _this$props$className === void 0 ? void 0 : _this$props$className.call(_this$props$className2, props);
    };
    _this.getElementId = function (element) {
      return "".concat(_this.state.instancePrefix, "-").concat(element);
    };
    _this.getComponents = function () {
      return defaultComponents(_this.props);
    };
    _this.buildCategorizedOptions = function () {
      return buildCategorizedOptions(_this.props, _this.state.selectValue);
    };
    _this.getCategorizedOptions = function () {
      return _this.props.menuIsOpen ? _this.buildCategorizedOptions() : [];
    };
    _this.buildFocusableOptions = function () {
      return buildFocusableOptionsFromCategorizedOptions(_this.buildCategorizedOptions());
    };
    _this.getFocusableOptions = function () {
      return _this.props.menuIsOpen ? _this.buildFocusableOptions() : [];
    };
    _this.ariaOnChange = function (value, actionMeta) {
      _this.setState({
        ariaSelection: _objectSpread2({
          value: value
        }, actionMeta)
      });
    };
    _this.onMenuMouseDown = function (event) {
      if (event.button !== 0) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      _this.focusInput();
    };
    _this.onMenuMouseMove = function (event) {
      _this.blockOptionHover = false;
    };
    _this.onControlMouseDown = function (event) {
      // Event captured by dropdown indicator
      if (event.defaultPrevented) {
        return;
      }
      var openMenuOnClick = _this.props.openMenuOnClick;
      if (!_this.state.isFocused) {
        if (openMenuOnClick) {
          _this.openAfterFocus = true;
        }
        _this.focusInput();
      } else if (!_this.props.menuIsOpen) {
        if (openMenuOnClick) {
          _this.openMenu('first');
        }
      } else {
        if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
          _this.onMenuClose();
        }
      }
      if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
        event.preventDefault();
      }
    };
    _this.onDropdownIndicatorMouseDown = function (event) {
      // ignore mouse events that weren't triggered by the primary button
      if (event && event.type === 'mousedown' && event.button !== 0) {
        return;
      }
      if (_this.props.isDisabled) return;
      var _this$props4 = _this.props,
        isMulti = _this$props4.isMulti,
        menuIsOpen = _this$props4.menuIsOpen;
      _this.focusInput();
      if (menuIsOpen) {
        _this.setState({
          inputIsHiddenAfterUpdate: !isMulti
        });
        _this.onMenuClose();
      } else {
        _this.openMenu('first');
      }
      event.preventDefault();
    };
    _this.onClearIndicatorMouseDown = function (event) {
      // ignore mouse events that weren't triggered by the primary button
      if (event && event.type === 'mousedown' && event.button !== 0) {
        return;
      }
      _this.clearValue();
      event.preventDefault();
      _this.openAfterFocus = false;
      if (event.type === 'touchend') {
        _this.focusInput();
      } else {
        setTimeout(function () {
          return _this.focusInput();
        });
      }
    };
    _this.onScroll = function (event) {
      if (typeof _this.props.closeMenuOnScroll === 'boolean') {
        if (event.target instanceof HTMLElement && isDocumentElement(event.target)) {
          _this.props.onMenuClose();
        }
      } else if (typeof _this.props.closeMenuOnScroll === 'function') {
        if (_this.props.closeMenuOnScroll(event)) {
          _this.props.onMenuClose();
        }
      }
    };
    _this.onCompositionStart = function () {
      _this.isComposing = true;
    };
    _this.onCompositionEnd = function () {
      _this.isComposing = false;
    };
    _this.onTouchStart = function (_ref2) {
      var touches = _ref2.touches;
      var touch = touches && touches.item(0);
      if (!touch) {
        return;
      }
      _this.initialTouchX = touch.clientX;
      _this.initialTouchY = touch.clientY;
      _this.userIsDragging = false;
    };
    _this.onTouchMove = function (_ref3) {
      var touches = _ref3.touches;
      var touch = touches && touches.item(0);
      if (!touch) {
        return;
      }
      var deltaX = Math.abs(touch.clientX - _this.initialTouchX);
      var deltaY = Math.abs(touch.clientY - _this.initialTouchY);
      var moveThreshold = 5;
      _this.userIsDragging = deltaX > moveThreshold || deltaY > moveThreshold;
    };
    _this.onTouchEnd = function (event) {
      if (_this.userIsDragging) return;

      // close the menu if the user taps outside
      // we're checking on event.target here instead of event.currentTarget, because we want to assert information
      // on events on child elements, not the document (which we've attached this handler to).
      if (_this.controlRef && !_this.controlRef.contains(event.target) && _this.menuListRef && !_this.menuListRef.contains(event.target)) {
        _this.blurInput();
      }

      // reset move vars
      _this.initialTouchX = 0;
      _this.initialTouchY = 0;
    };
    _this.onControlTouchEnd = function (event) {
      if (_this.userIsDragging) return;
      _this.onControlMouseDown(event);
    };
    _this.onClearIndicatorTouchEnd = function (event) {
      if (_this.userIsDragging) return;
      _this.onClearIndicatorMouseDown(event);
    };
    _this.onDropdownIndicatorTouchEnd = function (event) {
      if (_this.userIsDragging) return;
      _this.onDropdownIndicatorMouseDown(event);
    };
    _this.handleInputChange = function (event) {
      var prevInputValue = _this.props.inputValue;
      var inputValue = event.currentTarget.value;
      _this.setState({
        inputIsHiddenAfterUpdate: false
      });
      _this.onInputChange(inputValue, {
        action: 'input-change',
        prevInputValue: prevInputValue
      });
      if (!_this.props.menuIsOpen) {
        _this.onMenuOpen();
      }
    };
    _this.onInputFocus = function (event) {
      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
      _this.setState({
        inputIsHiddenAfterUpdate: false,
        isFocused: true
      });
      if (_this.openAfterFocus || _this.props.openMenuOnFocus) {
        _this.openMenu('first');
      }
      _this.openAfterFocus = false;
    };
    _this.onInputBlur = function (event) {
      var prevInputValue = _this.props.inputValue;
      if (_this.menuListRef && _this.menuListRef.contains(document.activeElement)) {
        _this.inputRef.focus();
        return;
      }
      if (_this.props.onBlur) {
        _this.props.onBlur(event);
      }
      _this.onInputChange('', {
        action: 'input-blur',
        prevInputValue: prevInputValue
      });
      _this.onMenuClose();
      _this.setState({
        focusedValue: null,
        isFocused: false
      });
    };
    _this.onOptionHover = function (focusedOption) {
      if (_this.blockOptionHover || _this.state.focusedOption === focusedOption) {
        return;
      }
      var options = _this.getFocusableOptions();
      var focusedOptionIndex = options.indexOf(focusedOption);
      _this.setState({
        focusedOption: focusedOption,
        focusedOptionId: focusedOptionIndex > -1 ? _this.getFocusedOptionId(focusedOption) : null
      });
    };
    _this.shouldHideSelectedOptions = function () {
      return shouldHideSelectedOptions(_this.props);
    };
    _this.onValueInputFocus = function (e) {
      e.preventDefault();
      e.stopPropagation();
      _this.focus();
    };
    _this.onKeyDown = function (event) {
      var _this$props5 = _this.props,
        isMulti = _this$props5.isMulti,
        backspaceRemovesValue = _this$props5.backspaceRemovesValue,
        escapeClearsValue = _this$props5.escapeClearsValue,
        inputValue = _this$props5.inputValue,
        isClearable = _this$props5.isClearable,
        isDisabled = _this$props5.isDisabled,
        menuIsOpen = _this$props5.menuIsOpen,
        onKeyDown = _this$props5.onKeyDown,
        tabSelectsValue = _this$props5.tabSelectsValue,
        openMenuOnFocus = _this$props5.openMenuOnFocus;
      var _this$state = _this.state,
        focusedOption = _this$state.focusedOption,
        focusedValue = _this$state.focusedValue,
        selectValue = _this$state.selectValue;
      if (isDisabled) return;
      if (typeof onKeyDown === 'function') {
        onKeyDown(event);
        if (event.defaultPrevented) {
          return;
        }
      }

      // Block option hover events when the user has just pressed a key
      _this.blockOptionHover = true;
      switch (event.key) {
        case 'ArrowLeft':
          if (!isMulti || inputValue) return;
          _this.focusValue('previous');
          break;
        case 'ArrowRight':
          if (!isMulti || inputValue) return;
          _this.focusValue('next');
          break;
        case 'Delete':
        case 'Backspace':
          if (inputValue) return;
          if (focusedValue) {
            _this.removeValue(focusedValue);
          } else {
            if (!backspaceRemovesValue) return;
            if (isMulti) {
              _this.popValue();
            } else if (isClearable) {
              _this.clearValue();
            }
          }
          break;
        case 'Tab':
          if (_this.isComposing) return;
          if (event.shiftKey || !menuIsOpen || !tabSelectsValue || !focusedOption ||
          // don't capture the event if the menu opens on focus and the focused
          // option is already selected; it breaks the flow of navigation
          openMenuOnFocus && _this.isOptionSelected(focusedOption, selectValue)) {
            return;
          }
          _this.selectOption(focusedOption);
          break;
        case 'Enter':
          if (event.keyCode === 229) {
            // ignore the keydown event from an Input Method Editor(IME)
            // ref. https://www.w3.org/TR/uievents/#determine-keydown-keyup-keyCode
            break;
          }
          if (menuIsOpen) {
            if (!focusedOption) return;
            if (_this.isComposing) return;
            _this.selectOption(focusedOption);
            break;
          }
          return;
        case 'Escape':
          if (menuIsOpen) {
            _this.setState({
              inputIsHiddenAfterUpdate: false
            });
            _this.onInputChange('', {
              action: 'menu-close',
              prevInputValue: inputValue
            });
            _this.onMenuClose();
          } else if (isClearable && escapeClearsValue) {
            _this.clearValue();
          }
          break;
        case ' ':
          // space
          if (inputValue) {
            return;
          }
          if (!menuIsOpen) {
            _this.openMenu('first');
            break;
          }
          if (!focusedOption) return;
          _this.selectOption(focusedOption);
          break;
        case 'ArrowUp':
          if (menuIsOpen) {
            _this.focusOption('up');
          } else {
            _this.openMenu('last');
          }
          break;
        case 'ArrowDown':
          if (menuIsOpen) {
            _this.focusOption('down');
          } else {
            _this.openMenu('first');
          }
          break;
        case 'PageUp':
          if (!menuIsOpen) return;
          _this.focusOption('pageup');
          break;
        case 'PageDown':
          if (!menuIsOpen) return;
          _this.focusOption('pagedown');
          break;
        case 'Home':
          if (!menuIsOpen) return;
          _this.focusOption('first');
          break;
        case 'End':
          if (!menuIsOpen) return;
          _this.focusOption('last');
          break;
        default:
          return;
      }
      event.preventDefault();
    };
    _this.state.instancePrefix = 'react-select-' + (_this.props.instanceId || ++instanceId);
    _this.state.selectValue = cleanValue(_props.value);
    // Set focusedOption if menuIsOpen is set on init (e.g. defaultMenuIsOpen)
    if (_props.menuIsOpen && _this.state.selectValue.length) {
      var focusableOptionsWithIds = _this.getFocusableOptionsWithIds();
      var focusableOptions = _this.buildFocusableOptions();
      var optionIndex = focusableOptions.indexOf(_this.state.selectValue[0]);
      _this.state.focusableOptionsWithIds = focusableOptionsWithIds;
      _this.state.focusedOption = focusableOptions[optionIndex];
      _this.state.focusedOptionId = getFocusedOptionId(focusableOptionsWithIds, focusableOptions[optionIndex]);
    }
    return _this;
  }
  _createClass(Select, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.startListeningComposition();
      this.startListeningToTouch();
      if (this.props.closeMenuOnScroll && document && document.addEventListener) {
        // Listen to all scroll events, and filter them out inside of 'onScroll'
        document.addEventListener('scroll', this.onScroll, true);
      }
      if (this.props.autoFocus) {
        this.focusInput();
      }

      // Scroll focusedOption into view if menuIsOpen is set on mount (e.g. defaultMenuIsOpen)
      if (this.props.menuIsOpen && this.state.focusedOption && this.menuListRef && this.focusedOptionRef) {
        scrollIntoView(this.menuListRef, this.focusedOptionRef);
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props6 = this.props,
        isDisabled = _this$props6.isDisabled,
        menuIsOpen = _this$props6.menuIsOpen;
      var isFocused = this.state.isFocused;
      if (
      // ensure focus is restored correctly when the control becomes enabled
      isFocused && !isDisabled && prevProps.isDisabled ||
      // ensure focus is on the Input when the menu opens
      isFocused && menuIsOpen && !prevProps.menuIsOpen) {
        this.focusInput();
      }
      if (isFocused && isDisabled && !prevProps.isDisabled) {
        // ensure select state gets blurred in case Select is programmatically disabled while focused
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          isFocused: false
        }, this.onMenuClose);
      } else if (!isFocused && !isDisabled && prevProps.isDisabled && this.inputRef === document.activeElement) {
        // ensure select state gets focused in case Select is programatically re-enabled while focused (Firefox)
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          isFocused: true
        });
      }

      // scroll the focused option into view if necessary
      if (this.menuListRef && this.focusedOptionRef && this.scrollToFocusedOptionOnUpdate) {
        scrollIntoView(this.menuListRef, this.focusedOptionRef);
        this.scrollToFocusedOptionOnUpdate = false;
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.stopListeningComposition();
      this.stopListeningToTouch();
      document.removeEventListener('scroll', this.onScroll, true);
    }

    // ==============================
    // Consumer Handlers
    // ==============================
  }, {
    key: "onMenuOpen",
    value: function onMenuOpen() {
      this.props.onMenuOpen();
    }
  }, {
    key: "onMenuClose",
    value: function onMenuClose() {
      this.onInputChange('', {
        action: 'menu-close',
        prevInputValue: this.props.inputValue
      });
      this.props.onMenuClose();
    }
  }, {
    key: "onInputChange",
    value: function onInputChange(newValue, actionMeta) {
      this.props.onInputChange(newValue, actionMeta);
    }

    // ==============================
    // Methods
    // ==============================
  }, {
    key: "focusInput",
    value: function focusInput() {
      if (!this.inputRef) return;
      this.inputRef.focus();
    }
  }, {
    key: "blurInput",
    value: function blurInput() {
      if (!this.inputRef) return;
      this.inputRef.blur();
    }

    // aliased for consumers
  }, {
    key: "openMenu",
    value: function openMenu(focusOption) {
      var _this2 = this;
      var _this$state2 = this.state,
        selectValue = _this$state2.selectValue,
        isFocused = _this$state2.isFocused;
      var focusableOptions = this.buildFocusableOptions();
      var openAtIndex = focusOption === 'first' ? 0 : focusableOptions.length - 1;
      if (!this.props.isMulti) {
        var selectedIndex = focusableOptions.indexOf(selectValue[0]);
        if (selectedIndex > -1) {
          openAtIndex = selectedIndex;
        }
      }

      // only scroll if the menu isn't already open
      this.scrollToFocusedOptionOnUpdate = !(isFocused && this.menuListRef);
      this.setState({
        inputIsHiddenAfterUpdate: false,
        focusedValue: null,
        focusedOption: focusableOptions[openAtIndex],
        focusedOptionId: this.getFocusedOptionId(focusableOptions[openAtIndex])
      }, function () {
        return _this2.onMenuOpen();
      });
    }
  }, {
    key: "focusValue",
    value: function focusValue(direction) {
      var _this$state3 = this.state,
        selectValue = _this$state3.selectValue,
        focusedValue = _this$state3.focusedValue;

      // Only multiselects support value focusing
      if (!this.props.isMulti) return;
      this.setState({
        focusedOption: null
      });
      var focusedIndex = selectValue.indexOf(focusedValue);
      if (!focusedValue) {
        focusedIndex = -1;
      }
      var lastIndex = selectValue.length - 1;
      var nextFocus = -1;
      if (!selectValue.length) return;
      switch (direction) {
        case 'previous':
          if (focusedIndex === 0) {
            // don't cycle from the start to the end
            nextFocus = 0;
          } else if (focusedIndex === -1) {
            // if nothing is focused, focus the last value first
            nextFocus = lastIndex;
          } else {
            nextFocus = focusedIndex - 1;
          }
          break;
        case 'next':
          if (focusedIndex > -1 && focusedIndex < lastIndex) {
            nextFocus = focusedIndex + 1;
          }
          break;
      }
      this.setState({
        inputIsHidden: nextFocus !== -1,
        focusedValue: selectValue[nextFocus]
      });
    }
  }, {
    key: "focusOption",
    value: function focusOption() {
      var direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'first';
      var pageSize = this.props.pageSize;
      var focusedOption = this.state.focusedOption;
      var options = this.getFocusableOptions();
      if (!options.length) return;
      var nextFocus = 0; // handles 'first'
      var focusedIndex = options.indexOf(focusedOption);
      if (!focusedOption) {
        focusedIndex = -1;
      }
      if (direction === 'up') {
        nextFocus = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
      } else if (direction === 'down') {
        nextFocus = (focusedIndex + 1) % options.length;
      } else if (direction === 'pageup') {
        nextFocus = focusedIndex - pageSize;
        if (nextFocus < 0) nextFocus = 0;
      } else if (direction === 'pagedown') {
        nextFocus = focusedIndex + pageSize;
        if (nextFocus > options.length - 1) nextFocus = options.length - 1;
      } else if (direction === 'last') {
        nextFocus = options.length - 1;
      }
      this.scrollToFocusedOptionOnUpdate = true;
      this.setState({
        focusedOption: options[nextFocus],
        focusedValue: null,
        focusedOptionId: this.getFocusedOptionId(options[nextFocus])
      });
    }
  }, {
    key: "getTheme",
    value:
    // ==============================
    // Getters
    // ==============================

    function getTheme() {
      // Use the default theme if there are no customisations.
      if (!this.props.theme) {
        return defaultTheme;
      }
      // If the theme prop is a function, assume the function
      // knows how to merge the passed-in default theme with
      // its own modifications.
      if (typeof this.props.theme === 'function') {
        return this.props.theme(defaultTheme);
      }
      // Otherwise, if a plain theme object was passed in,
      // overlay it with the default theme.
      return _objectSpread2(_objectSpread2({}, defaultTheme), this.props.theme);
    }
  }, {
    key: "getCommonProps",
    value: function getCommonProps() {
      var clearValue = this.clearValue,
        cx = this.cx,
        getStyles = this.getStyles,
        getClassNames = this.getClassNames,
        getValue = this.getValue,
        selectOption = this.selectOption,
        setValue = this.setValue,
        props = this.props;
      var isMulti = props.isMulti,
        isRtl = props.isRtl,
        options = props.options;
      var hasValue = this.hasValue();
      return {
        clearValue: clearValue,
        cx: cx,
        getStyles: getStyles,
        getClassNames: getClassNames,
        getValue: getValue,
        hasValue: hasValue,
        isMulti: isMulti,
        isRtl: isRtl,
        options: options,
        selectOption: selectOption,
        selectProps: props,
        setValue: setValue,
        theme: this.getTheme()
      };
    }
  }, {
    key: "hasValue",
    value: function hasValue() {
      var selectValue = this.state.selectValue;
      return selectValue.length > 0;
    }
  }, {
    key: "hasOptions",
    value: function hasOptions() {
      return !!this.getFocusableOptions().length;
    }
  }, {
    key: "isClearable",
    value: function isClearable() {
      var _this$props7 = this.props,
        isClearable = _this$props7.isClearable,
        isMulti = _this$props7.isMulti;

      // single select, by default, IS NOT clearable
      // multi select, by default, IS clearable
      if (isClearable === undefined) return isMulti;
      return isClearable;
    }
  }, {
    key: "isOptionDisabled",
    value: function isOptionDisabled(option, selectValue) {
      return _isOptionDisabled(this.props, option, selectValue);
    }
  }, {
    key: "isOptionSelected",
    value: function isOptionSelected(option, selectValue) {
      return _isOptionSelected(this.props, option, selectValue);
    }
  }, {
    key: "filterOption",
    value: function filterOption(option, inputValue) {
      return _filterOption(this.props, option, inputValue);
    }
  }, {
    key: "formatOptionLabel",
    value: function formatOptionLabel(data, context) {
      if (typeof this.props.formatOptionLabel === 'function') {
        var _inputValue = this.props.inputValue;
        var _selectValue = this.state.selectValue;
        return this.props.formatOptionLabel(data, {
          context: context,
          inputValue: _inputValue,
          selectValue: _selectValue
        });
      } else {
        return this.getOptionLabel(data);
      }
    }
  }, {
    key: "formatGroupLabel",
    value: function formatGroupLabel(data) {
      return this.props.formatGroupLabel(data);
    }

    // ==============================
    // Mouse Handlers
    // ==============================
  }, {
    key: "startListeningComposition",
    value:
    // ==============================
    // Composition Handlers
    // ==============================

    function startListeningComposition() {
      if (document && document.addEventListener) {
        document.addEventListener('compositionstart', this.onCompositionStart, false);
        document.addEventListener('compositionend', this.onCompositionEnd, false);
      }
    }
  }, {
    key: "stopListeningComposition",
    value: function stopListeningComposition() {
      if (document && document.removeEventListener) {
        document.removeEventListener('compositionstart', this.onCompositionStart);
        document.removeEventListener('compositionend', this.onCompositionEnd);
      }
    }
  }, {
    key: "startListeningToTouch",
    value:
    // ==============================
    // Touch Handlers
    // ==============================

    function startListeningToTouch() {
      if (document && document.addEventListener) {
        document.addEventListener('touchstart', this.onTouchStart, false);
        document.addEventListener('touchmove', this.onTouchMove, false);
        document.addEventListener('touchend', this.onTouchEnd, false);
      }
    }
  }, {
    key: "stopListeningToTouch",
    value: function stopListeningToTouch() {
      if (document && document.removeEventListener) {
        document.removeEventListener('touchstart', this.onTouchStart);
        document.removeEventListener('touchmove', this.onTouchMove);
        document.removeEventListener('touchend', this.onTouchEnd);
      }
    }
  }, {
    key: "renderInput",
    value:
    // ==============================
    // Renderers
    // ==============================
    function renderInput() {
      var _this$props8 = this.props,
        isDisabled = _this$props8.isDisabled,
        isSearchable = _this$props8.isSearchable,
        inputId = _this$props8.inputId,
        inputValue = _this$props8.inputValue,
        tabIndex = _this$props8.tabIndex,
        form = _this$props8.form,
        menuIsOpen = _this$props8.menuIsOpen,
        required = _this$props8.required;
      var _this$getComponents = this.getComponents(),
        Input = _this$getComponents.Input;
      var _this$state4 = this.state,
        inputIsHidden = _this$state4.inputIsHidden,
        ariaSelection = _this$state4.ariaSelection;
      var commonProps = this.commonProps;
      var id = inputId || this.getElementId('input');

      // aria attributes makes the JSX "noisy", separated for clarity
      var ariaAttributes = _objectSpread2(_objectSpread2(_objectSpread2({
        'aria-autocomplete': 'list',
        'aria-expanded': menuIsOpen,
        'aria-haspopup': true,
        'aria-errormessage': this.props['aria-errormessage'],
        'aria-invalid': this.props['aria-invalid'],
        'aria-label': this.props['aria-label'],
        'aria-labelledby': this.props['aria-labelledby'],
        'aria-required': required,
        role: 'combobox',
        'aria-activedescendant': this.isAppleDevice ? undefined : this.state.focusedOptionId || ''
      }, menuIsOpen && {
        'aria-controls': this.getElementId('listbox')
      }), !isSearchable && {
        'aria-readonly': true
      }), this.hasValue() ? (ariaSelection === null || ariaSelection === void 0 ? void 0 : ariaSelection.action) === 'initial-input-focus' && {
        'aria-describedby': this.getElementId('live-region')
      } : {
        'aria-describedby': this.getElementId('placeholder')
      });
      if (!isSearchable) {
        // use a dummy input to maintain focus/blur functionality
        return /*#__PURE__*/React.createElement(DummyInput, _extends({
          id: id,
          innerRef: this.getInputRef,
          onBlur: this.onInputBlur,
          onChange: noop,
          onFocus: this.onInputFocus,
          disabled: isDisabled,
          tabIndex: tabIndex,
          inputMode: "none",
          form: form,
          value: ""
        }, ariaAttributes));
      }
      return /*#__PURE__*/React.createElement(Input, _extends({}, commonProps, {
        autoCapitalize: "none",
        autoComplete: "off",
        autoCorrect: "off",
        id: id,
        innerRef: this.getInputRef,
        isDisabled: isDisabled,
        isHidden: inputIsHidden,
        onBlur: this.onInputBlur,
        onChange: this.handleInputChange,
        onFocus: this.onInputFocus,
        spellCheck: "false",
        tabIndex: tabIndex,
        form: form,
        type: "text",
        value: inputValue
      }, ariaAttributes));
    }
  }, {
    key: "renderPlaceholderOrValue",
    value: function renderPlaceholderOrValue() {
      var _this3 = this;
      var _this$getComponents2 = this.getComponents(),
        MultiValue = _this$getComponents2.MultiValue,
        MultiValueContainer = _this$getComponents2.MultiValueContainer,
        MultiValueLabel = _this$getComponents2.MultiValueLabel,
        MultiValueRemove = _this$getComponents2.MultiValueRemove,
        SingleValue = _this$getComponents2.SingleValue,
        Placeholder = _this$getComponents2.Placeholder;
      var commonProps = this.commonProps;
      var _this$props9 = this.props,
        controlShouldRenderValue = _this$props9.controlShouldRenderValue,
        isDisabled = _this$props9.isDisabled,
        isMulti = _this$props9.isMulti,
        inputValue = _this$props9.inputValue,
        placeholder = _this$props9.placeholder;
      var _this$state5 = this.state,
        selectValue = _this$state5.selectValue,
        focusedValue = _this$state5.focusedValue,
        isFocused = _this$state5.isFocused;
      if (!this.hasValue() || !controlShouldRenderValue) {
        return inputValue ? null : /*#__PURE__*/React.createElement(Placeholder, _extends({}, commonProps, {
          key: "placeholder",
          isDisabled: isDisabled,
          isFocused: isFocused,
          innerProps: {
            id: this.getElementId('placeholder')
          }
        }), placeholder);
      }
      if (isMulti) {
        return selectValue.map(function (opt, index) {
          var isOptionFocused = opt === focusedValue;
          var key = "".concat(_this3.getOptionLabel(opt), "-").concat(_this3.getOptionValue(opt));
          return /*#__PURE__*/React.createElement(MultiValue, _extends({}, commonProps, {
            components: {
              Container: MultiValueContainer,
              Label: MultiValueLabel,
              Remove: MultiValueRemove
            },
            isFocused: isOptionFocused,
            isDisabled: isDisabled,
            key: key,
            index: index,
            removeProps: {
              onClick: function onClick() {
                return _this3.removeValue(opt);
              },
              onTouchEnd: function onTouchEnd() {
                return _this3.removeValue(opt);
              },
              onMouseDown: function onMouseDown(e) {
                e.preventDefault();
              }
            },
            data: opt
          }), _this3.formatOptionLabel(opt, 'value'));
        });
      }
      if (inputValue) {
        return null;
      }
      var singleValue = selectValue[0];
      return /*#__PURE__*/React.createElement(SingleValue, _extends({}, commonProps, {
        data: singleValue,
        isDisabled: isDisabled
      }), this.formatOptionLabel(singleValue, 'value'));
    }
  }, {
    key: "renderClearIndicator",
    value: function renderClearIndicator() {
      var _this$getComponents3 = this.getComponents(),
        ClearIndicator = _this$getComponents3.ClearIndicator;
      var commonProps = this.commonProps;
      var _this$props10 = this.props,
        isDisabled = _this$props10.isDisabled,
        isLoading = _this$props10.isLoading;
      var isFocused = this.state.isFocused;
      if (!this.isClearable() || !ClearIndicator || isDisabled || !this.hasValue() || isLoading) {
        return null;
      }
      var innerProps = {
        onMouseDown: this.onClearIndicatorMouseDown,
        onTouchEnd: this.onClearIndicatorTouchEnd,
        'aria-hidden': 'true'
      };
      return /*#__PURE__*/React.createElement(ClearIndicator, _extends({}, commonProps, {
        innerProps: innerProps,
        isFocused: isFocused
      }));
    }
  }, {
    key: "renderLoadingIndicator",
    value: function renderLoadingIndicator() {
      var _this$getComponents4 = this.getComponents(),
        LoadingIndicator = _this$getComponents4.LoadingIndicator;
      var commonProps = this.commonProps;
      var _this$props11 = this.props,
        isDisabled = _this$props11.isDisabled,
        isLoading = _this$props11.isLoading;
      var isFocused = this.state.isFocused;
      if (!LoadingIndicator || !isLoading) return null;
      var innerProps = {
        'aria-hidden': 'true'
      };
      return /*#__PURE__*/React.createElement(LoadingIndicator, _extends({}, commonProps, {
        innerProps: innerProps,
        isDisabled: isDisabled,
        isFocused: isFocused
      }));
    }
  }, {
    key: "renderIndicatorSeparator",
    value: function renderIndicatorSeparator() {
      var _this$getComponents5 = this.getComponents(),
        DropdownIndicator = _this$getComponents5.DropdownIndicator,
        IndicatorSeparator = _this$getComponents5.IndicatorSeparator;

      // separator doesn't make sense without the dropdown indicator
      if (!DropdownIndicator || !IndicatorSeparator) return null;
      var commonProps = this.commonProps;
      var isDisabled = this.props.isDisabled;
      var isFocused = this.state.isFocused;
      return /*#__PURE__*/React.createElement(IndicatorSeparator, _extends({}, commonProps, {
        isDisabled: isDisabled,
        isFocused: isFocused
      }));
    }
  }, {
    key: "renderDropdownIndicator",
    value: function renderDropdownIndicator() {
      var _this$getComponents6 = this.getComponents(),
        DropdownIndicator = _this$getComponents6.DropdownIndicator;
      if (!DropdownIndicator) return null;
      var commonProps = this.commonProps;
      var isDisabled = this.props.isDisabled;
      var isFocused = this.state.isFocused;
      var innerProps = {
        onMouseDown: this.onDropdownIndicatorMouseDown,
        onTouchEnd: this.onDropdownIndicatorTouchEnd,
        'aria-hidden': 'true'
      };
      return /*#__PURE__*/React.createElement(DropdownIndicator, _extends({}, commonProps, {
        innerProps: innerProps,
        isDisabled: isDisabled,
        isFocused: isFocused
      }));
    }
  }, {
    key: "renderMenu",
    value: function renderMenu() {
      var _this4 = this;
      var _this$getComponents7 = this.getComponents(),
        Group = _this$getComponents7.Group,
        GroupHeading = _this$getComponents7.GroupHeading,
        Menu = _this$getComponents7.Menu,
        MenuList = _this$getComponents7.MenuList,
        MenuPortal = _this$getComponents7.MenuPortal,
        LoadingMessage = _this$getComponents7.LoadingMessage,
        NoOptionsMessage = _this$getComponents7.NoOptionsMessage,
        Option = _this$getComponents7.Option;
      var commonProps = this.commonProps;
      var focusedOption = this.state.focusedOption;
      var _this$props12 = this.props,
        captureMenuScroll = _this$props12.captureMenuScroll,
        inputValue = _this$props12.inputValue,
        isLoading = _this$props12.isLoading,
        loadingMessage = _this$props12.loadingMessage,
        minMenuHeight = _this$props12.minMenuHeight,
        maxMenuHeight = _this$props12.maxMenuHeight,
        menuIsOpen = _this$props12.menuIsOpen,
        menuPlacement = _this$props12.menuPlacement,
        menuPosition = _this$props12.menuPosition,
        menuPortalTarget = _this$props12.menuPortalTarget,
        menuShouldBlockScroll = _this$props12.menuShouldBlockScroll,
        menuShouldScrollIntoView = _this$props12.menuShouldScrollIntoView,
        noOptionsMessage = _this$props12.noOptionsMessage,
        onMenuScrollToTop = _this$props12.onMenuScrollToTop,
        onMenuScrollToBottom = _this$props12.onMenuScrollToBottom;
      if (!menuIsOpen) return null;

      // TODO: Internal Option Type here
      var render = function render(props, id) {
        var type = props.type,
          data = props.data,
          isDisabled = props.isDisabled,
          isSelected = props.isSelected,
          label = props.label,
          value = props.value;
        var isFocused = focusedOption === data;
        var onHover = isDisabled ? undefined : function () {
          return _this4.onOptionHover(data);
        };
        var onSelect = isDisabled ? undefined : function () {
          return _this4.selectOption(data);
        };
        var optionId = "".concat(_this4.getElementId('option'), "-").concat(id);
        var innerProps = {
          id: optionId,
          onClick: onSelect,
          onMouseMove: onHover,
          onMouseOver: onHover,
          tabIndex: -1,
          role: 'option',
          'aria-selected': _this4.isAppleDevice ? undefined : isSelected // is not supported on Apple devices
        };

        return /*#__PURE__*/React.createElement(Option, _extends({}, commonProps, {
          innerProps: innerProps,
          data: data,
          isDisabled: isDisabled,
          isSelected: isSelected,
          key: optionId,
          label: label,
          type: type,
          value: value,
          isFocused: isFocused,
          innerRef: isFocused ? _this4.getFocusedOptionRef : undefined
        }), _this4.formatOptionLabel(props.data, 'menu'));
      };
      var menuUI;
      if (this.hasOptions()) {
        menuUI = this.getCategorizedOptions().map(function (item) {
          if (item.type === 'group') {
            var _data = item.data,
              options = item.options,
              groupIndex = item.index;
            var groupId = "".concat(_this4.getElementId('group'), "-").concat(groupIndex);
            var headingId = "".concat(groupId, "-heading");
            return /*#__PURE__*/React.createElement(Group, _extends({}, commonProps, {
              key: groupId,
              data: _data,
              options: options,
              Heading: GroupHeading,
              headingProps: {
                id: headingId,
                data: item.data
              },
              label: _this4.formatGroupLabel(item.data)
            }), item.options.map(function (option) {
              return render(option, "".concat(groupIndex, "-").concat(option.index));
            }));
          } else if (item.type === 'option') {
            return render(item, "".concat(item.index));
          }
        });
      } else if (isLoading) {
        var message = loadingMessage({
          inputValue: inputValue
        });
        if (message === null) return null;
        menuUI = /*#__PURE__*/React.createElement(LoadingMessage, commonProps, message);
      } else {
        var _message = noOptionsMessage({
          inputValue: inputValue
        });
        if (_message === null) return null;
        menuUI = /*#__PURE__*/React.createElement(NoOptionsMessage, commonProps, _message);
      }
      var menuPlacementProps = {
        minMenuHeight: minMenuHeight,
        maxMenuHeight: maxMenuHeight,
        menuPlacement: menuPlacement,
        menuPosition: menuPosition,
        menuShouldScrollIntoView: menuShouldScrollIntoView
      };
      var menuElement = /*#__PURE__*/React.createElement(MenuPlacer, _extends({}, commonProps, menuPlacementProps), function (_ref4) {
        var ref = _ref4.ref,
          _ref4$placerProps = _ref4.placerProps,
          placement = _ref4$placerProps.placement,
          maxHeight = _ref4$placerProps.maxHeight;
        return /*#__PURE__*/React.createElement(Menu, _extends({}, commonProps, menuPlacementProps, {
          innerRef: ref,
          innerProps: {
            onMouseDown: _this4.onMenuMouseDown,
            onMouseMove: _this4.onMenuMouseMove
          },
          isLoading: isLoading,
          placement: placement
        }), /*#__PURE__*/React.createElement(ScrollManager, {
          captureEnabled: captureMenuScroll,
          onTopArrive: onMenuScrollToTop,
          onBottomArrive: onMenuScrollToBottom,
          lockEnabled: menuShouldBlockScroll
        }, function (scrollTargetRef) {
          return /*#__PURE__*/React.createElement(MenuList, _extends({}, commonProps, {
            innerRef: function innerRef(instance) {
              _this4.getMenuListRef(instance);
              scrollTargetRef(instance);
            },
            innerProps: {
              role: 'listbox',
              'aria-multiselectable': commonProps.isMulti,
              id: _this4.getElementId('listbox')
            },
            isLoading: isLoading,
            maxHeight: maxHeight,
            focusedOption: focusedOption
          }), menuUI);
        }));
      });

      // positioning behaviour is almost identical for portalled and fixed,
      // so we use the same component. the actual portalling logic is forked
      // within the component based on `menuPosition`
      return menuPortalTarget || menuPosition === 'fixed' ? /*#__PURE__*/React.createElement(MenuPortal, _extends({}, commonProps, {
        appendTo: menuPortalTarget,
        controlElement: this.controlRef,
        menuPlacement: menuPlacement,
        menuPosition: menuPosition
      }), menuElement) : menuElement;
    }
  }, {
    key: "renderFormField",
    value: function renderFormField() {
      var _this5 = this;
      var _this$props13 = this.props,
        delimiter = _this$props13.delimiter,
        isDisabled = _this$props13.isDisabled,
        isMulti = _this$props13.isMulti,
        name = _this$props13.name,
        required = _this$props13.required;
      var selectValue = this.state.selectValue;
      if (required && !this.hasValue() && !isDisabled) {
        return /*#__PURE__*/React.createElement(RequiredInput$1, {
          name: name,
          onFocus: this.onValueInputFocus
        });
      }
      if (!name || isDisabled) return;
      if (isMulti) {
        if (delimiter) {
          var value = selectValue.map(function (opt) {
            return _this5.getOptionValue(opt);
          }).join(delimiter);
          return /*#__PURE__*/React.createElement("input", {
            name: name,
            type: "hidden",
            value: value
          });
        } else {
          var input = selectValue.length > 0 ? selectValue.map(function (opt, i) {
            return /*#__PURE__*/React.createElement("input", {
              key: "i-".concat(i),
              name: name,
              type: "hidden",
              value: _this5.getOptionValue(opt)
            });
          }) : /*#__PURE__*/React.createElement("input", {
            name: name,
            type: "hidden",
            value: ""
          });
          return /*#__PURE__*/React.createElement("div", null, input);
        }
      } else {
        var _value = selectValue[0] ? this.getOptionValue(selectValue[0]) : '';
        return /*#__PURE__*/React.createElement("input", {
          name: name,
          type: "hidden",
          value: _value
        });
      }
    }
  }, {
    key: "renderLiveRegion",
    value: function renderLiveRegion() {
      var commonProps = this.commonProps;
      var _this$state6 = this.state,
        ariaSelection = _this$state6.ariaSelection,
        focusedOption = _this$state6.focusedOption,
        focusedValue = _this$state6.focusedValue,
        isFocused = _this$state6.isFocused,
        selectValue = _this$state6.selectValue;
      var focusableOptions = this.getFocusableOptions();
      return /*#__PURE__*/React.createElement(LiveRegion$1, _extends({}, commonProps, {
        id: this.getElementId('live-region'),
        ariaSelection: ariaSelection,
        focusedOption: focusedOption,
        focusedValue: focusedValue,
        isFocused: isFocused,
        selectValue: selectValue,
        focusableOptions: focusableOptions,
        isAppleDevice: this.isAppleDevice
      }));
    }
  }, {
    key: "render",
    value: function render() {
      var _this$getComponents8 = this.getComponents(),
        Control = _this$getComponents8.Control,
        IndicatorsContainer = _this$getComponents8.IndicatorsContainer,
        SelectContainer = _this$getComponents8.SelectContainer,
        ValueContainer = _this$getComponents8.ValueContainer;
      var _this$props14 = this.props,
        className = _this$props14.className,
        id = _this$props14.id,
        isDisabled = _this$props14.isDisabled,
        menuIsOpen = _this$props14.menuIsOpen;
      var isFocused = this.state.isFocused;
      var commonProps = this.commonProps = this.getCommonProps();
      return /*#__PURE__*/React.createElement(SelectContainer, _extends({}, commonProps, {
        className: className,
        innerProps: {
          id: id,
          onKeyDown: this.onKeyDown
        },
        isDisabled: isDisabled,
        isFocused: isFocused
      }), this.renderLiveRegion(), /*#__PURE__*/React.createElement(Control, _extends({}, commonProps, {
        innerRef: this.getControlRef,
        innerProps: {
          onMouseDown: this.onControlMouseDown,
          onTouchEnd: this.onControlTouchEnd
        },
        isDisabled: isDisabled,
        isFocused: isFocused,
        menuIsOpen: menuIsOpen
      }), /*#__PURE__*/React.createElement(ValueContainer, _extends({}, commonProps, {
        isDisabled: isDisabled
      }), this.renderPlaceholderOrValue(), this.renderInput()), /*#__PURE__*/React.createElement(IndicatorsContainer, _extends({}, commonProps, {
        isDisabled: isDisabled
      }), this.renderClearIndicator(), this.renderLoadingIndicator(), this.renderIndicatorSeparator(), this.renderDropdownIndicator())), this.renderMenu(), this.renderFormField());
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      var prevProps = state.prevProps,
        clearFocusValueOnUpdate = state.clearFocusValueOnUpdate,
        inputIsHiddenAfterUpdate = state.inputIsHiddenAfterUpdate,
        ariaSelection = state.ariaSelection,
        isFocused = state.isFocused,
        prevWasFocused = state.prevWasFocused,
        instancePrefix = state.instancePrefix;
      var options = props.options,
        value = props.value,
        menuIsOpen = props.menuIsOpen,
        inputValue = props.inputValue,
        isMulti = props.isMulti;
      var selectValue = cleanValue(value);
      var newMenuOptionsState = {};
      if (prevProps && (value !== prevProps.value || options !== prevProps.options || menuIsOpen !== prevProps.menuIsOpen || inputValue !== prevProps.inputValue)) {
        var focusableOptions = menuIsOpen ? buildFocusableOptions(props, selectValue) : [];
        var focusableOptionsWithIds = menuIsOpen ? buildFocusableOptionsWithIds(buildCategorizedOptions(props, selectValue), "".concat(instancePrefix, "-option")) : [];
        var focusedValue = clearFocusValueOnUpdate ? getNextFocusedValue(state, selectValue) : null;
        var focusedOption = getNextFocusedOption(state, focusableOptions);
        var focusedOptionId = getFocusedOptionId(focusableOptionsWithIds, focusedOption);
        newMenuOptionsState = {
          selectValue: selectValue,
          focusedOption: focusedOption,
          focusedOptionId: focusedOptionId,
          focusableOptionsWithIds: focusableOptionsWithIds,
          focusedValue: focusedValue,
          clearFocusValueOnUpdate: false
        };
      }
      // some updates should toggle the state of the input visibility
      var newInputIsHiddenState = inputIsHiddenAfterUpdate != null && props !== prevProps ? {
        inputIsHidden: inputIsHiddenAfterUpdate,
        inputIsHiddenAfterUpdate: undefined
      } : {};
      var newAriaSelection = ariaSelection;
      var hasKeptFocus = isFocused && prevWasFocused;
      if (isFocused && !hasKeptFocus) {
        // If `value` or `defaultValue` props are not empty then announce them
        // when the Select is initially focused
        newAriaSelection = {
          value: valueTernary(isMulti, selectValue, selectValue[0] || null),
          options: selectValue,
          action: 'initial-input-focus'
        };
        hasKeptFocus = !prevWasFocused;
      }

      // If the 'initial-input-focus' action has been set already
      // then reset the ariaSelection to null
      if ((ariaSelection === null || ariaSelection === void 0 ? void 0 : ariaSelection.action) === 'initial-input-focus') {
        newAriaSelection = null;
      }
      return _objectSpread2(_objectSpread2(_objectSpread2({}, newMenuOptionsState), newInputIsHiddenState), {}, {
        prevProps: props,
        ariaSelection: newAriaSelection,
        prevWasFocused: hasKeptFocus
      });
    }
  }]);
  return Select;
}(Component);
Select$2.defaultProps = defaultProps;

var StateManagedSelect = /*#__PURE__*/forwardRef(function (props, ref) {
  var baseSelectProps = useStateManager(props);
  return /*#__PURE__*/React.createElement(Select$2, _extends({
    ref: ref
  }, baseSelectProps));
});
var StateManagedSelect$1 = StateManagedSelect;

var css_248z$O = "/*\n  There are multiple ways to style the react-select field.\n  See https://react-select.com/styles\n\n  Currently we use the CSS attribute selectors $= to get the\n  react select sub-components we want to style.\n  The $='' attribute selector can be used since all react-select\n  components are named in the same way (xyz-componentName).\n  This allows us to still have the CSS styles in our css-modules\n  files and there is no need to have CSS-in-JS styling.\n\n  This might be subject of change.\n*/\n.ColorPicker-module_color-picker__URWZW {\n  width: 100%;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-control'] {\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  height: 100%;\n  min-height: 100%;\n  box-shadow: none;\n  cursor: pointer;\n  transition:\n    all 0.3s ease,\n    border-radius 0;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH {\n  height: var(--input-height-mobile);\n}\n\n@media (width >= 580px) {\n  .ColorPicker-module_color-picker-wrapper__anpSH {\n    height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n/* hovered */\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-control']:hover {\n  border-color: var(--input-border-color-hover);\n}\n\n/* focused */\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-control']:focus-within {\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-ValueContainer'] {\n  padding: 0 8px;\n  caret-color: transparent;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-indicatorSeparator'] {\n  display: none;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-indicatorContainer'] {\n  justify-content: center;\n  align-items: center;\n  padding: 0 8px;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-menu'] {\n  width: 100%;\n  min-width: 200px;\n  font-size: var(--font-size-s);\n  margin: 0;\n  box-shadow: var(--dropdown-box-shadow);\n  background-color: var(--input-bg-color-default);\n  border-radius: 0 0 var(--input-border-radius) var(--input-border-radius);\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-MenuList'] {\n  overflow-y: auto;\n  overflow-x: hidden;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-option'] {\n  background-color: transparent;\n  width: fit-content;\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH.ColorPicker-module_filled__AsovY [class$='-control'] {\n  color: var(--input-text-color-filled);\n  border-color: var(--input-border-color-filled);\n  background-color: var(--input-bg-color-filled);\n}\n\n/* read-only */\n.ColorPicker-module_color-picker-wrapper__anpSH.ColorPicker-module_read-only__omqHL [class$='-control'] {\n  color: var(--input-text-color-locked);\n  border-color: var(--input-border-color-locked);\n  background-color: var(--input-bg-color-locked);\n}\n\n/* disabled */\n.ColorPicker-module_color-picker-wrapper__anpSH.ColorPicker-module_disabled__U1Tx- [class$='-control'] {\n  pointer-events: initial;\n  cursor: not-allowed;\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-text-color-disabled);\n}\n\n/* error */\n.ColorPicker-module_color-picker-wrapper__anpSH.ColorPicker-module_error__FDs2Y [class$='-control'] {\n  border-color: var(--input-text-color-error);\n}\n\n.ColorPicker-module_color-picker-wrapper__anpSH [class$='-Group']:not(:last-child) {\n  border-bottom: 1px solid var(--color-grey-lighter);\n  padding-bottom: 8px;\n}\n\n.ColorPicker-module_error-message__8nfA- {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n";
var styles$M = {"color-picker":"ColorPicker-module_color-picker__URWZW","color-picker-wrapper":"ColorPicker-module_color-picker-wrapper__anpSH","filled":"ColorPicker-module_filled__AsovY","read-only":"ColorPicker-module_read-only__omqHL","disabled":"ColorPicker-module_disabled__U1Tx-","error":"ColorPicker-module_error__FDs2Y","error-message":"ColorPicker-module_error-message__8nfA-"};
styleInject(css_248z$O);

var css_248z$N = ".ColorPickerOption-module_color__XhvbS {\n  width: 32px;\n  height: 32px;\n  border-radius: 32px;\n  cursor: pointer;\n}.ColorPickerOption-module_color__XhvbS svg {\n  display: none;\n  width: 100%;\n  height: 100%;\n  color: var(--color-grey-darker);\n}.ColorPickerOption-module_color__XhvbS.ColorPickerOption-module_selected__kK3Ux {\n  border: 4px solid var(--color-white);\n  box-shadow: 0 0 0 2px var(--color-grey-darkest);\n}.ColorPickerOption-module_color__XhvbS:hover {\n  opacity: 0.7;\n}\n\n/* stylelint-disable-next-line at-rule-no-unknown */\n\n.ColorPickerGroup-module_group__5RoPF {\n  display: flex;\n  flex-wrap: wrap;\n}\n\n.ColorPickerGroup-module_group__5RoPF.ColorPickerGroup-module_used__dKMH8 .i__const_color_0 svg {\n  display: block;\n}\n";
var styles$L = {"group":"ColorPickerGroup-module_group__5RoPF","used":"ColorPickerGroup-module_used__dKMH8"};
styleInject(css_248z$N);

var ColorPickerGroup$1 = function (props) {
    var _a;
    return (jsx$1(components.Group, __assign({}, props, { children: jsx$1("div", { className: classNames$1(styles$L.group, (_a = {},
                _a[styles$L['used']] = props.data.isUsed,
                _a)), children: props.children }) })));
};

var ColorPickerMenuList = function (props) {
    return (jsx$1(components.MenuList, __assign({}, props, { children: jsx$1("div", { children: props.children }) })));
};

var css_248z$M = ".ColorPickerOption-module_color__XhvbS {\n  width: 32px;\n  height: 32px;\n  border-radius: 32px;\n  cursor: pointer;\n}\n\n.ColorPickerOption-module_color__XhvbS svg {\n  display: none;\n  width: 100%;\n  height: 100%;\n  color: var(--color-grey-darker);\n}\n\n.ColorPickerOption-module_color__XhvbS.ColorPickerOption-module_selected__kK3Ux {\n  border: 4px solid var(--color-white);\n  box-shadow: 0 0 0 2px var(--color-grey-darkest);\n}\n\n.ColorPickerOption-module_color__XhvbS:hover {\n  opacity: 0.7;\n}\n";
var styles$K = {"color":"ColorPickerOption-module_color__XhvbS","selected":"ColorPickerOption-module_selected__kK3Ux"};
styleInject(css_248z$M);

var ColorPickerOption = function (props) {
    var _a;
    var usedBy = props.data.usedBy
        ? ':' + props.data.usedBy.reduce(function (a, b) { return a + "<p>".concat(b, "</p>"); }, '')
        : '';
    return (jsx$1(components.Option, __assign({}, props, { children: jsx$1("div", { children: jsx$1(Tooltip$1, { content: React__default.createElement('div', {
                    dangerouslySetInnerHTML: {
                        __html: "".concat(props.data.label).concat(usedBy),
                    },
                }), children: jsx$1("div", { className: classNames$1(styles$K.color, (_a = {},
                        _a[styles$K.selected] = props.isSelected,
                        _a)), style: {
                        backgroundColor: props.data.html,
                    }, children: jsx$1(SvgCloseIcon, {}) }) }) }) })));
};

var css_248z$L = ".ColorPickerSingleValue-module_wrapper__VAOZP {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n}\n\n.ColorPickerSingleValue-module_color__Az4rB {\n  width: 16px;\n  height: 16px;\n  border-radius: 50%;\n  flex-basis: 16px;\n  padding-right: 14px;\n}\n";
var styles$J = {"wrapper":"ColorPickerSingleValue-module_wrapper__VAOZP","color":"ColorPickerSingleValue-module_color__Az4rB"};
styleInject(css_248z$L);

var ColorPickerSingleValue = function (props) {
    return (jsx$1(components.SingleValue, __assign({}, props, { children: jsxs("div", { className: styles$J.wrapper, children: [jsx$1("div", { className: styles$J.color, style: {
                        backgroundColor: props.data.html,
                        border: '1px solid #8e949f',
                    } }), props.children] }) })));
};

var css_248z$K = ".InputHeader-module_input-header__-iHdx {\n  display: flex;\n  align-items: center;\n  height: 16px;\n  line-height: 130%;\n  font-size: var(--input-label-font-size);\n  color: var(--input-label-color-text);\n  margin-bottom: var(--input-header-gap);\n  user-select: none;\n}\n\n.InputHeader-module_input-header__-iHdx label {\n  margin-right: 6px;\n  white-space: nowrap;\n}\n\n.InputHeader-module_input-header__-iHdx:not(.InputHeader-module_overflow__8krMA) label {\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.InputHeader-module_tooltip-icon__z1crl {\n  cursor: pointer;\n  width: calc(1rem + 4px);\n  height: calc(1rem + 4px);\n}\n\n.InputHeader-module_input-header__-iHdx svg.InputHeader-module_tooltip-icon__z1crl {\n  color: var(--input-label-color-text);\n}\n\n.InputHeader-module_input-header__-iHdx.InputHeader-module_disabled__-Gs4s {\n  color: var(--input-label-color-text-disabled);\n}\n\n.InputHeader-module_input-header__-iHdx.InputHeader-module_disabled__-Gs4s svg.InputHeader-module_tooltip-icon__z1crl {\n  color: var(--input-label-color-text-disabled);\n}\n\n.InputHeader-module_input-header__-iHdx.InputHeader-module_big-gap__LgkO9 {\n  margin-bottom: var(--input-header-gap-big);\n}\n";
var styles$I = {"input-header":"InputHeader-module_input-header__-iHdx","overflow":"InputHeader-module_overflow__8krMA","tooltip-icon":"InputHeader-module_tooltip-icon__z1crl","disabled":"InputHeader-module_disabled__-Gs4s","big-gap":"InputHeader-module_big-gap__LgkO9"};
styleInject(css_248z$K);

var InputHeader = function (_a, ref) {
    var _b;
    var name = _a.name, label = _a.label, disabled = _a.disabled, canOverflow = _a.canOverflow, tooltipText = _a.tooltipText, _c = _a.bigGap, bigGap = _c === void 0 ? false : _c;
    var wrapperClasses = classNames$1(styles$I['input-header'], (_b = {},
        _b[styles$I['overflow']] = canOverflow,
        _b[styles$I['disabled']] = disabled,
        _b[styles$I['big-gap']] = bigGap,
        _b));
    return (jsxs("div", { ref: ref, className: wrapperClasses, children: [label && jsx$1("label", { htmlFor: name !== null && name !== void 0 ? name : undefined, children: label }), tooltipText && (jsx$1(Tooltip$1, { content: tooltipText, children: jsx$1(SvgInfoIcon, { className: styles$I['tooltip-icon'] }) }))] }));
};
var InputHeader$1 = forwardRef(InputHeader);

/**
 *
 * Form input: ColorPicker
 *
 * Select a color from predefined Colors. Already used colors
 * can be passed in as a group.
 *
 * Based on React-Select (https://react-select.com/)
 *
 *
 * @param {string} name
 * @param {Array<GroupedColorOption>} options
 * @param {string} placement
 * @param {string|number} menuWidth
 * @param {number} minMenuHeight
 * @param {number} maxMenuHeight
 * @param {boolean} isClearable
 * @param {boolean} isSearchable
 * @param {ColorOption} defaultValue
 * @param {boolean} disabled
 * @param {string} label
 * @param {string} placeholder
 * @param {string} className
 * @param {string} error
 * @param {boolean} autoFocus
 * @param {string} tooltipText
 * @param {function} onChange
 * @param {function} onBlur
 * @param {function} onFocus
 * @param ref
 */
var ColorPicker = function (_a, ref) {
    var _b;
    var name = _a.name, options = _a.options, _c = _a.placement, placement = _c === void 0 ? 'bottom' : _c, _d = _a.menuWidth, menuWidth = _d === void 0 ? 'auto' : _d, _e = _a.minMenuHeight, minMenuHeight = _e === void 0 ? 100 : _e, _f = _a.maxMenuHeight, maxMenuHeight = _f === void 0 ? 200 : _f, _g = _a.menuPosition, menuPosition = _g === void 0 ? 'absolute' : _g, _h = _a.isClearable, isClearable = _h === void 0 ? false : _h, _j = _a.labelCanOverflow, labelCanOverflow = _j === void 0 ? false : _j, _k = _a.bigHeaderGap, bigHeaderGap = _k === void 0 ? false : _k, isSearchable = _a.isSearchable, defaultValue = _a.defaultValue, disabled = _a.disabled, readonly = _a.readonly, label = _a.label, placeholder = _a.placeholder, dense = _a.dense, className = _a.className, error = _a.error, autoFocus = _a.autoFocus, tooltipText = _a.tooltipText, _l = _a.tabIndex, tabIndex = _l === void 0 ? 0 : _l, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus;
    var _m = useState(defaultValue), value = _m[0], setValue = _m[1];
    var inputClass = classNames$1(styles$M['color-picker-wrapper'], (_b = {},
        _b[styles$M['error']] = error,
        _b[styles$M['disabled']] = disabled,
        _b[styles$M['read-only']] = readonly,
        _b[styles$M['filled']] = !!value,
        _b));
    var customStyles = {
        menu: function (provided) { return (__assign(__assign({}, provided), { width: menuWidth })); },
    };
    /**
     *
     * @param {SingleValue<ColorOption>} option
     *
     * Event handler that fires when ReactColorPicker onChange event
     * is invoked.
     * Sets the internal state and update onChange prop
     */
    function handleChange(option) {
        setValue(option);
        onChange(option);
    }
    var wrapperClasses = classNames$1(styles$M['color-picker'], className);
    return (jsxs("div", { ref: ref, className: wrapperClasses, children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), jsx$1(StateManagedSelect$1, { menuPosition: menuPosition, minMenuHeight: minMenuHeight, maxMenuHeight: maxMenuHeight, menuPlacement: placement, styles: customStyles, components: {
                    MenuList: ColorPickerMenuList,
                    Group: ColorPickerGroup$1,
                    Option: ColorPickerOption,
                    SingleValue: ColorPickerSingleValue,
                }, isClearable: isClearable, isSearchable: isSearchable, className: inputClass, options: options, isMulti: false, isDisabled: disabled || readonly, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '', blurInputOnSelect: true, onChange: function (option) { return handleChange(option); }, onBlur: onBlur, onFocus: onFocus, value: value, autoFocus: autoFocus, tabIndex: tabIndex }), jsx$1("div", { className: styles$M['error-message'], children: error })] }));
};
var ColorPicker$1 = forwardRef(ColorPicker);

function buildFormatLongFn$1(args) {
  return (options = {}) => {
    // TODO: Remove String()
    const width = options.width ? String(options.width) : args.defaultWidth;
    const format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

/**
 * The localize function argument callback which allows to convert raw value to
 * the actual type.
 *
 * @param value - The value to convert
 *
 * @returns The converted value
 */

/**
 * The map of localized values for each width.
 */

/**
 * The index type of the locale unit value. It types conversion of units of
 * values that don't start at 0 (i.e. quarters).
 */

/**
 * Converts the unit value to the tuple of values.
 */

/**
 * The tuple of localized era values. The first element represents BC,
 * the second element represents AD.
 */

/**
 * The tuple of localized quarter values. The first element represents Q1.
 */

/**
 * The tuple of localized day values. The first element represents Sunday.
 */

/**
 * The tuple of localized month values. The first element represents January.
 */

function buildLocalizeFn$1(args) {
  return (value, options) => {
    const context = options?.context ? String(options.context) : "standalone";

    let valuesArray;
    if (context === "formatting" && args.formattingValues) {
      const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      const width = options?.width ? String(options.width) : defaultWidth;

      valuesArray =
        args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      const defaultWidth = args.defaultWidth;
      const width = options?.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    const index = args.argumentCallback ? args.argumentCallback(value) : value;

    // @ts-expect-error - For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

function buildMatchFn$1(args) {
  return (string, options = {}) => {
    const width = options.width;

    const matchPattern =
      (width && args.matchPatterns[width]) ||
      args.matchPatterns[args.defaultMatchWidth];
    const matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }
    const matchedString = matchResult[0];

    const parsePatterns =
      (width && args.parsePatterns[width]) ||
      args.parsePatterns[args.defaultParseWidth];

    const key = Array.isArray(parsePatterns)
      ? findIndex$1(parsePatterns, (pattern) => pattern.test(matchedString))
      : // [TODO] -- I challenge you to fix the type
        findKey$1(parsePatterns, (pattern) => pattern.test(matchedString));

    let value;

    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback
      ? // [TODO] -- I challenge you to fix the type
        options.valueCallback(value)
      : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

function findKey$1(object, predicate) {
  for (const key in object) {
    if (
      Object.prototype.hasOwnProperty.call(object, key) &&
      predicate(object[key])
    ) {
      return key;
    }
  }
  return undefined;
}

function findIndex$1(array, predicate) {
  for (let key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn$1(args) {
  return (string, options = {}) => {
    const matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    const matchedString = matchResult[0];

    const parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    let value = args.valueCallback
      ? args.valueCallback(parseResult[0])
      : parseResult[0];

    // [TODO] I challenge you to fix the type
    value = options.valueCallback ? options.valueCallback(value) : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

/**
 * @module constants
 * @summary Useful constants
 * @description
 * Collection of useful date constants.
 *
 * The constants could be imported from `date-fns/constants`:
 *
 * ```ts
 * import { maxTime, minTime } from "./constants/date-fns/constants";
 *
 * function isAllowedTime(time) {
 *   return time <= maxTime && time >= minTime;
 * }
 * ```
 */


/**
 * @constant
 * @name millisecondsInWeek
 * @summary Milliseconds in 1 week.
 */
const millisecondsInWeek$1 = 604800000;

/**
 * @constant
 * @name millisecondsInDay
 * @summary Milliseconds in 1 day.
 */
const millisecondsInDay$1 = 86400000;

/**
 * @constant
 * @name constructFromSymbol
 * @summary Symbol enabling Date extensions to inherit properties from the reference date.
 *
 * The symbol is used to enable the `constructFrom` function to construct a date
 * using a reference date and a value. It allows to transfer extra properties
 * from the reference date to the new date. It's useful for extensions like
 * [`TZDate`](https://github.com/date-fns/tz) that accept a time zone as
 * a constructor argument.
 */
const constructFromSymbol = Symbol.for("constructDateFrom");

/**
 * @name constructFrom
 * @category Generic Helpers
 * @summary Constructs a date using the reference date and the value
 *
 * @description
 * The function constructs a new date using the constructor from the reference
 * date and the given value. It helps to build generic functions that accept
 * date extensions.
 *
 * It defaults to `Date` if the passed reference date is a number or a string.
 *
 * Starting from v3.7.0, it allows to construct a date using `[Symbol.for("constructDateFrom")]`
 * enabling to transfer extra properties from the reference date to the new date.
 * It's useful for extensions like [`TZDate`](https://github.com/date-fns/tz)
 * that accept a time zone as a constructor argument.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The reference date to take constructor from
 * @param value - The value to create the date
 *
 * @returns Date initialized using the given date and value
 *
 * @example
 * import { constructFrom } from "./constructFrom/date-fns";
 *
 * // A function that clones a date preserving the original type
 * function cloneDate<DateType extends Date>(date: DateType): DateType {
 *   return constructFrom(
 *     date, // Use constructor from the given date
 *     date.getTime() // Use the date value to create a new date
 *   );
 * }
 */
function constructFrom$1(date, value) {
  if (typeof date === "function") return date(value);

  if (date && typeof date === "object" && constructFromSymbol in date)
    return date[constructFromSymbol](value);

  if (date instanceof Date) return new date.constructor(value);

  return new Date(value);
}

function normalizeDates(context, ...dates) {
  const normalize = constructFrom$1.bind(
    null,
    dates.find((date) => typeof date === "object"),
  );
  return dates.map(normalize);
}

let defaultOptions$1 = {};

function getDefaultOptions$2() {
  return defaultOptions$1;
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * Starting from v3.7.0, it clones a date using `[Symbol.for("constructDateFrom")]`
 * enabling to transfer extra properties from the reference date to the new date.
 * It's useful for extensions like [`TZDate`](https://github.com/date-fns/tz)
 * that accept a time zone as a constructor argument.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param argument - The value to convert
 *
 * @returns The parsed date in the local time zone
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate$1(argument, context) {
  // [TODO] Get rid of `toDate` or `constructFrom`?
  return constructFrom$1(context || argument, argument);
}

/**
 * The {@link startOfWeek} function options.
 */

/**
 * @name startOfWeek
 * @category Week Helpers
 * @summary Return the start of a week for the given date.
 *
 * @description
 * Return the start of a week for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week
 *
 * @example
 * // The start of a week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfWeek$1(date, options) {
  const defaultOptions = getDefaultOptions$2();
  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate$1(date, options?.in);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  _date.setDate(_date.getDate() - diff);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

const formatDistanceLocale$2 = {
  lessThanXSeconds: {
    standalone: {
      one: "weniger als 1 Sekunde",
      other: "weniger als {{count}} Sekunden",
    },
    withPreposition: {
      one: "weniger als 1 Sekunde",
      other: "weniger als {{count}} Sekunden",
    },
  },

  xSeconds: {
    standalone: {
      one: "1 Sekunde",
      other: "{{count}} Sekunden",
    },
    withPreposition: {
      one: "1 Sekunde",
      other: "{{count}} Sekunden",
    },
  },

  halfAMinute: {
    standalone: "eine halbe Minute",
    withPreposition: "einer halben Minute",
  },

  lessThanXMinutes: {
    standalone: {
      one: "weniger als 1 Minute",
      other: "weniger als {{count}} Minuten",
    },
    withPreposition: {
      one: "weniger als 1 Minute",
      other: "weniger als {{count}} Minuten",
    },
  },

  xMinutes: {
    standalone: {
      one: "1 Minute",
      other: "{{count}} Minuten",
    },
    withPreposition: {
      one: "1 Minute",
      other: "{{count}} Minuten",
    },
  },

  aboutXHours: {
    standalone: {
      one: "etwa 1 Stunde",
      other: "etwa {{count}} Stunden",
    },
    withPreposition: {
      one: "etwa 1 Stunde",
      other: "etwa {{count}} Stunden",
    },
  },

  xHours: {
    standalone: {
      one: "1 Stunde",
      other: "{{count}} Stunden",
    },
    withPreposition: {
      one: "1 Stunde",
      other: "{{count}} Stunden",
    },
  },

  xDays: {
    standalone: {
      one: "1 Tag",
      other: "{{count}} Tage",
    },
    withPreposition: {
      one: "1 Tag",
      other: "{{count}} Tagen",
    },
  },

  aboutXWeeks: {
    standalone: {
      one: "etwa 1 Woche",
      other: "etwa {{count}} Wochen",
    },
    withPreposition: {
      one: "etwa 1 Woche",
      other: "etwa {{count}} Wochen",
    },
  },

  xWeeks: {
    standalone: {
      one: "1 Woche",
      other: "{{count}} Wochen",
    },
    withPreposition: {
      one: "1 Woche",
      other: "{{count}} Wochen",
    },
  },

  aboutXMonths: {
    standalone: {
      one: "etwa 1 Monat",
      other: "etwa {{count}} Monate",
    },
    withPreposition: {
      one: "etwa 1 Monat",
      other: "etwa {{count}} Monaten",
    },
  },

  xMonths: {
    standalone: {
      one: "1 Monat",
      other: "{{count}} Monate",
    },
    withPreposition: {
      one: "1 Monat",
      other: "{{count}} Monaten",
    },
  },

  aboutXYears: {
    standalone: {
      one: "etwa 1 Jahr",
      other: "etwa {{count}} Jahre",
    },
    withPreposition: {
      one: "etwa 1 Jahr",
      other: "etwa {{count}} Jahren",
    },
  },

  xYears: {
    standalone: {
      one: "1 Jahr",
      other: "{{count}} Jahre",
    },
    withPreposition: {
      one: "1 Jahr",
      other: "{{count}} Jahren",
    },
  },

  overXYears: {
    standalone: {
      one: "mehr als 1 Jahr",
      other: "mehr als {{count}} Jahre",
    },
    withPreposition: {
      one: "mehr als 1 Jahr",
      other: "mehr als {{count}} Jahren",
    },
  },

  almostXYears: {
    standalone: {
      one: "fast 1 Jahr",
      other: "fast {{count}} Jahre",
    },
    withPreposition: {
      one: "fast 1 Jahr",
      other: "fast {{count}} Jahren",
    },
  },
};

const formatDistance$2 = (token, count, options) => {
  let result;

  const tokenValue = options?.addSuffix
    ? formatDistanceLocale$2[token].withPreposition
    : formatDistanceLocale$2[token].standalone;
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", String(count));
  }

  if (options?.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return "vor " + result;
    }
  }

  return result;
};

// DIN 5008: https://de.wikipedia.org/wiki/Datumsformat#DIN_5008
const dateFormats$2 = {
  full: "EEEE, do MMMM y", // Montag, 7. Januar 2018
  long: "do MMMM y", // 7. Januar 2018
  medium: "do MMM y", // 7. Jan. 2018
  short: "dd.MM.y", // 07.01.2018
};

const timeFormats$2 = {
  full: "HH:mm:ss zzzz",
  long: "HH:mm:ss z",
  medium: "HH:mm:ss",
  short: "HH:mm",
};

const dateTimeFormats$2 = {
  full: "{{date}} 'um' {{time}}",
  long: "{{date}} 'um' {{time}}",
  medium: "{{date}} {{time}}",
  short: "{{date}} {{time}}",
};

const formatLong$2 = {
  date: buildFormatLongFn$1({
    formats: dateFormats$2,
    defaultWidth: "full",
  }),

  time: buildFormatLongFn$1({
    formats: timeFormats$2,
    defaultWidth: "full",
  }),

  dateTime: buildFormatLongFn$1({
    formats: dateTimeFormats$2,
    defaultWidth: "full",
  }),
};

const formatRelativeLocale$2 = {
  lastWeek: "'letzten' eeee 'um' p",
  yesterday: "'gestern um' p",
  today: "'heute um' p",
  tomorrow: "'morgen um' p",
  nextWeek: "eeee 'um' p",
  other: "P",
};

const formatRelative$2 = (token, _date, _baseDate, _options) =>
  formatRelativeLocale$2[token];

const eraValues$2 = {
  narrow: ["v.Chr.", "n.Chr."],
  abbreviated: ["v.Chr.", "n.Chr."],
  wide: ["vor Christus", "nach Christus"],
};

const quarterValues$2 = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1. Quartal", "2. Quartal", "3. Quartal", "4. Quartal"],
};

// Note: in German, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
const monthValues$2 = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ],

  wide: [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ],
};

// https://st.unicode.org/cldr-apps/v#/de/Gregorian/
const formattingMonthValues = {
  narrow: monthValues$2.narrow,
  abbreviated: [
    "Jan.",
    "Feb.",
    "März",
    "Apr.",
    "Mai",
    "Juni",
    "Juli",
    "Aug.",
    "Sep.",
    "Okt.",
    "Nov.",
    "Dez.",
  ],

  wide: monthValues$2.wide,
};

const dayValues$2 = {
  narrow: ["S", "M", "D", "M", "D", "F", "S"],
  short: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  abbreviated: ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."],
  wide: [
    "Sonntag",
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
  ],
};

// https://www.unicode.org/cldr/charts/32/summary/de.html#1881
const dayPeriodValues$2 = {
  narrow: {
    am: "vm.",
    pm: "nm.",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "Morgen",
    afternoon: "Nachm.",
    evening: "Abend",
    night: "Nacht",
  },
  abbreviated: {
    am: "vorm.",
    pm: "nachm.",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "Morgen",
    afternoon: "Nachmittag",
    evening: "Abend",
    night: "Nacht",
  },
  wide: {
    am: "vormittags",
    pm: "nachmittags",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "Morgen",
    afternoon: "Nachmittag",
    evening: "Abend",
    night: "Nacht",
  },
};
const formattingDayPeriodValues$2 = {
  narrow: {
    am: "vm.",
    pm: "nm.",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "morgens",
    afternoon: "nachm.",
    evening: "abends",
    night: "nachts",
  },
  abbreviated: {
    am: "vorm.",
    pm: "nachm.",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "morgens",
    afternoon: "nachmittags",
    evening: "abends",
    night: "nachts",
  },
  wide: {
    am: "vormittags",
    pm: "nachmittags",
    midnight: "Mitternacht",
    noon: "Mittag",
    morning: "morgens",
    afternoon: "nachmittags",
    evening: "abends",
    night: "nachts",
  },
};

const ordinalNumber$2 = (dirtyNumber) => {
  const number = Number(dirtyNumber);

  return number + ".";
};

const localize$2 = {
  ordinalNumber: ordinalNumber$2,

  era: buildLocalizeFn$1({
    values: eraValues$2,
    defaultWidth: "wide",
  }),

  quarter: buildLocalizeFn$1({
    values: quarterValues$2,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),

  month: buildLocalizeFn$1({
    values: monthValues$2,
    formattingValues: formattingMonthValues,
    defaultWidth: "wide",
  }),

  day: buildLocalizeFn$1({
    values: dayValues$2,
    defaultWidth: "wide",
  }),

  dayPeriod: buildLocalizeFn$1({
    values: dayPeriodValues$2,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues$2,
    defaultFormattingWidth: "wide",
  }),
};

const matchOrdinalNumberPattern$2 = /^(\d+)(\.)?/i;
const parseOrdinalNumberPattern$2 = /\d+/i;

const matchEraPatterns$2 = {
  narrow: /^(v\.? ?Chr\.?|n\.? ?Chr\.?)/i,
  abbreviated: /^(v\.? ?Chr\.?|n\.? ?Chr\.?)/i,
  wide: /^(vor Christus|vor unserer Zeitrechnung|nach Christus|unserer Zeitrechnung)/i,
};
const parseEraPatterns$2 = {
  any: [/^v/i, /^n/i],
};

const matchQuarterPatterns$2 = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](\.)? Quartal/i,
};
const parseQuarterPatterns$2 = {
  any: [/1/i, /2/i, /3/i, /4/i],
};

const matchMonthPatterns$2 = {
  narrow: /^[jfmasond]/i,
  abbreviated:
    /^(j[aä]n|feb|mär[z]?|apr|mai|jun[i]?|jul[i]?|aug|sep|okt|nov|dez)\.?/i,
  wide: /^(januar|februar|märz|april|mai|juni|juli|august|september|oktober|november|dezember)/i,
};
const parseMonthPatterns$2 = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],

  any: [
    /^j[aä]/i,
    /^f/i,
    /^mär/i,
    /^ap/i,
    /^mai/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],
};

const matchDayPatterns$2 = {
  narrow: /^[smdmf]/i,
  short: /^(so|mo|di|mi|do|fr|sa)/i,
  abbreviated: /^(son?|mon?|die?|mit?|don?|fre?|sam?)\.?/i,
  wide: /^(sonntag|montag|dienstag|mittwoch|donnerstag|freitag|samstag)/i,
};
const parseDayPatterns$2 = {
  any: [/^so/i, /^mo/i, /^di/i, /^mi/i, /^do/i, /^f/i, /^sa/i],
};

const matchDayPeriodPatterns$2 = {
  narrow: /^(vm\.?|nm\.?|Mitternacht|Mittag|morgens|nachm\.?|abends|nachts)/i,
  abbreviated:
    /^(vorm\.?|nachm\.?|Mitternacht|Mittag|morgens|nachm\.?|abends|nachts)/i,
  wide: /^(vormittags|nachmittags|Mitternacht|Mittag|morgens|nachmittags|abends|nachts)/i,
};
const parseDayPeriodPatterns$2 = {
  any: {
    am: /^v/i,
    pm: /^n/i,
    midnight: /^Mitte/i,
    noon: /^Mitta/i,
    morning: /morgens/i,
    afternoon: /nachmittags/i, // will never be matched. Afternoon is matched by `pm`
    evening: /abends/i,
    night: /nachts/i, // will never be matched. Night is matched by `pm`
  },
};

const match$2 = {
  ordinalNumber: buildMatchPatternFn$1({
    matchPattern: matchOrdinalNumberPattern$2,
    parsePattern: parseOrdinalNumberPattern$2,
    valueCallback: (value) => parseInt(value),
  }),

  era: buildMatchFn$1({
    matchPatterns: matchEraPatterns$2,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns$2,
    defaultParseWidth: "any",
  }),

  quarter: buildMatchFn$1({
    matchPatterns: matchQuarterPatterns$2,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns$2,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1,
  }),

  month: buildMatchFn$1({
    matchPatterns: matchMonthPatterns$2,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns$2,
    defaultParseWidth: "any",
  }),

  day: buildMatchFn$1({
    matchPatterns: matchDayPatterns$2,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns$2,
    defaultParseWidth: "any",
  }),

  dayPeriod: buildMatchFn$1({
    matchPatterns: matchDayPeriodPatterns$2,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPeriodPatterns$2,
    defaultParseWidth: "any",
  }),
};

/**
 * @category Locales
 * @summary German locale.
 * @language German
 * @iso-639-2 deu
 * @author Thomas Eilmsteiner [@DeMuu](https://github.com/DeMuu)
 * @author Asia [@asia-t](https://github.com/asia-t)
 * @author Van Vuong Ngo [@vanvuongngo](https://github.com/vanvuongngo)
 * @author RomanErnst [@pex](https://github.com/pex)
 * @author Philipp Keck [@Philipp91](https://github.com/Philipp91)
 */
const de = {
  code: "de",
  formatDistance: formatDistance$2,
  formatLong: formatLong$2,
  formatRelative: formatRelative$2,
  localize: localize$2,
  match: match$2,
  options: {
    weekStartsOn: 1 /* Monday */,
    firstWeekContainsDate: 4,
  },
};

const formatDistanceLocale$1 = {
  lessThanXSeconds: {
    one: "less than a second",
    other: "less than {{count}} seconds",
  },

  xSeconds: {
    one: "1 second",
    other: "{{count}} seconds",
  },

  halfAMinute: "half a minute",

  lessThanXMinutes: {
    one: "less than a minute",
    other: "less than {{count}} minutes",
  },

  xMinutes: {
    one: "1 minute",
    other: "{{count}} minutes",
  },

  aboutXHours: {
    one: "about 1 hour",
    other: "about {{count}} hours",
  },

  xHours: {
    one: "1 hour",
    other: "{{count}} hours",
  },

  xDays: {
    one: "1 day",
    other: "{{count}} days",
  },

  aboutXWeeks: {
    one: "about 1 week",
    other: "about {{count}} weeks",
  },

  xWeeks: {
    one: "1 week",
    other: "{{count}} weeks",
  },

  aboutXMonths: {
    one: "about 1 month",
    other: "about {{count}} months",
  },

  xMonths: {
    one: "1 month",
    other: "{{count}} months",
  },

  aboutXYears: {
    one: "about 1 year",
    other: "about {{count}} years",
  },

  xYears: {
    one: "1 year",
    other: "{{count}} years",
  },

  overXYears: {
    one: "over 1 year",
    other: "over {{count}} years",
  },

  almostXYears: {
    one: "almost 1 year",
    other: "almost {{count}} years",
  },
};

const formatDistance$1 = (token, count, options) => {
  let result;

  const tokenValue = formatDistanceLocale$1[token];
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", count.toString());
  }

  if (options?.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return result + " ago";
    }
  }

  return result;
};

const formatRelativeLocale$1 = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P",
};

const formatRelative$1 = (token, _date, _baseDate, _options) =>
  formatRelativeLocale$1[token];

const eraValues$1 = {
  narrow: ["B", "A"],
  abbreviated: ["BC", "AD"],
  wide: ["Before Christ", "Anno Domini"],
};

const quarterValues$1 = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1st quarter", "2nd quarter", "3rd quarter", "4th quarter"],
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
const monthValues$1 = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],

  wide: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

const dayValues$1 = {
  narrow: ["S", "M", "T", "W", "T", "F", "S"],
  short: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  abbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  wide: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
};

const dayPeriodValues$1 = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
};

const formattingDayPeriodValues$1 = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
};

const ordinalNumber$1 = (dirtyNumber, _options) => {
  const number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  const rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
    }
  }
  return number + "th";
};

const localize$1 = {
  ordinalNumber: ordinalNumber$1,

  era: buildLocalizeFn$1({
    values: eraValues$1,
    defaultWidth: "wide",
  }),

  quarter: buildLocalizeFn$1({
    values: quarterValues$1,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),

  month: buildLocalizeFn$1({
    values: monthValues$1,
    defaultWidth: "wide",
  }),

  day: buildLocalizeFn$1({
    values: dayValues$1,
    defaultWidth: "wide",
  }),

  dayPeriod: buildLocalizeFn$1({
    values: dayPeriodValues$1,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues$1,
    defaultFormattingWidth: "wide",
  }),
};

const matchOrdinalNumberPattern$1 = /^(\d+)(th|st|nd|rd)?/i;
const parseOrdinalNumberPattern$1 = /\d+/i;

const matchEraPatterns$1 = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i,
};
const parseEraPatterns$1 = {
  any: [/^b/i, /^(a|c)/i],
};

const matchQuarterPatterns$1 = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i,
};
const parseQuarterPatterns$1 = {
  any: [/1/i, /2/i, /3/i, /4/i],
};

const matchMonthPatterns$1 = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
};
const parseMonthPatterns$1 = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],

  any: [
    /^ja/i,
    /^f/i,
    /^mar/i,
    /^ap/i,
    /^may/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],
};

const matchDayPatterns$1 = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i,
};
const parseDayPatterns$1 = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i],
};

const matchDayPeriodPatterns$1 = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i,
};
const parseDayPeriodPatterns$1 = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i,
  },
};

const match$1 = {
  ordinalNumber: buildMatchPatternFn$1({
    matchPattern: matchOrdinalNumberPattern$1,
    parsePattern: parseOrdinalNumberPattern$1,
    valueCallback: (value) => parseInt(value, 10),
  }),

  era: buildMatchFn$1({
    matchPatterns: matchEraPatterns$1,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns$1,
    defaultParseWidth: "any",
  }),

  quarter: buildMatchFn$1({
    matchPatterns: matchQuarterPatterns$1,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns$1,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1,
  }),

  month: buildMatchFn$1({
    matchPatterns: matchMonthPatterns$1,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns$1,
    defaultParseWidth: "any",
  }),

  day: buildMatchFn$1({
    matchPatterns: matchDayPatterns$1,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns$1,
    defaultParseWidth: "any",
  }),

  dayPeriod: buildMatchFn$1({
    matchPatterns: matchDayPeriodPatterns$1,
    defaultMatchWidth: "any",
    parsePatterns: parseDayPeriodPatterns$1,
    defaultParseWidth: "any",
  }),
};

const dateFormats$1 = {
  full: "EEEE, MMMM do, y",
  long: "MMMM do, y",
  medium: "MMM d, y",
  short: "MM/dd/yyyy",
};

const timeFormats$1 = {
  full: "h:mm:ss a zzzz",
  long: "h:mm:ss a z",
  medium: "h:mm:ss a",
  short: "h:mm a",
};

const dateTimeFormats$1 = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: "{{date}}, {{time}}",
  short: "{{date}}, {{time}}",
};

const formatLong$1 = {
  date: buildFormatLongFn$1({
    formats: dateFormats$1,
    defaultWidth: "full",
  }),

  time: buildFormatLongFn$1({
    formats: timeFormats$1,
    defaultWidth: "full",
  }),

  dateTime: buildFormatLongFn$1({
    formats: dateTimeFormats$1,
    defaultWidth: "full",
  }),
};

/**
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp](https://github.com/kossnocorp)
 * @author Lesha Koss [@leshakoss](https://github.com/leshakoss)
 */
const enUS$1 = {
  code: "en-US",
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1,
  },
};

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param argument - The value to convert
 *
 * @returns The parsed date in the local time zone
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  const argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (
    argument instanceof Date ||
    (typeof argument === "object" && argStr === "[object Date]")
  ) {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new argument.constructor(+argument);
  } else if (
    typeof argument === "number" ||
    argStr === "[object Number]" ||
    typeof argument === "string" ||
    argStr === "[object String]"
  ) {
    // TODO: Can we get rid of as?
    return new Date(argument);
  } else {
    // TODO: Can we get rid of as?
    return new Date(NaN);
  }
}

/**
 * @name constructFrom
 * @category Generic Helpers
 * @summary Constructs a date using the reference date and the value
 *
 * @description
 * The function constructs a new date using the constructor from the reference
 * date and the given value. It helps to build generic functions that accept
 * date extensions.
 *
 * It defaults to `Date` if the passed reference date is a number or a string.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The reference date to take constructor from
 * @param value - The value to create the date
 *
 * @returns Date initialized using the given date and value
 *
 * @example
 * import { constructFrom } from 'date-fns'
 *
 * // A function that clones a date preserving the original type
 * function cloneDate<DateType extends Date(date: DateType): DateType {
 *   return constructFrom(
 *     date, // Use contrustor from the given date
 *     date.getTime() // Use the date value to create a new date
 *   )
 * }
 */
function constructFrom(date, value) {
  if (date instanceof Date) {
    return new date.constructor(value);
  } else {
    return new Date(value);
  }
}

/**
 * @name addDays
 * @category Day Helpers
 * @summary Add the specified number of days to the given date.
 *
 * @description
 * Add the specified number of days to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of days to be added.
 *
 * @returns The new date with the days added
 *
 * @example
 * // Add 10 days to 1 September 2014:
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */
function addDays(date, amount) {
  const _date = toDate(date);
  if (isNaN(amount)) return constructFrom(date, NaN);
  if (!amount) {
    // If 0 days, no-op to avoid changing times in the hour before end of DST
    return _date;
  }
  _date.setDate(_date.getDate() + amount);
  return _date;
}

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of months to be added.
 *
 * @returns The new date with the months added
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 *
 * // Add one month to 30 January 2023:
 * const result = addMonths(new Date(2023, 0, 30), 1)
 * //=> Tue Feb 28 2023 00:00:00
 */
function addMonths(date, amount) {
  const _date = toDate(date);
  if (isNaN(amount)) return constructFrom(date, NaN);
  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return _date;
  }
  const dayOfMonth = _date.getDate();

  // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.
  const endOfDesiredMonth = constructFrom(date, _date.getTime());
  endOfDesiredMonth.setMonth(_date.getMonth() + amount + 1, 0);
  const daysInMonth = endOfDesiredMonth.getDate();
  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    _date.setFullYear(
      endOfDesiredMonth.getFullYear(),
      endOfDesiredMonth.getMonth(),
      dayOfMonth,
    );
    return _date;
  }
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of milliseconds to be added.
 *
 * @returns The new date with the milliseconds added
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */
function addMilliseconds(date, amount) {
  const timestamp = +toDate(date);
  return constructFrom(date, timestamp + amount);
}

/**
 * @module constants
 * @summary Useful constants
 * @description
 * Collection of useful date constants.
 *
 * The constants could be imported from `date-fns/constants`:
 *
 * ```ts
 * import { maxTime, minTime } from "./constants/date-fns/constants";
 *
 * function isAllowedTime(time) {
 *   return time <= maxTime && time >= minTime;
 * }
 * ```
 */


/**
 * @constant
 * @name millisecondsInWeek
 * @summary Milliseconds in 1 week.
 */
const millisecondsInWeek = 604800000;

/**
 * @constant
 * @name millisecondsInDay
 * @summary Milliseconds in 1 day.
 */
const millisecondsInDay = 86400000;

/**
 * @constant
 * @name millisecondsInMinute
 * @summary Milliseconds in 1 minute
 */
const millisecondsInMinute = 60000;

/**
 * @constant
 * @name millisecondsInHour
 * @summary Milliseconds in 1 hour
 */
const millisecondsInHour = 3600000;

/**
 * @constant
 * @name millisecondsInSecond
 * @summary Milliseconds in 1 second
 */
const millisecondsInSecond = 1000;

/**
 * @name addHours
 * @category Hour Helpers
 * @summary Add the specified number of hours to the given date.
 *
 * @description
 * Add the specified number of hours to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of hours to be added.
 *
 * @returns The new date with the hours added
 *
 * @example
 * // Add 2 hours to 10 July 2014 23:00:00:
 * const result = addHours(new Date(2014, 6, 10, 23, 0), 2)
 * //=> Fri Jul 11 2014 01:00:00
 */
function addHours(date, amount) {
  return addMilliseconds(date, amount * millisecondsInHour);
}

let defaultOptions = {};

function getDefaultOptions$1() {
  return defaultOptions;
}

/**
 * The {@link startOfWeek} function options.
 */

/**
 * @name startOfWeek
 * @category Week Helpers
 * @summary Return the start of a week for the given date.
 *
 * @description
 * Return the start of a week for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week
 *
 * @example
 * // The start of a week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfWeek(date, options) {
  const defaultOptions = getDefaultOptions$1();
  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate(date);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  _date.setDate(_date.getDate() - diff);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * @name startOfISOWeek
 * @category ISO Week Helpers
 * @summary Return the start of an ISO week for the given date.
 *
 * @description
 * Return the start of an ISO week for the given date.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week
 *
 * @example
 * // The start of an ISO week for 2 September 2014 11:55:00:
 * const result = startOfISOWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfISOWeek$1(date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/**
 * @name getISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Get the ISO week-numbering year of the given date.
 *
 * @description
 * Get the ISO week-numbering year of the given date,
 * which always starts 3 days before the year's first Thursday.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week-numbering year
 *
 * @example
 * // Which ISO-week numbering year is 2 January 2005?
 * const result = getISOWeekYear(new Date(2005, 0, 2))
 * //=> 2004
 */
function getISOWeekYear$1(date) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const fourthOfJanuaryOfNextYear = constructFrom(date, 0);
  fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfISOWeek$1(fourthOfJanuaryOfNextYear);

  const fourthOfJanuaryOfThisYear = constructFrom(date, 0);
  fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfISOWeek$1(fourthOfJanuaryOfThisYear);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a day
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay$1(date) {
  const _date = toDate(date);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds$1(date) {
  const _date = toDate(date);
  const utcDate = new Date(
    Date.UTC(
      _date.getFullYear(),
      _date.getMonth(),
      _date.getDate(),
      _date.getHours(),
      _date.getMinutes(),
      _date.getSeconds(),
      _date.getMilliseconds(),
    ),
  );
  utcDate.setUTCFullYear(_date.getFullYear());
  return +date - +utcDate;
}

/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 *
 * @returns The number of calendar days
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */
function differenceInCalendarDays$1(dateLeft, dateRight) {
  const startOfDayLeft = startOfDay$1(dateLeft);
  const startOfDayRight = startOfDay$1(dateRight);

  const timestampLeft =
    +startOfDayLeft - getTimezoneOffsetInMilliseconds$1(startOfDayLeft);
  const timestampRight =
    +startOfDayRight - getTimezoneOffsetInMilliseconds$1(startOfDayRight);

  // Round the number of days to the nearest integer because the number of
  // milliseconds in a day is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round((timestampLeft - timestampRight) / millisecondsInDay);
}

/**
 * @name startOfISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Return the start of an ISO week-numbering year for the given date.
 *
 * @description
 * Return the start of an ISO week-numbering year,
 * which always starts 3 days before the year's first Thursday.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week-numbering year
 *
 * @example
 * // The start of an ISO week-numbering year for 2 July 2005:
 * const result = startOfISOWeekYear(new Date(2005, 6, 2))
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfISOWeekYear$1(date) {
  const year = getISOWeekYear$1(date);
  const fourthOfJanuary = constructFrom(date, 0);
  fourthOfJanuary.setFullYear(year, 0, 4);
  fourthOfJanuary.setHours(0, 0, 0, 0);
  return startOfISOWeek$1(fourthOfJanuary);
}

/**
 * @name addMinutes
 * @category Minute Helpers
 * @summary Add the specified number of minutes to the given date.
 *
 * @description
 * Add the specified number of minutes to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of minutes to be added.
 *
 * @returns The new date with the minutes added
 *
 * @example
 * // Add 30 minutes to 10 July 2014 12:00:00:
 * const result = addMinutes(new Date(2014, 6, 10, 12, 0), 30)
 * //=> Thu Jul 10 2014 12:30:00
 */
function addMinutes(date, amount) {
  return addMilliseconds(date, amount * millisecondsInMinute);
}

/**
 * @name addQuarters
 * @category Quarter Helpers
 * @summary Add the specified number of year quarters to the given date.
 *
 * @description
 * Add the specified number of year quarters to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of quarters to be added.
 *
 * @returns The new date with the quarters added
 *
 * @example
 * // Add 1 quarter to 1 September 2014:
 * const result = addQuarters(new Date(2014, 8, 1), 1)
 * //=> Mon Dec 01 2014 00:00:00
 */
function addQuarters(date, amount) {
  const months = amount * 3;
  return addMonths(date, months);
}

/**
 * @name addSeconds
 * @category Second Helpers
 * @summary Add the specified number of seconds to the given date.
 *
 * @description
 * Add the specified number of seconds to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of seconds to be added.
 *
 * @returns The new date with the seconds added
 *
 * @example
 * // Add 30 seconds to 10 July 2014 12:45:00:
 * const result = addSeconds(new Date(2014, 6, 10, 12, 45, 0), 30)
 * //=> Thu Jul 10 2014 12:45:30
 */
function addSeconds(date, amount) {
  return addMilliseconds(date, amount * 1000);
}

/**
 * @name addWeeks
 * @category Week Helpers
 * @summary Add the specified number of weeks to the given date.
 *
 * @description
 * Add the specified number of week to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of weeks to be added.
 *
 * @returns The new date with the weeks added
 *
 * @example
 * // Add 4 weeks to 1 September 2014:
 * const result = addWeeks(new Date(2014, 8, 1), 4)
 * //=> Mon Sep 29 2014 00:00:00
 */
function addWeeks(date, amount) {
  const days = amount * 7;
  return addDays(date, days);
}

/**
 * @name addYears
 * @category Year Helpers
 * @summary Add the specified number of years to the given date.
 *
 * @description
 * Add the specified number of years to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of years to be added.
 *
 * @returns The new date with the years added
 *
 * @example
 * // Add 5 years to 1 September 2014:
 * const result = addYears(new Date(2014, 8, 1), 5)
 * //=> Sun Sep 01 2019 00:00:00
 */
function addYears(date, amount) {
  return addMonths(date, amount * 12);
}

/**
 * @name max
 * @category Common Helpers
 * @summary Return the latest of the given dates.
 *
 * @description
 * Return the latest of the given dates.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dates - The dates to compare
 *
 * @returns The latest of the dates
 *
 * @example
 * // Which of these dates is the latest?
 * const result = max([
 *   new Date(1989, 6, 10),
 *   new Date(1987, 1, 11),
 *   new Date(1995, 6, 2),
 *   new Date(1990, 0, 1)
 * ])
 * //=> Sun Jul 02 1995 00:00:00
 */
function max$1(dates) {
  let result;
  dates.forEach(function (dirtyDate) {
    const currentDate = toDate(dirtyDate);

    if (
      result === undefined ||
      result < currentDate ||
      isNaN(Number(currentDate))
    ) {
      result = currentDate;
    }
  });

  return result || new Date(NaN);
}

/**
 * @name min
 * @category Common Helpers
 * @summary Returns the earliest of the given dates.
 *
 * @description
 * Returns the earliest of the given dates.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dates - The dates to compare
 *
 * @returns The earliest of the dates
 *
 * @example
 * // Which of these dates is the earliest?
 * const result = min([
 *   new Date(1989, 6, 10),
 *   new Date(1987, 1, 11),
 *   new Date(1995, 6, 2),
 *   new Date(1990, 0, 1)
 * ])
 * //=> Wed Feb 11 1987 00:00:00
 */
function min$1(dates) {
  let result;

  dates.forEach((dirtyDate) => {
    const date = toDate(dirtyDate);
    if (!result || result > date || isNaN(+date)) {
      result = date;
    }
  });

  return result || new Date(NaN);
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day (and year and month)?
 *
 * @description
 * Are the given dates in the same day (and year and month)?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The first date to check
 * @param dateRight - The second date to check

 * @returns The dates are in the same day (and year and month)
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 *
 * @example
 * // Are 4 September and 4 October in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2014, 9, 4))
 * //=> false
 *
 * @example
 * // Are 4 September, 2014 and 4 September, 2015 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2015, 8, 4))
 * //=> false
 */
function isSameDay$1(dateLeft, dateRight) {
  const dateLeftStartOfDay = startOfDay$1(dateLeft);
  const dateRightStartOfDay = startOfDay$1(dateRight);

  return +dateLeftStartOfDay === +dateRightStartOfDay;
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param value - The value to check
 *
 * @returns True if the given value is a date
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */
function isDate$1(value) {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Date]")
  );
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to check
 *
 * @returns The date is valid
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */
function isValid$2(date) {
  if (!isDate$1(date) && typeof date !== "number") {
    return false;
  }
  const _date = toDate(date);
  return !isNaN(Number(_date));
}

/**
 * @name differenceInCalendarMonths
 * @category Month Helpers
 * @summary Get the number of calendar months between the given dates.
 *
 * @description
 * Get the number of calendar months between the given dates.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 *
 * @returns The number of calendar months
 *
 * @example
 * // How many calendar months are between 31 January 2014 and 1 September 2014?
 * const result = differenceInCalendarMonths(
 *   new Date(2014, 8, 1),
 *   new Date(2014, 0, 31)
 * )
 * //=> 8
 */
function differenceInCalendarMonths(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);

  const yearDiff = _dateLeft.getFullYear() - _dateRight.getFullYear();
  const monthDiff = _dateLeft.getMonth() - _dateRight.getMonth();

  return yearDiff * 12 + monthDiff;
}

/**
 * @name getQuarter
 * @category Quarter Helpers
 * @summary Get the year quarter of the given date.
 *
 * @description
 * Get the year quarter of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The quarter
 *
 * @example
 * // Which quarter is 2 July 2014?
 * const result = getQuarter(new Date(2014, 6, 2))
 * //=> 3
 */
function getQuarter(date) {
  const _date = toDate(date);
  const quarter = Math.trunc(_date.getMonth() / 3) + 1;
  return quarter;
}

/**
 * @name differenceInCalendarQuarters
 * @category Quarter Helpers
 * @summary Get the number of calendar quarters between the given dates.
 *
 * @description
 * Get the number of calendar quarters between the given dates.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date

 * @returns The number of calendar quarters
 *
 * @example
 * // How many calendar quarters are between 31 December 2013 and 2 July 2014?
 * const result = differenceInCalendarQuarters(
 *   new Date(2014, 6, 2),
 *   new Date(2013, 11, 31)
 * )
 * //=> 3
 */
function differenceInCalendarQuarters(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);

  const yearDiff = _dateLeft.getFullYear() - _dateRight.getFullYear();
  const quarterDiff = getQuarter(_dateLeft) - getQuarter(_dateRight);

  return yearDiff * 4 + quarterDiff;
}

/**
 * @name differenceInCalendarYears
 * @category Year Helpers
 * @summary Get the number of calendar years between the given dates.
 *
 * @description
 * Get the number of calendar years between the given dates.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date

 * @returns The number of calendar years
 *
 * @example
 * // How many calendar years are between 31 December 2013 and 11 February 2015?
 * const result = differenceInCalendarYears(
 *   new Date(2015, 1, 11),
 *   new Date(2013, 11, 31)
 * )
 * //=> 2
 */
function differenceInCalendarYears(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);

  return _dateLeft.getFullYear() - _dateRight.getFullYear();
}

/**
 * @name differenceInDays
 * @category Day Helpers
 * @summary Get the number of full days between the given dates.
 *
 * @description
 * Get the number of full day periods between two dates. Fractional days are
 * truncated towards zero.
 *
 * One "full day" is the distance between a local time in one day to the same
 * local time on the next or previous day. A full day can sometimes be less than
 * or more than 24 hours if a daylight savings change happens between two dates.
 *
 * To ignore DST and only measure exact 24-hour periods, use this instead:
 * `Math.trunc(differenceInHours(dateLeft, dateRight)/24)|0`.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 *
 * @returns The number of full days according to the local timezone
 *
 * @example
 * // How many full days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 365
 *
 * @example
 * // How many full days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 0
 *
 * @example
 * // How many full days are between
 * // 1 March 2020 0:00 and 1 June 2020 0:00 ?
 * // Note: because local time is used, the
 * // result will always be 92 days, even in
 * // time zones where DST starts and the
 * // period has only 92*24-1 hours.
 * const result = differenceInDays(
 *   new Date(2020, 5, 1),
 *   new Date(2020, 2, 1)
 * )
 * //=> 92
 */
function differenceInDays(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);

  const sign = compareLocalAsc(_dateLeft, _dateRight);
  const difference = Math.abs(differenceInCalendarDays$1(_dateLeft, _dateRight));

  _dateLeft.setDate(_dateLeft.getDate() - sign * difference);

  // Math.abs(diff in full days - diff in calendar days) === 1 if last calendar day is not full
  // If so, result must be decreased by 1 in absolute value
  const isLastDayNotFull = Number(
    compareLocalAsc(_dateLeft, _dateRight) === -sign,
  );
  const result = sign * (difference - isLastDayNotFull);
  // Prevent negative zero
  return result === 0 ? 0 : result;
}

// Like `compareAsc` but uses local time not UTC, which is needed
// for accurate equality comparisons of UTC timestamps that end up
// having the same representation in local time, e.g. one hour before
// DST ends vs. the instant that DST ends.
function compareLocalAsc(dateLeft, dateRight) {
  const diff =
    dateLeft.getFullYear() - dateRight.getFullYear() ||
    dateLeft.getMonth() - dateRight.getMonth() ||
    dateLeft.getDate() - dateRight.getDate() ||
    dateLeft.getHours() - dateRight.getHours() ||
    dateLeft.getMinutes() - dateRight.getMinutes() ||
    dateLeft.getSeconds() - dateRight.getSeconds() ||
    dateLeft.getMilliseconds() - dateRight.getMilliseconds();

  if (diff < 0) {
    return -1;
  } else if (diff > 0) {
    return 1;
    // Return 0 if diff is 0; return NaN if diff is NaN
  } else {
    return diff;
  }
}

/**
 * @name endOfDay
 * @category Day Helpers
 * @summary Return the end of a day for the given date.
 *
 * @description
 * Return the end of a day for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The end of a day
 *
 * @example
 * // The end of a day for 2 September 2014 11:55:00:
 * const result = endOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 23:59:59.999
 */
function endOfDay(date) {
  const _date = toDate(date);
  _date.setHours(23, 59, 59, 999);
  return _date;
}

/**
 * @name endOfMonth
 * @category Month Helpers
 * @summary Return the end of a month for the given date.
 *
 * @description
 * Return the end of a month for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The end of a month
 *
 * @example
 * // The end of a month for 2 September 2014 11:55:00:
 * const result = endOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 30 2014 23:59:59.999
 */
function endOfMonth(date) {
  const _date = toDate(date);
  const month = _date.getMonth();
  _date.setFullYear(_date.getFullYear(), month + 1, 0);
  _date.setHours(23, 59, 59, 999);
  return _date;
}

/**
 * @name startOfQuarter
 * @category Quarter Helpers
 * @summary Return the start of a year quarter for the given date.
 *
 * @description
 * Return the start of a year quarter for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a quarter
 *
 * @example
 * // The start of a quarter for 2 September 2014 11:55:00:
 * const result = startOfQuarter(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Jul 01 2014 00:00:00
 */
function startOfQuarter(date) {
  const _date = toDate(date);
  const currentMonth = _date.getMonth();
  const month = currentMonth - (currentMonth % 3);
  _date.setMonth(month, 1);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * @name startOfMonth
 * @category Month Helpers
 * @summary Return the start of a month for the given date.
 *
 * @description
 * Return the start of a month for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a month
 *
 * @example
 * // The start of a month for 2 September 2014 11:55:00:
 * const result = startOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfMonth(date) {
  const _date = toDate(date);
  _date.setDate(1);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * @name endOfYear
 * @category Year Helpers
 * @summary Return the end of a year for the given date.
 *
 * @description
 * Return the end of a year for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The end of a year
 *
 * @example
 * // The end of a year for 2 September 2014 11:55:00:
 * const result = endOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Dec 31 2014 23:59:59.999
 */
function endOfYear(date) {
  const _date = toDate(date);
  const year = _date.getFullYear();
  _date.setFullYear(year + 1, 0, 0);
  _date.setHours(23, 59, 59, 999);
  return _date;
}

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a year
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear$1(date) {
  const cleanDate = toDate(date);
  const _date = constructFrom(date, 0);
  _date.setFullYear(cleanDate.getFullYear(), 0, 1);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * The {@link endOfWeek} function options.
 */

/**
 * @name endOfWeek
 * @category Week Helpers
 * @summary Return the end of a week for the given date.
 *
 * @description
 * Return the end of a week for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The end of a week
 *
 * @example
 * // The end of a week for 2 September 2014 11:55:00:
 * const result = endOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sat Sep 06 2014 23:59:59.999
 *
 * @example
 * // If the week starts on Monday, the end of the week for 2 September 2014 11:55:00:
 * const result = endOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Sun Sep 07 2014 23:59:59.999
 */
function endOfWeek(date, options) {
  const defaultOptions = getDefaultOptions$1();
  const weekStartsOn =
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate(date);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? -7 : 0) + 6 - (day - weekStartsOn);

  _date.setDate(_date.getDate() + diff);
  _date.setHours(23, 59, 59, 999);
  return _date;
}

const formatDistanceLocale = {
  lessThanXSeconds: {
    one: "less than a second",
    other: "less than {{count}} seconds",
  },

  xSeconds: {
    one: "1 second",
    other: "{{count}} seconds",
  },

  halfAMinute: "half a minute",

  lessThanXMinutes: {
    one: "less than a minute",
    other: "less than {{count}} minutes",
  },

  xMinutes: {
    one: "1 minute",
    other: "{{count}} minutes",
  },

  aboutXHours: {
    one: "about 1 hour",
    other: "about {{count}} hours",
  },

  xHours: {
    one: "1 hour",
    other: "{{count}} hours",
  },

  xDays: {
    one: "1 day",
    other: "{{count}} days",
  },

  aboutXWeeks: {
    one: "about 1 week",
    other: "about {{count}} weeks",
  },

  xWeeks: {
    one: "1 week",
    other: "{{count}} weeks",
  },

  aboutXMonths: {
    one: "about 1 month",
    other: "about {{count}} months",
  },

  xMonths: {
    one: "1 month",
    other: "{{count}} months",
  },

  aboutXYears: {
    one: "about 1 year",
    other: "about {{count}} years",
  },

  xYears: {
    one: "1 year",
    other: "{{count}} years",
  },

  overXYears: {
    one: "over 1 year",
    other: "over {{count}} years",
  },

  almostXYears: {
    one: "almost 1 year",
    other: "almost {{count}} years",
  },
};

const formatDistance = (token, count, options) => {
  let result;

  const tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", count.toString());
  }

  if (options?.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return result + " ago";
    }
  }

  return result;
};

function buildFormatLongFn(args) {
  return (options = {}) => {
    // TODO: Remove String()
    const width = options.width ? String(options.width) : args.defaultWidth;
    const format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

const dateFormats = {
  full: "EEEE, MMMM do, y",
  long: "MMMM do, y",
  medium: "MMM d, y",
  short: "MM/dd/yyyy",
};

const timeFormats = {
  full: "h:mm:ss a zzzz",
  long: "h:mm:ss a z",
  medium: "h:mm:ss a",
  short: "h:mm a",
};

const dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: "{{date}}, {{time}}",
  short: "{{date}}, {{time}}",
};

const formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: "full",
  }),

  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: "full",
  }),

  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: "full",
  }),
};

const formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P",
};

const formatRelative = (token, _date, _baseDate, _options) =>
  formatRelativeLocale[token];

/* eslint-disable no-unused-vars */

/**
 * The localize function argument callback which allows to convert raw value to
 * the actual type.
 *
 * @param value - The value to convert
 *
 * @returns The converted value
 */

/**
 * The map of localized values for each width.
 */

/**
 * The index type of the locale unit value. It types conversion of units of
 * values that don't start at 0 (i.e. quarters).
 */

/**
 * Converts the unit value to the tuple of values.
 */

/**
 * The tuple of localized era values. The first element represents BC,
 * the second element represents AD.
 */

/**
 * The tuple of localized quarter values. The first element represents Q1.
 */

/**
 * The tuple of localized day values. The first element represents Sunday.
 */

/**
 * The tuple of localized month values. The first element represents January.
 */

function buildLocalizeFn(args) {
  return (value, options) => {
    const context = options?.context ? String(options.context) : "standalone";

    let valuesArray;
    if (context === "formatting" && args.formattingValues) {
      const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      const width = options?.width ? String(options.width) : defaultWidth;

      valuesArray =
        args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      const defaultWidth = args.defaultWidth;
      const width = options?.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    const index = args.argumentCallback ? args.argumentCallback(value) : value;

    // @ts-expect-error - For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

const eraValues = {
  narrow: ["B", "A"],
  abbreviated: ["BC", "AD"],
  wide: ["Before Christ", "Anno Domini"],
};

const quarterValues = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1st quarter", "2nd quarter", "3rd quarter", "4th quarter"],
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
const monthValues = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],

  wide: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

const dayValues = {
  narrow: ["S", "M", "T", "W", "T", "F", "S"],
  short: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  abbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  wide: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
};

const dayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
};

const formattingDayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
};

const ordinalNumber = (dirtyNumber, _options) => {
  const number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  const rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
    }
  }
  return number + "th";
};

const localize = {
  ordinalNumber,

  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: "wide",
  }),

  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),

  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: "wide",
  }),

  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: "wide",
  }),

  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: "wide",
  }),
};

function buildMatchFn(args) {
  return (string, options = {}) => {
    const width = options.width;

    const matchPattern =
      (width && args.matchPatterns[width]) ||
      args.matchPatterns[args.defaultMatchWidth];
    const matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }
    const matchedString = matchResult[0];

    const parsePatterns =
      (width && args.parsePatterns[width]) ||
      args.parsePatterns[args.defaultParseWidth];

    const key = Array.isArray(parsePatterns)
      ? findIndex(parsePatterns, (pattern) => pattern.test(matchedString))
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        findKey(parsePatterns, (pattern) => pattern.test(matchedString));

    let value;

    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        options.valueCallback(value)
      : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

function findKey(object, predicate) {
  for (const key in object) {
    if (
      Object.prototype.hasOwnProperty.call(object, key) &&
      predicate(object[key])
    ) {
      return key;
    }
  }
  return undefined;
}

function findIndex(array, predicate) {
  for (let key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return (string, options = {}) => {
    const matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    const matchedString = matchResult[0];

    const parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    let value = args.valueCallback
      ? args.valueCallback(parseResult[0])
      : parseResult[0];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
    value = options.valueCallback ? options.valueCallback(value) : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

const matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
const parseOrdinalNumberPattern = /\d+/i;

const matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i,
};
const parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i],
};

const matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i,
};
const parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i],
};

const matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
};
const parseMonthPatterns = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],

  any: [
    /^ja/i,
    /^f/i,
    /^mar/i,
    /^ap/i,
    /^may/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],
};

const matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i,
};
const parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i],
};

const matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i,
};
const parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i,
  },
};

const match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: (value) => parseInt(value, 10),
  }),

  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns,
    defaultParseWidth: "any",
  }),

  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1,
  }),

  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: "any",
  }),

  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns,
    defaultParseWidth: "any",
  }),

  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: "any",
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: "any",
  }),
};

/**
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp](https://github.com/kossnocorp)
 * @author Lesha Koss [@leshakoss](https://github.com/leshakoss)
 */
const enUS = {
  code: "en-US",
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1,
  },
};

/**
 * @name getDayOfYear
 * @category Day Helpers
 * @summary Get the day of the year of the given date.
 *
 * @description
 * Get the day of the year of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of year
 *
 * @example
 * // Which day of the year is 2 July 2014?
 * const result = getDayOfYear(new Date(2014, 6, 2))
 * //=> 183
 */
function getDayOfYear$1(date) {
  const _date = toDate(date);
  const diff = differenceInCalendarDays$1(_date, startOfYear$1(_date));
  const dayOfYear = diff + 1;
  return dayOfYear;
}

/**
 * @name getISOWeek
 * @category ISO Week Helpers
 * @summary Get the ISO week of the given date.
 *
 * @description
 * Get the ISO week of the given date.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week
 *
 * @example
 * // Which week of the ISO-week numbering year is 2 January 2005?
 * const result = getISOWeek(new Date(2005, 0, 2))
 * //=> 53
 */
function getISOWeek$1(date) {
  const _date = toDate(date);
  const diff = +startOfISOWeek$1(_date) - +startOfISOWeekYear$1(_date);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

/**
 * The {@link getWeekYear} function options.
 */

/**
 * @name getWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Get the local week-numbering year of the given date.
 *
 * @description
 * Get the local week-numbering year of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options.
 *
 * @returns The local week-numbering year
 *
 * @example
 * // Which week numbering year is 26 December 2004 with the default settings?
 * const result = getWeekYear(new Date(2004, 11, 26))
 * //=> 2005
 *
 * @example
 * // Which week numbering year is 26 December 2004 if week starts on Saturday?
 * const result = getWeekYear(new Date(2004, 11, 26), { weekStartsOn: 6 })
 * //=> 2004
 *
 * @example
 * // Which week numbering year is 26 December 2004 if the first week contains 4 January?
 * const result = getWeekYear(new Date(2004, 11, 26), { firstWeekContainsDate: 4 })
 * //=> 2004
 */
function getWeekYear$1(date, options) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const defaultOptions = getDefaultOptions$1();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const firstWeekOfNextYear = constructFrom(date, 0);
  firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfWeek(firstWeekOfNextYear, options);

  const firstWeekOfThisYear = constructFrom(date, 0);
  firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfWeek(firstWeekOfThisYear, options);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * The {@link startOfWeekYear} function options.
 */

/**
 * @name startOfWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Return the start of a local week-numbering year for the given date.
 *
 * @description
 * Return the start of a local week-numbering year.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week-numbering year
 *
 * @example
 * // The start of an a week-numbering year for 2 July 2005 with default settings:
 * const result = startOfWeekYear(new Date(2005, 6, 2))
 * //=> Sun Dec 26 2004 00:00:00
 *
 * @example
 * // The start of a week-numbering year for 2 July 2005
 * // if Monday is the first day of week
 * // and 4 January is always in the first week of the year:
 * const result = startOfWeekYear(new Date(2005, 6, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfWeekYear$1(date, options) {
  const defaultOptions = getDefaultOptions$1();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const year = getWeekYear$1(date, options);
  const firstWeek = constructFrom(date, 0);
  firstWeek.setFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setHours(0, 0, 0, 0);
  const _date = startOfWeek(firstWeek, options);
  return _date;
}

/**
 * The {@link getWeek} function options.
 */

/**
 * @name getWeek
 * @category Week Helpers
 * @summary Get the local week index of the given date.
 *
 * @description
 * Get the local week index of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options
 *
 * @returns The week
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005 with default options?
 * const result = getWeek(new Date(2005, 0, 2))
 * //=> 2
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005,
 * // if Monday is the first day of the week,
 * // and the first week of the year always contains 4 January?
 * const result = getWeek(new Date(2005, 0, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> 53
 */

function getWeek$2(date, options) {
  const _date = toDate(date);
  const diff = +startOfWeek(_date, options) - +startOfWeekYear$1(_date, options);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

function addLeadingZeros$1(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  const output = Math.abs(number).toString().padStart(targetLength, "0");
  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

const lightFormatters$1 = {
  // Year
  y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    const signedYear = date.getFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros$1(token === "yy" ? year % 100 : year, token.length);
  },

  // Month
  M(date, token) {
    const month = date.getMonth();
    return token === "M" ? String(month + 1) : addLeadingZeros$1(month + 1, 2);
  },

  // Day of the month
  d(date, token) {
    return addLeadingZeros$1(date.getDate(), token.length);
  },

  // AM or PM
  a(date, token) {
    const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return dayPeriodEnumValue.toUpperCase();
      case "aaa":
        return dayPeriodEnumValue;
      case "aaaaa":
        return dayPeriodEnumValue[0];
      case "aaaa":
      default:
        return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
    }
  },

  // Hour [1-12]
  h(date, token) {
    return addLeadingZeros$1(date.getHours() % 12 || 12, token.length);
  },

  // Hour [0-23]
  H(date, token) {
    return addLeadingZeros$1(date.getHours(), token.length);
  },

  // Minute
  m(date, token) {
    return addLeadingZeros$1(date.getMinutes(), token.length);
  },

  // Second
  s(date, token) {
    return addLeadingZeros$1(date.getSeconds(), token.length);
  },

  // Fraction of second
  S(date, token) {
    const numberOfDigits = token.length;
    const milliseconds = date.getMilliseconds();
    const fractionalSeconds = Math.trunc(
      milliseconds * Math.pow(10, numberOfDigits - 3),
    );
    return addLeadingZeros$1(fractionalSeconds, token.length);
  },
};

const dayPeriodEnum$1 = {
  midnight: "midnight",
  noon: "noon",
  morning: "morning",
  afternoon: "afternoon",
  evening: "evening",
  night: "night",
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

const formatters$1 = {
  // Era
  G: function (date, token, localize) {
    const era = date.getFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return localize.era(era, { width: "abbreviated" });
      // A, B
      case "GGGGG":
        return localize.era(era, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return localize.era(era, { width: "wide" });
    }
  },

  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === "yo") {
      const signedYear = date.getFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      const year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, { unit: "year" });
    }

    return lightFormatters$1.y(date, token);
  },

  // Local week-numbering year
  Y: function (date, token, localize, options) {
    const signedWeekYear = getWeekYear$1(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === "YY") {
      const twoDigitYear = weekYear % 100;
      return addLeadingZeros$1(twoDigitYear, 2);
    }

    // Ordinal number
    if (token === "Yo") {
      return localize.ordinalNumber(weekYear, { unit: "year" });
    }

    // Padding
    return addLeadingZeros$1(weekYear, token.length);
  },

  // ISO week-numbering year
  R: function (date, token) {
    const isoWeekYear = getISOWeekYear$1(date);

    // Padding
    return addLeadingZeros$1(isoWeekYear, token.length);
  },

  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    const year = date.getFullYear();
    return addLeadingZeros$1(year, token.length);
  },

  // Quarter
  Q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
        return String(quarter);
      // 01, 02, 03, 04
      case "QQ":
        return addLeadingZeros$1(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "formatting",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "formatting",
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone quarter
  q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "q":
        return String(quarter);
      // 01, 02, 03, 04
      case "qq":
        return addLeadingZeros$1(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "standalone",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "standalone",
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // Month
  M: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      case "M":
      case "MM":
        return lightFormatters$1.M(date, token);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "MMM":
        return localize.month(month, {
          width: "abbreviated",
          context: "formatting",
        });
      // J, F, ..., D
      case "MMMMM":
        return localize.month(month, {
          width: "narrow",
          context: "formatting",
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return localize.month(month, { width: "wide", context: "formatting" });
    }
  },

  // Stand-alone month
  L: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return String(month + 1);
      // 01, 02, ..., 12
      case "LL":
        return addLeadingZeros$1(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "LLL":
        return localize.month(month, {
          width: "abbreviated",
          context: "standalone",
        });
      // J, F, ..., D
      case "LLLLL":
        return localize.month(month, {
          width: "narrow",
          context: "standalone",
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return localize.month(month, { width: "wide", context: "standalone" });
    }
  },

  // Local week of year
  w: function (date, token, localize, options) {
    const week = getWeek$2(date, options);

    if (token === "wo") {
      return localize.ordinalNumber(week, { unit: "week" });
    }

    return addLeadingZeros$1(week, token.length);
  },

  // ISO week of year
  I: function (date, token, localize) {
    const isoWeek = getISOWeek$1(date);

    if (token === "Io") {
      return localize.ordinalNumber(isoWeek, { unit: "week" });
    }

    return addLeadingZeros$1(isoWeek, token.length);
  },

  // Day of the month
  d: function (date, token, localize) {
    if (token === "do") {
      return localize.ordinalNumber(date.getDate(), { unit: "date" });
    }

    return lightFormatters$1.d(date, token);
  },

  // Day of year
  D: function (date, token, localize) {
    const dayOfYear = getDayOfYear$1(date);

    if (token === "Do") {
      return localize.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
    }

    return addLeadingZeros$1(dayOfYear, token.length);
  },

  // Day of week
  E: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "EEEEE":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "EEEEEE":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "EEEE":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Local day of week
  e: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case "e":
        return String(localDayOfWeek);
      // Padded numerical value
      case "ee":
        return addLeadingZeros$1(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case "eo":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "eee":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "eeeee":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "eeeeee":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "eeee":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case "c":
        return String(localDayOfWeek);
      // Padded numerical value
      case "cc":
        return addLeadingZeros$1(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case "co":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "ccc":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "standalone",
        });
      // T
      case "ccccc":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "standalone",
        });
      // Tu
      case "cccccc":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "standalone",
        });
      // Tuesday
      case "cccc":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // ISO day of week
  i: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case "i":
        return String(isoDayOfWeek);
      // 02
      case "ii":
        return addLeadingZeros$1(isoDayOfWeek, token.length);
      // 2nd
      case "io":
        return localize.ordinalNumber(isoDayOfWeek, { unit: "day" });
      // Tue
      case "iii":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "iiiii":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "iiiiii":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "iiii":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM or PM
  a: function (date, token, localize) {
    const hours = date.getHours();
    const dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "aaa":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "aaaaa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "aaaa":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum$1.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum$1.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    }

    switch (token) {
      case "b":
      case "bb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "bbb":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "bbbbb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "bbbb":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum$1.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum$1.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum$1.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum$1.night;
    }

    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "BBBBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "BBBB":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === "ho") {
      let hours = date.getHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return lightFormatters$1.h(date, token);
  },

  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === "Ho") {
      return localize.ordinalNumber(date.getHours(), { unit: "hour" });
    }

    return lightFormatters$1.H(date, token);
  },

  // Hour [0-11]
  K: function (date, token, localize) {
    const hours = date.getHours() % 12;

    if (token === "Ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros$1(hours, token.length);
  },

  // Hour [1-24]
  k: function (date, token, localize) {
    let hours = date.getHours();
    if (hours === 0) hours = 24;

    if (token === "ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros$1(hours, token.length);
  },

  // Minute
  m: function (date, token, localize) {
    if (token === "mo") {
      return localize.ordinalNumber(date.getMinutes(), { unit: "minute" });
    }

    return lightFormatters$1.m(date, token);
  },

  // Second
  s: function (date, token, localize) {
    if (token === "so") {
      return localize.ordinalNumber(date.getSeconds(), { unit: "second" });
    }

    return lightFormatters$1.s(date, token);
  },

  // Fraction of second
  S: function (date, token) {
    return lightFormatters$1.S(date, token);
  },

  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return "Z";
    }

    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes$1(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX": // Hours and minutes without `:` delimiter
        return formatTimezone$1(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone$1(timezoneOffset, ":");
    }
  },

  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes$1(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx": // Hours and minutes without `:` delimiter
        return formatTimezone$1(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone$1(timezoneOffset, ":");
    }
  },

  // Timezone (GMT)
  O: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort$1(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone$1(timezoneOffset, ":");
    }
  },

  // Timezone (specific non-location)
  z: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return "GMT" + formatTimezoneShort$1(timezoneOffset, ":");
      // Long
      case "zzzz":
      default:
        return "GMT" + formatTimezone$1(timezoneOffset, ":");
    }
  },

  // Seconds timestamp
  t: function (date, token, _localize) {
    const timestamp = Math.trunc(date.getTime() / 1000);
    return addLeadingZeros$1(timestamp, token.length);
  },

  // Milliseconds timestamp
  T: function (date, token, _localize) {
    const timestamp = date.getTime();
    return addLeadingZeros$1(timestamp, token.length);
  },
};

function formatTimezoneShort$1(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.trunc(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros$1(minutes, 2);
}

function formatTimezoneWithOptionalMinutes$1(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros$1(Math.abs(offset) / 60, 2);
  }
  return formatTimezone$1(offset, delimiter);
}

function formatTimezone$1(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros$1(Math.trunc(absOffset / 60), 2);
  const minutes = addLeadingZeros$1(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

const dateLongFormatter$1 = (pattern, formatLong) => {
  switch (pattern) {
    case "P":
      return formatLong.date({ width: "short" });
    case "PP":
      return formatLong.date({ width: "medium" });
    case "PPP":
      return formatLong.date({ width: "long" });
    case "PPPP":
    default:
      return formatLong.date({ width: "full" });
  }
};

const timeLongFormatter$1 = (pattern, formatLong) => {
  switch (pattern) {
    case "p":
      return formatLong.time({ width: "short" });
    case "pp":
      return formatLong.time({ width: "medium" });
    case "ppp":
      return formatLong.time({ width: "long" });
    case "pppp":
    default:
      return formatLong.time({ width: "full" });
  }
};

const dateTimeLongFormatter$1 = (pattern, formatLong) => {
  const matchResult = pattern.match(/(P+)(p+)?/) || [];
  const datePattern = matchResult[1];
  const timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter$1(pattern, formatLong);
  }

  let dateTimeFormat;

  switch (datePattern) {
    case "P":
      dateTimeFormat = formatLong.dateTime({ width: "short" });
      break;
    case "PP":
      dateTimeFormat = formatLong.dateTime({ width: "medium" });
      break;
    case "PPP":
      dateTimeFormat = formatLong.dateTime({ width: "long" });
      break;
    case "PPPP":
    default:
      dateTimeFormat = formatLong.dateTime({ width: "full" });
      break;
  }

  return dateTimeFormat
    .replace("{{date}}", dateLongFormatter$1(datePattern, formatLong))
    .replace("{{time}}", timeLongFormatter$1(timePattern, formatLong));
};

const longFormatters$1 = {
  p: timeLongFormatter$1,
  P: dateTimeLongFormatter$1,
};

const dayOfYearTokenRE$1 = /^D+$/;
const weekYearTokenRE$1 = /^Y+$/;

const throwTokens$1 = ["D", "DD", "YY", "YYYY"];

function isProtectedDayOfYearToken$1(token) {
  return dayOfYearTokenRE$1.test(token);
}

function isProtectedWeekYearToken$1(token) {
  return weekYearTokenRE$1.test(token);
}

function warnOrThrowProtectedError$1(token, format, input) {
  const _message = message$1(token, format, input);
  console.warn(_message);
  if (throwTokens$1.includes(token)) throw new RangeError(_message);
}

function message$1(token, format, input) {
  const subject = token[0] === "Y" ? "years" : "days of the month";
  return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
const formattingTokensRegExp$2 =
  /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
const longFormattingTokensRegExp$3 = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

const escapedStringRegExp$2 = /^'([^]*?)'?$/;
const doubleQuoteRegExp$2 = /''/g;
const unescapedLatinCharacterRegExp$2 = /[a-zA-Z]/;

/**
 * The {@link format} function options.
 */

/**
 * @name format
 * @alias formatDate
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear](https://date-fns.org/docs/getISOWeekYear)
 *    and [getWeekYear](https://date-fns.org/docs/getWeekYear)).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param format - The string of tokens
 * @param options - An object with options
 *
 * @returns The formatted date string
 *
 * @throws `date` must not be Invalid Date
 * @throws `options.locale` must contain `localize` property
 * @throws `options.locale` must contain `formatLong` property
 * @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */
function format$1(date, formatStr, options) {
  const defaultOptions = getDefaultOptions$1();
  const locale = options?.locale ?? defaultOptions.locale ?? enUS;

  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const originalDate = toDate(date);

  if (!isValid$2(originalDate)) {
    throw new RangeError("Invalid time value");
  }

  let parts = formatStr
    .match(longFormattingTokensRegExp$3)
    .map((substring) => {
      const firstCharacter = substring[0];
      if (firstCharacter === "p" || firstCharacter === "P") {
        const longFormatter = longFormatters$1[firstCharacter];
        return longFormatter(substring, locale.formatLong);
      }
      return substring;
    })
    .join("")
    .match(formattingTokensRegExp$2)
    .map((substring) => {
      // Replace two single quote characters with one single quote character
      if (substring === "''") {
        return { isToken: false, value: "'" };
      }

      const firstCharacter = substring[0];
      if (firstCharacter === "'") {
        return { isToken: false, value: cleanEscapedString$2(substring) };
      }

      if (formatters$1[firstCharacter]) {
        return { isToken: true, value: substring };
      }

      if (firstCharacter.match(unescapedLatinCharacterRegExp$2)) {
        throw new RangeError(
          "Format string contains an unescaped latin alphabet character `" +
            firstCharacter +
            "`",
        );
      }

      return { isToken: false, value: substring };
    });

  // invoke localize preprocessor (only for french locales at the moment)
  if (locale.localize.preprocessor) {
    parts = locale.localize.preprocessor(originalDate, parts);
  }

  const formatterOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale,
  };

  return parts
    .map((part) => {
      if (!part.isToken) return part.value;

      const token = part.value;

      if (
        (!options?.useAdditionalWeekYearTokens &&
          isProtectedWeekYearToken$1(token)) ||
        (!options?.useAdditionalDayOfYearTokens &&
          isProtectedDayOfYearToken$1(token))
      ) {
        warnOrThrowProtectedError$1(token, formatStr, String(date));
      }

      const formatter = formatters$1[token[0]];
      return formatter(originalDate, token, locale.localize, formatterOptions);
    })
    .join("");
}

function cleanEscapedString$2(input) {
  const matched = input.match(escapedStringRegExp$2);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp$2, "'");
}

/**
 * @name getDate
 * @category Day Helpers
 * @summary Get the day of the month of the given date.
 *
 * @description
 * Get the day of the month of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of month
 *
 * @example
 * // Which day of the month is 29 February 2012?
 * const result = getDate(new Date(2012, 1, 29))
 * //=> 29
 */
function getDate(date) {
  const _date = toDate(date);
  const dayOfMonth = _date.getDate();
  return dayOfMonth;
}

/**
 * @name getDay
 * @category Weekday Helpers
 * @summary Get the day of the week of the given date.
 *
 * @description
 * Get the day of the week of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of week, 0 represents Sunday
 *
 * @example
 * // Which day of the week is 29 February 2012?
 * const result = getDay(new Date(2012, 1, 29))
 * //=> 3
 */
function getDay(date) {
  const _date = toDate(date);
  const day = _date.getDay();
  return day;
}

/**
 * @name getDaysInMonth
 * @category Month Helpers
 * @summary Get the number of days in a month of the given date.
 *
 * @description
 * Get the number of days in a month of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The number of days in a month
 *
 * @example
 * // How many days are in February 2000?
 * const result = getDaysInMonth(new Date(2000, 1))
 * //=> 29
 */
function getDaysInMonth(date) {
  const _date = toDate(date);
  const year = _date.getFullYear();
  const monthIndex = _date.getMonth();
  const lastDayOfMonth = constructFrom(date, 0);
  lastDayOfMonth.setFullYear(year, monthIndex + 1, 0);
  lastDayOfMonth.setHours(0, 0, 0, 0);
  return lastDayOfMonth.getDate();
}

/**
 * @name getDefaultOptions
 * @category Common Helpers
 * @summary Get default options.
 * @pure false
 *
 * @description
 * Returns an object that contains defaults for
 * `options.locale`, `options.weekStartsOn` and `options.firstWeekContainsDate`
 * arguments for all functions.
 *
 * You can change these with [setDefaultOptions](https://date-fns.org/docs/setDefaultOptions).
 *
 * @returns The default options
 *
 * @example
 * const result = getDefaultOptions()
 * //=> {}
 *
 * @example
 * setDefaultOptions({ weekStarsOn: 1, firstWeekContainsDate: 4 })
 * const result = getDefaultOptions()
 * //=> { weekStarsOn: 1, firstWeekContainsDate: 4 }
 */
function getDefaultOptions() {
  return Object.assign({}, getDefaultOptions$1());
}

/**
 * @name getHours
 * @category Hour Helpers
 * @summary Get the hours of the given date.
 *
 * @description
 * Get the hours of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The hours
 *
 * @example
 * // Get the hours of 29 February 2012 11:45:00:
 * const result = getHours(new Date(2012, 1, 29, 11, 45))
 * //=> 11
 */
function getHours(date) {
  const _date = toDate(date);
  const hours = _date.getHours();
  return hours;
}

/**
 * @name getISODay
 * @category Weekday Helpers
 * @summary Get the day of the ISO week of the given date.
 *
 * @description
 * Get the day of the ISO week of the given date,
 * which is 7 for Sunday, 1 for Monday etc.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of ISO week
 *
 * @example
 * // Which day of the ISO week is 26 February 2012?
 * const result = getISODay(new Date(2012, 1, 26))
 * //=> 7
 */
function getISODay(date) {
  const _date = toDate(date);
  let day = _date.getDay();

  if (day === 0) {
    day = 7;
  }

  return day;
}

/**
 * @name getMinutes
 * @category Minute Helpers
 * @summary Get the minutes of the given date.
 *
 * @description
 * Get the minutes of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The minutes
 *
 * @example
 * // Get the minutes of 29 February 2012 11:45:05:
 * const result = getMinutes(new Date(2012, 1, 29, 11, 45, 5))
 * //=> 45
 */
function getMinutes(date) {
  const _date = toDate(date);
  const minutes = _date.getMinutes();
  return minutes;
}

/**
 * @name getMonth
 * @category Month Helpers
 * @summary Get the month of the given date.
 *
 * @description
 * Get the month of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The month index (0-11)
 *
 * @example
 * // Which month is 29 February 2012?
 * const result = getMonth(new Date(2012, 1, 29))
 * //=> 1
 */
function getMonth(date) {
  const _date = toDate(date);
  const month = _date.getMonth();
  return month;
}

/**
 * @name getSeconds
 * @category Second Helpers
 * @summary Get the seconds of the given date.
 *
 * @description
 * Get the seconds of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The seconds
 *
 * @example
 * // Get the seconds of 29 February 2012 11:45:05.123:
 * const result = getSeconds(new Date(2012, 1, 29, 11, 45, 5, 123))
 * //=> 5
 */
function getSeconds(date) {
  const _date = toDate(date);
  const seconds = _date.getSeconds();
  return seconds;
}

/**
 * @name getTime
 * @category Timestamp Helpers
 * @summary Get the milliseconds timestamp of the given date.
 *
 * @description
 * Get the milliseconds timestamp of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The timestamp
 *
 * @example
 * // Get the timestamp of 29 February 2012 11:45:05.123:
 * const result = getTime(new Date(2012, 1, 29, 11, 45, 5, 123))
 * //=> 1330515905123
 */
function getTime(date) {
  const _date = toDate(date);
  const timestamp = _date.getTime();
  return timestamp;
}

/**
 * @name getYear
 * @category Year Helpers
 * @summary Get the year of the given date.
 *
 * @description
 * Get the year of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The year
 *
 * @example
 * // Which year is 2 July 2014?
 * const result = getYear(new Date(2014, 6, 2))
 * //=> 2014
 */
function getYear$1(date) {
  return toDate(date).getFullYear();
}

/**
 * @name isAfter
 * @category Common Helpers
 * @summary Is the first date after the second one?
 *
 * @description
 * Is the first date after the second one?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date that should be after the other one to return true
 * @param dateToCompare - The date to compare with
 *
 * @returns The first date is after the second date
 *
 * @example
 * // Is 10 July 1989 after 11 February 1987?
 * const result = isAfter(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> true
 */
function isAfter(date, dateToCompare) {
  const _date = toDate(date);
  const _dateToCompare = toDate(dateToCompare);
  return _date.getTime() > _dateToCompare.getTime();
}

/**
 * @name isBefore
 * @category Common Helpers
 * @summary Is the first date before the second one?
 *
 * @description
 * Is the first date before the second one?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date that should be before the other one to return true
 * @param dateToCompare - The date to compare with
 *
 * @returns The first date is before the second date
 *
 * @example
 * // Is 10 July 1989 before 11 February 1987?
 * const result = isBefore(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> false
 */
function isBefore(date, dateToCompare) {
  const _date = toDate(date);
  const _dateToCompare = toDate(dateToCompare);
  return +_date < +_dateToCompare;
}

/**
 * @name isEqual
 * @category Common Helpers
 * @summary Are the given dates equal?
 *
 * @description
 * Are the given dates equal?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The first date to compare
 * @param dateRight - The second date to compare
 *
 * @returns The dates are equal
 *
 * @example
 * // Are 2 July 2014 06:30:45.000 and 2 July 2014 06:30:45.500 equal?
 * const result = isEqual(
 *   new Date(2014, 6, 2, 6, 30, 45, 0),
 *   new Date(2014, 6, 2, 6, 30, 45, 500)
 * )
 * //=> false
 */
function isEqual$1(leftDate, rightDate) {
  const _dateLeft = toDate(leftDate);
  const _dateRight = toDate(rightDate);
  return +_dateLeft === +_dateRight;
}

/**
 * @name transpose
 * @category Generic Helpers
 * @summary Transpose the date to the given constructor.
 *
 * @description
 * The function transposes the date to the given constructor. It helps you
 * to transpose the date in the system time zone to say `UTCDate` or any other
 * date extension.
 *
 * @typeParam DateInputType - The input `Date` type derived from the passed argument.
 * @typeParam DateOutputType - The output `Date` type derived from the passed constructor.
 *
 * @param fromDate - The date to use values from
 * @param constructor - The date constructor to use
 *
 * @returns Date transposed to the given constructor
 *
 * @example
 * // Create July 10, 2022 00:00 in locale time zone
 * const date = new Date(2022, 6, 10)
 * //=> 'Sun Jul 10 2022 00:00:00 GMT+0800 (Singapore Standard Time)'
 *
 * @example
 * // Transpose the date to July 10, 2022 00:00 in UTC
 * transpose(date, UTCDate)
 * //=> 'Sun Jul 10 2022 00:00:00 GMT+0000 (Coordinated Universal Time)'
 */
function transpose(fromDate, constructor) {
  const date =
    constructor instanceof Date
      ? constructFrom(constructor, 0)
      : new constructor(0);
  date.setFullYear(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate(),
  );
  date.setHours(
    fromDate.getHours(),
    fromDate.getMinutes(),
    fromDate.getSeconds(),
    fromDate.getMilliseconds(),
  );
  return date;
}

const TIMEZONE_UNIT_PRIORITY = 10;

class Setter {
  subPriority = 0;

  validate(_utcDate, _options) {
    return true;
  }
}

class ValueSetter extends Setter {
  constructor(
    value,

    validateValue,

    setValue,

    priority,
    subPriority,
  ) {
    super();
    this.value = value;
    this.validateValue = validateValue;
    this.setValue = setValue;
    this.priority = priority;
    if (subPriority) {
      this.subPriority = subPriority;
    }
  }

  validate(date, options) {
    return this.validateValue(date, this.value, options);
  }

  set(date, flags, options) {
    return this.setValue(date, flags, this.value, options);
  }
}

class DateToSystemTimezoneSetter extends Setter {
  priority = TIMEZONE_UNIT_PRIORITY;
  subPriority = -1;
  set(date, flags) {
    if (flags.timestampIsSet) return date;
    return constructFrom(date, transpose(date, Date));
  }
}

class Parser {
  run(dateString, token, match, options) {
    const result = this.parse(dateString, token, match, options);
    if (!result) {
      return null;
    }

    return {
      setter: new ValueSetter(
        result.value,
        this.validate,
        this.set,
        this.priority,
        this.subPriority,
      ),
      rest: result.rest,
    };
  }

  validate(_utcDate, _value, _options) {
    return true;
  }
}

class EraParser extends Parser {
  priority = 140;

  parse(dateString, token, match) {
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return (
          match.era(dateString, { width: "abbreviated" }) ||
          match.era(dateString, { width: "narrow" })
        );

      // A, B
      case "GGGGG":
        return match.era(dateString, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return (
          match.era(dateString, { width: "wide" }) ||
          match.era(dateString, { width: "abbreviated" }) ||
          match.era(dateString, { width: "narrow" })
        );
    }
  }

  set(date, flags, value) {
    flags.era = value;
    date.setFullYear(value, 0, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["R", "u", "t", "T"];
}

const numericPatterns = {
  month: /^(1[0-2]|0?\d)/, // 0 to 12
  date: /^(3[0-1]|[0-2]?\d)/, // 0 to 31
  dayOfYear: /^(36[0-6]|3[0-5]\d|[0-2]?\d?\d)/, // 0 to 366
  week: /^(5[0-3]|[0-4]?\d)/, // 0 to 53
  hour23h: /^(2[0-3]|[0-1]?\d)/, // 0 to 23
  hour24h: /^(2[0-4]|[0-1]?\d)/, // 0 to 24
  hour11h: /^(1[0-1]|0?\d)/, // 0 to 11
  hour12h: /^(1[0-2]|0?\d)/, // 0 to 12
  minute: /^[0-5]?\d/, // 0 to 59
  second: /^[0-5]?\d/, // 0 to 59

  singleDigit: /^\d/, // 0 to 9
  twoDigits: /^\d{1,2}/, // 0 to 99
  threeDigits: /^\d{1,3}/, // 0 to 999
  fourDigits: /^\d{1,4}/, // 0 to 9999

  anyDigitsSigned: /^-?\d+/,
  singleDigitSigned: /^-?\d/, // 0 to 9, -0 to -9
  twoDigitsSigned: /^-?\d{1,2}/, // 0 to 99, -0 to -99
  threeDigitsSigned: /^-?\d{1,3}/, // 0 to 999, -0 to -999
  fourDigitsSigned: /^-?\d{1,4}/, // 0 to 9999, -0 to -9999
};

const timezonePatterns = {
  basicOptionalMinutes: /^([+-])(\d{2})(\d{2})?|Z/,
  basic: /^([+-])(\d{2})(\d{2})|Z/,
  basicOptionalSeconds: /^([+-])(\d{2})(\d{2})((\d{2}))?|Z/,
  extended: /^([+-])(\d{2}):(\d{2})|Z/,
  extendedOptionalSeconds: /^([+-])(\d{2}):(\d{2})(:(\d{2}))?|Z/,
};

function mapValue(parseFnResult, mapFn) {
  if (!parseFnResult) {
    return parseFnResult;
  }

  return {
    value: mapFn(parseFnResult.value),
    rest: parseFnResult.rest,
  };
}

function parseNumericPattern(pattern, dateString) {
  const matchResult = dateString.match(pattern);

  if (!matchResult) {
    return null;
  }

  return {
    value: parseInt(matchResult[0], 10),
    rest: dateString.slice(matchResult[0].length),
  };
}

function parseTimezonePattern(pattern, dateString) {
  const matchResult = dateString.match(pattern);

  if (!matchResult) {
    return null;
  }

  // Input is 'Z'
  if (matchResult[0] === "Z") {
    return {
      value: 0,
      rest: dateString.slice(1),
    };
  }

  const sign = matchResult[1] === "+" ? 1 : -1;
  const hours = matchResult[2] ? parseInt(matchResult[2], 10) : 0;
  const minutes = matchResult[3] ? parseInt(matchResult[3], 10) : 0;
  const seconds = matchResult[5] ? parseInt(matchResult[5], 10) : 0;

  return {
    value:
      sign *
      (hours * millisecondsInHour +
        minutes * millisecondsInMinute +
        seconds * millisecondsInSecond),
    rest: dateString.slice(matchResult[0].length),
  };
}

function parseAnyDigitsSigned(dateString) {
  return parseNumericPattern(numericPatterns.anyDigitsSigned, dateString);
}

function parseNDigits(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigit, dateString);
    case 2:
      return parseNumericPattern(numericPatterns.twoDigits, dateString);
    case 3:
      return parseNumericPattern(numericPatterns.threeDigits, dateString);
    case 4:
      return parseNumericPattern(numericPatterns.fourDigits, dateString);
    default:
      return parseNumericPattern(new RegExp("^\\d{1," + n + "}"), dateString);
  }
}

function parseNDigitsSigned(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigitSigned, dateString);
    case 2:
      return parseNumericPattern(numericPatterns.twoDigitsSigned, dateString);
    case 3:
      return parseNumericPattern(numericPatterns.threeDigitsSigned, dateString);
    case 4:
      return parseNumericPattern(numericPatterns.fourDigitsSigned, dateString);
    default:
      return parseNumericPattern(new RegExp("^-?\\d{1," + n + "}"), dateString);
  }
}

function dayPeriodEnumToHours(dayPeriod) {
  switch (dayPeriod) {
    case "morning":
      return 4;
    case "evening":
      return 17;
    case "pm":
    case "noon":
    case "afternoon":
      return 12;
    case "am":
    case "midnight":
    case "night":
    default:
      return 0;
  }
}

function normalizeTwoDigitYear(twoDigitYear, currentYear) {
  const isCommonEra = currentYear > 0;
  // Absolute number of the current year:
  // 1 -> 1 AC
  // 0 -> 1 BC
  // -1 -> 2 BC
  const absCurrentYear = isCommonEra ? currentYear : 1 - currentYear;

  let result;
  if (absCurrentYear <= 50) {
    result = twoDigitYear || 100;
  } else {
    const rangeEnd = absCurrentYear + 50;
    const rangeEndCentury = Math.trunc(rangeEnd / 100) * 100;
    const isPreviousCentury = twoDigitYear >= rangeEnd % 100;
    result = twoDigitYear + rangeEndCentury - (isPreviousCentury ? 100 : 0);
  }

  return isCommonEra ? result : 1 - result;
}

function isLeapYearIndex$1(year) {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}

// From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_Patterns
// | Year     |     y | yy |   yyy |  yyyy | yyyyy |
// |----------|-------|----|-------|-------|-------|
// | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
// | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
// | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
// | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
// | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
class YearParser extends Parser {
  priority = 130;
  incompatibleTokens = ["Y", "R", "u", "w", "I", "i", "e", "c", "t", "T"];

  parse(dateString, token, match) {
    const valueCallback = (year) => ({
      year,
      isTwoDigitYear: token === "yy",
    });

    switch (token) {
      case "y":
        return mapValue(parseNDigits(4, dateString), valueCallback);
      case "yo":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "year",
          }),
          valueCallback,
        );
      default:
        return mapValue(parseNDigits(token.length, dateString), valueCallback);
    }
  }

  validate(_date, value) {
    return value.isTwoDigitYear || value.year > 0;
  }

  set(date, flags, value) {
    const currentYear = date.getFullYear();

    if (value.isTwoDigitYear) {
      const normalizedTwoDigitYear = normalizeTwoDigitYear(
        value.year,
        currentYear,
      );
      date.setFullYear(normalizedTwoDigitYear, 0, 1);
      date.setHours(0, 0, 0, 0);
      return date;
    }

    const year =
      !("era" in flags) || flags.era === 1 ? value.year : 1 - value.year;
    date.setFullYear(year, 0, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}

// Local week-numbering year
class LocalWeekYearParser extends Parser {
  priority = 130;

  parse(dateString, token, match) {
    const valueCallback = (year) => ({
      year,
      isTwoDigitYear: token === "YY",
    });

    switch (token) {
      case "Y":
        return mapValue(parseNDigits(4, dateString), valueCallback);
      case "Yo":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "year",
          }),
          valueCallback,
        );
      default:
        return mapValue(parseNDigits(token.length, dateString), valueCallback);
    }
  }

  validate(_date, value) {
    return value.isTwoDigitYear || value.year > 0;
  }

  set(date, flags, value, options) {
    const currentYear = getWeekYear$1(date, options);

    if (value.isTwoDigitYear) {
      const normalizedTwoDigitYear = normalizeTwoDigitYear(
        value.year,
        currentYear,
      );
      date.setFullYear(
        normalizedTwoDigitYear,
        0,
        options.firstWeekContainsDate,
      );
      date.setHours(0, 0, 0, 0);
      return startOfWeek(date, options);
    }

    const year =
      !("era" in flags) || flags.era === 1 ? value.year : 1 - value.year;
    date.setFullYear(year, 0, options.firstWeekContainsDate);
    date.setHours(0, 0, 0, 0);
    return startOfWeek(date, options);
  }

  incompatibleTokens = [
    "y",
    "R",
    "u",
    "Q",
    "q",
    "M",
    "L",
    "I",
    "d",
    "D",
    "i",
    "t",
    "T",
  ];
}

// ISO week-numbering year
class ISOWeekYearParser extends Parser {
  priority = 130;

  parse(dateString, token) {
    if (token === "R") {
      return parseNDigitsSigned(4, dateString);
    }

    return parseNDigitsSigned(token.length, dateString);
  }

  set(date, _flags, value) {
    const firstWeekOfYear = constructFrom(date, 0);
    firstWeekOfYear.setFullYear(value, 0, 4);
    firstWeekOfYear.setHours(0, 0, 0, 0);
    return startOfISOWeek$1(firstWeekOfYear);
  }

  incompatibleTokens = [
    "G",
    "y",
    "Y",
    "u",
    "Q",
    "q",
    "M",
    "L",
    "w",
    "d",
    "D",
    "e",
    "c",
    "t",
    "T",
  ];
}

class ExtendedYearParser extends Parser {
  priority = 130;

  parse(dateString, token) {
    if (token === "u") {
      return parseNDigitsSigned(4, dateString);
    }

    return parseNDigitsSigned(token.length, dateString);
  }

  set(date, _flags, value) {
    date.setFullYear(value, 0, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["G", "y", "Y", "R", "w", "I", "i", "e", "c", "t", "T"];
}

class QuarterParser extends Parser {
  priority = 120;

  parse(dateString, token, match) {
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
      case "QQ": // 01, 02, 03, 04
        return parseNDigits(token.length, dateString);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return match.ordinalNumber(dateString, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return (
          match.quarter(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.quarter(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );

      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return match.quarter(dateString, {
          width: "narrow",
          context: "formatting",
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return (
          match.quarter(dateString, {
            width: "wide",
            context: "formatting",
          }) ||
          match.quarter(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.quarter(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 4;
  }

  set(date, _flags, value) {
    date.setMonth((value - 1) * 3, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "Y",
    "R",
    "q",
    "M",
    "L",
    "w",
    "I",
    "d",
    "D",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];
}

class StandAloneQuarterParser extends Parser {
  priority = 120;

  parse(dateString, token, match) {
    switch (token) {
      // 1, 2, 3, 4
      case "q":
      case "qq": // 01, 02, 03, 04
        return parseNDigits(token.length, dateString);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return match.ordinalNumber(dateString, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return (
          match.quarter(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.quarter(dateString, {
            width: "narrow",
            context: "standalone",
          })
        );

      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return match.quarter(dateString, {
          width: "narrow",
          context: "standalone",
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return (
          match.quarter(dateString, {
            width: "wide",
            context: "standalone",
          }) ||
          match.quarter(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.quarter(dateString, {
            width: "narrow",
            context: "standalone",
          })
        );
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 4;
  }

  set(date, _flags, value) {
    date.setMonth((value - 1) * 3, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "Y",
    "R",
    "Q",
    "M",
    "L",
    "w",
    "I",
    "d",
    "D",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];
}

class MonthParser extends Parser {
  incompatibleTokens = [
    "Y",
    "R",
    "q",
    "Q",
    "L",
    "w",
    "I",
    "D",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];

  priority = 110;

  parse(dateString, token, match) {
    const valueCallback = (value) => value - 1;

    switch (token) {
      // 1, 2, ..., 12
      case "M":
        return mapValue(
          parseNumericPattern(numericPatterns.month, dateString),
          valueCallback,
        );
      // 01, 02, ..., 12
      case "MM":
        return mapValue(parseNDigits(2, dateString), valueCallback);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "month",
          }),
          valueCallback,
        );
      // Jan, Feb, ..., Dec
      case "MMM":
        return (
          match.month(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.month(dateString, { width: "narrow", context: "formatting" })
        );

      // J, F, ..., D
      case "MMMMM":
        return match.month(dateString, {
          width: "narrow",
          context: "formatting",
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return (
          match.month(dateString, { width: "wide", context: "formatting" }) ||
          match.month(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.month(dateString, { width: "narrow", context: "formatting" })
        );
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 11;
  }

  set(date, _flags, value) {
    date.setMonth(value, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}

class StandAloneMonthParser extends Parser {
  priority = 110;

  parse(dateString, token, match) {
    const valueCallback = (value) => value - 1;

    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return mapValue(
          parseNumericPattern(numericPatterns.month, dateString),
          valueCallback,
        );
      // 01, 02, ..., 12
      case "LL":
        return mapValue(parseNDigits(2, dateString), valueCallback);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "month",
          }),
          valueCallback,
        );
      // Jan, Feb, ..., Dec
      case "LLL":
        return (
          match.month(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.month(dateString, { width: "narrow", context: "standalone" })
        );

      // J, F, ..., D
      case "LLLLL":
        return match.month(dateString, {
          width: "narrow",
          context: "standalone",
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return (
          match.month(dateString, { width: "wide", context: "standalone" }) ||
          match.month(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.month(dateString, { width: "narrow", context: "standalone" })
        );
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 11;
  }

  set(date, _flags, value) {
    date.setMonth(value, 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "Y",
    "R",
    "q",
    "Q",
    "M",
    "w",
    "I",
    "D",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];
}

/**
 * The {@link setWeek} function options.
 */

/**
 * @name setWeek
 * @category Week Helpers
 * @summary Set the local week to the given date.
 *
 * @description
 * Set the local week to the given date, saving the weekday number.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param week - The week of the new date
 * @param options - An object with options
 *
 * @returns The new date with the local week set
 *
 * @example
 * // Set the 1st week to 2 January 2005 with default options:
 * const result = setWeek(new Date(2005, 0, 2), 1)
 * //=> Sun Dec 26 2004 00:00:00
 *
 * @example
 * // Set the 1st week to 2 January 2005,
 * // if Monday is the first day of the week,
 * // and the first week of the year always contains 4 January:
 * const result = setWeek(new Date(2005, 0, 2), 1, {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> Sun Jan 4 2004 00:00:00
 */
function setWeek(date, week, options) {
  const _date = toDate(date);
  const diff = getWeek$2(_date, options) - week;
  _date.setDate(_date.getDate() - diff * 7);
  return _date;
}

// Local week of year
class LocalWeekParser extends Parser {
  priority = 100;

  parse(dateString, token, match) {
    switch (token) {
      case "w":
        return parseNumericPattern(numericPatterns.week, dateString);
      case "wo":
        return match.ordinalNumber(dateString, { unit: "week" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 53;
  }

  set(date, _flags, value, options) {
    return startOfWeek(setWeek(date, value, options), options);
  }

  incompatibleTokens = [
    "y",
    "R",
    "u",
    "q",
    "Q",
    "M",
    "L",
    "I",
    "d",
    "D",
    "i",
    "t",
    "T",
  ];
}

/**
 * @name setISOWeek
 * @category ISO Week Helpers
 * @summary Set the ISO week to the given date.
 *
 * @description
 * Set the ISO week to the given date, saving the weekday number.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param week - The ISO week of the new date
 *
 * @returns The new date with the ISO week set
 *
 * @example
 * // Set the 53rd ISO week to 7 August 2004:
 * const result = setISOWeek(new Date(2004, 7, 7), 53)
 * //=> Sat Jan 01 2005 00:00:00
 */
function setISOWeek(date, week) {
  const _date = toDate(date);
  const diff = getISOWeek$1(_date) - week;
  _date.setDate(_date.getDate() - diff * 7);
  return _date;
}

// ISO week of year
class ISOWeekParser extends Parser {
  priority = 100;

  parse(dateString, token, match) {
    switch (token) {
      case "I":
        return parseNumericPattern(numericPatterns.week, dateString);
      case "Io":
        return match.ordinalNumber(dateString, { unit: "week" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 53;
  }

  set(date, _flags, value) {
    return startOfISOWeek$1(setISOWeek(date, value));
  }

  incompatibleTokens = [
    "y",
    "Y",
    "u",
    "q",
    "Q",
    "M",
    "L",
    "w",
    "d",
    "D",
    "e",
    "c",
    "t",
    "T",
  ];
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DAYS_IN_MONTH_LEAP_YEAR = [
  31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
];

// Day of the month
class DateParser extends Parser {
  priority = 90;
  subPriority = 1;

  parse(dateString, token, match) {
    switch (token) {
      case "d":
        return parseNumericPattern(numericPatterns.date, dateString);
      case "do":
        return match.ordinalNumber(dateString, { unit: "date" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(date, value) {
    const year = date.getFullYear();
    const isLeapYear = isLeapYearIndex$1(year);
    const month = date.getMonth();
    if (isLeapYear) {
      return value >= 1 && value <= DAYS_IN_MONTH_LEAP_YEAR[month];
    } else {
      return value >= 1 && value <= DAYS_IN_MONTH[month];
    }
  }

  set(date, _flags, value) {
    date.setDate(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "Y",
    "R",
    "q",
    "Q",
    "w",
    "I",
    "D",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];
}

class DayOfYearParser extends Parser {
  priority = 90;

  subpriority = 1;

  parse(dateString, token, match) {
    switch (token) {
      case "D":
      case "DD":
        return parseNumericPattern(numericPatterns.dayOfYear, dateString);
      case "Do":
        return match.ordinalNumber(dateString, { unit: "date" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(date, value) {
    const year = date.getFullYear();
    const isLeapYear = isLeapYearIndex$1(year);
    if (isLeapYear) {
      return value >= 1 && value <= 366;
    } else {
      return value >= 1 && value <= 365;
    }
  }

  set(date, _flags, value) {
    date.setMonth(0, value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "Y",
    "R",
    "q",
    "Q",
    "M",
    "L",
    "w",
    "I",
    "d",
    "E",
    "i",
    "e",
    "c",
    "t",
    "T",
  ];
}

/**
 * The {@link setDay} function options.
 */

/**
 * @name setDay
 * @category Weekday Helpers
 * @summary Set the day of the week to the given date.
 *
 * @description
 * Set the day of the week to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param day - The day of the week of the new date
 * @param options - An object with options.
 *
 * @returns The new date with the day of the week set
 *
 * @example
 * // Set week day to Sunday, with the default weekStartsOn of Sunday:
 * const result = setDay(new Date(2014, 8, 1), 0)
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // Set week day to Sunday, with a weekStartsOn of Monday:
 * const result = setDay(new Date(2014, 8, 1), 0, { weekStartsOn: 1 })
 * //=> Sun Sep 07 2014 00:00:00
 */
function setDay(date, day, options) {
  const defaultOptions = getDefaultOptions$1();
  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate(date);
  const currentDay = _date.getDay();

  const remainder = day % 7;
  const dayIndex = (remainder + 7) % 7;

  const delta = 7 - weekStartsOn;
  const diff =
    day < 0 || day > 6
      ? day - ((currentDay + delta) % 7)
      : ((dayIndex + delta) % 7) - ((currentDay + delta) % 7);
  return addDays(_date, diff);
}

// Day of week
class DayParser extends Parser {
  priority = 90;

  parse(dateString, token, match) {
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return (
          match.day(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );

      // T
      case "EEEEE":
        return match.day(dateString, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "EEEEEE":
        return (
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );

      // Tuesday
      case "EEEE":
      default:
        return (
          match.day(dateString, { width: "wide", context: "formatting" }) ||
          match.day(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 6;
  }

  set(date, _flags, value, options) {
    date = setDay(date, value, options);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["D", "i", "e", "c", "t", "T"];
}

// Local day of week
class LocalDayParser extends Parser {
  priority = 90;
  parse(dateString, token, match, options) {
    const valueCallback = (value) => {
      // We want here floor instead of trunc, so we get -7 for value 0 instead of 0
      const wholeWeekDays = Math.floor((value - 1) / 7) * 7;
      return ((value + options.weekStartsOn + 6) % 7) + wholeWeekDays;
    };

    switch (token) {
      // 3
      case "e":
      case "ee": // 03
        return mapValue(parseNDigits(token.length, dateString), valueCallback);
      // 3rd
      case "eo":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "day",
          }),
          valueCallback,
        );
      // Tue
      case "eee":
        return (
          match.day(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );

      // T
      case "eeeee":
        return match.day(dateString, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "eeeeee":
        return (
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );

      // Tuesday
      case "eeee":
      default:
        return (
          match.day(dateString, { width: "wide", context: "formatting" }) ||
          match.day(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.day(dateString, { width: "short", context: "formatting" }) ||
          match.day(dateString, { width: "narrow", context: "formatting" })
        );
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 6;
  }

  set(date, _flags, value, options) {
    date = setDay(date, value, options);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "y",
    "R",
    "u",
    "q",
    "Q",
    "M",
    "L",
    "I",
    "d",
    "D",
    "E",
    "i",
    "c",
    "t",
    "T",
  ];
}

// Stand-alone local day of week
class StandAloneLocalDayParser extends Parser {
  priority = 90;

  parse(dateString, token, match, options) {
    const valueCallback = (value) => {
      // We want here floor instead of trunc, so we get -7 for value 0 instead of 0
      const wholeWeekDays = Math.floor((value - 1) / 7) * 7;
      return ((value + options.weekStartsOn + 6) % 7) + wholeWeekDays;
    };

    switch (token) {
      // 3
      case "c":
      case "cc": // 03
        return mapValue(parseNDigits(token.length, dateString), valueCallback);
      // 3rd
      case "co":
        return mapValue(
          match.ordinalNumber(dateString, {
            unit: "day",
          }),
          valueCallback,
        );
      // Tue
      case "ccc":
        return (
          match.day(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.day(dateString, { width: "short", context: "standalone" }) ||
          match.day(dateString, { width: "narrow", context: "standalone" })
        );

      // T
      case "ccccc":
        return match.day(dateString, {
          width: "narrow",
          context: "standalone",
        });
      // Tu
      case "cccccc":
        return (
          match.day(dateString, { width: "short", context: "standalone" }) ||
          match.day(dateString, { width: "narrow", context: "standalone" })
        );

      // Tuesday
      case "cccc":
      default:
        return (
          match.day(dateString, { width: "wide", context: "standalone" }) ||
          match.day(dateString, {
            width: "abbreviated",
            context: "standalone",
          }) ||
          match.day(dateString, { width: "short", context: "standalone" }) ||
          match.day(dateString, { width: "narrow", context: "standalone" })
        );
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 6;
  }

  set(date, _flags, value, options) {
    date = setDay(date, value, options);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "y",
    "R",
    "u",
    "q",
    "Q",
    "M",
    "L",
    "I",
    "d",
    "D",
    "E",
    "i",
    "e",
    "t",
    "T",
  ];
}

/**
 * @name setISODay
 * @category Weekday Helpers
 * @summary Set the day of the ISO week to the given date.
 *
 * @description
 * Set the day of the ISO week to the given date.
 * ISO week starts with Monday.
 * 7 is the index of Sunday, 1 is the index of Monday etc.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param day - The day of the ISO week of the new date
 *
 * @returns The new date with the day of the ISO week set
 *
 * @example
 * // Set Sunday to 1 September 2014:
 * const result = setISODay(new Date(2014, 8, 1), 7)
 * //=> Sun Sep 07 2014 00:00:00
 */
function setISODay(date, day) {
  const _date = toDate(date);
  const currentDay = getISODay(_date);
  const diff = day - currentDay;
  return addDays(_date, diff);
}

// ISO day of week
class ISODayParser extends Parser {
  priority = 90;

  parse(dateString, token, match) {
    const valueCallback = (value) => {
      if (value === 0) {
        return 7;
      }
      return value;
    };

    switch (token) {
      // 2
      case "i":
      case "ii": // 02
        return parseNDigits(token.length, dateString);
      // 2nd
      case "io":
        return match.ordinalNumber(dateString, { unit: "day" });
      // Tue
      case "iii":
        return mapValue(
          match.day(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
            match.day(dateString, {
              width: "short",
              context: "formatting",
            }) ||
            match.day(dateString, {
              width: "narrow",
              context: "formatting",
            }),
          valueCallback,
        );
      // T
      case "iiiii":
        return mapValue(
          match.day(dateString, {
            width: "narrow",
            context: "formatting",
          }),
          valueCallback,
        );
      // Tu
      case "iiiiii":
        return mapValue(
          match.day(dateString, {
            width: "short",
            context: "formatting",
          }) ||
            match.day(dateString, {
              width: "narrow",
              context: "formatting",
            }),
          valueCallback,
        );
      // Tuesday
      case "iiii":
      default:
        return mapValue(
          match.day(dateString, {
            width: "wide",
            context: "formatting",
          }) ||
            match.day(dateString, {
              width: "abbreviated",
              context: "formatting",
            }) ||
            match.day(dateString, {
              width: "short",
              context: "formatting",
            }) ||
            match.day(dateString, {
              width: "narrow",
              context: "formatting",
            }),
          valueCallback,
        );
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 7;
  }

  set(date, _flags, value) {
    date = setISODay(date, value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  incompatibleTokens = [
    "y",
    "Y",
    "u",
    "q",
    "Q",
    "M",
    "L",
    "w",
    "d",
    "D",
    "E",
    "e",
    "c",
    "t",
    "T",
  ];
}

class AMPMParser extends Parser {
  priority = 80;

  parse(dateString, token, match) {
    switch (token) {
      case "a":
      case "aa":
      case "aaa":
        return (
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );

      case "aaaaa":
        return match.dayPeriod(dateString, {
          width: "narrow",
          context: "formatting",
        });
      case "aaaa":
      default:
        return (
          match.dayPeriod(dateString, {
            width: "wide",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );
    }
  }

  set(date, _flags, value) {
    date.setHours(dayPeriodEnumToHours(value), 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["b", "B", "H", "k", "t", "T"];
}

class AMPMMidnightParser extends Parser {
  priority = 80;

  parse(dateString, token, match) {
    switch (token) {
      case "b":
      case "bb":
      case "bbb":
        return (
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );

      case "bbbbb":
        return match.dayPeriod(dateString, {
          width: "narrow",
          context: "formatting",
        });
      case "bbbb":
      default:
        return (
          match.dayPeriod(dateString, {
            width: "wide",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );
    }
  }

  set(date, _flags, value) {
    date.setHours(dayPeriodEnumToHours(value), 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["a", "B", "H", "k", "t", "T"];
}

// in the morning, in the afternoon, in the evening, at night
class DayPeriodParser extends Parser {
  priority = 80;

  parse(dateString, token, match) {
    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return (
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );

      case "BBBBB":
        return match.dayPeriod(dateString, {
          width: "narrow",
          context: "formatting",
        });
      case "BBBB":
      default:
        return (
          match.dayPeriod(dateString, {
            width: "wide",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "abbreviated",
            context: "formatting",
          }) ||
          match.dayPeriod(dateString, {
            width: "narrow",
            context: "formatting",
          })
        );
    }
  }

  set(date, _flags, value) {
    date.setHours(dayPeriodEnumToHours(value), 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["a", "b", "t", "T"];
}

class Hour1to12Parser extends Parser {
  priority = 70;

  parse(dateString, token, match) {
    switch (token) {
      case "h":
        return parseNumericPattern(numericPatterns.hour12h, dateString);
      case "ho":
        return match.ordinalNumber(dateString, { unit: "hour" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 12;
  }

  set(date, _flags, value) {
    const isPM = date.getHours() >= 12;
    if (isPM && value < 12) {
      date.setHours(value + 12, 0, 0, 0);
    } else if (!isPM && value === 12) {
      date.setHours(0, 0, 0, 0);
    } else {
      date.setHours(value, 0, 0, 0);
    }
    return date;
  }

  incompatibleTokens = ["H", "K", "k", "t", "T"];
}

class Hour0to23Parser extends Parser {
  priority = 70;

  parse(dateString, token, match) {
    switch (token) {
      case "H":
        return parseNumericPattern(numericPatterns.hour23h, dateString);
      case "Ho":
        return match.ordinalNumber(dateString, { unit: "hour" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 23;
  }

  set(date, _flags, value) {
    date.setHours(value, 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["a", "b", "h", "K", "k", "t", "T"];
}

class Hour0To11Parser extends Parser {
  priority = 70;

  parse(dateString, token, match) {
    switch (token) {
      case "K":
        return parseNumericPattern(numericPatterns.hour11h, dateString);
      case "Ko":
        return match.ordinalNumber(dateString, { unit: "hour" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 11;
  }

  set(date, _flags, value) {
    const isPM = date.getHours() >= 12;
    if (isPM && value < 12) {
      date.setHours(value + 12, 0, 0, 0);
    } else {
      date.setHours(value, 0, 0, 0);
    }
    return date;
  }

  incompatibleTokens = ["h", "H", "k", "t", "T"];
}

class Hour1To24Parser extends Parser {
  priority = 70;

  parse(dateString, token, match) {
    switch (token) {
      case "k":
        return parseNumericPattern(numericPatterns.hour24h, dateString);
      case "ko":
        return match.ordinalNumber(dateString, { unit: "hour" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 1 && value <= 24;
  }

  set(date, _flags, value) {
    const hours = value <= 24 ? value % 24 : value;
    date.setHours(hours, 0, 0, 0);
    return date;
  }

  incompatibleTokens = ["a", "b", "h", "H", "K", "t", "T"];
}

class MinuteParser extends Parser {
  priority = 60;

  parse(dateString, token, match) {
    switch (token) {
      case "m":
        return parseNumericPattern(numericPatterns.minute, dateString);
      case "mo":
        return match.ordinalNumber(dateString, { unit: "minute" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 59;
  }

  set(date, _flags, value) {
    date.setMinutes(value, 0, 0);
    return date;
  }

  incompatibleTokens = ["t", "T"];
}

class SecondParser extends Parser {
  priority = 50;

  parse(dateString, token, match) {
    switch (token) {
      case "s":
        return parseNumericPattern(numericPatterns.second, dateString);
      case "so":
        return match.ordinalNumber(dateString, { unit: "second" });
      default:
        return parseNDigits(token.length, dateString);
    }
  }

  validate(_date, value) {
    return value >= 0 && value <= 59;
  }

  set(date, _flags, value) {
    date.setSeconds(value, 0);
    return date;
  }

  incompatibleTokens = ["t", "T"];
}

class FractionOfSecondParser extends Parser {
  priority = 30;

  parse(dateString, token) {
    const valueCallback = (value) =>
      Math.trunc(value * Math.pow(10, -token.length + 3));
    return mapValue(parseNDigits(token.length, dateString), valueCallback);
  }

  set(date, _flags, value) {
    date.setMilliseconds(value);
    return date;
  }

  incompatibleTokens = ["t", "T"];
}

// Timezone (ISO-8601. +00:00 is `'Z'`)
class ISOTimezoneWithZParser extends Parser {
  priority = 10;

  parse(dateString, token) {
    switch (token) {
      case "X":
        return parseTimezonePattern(
          timezonePatterns.basicOptionalMinutes,
          dateString,
        );
      case "XX":
        return parseTimezonePattern(timezonePatterns.basic, dateString);
      case "XXXX":
        return parseTimezonePattern(
          timezonePatterns.basicOptionalSeconds,
          dateString,
        );
      case "XXXXX":
        return parseTimezonePattern(
          timezonePatterns.extendedOptionalSeconds,
          dateString,
        );
      case "XXX":
      default:
        return parseTimezonePattern(timezonePatterns.extended, dateString);
    }
  }

  set(date, flags, value) {
    if (flags.timestampIsSet) return date;
    return constructFrom(
      date,
      date.getTime() - getTimezoneOffsetInMilliseconds$1(date) - value,
    );
  }

  incompatibleTokens = ["t", "T", "x"];
}

// Timezone (ISO-8601)
class ISOTimezoneParser extends Parser {
  priority = 10;

  parse(dateString, token) {
    switch (token) {
      case "x":
        return parseTimezonePattern(
          timezonePatterns.basicOptionalMinutes,
          dateString,
        );
      case "xx":
        return parseTimezonePattern(timezonePatterns.basic, dateString);
      case "xxxx":
        return parseTimezonePattern(
          timezonePatterns.basicOptionalSeconds,
          dateString,
        );
      case "xxxxx":
        return parseTimezonePattern(
          timezonePatterns.extendedOptionalSeconds,
          dateString,
        );
      case "xxx":
      default:
        return parseTimezonePattern(timezonePatterns.extended, dateString);
    }
  }

  set(date, flags, value) {
    if (flags.timestampIsSet) return date;
    return constructFrom(
      date,
      date.getTime() - getTimezoneOffsetInMilliseconds$1(date) - value,
    );
  }

  incompatibleTokens = ["t", "T", "X"];
}

class TimestampSecondsParser extends Parser {
  priority = 40;

  parse(dateString) {
    return parseAnyDigitsSigned(dateString);
  }

  set(date, _flags, value) {
    return [constructFrom(date, value * 1000), { timestampIsSet: true }];
  }

  incompatibleTokens = "*";
}

class TimestampMillisecondsParser extends Parser {
  priority = 20;

  parse(dateString) {
    return parseAnyDigitsSigned(dateString);
  }

  set(date, _flags, value) {
    return [constructFrom(date, value), { timestampIsSet: true }];
  }

  incompatibleTokens = "*";
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O* | Timezone (GMT)                 |
 * |  p  |                                |  P  |                                |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z* | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `parse` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- It's ok, we want any here
const parsers = {
  G: new EraParser(),
  y: new YearParser(),
  Y: new LocalWeekYearParser(),
  R: new ISOWeekYearParser(),
  u: new ExtendedYearParser(),
  Q: new QuarterParser(),
  q: new StandAloneQuarterParser(),
  M: new MonthParser(),
  L: new StandAloneMonthParser(),
  w: new LocalWeekParser(),
  I: new ISOWeekParser(),
  d: new DateParser(),
  D: new DayOfYearParser(),
  E: new DayParser(),
  e: new LocalDayParser(),
  c: new StandAloneLocalDayParser(),
  i: new ISODayParser(),
  a: new AMPMParser(),
  b: new AMPMMidnightParser(),
  B: new DayPeriodParser(),
  h: new Hour1to12Parser(),
  H: new Hour0to23Parser(),
  K: new Hour0To11Parser(),
  k: new Hour1To24Parser(),
  m: new MinuteParser(),
  s: new SecondParser(),
  S: new FractionOfSecondParser(),
  X: new ISOTimezoneWithZParser(),
  x: new ISOTimezoneParser(),
  t: new TimestampSecondsParser(),
  T: new TimestampMillisecondsParser(),
};

/**
 * The {@link parse} function options.
 */

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
const formattingTokensRegExp$1 =
  /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
const longFormattingTokensRegExp$2 = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

const escapedStringRegExp$1 = /^'([^]*?)'?$/;
const doubleQuoteRegExp$1 = /''/g;

const notWhitespaceRegExp = /\S/;
const unescapedLatinCharacterRegExp$1 = /[a-zA-Z]/;

/**
 * @name parse
 * @category Common Helpers
 * @summary Parse the date.
 *
 * @description
 * Return the date parsed from string using the given format string.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters in the format string wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 *
 * Format of the format string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 5 below the table).
 *
 * Not all tokens are compatible. Combinations that don't make sense or could lead to bugs are prohibited
 * and will throw `RangeError`. For example usage of 24-hour format token with AM/PM token will throw an exception:
 *
 * ```javascript
 * parse('23 AM', 'HH a', new Date())
 * //=> RangeError: The format string mustn't contain `HH` and `a` at the same time
 * ```
 *
 * See the compatibility table: https://docs.google.com/spreadsheets/d/e/2PACX-1vQOPU3xUhplll6dyoMmVUXHKl_8CRDs6_ueLmex3SoqwhuolkuN3O05l4rqx5h1dKX8eb46Ul-CCSrq/pubhtml?gid=0&single=true
 *
 * Accepted format string patterns:
 * | Unit                            |Prior| Pattern | Result examples                   | Notes |
 * |---------------------------------|-----|---------|-----------------------------------|-------|
 * | Era                             | 140 | G..GGG  | AD, BC                            |       |
 * |                                 |     | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 |     | GGGGG   | A, B                              |       |
 * | Calendar year                   | 130 | y       | 44, 1, 1900, 2017, 9999           | 4     |
 * |                                 |     | yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | yy      | 44, 01, 00, 17                    | 4     |
 * |                                 |     | yyy     | 044, 001, 123, 999                | 4     |
 * |                                 |     | yyyy    | 0044, 0001, 1900, 2017            | 4     |
 * |                                 |     | yyyyy   | ...                               | 2,4   |
 * | Local week-numbering year       | 130 | Y       | 44, 1, 1900, 2017, 9000           | 4     |
 * |                                 |     | Yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | YY      | 44, 01, 00, 17                    | 4,6   |
 * |                                 |     | YYY     | 044, 001, 123, 999                | 4     |
 * |                                 |     | YYYY    | 0044, 0001, 1900, 2017            | 4,6   |
 * |                                 |     | YYYYY   | ...                               | 2,4   |
 * | ISO week-numbering year         | 130 | R       | -43, 1, 1900, 2017, 9999, -9999   | 4,5   |
 * |                                 |     | RR      | -43, 01, 00, 17                   | 4,5   |
 * |                                 |     | RRR     | -043, 001, 123, 999, -999         | 4,5   |
 * |                                 |     | RRRR    | -0043, 0001, 2017, 9999, -9999    | 4,5   |
 * |                                 |     | RRRRR   | ...                               | 2,4,5 |
 * | Extended year                   | 130 | u       | -43, 1, 1900, 2017, 9999, -999    | 4     |
 * |                                 |     | uu      | -43, 01, 99, -99                  | 4     |
 * |                                 |     | uuu     | -043, 001, 123, 999, -999         | 4     |
 * |                                 |     | uuuu    | -0043, 0001, 2017, 9999, -9999    | 4     |
 * |                                 |     | uuuuu   | ...                               | 2,4   |
 * | Quarter (formatting)            | 120 | Q       | 1, 2, 3, 4                        |       |
 * |                                 |     | Qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | QQ      | 01, 02, 03, 04                    |       |
 * |                                 |     | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | 120 | q       | 1, 2, 3, 4                        |       |
 * |                                 |     | qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | qq      | 01, 02, 03, 04                    |       |
 * |                                 |     | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | qqqqq   | 1, 2, 3, 4                        | 3     |
 * | Month (formatting)              | 110 | M       | 1, 2, ..., 12                     |       |
 * |                                 |     | Mo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | MM      | 01, 02, ..., 12                   |       |
 * |                                 |     | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | MMMM    | January, February, ..., December  | 2     |
 * |                                 |     | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | 110 | L       | 1, 2, ..., 12                     |       |
 * |                                 |     | Lo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | LL      | 01, 02, ..., 12                   |       |
 * |                                 |     | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | LLLL    | January, February, ..., December  | 2     |
 * |                                 |     | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | 100 | w       | 1, 2, ..., 53                     |       |
 * |                                 |     | wo      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | 100 | I       | 1, 2, ..., 53                     | 5     |
 * |                                 |     | Io      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | II      | 01, 02, ..., 53                   | 5     |
 * | Day of month                    |  90 | d       | 1, 2, ..., 31                     |       |
 * |                                 |     | do      | 1st, 2nd, ..., 31st               | 5     |
 * |                                 |     | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     |  90 | D       | 1, 2, ..., 365, 366               | 7     |
 * |                                 |     | Do      | 1st, 2nd, ..., 365th, 366th       | 5     |
 * |                                 |     | DD      | 01, 02, ..., 365, 366             | 7     |
 * |                                 |     | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 |     | DDDD    | ...                               | 2     |
 * | Day of week (formatting)        |  90 | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 |     | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    |  90 | i       | 1, 2, 3, ..., 7                   | 5     |
 * |                                 |     | io      | 1st, 2nd, ..., 7th                | 5     |
 * |                                 |     | ii      | 01, 02, ..., 07                   | 5     |
 * |                                 |     | iii     | Mon, Tue, Wed, ..., Sun           | 5     |
 * |                                 |     | iiii    | Monday, Tuesday, ..., Sunday      | 2,5   |
 * |                                 |     | iiiii   | M, T, W, T, F, S, S               | 5     |
 * |                                 |     | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 5     |
 * | Local day of week (formatting)  |  90 | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | eo      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | ee      | 02, 03, ..., 01                   |       |
 * |                                 |     | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 |     | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) |  90 | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | co      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | cc      | 02, 03, ..., 01                   |       |
 * |                                 |     | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 |     | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          |  80 | a..aaa  | AM, PM                            |       |
 * |                                 |     | aaaa    | a.m., p.m.                        | 2     |
 * |                                 |     | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          |  80 | b..bbb  | AM, PM, noon, midnight            |       |
 * |                                 |     | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 |     | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             |  80 | B..BBB  | at night, in the morning, ...     |       |
 * |                                 |     | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 |     | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     |  70 | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 |     | ho      | 1st, 2nd, ..., 11th, 12th         | 5     |
 * |                                 |     | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     |  70 | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 |     | Ho      | 0th, 1st, 2nd, ..., 23rd          | 5     |
 * |                                 |     | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     |  70 | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 |     | Ko      | 1st, 2nd, ..., 11th, 0th          | 5     |
 * |                                 |     | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     |  70 | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 |     | ko      | 24th, 1st, 2nd, ..., 23rd         | 5     |
 * |                                 |     | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          |  60 | m       | 0, 1, ..., 59                     |       |
 * |                                 |     | mo      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | mm      | 00, 01, ..., 59                   |       |
 * | Second                          |  50 | s       | 0, 1, ..., 59                     |       |
 * |                                 |     | so      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | ss      | 00, 01, ..., 59                   |       |
 * | Seconds timestamp               |  40 | t       | 512969520                         |       |
 * |                                 |     | tt      | ...                               | 2     |
 * | Fraction of second              |  30 | S       | 0, 1, ..., 9                      |       |
 * |                                 |     | SS      | 00, 01, ..., 99                   |       |
 * |                                 |     | SSS     | 000, 001, ..., 999                |       |
 * |                                 |     | SSSS    | ...                               | 2     |
 * | Milliseconds timestamp          |  20 | T       | 512969520900                      |       |
 * |                                 |     | TT      | ...                               | 2     |
 * | Timezone (ISO-8601 w/ Z)        |  10 | X       | -08, +0530, Z                     |       |
 * |                                 |     | XX      | -0800, +0530, Z                   |       |
 * |                                 |     | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 |     | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 |     | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       |  10 | x       | -08, +0530, +00                   |       |
 * |                                 |     | xx      | -0800, +0530, +0000               |       |
 * |                                 |     | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 |     | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 |     | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Long localized date             |  NA | P       | 05/29/1453                        | 5,8   |
 * |                                 |     | PP      | May 29, 1453                      |       |
 * |                                 |     | PPP     | May 29th, 1453                    |       |
 * |                                 |     | PPPP    | Sunday, May 29th, 1453            | 2,5,8 |
 * | Long localized time             |  NA | p       | 12:00 AM                          | 5,8   |
 * |                                 |     | pp      | 12:00:00 AM                       |       |
 * | Combination of date and time    |  NA | Pp      | 05/29/1453, 12:00 AM              |       |
 * |                                 |     | PPpp    | May 29, 1453, 12:00:00 AM         |       |
 * |                                 |     | PPPpp   | May 29th, 1453 at ...             |       |
 * |                                 |     | PPPPpp  | Sunday, May 29th, 1453 at ...     | 2,5,8 |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular.
 *    In `format` function, they will produce different result:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 *    `parse` will try to match both formatting and stand-alone units interchangably.
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table:
 *    - for numerical units (`yyyyyyyy`) `parse` will try to match a number
 *      as wide as the sequence
 *    - for text units (`MMMMMMMM`) `parse` will try to match the widest variation of the unit.
 *      These variations are marked with "2" in the last column of the table.
 *
 * 3. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 4. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` will try to guess the century of two digit year by proximity with `referenceDate`:
 *
 *    `parse('50', 'yy', new Date(2018, 0, 1)) //=> Sat Jan 01 2050 00:00:00`
 *
 *    `parse('75', 'yy', new Date(2018, 0, 1)) //=> Wed Jan 01 1975 00:00:00`
 *
 *    while `uu` will just assign the year as is:
 *
 *    `parse('50', 'uu', new Date(2018, 0, 1)) //=> Sat Jan 01 0050 00:00:00`
 *
 *    `parse('75', 'uu', new Date(2018, 0, 1)) //=> Tue Jan 01 0075 00:00:00`
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [setISOWeekYear](https://date-fns.org/docs/setISOWeekYear)
 *    and [setWeekYear](https://date-fns.org/docs/setWeekYear)).
 *
 * 5. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 6. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 7. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 8. `P+` tokens do not have a defined priority since they are merely aliases to other tokens based
 *    on the given locale.
 *
 *    using `en-US` locale: `P` => `MM/dd/yyyy`
 *    using `en-US` locale: `p` => `hh:mm a`
 *    using `pt-BR` locale: `P` => `dd/MM/yyyy`
 *    using `pt-BR` locale: `p` => `HH:mm`
 *
 * Values will be assigned to the date in the descending order of its unit's priority.
 * Units of an equal priority overwrite each other in the order of appearance.
 *
 * If no values of higher priority are parsed (e.g. when parsing string 'January 1st' without a year),
 * the values will be taken from 3rd argument `referenceDate` which works as a context of parsing.
 *
 * `referenceDate` must be passed for correct work of the function.
 * If you're not sure which `referenceDate` to supply, create a new instance of Date:
 * `parse('02/11/2014', 'MM/dd/yyyy', new Date())`
 * In this case parsing will be done in the context of the current date.
 * If `referenceDate` is `Invalid Date` or a value not convertible to valid `Date`,
 * then `Invalid Date` will be returned.
 *
 * The result may vary by locale.
 *
 * If `formatString` matches with `dateString` but does not provides tokens, `referenceDate` will be returned.
 *
 * If parsing failed, `Invalid Date` will be returned.
 * Invalid Date is a Date, whose time value is NaN.
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateStr - The string to parse
 * @param formatStr - The string of tokens
 * @param referenceDate - defines values missing from the parsed dateString
 * @param options - An object with options.
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @returns The parsed date
 *
 * @throws `options.locale` must contain `match` property
 * @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws format string contains an unescaped latin alphabet character
 *
 * @example
 * // Parse 11 February 2014 from middle-endian format:
 * var result = parse('02/11/2014', 'MM/dd/yyyy', new Date())
 * //=> Tue Feb 11 2014 00:00:00
 *
 * @example
 * // Parse 28th of February in Esperanto locale in the context of 2010 year:
 * import eo from 'date-fns/locale/eo'
 * var result = parse('28-a de februaro', "do 'de' MMMM", new Date(2010, 0, 1), {
 *   locale: eo
 * })
 * //=> Sun Feb 28 2010 00:00:00
 */
function parse(dateStr, formatStr, referenceDate, options) {
  const defaultOptions = getDefaultOptions();
  const locale = options?.locale ?? defaultOptions.locale ?? enUS;

  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  if (formatStr === "") {
    if (dateStr === "") {
      return toDate(referenceDate);
    } else {
      return constructFrom(referenceDate, NaN);
    }
  }

  const subFnOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale,
  };

  // If timezone isn't specified, it will be set to the system timezone
  const setters = [new DateToSystemTimezoneSetter()];

  const tokens = formatStr
    .match(longFormattingTokensRegExp$2)
    .map((substring) => {
      const firstCharacter = substring[0];
      if (firstCharacter in longFormatters$1) {
        const longFormatter = longFormatters$1[firstCharacter];
        return longFormatter(substring, locale.formatLong);
      }
      return substring;
    })
    .join("")
    .match(formattingTokensRegExp$1);

  const usedTokens = [];

  for (let token of tokens) {
    if (
      !options?.useAdditionalWeekYearTokens &&
      isProtectedWeekYearToken$1(token)
    ) {
      warnOrThrowProtectedError$1(token, formatStr, dateStr);
    }
    if (
      !options?.useAdditionalDayOfYearTokens &&
      isProtectedDayOfYearToken$1(token)
    ) {
      warnOrThrowProtectedError$1(token, formatStr, dateStr);
    }

    const firstCharacter = token[0];
    const parser = parsers[firstCharacter];
    if (parser) {
      const { incompatibleTokens } = parser;
      if (Array.isArray(incompatibleTokens)) {
        const incompatibleToken = usedTokens.find(
          (usedToken) =>
            incompatibleTokens.includes(usedToken.token) ||
            usedToken.token === firstCharacter,
        );
        if (incompatibleToken) {
          throw new RangeError(
            `The format string mustn't contain \`${incompatibleToken.fullToken}\` and \`${token}\` at the same time`,
          );
        }
      } else if (parser.incompatibleTokens === "*" && usedTokens.length > 0) {
        throw new RangeError(
          `The format string mustn't contain \`${token}\` and any other token at the same time`,
        );
      }

      usedTokens.push({ token: firstCharacter, fullToken: token });

      const parseResult = parser.run(
        dateStr,
        token,
        locale.match,
        subFnOptions,
      );

      if (!parseResult) {
        return constructFrom(referenceDate, NaN);
      }

      setters.push(parseResult.setter);

      dateStr = parseResult.rest;
    } else {
      if (firstCharacter.match(unescapedLatinCharacterRegExp$1)) {
        throw new RangeError(
          "Format string contains an unescaped latin alphabet character `" +
            firstCharacter +
            "`",
        );
      }

      // Replace two single quote characters with one single quote character
      if (token === "''") {
        token = "'";
      } else if (firstCharacter === "'") {
        token = cleanEscapedString$1(token);
      }

      // Cut token from string, or, if string doesn't match the token, return Invalid Date
      if (dateStr.indexOf(token) === 0) {
        dateStr = dateStr.slice(token.length);
      } else {
        return constructFrom(referenceDate, NaN);
      }
    }
  }

  // Check if the remaining input contains something other than whitespace
  if (dateStr.length > 0 && notWhitespaceRegExp.test(dateStr)) {
    return constructFrom(referenceDate, NaN);
  }

  const uniquePrioritySetters = setters
    .map((setter) => setter.priority)
    .sort((a, b) => b - a)
    .filter((priority, index, array) => array.indexOf(priority) === index)
    .map((priority) =>
      setters
        .filter((setter) => setter.priority === priority)
        .sort((a, b) => b.subPriority - a.subPriority),
    )
    .map((setterArray) => setterArray[0]);

  let date = toDate(referenceDate);

  if (isNaN(date.getTime())) {
    return constructFrom(referenceDate, NaN);
  }

  const flags = {};
  for (const setter of uniquePrioritySetters) {
    if (!setter.validate(date, subFnOptions)) {
      return constructFrom(referenceDate, NaN);
    }

    const result = setter.set(date, flags, subFnOptions);
    // Result is tuple (date, flags)
    if (Array.isArray(result)) {
      date = result[0];
      Object.assign(flags, result[1]);
      // Result is date
    } else {
      date = result;
    }
  }

  return constructFrom(referenceDate, date);
}

function cleanEscapedString$1(input) {
  return input.match(escapedStringRegExp$1)[1].replace(doubleQuoteRegExp$1, "'");
}

/**
 * @name isSameMonth
 * @category Month Helpers
 * @summary Are the given dates in the same month (and year)?
 *
 * @description
 * Are the given dates in the same month (and year)?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The first date to check
 * @param dateRight - The second date to check
 *
 * @returns The dates are in the same month (and year)
 *
 * @example
 * // Are 2 September 2014 and 25 September 2014 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2014, 8, 25))
 * //=> true
 *
 * @example
 * // Are 2 September 2014 and 25 September 2015 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2015, 8, 25))
 * //=> false
 */
function isSameMonth$1(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);
  return (
    _dateLeft.getFullYear() === _dateRight.getFullYear() &&
    _dateLeft.getMonth() === _dateRight.getMonth()
  );
}

/**
 * @name isSameQuarter
 * @category Quarter Helpers
 * @summary Are the given dates in the same quarter (and year)?
 *
 * @description
 * Are the given dates in the same quarter (and year)?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The first date to check
 * @param dateRight - The second date to check

 * @returns The dates are in the same quarter (and year)
 *
 * @example
 * // Are 1 January 2014 and 8 March 2014 in the same quarter?
 * const result = isSameQuarter(new Date(2014, 0, 1), new Date(2014, 2, 8))
 * //=> true
 *
 * @example
 * // Are 1 January 2014 and 1 January 2015 in the same quarter?
 * const result = isSameQuarter(new Date(2014, 0, 1), new Date(2015, 0, 1))
 * //=> false
 */
function isSameQuarter$1(dateLeft, dateRight) {
  const dateLeftStartOfQuarter = startOfQuarter(dateLeft);
  const dateRightStartOfQuarter = startOfQuarter(dateRight);

  return +dateLeftStartOfQuarter === +dateRightStartOfQuarter;
}

/**
 * @name isSameYear
 * @category Year Helpers
 * @summary Are the given dates in the same year?
 *
 * @description
 * Are the given dates in the same year?
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The first date to check
 * @param dateRight - The second date to check
 *
 * @returns The dates are in the same year
 *
 * @example
 * // Are 2 September 2014 and 25 September 2014 in the same year?
 * const result = isSameYear(new Date(2014, 8, 2), new Date(2014, 8, 25))
 * //=> true
 */
function isSameYear$1(dateLeft, dateRight) {
  const _dateLeft = toDate(dateLeft);
  const _dateRight = toDate(dateRight);
  return _dateLeft.getFullYear() === _dateRight.getFullYear();
}

/**
 * @name isWithinInterval
 * @category Interval Helpers
 * @summary Is the given date within the interval?
 *
 * @description
 * Is the given date within the interval? (Including start and end.)
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to check
 * @param interval - The interval to check
 *
 * @returns The date is within the interval
 *
 * @example
 * // For the date within the interval:
 * isWithinInterval(new Date(2014, 0, 3), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> true
 *
 * @example
 * // For the date outside of the interval:
 * isWithinInterval(new Date(2014, 0, 10), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> false
 *
 * @example
 * // For date equal to interval start:
 * isWithinInterval(date, { start, end: date })
 * // => true
 *
 * @example
 * // For date equal to interval end:
 * isWithinInterval(date, { start: date, end })
 * // => true
 */
function isWithinInterval(date, interval) {
  const time = +toDate(date);
  const [startTime, endTime] = [
    +toDate(interval.start),
    +toDate(interval.end),
  ].sort((a, b) => a - b);

  return time >= startTime && time <= endTime;
}

/**
 * @name subDays
 * @category Day Helpers
 * @summary Subtract the specified number of days from the given date.
 *
 * @description
 * Subtract the specified number of days from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of days to be subtracted.
 *
 * @returns The new date with the days subtracted
 *
 * @example
 * // Subtract 10 days from 1 September 2014:
 * const result = subDays(new Date(2014, 8, 1), 10)
 * //=> Fri Aug 22 2014 00:00:00
 */
function subDays(date, amount) {
  return addDays(date, -1);
}

/**
 * The {@link parseISO} function options.
 */

/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param argument - The value to convert
 * @param options - An object with options
 *
 * @returns The parsed date in the local time zone
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * const result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * const result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */
function parseISO(argument, options) {
  const additionalDigits = 2;
  const dateStrings = splitDateString(argument);

  let date;
  if (dateStrings.date) {
    const parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate$1(parseYearResult.restDateString, parseYearResult.year);
  }

  if (!date || isNaN(date.getTime())) {
    return new Date(NaN);
  }

  const timestamp = date.getTime();
  let time = 0;
  let offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);
    if (isNaN(time)) {
      return new Date(NaN);
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);
    if (isNaN(offset)) {
      return new Date(NaN);
    }
  } else {
    const dirtyDate = new Date(timestamp + time);
    // JS parsed string assuming it's in UTC timezone
    // but we need it to be parsed in our timezone
    // so we use utc values to build date in our timezone.
    // Year values from 0 to 99 map to the years 1900 to 1999
    // so set year explicitly with setFullYear.
    const result = new Date(0);
    result.setFullYear(
      dirtyDate.getUTCFullYear(),
      dirtyDate.getUTCMonth(),
      dirtyDate.getUTCDate(),
    );
    result.setHours(
      dirtyDate.getUTCHours(),
      dirtyDate.getUTCMinutes(),
      dirtyDate.getUTCSeconds(),
      dirtyDate.getUTCMilliseconds(),
    );
    return result;
  }

  return new Date(timestamp + time + offset);
}

const patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/,
};

const dateRegex =
  /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
const timeRegex =
  /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
const timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;

function splitDateString(dateString) {
  const dateStrings = {};
  const array = dateString.split(patterns.dateTimeDelimiter);
  let timeString;

  // The regex match should only return at maximum two array elements.
  // [date], [time], or [date, time].
  if (array.length > 2) {
    return dateStrings;
  }

  if (/:/.test(array[0])) {
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];
    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(
        dateStrings.date.length,
        dateString.length,
      );
    }
  }

  if (timeString) {
    const token = patterns.timezone.exec(timeString);
    if (token) {
      dateStrings.time = timeString.replace(token[1], "");
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings;
}

function parseYear(dateString, additionalDigits) {
  const regex = new RegExp(
    "^(?:(\\d{4}|[+-]\\d{" +
      (4 + additionalDigits) +
      "})|(\\d{2}|[+-]\\d{" +
      (2 + additionalDigits) +
      "})$)",
  );

  const captures = dateString.match(regex);
  // Invalid ISO-formatted year
  if (!captures) return { year: NaN, restDateString: "" };

  const year = captures[1] ? parseInt(captures[1]) : null;
  const century = captures[2] ? parseInt(captures[2]) : null;

  // either year or century is null, not both
  return {
    year: century === null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length),
  };
}

function parseDate$1(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return new Date(NaN);

  const captures = dateString.match(dateRegex);
  // Invalid ISO-formatted string
  if (!captures) return new Date(NaN);

  const isWeekDate = !!captures[4];
  const dayOfYear = parseDateUnit(captures[1]);
  const month = parseDateUnit(captures[2]) - 1;
  const day = parseDateUnit(captures[3]);
  const week = parseDateUnit(captures[4]);
  const dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN);
    }
    return dayOfISOWeekYear(year, week, dayOfWeek);
  } else {
    const date = new Date(0);
    if (
      !validateDate(year, month, day) ||
      !validateDayOfYearDate(year, dayOfYear)
    ) {
      return new Date(NaN);
    }
    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date;
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1;
}

function parseTime(timeString) {
  const captures = timeString.match(timeRegex);
  if (!captures) return NaN; // Invalid ISO-formatted time

  const hours = parseTimeUnit(captures[1]);
  const minutes = parseTimeUnit(captures[2]);
  const seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN;
  }

  return (
    hours * millisecondsInHour + minutes * millisecondsInMinute + seconds * 1000
  );
}

function parseTimeUnit(value) {
  return (value && parseFloat(value.replace(",", "."))) || 0;
}

function parseTimezone(timezoneString) {
  if (timezoneString === "Z") return 0;

  const captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0;

  const sign = captures[1] === "+" ? -1 : 1;
  const hours = parseInt(captures[2]);
  const minutes = (captures[3] && parseInt(captures[3])) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN;
  }

  return sign * (hours * millisecondsInHour + minutes * millisecondsInMinute);
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  const date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  const fourthOfJanuaryDay = date.getUTCDay() || 7;
  const diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

// Validation functions

// February is null to handle the leap year (using ||)
const daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}

function validateDate(year, month, date) {
  return (
    month >= 0 &&
    month <= 11 &&
    date >= 1 &&
    date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28))
  );
}

function validateDayOfYearDate(year, dayOfYear) {
  return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}

function validateWeekDate(_year, week, day) {
  return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}

function validateTime(hours, minutes, seconds) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return (
    seconds >= 0 &&
    seconds < 60 &&
    minutes >= 0 &&
    minutes < 60 &&
    hours >= 0 &&
    hours < 25
  );
}

function validateTimezone(_hours, minutes) {
  return minutes >= 0 && minutes <= 59;
}

/**
 * @name setMonth
 * @category Month Helpers
 * @summary Set the month to the given date.
 *
 * @description
 * Set the month to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param month - The month index to set (0-11)
 *
 * @returns The new date with the month set
 *
 * @example
 * // Set February to 1 September 2014:
 * const result = setMonth(new Date(2014, 8, 1), 1)
 * //=> Sat Feb 01 2014 00:00:00
 */
function setMonth(date, month) {
  const _date = toDate(date);
  const year = _date.getFullYear();
  const day = _date.getDate();

  const dateWithDesiredMonth = constructFrom(date, 0);
  dateWithDesiredMonth.setFullYear(year, month, 15);
  dateWithDesiredMonth.setHours(0, 0, 0, 0);
  const daysInMonth = getDaysInMonth(dateWithDesiredMonth);
  // Set the last day of the new month
  // if the original date was the last day of the longer month
  _date.setMonth(month, Math.min(day, daysInMonth));
  return _date;
}

/**
 * @name set
 * @category Common Helpers
 * @summary Set date values to a given date.
 *
 * @description
 * Set date values to a given date.
 *
 * Sets time values to date from object `values`.
 * A value is not set if it is undefined or null or doesn't exist in `values`.
 *
 * Note about bundle size: `set` does not internally use `setX` functions from date-fns but instead opts
 * to use native `Date#setX` methods. If you use this function, you may not want to include the
 * other `setX` functions that date-fns provides if you are concerned about the bundle size.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param values - The date values to be set
 *
 * @returns The new date with options set
 *
 * @example
 * // Transform 1 September 2014 into 20 October 2015 in a single line:
 * const result = set(new Date(2014, 8, 20), { year: 2015, month: 9, date: 20 })
 * //=> Tue Oct 20 2015 00:00:00
 *
 * @example
 * // Set 12 PM to 1 September 2014 01:23:45 to 1 September 2014 12:00:00:
 * const result = set(new Date(2014, 8, 1, 1, 23, 45), { hours: 12 })
 * //=> Mon Sep 01 2014 12:23:45
 */

function set(date, values) {
  let _date = toDate(date);

  // Check if date is Invalid Date because Date.prototype.setFullYear ignores the value of Invalid Date
  if (isNaN(+_date)) {
    return constructFrom(date, NaN);
  }

  if (values.year != null) {
    _date.setFullYear(values.year);
  }

  if (values.month != null) {
    _date = setMonth(_date, values.month);
  }

  if (values.date != null) {
    _date.setDate(values.date);
  }

  if (values.hours != null) {
    _date.setHours(values.hours);
  }

  if (values.minutes != null) {
    _date.setMinutes(values.minutes);
  }

  if (values.seconds != null) {
    _date.setSeconds(values.seconds);
  }

  if (values.milliseconds != null) {
    _date.setMilliseconds(values.milliseconds);
  }

  return _date;
}

/**
 * @name setHours
 * @category Hour Helpers
 * @summary Set the hours to the given date.
 *
 * @description
 * Set the hours to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param hours - The hours of the new date
 *
 * @returns The new date with the hours set
 *
 * @example
 * // Set 4 hours to 1 September 2014 11:30:00:
 * const result = setHours(new Date(2014, 8, 1, 11, 30), 4)
 * //=> Mon Sep 01 2014 04:30:00
 */
function setHours(date, hours) {
  const _date = toDate(date);
  _date.setHours(hours);
  return _date;
}

/**
 * @name setMinutes
 * @category Minute Helpers
 * @summary Set the minutes to the given date.
 *
 * @description
 * Set the minutes to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param minutes - The minutes of the new date
 *
 * @returns The new date with the minutes set
 *
 * @example
 * // Set 45 minutes to 1 September 2014 11:30:40:
 * const result = setMinutes(new Date(2014, 8, 1, 11, 30, 40), 45)
 * //=> Mon Sep 01 2014 11:45:40
 */
function setMinutes(date, minutes) {
  const _date = toDate(date);
  _date.setMinutes(minutes);
  return _date;
}

/**
 * @name setQuarter
 * @category Quarter Helpers
 * @summary Set the year quarter to the given date.
 *
 * @description
 * Set the year quarter to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param quarter - The quarter of the new date
 *
 * @returns The new date with the quarter set
 *
 * @example
 * // Set the 2nd quarter to 2 July 2014:
 * const result = setQuarter(new Date(2014, 6, 2), 2)
 * //=> Wed Apr 02 2014 00:00:00
 */
function setQuarter(date, quarter) {
  const _date = toDate(date);
  const oldQuarter = Math.trunc(_date.getMonth() / 3) + 1;
  const diff = quarter - oldQuarter;
  return setMonth(_date, _date.getMonth() + diff * 3);
}

/**
 * @name setSeconds
 * @category Second Helpers
 * @summary Set the seconds to the given date.
 *
 * @description
 * Set the seconds to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param seconds - The seconds of the new date
 *
 * @returns The new date with the seconds set
 *
 * @example
 * // Set 45 seconds to 1 September 2014 11:30:40:
 * const result = setSeconds(new Date(2014, 8, 1, 11, 30, 40), 45)
 * //=> Mon Sep 01 2014 11:30:45
 */
function setSeconds(date, seconds) {
  const _date = toDate(date);
  _date.setSeconds(seconds);
  return _date;
}

/**
 * @name setYear
 * @category Year Helpers
 * @summary Set the year to the given date.
 *
 * @description
 * Set the year to the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param year - The year of the new date
 *
 * @returns The new date with the year set
 *
 * @example
 * // Set year 2013 to 1 September 2014:
 * const result = setYear(new Date(2014, 8, 1), 2013)
 * //=> Sun Sep 01 2013 00:00:00
 */
function setYear(date, year) {
  const _date = toDate(date);

  // Check if date is Invalid Date because Date.prototype.setFullYear ignores the value of Invalid Date
  if (isNaN(+_date)) {
    return constructFrom(date, NaN);
  }

  _date.setFullYear(year);
  return _date;
}

/**
 * @name subMonths
 * @category Month Helpers
 * @summary Subtract the specified number of months from the given date.
 *
 * @description
 * Subtract the specified number of months from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of months to be subtracted.
 *
 * @returns The new date with the months subtracted
 *
 * @example
 * // Subtract 5 months from 1 February 2015:
 * const result = subMonths(new Date(2015, 1, 1), 5)
 * //=> Mon Sep 01 2014 00:00:00
 */
function subMonths(date, amount) {
  return addMonths(date, -amount);
}

/**
 * @name subQuarters
 * @category Quarter Helpers
 * @summary Subtract the specified number of year quarters from the given date.
 *
 * @description
 * Subtract the specified number of year quarters from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of quarters to be subtracted.
 *
 * @returns The new date with the quarters subtracted
 *
 * @example
 * // Subtract 3 quarters from 1 September 2014:
 * const result = subQuarters(new Date(2014, 8, 1), 3)
 * //=> Sun Dec 01 2013 00:00:00
 */
function subQuarters(date, amount) {
  return addQuarters(date, -1);
}

/**
 * @name subWeeks
 * @category Week Helpers
 * @summary Subtract the specified number of weeks from the given date.
 *
 * @description
 * Subtract the specified number of weeks from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of weeks to be subtracted.
 *
 * @returns The new date with the weeks subtracted
 *
 * @example
 * // Subtract 4 weeks from 1 September 2014:
 * const result = subWeeks(new Date(2014, 8, 1), 4)
 * //=> Mon Aug 04 2014 00:00:00
 */
function subWeeks(date, amount) {
  return addWeeks(date, -1);
}

/**
 * @name subYears
 * @category Year Helpers
 * @summary Subtract the specified number of years from the given date.
 *
 * @description
 * Subtract the specified number of years from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to be changed
 * @param amount - The amount of years to be subtracted.
 *
 * @returns The new date with the years subtracted
 *
 * @example
 * // Subtract 5 years from 1 September 2014:
 * const result = subYears(new Date(2014, 8, 1), 5)
 * //=> Tue Sep 01 2009 00:00:00
 */
function subYears(date, amount) {
  return addYears(date, -amount);
}

// https://github.com/mui/material-ui/issues/41190#issuecomment-2040873379
const SafeReact = {
  ...React
};

const useInsertionEffect = SafeReact.useInsertionEffect;
const useSafeInsertionEffect = useInsertionEffect || (fn => fn());
function useEffectEvent(callback) {
  const ref = React.useRef(() => {
    if (process.env.NODE_ENV !== "production") {
      throw new Error('Cannot call an event handler while rendering.');
    }
  });
  useSafeInsertionEffect(() => {
    ref.current = callback;
  });
  return React.useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return ref.current == null ? void 0 : ref.current(...args);
  }, []);
}

var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

let serverHandoffComplete = false;
let count$1 = 0;
const genId = () => // Ensure the id is unique with multiple independent versions of Floating UI
// on <React 18
"floating-ui-" + Math.random().toString(36).slice(2, 6) + count$1++;
function useFloatingId() {
  const [id, setId] = React.useState(() => serverHandoffComplete ? genId() : undefined);
  index(() => {
    if (id == null) {
      setId(genId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    serverHandoffComplete = true;
  }, []);
  return id;
}
const useReactId = SafeReact.useId;

/**
 * Uses React 18's built-in `useId()` when available, or falls back to a
 * slightly less performant (requiring a double render) implementation for
 * earlier React versions.
 * @see https://floating-ui.com/docs/react-utils#useid
 */
const useId = useReactId || useFloatingId;

let devMessageSet;
if (process.env.NODE_ENV !== "production") {
  devMessageSet = /*#__PURE__*/new Set();
}
function warn() {
  var _devMessageSet;
  for (var _len = arguments.length, messages = new Array(_len), _key = 0; _key < _len; _key++) {
    messages[_key] = arguments[_key];
  }
  const message = "Floating UI: " + messages.join(' ');
  if (!((_devMessageSet = devMessageSet) != null && _devMessageSet.has(message))) {
    var _devMessageSet2;
    (_devMessageSet2 = devMessageSet) == null || _devMessageSet2.add(message);
    console.warn(message);
  }
}
function error() {
  var _devMessageSet3;
  for (var _len2 = arguments.length, messages = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    messages[_key2] = arguments[_key2];
  }
  const message = "Floating UI: " + messages.join(' ');
  if (!((_devMessageSet3 = devMessageSet) != null && _devMessageSet3.has(message))) {
    var _devMessageSet4;
    (_devMessageSet4 = devMessageSet) == null || _devMessageSet4.add(message);
    console.error(message);
  }
}

/**
 * Renders a pointing arrow triangle.
 * @see https://floating-ui.com/docs/FloatingArrow
 */
const FloatingArrow = /*#__PURE__*/React.forwardRef(function FloatingArrow(props, ref) {
  const {
    context: {
      placement,
      elements: {
        floating
      },
      middlewareData: {
        arrow,
        shift
      }
    },
    width = 14,
    height = 7,
    tipRadius = 0,
    strokeWidth = 0,
    staticOffset,
    stroke,
    d,
    style: {
      transform,
      ...restStyle
    } = {},
    ...rest
  } = props;
  if (process.env.NODE_ENV !== "production") {
    if (!ref) {
      warn('The `ref` prop is required for `FloatingArrow`.');
    }
  }
  const clipPathId = useId();
  const [isRTL, setIsRTL] = React.useState(false);

  // https://github.com/floating-ui/floating-ui/issues/2932
  index(() => {
    if (!floating) return;
    const isRTL = getComputedStyle$1(floating).direction === 'rtl';
    if (isRTL) {
      setIsRTL(true);
    }
  }, [floating]);
  if (!floating) {
    return null;
  }
  const [side, alignment] = placement.split('-');
  const isVerticalSide = side === 'top' || side === 'bottom';
  let computedStaticOffset = staticOffset;
  if (isVerticalSide && shift != null && shift.x || !isVerticalSide && shift != null && shift.y) {
    computedStaticOffset = null;
  }

  // Strokes must be double the border width, this ensures the stroke's width
  // works as you'd expect.
  const computedStrokeWidth = strokeWidth * 2;
  const halfStrokeWidth = computedStrokeWidth / 2;
  const svgX = width / 2 * (tipRadius / -8 + 1);
  const svgY = height / 2 * tipRadius / 4;
  const isCustomShape = !!d;
  const yOffsetProp = computedStaticOffset && alignment === 'end' ? 'bottom' : 'top';
  let xOffsetProp = computedStaticOffset && alignment === 'end' ? 'right' : 'left';
  if (computedStaticOffset && isRTL) {
    xOffsetProp = alignment === 'end' ? 'left' : 'right';
  }
  const arrowX = (arrow == null ? void 0 : arrow.x) != null ? computedStaticOffset || arrow.x : '';
  const arrowY = (arrow == null ? void 0 : arrow.y) != null ? computedStaticOffset || arrow.y : '';
  const dValue = d || 'M0,0' + (" H" + width) + (" L" + (width - svgX) + "," + (height - svgY)) + (" Q" + width / 2 + "," + height + " " + svgX + "," + (height - svgY)) + ' Z';
  const rotation = {
    top: isCustomShape ? 'rotate(180deg)' : '',
    left: isCustomShape ? 'rotate(90deg)' : 'rotate(-90deg)',
    bottom: isCustomShape ? '' : 'rotate(180deg)',
    right: isCustomShape ? 'rotate(-90deg)' : 'rotate(90deg)'
  }[side];
  return /*#__PURE__*/jsxs("svg", {
    ...rest,
    "aria-hidden": true,
    ref: ref,
    width: isCustomShape ? width : width + computedStrokeWidth,
    height: width,
    viewBox: "0 0 " + width + " " + (height > width ? height : width),
    style: {
      position: 'absolute',
      pointerEvents: 'none',
      [xOffsetProp]: arrowX,
      [yOffsetProp]: arrowY,
      [side]: isVerticalSide || isCustomShape ? '100%' : "calc(100% - " + computedStrokeWidth / 2 + "px)",
      transform: [rotation, transform].filter(t => !!t).join(' '),
      ...restStyle
    },
    children: [computedStrokeWidth > 0 && /*#__PURE__*/jsx$1("path", {
      clipPath: "url(#" + clipPathId + ")",
      fill: "none",
      stroke: stroke
      // Account for the stroke on the fill path rendered below.
      ,
      strokeWidth: computedStrokeWidth + (d ? 0 : 1),
      d: dValue
    }), /*#__PURE__*/jsx$1("path", {
      stroke: computedStrokeWidth && !d ? rest.fill : 'none',
      d: dValue
    }), /*#__PURE__*/jsx$1("clipPath", {
      id: clipPathId,
      children: /*#__PURE__*/jsx$1("rect", {
        x: -halfStrokeWidth,
        y: halfStrokeWidth * (isCustomShape ? -1 : 1),
        width: width + computedStrokeWidth,
        height: width
      })
    })]
  });
});

function createPubSub() {
  const map = new Map();
  return {
    emit(event, data) {
      var _map$get;
      (_map$get = map.get(event)) == null || _map$get.forEach(handler => handler(data));
    },
    on(event, listener) {
      map.set(event, [...(map.get(event) || []), listener]);
    },
    off(event, listener) {
      var _map$get2;
      map.set(event, ((_map$get2 = map.get(event)) == null ? void 0 : _map$get2.filter(l => l !== listener)) || []);
    }
  };
}

const FloatingNodeContext = /*#__PURE__*/React.createContext(null);
const FloatingTreeContext = /*#__PURE__*/React.createContext(null);

/**
 * Returns the parent node id for nested floating elements, if available.
 * Returns `null` for top-level floating elements.
 */
const useFloatingParentNodeId = () => {
  var _React$useContext;
  return ((_React$useContext = React.useContext(FloatingNodeContext)) == null ? void 0 : _React$useContext.id) || null;
};

/**
 * Returns the nearest floating tree context, if available.
 */
const useFloatingTree = () => React.useContext(FloatingTreeContext);

function useFloatingRootContext(options) {
  const {
    open = false,
    onOpenChange: onOpenChangeProp,
    elements: elementsProp
  } = options;
  const floatingId = useId();
  const dataRef = React.useRef({});
  const [events] = React.useState(() => createPubSub());
  const nested = useFloatingParentNodeId() != null;
  if (process.env.NODE_ENV !== "production") {
    const optionDomReference = elementsProp.reference;
    if (optionDomReference && !isElement(optionDomReference)) {
      error('Cannot pass a virtual element to the `elements.reference` option,', 'as it must be a real DOM element. Use `refs.setPositionReference()`', 'instead.');
    }
  }
  const [positionReference, setPositionReference] = React.useState(elementsProp.reference);
  const onOpenChange = useEffectEvent((open, event, reason) => {
    dataRef.current.openEvent = open ? event : undefined;
    events.emit('openchange', {
      open,
      event,
      reason,
      nested
    });
    onOpenChangeProp == null || onOpenChangeProp(open, event, reason);
  });
  const refs = React.useMemo(() => ({
    setPositionReference
  }), []);
  const elements = React.useMemo(() => ({
    reference: positionReference || elementsProp.reference || null,
    floating: elementsProp.floating || null,
    domReference: elementsProp.reference
  }), [positionReference, elementsProp.reference, elementsProp.floating]);
  return React.useMemo(() => ({
    dataRef,
    open,
    onOpenChange,
    elements,
    events,
    floatingId,
    refs
  }), [open, onOpenChange, elements, events, floatingId, refs]);
}

/**
 * Provides data to position a floating element and context to add interactions.
 * @see https://floating-ui.com/docs/useFloating
 */
function useFloating(options) {
  if (options === void 0) {
    options = {};
  }
  const {
    nodeId
  } = options;
  const internalRootContext = useFloatingRootContext({
    ...options,
    elements: {
      reference: null,
      floating: null,
      ...options.elements
    }
  });
  const rootContext = options.rootContext || internalRootContext;
  const computedElements = rootContext.elements;
  const [_domReference, setDomReference] = React.useState(null);
  const [positionReference, _setPositionReference] = React.useState(null);
  const optionDomReference = computedElements == null ? void 0 : computedElements.domReference;
  const domReference = optionDomReference || _domReference;
  const domReferenceRef = React.useRef(null);
  const tree = useFloatingTree();
  index(() => {
    if (domReference) {
      domReferenceRef.current = domReference;
    }
  }, [domReference]);
  const position = useFloating$1({
    ...options,
    elements: {
      ...computedElements,
      ...(positionReference && {
        reference: positionReference
      })
    }
  });
  const setPositionReference = React.useCallback(node => {
    const computedPositionReference = isElement(node) ? {
      getBoundingClientRect: () => node.getBoundingClientRect(),
      contextElement: node
    } : node;
    // Store the positionReference in state if the DOM reference is specified externally via the
    // `elements.reference` option. This ensures that it won't be overridden on future renders.
    _setPositionReference(computedPositionReference);
    position.refs.setReference(computedPositionReference);
  }, [position.refs]);
  const setReference = React.useCallback(node => {
    if (isElement(node) || node === null) {
      domReferenceRef.current = node;
      setDomReference(node);
    }

    // Backwards-compatibility for passing a virtual element to `reference`
    // after it has set the DOM reference.
    if (isElement(position.refs.reference.current) || position.refs.reference.current === null ||
    // Don't allow setting virtual elements using the old technique back to
    // `null` to support `positionReference` + an unstable `reference`
    // callback ref.
    node !== null && !isElement(node)) {
      position.refs.setReference(node);
    }
  }, [position.refs]);
  const refs = React.useMemo(() => ({
    ...position.refs,
    setReference,
    setPositionReference,
    domReference: domReferenceRef
  }), [position.refs, setReference, setPositionReference]);
  const elements = React.useMemo(() => ({
    ...position.elements,
    domReference: domReference
  }), [position.elements, domReference]);
  const context = React.useMemo(() => ({
    ...position,
    ...rootContext,
    refs,
    elements,
    nodeId
  }), [position, refs, elements, nodeId, rootContext]);
  index(() => {
    rootContext.dataRef.current.floatingContext = context;
    const node = tree == null ? void 0 : tree.nodesRef.current.find(node => node.id === nodeId);
    if (node) {
      node.context = context;
    }
  });
  return React.useMemo(() => ({
    ...position,
    context,
    refs,
    elements
  }), [position, refs, elements, context]);
}

/*!
  react-datepicker v7.6.0
  https://github.com/Hacker0x01/react-datepicker
  Released under the MIT License.
*/

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var _extendStatics = function extendStatics(d, b) {
  _extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
  };
  return _extendStatics(d, b);
};
function __extends(d, b) {
  if (typeof b !== "function" && b !== null) throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
  _extendStatics(d, b);
  function __() {
    this.constructor = d;
  }
  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var CalendarContainer = function (_a) {
    var _b = _a.showTimeSelectOnly, showTimeSelectOnly = _b === void 0 ? false : _b, _c = _a.showTime, showTime = _c === void 0 ? false : _c, className = _a.className, children = _a.children;
    var ariaLabel = showTimeSelectOnly
        ? "Choose Time"
        : "Choose Date".concat(showTime ? " and Time" : "");
    return (React__default.createElement("div", { className: className, role: "dialog", "aria-label": ariaLabel, "aria-modal": "true" }, children));
};

var useDetectClickOutside = function (onClickOutside, ignoreClass) {
    var ref = useRef(null);
    var onClickOutsideRef = useRef(onClickOutside);
    onClickOutsideRef.current = onClickOutside;
    var handleClickOutside = useCallback(function (event) {
        var _a;
        if (ref.current && !ref.current.contains(event.target)) {
            if (!(ignoreClass &&
                event.target instanceof HTMLElement &&
                event.target.classList.contains(ignoreClass))) {
                (_a = onClickOutsideRef.current) === null || _a === void 0 ? void 0 : _a.call(onClickOutsideRef, event);
            }
        }
    }, [ignoreClass]);
    useEffect(function () {
        document.addEventListener("mousedown", handleClickOutside);
        return function () {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);
    return ref;
};
var ClickOutsideWrapper = function (_a) {
    var children = _a.children, onClickOutside = _a.onClickOutside, className = _a.className, containerRef = _a.containerRef, style = _a.style, ignoreClass = _a.ignoreClass;
    var detectRef = useDetectClickOutside(onClickOutside, ignoreClass);
    return (React__default.createElement("div", { className: className, style: style, ref: function (node) {
            detectRef.current = node;
            if (containerRef) {
                containerRef.current = node;
            }
        } }, children));
};

var KeyType;
(function (KeyType) {
    KeyType["ArrowUp"] = "ArrowUp";
    KeyType["ArrowDown"] = "ArrowDown";
    KeyType["ArrowLeft"] = "ArrowLeft";
    KeyType["ArrowRight"] = "ArrowRight";
    KeyType["PageUp"] = "PageUp";
    KeyType["PageDown"] = "PageDown";
    KeyType["Home"] = "Home";
    KeyType["End"] = "End";
    KeyType["Enter"] = "Enter";
    KeyType["Space"] = " ";
    KeyType["Tab"] = "Tab";
    KeyType["Escape"] = "Escape";
    KeyType["Backspace"] = "Backspace";
    KeyType["X"] = "x";
})(KeyType || (KeyType = {}));
function getLocaleScope() {
    // Use this cast to avoid messing with users globalThis (like window) and the rest of keys in the globalThis object we don't care about
    var scope = (typeof window !== "undefined"
        ? window
        : globalThis);
    return scope;
}
var DEFAULT_YEAR_ITEM_NUMBER = 12;
// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
var longFormattingTokensRegExp$1 = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
// ** Date Constructors **
function newDate(value) {
    if (value == null) {
        return new Date();
    }
    var d = typeof value === "string" ? parseISO(value) : toDate(value);
    return isValid$1(d) ? d : new Date();
}
/**
 * Parses a date.
 *
 * @param value - The string representing the Date in a parsable form, e.g., ISO 1861
 * @param dateFormat - The date format.
 * @param locale - The locale.
 * @param strictParsing - The strict parsing flag.
 * @param minDate - The minimum date.
 * @returns - The parsed date or null.
 */
function parseDate(value, dateFormat, locale, strictParsing, minDate) {
    var _a;
    var parsedDate = null;
    var localeObject = getLocaleObject(locale) || getLocaleObject(getDefaultLocale());
    var strictParsingValueMatch = true;
    if (Array.isArray(dateFormat)) {
        dateFormat.forEach(function (df) {
            var tryParseDate = parse(value, df, new Date(), {
                locale: localeObject,
                useAdditionalWeekYearTokens: true,
                useAdditionalDayOfYearTokens: true,
            });
            if (strictParsing) {
                strictParsingValueMatch =
                    isValid$1(tryParseDate, minDate) &&
                        value === formatDate(tryParseDate, df, locale);
            }
            if (isValid$1(tryParseDate, minDate) && strictParsingValueMatch) {
                parsedDate = tryParseDate;
            }
        });
        return parsedDate;
    }
    parsedDate = parse(value, dateFormat, new Date(), {
        locale: localeObject,
        useAdditionalWeekYearTokens: true,
        useAdditionalDayOfYearTokens: true,
    });
    if (strictParsing) {
        strictParsingValueMatch =
            isValid$1(parsedDate) &&
                value === formatDate(parsedDate, dateFormat, locale);
    }
    else if (!isValid$1(parsedDate)) {
        var format_1 = ((_a = dateFormat.match(longFormattingTokensRegExp$1)) !== null && _a !== void 0 ? _a : [])
            .map(function (substring) {
            var firstCharacter = substring[0];
            if (firstCharacter === "p" || firstCharacter === "P") {
                // The type in date-fns is `Record<string, LongFormatter>` so we can do our firstCharacter a bit loos but I don't think that this is a good idea
                var longFormatter = longFormatters$1[firstCharacter];
                return localeObject
                    ? longFormatter(substring, localeObject.formatLong)
                    : firstCharacter;
            }
            return substring;
        })
            .join("");
        if (value.length > 0) {
            parsedDate = parse(value, format_1.slice(0, value.length), new Date(), {
                useAdditionalWeekYearTokens: true,
                useAdditionalDayOfYearTokens: true,
            });
        }
        if (!isValid$1(parsedDate)) {
            parsedDate = new Date(value);
        }
    }
    return isValid$1(parsedDate) && strictParsingValueMatch ? parsedDate : null;
}
/**
 * Checks if a given date is valid and not before the minimum date.
 * @param date - The date to be checked.
 * @param minDate - The minimum date allowed. If not provided, defaults to "1/1/1800".
 * @returns A boolean value indicating whether the date is valid and not before the minimum date.
 */
function isValid$1(date, minDate) {
    /* the fallback date is essential to not break test case
     * `should auto update calendar when the updated date text is after props.minDate`
     * and backward compatibility respectfully
     */
    return isValid$2(date) && !isBefore(date, minDate !== null && minDate !== void 0 ? minDate : new Date("1/1/1800"));
}
// ** Date Formatting **
/**
 * Formats a date.
 *
 * @param date - The date.
 * @param formatStr - The format string.
 * @param locale - The locale.
 * @returns - The formatted date.
 */
function formatDate(date, formatStr, locale) {
    if (locale === "en") {
        return format$1(date, formatStr, {
            useAdditionalWeekYearTokens: true,
            useAdditionalDayOfYearTokens: true,
        });
    }
    var localeObj = locale ? getLocaleObject(locale) : undefined;
    if (locale && !localeObj) {
        console.warn("A locale object was not found for the provided string [\"".concat(locale, "\"]."));
    }
    if (!localeObj &&
        !!getDefaultLocale() &&
        !!getLocaleObject(getDefaultLocale())) {
        localeObj = getLocaleObject(getDefaultLocale());
    }
    return format$1(date, formatStr, {
        locale: localeObj,
        useAdditionalWeekYearTokens: true,
        useAdditionalDayOfYearTokens: true,
    });
}
/**
 * Safely formats a date.
 *
 * @param date - The date.
 * @param options - An object containing the dateFormat and locale.
 * @returns - The formatted date or an empty string.
 */
function safeDateFormat(date, _a) {
    var dateFormat = _a.dateFormat, locale = _a.locale;
    var formatStr = (Array.isArray(dateFormat) && dateFormat.length > 0
        ? dateFormat[0]
        : dateFormat); // Cast to string because it's impossible to get `string | string[] | undefined` here and typescript doesn't know that
    return (date && formatDate(date, formatStr, locale)) || "";
}
/**
 * Safely formats a date range.
 *
 * @param startDate - The start date.
 * @param endDate - The end date.
 * @param props - The props.
 * @returns - The formatted date range or an empty string.
 */
function safeDateRangeFormat(startDate, endDate, props) {
    if (!startDate) {
        return "";
    }
    var formattedStartDate = safeDateFormat(startDate, props);
    var formattedEndDate = endDate ? safeDateFormat(endDate, props) : "";
    return "".concat(formattedStartDate, " - ").concat(formattedEndDate);
}
/**
 * Safely formats multiple dates.
 *
 * @param dates - The dates.
 * @param props - The props.
 * @returns - The formatted dates or an empty string.
 */
function safeMultipleDatesFormat(dates, props) {
    if (!(dates === null || dates === void 0 ? void 0 : dates.length)) {
        return "";
    }
    var formattedFirstDate = dates[0] ? safeDateFormat(dates[0], props) : "";
    if (dates.length === 1) {
        return formattedFirstDate;
    }
    if (dates.length === 2 && dates[1]) {
        var formattedSecondDate = safeDateFormat(dates[1], props);
        return "".concat(formattedFirstDate, ", ").concat(formattedSecondDate);
    }
    var extraDatesCount = dates.length - 1;
    return "".concat(formattedFirstDate, " (+").concat(extraDatesCount, ")");
}
// ** Date Setters **
/**
 * Sets the time for a given date.
 *
 * @param date - The date.
 * @param time - An object containing the hour, minute, and second.
 * @returns - The date with the time set.
 */
function setTime(date, _a) {
    var _b = _a.hour, hour = _b === void 0 ? 0 : _b, _c = _a.minute, minute = _c === void 0 ? 0 : _c, _d = _a.second, second = _d === void 0 ? 0 : _d;
    return setHours(setMinutes(setSeconds(date, second), minute), hour);
}
/**
 * Gets the week of the year for a given date.
 *
 * @param date - The date.
 * @returns - The week of the year.
 */
function getWeek$1(date) {
    return getISOWeek$1(date);
}
/**
 * Gets the day of the week code for a given day.
 *
 * @param day - The day.
 * @param locale - The locale.
 * @returns - The day of the week code.
 */
function getDayOfWeekCode(day, locale) {
    return formatDate(day, "ddd", locale);
}
// *** Start of ***
/**
 * Gets the start of the day for a given date.
 *
 * @param date - The date.
 * @returns - The start of the day.
 */
function getStartOfDay(date) {
    return startOfDay$1(date);
}
/**
 * Gets the start of the week for a given date.
 *
 * @param date - The date.
 * @param locale - The locale.
 * @param calendarStartDay - The day the calendar starts on.
 * @returns - The start of the week.
 */
function getStartOfWeek(date, locale, calendarStartDay) {
    var localeObj = locale
        ? getLocaleObject(locale)
        : getLocaleObject(getDefaultLocale());
    return startOfWeek(date, {
        locale: localeObj,
        weekStartsOn: calendarStartDay,
    });
}
/**
 * Gets the start of the month for a given date.
 *
 * @param date - The date.
 * @returns - The start of the month.
 */
function getStartOfMonth(date) {
    return startOfMonth(date);
}
/**
 * Gets the start of the year for a given date.
 *
 * @param date - The date.
 * @returns - The start of the year.
 */
function getStartOfYear(date) {
    return startOfYear$1(date);
}
/**
 * Gets the start of the quarter for a given date.
 *
 * @param date - The date.
 * @returns - The start of the quarter.
 */
function getStartOfQuarter(date) {
    return startOfQuarter(date);
}
/**
 * Gets the start of today.
 *
 * @returns - The start of today.
 */
function getStartOfToday() {
    return startOfDay$1(newDate());
}
// *** End of ***
/**
 * Gets the end of the day for a given date.
 *
 * @param date - The date.
 * @returns - The end of the day.
 */
function getEndOfDay(date) {
    return endOfDay(date);
}
/**
 * Gets the end of the week for a given date.
 *
 * @param date - The date.
 * @returns - The end of the week.
 */
function getEndOfWeek(date) {
    return endOfWeek(date);
}
/**
 * Gets the end of the month for a given date.
 *
 * @param date - The date.
 * @returns - The end of the month.
 */
function getEndOfMonth(date) {
    return endOfMonth(date);
}
/**
 * Checks if two dates are in the same year.
 *
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns - True if the dates are in the same year, false otherwise.
 */
function isSameYear(date1, date2) {
    if (date1 && date2) {
        return isSameYear$1(date1, date2);
    }
    else {
        return !date1 && !date2;
    }
}
/**
 * Checks if two dates are in the same month.
 *
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns - True if the dates are in the same month, false otherwise.
 */
function isSameMonth(date1, date2) {
    if (date1 && date2) {
        return isSameMonth$1(date1, date2);
    }
    else {
        return !date1 && !date2;
    }
}
/**
 * Checks if two dates are in the same quarter.
 *
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns - True if the dates are in the same quarter, false otherwise.
 */
function isSameQuarter(date1, date2) {
    if (date1 && date2) {
        return isSameQuarter$1(date1, date2);
    }
    else {
        return !date1 && !date2;
    }
}
/**
 * Checks if two dates are on the same day.
 *
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns - True if the dates are on the same day, false otherwise.
 */
function isSameDay(date1, date2) {
    if (date1 && date2) {
        return isSameDay$1(date1, date2);
    }
    else {
        return !date1 && !date2;
    }
}
/**
 * Checks if two dates are equal.
 *
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns - True if the dates are equal, false otherwise.
 */
function isEqual(date1, date2) {
    if (date1 && date2) {
        return isEqual$1(date1, date2);
    }
    else {
        return !date1 && !date2;
    }
}
/**
 * Checks if a day is within a date range.
 *
 * @param day - The day to check.
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @returns - True if the day is within the range, false otherwise.
 */
function isDayInRange(day, startDate, endDate) {
    var valid;
    var start = startOfDay$1(startDate);
    var end = endOfDay(endDate);
    try {
        valid = isWithinInterval(day, { start: start, end: end });
    }
    catch (err) {
        valid = false;
    }
    return valid;
}
// ** Date Localization **
/**
 * Registers a locale.
 *
 * @param localeName - The name of the locale.
 * @param localeData - The data of the locale.
 */
function registerLocale(localeName, localeData) {
    var scope = getLocaleScope();
    if (!scope.__localeData__) {
        scope.__localeData__ = {};
    }
    scope.__localeData__[localeName] = localeData;
}
/**
 * Gets the default locale.
 *
 * @returns - The default locale.
 */
function getDefaultLocale() {
    var scope = getLocaleScope();
    return scope.__localeId__;
}
/**
 * Gets the locale object.
 *
 * @param localeSpec - The locale specification.
 * @returns - The locale object.
 */
function getLocaleObject(localeSpec) {
    if (typeof localeSpec === "string") {
        // Treat it as a locale name registered by registerLocale
        var scope = getLocaleScope();
        // Null was replaced with undefined to avoid type coercion
        return scope.__localeData__ ? scope.__localeData__[localeSpec] : undefined;
    }
    else {
        // Treat it as a raw date-fns locale object
        return localeSpec;
    }
}
/**
 * Formats the weekday in a given locale.
 *
 * @param date - The date to format.
 * @param formatFunc - The formatting function.
 * @param locale - The locale to use for formatting.
 * @returns - The formatted weekday.
 */
function getFormattedWeekdayInLocale(date, formatFunc, locale) {
    return formatFunc(formatDate(date, "EEEE", locale));
}
/**
 * Gets the minimum weekday in a given locale.
 *
 * @param date - The date to format.
 * @param locale - The locale to use for formatting.
 * @returns - The minimum weekday.
 */
function getWeekdayMinInLocale(date, locale) {
    return formatDate(date, "EEEEEE", locale);
}
/**
 * Gets the short weekday in a given locale.
 *
 * @param date - The date to format.
 * @param locale - The locale to use for formatting.
 * @returns - The short weekday.
 */
function getWeekdayShortInLocale(date, locale) {
    return formatDate(date, "EEE", locale);
}
/**
 * Gets the month in a given locale.
 *
 * @param month - The month to format.
 * @param locale - The locale to use for formatting.
 * @returns - The month.
 */
function getMonthInLocale(month, locale) {
    return formatDate(setMonth(newDate(), month), "LLLL", locale);
}
/**
 * Gets the short month in a given locale.
 *
 * @param month - The month to format.
 * @param locale - The locale to use for formatting.
 * @returns - The short month.
 */
function getMonthShortInLocale(month, locale) {
    return formatDate(setMonth(newDate(), month), "LLL", locale);
}
/**
 * Gets the short quarter in a given locale.
 *
 * @param quarter - The quarter to format.
 * @param locale - The locale to use for formatting.
 * @returns - The short quarter.
 */
function getQuarterShortInLocale(quarter, locale) {
    return formatDate(setQuarter(newDate(), quarter), "QQQ", locale);
}
/**
 * Checks if a day is disabled.
 *
 * @param day - The day to check.
 * @param options - The options to consider when checking.
 * @returns - Returns true if the day is disabled, false otherwise.
 */
function isDayDisabled(day, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, excludeDateIntervals = _b.excludeDateIntervals, includeDates = _b.includeDates, includeDateIntervals = _b.includeDateIntervals, filterDate = _b.filterDate;
    return (isOutOfBounds(day, { minDate: minDate, maxDate: maxDate }) ||
        (excludeDates &&
            excludeDates.some(function (excludeDate) {
                if (excludeDate instanceof Date) {
                    return isSameDay(day, excludeDate);
                }
                else {
                    return isSameDay(day, excludeDate.date);
                }
            })) ||
        (excludeDateIntervals &&
            excludeDateIntervals.some(function (_a) {
                var start = _a.start, end = _a.end;
                return isWithinInterval(day, { start: start, end: end });
            })) ||
        (includeDates &&
            !includeDates.some(function (includeDate) { return isSameDay(day, includeDate); })) ||
        (includeDateIntervals &&
            !includeDateIntervals.some(function (_a) {
                var start = _a.start, end = _a.end;
                return isWithinInterval(day, { start: start, end: end });
            })) ||
        (filterDate && !filterDate(newDate(day))) ||
        false);
}
/**
 * Checks if a day is excluded.
 *
 * @param day - The day to check.
 * @param options - The options to consider when checking.
 * @returns - Returns true if the day is excluded, false otherwise.
 */
function isDayExcluded(day, _a) {
    var _b = _a === void 0 ? {} : _a, excludeDates = _b.excludeDates, excludeDateIntervals = _b.excludeDateIntervals;
    if (excludeDateIntervals && excludeDateIntervals.length > 0) {
        return excludeDateIntervals.some(function (_a) {
            var start = _a.start, end = _a.end;
            return isWithinInterval(day, { start: start, end: end });
        });
    }
    return ((excludeDates &&
        excludeDates.some(function (excludeDate) {
            var _a;
            if (excludeDate instanceof Date) {
                return isSameDay(day, excludeDate);
            }
            else {
                return isSameDay(day, (_a = excludeDate.date) !== null && _a !== void 0 ? _a : new Date());
            }
        })) ||
        false);
}
function isMonthDisabled(month, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, includeDates = _b.includeDates, filterDate = _b.filterDate;
    return (isOutOfBounds(month, {
        minDate: minDate ? startOfMonth(minDate) : undefined,
        maxDate: maxDate ? endOfMonth(maxDate) : undefined,
    }) ||
        (excludeDates === null || excludeDates === void 0 ? void 0 : excludeDates.some(function (excludeDate) {
            return isSameMonth(month, excludeDate instanceof Date ? excludeDate : excludeDate.date);
        })) ||
        (includeDates &&
            !includeDates.some(function (includeDate) { return isSameMonth(month, includeDate); })) ||
        (filterDate && !filterDate(newDate(month))) ||
        false);
}
function isMonthInRange(startDate, endDate, m, day) {
    var startDateYear = getYear$1(startDate);
    var startDateMonth = getMonth(startDate);
    var endDateYear = getYear$1(endDate);
    var endDateMonth = getMonth(endDate);
    var dayYear = getYear$1(day);
    if (startDateYear === endDateYear && startDateYear === dayYear) {
        return startDateMonth <= m && m <= endDateMonth;
    }
    else if (startDateYear < endDateYear) {
        return ((dayYear === startDateYear && startDateMonth <= m) ||
            (dayYear === endDateYear && endDateMonth >= m) ||
            (dayYear < endDateYear && dayYear > startDateYear));
    }
    return false;
}
/**
 * To check if a date's month and year are disabled/excluded
 * @param date Date to check
 * @returns {boolean} true if month and year are disabled/excluded, false otherwise
 */
function isMonthYearDisabled(date, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, includeDates = _b.includeDates;
    return (isOutOfBounds(date, { minDate: minDate, maxDate: maxDate }) ||
        (excludeDates &&
            excludeDates.some(function (excludedDate) {
                return isSameMonth(excludedDate instanceof Date ? excludedDate : excludedDate.date, date);
            })) ||
        (includeDates &&
            !includeDates.some(function (includedDate) { return isSameMonth(includedDate, date); })) ||
        false);
}
function isQuarterDisabled(quarter, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, includeDates = _b.includeDates, filterDate = _b.filterDate;
    return (isOutOfBounds(quarter, { minDate: minDate, maxDate: maxDate }) ||
        (excludeDates === null || excludeDates === void 0 ? void 0 : excludeDates.some(function (excludeDate) {
            return isSameQuarter(quarter, excludeDate instanceof Date ? excludeDate : excludeDate.date);
        })) ||
        (includeDates &&
            !includeDates.some(function (includeDate) {
                return isSameQuarter(quarter, includeDate);
            })) ||
        (filterDate && !filterDate(newDate(quarter))) ||
        false);
}
function isYearInRange(year, start, end) {
    if (!start || !end)
        return false;
    if (!isValid$2(start) || !isValid$2(end))
        return false;
    var startYear = getYear$1(start);
    var endYear = getYear$1(end);
    return startYear <= year && endYear >= year;
}
function isYearDisabled(year, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, includeDates = _b.includeDates, filterDate = _b.filterDate;
    var date = new Date(year, 0, 1);
    return (isOutOfBounds(date, {
        minDate: minDate ? startOfYear$1(minDate) : undefined,
        maxDate: maxDate ? endOfYear(maxDate) : undefined,
    }) ||
        (excludeDates === null || excludeDates === void 0 ? void 0 : excludeDates.some(function (excludeDate) {
            return isSameYear(date, excludeDate instanceof Date ? excludeDate : excludeDate.date);
        })) ||
        (includeDates &&
            !includeDates.some(function (includeDate) { return isSameYear(date, includeDate); })) ||
        (filterDate && !filterDate(newDate(date))) ||
        false);
}
function isQuarterInRange(startDate, endDate, q, day) {
    var startDateYear = getYear$1(startDate);
    var startDateQuarter = getQuarter(startDate);
    var endDateYear = getYear$1(endDate);
    var endDateQuarter = getQuarter(endDate);
    var dayYear = getYear$1(day);
    if (startDateYear === endDateYear && startDateYear === dayYear) {
        return startDateQuarter <= q && q <= endDateQuarter;
    }
    else if (startDateYear < endDateYear) {
        return ((dayYear === startDateYear && startDateQuarter <= q) ||
            (dayYear === endDateYear && endDateQuarter >= q) ||
            (dayYear < endDateYear && dayYear > startDateYear));
    }
    return false;
}
function isOutOfBounds(day, _a) {
    var _b;
    var _c = _a === void 0 ? {} : _a, minDate = _c.minDate, maxDate = _c.maxDate;
    return ((_b = ((minDate && differenceInCalendarDays$1(day, minDate) < 0) ||
        (maxDate && differenceInCalendarDays$1(day, maxDate) > 0))) !== null && _b !== void 0 ? _b : false);
}
function isTimeInList(time, times) {
    return times.some(function (listTime) {
        return getHours(listTime) === getHours(time) &&
            getMinutes(listTime) === getMinutes(time) &&
            getSeconds(listTime) === getSeconds(time);
    });
}
function isTimeDisabled(time, _a) {
    var _b = _a === void 0 ? {} : _a, excludeTimes = _b.excludeTimes, includeTimes = _b.includeTimes, filterTime = _b.filterTime;
    return ((excludeTimes && isTimeInList(time, excludeTimes)) ||
        (includeTimes && !isTimeInList(time, includeTimes)) ||
        (filterTime && !filterTime(time)) ||
        false);
}
function isTimeInDisabledRange(time, _a) {
    var minTime = _a.minTime, maxTime = _a.maxTime;
    if (!minTime || !maxTime) {
        throw new Error("Both minTime and maxTime props required");
    }
    var baseTime = newDate();
    baseTime = setHours(baseTime, getHours(time));
    baseTime = setMinutes(baseTime, getMinutes(time));
    baseTime = setSeconds(baseTime, getSeconds(time));
    var min = newDate();
    min = setHours(min, getHours(minTime));
    min = setMinutes(min, getMinutes(minTime));
    min = setSeconds(min, getSeconds(minTime));
    var max = newDate();
    max = setHours(max, getHours(maxTime));
    max = setMinutes(max, getMinutes(maxTime));
    max = setSeconds(max, getSeconds(maxTime));
    var valid;
    try {
        valid = !isWithinInterval(baseTime, { start: min, end: max });
    }
    catch (err) {
        valid = false;
    }
    return valid;
}
function monthDisabledBefore(day, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, includeDates = _b.includeDates;
    var previousMonth = subMonths(day, 1);
    return ((minDate && differenceInCalendarMonths(minDate, previousMonth) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) {
                return differenceInCalendarMonths(includeDate, previousMonth) > 0;
            })) ||
        false);
}
function monthDisabledAfter(day, _a) {
    var _b = _a === void 0 ? {} : _a, maxDate = _b.maxDate, includeDates = _b.includeDates;
    var nextMonth = addMonths(day, 1);
    return ((maxDate && differenceInCalendarMonths(nextMonth, maxDate) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) { return differenceInCalendarMonths(nextMonth, includeDate) > 0; })) ||
        false);
}
function quarterDisabledBefore(date, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, includeDates = _b.includeDates;
    var firstDateOfYear = startOfYear$1(date);
    var previousQuarter = subQuarters(firstDateOfYear);
    return ((minDate && differenceInCalendarQuarters(minDate, previousQuarter) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) {
                return differenceInCalendarQuarters(includeDate, previousQuarter) > 0;
            })) ||
        false);
}
function quarterDisabledAfter(date, _a) {
    var _b = _a === void 0 ? {} : _a, maxDate = _b.maxDate, includeDates = _b.includeDates;
    var lastDateOfYear = endOfYear(date);
    var nextQuarter = addQuarters(lastDateOfYear, 1);
    return ((maxDate && differenceInCalendarQuarters(nextQuarter, maxDate) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) {
                return differenceInCalendarQuarters(nextQuarter, includeDate) > 0;
            })) ||
        false);
}
function yearDisabledBefore(day, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, includeDates = _b.includeDates;
    var previousYear = subYears(day, 1);
    return ((minDate && differenceInCalendarYears(minDate, previousYear) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) {
                return differenceInCalendarYears(includeDate, previousYear) > 0;
            })) ||
        false);
}
function yearsDisabledBefore(day, _a) {
    var _b = _a === void 0 ? {} : _a, minDate = _b.minDate, _c = _b.yearItemNumber, yearItemNumber = _c === void 0 ? DEFAULT_YEAR_ITEM_NUMBER : _c;
    var previousYear = getStartOfYear(subYears(day, yearItemNumber));
    var endPeriod = getYearsPeriod(previousYear, yearItemNumber).endPeriod;
    var minDateYear = minDate && getYear$1(minDate);
    return (minDateYear && minDateYear > endPeriod) || false;
}
function yearDisabledAfter(day, _a) {
    var _b = _a === void 0 ? {} : _a, maxDate = _b.maxDate, includeDates = _b.includeDates;
    var nextYear = addYears(day, 1);
    return ((maxDate && differenceInCalendarYears(nextYear, maxDate) > 0) ||
        (includeDates &&
            includeDates.every(function (includeDate) { return differenceInCalendarYears(nextYear, includeDate) > 0; })) ||
        false);
}
function yearsDisabledAfter(day, _a) {
    var _b = _a === void 0 ? {} : _a, maxDate = _b.maxDate, _c = _b.yearItemNumber, yearItemNumber = _c === void 0 ? DEFAULT_YEAR_ITEM_NUMBER : _c;
    var nextYear = addYears(day, yearItemNumber);
    var startPeriod = getYearsPeriod(nextYear, yearItemNumber).startPeriod;
    var maxDateYear = maxDate && getYear$1(maxDate);
    return (maxDateYear && maxDateYear < startPeriod) || false;
}
function getEffectiveMinDate(_a) {
    var minDate = _a.minDate, includeDates = _a.includeDates;
    if (includeDates && minDate) {
        var minDates = includeDates.filter(function (includeDate) { return differenceInCalendarDays$1(includeDate, minDate) >= 0; });
        return min$1(minDates);
    }
    else if (includeDates) {
        return min$1(includeDates);
    }
    else {
        return minDate;
    }
}
function getEffectiveMaxDate(_a) {
    var maxDate = _a.maxDate, includeDates = _a.includeDates;
    if (includeDates && maxDate) {
        var maxDates = includeDates.filter(function (includeDate) { return differenceInCalendarDays$1(includeDate, maxDate) <= 0; });
        return max$1(maxDates);
    }
    else if (includeDates) {
        return max$1(includeDates);
    }
    else {
        return maxDate;
    }
}
/**
 * Get a map of highlighted dates with their corresponding classes.
 * @param highlightDates The dates to highlight.
 * @param defaultClassName The default class to use for highlighting.
 * @returns A map with dates as keys and arrays of class names as values.
 */
function getHighLightDaysMap(highlightDates, defaultClassName) {
    var _a;
    if (highlightDates === void 0) { highlightDates = []; }
    if (defaultClassName === void 0) { defaultClassName = "react-datepicker__day--highlighted"; }
    var dateClasses = new Map();
    for (var i = 0, len = highlightDates.length; i < len; i++) {
        var obj = highlightDates[i];
        if (isDate$1(obj)) {
            var key = formatDate(obj, "MM.dd.yyyy");
            var classNamesArr = dateClasses.get(key) || [];
            if (!classNamesArr.includes(defaultClassName)) {
                classNamesArr.push(defaultClassName);
                dateClasses.set(key, classNamesArr);
            }
        }
        else if (typeof obj === "object") {
            var keys = Object.keys(obj);
            var className = (_a = keys[0]) !== null && _a !== void 0 ? _a : "";
            var arrOfDates = obj[className];
            if (typeof className === "string" && Array.isArray(arrOfDates)) {
                for (var k = 0, len_1 = arrOfDates.length; k < len_1; k++) {
                    var dateK = arrOfDates[k];
                    if (dateK) {
                        var key = formatDate(dateK, "MM.dd.yyyy");
                        var classNamesArr = dateClasses.get(key) || [];
                        if (!classNamesArr.includes(className)) {
                            classNamesArr.push(className);
                            dateClasses.set(key, classNamesArr);
                        }
                    }
                }
            }
        }
    }
    return dateClasses;
}
/**
 * Compare the two arrays
 * @param array1 The first array to compare.
 * @param array2 The second array to compare.
 * @returns true, if the passed arrays are equal, false otherwise.
 */
function arraysAreEqual(array1, array2) {
    if (array1.length !== array2.length) {
        return false;
    }
    return array1.every(function (value, index) { return value === array2[index]; });
}
/**
 * Assign the custom class to each date
 * @param holidayDates array of object containing date and name of the holiday
 * @param defaultClassName className to be added.
 * @returns Map containing date as key and array of className and holiday name as value
 */
function getHolidaysMap(holidayDates, defaultClassName) {
    if (holidayDates === void 0) { holidayDates = []; }
    if (defaultClassName === void 0) { defaultClassName = "react-datepicker__day--holidays"; }
    var dateClasses = new Map();
    holidayDates.forEach(function (holiday) {
        var dateObj = holiday.date, holidayName = holiday.holidayName;
        if (!isDate$1(dateObj)) {
            return;
        }
        var key = formatDate(dateObj, "MM.dd.yyyy");
        var classNamesObj = dateClasses.get(key) || {
            className: "",
            holidayNames: [],
        };
        if ("className" in classNamesObj &&
            classNamesObj["className"] === defaultClassName &&
            arraysAreEqual(classNamesObj["holidayNames"], [holidayName])) {
            return;
        }
        classNamesObj["className"] = defaultClassName;
        var holidayNameArr = classNamesObj["holidayNames"];
        classNamesObj["holidayNames"] = holidayNameArr
            ? __spreadArray(__spreadArray([], holidayNameArr, true), [holidayName], false) : [holidayName];
        dateClasses.set(key, classNamesObj);
    });
    return dateClasses;
}
/**
 * Determines the times to inject after a given start of day, current time, and multiplier.
 * @param startOfDay The start of the day.
 * @param currentTime The current time.
 * @param currentMultiplier The current multiplier.
 * @param intervals The intervals.
 * @param injectedTimes The times to potentially inject.
 * @returns An array of times to inject.
 */
function timesToInjectAfter(startOfDay, currentTime, currentMultiplier, intervals, injectedTimes) {
    var l = injectedTimes.length;
    var times = [];
    for (var i = 0; i < l; i++) {
        var injectedTime = startOfDay;
        var injectedTimeValue = injectedTimes[i];
        if (injectedTimeValue) {
            injectedTime = addHours(injectedTime, getHours(injectedTimeValue));
            injectedTime = addMinutes(injectedTime, getMinutes(injectedTimeValue));
            injectedTime = addSeconds(injectedTime, getSeconds(injectedTimeValue));
        }
        var nextTime = addMinutes(startOfDay, (currentMultiplier + 1) * intervals);
        if (isAfter(injectedTime, currentTime) &&
            isBefore(injectedTime, nextTime) &&
            injectedTimeValue != undefined) {
            times.push(injectedTimeValue);
        }
    }
    return times;
}
/**
 * Adds a leading zero to a number if it's less than 10.
 * @param i The number to add a leading zero to.
 * @returns The number as a string, with a leading zero if it was less than 10.
 */
function addZero(i) {
    return i < 10 ? "0".concat(i) : "".concat(i);
}
/**
 * Gets the start and end years for a period.
 * @param date The date to get the period for.
 * @param yearItemNumber The number of years in the period. Defaults to DEFAULT_YEAR_ITEM_NUMBER.
 * @returns An object with the start and end years for the period.
 */
function getYearsPeriod(date, yearItemNumber) {
    if (yearItemNumber === void 0) { yearItemNumber = DEFAULT_YEAR_ITEM_NUMBER; }
    var endPeriod = Math.ceil(getYear$1(date) / yearItemNumber) * yearItemNumber;
    var startPeriod = endPeriod - (yearItemNumber - 1);
    return { startPeriod: startPeriod, endPeriod: endPeriod };
}
/**
 * Gets the number of hours in a day.
 * @param d The date to get the number of hours for.
 * @returns The number of hours in the day.
 */
function getHoursInDay(d) {
    var startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    var startOfTheNextDay = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 24);
    return Math.round((+startOfTheNextDay - +startOfDay) / 3600000);
}
/**
 * Returns the start of the minute for the given date
 *
 * NOTE: this function is a DST and timezone-safe analog of `date-fns/startOfMinute`
 * do not make changes unless you know what you're doing
 *
 * See comments on https://github.com/Hacker0x01/react-datepicker/pull/4244
 * for more details
 *
 * @param d date
 * @returns start of the minute
 */
function startOfMinute(d) {
    var seconds = d.getSeconds();
    var milliseconds = d.getMilliseconds();
    return toDate(d.getTime() - seconds * 1000 - milliseconds);
}
/**
 * Returns whether the given dates are in the same minute
 *
 * This function is a DST and timezone-safe analog of `date-fns/isSameMinute`
 *
 * @param d1
 * @param d2
 * @returns
 */
function isSameMinute(d1, d2) {
    return startOfMinute(d1).getTime() === startOfMinute(d2).getTime();
}
/**
 * Returns a new datetime object representing the input date with midnight time
 * @param date The date to get the midnight time for
 * @returns A new datetime object representing the input date with midnight time
 */
function getMidnightDate(date) {
    if (!isDate$1(date)) {
        throw new Error("Invalid date");
    }
    var dateWithoutTime = new Date(date);
    dateWithoutTime.setHours(0, 0, 0, 0);
    return dateWithoutTime;
}
/**
 * Is the first date before the second one?
 * @param date The date that should be before the other one to return true
 * @param dateToCompare The date to compare with
 * @returns The first date is before the second date
 *
 * Note:
 *  This function considers the mid-night of the given dates for comparison.
 *  It evaluates whether date is before dateToCompare based on their mid-night timestamps.
 */
function isDateBefore(date, dateToCompare) {
    if (!isDate$1(date) || !isDate$1(dateToCompare)) {
        throw new Error("Invalid date received");
    }
    var midnightDate = getMidnightDate(date);
    var midnightDateToCompare = getMidnightDate(dateToCompare);
    return isBefore(midnightDate, midnightDateToCompare);
}
/**
 * Checks if the space key was pressed down.
 *
 * @param event - The keyboard event.
 * @returns - Returns true if the space key was pressed down, false otherwise.
 */
function isSpaceKeyDown(event) {
    return event.key === KeyType.Space;
}

/**
 * `InputTime` is a React component that manages time input.
 *
 * @component
 * @example
 * <InputTime timeString="12:00" />
 *
 * @param props - The properties that define the `InputTime` component.
 * @param props.onChange - Function that is called when the date changes.
 * @param props.date - The initial date value.
 * @param props.timeString - The initial time string value.
 * @param props.timeInputLabel - The label for the time input.
 * @param props.customTimeInput - An optional custom time input element.
 *
 * @returns The `InputTime` component.
 */
var InputTime = /** @class */ (function (_super) {
    __extends(InputTime, _super);
    function InputTime(props) {
        var _this = _super.call(this, props) || this;
        _this.inputRef = React__default.createRef();
        _this.onTimeChange = function (time) {
            var _a, _b;
            _this.setState({ time: time });
            var propDate = _this.props.date;
            var isPropDateValid = propDate instanceof Date && !isNaN(+propDate);
            var date = isPropDateValid ? propDate : new Date();
            if (time === null || time === void 0 ? void 0 : time.includes(":")) {
                var _c = time.split(":"), hours = _c[0], minutes = _c[1];
                date.setHours(Number(hours));
                date.setMinutes(Number(minutes));
            }
            (_b = (_a = _this.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, date);
        };
        _this.renderTimeInput = function () {
            var time = _this.state.time;
            var _a = _this.props, date = _a.date, timeString = _a.timeString, customTimeInput = _a.customTimeInput;
            if (customTimeInput) {
                return cloneElement(customTimeInput, {
                    date: date,
                    value: time,
                    onChange: _this.onTimeChange,
                });
            }
            return (React__default.createElement("input", { type: "time", className: "react-datepicker-time__input", placeholder: "Time", name: "time-input", ref: _this.inputRef, onClick: function () {
                    var _a;
                    (_a = _this.inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, required: true, value: time, onChange: function (event) {
                    _this.onTimeChange(event.target.value || timeString);
                } }));
        };
        _this.state = {
            time: _this.props.timeString,
        };
        return _this;
    }
    InputTime.getDerivedStateFromProps = function (props, state) {
        if (props.timeString !== state.time) {
            return {
                time: props.timeString,
            };
        }
        // Return null to indicate no change to state.
        return null;
    };
    InputTime.prototype.render = function () {
        return (React__default.createElement("div", { className: "react-datepicker__input-time-container" },
            React__default.createElement("div", { className: "react-datepicker-time__caption" }, this.props.timeInputLabel),
            React__default.createElement("div", { className: "react-datepicker-time__input-container" },
                React__default.createElement("div", { className: "react-datepicker-time__input" }, this.renderTimeInput()))));
    };
    return InputTime;
}(Component));

/**
 * `Day` is a React component that represents a single day in a date picker.
 * It handles the rendering and interaction of a day.
 *
 * @prop ariaLabelPrefixWhenEnabled - Aria label prefix when the day is enabled.
 * @prop ariaLabelPrefixWhenDisabled - Aria label prefix when the day is disabled.
 * @prop disabledKeyboardNavigation - Whether keyboard navigation is disabled.
 * @prop day - The day to be displayed.
 * @prop dayClassName - Function to customize the CSS class of the day.
 * @prop endDate - The end date in a range.
 * @prop highlightDates - Map of dates to be highlighted.
 * @prop holidays - Map of holiday dates.
 * @prop inline - Whether the date picker is inline.
 * @prop shouldFocusDayInline - Whether the day should be focused when date picker is inline.
 * @prop month - The month the day belongs to.
 * @prop onClick - Click event handler.
 * @prop onMouseEnter - Mouse enter event handler.
 * @prop handleOnKeyDown - Key down event handler.
 * @prop usePointerEvent - Whether to use pointer events.
 * @prop preSelection - The date that is currently selected.
 * @prop selected - The selected date.
 * @prop selectingDate - The date currently being selected.
 * @prop selectsEnd - Whether the day can be the end date in a range.
 * @prop selectsStart - Whether the day can be the start date in a range.
 * @prop selectsRange - Whether the day can be in a range.
 * @prop showWeekPicker - Whether to show week picker.
 * @prop showWeekNumber - Whether to show week numbers.
 * @prop selectsDisabledDaysInRange - Whether to select disabled days in a range.
 * @prop selectsMultiple - Whether to allow multiple date selection.
 * @prop selectedDates - Array of selected dates.
 * @prop startDate - The start date in a range.
 * @prop renderDayContents - Function to customize the rendering of the day's contents.
 * @prop containerRef - Ref for the container.
 * @prop excludeDates - Array of dates to be excluded.
 * @prop calendarStartDay - The start day of the week.
 * @prop locale - The locale object.
 * @prop monthShowsDuplicateDaysEnd - Whether to show duplicate days at the end of the month.
 * @prop monthShowsDuplicateDaysStart - Whether to show duplicate days at the start of the month.
 * @prop includeDates - Array of dates to be included.
 * @prop includeDateIntervals - Array of date intervals to be included.
 * @prop minDate - The minimum date that can be selected.
 * @prop maxDate - The maximum date that can be selected.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import Day from './day';
 *
 * function MyComponent() {
 *   const handleDayClick = (event) => {
 *     console.log('Day clicked', event);
 *   };
 *
 *   const handleDayMouseEnter = (event) => {
 *     console.log('Mouse entered day', event);
 *   };
 *
 *   const renderDayContents = (date) => {
 *     return <div>{date.getDate()}</div>;
 *   };
 *
 *   return (
 *     <Day
 *       day={new Date()}
 *       onClick={handleDayClick}
 *       onMouseEnter={handleDayMouseEnter}
 *       renderDayContents={renderDayContents}
 *     />
 *   );
 * }
 *
 * export default MyComponent;
 * ```
 */
var Day = /** @class */ (function (_super) {
    __extends(Day, _super);
    function Day() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.dayEl = createRef();
        _this.handleClick = function (event) {
            if (!_this.isDisabled() && _this.props.onClick) {
                _this.props.onClick(event);
            }
        };
        _this.handleMouseEnter = function (event) {
            if (!_this.isDisabled() && _this.props.onMouseEnter) {
                _this.props.onMouseEnter(event);
            }
        };
        _this.handleOnKeyDown = function (event) {
            var _a, _b;
            var eventKey = event.key;
            if (eventKey === KeyType.Space) {
                event.preventDefault();
                event.key = KeyType.Enter;
            }
            (_b = (_a = _this.props).handleOnKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        };
        _this.isSameDay = function (other) {
            return isSameDay(_this.props.day, other);
        };
        _this.isKeyboardSelected = function () {
            var _a;
            if (_this.props.disabledKeyboardNavigation) {
                return false;
            }
            var isSelectedDate = _this.props.selectsMultiple
                ? (_a = _this.props.selectedDates) === null || _a === void 0 ? void 0 : _a.some(function (date) { return _this.isSameDayOrWeek(date); })
                : _this.isSameDayOrWeek(_this.props.selected);
            var isDisabled = _this.props.preSelection && _this.isDisabled(_this.props.preSelection);
            return (!isSelectedDate &&
                _this.isSameDayOrWeek(_this.props.preSelection) &&
                !isDisabled);
        };
        _this.isDisabled = function (day) {
            if (day === void 0) { day = _this.props.day; }
            // Almost all props previously were passed as this.props w/o proper typing with prop-types
            // after the migration to TS i made it explicit
            return isDayDisabled(day, {
                minDate: _this.props.minDate,
                maxDate: _this.props.maxDate,
                excludeDates: _this.props.excludeDates,
                excludeDateIntervals: _this.props.excludeDateIntervals,
                includeDateIntervals: _this.props.includeDateIntervals,
                includeDates: _this.props.includeDates,
                filterDate: _this.props.filterDate,
            });
        };
        _this.isExcluded = function () {
            // Almost all props previously were passed as this.props w/o proper typing with prop-types
            // after the migration to TS i made it explicit
            return isDayExcluded(_this.props.day, {
                excludeDates: _this.props.excludeDates,
                excludeDateIntervals: _this.props.excludeDateIntervals,
            });
        };
        _this.isStartOfWeek = function () {
            return isSameDay(_this.props.day, getStartOfWeek(_this.props.day, _this.props.locale, _this.props.calendarStartDay));
        };
        _this.isSameWeek = function (other) {
            return _this.props.showWeekPicker &&
                isSameDay(other, getStartOfWeek(_this.props.day, _this.props.locale, _this.props.calendarStartDay));
        };
        _this.isSameDayOrWeek = function (other) {
            return _this.isSameDay(other) || _this.isSameWeek(other);
        };
        _this.getHighLightedClass = function () {
            var _a = _this.props, day = _a.day, highlightDates = _a.highlightDates;
            if (!highlightDates) {
                return false;
            }
            // Looking for className in the Map of {'day string, 'className'}
            var dayStr = formatDate(day, "MM.dd.yyyy");
            return highlightDates.get(dayStr);
        };
        // Function to return the array containing className associated to the date
        _this.getHolidaysClass = function () {
            var _a;
            var _b = _this.props, day = _b.day, holidays = _b.holidays;
            if (!holidays) {
                // For type consistency no other reasons
                return [undefined];
            }
            var dayStr = formatDate(day, "MM.dd.yyyy");
            // Looking for className in the Map of {day string: {className, holidayName}}
            if (holidays.has(dayStr)) {
                return [(_a = holidays.get(dayStr)) === null || _a === void 0 ? void 0 : _a.className];
            }
            // For type consistency no other reasons
            return [undefined];
        };
        _this.isInRange = function () {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isDayInRange(day, startDate, endDate);
        };
        _this.isInSelectingRange = function () {
            var _a;
            var _b = _this.props, day = _b.day, selectsStart = _b.selectsStart, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange, selectsDisabledDaysInRange = _b.selectsDisabledDaysInRange, startDate = _b.startDate, endDate = _b.endDate;
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (!(selectsStart || selectsEnd || selectsRange) ||
                !selectingDate ||
                (!selectsDisabledDaysInRange && _this.isDisabled())) {
                return false;
            }
            if (selectsStart &&
                endDate &&
                (isBefore(selectingDate, endDate) || isEqual(selectingDate, endDate))) {
                return isDayInRange(day, selectingDate, endDate);
            }
            if (selectsEnd &&
                startDate &&
                (isAfter(selectingDate, startDate) || isEqual(selectingDate, startDate))) {
                return isDayInRange(day, startDate, selectingDate);
            }
            if (selectsRange &&
                startDate &&
                !endDate &&
                (isAfter(selectingDate, startDate) || isEqual(selectingDate, startDate))) {
                return isDayInRange(day, startDate, selectingDate);
            }
            return false;
        };
        _this.isSelectingRangeStart = function () {
            var _a;
            if (!_this.isInSelectingRange()) {
                return false;
            }
            var _b = _this.props, day = _b.day, startDate = _b.startDate, selectsStart = _b.selectsStart;
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (selectsStart) {
                return isSameDay(day, selectingDate);
            }
            else {
                return isSameDay(day, startDate);
            }
        };
        _this.isSelectingRangeEnd = function () {
            var _a;
            if (!_this.isInSelectingRange()) {
                return false;
            }
            var _b = _this.props, day = _b.day, endDate = _b.endDate, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange;
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (selectsEnd || selectsRange) {
                return isSameDay(day, selectingDate);
            }
            else {
                return isSameDay(day, endDate);
            }
        };
        _this.isRangeStart = function () {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameDay(startDate, day);
        };
        _this.isRangeEnd = function () {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameDay(endDate, day);
        };
        _this.isWeekend = function () {
            var weekday = getDay(_this.props.day);
            return weekday === 0 || weekday === 6;
        };
        _this.isAfterMonth = function () {
            return (_this.props.month !== undefined &&
                (_this.props.month + 1) % 12 === getMonth(_this.props.day));
        };
        _this.isBeforeMonth = function () {
            return (_this.props.month !== undefined &&
                (getMonth(_this.props.day) + 1) % 12 === _this.props.month);
        };
        _this.isCurrentDay = function () { return _this.isSameDay(newDate()); };
        _this.isSelected = function () {
            var _a;
            if (_this.props.selectsMultiple) {
                return (_a = _this.props.selectedDates) === null || _a === void 0 ? void 0 : _a.some(function (date) {
                    return _this.isSameDayOrWeek(date);
                });
            }
            return _this.isSameDayOrWeek(_this.props.selected);
        };
        _this.getClassNames = function (date) {
            var dayClassName = _this.props.dayClassName
                ? _this.props.dayClassName(date)
                : undefined;
            return clsx("react-datepicker__day", dayClassName, "react-datepicker__day--" + getDayOfWeekCode(_this.props.day), {
                "react-datepicker__day--disabled": _this.isDisabled(),
                "react-datepicker__day--excluded": _this.isExcluded(),
                "react-datepicker__day--selected": _this.isSelected(),
                "react-datepicker__day--keyboard-selected": _this.isKeyboardSelected(),
                "react-datepicker__day--range-start": _this.isRangeStart(),
                "react-datepicker__day--range-end": _this.isRangeEnd(),
                "react-datepicker__day--in-range": _this.isInRange(),
                "react-datepicker__day--in-selecting-range": _this.isInSelectingRange(),
                "react-datepicker__day--selecting-range-start": _this.isSelectingRangeStart(),
                "react-datepicker__day--selecting-range-end": _this.isSelectingRangeEnd(),
                "react-datepicker__day--today": _this.isCurrentDay(),
                "react-datepicker__day--weekend": _this.isWeekend(),
                "react-datepicker__day--outside-month": _this.isAfterMonth() || _this.isBeforeMonth(),
            }, _this.getHighLightedClass(), _this.getHolidaysClass());
        };
        _this.getAriaLabel = function () {
            var _a = _this.props, day = _a.day, _b = _a.ariaLabelPrefixWhenEnabled, ariaLabelPrefixWhenEnabled = _b === void 0 ? "Choose" : _b, _c = _a.ariaLabelPrefixWhenDisabled, ariaLabelPrefixWhenDisabled = _c === void 0 ? "Not available" : _c;
            var prefix = _this.isDisabled() || _this.isExcluded()
                ? ariaLabelPrefixWhenDisabled
                : ariaLabelPrefixWhenEnabled;
            return "".concat(prefix, " ").concat(formatDate(day, "PPPP", _this.props.locale));
        };
        // A function to return the holiday's name as title's content
        _this.getTitle = function () {
            var _a = _this.props, day = _a.day, _b = _a.holidays, holidays = _b === void 0 ? new Map() : _b, excludeDates = _a.excludeDates;
            var compareDt = formatDate(day, "MM.dd.yyyy");
            var titles = [];
            if (holidays.has(compareDt)) {
                titles.push.apply(titles, holidays.get(compareDt).holidayNames);
            }
            if (_this.isExcluded()) {
                titles.push(excludeDates === null || excludeDates === void 0 ? void 0 : excludeDates.filter(function (excludeDate) {
                    if (excludeDate instanceof Date) {
                        return isSameDay(excludeDate, day);
                    }
                    return isSameDay(excludeDate === null || excludeDate === void 0 ? void 0 : excludeDate.date, day);
                }).map(function (excludeDate) {
                    if (excludeDate instanceof Date) {
                        return undefined;
                    }
                    return excludeDate === null || excludeDate === void 0 ? void 0 : excludeDate.message;
                }));
            }
            // I'm not sure that this is a right output, but all tests are green
            return titles.join(", ");
        };
        _this.getTabIndex = function () {
            var selectedDay = _this.props.selected;
            var preSelectionDay = _this.props.preSelection;
            var tabIndex = !(_this.props.showWeekPicker &&
                (_this.props.showWeekNumber || !_this.isStartOfWeek())) &&
                (_this.isKeyboardSelected() ||
                    (_this.isSameDay(selectedDay) &&
                        isSameDay(preSelectionDay, selectedDay)))
                ? 0
                : -1;
            return tabIndex;
        };
        // various cases when we need to apply focus to the preselected day
        // focus the day on mount/update so that keyboard navigation works while cycling through months with up or down keys (not for prev and next month buttons)
        // prevent focus for these activeElement cases so we don't pull focus from the input as the calendar opens
        _this.handleFocusDay = function () {
            var _a;
            // only do this while the input isn't focused
            // otherwise, typing/backspacing the date manually may steal focus away from the input
            _this.shouldFocusDay() && ((_a = _this.dayEl.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true }));
        };
        _this.renderDayContents = function () {
            if (_this.props.monthShowsDuplicateDaysEnd && _this.isAfterMonth())
                return null;
            if (_this.props.monthShowsDuplicateDaysStart && _this.isBeforeMonth())
                return null;
            return _this.props.renderDayContents
                ? _this.props.renderDayContents(getDate(_this.props.day), _this.props.day)
                : getDate(_this.props.day);
        };
        _this.render = function () { return (
        // TODO: Use <option> instead of the "option" role to ensure accessibility across all devices.
        React__default.createElement("div", { ref: _this.dayEl, className: _this.getClassNames(_this.props.day), onKeyDown: _this.handleOnKeyDown, onClick: _this.handleClick, onMouseEnter: !_this.props.usePointerEvent ? _this.handleMouseEnter : undefined, onPointerEnter: _this.props.usePointerEvent ? _this.handleMouseEnter : undefined, tabIndex: _this.getTabIndex(), "aria-label": _this.getAriaLabel(), role: "option", title: _this.getTitle(), "aria-disabled": _this.isDisabled(), "aria-current": _this.isCurrentDay() ? "date" : undefined, "aria-selected": _this.isSelected() || _this.isInRange() },
            _this.renderDayContents(),
            _this.getTitle() !== "" && (React__default.createElement("span", { className: "overlay" }, _this.getTitle())))); };
        return _this;
    }
    Day.prototype.componentDidMount = function () {
        this.handleFocusDay();
    };
    Day.prototype.componentDidUpdate = function () {
        this.handleFocusDay();
    };
    Day.prototype.shouldFocusDay = function () {
        var shouldFocusDay = false;
        if (this.getTabIndex() === 0 && this.isSameDay(this.props.preSelection)) {
            // there is currently no activeElement and not inline
            if (!document.activeElement || document.activeElement === document.body) {
                shouldFocusDay = true;
            }
            // inline version:
            // do not focus on initial render to prevent autoFocus issue
            // focus after month has changed via keyboard
            if (this.props.inline && !this.props.shouldFocusDayInline) {
                shouldFocusDay = false;
            }
            if (this.isDayActiveElement()) {
                shouldFocusDay = true;
            }
            if (this.isDuplicateDay()) {
                shouldFocusDay = false;
            }
        }
        return shouldFocusDay;
    };
    // the activeElement is in the container, and it is another instance of Day
    Day.prototype.isDayActiveElement = function () {
        var _a, _b, _c;
        return (((_b = (_a = this.props.containerRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement)) &&
            ((_c = document.activeElement) === null || _c === void 0 ? void 0 : _c.classList.contains("react-datepicker__day")));
    };
    Day.prototype.isDuplicateDay = function () {
        return (
        //day is one of the non rendered duplicate days
        (this.props.monthShowsDuplicateDaysEnd && this.isAfterMonth()) ||
            (this.props.monthShowsDuplicateDaysStart && this.isBeforeMonth()));
    };
    return Day;
}(Component));

var WeekNumber = /** @class */ (function (_super) {
    __extends(WeekNumber, _super);
    function WeekNumber() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.weekNumberEl = createRef();
        _this.handleClick = function (event) {
            if (_this.props.onClick) {
                _this.props.onClick(event);
            }
        };
        _this.handleOnKeyDown = function (event) {
            var _a, _b;
            var eventKey = event.key;
            if (eventKey === KeyType.Space) {
                event.preventDefault();
                event.key = KeyType.Enter;
            }
            (_b = (_a = _this.props).handleOnKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        };
        _this.isKeyboardSelected = function () {
            return !_this.props.disabledKeyboardNavigation &&
                !isSameDay(_this.props.date, _this.props.selected) &&
                isSameDay(_this.props.date, _this.props.preSelection);
        };
        _this.getTabIndex = function () {
            return _this.props.showWeekPicker &&
                _this.props.showWeekNumber &&
                (_this.isKeyboardSelected() ||
                    (isSameDay(_this.props.date, _this.props.selected) &&
                        isSameDay(_this.props.preSelection, _this.props.selected)))
                ? 0
                : -1;
        };
        // various cases when we need to apply focus to the preselected week-number
        // focus the week-number on mount/update so that keyboard navigation works while cycling through months with up or down keys (not for prev and next month buttons)
        // prevent focus for these activeElement cases so we don't pull focus from the input as the calendar opens
        _this.handleFocusWeekNumber = function (prevProps) {
            var shouldFocusWeekNumber = false;
            // only do this while the input isn't focused
            // otherwise, typing/backspacing the date manually may steal focus away from the input
            if (_this.getTabIndex() === 0 &&
                !(prevProps === null || prevProps === void 0 ? void 0 : prevProps.isInputFocused) &&
                isSameDay(_this.props.date, _this.props.preSelection)) {
                // there is currently no activeElement and not inline
                if (!document.activeElement || document.activeElement === document.body) {
                    shouldFocusWeekNumber = true;
                }
                // inline version:
                // do not focus on initial render to prevent autoFocus issue
                // focus after month has changed via keyboard
                if (_this.props.inline && !_this.props.shouldFocusDayInline) {
                    shouldFocusWeekNumber = false;
                }
                // the activeElement is in the container, and it is another instance of WeekNumber
                if (_this.props.containerRef &&
                    _this.props.containerRef.current &&
                    _this.props.containerRef.current.contains(document.activeElement) &&
                    document.activeElement &&
                    document.activeElement.classList.contains("react-datepicker__week-number")) {
                    shouldFocusWeekNumber = true;
                }
            }
            shouldFocusWeekNumber &&
                _this.weekNumberEl.current &&
                _this.weekNumberEl.current.focus({ preventScroll: true });
        };
        return _this;
    }
    Object.defineProperty(WeekNumber, "defaultProps", {
        get: function () {
            return {
                ariaLabelPrefix: "week ",
            };
        },
        enumerable: false,
        configurable: true
    });
    WeekNumber.prototype.componentDidMount = function () {
        this.handleFocusWeekNumber();
    };
    WeekNumber.prototype.componentDidUpdate = function (prevProps) {
        this.handleFocusWeekNumber(prevProps);
    };
    WeekNumber.prototype.render = function () {
        var _a = this.props, weekNumber = _a.weekNumber, isWeekDisabled = _a.isWeekDisabled, _b = _a.ariaLabelPrefix, ariaLabelPrefix = _b === void 0 ? WeekNumber.defaultProps.ariaLabelPrefix : _b, onClick = _a.onClick;
        var weekNumberClasses = {
            "react-datepicker__week-number": true,
            "react-datepicker__week-number--clickable": !!onClick && !isWeekDisabled,
            "react-datepicker__week-number--selected": !!onClick && isSameDay(this.props.date, this.props.selected),
        };
        return (React__default.createElement("div", { ref: this.weekNumberEl, className: clsx(weekNumberClasses), "aria-label": "".concat(ariaLabelPrefix, " ").concat(this.props.weekNumber), onClick: this.handleClick, onKeyDown: this.handleOnKeyDown, tabIndex: this.getTabIndex() }, weekNumber));
    };
    return WeekNumber;
}(Component));

var Week = /** @class */ (function (_super) {
    __extends(Week, _super);
    function Week() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.isDisabled = function (day) {
            return isDayDisabled(day, {
                minDate: _this.props.minDate,
                maxDate: _this.props.maxDate,
                excludeDates: _this.props.excludeDates,
                excludeDateIntervals: _this.props.excludeDateIntervals,
                includeDateIntervals: _this.props.includeDateIntervals,
                includeDates: _this.props.includeDates,
                filterDate: _this.props.filterDate,
            });
        };
        _this.handleDayClick = function (day, event) {
            if (_this.props.onDayClick) {
                _this.props.onDayClick(day, event);
            }
        };
        _this.handleDayMouseEnter = function (day) {
            if (_this.props.onDayMouseEnter) {
                _this.props.onDayMouseEnter(day);
            }
        };
        _this.handleWeekClick = function (day, weekNumber, event) {
            var _a, _b, _c;
            var enabledWeekDay = new Date(day);
            for (var i = 0; i < 7; i++) {
                var processingDay = new Date(day);
                processingDay.setDate(processingDay.getDate() + i);
                var isEnabled = !_this.isDisabled(processingDay);
                if (isEnabled) {
                    enabledWeekDay = processingDay;
                    break;
                }
            }
            if (typeof _this.props.onWeekSelect === "function") {
                _this.props.onWeekSelect(enabledWeekDay, weekNumber, event);
            }
            if (_this.props.showWeekPicker) {
                _this.handleDayClick(enabledWeekDay, event);
            }
            if ((_a = _this.props.shouldCloseOnSelect) !== null && _a !== void 0 ? _a : Week.defaultProps.shouldCloseOnSelect) {
                (_c = (_b = _this.props).setOpen) === null || _c === void 0 ? void 0 : _c.call(_b, false);
            }
        };
        _this.formatWeekNumber = function (date) {
            if (_this.props.formatWeekNumber) {
                return _this.props.formatWeekNumber(date);
            }
            return getWeek$1(date);
        };
        _this.isWeekDisabled = function () {
            var startOfWeek = _this.startOfWeek();
            var endOfWeek = addDays(startOfWeek, 6);
            var processingDate = new Date(startOfWeek);
            while (processingDate <= endOfWeek) {
                if (!_this.isDisabled(processingDate))
                    return false;
                processingDate = addDays(processingDate, 1);
            }
            return true;
        };
        _this.renderDays = function () {
            var startOfWeek = _this.startOfWeek();
            var days = [];
            var weekNumber = _this.formatWeekNumber(startOfWeek);
            if (_this.props.showWeekNumber) {
                var onClickAction = _this.props.onWeekSelect || _this.props.showWeekPicker
                    ? _this.handleWeekClick.bind(_this, startOfWeek, weekNumber)
                    : undefined;
                days.push(React__default.createElement(WeekNumber, _assign({ key: "W" }, Week.defaultProps, _this.props, { weekNumber: weekNumber, isWeekDisabled: _this.isWeekDisabled(), date: startOfWeek, onClick: onClickAction })));
            }
            return days.concat([0, 1, 2, 3, 4, 5, 6].map(function (offset) {
                var day = addDays(startOfWeek, offset);
                return (React__default.createElement(Day, _assign({}, Week.defaultProps, _this.props, { ariaLabelPrefixWhenEnabled: _this.props.chooseDayAriaLabelPrefix, ariaLabelPrefixWhenDisabled: _this.props.disabledDayAriaLabelPrefix, key: day.valueOf(), day: day, onClick: _this.handleDayClick.bind(_this, day), onMouseEnter: _this.handleDayMouseEnter.bind(_this, day) })));
            }));
        };
        _this.startOfWeek = function () {
            return getStartOfWeek(_this.props.day, _this.props.locale, _this.props.calendarStartDay);
        };
        _this.isKeyboardSelected = function () {
            return !_this.props.disabledKeyboardNavigation &&
                !isSameDay(_this.startOfWeek(), _this.props.selected) &&
                isSameDay(_this.startOfWeek(), _this.props.preSelection);
        };
        return _this;
    }
    Object.defineProperty(Week, "defaultProps", {
        get: function () {
            return {
                shouldCloseOnSelect: true,
            };
        },
        enumerable: false,
        configurable: true
    });
    Week.prototype.render = function () {
        var weekNumberClasses = {
            "react-datepicker__week": true,
            "react-datepicker__week--selected": isSameDay(this.startOfWeek(), this.props.selected),
            "react-datepicker__week--keyboard-selected": this.isKeyboardSelected(),
        };
        return React__default.createElement("div", { className: clsx(weekNumberClasses) }, this.renderDays());
    };
    return Week;
}(Component));

var _a;
var FIXED_HEIGHT_STANDARD_WEEK_COUNT = 6;
var MONTH_COLUMNS_LAYOUT = {
    TWO_COLUMNS: "two_columns",
    THREE_COLUMNS: "three_columns",
    FOUR_COLUMNS: "four_columns",
};
var MONTH_COLUMNS = (_a = {},
    _a[MONTH_COLUMNS_LAYOUT.TWO_COLUMNS] = {
        grid: [
            [0, 1],
            [2, 3],
            [4, 5],
            [6, 7],
            [8, 9],
            [10, 11],
        ],
        verticalNavigationOffset: 2,
    },
    _a[MONTH_COLUMNS_LAYOUT.THREE_COLUMNS] = {
        grid: [
            [0, 1, 2],
            [3, 4, 5],
            [6, 7, 8],
            [9, 10, 11],
        ],
        verticalNavigationOffset: 3,
    },
    _a[MONTH_COLUMNS_LAYOUT.FOUR_COLUMNS] = {
        grid: [
            [0, 1, 2, 3],
            [4, 5, 6, 7],
            [8, 9, 10, 11],
        ],
        verticalNavigationOffset: 4,
    },
    _a);
var MONTH_NAVIGATION_HORIZONTAL_OFFSET = 1;
function getMonthColumnsLayout(showFourColumnMonthYearPicker, showTwoColumnMonthYearPicker) {
    if (showFourColumnMonthYearPicker) {
        return MONTH_COLUMNS_LAYOUT.FOUR_COLUMNS;
    }
    if (showTwoColumnMonthYearPicker) {
        return MONTH_COLUMNS_LAYOUT.TWO_COLUMNS;
    }
    return MONTH_COLUMNS_LAYOUT.THREE_COLUMNS;
}
/**
 * `Month` is a React component that represents a month in a calendar.
 * It accepts a `MonthProps` object as props which provides various configurations and event handlers.
 *
 * @prop dayClassName - Function to determine the class name for a day.
 * @prop monthClassName - Function to determine the class name for a month.
 * @prop filterDate - Function to filter dates.
 * @prop formatWeekNumber - Function to format the week number.
 * @prop onDayClick - Function to handle day click events.
 * @prop onDayMouseEnter - Function to handle mouse enter events on a day.
 * @prop onMouseLeave - Function to handle mouse leave events.
 * @prop onWeekSelect - Function to handle week selection.
 * @prop setPreSelection - Function to set pre-selection.
 * @prop setOpen - Function to set open state.
 * @prop renderDayContents - Function to render day contents.
 * @prop renderMonthContent - Function to render month content.
 * @prop renderQuarterContent - Function to render quarter content.
 * @prop handleOnKeyDown - Function to handle key down events.
 * @prop handleOnMonthKeyDown - Function to handle key down events on a month.
 * @prop ariaLabelPrefix - Aria label prefix.
 * @prop chooseDayAriaLabelPrefix - Aria label prefix for choosing a day.
 * @prop disabledDayAriaLabelPrefix - Aria label prefix for disabled day.
 * @prop disabledKeyboardNavigation - Flag to disable keyboard navigation.
 * @prop day - The day.
 * @prop endDate - The end date.
 * @prop orderInDisplay - The order in display.
 * @prop excludeDates - Dates to exclude.
 * @prop excludeDateIntervals - Date intervals to exclude.
 * @prop fixedHeight - Flag to set fixed height.
 * @prop highlightDates - Dates to highlight.
 * @prop holidays - Holidays.
 * @prop includeDates - Dates to include.
 * @prop includeDateIntervals - Date intervals to include.
 * @prop inline - Flag to set inline.
 * @prop shouldFocusDayInline - Flag to set focus on day inline.
 * @prop locale - The locale.
 * @prop maxDate - The maximum date.
 * @prop minDate - The minimum date.
 * @prop usePointerEvent - Flag to use pointer event.
 * @prop peekNextMonth - Flag to peek next month.
 * @prop preSelection - The pre-selection.
 * @prop selected - The selected date.
 * @prop selectingDate - The selecting date.
 * @prop calendarStartDay - The calendar start day.
 * @prop selectsEnd - Flag to select end.
 * @prop selectsStart - Flag to select start.
 * @prop selectsRange - Flag to select range.
 * @prop selectsDisabledDaysInRange - Flag to select disabled days in range.
 * @prop selectsMultiple - Flag to select multiple.
 * @prop selectedDates - The selected dates.
 * @prop showWeekNumbers - Flag to show week numbers.
 * @prop startDate - The start date.
 * @prop shouldCloseOnSelect - Flag to close on select.
 * @prop showMonthYearPicker - Flag to show month year picker.
 * @prop showFullMonthYearPicker - Flag to show full month year picker.
 * @prop showTwoColumnMonthYearPicker - Flag to show two column month year picker.
 * @prop showFourColumnMonthYearPicker - Flag to show four column month year picker.
 * @prop showQuarterYearPicker - Flag to show quarter year picker.
 * @prop showWeekPicker - Flag to show week picker.
 * @prop isInputFocused - Flag to set input focus.
 * @prop weekAriaLabelPrefix - Aria label prefix for week.
 * @prop containerRef - The container reference.
 * @prop monthShowsDuplicateDaysEnd - Flag to show duplicate days at the end of the month.
 * @prop monthShowsDuplicateDaysStart - Flag to show duplicate days at the start of the month.
 *
 * @example
 * ```tsx
 * function App() {
 *  const handleDayClick = (date) => {
 *     console.log('Day clicked: ', date);
 *   };
 *
 *   const handleDayMouseEnter = (date) => {
 *     console.log('Mouse entered on day: ', date);
 *   };
 *
 *   return (
 *     <div>
 *       <Month
 *         day={new Date()}
 *         endDate={new Date()}
 *         onDayClick={handleDayClick}
 *         onDayMouseEnter={handleDayMouseEnter}
 *         disabledKeyboardNavigation={false}
 *         showWeekNumbers={true}
 *         showMonthYearPicker={false}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
var Month = /** @class */ (function (_super) {
    __extends(Month, _super);
    function Month() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.MONTH_REFS = __spreadArray([], Array(12), true).map(function () { return createRef(); });
        _this.QUARTER_REFS = __spreadArray([], Array(4), true).map(function () { return createRef(); });
        _this.isDisabled = function (day) {
            // Almost all props previously were passed as this.props w/o proper typing with prop-types
            // after the migration to TS i made it explicit
            return isDayDisabled(day, {
                minDate: _this.props.minDate,
                maxDate: _this.props.maxDate,
                excludeDates: _this.props.excludeDates,
                excludeDateIntervals: _this.props.excludeDateIntervals,
                includeDateIntervals: _this.props.includeDateIntervals,
                includeDates: _this.props.includeDates,
                filterDate: _this.props.filterDate,
            });
        };
        _this.isExcluded = function (day) {
            // Almost all props previously were passed as this.props w/o proper typing with prop-types
            // after the migration to TS i made it explicit
            return isDayExcluded(day, {
                excludeDates: _this.props.excludeDates,
                excludeDateIntervals: _this.props.excludeDateIntervals,
            });
        };
        _this.handleDayClick = function (day, event) {
            var _a, _b;
            (_b = (_a = _this.props).onDayClick) === null || _b === void 0 ? void 0 : _b.call(_a, day, event, _this.props.orderInDisplay);
        };
        _this.handleDayMouseEnter = function (day) {
            var _a, _b;
            (_b = (_a = _this.props).onDayMouseEnter) === null || _b === void 0 ? void 0 : _b.call(_a, day);
        };
        _this.handleMouseLeave = function () {
            var _a, _b;
            (_b = (_a = _this.props).onMouseLeave) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        _this.isRangeStartMonth = function (m) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameMonth(setMonth(day, m), startDate);
        };
        _this.isRangeStartQuarter = function (q) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameQuarter(setQuarter(day, q), startDate);
        };
        _this.isRangeEndMonth = function (m) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameMonth(setMonth(day, m), endDate);
        };
        _this.isRangeEndQuarter = function (q) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate;
            if (!startDate || !endDate) {
                return false;
            }
            return isSameQuarter(setQuarter(day, q), endDate);
        };
        _this.isInSelectingRangeMonth = function (m) {
            var _a;
            var _b = _this.props, day = _b.day, selectsStart = _b.selectsStart, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange, startDate = _b.startDate, endDate = _b.endDate;
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (!(selectsStart || selectsEnd || selectsRange) || !selectingDate) {
                return false;
            }
            if (selectsStart && endDate) {
                return isMonthInRange(selectingDate, endDate, m, day);
            }
            if (selectsEnd && startDate) {
                return isMonthInRange(startDate, selectingDate, m, day);
            }
            if (selectsRange && startDate && !endDate) {
                return isMonthInRange(startDate, selectingDate, m, day);
            }
            return false;
        };
        _this.isSelectingMonthRangeStart = function (m) {
            var _a;
            if (!_this.isInSelectingRangeMonth(m)) {
                return false;
            }
            var _b = _this.props, day = _b.day, startDate = _b.startDate, selectsStart = _b.selectsStart;
            var _month = setMonth(day, m);
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (selectsStart) {
                return isSameMonth(_month, selectingDate);
            }
            else {
                return isSameMonth(_month, startDate);
            }
        };
        _this.isSelectingMonthRangeEnd = function (m) {
            var _a;
            if (!_this.isInSelectingRangeMonth(m)) {
                return false;
            }
            var _b = _this.props, day = _b.day, endDate = _b.endDate, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange;
            var _month = setMonth(day, m);
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (selectsEnd || selectsRange) {
                return isSameMonth(_month, selectingDate);
            }
            else {
                return isSameMonth(_month, endDate);
            }
        };
        _this.isInSelectingRangeQuarter = function (q) {
            var _a;
            var _b = _this.props, day = _b.day, selectsStart = _b.selectsStart, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange, startDate = _b.startDate, endDate = _b.endDate;
            var selectingDate = (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection;
            if (!(selectsStart || selectsEnd || selectsRange) || !selectingDate) {
                return false;
            }
            if (selectsStart && endDate) {
                return isQuarterInRange(selectingDate, endDate, q, day);
            }
            if (selectsEnd && startDate) {
                return isQuarterInRange(startDate, selectingDate, q, day);
            }
            if (selectsRange && startDate && !endDate) {
                return isQuarterInRange(startDate, selectingDate, q, day);
            }
            return false;
        };
        _this.isWeekInMonth = function (startOfWeek) {
            var day = _this.props.day;
            var endOfWeek = addDays(startOfWeek, 6);
            return isSameMonth(startOfWeek, day) || isSameMonth(endOfWeek, day);
        };
        _this.isCurrentMonth = function (day, m) {
            return getYear$1(day) === getYear$1(newDate()) && m === getMonth(newDate());
        };
        _this.isCurrentQuarter = function (day, q) {
            return getYear$1(day) === getYear$1(newDate()) && q === getQuarter(newDate());
        };
        _this.isSelectedMonth = function (day, m, selected) {
            return getMonth(selected) === m && getYear$1(day) === getYear$1(selected);
        };
        _this.isSelectMonthInList = function (day, m, selectedDates) {
            return selectedDates.some(function (selectedDate) {
                return _this.isSelectedMonth(day, m, selectedDate);
            });
        };
        _this.isSelectedQuarter = function (day, q, selected) {
            return getQuarter(day) === q && getYear$1(day) === getYear$1(selected);
        };
        _this.renderWeeks = function () {
            var weeks = [];
            var isFixedHeight = _this.props.fixedHeight;
            var i = 0;
            var breakAfterNextPush = false;
            var currentWeekStart = getStartOfWeek(getStartOfMonth(_this.props.day), _this.props.locale, _this.props.calendarStartDay);
            var isPreSelected = function (preSelection) {
                return _this.props.showWeekPicker
                    ? getStartOfWeek(preSelection, _this.props.locale, _this.props.calendarStartDay)
                    : _this.props.preSelection;
            };
            var isSelected = function (selected) {
                return _this.props.showWeekPicker
                    ? getStartOfWeek(selected, _this.props.locale, _this.props.calendarStartDay)
                    : _this.props.selected;
            };
            var selected = _this.props.selected
                ? isSelected(_this.props.selected)
                : undefined;
            var preSelection = _this.props.preSelection
                ? isPreSelected(_this.props.preSelection)
                : undefined;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                weeks.push(React__default.createElement(Week, _assign({}, _this.props, { ariaLabelPrefix: _this.props.weekAriaLabelPrefix, key: i, day: currentWeekStart, month: getMonth(_this.props.day), onDayClick: _this.handleDayClick, onDayMouseEnter: _this.handleDayMouseEnter, selected: selected, preSelection: preSelection, showWeekNumber: _this.props.showWeekNumbers })));
                if (breakAfterNextPush)
                    break;
                i++;
                currentWeekStart = addWeeks(currentWeekStart, 1);
                // If one of these conditions is true, we will either break on this week
                // or break on the next week
                var isFixedAndFinalWeek = isFixedHeight && i >= FIXED_HEIGHT_STANDARD_WEEK_COUNT;
                var isNonFixedAndOutOfMonth = !isFixedHeight && !_this.isWeekInMonth(currentWeekStart);
                if (isFixedAndFinalWeek || isNonFixedAndOutOfMonth) {
                    if (_this.props.peekNextMonth) {
                        breakAfterNextPush = true;
                    }
                    else {
                        break;
                    }
                }
            }
            return weeks;
        };
        _this.onMonthClick = function (event, m) {
            var _a = _this.isMonthDisabledForLabelDate(m), isDisabled = _a.isDisabled, labelDate = _a.labelDate;
            if (isDisabled) {
                return;
            }
            _this.handleDayClick(getStartOfMonth(labelDate), event);
        };
        _this.onMonthMouseEnter = function (m) {
            var _a = _this.isMonthDisabledForLabelDate(m), isDisabled = _a.isDisabled, labelDate = _a.labelDate;
            if (isDisabled) {
                return;
            }
            _this.handleDayMouseEnter(getStartOfMonth(labelDate));
        };
        _this.handleMonthNavigation = function (newMonth, newDate) {
            var _a, _b, _c, _d;
            (_b = (_a = _this.props).setPreSelection) === null || _b === void 0 ? void 0 : _b.call(_a, newDate);
            (_d = (_c = _this.MONTH_REFS[newMonth]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
        };
        _this.handleKeyboardNavigation = function (event, eventKey, month) {
            var _a;
            var _b = _this.props, selected = _b.selected, preSelection = _b.preSelection, setPreSelection = _b.setPreSelection, minDate = _b.minDate, maxDate = _b.maxDate, showFourColumnMonthYearPicker = _b.showFourColumnMonthYearPicker, showTwoColumnMonthYearPicker = _b.showTwoColumnMonthYearPicker;
            if (!preSelection)
                return;
            var monthColumnsLayout = getMonthColumnsLayout(showFourColumnMonthYearPicker, showTwoColumnMonthYearPicker);
            var verticalOffset = _this.getVerticalOffset(monthColumnsLayout);
            var monthsGrid = (_a = MONTH_COLUMNS[monthColumnsLayout]) === null || _a === void 0 ? void 0 : _a.grid;
            var calculateNewDateAndMonth = function (eventKey, date, month) {
                var _a, _b;
                var newCalculatedDate = date;
                var newCalculatedMonth = month;
                switch (eventKey) {
                    case KeyType.ArrowRight:
                        newCalculatedDate = addMonths(date, MONTH_NAVIGATION_HORIZONTAL_OFFSET);
                        newCalculatedMonth =
                            month === 11 ? 0 : month + MONTH_NAVIGATION_HORIZONTAL_OFFSET;
                        break;
                    case KeyType.ArrowLeft:
                        newCalculatedDate = subMonths(date, MONTH_NAVIGATION_HORIZONTAL_OFFSET);
                        newCalculatedMonth =
                            month === 0 ? 11 : month - MONTH_NAVIGATION_HORIZONTAL_OFFSET;
                        break;
                    case KeyType.ArrowUp:
                        newCalculatedDate = subMonths(date, verticalOffset);
                        newCalculatedMonth = ((_a = monthsGrid === null || monthsGrid === void 0 ? void 0 : monthsGrid[0]) === null || _a === void 0 ? void 0 : _a.includes(month))
                            ? month + 12 - verticalOffset
                            : month - verticalOffset;
                        break;
                    case KeyType.ArrowDown:
                        newCalculatedDate = addMonths(date, verticalOffset);
                        newCalculatedMonth = ((_b = monthsGrid === null || monthsGrid === void 0 ? void 0 : monthsGrid[monthsGrid.length - 1]) === null || _b === void 0 ? void 0 : _b.includes(month))
                            ? month - 12 + verticalOffset
                            : month + verticalOffset;
                        break;
                }
                return { newCalculatedDate: newCalculatedDate, newCalculatedMonth: newCalculatedMonth };
            };
            var getNewDateAndMonth = function (eventKey, selectedDate, month) {
                var MAX_ITERATIONS = 40;
                var eventKeyCopy = eventKey;
                var validDateFound = false;
                var iterations = 0;
                var _a = calculateNewDateAndMonth(eventKeyCopy, selectedDate, month), newCalculatedDate = _a.newCalculatedDate, newCalculatedMonth = _a.newCalculatedMonth;
                while (!validDateFound) {
                    if (iterations >= MAX_ITERATIONS) {
                        newCalculatedDate = selectedDate;
                        newCalculatedMonth = month;
                        break;
                    }
                    // if minDate exists and the new month is before the minimum month, it will try to find the next available month after
                    if (minDate && newCalculatedDate < minDate) {
                        eventKeyCopy = KeyType.ArrowRight;
                        var obj = calculateNewDateAndMonth(eventKeyCopy, newCalculatedDate, newCalculatedMonth);
                        newCalculatedDate = obj.newCalculatedDate;
                        newCalculatedMonth = obj.newCalculatedMonth;
                    }
                    // if maxDate exists and the new month is after the maximum month, it will try to find the next available month before
                    if (maxDate && newCalculatedDate > maxDate) {
                        eventKeyCopy = KeyType.ArrowLeft;
                        var obj = calculateNewDateAndMonth(eventKeyCopy, newCalculatedDate, newCalculatedMonth);
                        newCalculatedDate = obj.newCalculatedDate;
                        newCalculatedMonth = obj.newCalculatedMonth;
                    }
                    if (isMonthYearDisabled(newCalculatedDate, _this.props)) {
                        var obj = calculateNewDateAndMonth(eventKeyCopy, newCalculatedDate, newCalculatedMonth);
                        newCalculatedDate = obj.newCalculatedDate;
                        newCalculatedMonth = obj.newCalculatedMonth;
                    }
                    else {
                        validDateFound = true;
                    }
                    iterations++;
                }
                return { newCalculatedDate: newCalculatedDate, newCalculatedMonth: newCalculatedMonth };
            };
            if (eventKey === KeyType.Enter) {
                if (!_this.isMonthDisabled(month)) {
                    _this.onMonthClick(event, month);
                    setPreSelection === null || setPreSelection === void 0 ? void 0 : setPreSelection(selected);
                }
                return;
            }
            var _c = getNewDateAndMonth(eventKey, preSelection, month), newCalculatedDate = _c.newCalculatedDate, newCalculatedMonth = _c.newCalculatedMonth;
            switch (eventKey) {
                case KeyType.ArrowRight:
                case KeyType.ArrowLeft:
                case KeyType.ArrowUp:
                case KeyType.ArrowDown:
                    _this.handleMonthNavigation(newCalculatedMonth, newCalculatedDate);
                    break;
            }
        };
        _this.getVerticalOffset = function (monthColumnsLayout) {
            var _a, _b;
            return (_b = (_a = MONTH_COLUMNS[monthColumnsLayout]) === null || _a === void 0 ? void 0 : _a.verticalNavigationOffset) !== null && _b !== void 0 ? _b : 0;
        };
        _this.onMonthKeyDown = function (event, month) {
            var _a = _this.props, disabledKeyboardNavigation = _a.disabledKeyboardNavigation, handleOnMonthKeyDown = _a.handleOnMonthKeyDown;
            var eventKey = event.key;
            if (eventKey !== KeyType.Tab) {
                // preventDefault on tab event blocks focus change
                event.preventDefault();
            }
            if (!disabledKeyboardNavigation) {
                _this.handleKeyboardNavigation(event, eventKey, month);
            }
            handleOnMonthKeyDown && handleOnMonthKeyDown(event);
        };
        _this.onQuarterClick = function (event, q) {
            var labelDate = setQuarter(_this.props.day, q);
            if (isQuarterDisabled(labelDate, _this.props)) {
                return;
            }
            _this.handleDayClick(getStartOfQuarter(labelDate), event);
        };
        _this.onQuarterMouseEnter = function (q) {
            var labelDate = setQuarter(_this.props.day, q);
            if (isQuarterDisabled(labelDate, _this.props)) {
                return;
            }
            _this.handleDayMouseEnter(getStartOfQuarter(labelDate));
        };
        _this.handleQuarterNavigation = function (newQuarter, newDate) {
            var _a, _b, _c, _d;
            if (_this.isDisabled(newDate) || _this.isExcluded(newDate)) {
                return;
            }
            (_b = (_a = _this.props).setPreSelection) === null || _b === void 0 ? void 0 : _b.call(_a, newDate);
            (_d = (_c = _this.QUARTER_REFS[newQuarter - 1]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
        };
        _this.onQuarterKeyDown = function (event, quarter) {
            var _a, _b;
            var eventKey = event.key;
            if (!_this.props.disabledKeyboardNavigation) {
                switch (eventKey) {
                    case KeyType.Enter:
                        _this.onQuarterClick(event, quarter);
                        (_b = (_a = _this.props).setPreSelection) === null || _b === void 0 ? void 0 : _b.call(_a, _this.props.selected);
                        break;
                    case KeyType.ArrowRight:
                        if (!_this.props.preSelection) {
                            break;
                        }
                        _this.handleQuarterNavigation(quarter === 4 ? 1 : quarter + 1, addQuarters(_this.props.preSelection, 1));
                        break;
                    case KeyType.ArrowLeft:
                        if (!_this.props.preSelection) {
                            break;
                        }
                        _this.handleQuarterNavigation(quarter === 1 ? 4 : quarter - 1, subQuarters(_this.props.preSelection));
                        break;
                }
            }
        };
        _this.isMonthDisabledForLabelDate = function (month) {
            var _a;
            var _b = _this.props, day = _b.day, minDate = _b.minDate, maxDate = _b.maxDate, excludeDates = _b.excludeDates, includeDates = _b.includeDates;
            var labelDate = setMonth(day, month);
            return {
                isDisabled: (_a = ((minDate || maxDate || excludeDates || includeDates) &&
                    isMonthDisabled(labelDate, _this.props))) !== null && _a !== void 0 ? _a : false,
                labelDate: labelDate,
            };
        };
        _this.isMonthDisabled = function (month) {
            var isDisabled = _this.isMonthDisabledForLabelDate(month).isDisabled;
            return isDisabled;
        };
        _this.getMonthClassNames = function (m) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate, preSelection = _a.preSelection, monthClassName = _a.monthClassName;
            var _monthClassName = monthClassName
                ? monthClassName(setMonth(day, m))
                : undefined;
            var selection = _this.getSelection();
            return clsx("react-datepicker__month-text", "react-datepicker__month-".concat(m), _monthClassName, {
                "react-datepicker__month-text--disabled": _this.isMonthDisabled(m),
                "react-datepicker__month-text--selected": selection
                    ? _this.isSelectMonthInList(day, m, selection)
                    : undefined,
                "react-datepicker__month-text--keyboard-selected": !_this.props.disabledKeyboardNavigation &&
                    preSelection &&
                    _this.isSelectedMonth(day, m, preSelection) &&
                    !_this.isMonthDisabled(m),
                "react-datepicker__month-text--in-selecting-range": _this.isInSelectingRangeMonth(m),
                "react-datepicker__month-text--in-range": startDate && endDate
                    ? isMonthInRange(startDate, endDate, m, day)
                    : undefined,
                "react-datepicker__month-text--range-start": _this.isRangeStartMonth(m),
                "react-datepicker__month-text--range-end": _this.isRangeEndMonth(m),
                "react-datepicker__month-text--selecting-range-start": _this.isSelectingMonthRangeStart(m),
                "react-datepicker__month-text--selecting-range-end": _this.isSelectingMonthRangeEnd(m),
                "react-datepicker__month-text--today": _this.isCurrentMonth(day, m),
            });
        };
        _this.getTabIndex = function (m) {
            if (_this.props.preSelection == null) {
                return "-1";
            }
            var preSelectedMonth = getMonth(_this.props.preSelection);
            var isPreSelectedMonthDisabled = _this.isMonthDisabledForLabelDate(preSelectedMonth).isDisabled;
            var tabIndex = m === preSelectedMonth &&
                !(isPreSelectedMonthDisabled || _this.props.disabledKeyboardNavigation)
                ? "0"
                : "-1";
            return tabIndex;
        };
        _this.getQuarterTabIndex = function (q) {
            if (_this.props.preSelection == null) {
                return "-1";
            }
            var preSelectedQuarter = getQuarter(_this.props.preSelection);
            var isCurrentQuarterDisabled = isQuarterDisabled(_this.props.day, _this.props);
            var tabIndex = q === preSelectedQuarter &&
                !(isCurrentQuarterDisabled || _this.props.disabledKeyboardNavigation)
                ? "0"
                : "-1";
            return tabIndex;
        };
        _this.getAriaLabel = function (month) {
            var _a = _this.props, _b = _a.chooseDayAriaLabelPrefix, chooseDayAriaLabelPrefix = _b === void 0 ? "Choose" : _b, _c = _a.disabledDayAriaLabelPrefix, disabledDayAriaLabelPrefix = _c === void 0 ? "Not available" : _c, day = _a.day, locale = _a.locale;
            var labelDate = setMonth(day, month);
            var prefix = _this.isDisabled(labelDate) || _this.isExcluded(labelDate)
                ? disabledDayAriaLabelPrefix
                : chooseDayAriaLabelPrefix;
            return "".concat(prefix, " ").concat(formatDate(labelDate, "MMMM yyyy", locale));
        };
        _this.getQuarterClassNames = function (q) {
            var _a = _this.props, day = _a.day, startDate = _a.startDate, endDate = _a.endDate, selected = _a.selected, minDate = _a.minDate, maxDate = _a.maxDate, excludeDates = _a.excludeDates, includeDates = _a.includeDates, filterDate = _a.filterDate, preSelection = _a.preSelection, disabledKeyboardNavigation = _a.disabledKeyboardNavigation;
            var isDisabled = (minDate || maxDate || excludeDates || includeDates || filterDate) &&
                isQuarterDisabled(setQuarter(day, q), _this.props);
            return clsx("react-datepicker__quarter-text", "react-datepicker__quarter-".concat(q), {
                "react-datepicker__quarter-text--disabled": isDisabled,
                "react-datepicker__quarter-text--selected": selected
                    ? _this.isSelectedQuarter(day, q, selected)
                    : undefined,
                "react-datepicker__quarter-text--keyboard-selected": !disabledKeyboardNavigation &&
                    preSelection &&
                    _this.isSelectedQuarter(day, q, preSelection) &&
                    !isDisabled,
                "react-datepicker__quarter-text--in-selecting-range": _this.isInSelectingRangeQuarter(q),
                "react-datepicker__quarter-text--in-range": startDate && endDate
                    ? isQuarterInRange(startDate, endDate, q, day)
                    : undefined,
                "react-datepicker__quarter-text--range-start": _this.isRangeStartQuarter(q),
                "react-datepicker__quarter-text--range-end": _this.isRangeEndQuarter(q),
            });
        };
        _this.getMonthContent = function (m) {
            var _a = _this.props, showFullMonthYearPicker = _a.showFullMonthYearPicker, renderMonthContent = _a.renderMonthContent, locale = _a.locale, day = _a.day;
            var shortMonthText = getMonthShortInLocale(m, locale);
            var fullMonthText = getMonthInLocale(m, locale);
            if (renderMonthContent) {
                return renderMonthContent(m, shortMonthText, fullMonthText, day);
            }
            return showFullMonthYearPicker ? fullMonthText : shortMonthText;
        };
        _this.getQuarterContent = function (q) {
            var _a;
            var _b = _this.props, renderQuarterContent = _b.renderQuarterContent, locale = _b.locale;
            var shortQuarter = getQuarterShortInLocale(q, locale);
            return (_a = renderQuarterContent === null || renderQuarterContent === void 0 ? void 0 : renderQuarterContent(q, shortQuarter)) !== null && _a !== void 0 ? _a : shortQuarter;
        };
        _this.renderMonths = function () {
            var _a;
            var _b = _this.props, showTwoColumnMonthYearPicker = _b.showTwoColumnMonthYearPicker, showFourColumnMonthYearPicker = _b.showFourColumnMonthYearPicker, day = _b.day, selected = _b.selected;
            var monthColumns = (_a = MONTH_COLUMNS[getMonthColumnsLayout(showFourColumnMonthYearPicker, showTwoColumnMonthYearPicker)]) === null || _a === void 0 ? void 0 : _a.grid;
            return monthColumns === null || monthColumns === void 0 ? void 0 : monthColumns.map(function (month, i) { return (React__default.createElement("div", { className: "react-datepicker__month-wrapper", key: i }, month.map(function (m, j) { return (React__default.createElement("div", { ref: _this.MONTH_REFS[m], key: j, onClick: function (event) {
                    _this.onMonthClick(event, m);
                }, onKeyDown: function (event) {
                    if (isSpaceKeyDown(event)) {
                        event.preventDefault();
                        event.key = KeyType.Enter;
                    }
                    _this.onMonthKeyDown(event, m);
                }, onMouseEnter: !_this.props.usePointerEvent
                    ? function () { return _this.onMonthMouseEnter(m); }
                    : undefined, onPointerEnter: _this.props.usePointerEvent
                    ? function () { return _this.onMonthMouseEnter(m); }
                    : undefined, tabIndex: Number(_this.getTabIndex(m)), className: _this.getMonthClassNames(m), "aria-disabled": _this.isMonthDisabled(m), role: "option", "aria-label": _this.getAriaLabel(m), "aria-current": _this.isCurrentMonth(day, m) ? "date" : undefined, "aria-selected": selected ? _this.isSelectedMonth(day, m, selected) : undefined }, _this.getMonthContent(m))); }))); });
        };
        _this.renderQuarters = function () {
            var _a = _this.props, day = _a.day, selected = _a.selected;
            var quarters = [1, 2, 3, 4];
            return (React__default.createElement("div", { className: "react-datepicker__quarter-wrapper" }, quarters.map(function (q, j) { return (React__default.createElement("div", { key: j, ref: _this.QUARTER_REFS[j], role: "option", onClick: function (event) {
                    _this.onQuarterClick(event, q);
                }, onKeyDown: function (event) {
                    _this.onQuarterKeyDown(event, q);
                }, onMouseEnter: !_this.props.usePointerEvent
                    ? function () { return _this.onQuarterMouseEnter(q); }
                    : undefined, onPointerEnter: _this.props.usePointerEvent
                    ? function () { return _this.onQuarterMouseEnter(q); }
                    : undefined, className: _this.getQuarterClassNames(q), "aria-selected": selected ? _this.isSelectedQuarter(day, q, selected) : undefined, tabIndex: Number(_this.getQuarterTabIndex(q)), "aria-current": _this.isCurrentQuarter(day, q) ? "date" : undefined }, _this.getQuarterContent(q))); })));
        };
        _this.getClassNames = function () {
            var _a = _this.props, selectingDate = _a.selectingDate, selectsStart = _a.selectsStart, selectsEnd = _a.selectsEnd, showMonthYearPicker = _a.showMonthYearPicker, showQuarterYearPicker = _a.showQuarterYearPicker, showWeekPicker = _a.showWeekPicker;
            return clsx("react-datepicker__month", {
                "react-datepicker__month--selecting-range": selectingDate && (selectsStart || selectsEnd),
            }, { "react-datepicker__monthPicker": showMonthYearPicker }, { "react-datepicker__quarterPicker": showQuarterYearPicker }, { "react-datepicker__weekPicker": showWeekPicker });
        };
        return _this;
    }
    Month.prototype.getSelection = function () {
        var _a = this.props, selected = _a.selected, selectedDates = _a.selectedDates, selectsMultiple = _a.selectsMultiple;
        if (selectsMultiple) {
            return selectedDates;
        }
        if (selected) {
            return [selected];
        }
        return undefined;
    };
    Month.prototype.render = function () {
        var _a = this.props, showMonthYearPicker = _a.showMonthYearPicker, showQuarterYearPicker = _a.showQuarterYearPicker, day = _a.day, _b = _a.ariaLabelPrefix, ariaLabelPrefix = _b === void 0 ? "Month " : _b;
        var formattedAriaLabelPrefix = ariaLabelPrefix
            ? ariaLabelPrefix.trim() + " "
            : "";
        return (React__default.createElement("div", { className: this.getClassNames(), onMouseLeave: !this.props.usePointerEvent ? this.handleMouseLeave : undefined, onPointerLeave: this.props.usePointerEvent ? this.handleMouseLeave : undefined, "aria-label": "".concat(formattedAriaLabelPrefix).concat(formatDate(day, "MMMM, yyyy", this.props.locale)), role: "listbox" }, showMonthYearPicker
            ? this.renderMonths()
            : showQuarterYearPicker
                ? this.renderQuarters()
                : this.renderWeeks()));
    };
    return Month;
}(Component));

var MonthDropdownOptions = /** @class */ (function (_super) {
    __extends(MonthDropdownOptions, _super);
    function MonthDropdownOptions() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.isSelectedMonth = function (i) { return _this.props.month === i; };
        _this.renderOptions = function () {
            return _this.props.monthNames.map(function (month, i) { return (React__default.createElement("div", { className: _this.isSelectedMonth(i)
                    ? "react-datepicker__month-option react-datepicker__month-option--selected_month"
                    : "react-datepicker__month-option", key: month, onClick: _this.onChange.bind(_this, i), "aria-selected": _this.isSelectedMonth(i) ? "true" : undefined },
                _this.isSelectedMonth(i) ? (React__default.createElement("span", { className: "react-datepicker__month-option--selected" }, "\u2713")) : (""),
                month)); });
        };
        _this.onChange = function (month) { return _this.props.onChange(month); };
        _this.handleClickOutside = function () { return _this.props.onCancel(); };
        return _this;
    }
    MonthDropdownOptions.prototype.render = function () {
        return (React__default.createElement(ClickOutsideWrapper, { className: "react-datepicker__month-dropdown", onClickOutside: this.handleClickOutside }, this.renderOptions()));
    };
    return MonthDropdownOptions;
}(Component));

var MonthDropdown = /** @class */ (function (_super) {
    __extends(MonthDropdown, _super);
    function MonthDropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            dropdownVisible: false,
        };
        _this.renderSelectOptions = function (monthNames) {
            return monthNames.map(function (m, i) { return (React__default.createElement("option", { key: m, value: i }, m)); });
        };
        _this.renderSelectMode = function (monthNames) { return (React__default.createElement("select", { value: _this.props.month, className: "react-datepicker__month-select", onChange: function (e) { return _this.onChange(parseInt(e.target.value)); } }, _this.renderSelectOptions(monthNames))); };
        _this.renderReadView = function (visible, monthNames) { return (React__default.createElement("div", { key: "read", style: { visibility: visible ? "visible" : "hidden" }, className: "react-datepicker__month-read-view", onClick: _this.toggleDropdown },
            React__default.createElement("span", { className: "react-datepicker__month-read-view--down-arrow" }),
            React__default.createElement("span", { className: "react-datepicker__month-read-view--selected-month" }, monthNames[_this.props.month]))); };
        _this.renderDropdown = function (monthNames) { return (React__default.createElement(MonthDropdownOptions, _assign({ key: "dropdown" }, _this.props, { monthNames: monthNames, onChange: _this.onChange, onCancel: _this.toggleDropdown }))); };
        _this.renderScrollMode = function (monthNames) {
            var dropdownVisible = _this.state.dropdownVisible;
            var result = [_this.renderReadView(!dropdownVisible, monthNames)];
            if (dropdownVisible) {
                result.unshift(_this.renderDropdown(monthNames));
            }
            return result;
        };
        _this.onChange = function (month) {
            _this.toggleDropdown();
            if (month !== _this.props.month) {
                _this.props.onChange(month);
            }
        };
        _this.toggleDropdown = function () {
            return _this.setState({
                dropdownVisible: !_this.state.dropdownVisible,
            });
        };
        return _this;
    }
    MonthDropdown.prototype.render = function () {
        var _this = this;
        var monthNames = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(this.props.useShortMonthInDropdown
            ? function (m) { return getMonthShortInLocale(m, _this.props.locale); }
            : function (m) { return getMonthInLocale(m, _this.props.locale); });
        var renderedDropdown;
        switch (this.props.dropdownMode) {
            case "scroll":
                renderedDropdown = this.renderScrollMode(monthNames);
                break;
            case "select":
                renderedDropdown = this.renderSelectMode(monthNames);
                break;
        }
        return (React__default.createElement("div", { className: "react-datepicker__month-dropdown-container react-datepicker__month-dropdown-container--".concat(this.props.dropdownMode) }, renderedDropdown));
    };
    return MonthDropdown;
}(Component));

function generateMonthYears(minDate, maxDate) {
    var list = [];
    var currDate = getStartOfMonth(minDate);
    var lastDate = getStartOfMonth(maxDate);
    while (!isAfter(currDate, lastDate)) {
        list.push(newDate(currDate));
        currDate = addMonths(currDate, 1);
    }
    return list;
}
var MonthYearDropdownOptions = /** @class */ (function (_super) {
    __extends(MonthYearDropdownOptions, _super);
    function MonthYearDropdownOptions(props) {
        var _this = _super.call(this, props) || this;
        _this.renderOptions = function () {
            return _this.state.monthYearsList.map(function (monthYear) {
                var monthYearPoint = getTime(monthYear);
                var isSameMonthYear = isSameYear(_this.props.date, monthYear) &&
                    isSameMonth(_this.props.date, monthYear);
                return (React__default.createElement("div", { className: isSameMonthYear
                        ? "react-datepicker__month-year-option--selected_month-year"
                        : "react-datepicker__month-year-option", key: monthYearPoint, onClick: _this.onChange.bind(_this, monthYearPoint), "aria-selected": isSameMonthYear ? "true" : undefined },
                    isSameMonthYear ? (React__default.createElement("span", { className: "react-datepicker__month-year-option--selected" }, "\u2713")) : (""),
                    formatDate(monthYear, _this.props.dateFormat, _this.props.locale)));
            });
        };
        _this.onChange = function (monthYear) { return _this.props.onChange(monthYear); };
        _this.handleClickOutside = function () {
            _this.props.onCancel();
        };
        _this.state = {
            monthYearsList: generateMonthYears(_this.props.minDate, _this.props.maxDate),
        };
        return _this;
    }
    MonthYearDropdownOptions.prototype.render = function () {
        var dropdownClass = clsx({
            "react-datepicker__month-year-dropdown": true,
            "react-datepicker__month-year-dropdown--scrollable": this.props.scrollableMonthYearDropdown,
        });
        return (React__default.createElement(ClickOutsideWrapper, { className: dropdownClass, onClickOutside: this.handleClickOutside }, this.renderOptions()));
    };
    return MonthYearDropdownOptions;
}(Component));

var MonthYearDropdown = /** @class */ (function (_super) {
    __extends(MonthYearDropdown, _super);
    function MonthYearDropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            dropdownVisible: false,
        };
        _this.renderSelectOptions = function () {
            var currDate = getStartOfMonth(_this.props.minDate);
            var lastDate = getStartOfMonth(_this.props.maxDate);
            var options = [];
            while (!isAfter(currDate, lastDate)) {
                var timePoint = getTime(currDate);
                options.push(React__default.createElement("option", { key: timePoint, value: timePoint }, formatDate(currDate, _this.props.dateFormat, _this.props.locale)));
                currDate = addMonths(currDate, 1);
            }
            return options;
        };
        _this.onSelectChange = function (event) {
            _this.onChange(parseInt(event.target.value));
        };
        _this.renderSelectMode = function () { return (React__default.createElement("select", { value: getTime(getStartOfMonth(_this.props.date)), className: "react-datepicker__month-year-select", onChange: _this.onSelectChange }, _this.renderSelectOptions())); };
        _this.renderReadView = function (visible) {
            var yearMonth = formatDate(_this.props.date, _this.props.dateFormat, _this.props.locale);
            return (React__default.createElement("div", { key: "read", style: { visibility: visible ? "visible" : "hidden" }, className: "react-datepicker__month-year-read-view", onClick: _this.toggleDropdown },
                React__default.createElement("span", { className: "react-datepicker__month-year-read-view--down-arrow" }),
                React__default.createElement("span", { className: "react-datepicker__month-year-read-view--selected-month-year" }, yearMonth)));
        };
        _this.renderDropdown = function () { return (React__default.createElement(MonthYearDropdownOptions, _assign({ key: "dropdown" }, _this.props, { onChange: _this.onChange, onCancel: _this.toggleDropdown }))); };
        _this.renderScrollMode = function () {
            var dropdownVisible = _this.state.dropdownVisible;
            var result = [_this.renderReadView(!dropdownVisible)];
            if (dropdownVisible) {
                result.unshift(_this.renderDropdown());
            }
            return result;
        };
        _this.onChange = function (monthYearPoint) {
            _this.toggleDropdown();
            var changedDate = newDate(monthYearPoint);
            if (isSameYear(_this.props.date, changedDate) &&
                isSameMonth(_this.props.date, changedDate)) {
                return;
            }
            _this.props.onChange(changedDate);
        };
        _this.toggleDropdown = function () {
            return _this.setState({
                dropdownVisible: !_this.state.dropdownVisible,
            });
        };
        return _this;
    }
    MonthYearDropdown.prototype.render = function () {
        var renderedDropdown;
        switch (this.props.dropdownMode) {
            case "scroll":
                renderedDropdown = this.renderScrollMode();
                break;
            case "select":
                renderedDropdown = this.renderSelectMode();
                break;
        }
        return (React__default.createElement("div", { className: "react-datepicker__month-year-dropdown-container react-datepicker__month-year-dropdown-container--".concat(this.props.dropdownMode) }, renderedDropdown));
    };
    return MonthYearDropdown;
}(Component));

var Time = /** @class */ (function (_super) {
    __extends(Time, _super);
    function Time() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            height: null,
        };
        _this.scrollToTheSelectedTime = function () {
            requestAnimationFrame(function () {
                var _a, _b, _c;
                if (!_this.list)
                    return;
                _this.list.scrollTop =
                    (_c = (_this.centerLi &&
                        Time.calcCenterPosition(_this.props.monthRef
                            ? _this.props.monthRef.clientHeight -
                                ((_b = (_a = _this.header) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : 0)
                            : _this.list.clientHeight, _this.centerLi))) !== null && _c !== void 0 ? _c : 0;
            });
        };
        _this.handleClick = function (time) {
            var _a, _b;
            if (((_this.props.minTime || _this.props.maxTime) &&
                isTimeInDisabledRange(time, _this.props)) ||
                ((_this.props.excludeTimes ||
                    _this.props.includeTimes ||
                    _this.props.filterTime) &&
                    isTimeDisabled(time, _this.props))) {
                return;
            }
            (_b = (_a = _this.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, time);
        };
        _this.isSelectedTime = function (time) {
            return _this.props.selected && isSameMinute(_this.props.selected, time);
        };
        _this.isDisabledTime = function (time) {
            return ((_this.props.minTime || _this.props.maxTime) &&
                isTimeInDisabledRange(time, _this.props)) ||
                ((_this.props.excludeTimes ||
                    _this.props.includeTimes ||
                    _this.props.filterTime) &&
                    isTimeDisabled(time, _this.props));
        };
        _this.liClasses = function (time) {
            var _a;
            var classes = [
                "react-datepicker__time-list-item",
                _this.props.timeClassName ? _this.props.timeClassName(time) : undefined,
            ];
            if (_this.isSelectedTime(time)) {
                classes.push("react-datepicker__time-list-item--selected");
            }
            if (_this.isDisabledTime(time)) {
                classes.push("react-datepicker__time-list-item--disabled");
            }
            //convert this.props.intervals and the relevant time to seconds and check if it it's a clean multiple of the interval
            if (_this.props.injectTimes &&
                (getHours(time) * 3600 + getMinutes(time) * 60 + getSeconds(time)) %
                    (((_a = _this.props.intervals) !== null && _a !== void 0 ? _a : Time.defaultProps.intervals) * 60) !==
                    0) {
                classes.push("react-datepicker__time-list-item--injected");
            }
            return classes.join(" ");
        };
        _this.handleOnKeyDown = function (event, time) {
            var _a, _b;
            if (event.key === KeyType.Space) {
                event.preventDefault();
                event.key = KeyType.Enter;
            }
            if ((event.key === KeyType.ArrowUp || event.key === KeyType.ArrowLeft) &&
                event.target instanceof HTMLElement &&
                event.target.previousSibling) {
                event.preventDefault();
                event.target.previousSibling instanceof HTMLElement &&
                    event.target.previousSibling.focus();
            }
            if ((event.key === KeyType.ArrowDown || event.key === KeyType.ArrowRight) &&
                event.target instanceof HTMLElement &&
                event.target.nextSibling) {
                event.preventDefault();
                event.target.nextSibling instanceof HTMLElement &&
                    event.target.nextSibling.focus();
            }
            if (event.key === KeyType.Enter) {
                _this.handleClick(time);
            }
            (_b = (_a = _this.props).handleOnKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        };
        _this.renderTimes = function () {
            var _a;
            var times = [];
            var format = typeof _this.props.format === "string" ? _this.props.format : "p";
            var intervals = (_a = _this.props.intervals) !== null && _a !== void 0 ? _a : Time.defaultProps.intervals;
            var activeDate = _this.props.selected || _this.props.openToDate || newDate();
            var base = getStartOfDay(activeDate);
            var sortedInjectTimes = _this.props.injectTimes &&
                _this.props.injectTimes.sort(function (a, b) {
                    return a.getTime() - b.getTime();
                });
            var minutesInDay = 60 * getHoursInDay(activeDate);
            var multiplier = minutesInDay / intervals;
            for (var i = 0; i < multiplier; i++) {
                var currentTime = addMinutes(base, i * intervals);
                times.push(currentTime);
                if (sortedInjectTimes) {
                    var timesToInject = timesToInjectAfter(base, currentTime, i, intervals, sortedInjectTimes);
                    times = times.concat(timesToInject);
                }
            }
            // Determine which time to focus and scroll into view when component mounts
            var timeToFocus = times.reduce(function (prev, time) {
                if (time.getTime() <= activeDate.getTime()) {
                    return time;
                }
                return prev;
            }, times[0]);
            return times.map(function (time) {
                return (React__default.createElement("li", { key: time.valueOf(), onClick: _this.handleClick.bind(_this, time), className: _this.liClasses(time), ref: function (li) {
                        if (time === timeToFocus) {
                            _this.centerLi = li;
                        }
                    }, onKeyDown: function (event) {
                        _this.handleOnKeyDown(event, time);
                    }, tabIndex: time === timeToFocus ? 0 : -1, role: "option", "aria-selected": _this.isSelectedTime(time) ? "true" : undefined, "aria-disabled": _this.isDisabledTime(time) ? "true" : undefined }, formatDate(time, format, _this.props.locale)));
            });
        };
        _this.renderTimeCaption = function () {
            if (_this.props.showTimeCaption === false) {
                return React__default.createElement(React__default.Fragment, null);
            }
            return (React__default.createElement("div", { className: "react-datepicker__header react-datepicker__header--time ".concat(_this.props.showTimeSelectOnly
                    ? "react-datepicker__header--time--only"
                    : ""), ref: function (header) {
                    _this.header = header;
                } },
                React__default.createElement("div", { className: "react-datepicker-time__header" }, _this.props.timeCaption)));
        };
        return _this;
    }
    Object.defineProperty(Time, "defaultProps", {
        get: function () {
            return {
                intervals: 30,
                todayButton: null,
                timeCaption: "Time",
                showTimeCaption: true,
            };
        },
        enumerable: false,
        configurable: true
    });
    Time.prototype.componentDidMount = function () {
        // code to ensure selected time will always be in focus within time window when it first appears
        this.scrollToTheSelectedTime();
        if (this.props.monthRef && this.header) {
            this.setState({
                height: this.props.monthRef.clientHeight - this.header.clientHeight,
            });
        }
    };
    Time.prototype.render = function () {
        var _this = this;
        var _a;
        var height = this.state.height;
        return (React__default.createElement("div", { className: "react-datepicker__time-container ".concat(((_a = this.props.todayButton) !== null && _a !== void 0 ? _a : Time.defaultProps.todayButton)
                ? "react-datepicker__time-container--with-today-button"
                : "") },
            this.renderTimeCaption(),
            React__default.createElement("div", { className: "react-datepicker__time" },
                React__default.createElement("div", { className: "react-datepicker__time-box" },
                    React__default.createElement("ul", { className: "react-datepicker__time-list", ref: function (list) {
                            _this.list = list;
                        }, style: height ? { height: height } : {}, role: "listbox", "aria-label": this.props.timeCaption }, this.renderTimes())))));
    };
    Time.calcCenterPosition = function (listHeight, centerLiRef) {
        return (centerLiRef.offsetTop - (listHeight / 2 - centerLiRef.clientHeight / 2));
    };
    return Time;
}(Component));

var VERTICAL_NAVIGATION_OFFSET = 3;
/**
 * `Year` is a component that represents a year in a date picker.
 *
 * @class
 * @param {YearProps} props - The properties that define the `Year` component.
 * @property {VoidFunction} [props.clearSelectingDate] - Function to clear the selected date.
 * @property {Date} [props.date] - The currently selected date.
 * @property {boolean} [props.disabledKeyboardNavigation] - If true, keyboard navigation is disabled.
 * @property {Date} [props.endDate] - The end date in a range selection.
 * @property {(date: Date) => void} props.onDayClick - Function to handle day click events.
 * @property {Date} props.preSelection - The date that is currently in focus.
 * @property {(date: Date) => void} props.setPreSelection - Function to set the pre-selected date.
 * @property {{ [key: string]: any }} props.selected - The selected date(s).
 * @property {boolean} props.inline - If true, the date picker is displayed inline.
 * @property {Date} props.maxDate - The maximum selectable date.
 * @property {Date} props.minDate - The minimum selectable date.
 * @property {boolean} props.usePointerEvent - If true, pointer events are used instead of mouse events.
 * @property {(date: Date) => void} props.onYearMouseEnter - Function to handle mouse enter events on a year.
 * @property {(date: Date) => void} props.onYearMouseLeave - Function to handle mouse leave events on a year.
 */
var Year = /** @class */ (function (_super) {
    __extends(Year, _super);
    function Year(props) {
        var _this = _super.call(this, props) || this;
        _this.YEAR_REFS = __spreadArray([], Array(_this.props.yearItemNumber), true).map(function () {
            return createRef();
        });
        _this.isDisabled = function (date) {
            return isDayDisabled(date, {
                minDate: _this.props.minDate,
                maxDate: _this.props.maxDate,
                excludeDates: _this.props.excludeDates,
                includeDates: _this.props.includeDates,
                filterDate: _this.props.filterDate,
            });
        };
        _this.isExcluded = function (date) {
            return isDayExcluded(date, {
                excludeDates: _this.props.excludeDates,
            });
        };
        _this.selectingDate = function () { var _a; return (_a = _this.props.selectingDate) !== null && _a !== void 0 ? _a : _this.props.preSelection; };
        _this.updateFocusOnPaginate = function (refIndex) {
            var waitForReRender = function () {
                var _a, _b;
                (_b = (_a = _this.YEAR_REFS[refIndex]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
            };
            window.requestAnimationFrame(waitForReRender);
        };
        _this.handleYearClick = function (day, event) {
            if (_this.props.onDayClick) {
                _this.props.onDayClick(day, event);
            }
        };
        _this.handleYearNavigation = function (newYear, newDate) {
            var _a, _b, _c, _d;
            var _e = _this.props, date = _e.date, yearItemNumber = _e.yearItemNumber;
            if (date === undefined || yearItemNumber === undefined) {
                return;
            }
            var startPeriod = getYearsPeriod(date, yearItemNumber).startPeriod;
            if (_this.isDisabled(newDate) || _this.isExcluded(newDate)) {
                return;
            }
            (_b = (_a = _this.props).setPreSelection) === null || _b === void 0 ? void 0 : _b.call(_a, newDate);
            if (newYear - startPeriod < 0) {
                _this.updateFocusOnPaginate(yearItemNumber - (startPeriod - newYear));
            }
            else if (newYear - startPeriod >= yearItemNumber) {
                _this.updateFocusOnPaginate(Math.abs(yearItemNumber - (newYear - startPeriod)));
            }
            else
                (_d = (_c = _this.YEAR_REFS[newYear - startPeriod]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
        };
        _this.isSameDay = function (y, other) { return isSameDay(y, other); };
        _this.isCurrentYear = function (y) { return y === getYear$1(newDate()); };
        _this.isRangeStart = function (y) {
            return _this.props.startDate &&
                _this.props.endDate &&
                isSameYear(setYear(newDate(), y), _this.props.startDate);
        };
        _this.isRangeEnd = function (y) {
            return _this.props.startDate &&
                _this.props.endDate &&
                isSameYear(setYear(newDate(), y), _this.props.endDate);
        };
        _this.isInRange = function (y) {
            return isYearInRange(y, _this.props.startDate, _this.props.endDate);
        };
        _this.isInSelectingRange = function (y) {
            var _a = _this.props, selectsStart = _a.selectsStart, selectsEnd = _a.selectsEnd, selectsRange = _a.selectsRange, startDate = _a.startDate, endDate = _a.endDate;
            if (!(selectsStart || selectsEnd || selectsRange) ||
                !_this.selectingDate()) {
                return false;
            }
            if (selectsStart && endDate) {
                return isYearInRange(y, _this.selectingDate(), endDate);
            }
            if (selectsEnd && startDate) {
                return isYearInRange(y, startDate, _this.selectingDate());
            }
            if (selectsRange && startDate && !endDate) {
                return isYearInRange(y, startDate, _this.selectingDate());
            }
            return false;
        };
        _this.isSelectingRangeStart = function (y) {
            var _a;
            if (!_this.isInSelectingRange(y)) {
                return false;
            }
            var _b = _this.props, startDate = _b.startDate, selectsStart = _b.selectsStart;
            var _year = setYear(newDate(), y);
            if (selectsStart) {
                return isSameYear(_year, (_a = _this.selectingDate()) !== null && _a !== void 0 ? _a : null);
            }
            return isSameYear(_year, startDate !== null && startDate !== void 0 ? startDate : null);
        };
        _this.isSelectingRangeEnd = function (y) {
            var _a;
            if (!_this.isInSelectingRange(y)) {
                return false;
            }
            var _b = _this.props, endDate = _b.endDate, selectsEnd = _b.selectsEnd, selectsRange = _b.selectsRange;
            var _year = setYear(newDate(), y);
            if (selectsEnd || selectsRange) {
                return isSameYear(_year, (_a = _this.selectingDate()) !== null && _a !== void 0 ? _a : null);
            }
            return isSameYear(_year, endDate !== null && endDate !== void 0 ? endDate : null);
        };
        _this.isKeyboardSelected = function (y) {
            if (_this.props.date === undefined ||
                _this.props.selected == null ||
                _this.props.preSelection == null) {
                return;
            }
            var _a = _this.props, minDate = _a.minDate, maxDate = _a.maxDate, excludeDates = _a.excludeDates, includeDates = _a.includeDates, filterDate = _a.filterDate;
            var date = getStartOfYear(setYear(_this.props.date, y));
            var isDisabled = (minDate || maxDate || excludeDates || includeDates || filterDate) &&
                isYearDisabled(y, _this.props);
            return (!_this.props.disabledKeyboardNavigation &&
                !_this.props.inline &&
                !isSameDay(date, getStartOfYear(_this.props.selected)) &&
                isSameDay(date, getStartOfYear(_this.props.preSelection)) &&
                !isDisabled);
        };
        _this.onYearClick = function (event, y) {
            var date = _this.props.date;
            if (date === undefined) {
                return;
            }
            _this.handleYearClick(getStartOfYear(setYear(date, y)), event);
        };
        _this.onYearKeyDown = function (event, y) {
            var _a, _b;
            var key = event.key;
            var _c = _this.props, date = _c.date, yearItemNumber = _c.yearItemNumber, handleOnKeyDown = _c.handleOnKeyDown;
            if (key !== KeyType.Tab) {
                // preventDefault on tab event blocks focus change
                event.preventDefault();
            }
            if (!_this.props.disabledKeyboardNavigation) {
                switch (key) {
                    case KeyType.Enter:
                        if (_this.props.selected == null) {
                            break;
                        }
                        _this.onYearClick(event, y);
                        (_b = (_a = _this.props).setPreSelection) === null || _b === void 0 ? void 0 : _b.call(_a, _this.props.selected);
                        break;
                    case KeyType.ArrowRight:
                        if (_this.props.preSelection == null) {
                            break;
                        }
                        _this.handleYearNavigation(y + 1, addYears(_this.props.preSelection, 1));
                        break;
                    case KeyType.ArrowLeft:
                        if (_this.props.preSelection == null) {
                            break;
                        }
                        _this.handleYearNavigation(y - 1, subYears(_this.props.preSelection, 1));
                        break;
                    case KeyType.ArrowUp: {
                        if (date === undefined ||
                            yearItemNumber === undefined ||
                            _this.props.preSelection == null) {
                            break;
                        }
                        var startPeriod = getYearsPeriod(date, yearItemNumber).startPeriod;
                        var offset = VERTICAL_NAVIGATION_OFFSET;
                        var newYear = y - offset;
                        if (newYear < startPeriod) {
                            var leftOverOffset = yearItemNumber % offset;
                            if (y >= startPeriod && y < startPeriod + leftOverOffset) {
                                offset = leftOverOffset;
                            }
                            else {
                                offset += leftOverOffset;
                            }
                            newYear = y - offset;
                        }
                        _this.handleYearNavigation(newYear, subYears(_this.props.preSelection, offset));
                        break;
                    }
                    case KeyType.ArrowDown: {
                        if (date === undefined ||
                            yearItemNumber === undefined ||
                            _this.props.preSelection == null) {
                            break;
                        }
                        var endPeriod = getYearsPeriod(date, yearItemNumber).endPeriod;
                        var offset = VERTICAL_NAVIGATION_OFFSET;
                        var newYear = y + offset;
                        if (newYear > endPeriod) {
                            var leftOverOffset = yearItemNumber % offset;
                            if (y <= endPeriod && y > endPeriod - leftOverOffset) {
                                offset = leftOverOffset;
                            }
                            else {
                                offset += leftOverOffset;
                            }
                            newYear = y + offset;
                        }
                        _this.handleYearNavigation(newYear, addYears(_this.props.preSelection, offset));
                        break;
                    }
                }
            }
            handleOnKeyDown && handleOnKeyDown(event);
        };
        _this.getYearClassNames = function (y) {
            var _a = _this.props, date = _a.date, minDate = _a.minDate, maxDate = _a.maxDate, selected = _a.selected, excludeDates = _a.excludeDates, includeDates = _a.includeDates, filterDate = _a.filterDate, yearClassName = _a.yearClassName;
            return clsx("react-datepicker__year-text", "react-datepicker__year-".concat(y), date ? yearClassName === null || yearClassName === void 0 ? void 0 : yearClassName(setYear(date, y)) : undefined, {
                "react-datepicker__year-text--selected": selected
                    ? y === getYear$1(selected)
                    : undefined,
                "react-datepicker__year-text--disabled": (minDate || maxDate || excludeDates || includeDates || filterDate) &&
                    isYearDisabled(y, _this.props),
                "react-datepicker__year-text--keyboard-selected": _this.isKeyboardSelected(y),
                "react-datepicker__year-text--range-start": _this.isRangeStart(y),
                "react-datepicker__year-text--range-end": _this.isRangeEnd(y),
                "react-datepicker__year-text--in-range": _this.isInRange(y),
                "react-datepicker__year-text--in-selecting-range": _this.isInSelectingRange(y),
                "react-datepicker__year-text--selecting-range-start": _this.isSelectingRangeStart(y),
                "react-datepicker__year-text--selecting-range-end": _this.isSelectingRangeEnd(y),
                "react-datepicker__year-text--today": _this.isCurrentYear(y),
            });
        };
        _this.getYearTabIndex = function (y) {
            if (_this.props.disabledKeyboardNavigation ||
                _this.props.preSelection == null) {
                return "-1";
            }
            var preSelected = getYear$1(_this.props.preSelection);
            var isPreSelectedYearDisabled = isYearDisabled(y, _this.props);
            return y === preSelected && !isPreSelectedYearDisabled ? "0" : "-1";
        };
        _this.getYearContent = function (y) {
            return _this.props.renderYearContent ? _this.props.renderYearContent(y) : y;
        };
        return _this;
    }
    Year.prototype.render = function () {
        var _this = this;
        var yearsList = [];
        var _a = this.props, date = _a.date, yearItemNumber = _a.yearItemNumber, onYearMouseEnter = _a.onYearMouseEnter, onYearMouseLeave = _a.onYearMouseLeave;
        if (date === undefined) {
            return null;
        }
        var _b = getYearsPeriod(date, yearItemNumber), startPeriod = _b.startPeriod, endPeriod = _b.endPeriod;
        var _loop_1 = function (y) {
            yearsList.push(React__default.createElement("div", { ref: this_1.YEAR_REFS[y - startPeriod], onClick: function (event) {
                    _this.onYearClick(event, y);
                }, onKeyDown: function (event) {
                    if (isSpaceKeyDown(event)) {
                        event.preventDefault();
                        event.key = KeyType.Enter;
                    }
                    _this.onYearKeyDown(event, y);
                }, tabIndex: Number(this_1.getYearTabIndex(y)), className: this_1.getYearClassNames(y), onMouseEnter: !this_1.props.usePointerEvent
                    ? function (event) { return onYearMouseEnter(event, y); }
                    : undefined, onPointerEnter: this_1.props.usePointerEvent
                    ? function (event) { return onYearMouseEnter(event, y); }
                    : undefined, onMouseLeave: !this_1.props.usePointerEvent
                    ? function (event) { return onYearMouseLeave(event, y); }
                    : undefined, onPointerLeave: this_1.props.usePointerEvent
                    ? function (event) { return onYearMouseLeave(event, y); }
                    : undefined, key: y, "aria-current": this_1.isCurrentYear(y) ? "date" : undefined }, this_1.getYearContent(y)));
        };
        var this_1 = this;
        for (var y = startPeriod; y <= endPeriod; y++) {
            _loop_1(y);
        }
        return (React__default.createElement("div", { className: "react-datepicker__year" },
            React__default.createElement("div", { className: "react-datepicker__year-wrapper", onMouseLeave: !this.props.usePointerEvent
                    ? this.props.clearSelectingDate
                    : undefined, onPointerLeave: this.props.usePointerEvent
                    ? this.props.clearSelectingDate
                    : undefined }, yearsList)));
    };
    return Year;
}(Component));

function generateYears(year, noOfYear, minDate, maxDate) {
    var list = [];
    for (var i = 0; i < 2 * noOfYear + 1; i++) {
        var newYear = year + noOfYear - i;
        var isInRange = true;
        if (minDate) {
            isInRange = getYear$1(minDate) <= newYear;
        }
        if (maxDate && isInRange) {
            isInRange = getYear$1(maxDate) >= newYear;
        }
        if (isInRange) {
            list.push(newYear);
        }
    }
    return list;
}
var YearDropdownOptions = /** @class */ (function (_super) {
    __extends(YearDropdownOptions, _super);
    function YearDropdownOptions(props) {
        var _this = _super.call(this, props) || this;
        _this.renderOptions = function () {
            var selectedYear = _this.props.year;
            var options = _this.state.yearsList.map(function (year) { return (React__default.createElement("div", { className: selectedYear === year
                    ? "react-datepicker__year-option react-datepicker__year-option--selected_year"
                    : "react-datepicker__year-option", key: year, onClick: _this.onChange.bind(_this, year), "aria-selected": selectedYear === year ? "true" : undefined },
                selectedYear === year ? (React__default.createElement("span", { className: "react-datepicker__year-option--selected" }, "\u2713")) : (""),
                year)); });
            var minYear = _this.props.minDate ? getYear$1(_this.props.minDate) : null;
            var maxYear = _this.props.maxDate ? getYear$1(_this.props.maxDate) : null;
            if (!maxYear || !_this.state.yearsList.find(function (year) { return year === maxYear; })) {
                options.unshift(React__default.createElement("div", { className: "react-datepicker__year-option", key: "upcoming", onClick: _this.incrementYears },
                    React__default.createElement("a", { className: "react-datepicker__navigation react-datepicker__navigation--years react-datepicker__navigation--years-upcoming" })));
            }
            if (!minYear || !_this.state.yearsList.find(function (year) { return year === minYear; })) {
                options.push(React__default.createElement("div", { className: "react-datepicker__year-option", key: "previous", onClick: _this.decrementYears },
                    React__default.createElement("a", { className: "react-datepicker__navigation react-datepicker__navigation--years react-datepicker__navigation--years-previous" })));
            }
            return options;
        };
        _this.onChange = function (year) {
            _this.props.onChange(year);
        };
        _this.handleClickOutside = function () {
            _this.props.onCancel();
        };
        _this.shiftYears = function (amount) {
            var years = _this.state.yearsList.map(function (year) {
                return year + amount;
            });
            _this.setState({
                yearsList: years,
            });
        };
        _this.incrementYears = function () {
            return _this.shiftYears(1);
        };
        _this.decrementYears = function () {
            return _this.shiftYears(-1);
        };
        var yearDropdownItemNumber = props.yearDropdownItemNumber, scrollableYearDropdown = props.scrollableYearDropdown;
        var noOfYear = yearDropdownItemNumber || (scrollableYearDropdown ? 10 : 5);
        _this.state = {
            yearsList: generateYears(_this.props.year, noOfYear, _this.props.minDate, _this.props.maxDate),
        };
        _this.dropdownRef = createRef();
        return _this;
    }
    YearDropdownOptions.prototype.componentDidMount = function () {
        var dropdownCurrent = this.dropdownRef.current;
        if (dropdownCurrent) {
            // Get array from HTMLCollection
            var dropdownCurrentChildren = dropdownCurrent.children
                ? Array.from(dropdownCurrent.children)
                : null;
            var selectedYearOptionEl = dropdownCurrentChildren
                ? dropdownCurrentChildren.find(function (childEl) { return childEl.ariaSelected; })
                : null;
            dropdownCurrent.scrollTop =
                selectedYearOptionEl && selectedYearOptionEl instanceof HTMLElement
                    ? selectedYearOptionEl.offsetTop +
                        (selectedYearOptionEl.clientHeight - dropdownCurrent.clientHeight) /
                            2
                    : (dropdownCurrent.scrollHeight - dropdownCurrent.clientHeight) / 2;
        }
    };
    YearDropdownOptions.prototype.render = function () {
        var dropdownClass = clsx({
            "react-datepicker__year-dropdown": true,
            "react-datepicker__year-dropdown--scrollable": this.props.scrollableYearDropdown,
        });
        return (React__default.createElement(ClickOutsideWrapper, { className: dropdownClass, containerRef: this.dropdownRef, onClickOutside: this.handleClickOutside }, this.renderOptions()));
    };
    return YearDropdownOptions;
}(Component));

var YearDropdown = /** @class */ (function (_super) {
    __extends(YearDropdown, _super);
    function YearDropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            dropdownVisible: false,
        };
        _this.renderSelectOptions = function () {
            var minYear = _this.props.minDate
                ? getYear$1(_this.props.minDate)
                : 1900;
            var maxYear = _this.props.maxDate
                ? getYear$1(_this.props.maxDate)
                : 2100;
            var options = [];
            for (var i = minYear; i <= maxYear; i++) {
                options.push(React__default.createElement("option", { key: i, value: i }, i));
            }
            return options;
        };
        _this.onSelectChange = function (event) {
            _this.onChange(parseInt(event.target.value));
        };
        _this.renderSelectMode = function () { return (React__default.createElement("select", { value: _this.props.year, className: "react-datepicker__year-select", onChange: _this.onSelectChange }, _this.renderSelectOptions())); };
        _this.renderReadView = function (visible) { return (React__default.createElement("div", { key: "read", style: { visibility: visible ? "visible" : "hidden" }, className: "react-datepicker__year-read-view", onClick: function (event) {
                return _this.toggleDropdown(event);
            } },
            React__default.createElement("span", { className: "react-datepicker__year-read-view--down-arrow" }),
            React__default.createElement("span", { className: "react-datepicker__year-read-view--selected-year" }, _this.props.year))); };
        _this.renderDropdown = function () { return (React__default.createElement(YearDropdownOptions, _assign({ key: "dropdown" }, _this.props, { onChange: _this.onChange, onCancel: _this.toggleDropdown }))); };
        _this.renderScrollMode = function () {
            var dropdownVisible = _this.state.dropdownVisible;
            var result = [_this.renderReadView(!dropdownVisible)];
            if (dropdownVisible) {
                result.unshift(_this.renderDropdown());
            }
            return result;
        };
        _this.onChange = function (year) {
            _this.toggleDropdown();
            if (year === _this.props.year)
                return;
            _this.props.onChange(year);
        };
        _this.toggleDropdown = function (event) {
            _this.setState({
                dropdownVisible: !_this.state.dropdownVisible,
            }, function () {
                if (_this.props.adjustDateOnChange) {
                    _this.handleYearChange(_this.props.date, event);
                }
            });
        };
        _this.handleYearChange = function (date, event) {
            var _a;
            (_a = _this.onSelect) === null || _a === void 0 ? void 0 : _a.call(_this, date, event);
            _this.setOpen();
        };
        _this.onSelect = function (date, event) {
            var _a, _b;
            (_b = (_a = _this.props).onSelect) === null || _b === void 0 ? void 0 : _b.call(_a, date, event);
        };
        _this.setOpen = function () {
            var _a, _b;
            (_b = (_a = _this.props).setOpen) === null || _b === void 0 ? void 0 : _b.call(_a, true);
        };
        return _this;
    }
    YearDropdown.prototype.render = function () {
        var renderedDropdown;
        switch (this.props.dropdownMode) {
            case "scroll":
                renderedDropdown = this.renderScrollMode();
                break;
            case "select":
                renderedDropdown = this.renderSelectMode();
                break;
        }
        return (React__default.createElement("div", { className: "react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--".concat(this.props.dropdownMode) }, renderedDropdown));
    };
    return YearDropdown;
}(Component));

var DROPDOWN_FOCUS_CLASSNAMES = [
    "react-datepicker__year-select",
    "react-datepicker__month-select",
    "react-datepicker__month-year-select",
];
var isDropdownSelect = function (element) {
    var classNames = (element.className || "").split(/\s+/);
    return DROPDOWN_FOCUS_CLASSNAMES.some(function (testClassname) { return classNames.indexOf(testClassname) >= 0; });
};
var Calendar = /** @class */ (function (_super) {
    __extends(Calendar, _super);
    function Calendar(props) {
        var _this = _super.call(this, props) || this;
        _this.monthContainer = undefined;
        _this.handleClickOutside = function (event) {
            _this.props.onClickOutside(event);
        };
        _this.setClickOutsideRef = function () {
            return _this.containerRef.current;
        };
        _this.handleDropdownFocus = function (event) {
            var _a, _b;
            if (isDropdownSelect(event.target)) {
                (_b = (_a = _this.props).onDropdownFocus) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            }
        };
        _this.getDateInView = function () {
            var _a = _this.props, preSelection = _a.preSelection, selected = _a.selected, openToDate = _a.openToDate;
            var minDate = getEffectiveMinDate(_this.props);
            var maxDate = getEffectiveMaxDate(_this.props);
            var current = newDate();
            var initialDate = openToDate || selected || preSelection;
            if (initialDate) {
                return initialDate;
            }
            else {
                if (minDate && isBefore(current, minDate)) {
                    return minDate;
                }
                else if (maxDate && isAfter(current, maxDate)) {
                    return maxDate;
                }
            }
            return current;
        };
        _this.increaseMonth = function () {
            _this.setState(function (_a) {
                var date = _a.date;
                return ({
                    date: addMonths(date, 1),
                });
            }, function () { return _this.handleMonthChange(_this.state.date); });
        };
        _this.decreaseMonth = function () {
            _this.setState(function (_a) {
                var date = _a.date;
                return ({
                    date: subMonths(date, 1),
                });
            }, function () { return _this.handleMonthChange(_this.state.date); });
        };
        _this.handleDayClick = function (day, event, monthSelectedIn) {
            _this.props.onSelect(day, event, monthSelectedIn);
            _this.props.setPreSelection && _this.props.setPreSelection(day);
        };
        _this.handleDayMouseEnter = function (day) {
            _this.setState({ selectingDate: day });
            _this.props.onDayMouseEnter && _this.props.onDayMouseEnter(day);
        };
        _this.handleMonthMouseLeave = function () {
            _this.setState({ selectingDate: undefined });
            _this.props.onMonthMouseLeave && _this.props.onMonthMouseLeave();
        };
        _this.handleYearMouseEnter = function (event, year) {
            _this.setState({ selectingDate: setYear(newDate(), year) });
            !!_this.props.onYearMouseEnter && _this.props.onYearMouseEnter(event, year);
        };
        _this.handleYearMouseLeave = function (event, year) {
            !!_this.props.onYearMouseLeave && _this.props.onYearMouseLeave(event, year);
        };
        _this.handleYearChange = function (date) {
            var _a, _b, _c, _d;
            (_b = (_a = _this.props).onYearChange) === null || _b === void 0 ? void 0 : _b.call(_a, date);
            _this.setState({ isRenderAriaLiveMessage: true });
            if (_this.props.adjustDateOnChange) {
                _this.props.onSelect(date);
                (_d = (_c = _this.props).setOpen) === null || _d === void 0 ? void 0 : _d.call(_c, true);
            }
            _this.props.setPreSelection && _this.props.setPreSelection(date);
        };
        _this.getEnabledPreSelectionDateForMonth = function (date) {
            if (!isDayDisabled(date, _this.props)) {
                return date;
            }
            var startOfMonth = getStartOfMonth(date);
            var endOfMonth = getEndOfMonth(date);
            var totalDays = differenceInDays(endOfMonth, startOfMonth);
            var preSelectedDate = null;
            for (var dayIdx = 0; dayIdx <= totalDays; dayIdx++) {
                var processingDate = addDays(startOfMonth, dayIdx);
                if (!isDayDisabled(processingDate, _this.props)) {
                    preSelectedDate = processingDate;
                    break;
                }
            }
            return preSelectedDate;
        };
        _this.handleMonthChange = function (date) {
            var _a, _b, _c;
            var enabledPreSelectionDate = (_a = _this.getEnabledPreSelectionDateForMonth(date)) !== null && _a !== void 0 ? _a : date;
            _this.handleCustomMonthChange(enabledPreSelectionDate);
            if (_this.props.adjustDateOnChange) {
                _this.props.onSelect(enabledPreSelectionDate);
                (_c = (_b = _this.props).setOpen) === null || _c === void 0 ? void 0 : _c.call(_b, true);
            }
            _this.props.setPreSelection &&
                _this.props.setPreSelection(enabledPreSelectionDate);
        };
        _this.handleCustomMonthChange = function (date) {
            var _a, _b;
            (_b = (_a = _this.props).onMonthChange) === null || _b === void 0 ? void 0 : _b.call(_a, date);
            _this.setState({ isRenderAriaLiveMessage: true });
        };
        _this.handleMonthYearChange = function (date) {
            _this.handleYearChange(date);
            _this.handleMonthChange(date);
        };
        _this.changeYear = function (year) {
            _this.setState(function (_a) {
                var date = _a.date;
                return ({
                    date: setYear(date, Number(year)),
                });
            }, function () { return _this.handleYearChange(_this.state.date); });
        };
        _this.changeMonth = function (month) {
            _this.setState(function (_a) {
                var date = _a.date;
                return ({
                    date: setMonth(date, Number(month)),
                });
            }, function () { return _this.handleMonthChange(_this.state.date); });
        };
        _this.changeMonthYear = function (monthYear) {
            _this.setState(function (_a) {
                var date = _a.date;
                return ({
                    date: setYear(setMonth(date, getMonth(monthYear)), getYear$1(monthYear)),
                });
            }, function () { return _this.handleMonthYearChange(_this.state.date); });
        };
        _this.header = function (date) {
            if (date === void 0) { date = _this.state.date; }
            var startOfWeek = getStartOfWeek(date, _this.props.locale, _this.props.calendarStartDay);
            var dayNames = [];
            if (_this.props.showWeekNumbers) {
                dayNames.push(React__default.createElement("div", { key: "W", className: "react-datepicker__day-name" }, _this.props.weekLabel || "#"));
            }
            return dayNames.concat([0, 1, 2, 3, 4, 5, 6].map(function (offset) {
                var day = addDays(startOfWeek, offset);
                var weekDayName = _this.formatWeekday(day, _this.props.locale);
                var weekDayClassName = _this.props.weekDayClassName
                    ? _this.props.weekDayClassName(day)
                    : undefined;
                return (React__default.createElement("div", { key: offset, "aria-label": formatDate(day, "EEEE", _this.props.locale), className: clsx("react-datepicker__day-name", weekDayClassName) }, weekDayName));
            }));
        };
        _this.formatWeekday = function (day, locale) {
            if (_this.props.formatWeekDay) {
                return getFormattedWeekdayInLocale(day, _this.props.formatWeekDay, locale);
            }
            return _this.props.useWeekdaysShort
                ? getWeekdayShortInLocale(day, locale)
                : getWeekdayMinInLocale(day, locale);
        };
        _this.decreaseYear = function () {
            _this.setState(function (_a) {
                var _b;
                var date = _a.date;
                return ({
                    date: subYears(date, _this.props.showYearPicker
                        ? ((_b = _this.props.yearItemNumber) !== null && _b !== void 0 ? _b : Calendar.defaultProps.yearItemNumber)
                        : 1),
                });
            }, function () { return _this.handleYearChange(_this.state.date); });
        };
        _this.clearSelectingDate = function () {
            _this.setState({ selectingDate: undefined });
        };
        _this.renderPreviousButton = function () {
            var _a, _b, _c;
            if (_this.props.renderCustomHeader) {
                return;
            }
            var monthsShown = (_a = _this.props.monthsShown) !== null && _a !== void 0 ? _a : Calendar.defaultProps.monthsShown;
            var monthsToSubtract = _this.props.showPreviousMonths
                ? monthsShown - 1
                : 0;
            var monthSelectedIn = (_b = _this.props.monthSelectedIn) !== null && _b !== void 0 ? _b : monthsToSubtract;
            var fromMonthDate = subMonths(_this.state.date, monthSelectedIn);
            var allPrevDaysDisabled;
            switch (true) {
                case _this.props.showMonthYearPicker:
                    allPrevDaysDisabled = yearDisabledBefore(_this.state.date, _this.props);
                    break;
                case _this.props.showYearPicker:
                    allPrevDaysDisabled = yearsDisabledBefore(_this.state.date, _this.props);
                    break;
                case _this.props.showQuarterYearPicker:
                    allPrevDaysDisabled = quarterDisabledBefore(_this.state.date, _this.props);
                    break;
                default:
                    allPrevDaysDisabled = monthDisabledBefore(fromMonthDate, _this.props);
                    break;
            }
            if ((!((_c = _this.props.forceShowMonthNavigation) !== null && _c !== void 0 ? _c : Calendar.defaultProps.forceShowMonthNavigation) &&
                !_this.props.showDisabledMonthNavigation &&
                allPrevDaysDisabled) ||
                _this.props.showTimeSelectOnly) {
                return;
            }
            var iconClasses = [
                "react-datepicker__navigation-icon",
                "react-datepicker__navigation-icon--previous",
            ];
            var classes = [
                "react-datepicker__navigation",
                "react-datepicker__navigation--previous",
            ];
            var clickHandler = _this.decreaseMonth;
            if (_this.props.showMonthYearPicker ||
                _this.props.showQuarterYearPicker ||
                _this.props.showYearPicker) {
                clickHandler = _this.decreaseYear;
            }
            if (allPrevDaysDisabled && _this.props.showDisabledMonthNavigation) {
                classes.push("react-datepicker__navigation--previous--disabled");
                clickHandler = undefined;
            }
            var isForYear = _this.props.showMonthYearPicker ||
                _this.props.showQuarterYearPicker ||
                _this.props.showYearPicker;
            var _d = _this.props, _e = _d.previousMonthButtonLabel, previousMonthButtonLabel = _e === void 0 ? Calendar.defaultProps.previousMonthButtonLabel : _e, _f = _d.previousYearButtonLabel, previousYearButtonLabel = _f === void 0 ? Calendar.defaultProps.previousYearButtonLabel : _f;
            var _g = _this.props, _h = _g.previousMonthAriaLabel, previousMonthAriaLabel = _h === void 0 ? typeof previousMonthButtonLabel === "string"
                ? previousMonthButtonLabel
                : "Previous Month" : _h, _j = _g.previousYearAriaLabel, previousYearAriaLabel = _j === void 0 ? typeof previousYearButtonLabel === "string"
                ? previousYearButtonLabel
                : "Previous Year" : _j;
            return (React__default.createElement("button", { type: "button", className: classes.join(" "), onClick: clickHandler, onKeyDown: _this.props.handleOnKeyDown, "aria-label": isForYear ? previousYearAriaLabel : previousMonthAriaLabel },
                React__default.createElement("span", { className: iconClasses.join(" ") }, isForYear ? previousYearButtonLabel : previousMonthButtonLabel)));
        };
        _this.increaseYear = function () {
            _this.setState(function (_a) {
                var _b;
                var date = _a.date;
                return ({
                    date: addYears(date, _this.props.showYearPicker
                        ? ((_b = _this.props.yearItemNumber) !== null && _b !== void 0 ? _b : Calendar.defaultProps.yearItemNumber)
                        : 1),
                });
            }, function () { return _this.handleYearChange(_this.state.date); });
        };
        _this.renderNextButton = function () {
            var _a;
            if (_this.props.renderCustomHeader) {
                return;
            }
            var allNextDaysDisabled;
            switch (true) {
                case _this.props.showMonthYearPicker:
                    allNextDaysDisabled = yearDisabledAfter(_this.state.date, _this.props);
                    break;
                case _this.props.showYearPicker:
                    allNextDaysDisabled = yearsDisabledAfter(_this.state.date, _this.props);
                    break;
                case _this.props.showQuarterYearPicker:
                    allNextDaysDisabled = quarterDisabledAfter(_this.state.date, _this.props);
                    break;
                default:
                    allNextDaysDisabled = monthDisabledAfter(_this.state.date, _this.props);
                    break;
            }
            if ((!((_a = _this.props.forceShowMonthNavigation) !== null && _a !== void 0 ? _a : Calendar.defaultProps.forceShowMonthNavigation) &&
                !_this.props.showDisabledMonthNavigation &&
                allNextDaysDisabled) ||
                _this.props.showTimeSelectOnly) {
                return;
            }
            var classes = [
                "react-datepicker__navigation",
                "react-datepicker__navigation--next",
            ];
            var iconClasses = [
                "react-datepicker__navigation-icon",
                "react-datepicker__navigation-icon--next",
            ];
            if (_this.props.showTimeSelect) {
                classes.push("react-datepicker__navigation--next--with-time");
            }
            if (_this.props.todayButton) {
                classes.push("react-datepicker__navigation--next--with-today-button");
            }
            var clickHandler = _this.increaseMonth;
            if (_this.props.showMonthYearPicker ||
                _this.props.showQuarterYearPicker ||
                _this.props.showYearPicker) {
                clickHandler = _this.increaseYear;
            }
            if (allNextDaysDisabled && _this.props.showDisabledMonthNavigation) {
                classes.push("react-datepicker__navigation--next--disabled");
                clickHandler = undefined;
            }
            var isForYear = _this.props.showMonthYearPicker ||
                _this.props.showQuarterYearPicker ||
                _this.props.showYearPicker;
            var _b = _this.props, _c = _b.nextMonthButtonLabel, nextMonthButtonLabel = _c === void 0 ? Calendar.defaultProps.nextMonthButtonLabel : _c, _d = _b.nextYearButtonLabel, nextYearButtonLabel = _d === void 0 ? Calendar.defaultProps.nextYearButtonLabel : _d;
            var _e = _this.props, _f = _e.nextMonthAriaLabel, nextMonthAriaLabel = _f === void 0 ? typeof nextMonthButtonLabel === "string"
                ? nextMonthButtonLabel
                : "Next Month" : _f, _g = _e.nextYearAriaLabel, nextYearAriaLabel = _g === void 0 ? typeof nextYearButtonLabel === "string"
                ? nextYearButtonLabel
                : "Next Year" : _g;
            return (React__default.createElement("button", { type: "button", className: classes.join(" "), onClick: clickHandler, onKeyDown: _this.props.handleOnKeyDown, "aria-label": isForYear ? nextYearAriaLabel : nextMonthAriaLabel },
                React__default.createElement("span", { className: iconClasses.join(" ") }, isForYear ? nextYearButtonLabel : nextMonthButtonLabel)));
        };
        _this.renderCurrentMonth = function (date) {
            if (date === void 0) { date = _this.state.date; }
            var classes = ["react-datepicker__current-month"];
            if (_this.props.showYearDropdown) {
                classes.push("react-datepicker__current-month--hasYearDropdown");
            }
            if (_this.props.showMonthDropdown) {
                classes.push("react-datepicker__current-month--hasMonthDropdown");
            }
            if (_this.props.showMonthYearDropdown) {
                classes.push("react-datepicker__current-month--hasMonthYearDropdown");
            }
            return (React__default.createElement("h2", { className: classes.join(" ") }, formatDate(date, _this.props.dateFormat, _this.props.locale)));
        };
        _this.renderYearDropdown = function (overrideHide) {
            if (overrideHide === void 0) { overrideHide = false; }
            if (!_this.props.showYearDropdown || overrideHide) {
                return;
            }
            return (React__default.createElement(YearDropdown, _assign({}, Calendar.defaultProps, _this.props, { date: _this.state.date, onChange: _this.changeYear, year: getYear$1(_this.state.date) })));
        };
        _this.renderMonthDropdown = function (overrideHide) {
            if (overrideHide === void 0) { overrideHide = false; }
            if (!_this.props.showMonthDropdown || overrideHide) {
                return;
            }
            return (React__default.createElement(MonthDropdown, _assign({}, Calendar.defaultProps, _this.props, { month: getMonth(_this.state.date), onChange: _this.changeMonth })));
        };
        _this.renderMonthYearDropdown = function (overrideHide) {
            if (overrideHide === void 0) { overrideHide = false; }
            if (!_this.props.showMonthYearDropdown || overrideHide) {
                return;
            }
            return (React__default.createElement(MonthYearDropdown, _assign({}, Calendar.defaultProps, _this.props, { date: _this.state.date, onChange: _this.changeMonthYear })));
        };
        _this.handleTodayButtonClick = function (event) {
            _this.props.onSelect(getStartOfToday(), event);
            _this.props.setPreSelection && _this.props.setPreSelection(getStartOfToday());
        };
        _this.renderTodayButton = function () {
            if (!_this.props.todayButton || _this.props.showTimeSelectOnly) {
                return;
            }
            return (React__default.createElement("div", { className: "react-datepicker__today-button", onClick: _this.handleTodayButtonClick }, _this.props.todayButton));
        };
        _this.renderDefaultHeader = function (_a) {
            var monthDate = _a.monthDate, i = _a.i;
            return (React__default.createElement("div", { className: "react-datepicker__header ".concat(_this.props.showTimeSelect
                    ? "react-datepicker__header--has-time-select"
                    : "") },
                _this.renderCurrentMonth(monthDate),
                React__default.createElement("div", { className: "react-datepicker__header__dropdown react-datepicker__header__dropdown--".concat(_this.props.dropdownMode), onFocus: _this.handleDropdownFocus },
                    _this.renderMonthDropdown(i !== 0),
                    _this.renderMonthYearDropdown(i !== 0),
                    _this.renderYearDropdown(i !== 0)),
                React__default.createElement("div", { className: "react-datepicker__day-names" }, _this.header(monthDate))));
        };
        _this.renderCustomHeader = function (headerArgs) {
            var _a, _b;
            var monthDate = headerArgs.monthDate, i = headerArgs.i;
            if ((_this.props.showTimeSelect && !_this.state.monthContainer) ||
                _this.props.showTimeSelectOnly) {
                return null;
            }
            var prevMonthButtonDisabled = monthDisabledBefore(_this.state.date, _this.props);
            var nextMonthButtonDisabled = monthDisabledAfter(_this.state.date, _this.props);
            var prevYearButtonDisabled = yearDisabledBefore(_this.state.date, _this.props);
            var nextYearButtonDisabled = yearDisabledAfter(_this.state.date, _this.props);
            var showDayNames = !_this.props.showMonthYearPicker &&
                !_this.props.showQuarterYearPicker &&
                !_this.props.showYearPicker;
            return (React__default.createElement("div", { className: "react-datepicker__header react-datepicker__header--custom", onFocus: _this.props.onDropdownFocus }, (_b = (_a = _this.props).renderCustomHeader) === null || _b === void 0 ? void 0 :
                _b.call(_a, _assign(_assign({}, _this.state), { customHeaderCount: i, monthDate: monthDate, changeMonth: _this.changeMonth, changeYear: _this.changeYear, decreaseMonth: _this.decreaseMonth, increaseMonth: _this.increaseMonth, decreaseYear: _this.decreaseYear, increaseYear: _this.increaseYear, prevMonthButtonDisabled: prevMonthButtonDisabled, nextMonthButtonDisabled: nextMonthButtonDisabled, prevYearButtonDisabled: prevYearButtonDisabled, nextYearButtonDisabled: nextYearButtonDisabled })),
                showDayNames && (React__default.createElement("div", { className: "react-datepicker__day-names" }, _this.header(monthDate)))));
        };
        _this.renderYearHeader = function (_a) {
            var monthDate = _a.monthDate;
            var _b = _this.props, showYearPicker = _b.showYearPicker, _c = _b.yearItemNumber, yearItemNumber = _c === void 0 ? Calendar.defaultProps.yearItemNumber : _c;
            var _d = getYearsPeriod(monthDate, yearItemNumber), startPeriod = _d.startPeriod, endPeriod = _d.endPeriod;
            return (React__default.createElement("div", { className: "react-datepicker__header react-datepicker-year-header" }, showYearPicker ? "".concat(startPeriod, " - ").concat(endPeriod) : getYear$1(monthDate)));
        };
        _this.renderHeader = function (_a) {
            var monthDate = _a.monthDate, _b = _a.i, i = _b === void 0 ? 0 : _b;
            var headerArgs = { monthDate: monthDate, i: i };
            switch (true) {
                case _this.props.renderCustomHeader !== undefined:
                    return _this.renderCustomHeader(headerArgs);
                case _this.props.showMonthYearPicker ||
                    _this.props.showQuarterYearPicker ||
                    _this.props.showYearPicker:
                    return _this.renderYearHeader(headerArgs);
                default:
                    return _this.renderDefaultHeader(headerArgs);
            }
        };
        _this.renderMonths = function () {
            var _a, _b;
            if (_this.props.showTimeSelectOnly || _this.props.showYearPicker) {
                return;
            }
            var monthList = [];
            var monthsShown = (_a = _this.props.monthsShown) !== null && _a !== void 0 ? _a : Calendar.defaultProps.monthsShown;
            var monthsToSubtract = _this.props.showPreviousMonths
                ? monthsShown - 1
                : 0;
            var fromMonthDate = _this.props.showMonthYearPicker || _this.props.showQuarterYearPicker
                ? addYears(_this.state.date, monthsToSubtract)
                : subMonths(_this.state.date, monthsToSubtract);
            var monthSelectedIn = (_b = _this.props.monthSelectedIn) !== null && _b !== void 0 ? _b : monthsToSubtract;
            for (var i = 0; i < monthsShown; ++i) {
                var monthsToAdd = i - monthSelectedIn + monthsToSubtract;
                var monthDate = _this.props.showMonthYearPicker || _this.props.showQuarterYearPicker
                    ? addYears(fromMonthDate, monthsToAdd)
                    : addMonths(fromMonthDate, monthsToAdd);
                var monthKey = "month-".concat(i);
                var monthShowsDuplicateDaysEnd = i < monthsShown - 1;
                var monthShowsDuplicateDaysStart = i > 0;
                monthList.push(React__default.createElement("div", { key: monthKey, ref: function (div) {
                        _this.monthContainer = div !== null && div !== void 0 ? div : undefined;
                    }, className: "react-datepicker__month-container" },
                    _this.renderHeader({ monthDate: monthDate, i: i }),
                    React__default.createElement(Month, _assign({}, Calendar.defaultProps, _this.props, { ariaLabelPrefix: _this.props.monthAriaLabelPrefix, day: monthDate, onDayClick: _this.handleDayClick, handleOnKeyDown: _this.props.handleOnDayKeyDown, handleOnMonthKeyDown: _this.props.handleOnKeyDown, onDayMouseEnter: _this.handleDayMouseEnter, onMouseLeave: _this.handleMonthMouseLeave, orderInDisplay: i, selectingDate: _this.state.selectingDate, monthShowsDuplicateDaysEnd: monthShowsDuplicateDaysEnd, monthShowsDuplicateDaysStart: monthShowsDuplicateDaysStart }))));
            }
            return monthList;
        };
        _this.renderYears = function () {
            if (_this.props.showTimeSelectOnly) {
                return;
            }
            if (_this.props.showYearPicker) {
                return (React__default.createElement("div", { className: "react-datepicker__year--container" },
                    _this.renderHeader({ monthDate: _this.state.date }),
                    React__default.createElement(Year, _assign({}, Calendar.defaultProps, _this.props, { selectingDate: _this.state.selectingDate, date: _this.state.date, onDayClick: _this.handleDayClick, clearSelectingDate: _this.clearSelectingDate, onYearMouseEnter: _this.handleYearMouseEnter, onYearMouseLeave: _this.handleYearMouseLeave }))));
            }
            return;
        };
        _this.renderTimeSection = function () {
            if (_this.props.showTimeSelect &&
                (_this.state.monthContainer || _this.props.showTimeSelectOnly)) {
                return (React__default.createElement(Time, _assign({}, Calendar.defaultProps, _this.props, { onChange: _this.props.onTimeChange, format: _this.props.timeFormat, intervals: _this.props.timeIntervals, monthRef: _this.state.monthContainer })));
            }
            return;
        };
        _this.renderInputTimeSection = function () {
            var time = _this.props.selected
                ? new Date(_this.props.selected)
                : undefined;
            var timeValid = time && isValid$1(time) && Boolean(_this.props.selected);
            var timeString = timeValid
                ? "".concat(addZero(time.getHours()), ":").concat(addZero(time.getMinutes()))
                : "";
            if (_this.props.showTimeInput) {
                return (React__default.createElement(InputTime, _assign({}, Calendar.defaultProps, _this.props, { date: time, timeString: timeString, onChange: _this.props.onTimeChange })));
            }
            return;
        };
        _this.renderAriaLiveRegion = function () {
            var _a;
            var _b = getYearsPeriod(_this.state.date, (_a = _this.props.yearItemNumber) !== null && _a !== void 0 ? _a : Calendar.defaultProps.yearItemNumber), startPeriod = _b.startPeriod, endPeriod = _b.endPeriod;
            var ariaLiveMessage;
            if (_this.props.showYearPicker) {
                ariaLiveMessage = "".concat(startPeriod, " - ").concat(endPeriod);
            }
            else if (_this.props.showMonthYearPicker ||
                _this.props.showQuarterYearPicker) {
                ariaLiveMessage = getYear$1(_this.state.date);
            }
            else {
                ariaLiveMessage = "".concat(getMonthInLocale(getMonth(_this.state.date), _this.props.locale), " ").concat(getYear$1(_this.state.date));
            }
            return (React__default.createElement("span", { role: "alert", "aria-live": "polite", className: "react-datepicker__aria-live" }, _this.state.isRenderAriaLiveMessage && ariaLiveMessage));
        };
        _this.renderChildren = function () {
            if (_this.props.children) {
                return (React__default.createElement("div", { className: "react-datepicker__children-container" }, _this.props.children));
            }
            return;
        };
        _this.containerRef = createRef();
        _this.state = {
            date: _this.getDateInView(),
            selectingDate: undefined,
            monthContainer: undefined,
            isRenderAriaLiveMessage: false,
        };
        return _this;
    }
    Object.defineProperty(Calendar, "defaultProps", {
        get: function () {
            return {
                monthsShown: 1,
                forceShowMonthNavigation: false,
                timeCaption: "Time",
                previousYearButtonLabel: "Previous Year",
                nextYearButtonLabel: "Next Year",
                previousMonthButtonLabel: "Previous Month",
                nextMonthButtonLabel: "Next Month",
                yearItemNumber: DEFAULT_YEAR_ITEM_NUMBER,
            };
        },
        enumerable: false,
        configurable: true
    });
    Calendar.prototype.componentDidMount = function () {
        var _this = this;
        // monthContainer height is needed in time component
        // to determine the height for the ul in the time component
        // setState here so height is given after final component
        // layout is rendered
        if (this.props.showTimeSelect) {
            this.assignMonthContainer = (function () {
                _this.setState({ monthContainer: _this.monthContainer });
            })();
        }
    };
    Calendar.prototype.componentDidUpdate = function (prevProps) {
        var _this = this;
        if (this.props.preSelection &&
            (!isSameDay(this.props.preSelection, prevProps.preSelection) ||
                this.props.monthSelectedIn !== prevProps.monthSelectedIn)) {
            var hasMonthChanged_1 = !isSameMonth(this.state.date, this.props.preSelection);
            this.setState({
                date: this.props.preSelection,
            }, function () { return hasMonthChanged_1 && _this.handleCustomMonthChange(_this.state.date); });
        }
        else if (this.props.openToDate &&
            !isSameDay(this.props.openToDate, prevProps.openToDate)) {
            this.setState({
                date: this.props.openToDate,
            });
        }
    };
    Calendar.prototype.render = function () {
        var Container = this.props.container || CalendarContainer;
        return (React__default.createElement(ClickOutsideWrapper, { onClickOutside: this.handleClickOutside, style: { display: "contents" }, containerRef: this.containerRef, ignoreClass: this.props.outsideClickIgnoreClass },
            React__default.createElement(Container, { className: clsx("react-datepicker", this.props.className, {
                    "react-datepicker--time-only": this.props.showTimeSelectOnly,
                }), showTime: this.props.showTimeSelect || this.props.showTimeInput, showTimeSelectOnly: this.props.showTimeSelectOnly },
                this.renderAriaLiveRegion(),
                this.renderPreviousButton(),
                this.renderNextButton(),
                this.renderMonths(),
                this.renderYears(),
                this.renderTodayButton(),
                this.renderTimeSection(),
                this.renderInputTimeSection(),
                this.renderChildren())));
    };
    return Calendar;
}(Component));

/**
 * `CalendarIcon` is a React component that renders an icon for a calendar.
 * The icon can be a string representing a CSS class, a React node, or a default SVG icon.
 *
 * @component
 * @prop  icon - The icon to be displayed. This can be a string representing a CSS class or a React node.
 * @prop  className - An optional string representing additional CSS classes to be applied to the icon.
 * @prop  onClick - An optional function to be called when the icon is clicked.
 *
 * @example
 * // To use a CSS class as the icon
 * <CalendarIcon icon="my-icon-class" onClick={myClickHandler} />
 *
 * @example
 * // To use a React node as the icon
 * <CalendarIcon icon={<MyIconComponent />} onClick={myClickHandler} />
 *
 * @returns  The `CalendarIcon` component.
 */
var CalendarIcon = function (_a) {
    var icon = _a.icon, _b = _a.className, className = _b === void 0 ? "" : _b, onClick = _a.onClick;
    var defaultClass = "react-datepicker__calendar-icon";
    if (typeof icon === "string") {
        return (React__default.createElement("i", { className: "".concat(defaultClass, " ").concat(icon, " ").concat(className), "aria-hidden": "true", onClick: onClick }));
    }
    if (React__default.isValidElement(icon)) {
        // Because we are checking that typeof icon is string first, we can safely cast icon as React.ReactElement on types level and code level
        return React__default.cloneElement(icon, {
            className: "".concat(icon.props.className || "", " ").concat(defaultClass, " ").concat(className),
            onClick: function (event) {
                if (typeof icon.props.onClick === "function") {
                    icon.props.onClick(event);
                }
                if (typeof onClick === "function") {
                    onClick(event);
                }
            },
        });
    }
    // Default SVG Icon
    return (React__default.createElement("svg", { className: "".concat(defaultClass, " ").concat(className), xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 448 512", onClick: onClick },
        React__default.createElement("path", { d: "M96 32V64H48C21.5 64 0 85.5 0 112v48H448V112c0-26.5-21.5-48-48-48H352V32c0-17.7-14.3-32-32-32s-32 14.3-32 32V64H160V32c0-17.7-14.3-32-32-32S96 14.3 96 32zM448 192H0V464c0 26.5 21.5 48 48 48H400c26.5 0 48-21.5 48-48V192z" })));
};

/**
 * `Portal` is a React component that allows you to render children into a DOM node
 * that exists outside the DOM hierarchy of the parent component.
 *
 * @class
 * @param {PortalProps} props - The properties that define the `Portal` component.
 * @property {React.ReactNode} props.children - The children to be rendered into the `Portal`.
 * @property {string} props.portalId - The id of the DOM node into which the `Portal` will render.
 * @property {ShadowRoot} [props.portalHost] - The DOM node to host the `Portal`.
 */
var Portal$1 = /** @class */ (function (_super) {
    __extends(Portal, _super);
    function Portal(props) {
        var _this = _super.call(this, props) || this;
        _this.portalRoot = null;
        _this.el = document.createElement("div");
        return _this;
    }
    Portal.prototype.componentDidMount = function () {
        this.portalRoot = (this.props.portalHost || document).getElementById(this.props.portalId);
        if (!this.portalRoot) {
            this.portalRoot = document.createElement("div");
            this.portalRoot.setAttribute("id", this.props.portalId);
            (this.props.portalHost || document.body).appendChild(this.portalRoot);
        }
        this.portalRoot.appendChild(this.el);
    };
    Portal.prototype.componentWillUnmount = function () {
        if (this.portalRoot) {
            this.portalRoot.removeChild(this.el);
        }
    };
    Portal.prototype.render = function () {
        return ReactDOM__default.createPortal(this.props.children, this.el);
    };
    return Portal;
}(Component));

var focusableElementsSelector = "[tabindex], a, button, input, select, textarea";
var focusableFilter = function (node) {
    if (node instanceof HTMLAnchorElement) {
        return node.tabIndex !== -1;
    }
    return !node.disabled && node.tabIndex !== -1;
};
/**
 * `TabLoop` is a React component that manages tabbing behavior for its children.
 *
 * TabLoop prevents the user from tabbing outside of the popper
 * It creates a tabindex loop so that "Tab" on the last element will focus the first element
 * and "Shift Tab" on the first element will focus the last element
 *
 * @component
 * @example
 * <TabLoop enableTabLoop={true}>
 *   <ChildComponent />
 * </TabLoop>
 *
 * @param props - The properties that define the `TabLoop` component.
 * @param props.children - The child components.
 * @param props.enableTabLoop - Whether to enable the tab loop.
 *
 * @returns The `TabLoop` component.
 */
var TabLoop = /** @class */ (function (_super) {
    __extends(TabLoop, _super);
    function TabLoop(props) {
        var _this = _super.call(this, props) || this;
        /**
         * `getTabChildren` is a method of the `TabLoop` class that retrieves all tabbable children of the component.
         *
         * This method uses the `tabbable` library to find all tabbable elements within the `TabLoop` component.
         * It then filters out any elements that are not visible.
         *
         * @returns An array of all tabbable and visible children of the `TabLoop` component.
         */
        _this.getTabChildren = function () {
            var _a;
            return Array.prototype.slice
                .call((_a = _this.tabLoopRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(focusableElementsSelector), 1, -1)
                .filter(focusableFilter);
        };
        _this.handleFocusStart = function () {
            var tabChildren = _this.getTabChildren();
            tabChildren &&
                tabChildren.length > 1 &&
                tabChildren[tabChildren.length - 1].focus();
        };
        _this.handleFocusEnd = function () {
            var tabChildren = _this.getTabChildren();
            tabChildren && tabChildren.length > 1 && tabChildren[0].focus();
        };
        _this.tabLoopRef = createRef();
        return _this;
    }
    TabLoop.prototype.render = function () {
        var _a;
        if (!((_a = this.props.enableTabLoop) !== null && _a !== void 0 ? _a : TabLoop.defaultProps.enableTabLoop)) {
            return this.props.children;
        }
        return (React__default.createElement("div", { className: "react-datepicker__tab-loop", ref: this.tabLoopRef },
            React__default.createElement("div", { className: "react-datepicker__tab-loop__start", tabIndex: 0, onFocus: this.handleFocusStart }),
            this.props.children,
            React__default.createElement("div", { className: "react-datepicker__tab-loop__end", tabIndex: 0, onFocus: this.handleFocusEnd })));
    };
    TabLoop.defaultProps = {
        enableTabLoop: true,
    };
    return TabLoop;
}(Component));

/**
 * `withFloating` is a higher-order component that adds floating behavior to a component.
 *
 * @param Component - The component to enhance.
 *
 * @example
 * const FloatingComponent = withFloating(MyComponent);
 * <FloatingComponent popperModifiers={[]} popperProps={{}} hidePopper={true} />
 *
 * @param popperModifiers - The modifiers to use for the popper.
 * @param popperProps - The props to pass to the popper.
 * @param hidePopper - Whether to hide the popper.
 * @param popperPlacement - The placement of the popper.
 *
 * @returns A new component with floating behavior.
 */
function withFloating(Component) {
    var WithFloating = function (props) {
        var _a;
        var hidePopper = typeof props.hidePopper === "boolean" ? props.hidePopper : true;
        var arrowRef = useRef(null);
        var floatingProps = useFloating(_assign({ open: !hidePopper, whileElementsMounted: autoUpdate, placement: props.popperPlacement, middleware: __spreadArray([
                flip({ padding: 15 }),
                offset(10),
                arrow({ element: arrowRef })
            ], ((_a = props.popperModifiers) !== null && _a !== void 0 ? _a : []), true) }, props.popperProps));
        var componentProps = _assign(_assign({}, props), { hidePopper: hidePopper, popperProps: _assign(_assign({}, floatingProps), { arrowRef: arrowRef }) });
        return React__default.createElement(Component, _assign({}, componentProps));
    };
    return WithFloating;
}

// Exported for testing purposes
var PopperComponent = /** @class */ (function (_super) {
    __extends(PopperComponent, _super);
    function PopperComponent() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(PopperComponent, "defaultProps", {
        get: function () {
            return {
                hidePopper: true,
            };
        },
        enumerable: false,
        configurable: true
    });
    PopperComponent.prototype.render = function () {
        var _a = this.props, className = _a.className, wrapperClassName = _a.wrapperClassName, _b = _a.hidePopper, hidePopper = _b === void 0 ? PopperComponent.defaultProps.hidePopper : _b, popperComponent = _a.popperComponent, targetComponent = _a.targetComponent, enableTabLoop = _a.enableTabLoop, popperOnKeyDown = _a.popperOnKeyDown, portalId = _a.portalId, portalHost = _a.portalHost, popperProps = _a.popperProps, showArrow = _a.showArrow;
        var popper = undefined;
        if (!hidePopper) {
            var classes = clsx("react-datepicker-popper", className);
            popper = (React__default.createElement(TabLoop, { enableTabLoop: enableTabLoop },
                React__default.createElement("div", { ref: popperProps.refs.setFloating, style: popperProps.floatingStyles, className: classes, "data-placement": popperProps.placement, onKeyDown: popperOnKeyDown },
                    popperComponent,
                    showArrow && (React__default.createElement(FloatingArrow, { ref: popperProps.arrowRef, context: popperProps.context, fill: "currentColor", strokeWidth: 1, height: 8, width: 16, style: { transform: "translateY(-1px)" }, className: "react-datepicker__triangle" })))));
        }
        if (this.props.popperContainer) {
            popper = createElement(this.props.popperContainer, {}, popper);
        }
        if (portalId && !hidePopper) {
            popper = (React__default.createElement(Portal$1, { portalId: portalId, portalHost: portalHost }, popper));
        }
        var wrapperClasses = clsx("react-datepicker-wrapper", wrapperClassName);
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { ref: popperProps.refs.setReference, className: wrapperClasses }, targetComponent),
            popper));
    };
    return PopperComponent;
}(Component));
var PopperComponent$1 = withFloating(PopperComponent);

var outsideClickIgnoreClass = "react-datepicker-ignore-onclickoutside";
// Compares dates year+month combinations
function hasPreSelectionChanged(date1, date2) {
    if (date1 && date2) {
        return (getMonth(date1) !== getMonth(date2) || getYear$1(date1) !== getYear$1(date2));
    }
    return date1 !== date2;
}
/**
 * General datepicker component.
 */
var INPUT_ERR_1 = "Date input not valid.";
var DatePicker$2 = /** @class */ (function (_super) {
    __extends(DatePicker, _super);
    function DatePicker(props) {
        var _this = _super.call(this, props) || this;
        _this.calendar = null;
        _this.input = null;
        _this.getPreSelection = function () {
            return _this.props.openToDate
                ? _this.props.openToDate
                : _this.props.selectsEnd && _this.props.startDate
                    ? _this.props.startDate
                    : _this.props.selectsStart && _this.props.endDate
                        ? _this.props.endDate
                        : newDate();
        };
        // Convert the date from string format to standard Date format
        _this.modifyHolidays = function () {
            var _a;
            return (_a = _this.props.holidays) === null || _a === void 0 ? void 0 : _a.reduce(function (accumulator, holiday) {
                var date = new Date(holiday.date);
                if (!isValid$1(date)) {
                    return accumulator;
                }
                return __spreadArray(__spreadArray([], accumulator, true), [_assign(_assign({}, holiday), { date: date })], false);
            }, []);
        };
        _this.calcInitialState = function () {
            var _a;
            var defaultPreSelection = _this.getPreSelection();
            var minDate = getEffectiveMinDate(_this.props);
            var maxDate = getEffectiveMaxDate(_this.props);
            var boundedPreSelection = minDate && isBefore(defaultPreSelection, getStartOfDay(minDate))
                ? minDate
                : maxDate && isAfter(defaultPreSelection, getEndOfDay(maxDate))
                    ? maxDate
                    : defaultPreSelection;
            return {
                open: _this.props.startOpen || false,
                preventFocus: false,
                inputValue: null,
                preSelection: (_a = (_this.props.selectsRange
                    ? _this.props.startDate
                    : _this.props.selected)) !== null && _a !== void 0 ? _a : boundedPreSelection,
                // transforming highlighted days (perhaps nested array)
                // to flat Map for faster access in day.jsx
                highlightDates: getHighLightDaysMap(_this.props.highlightDates),
                focused: false,
                // used to focus day in inline version after month has changed, but not on
                // initial render
                shouldFocusDayInline: false,
                isRenderAriaLiveMessage: false,
                wasHidden: false,
            };
        };
        _this.resetHiddenStatus = function () {
            _this.setState(_assign(_assign({}, _this.state), { wasHidden: false }));
        };
        _this.setHiddenStatus = function () {
            _this.setState(_assign(_assign({}, _this.state), { wasHidden: true }));
        };
        _this.setHiddenStateOnVisibilityHidden = function () {
            if (document.visibilityState !== "hidden") {
                return;
            }
            _this.setHiddenStatus();
        };
        _this.clearPreventFocusTimeout = function () {
            if (_this.preventFocusTimeout) {
                clearTimeout(_this.preventFocusTimeout);
            }
        };
        _this.safeFocus = function () {
            setTimeout(function () {
                var _a, _b;
                (_b = (_a = _this.input) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a, { preventScroll: true });
            }, 0);
        };
        _this.safeBlur = function () {
            setTimeout(function () {
                var _a, _b;
                (_b = (_a = _this.input) === null || _a === void 0 ? void 0 : _a.blur) === null || _b === void 0 ? void 0 : _b.call(_a);
            }, 0);
        };
        _this.setFocus = function () {
            _this.safeFocus();
        };
        _this.setBlur = function () {
            _this.safeBlur();
            _this.cancelFocusInput();
        };
        _this.setOpen = function (open, skipSetBlur) {
            if (skipSetBlur === void 0) { skipSetBlur = false; }
            _this.setState({
                open: open,
                preSelection: open && _this.state.open
                    ? _this.state.preSelection
                    : _this.calcInitialState().preSelection,
                lastPreSelectChange: PRESELECT_CHANGE_VIA_NAVIGATE,
            }, function () {
                if (!open) {
                    _this.setState(function (prev) { return ({
                        focused: skipSetBlur ? prev.focused : false,
                    }); }, function () {
                        !skipSetBlur && _this.setBlur();
                        _this.setState({ inputValue: null });
                    });
                }
            });
        };
        _this.inputOk = function () { return isDate$1(_this.state.preSelection); };
        _this.isCalendarOpen = function () {
            return _this.props.open === undefined
                ? _this.state.open && !_this.props.disabled && !_this.props.readOnly
                : _this.props.open;
        };
        _this.handleFocus = function (event) {
            var _a, _b;
            var isAutoReFocus = _this.state.wasHidden;
            var isOpenAllowed = isAutoReFocus ? _this.state.open : true;
            if (isAutoReFocus) {
                _this.resetHiddenStatus();
            }
            if (!_this.state.preventFocus && isOpenAllowed) {
                (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                if (!_this.props.preventOpenOnFocus && !_this.props.readOnly) {
                    _this.setOpen(true);
                }
            }
            _this.setState({ focused: true });
        };
        _this.sendFocusBackToInput = function () {
            // Clear previous timeout if it exists
            if (_this.preventFocusTimeout) {
                _this.clearPreventFocusTimeout();
            }
            // close the popper and refocus the input
            // stop the input from auto opening onFocus
            // setFocus to the input
            _this.setState({ preventFocus: true }, function () {
                _this.preventFocusTimeout = setTimeout(function () {
                    _this.setFocus();
                    _this.setState({ preventFocus: false });
                });
            });
        };
        _this.cancelFocusInput = function () {
            clearTimeout(_this.inputFocusTimeout);
            _this.inputFocusTimeout = undefined;
        };
        _this.deferFocusInput = function () {
            _this.cancelFocusInput();
            _this.inputFocusTimeout = setTimeout(function () { return _this.setFocus(); }, 1);
        };
        _this.handleDropdownFocus = function () {
            _this.cancelFocusInput();
        };
        _this.handleBlur = function (event) {
            var _a, _b;
            if (!_this.state.open || _this.props.withPortal || _this.props.showTimeInput) {
                (_b = (_a = _this.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            }
            _this.setState({ focused: false });
        };
        _this.handleCalendarClickOutside = function (event) {
            var _a, _b;
            if (!_this.props.inline) {
                _this.setOpen(false);
            }
            (_b = (_a = _this.props).onClickOutside) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            if (_this.props.withPortal) {
                event.preventDefault();
            }
        };
        // handleChange is called when user types in the textbox
        _this.handleChange = function () {
            var _a, _b;
            var allArgs = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                allArgs[_i] = arguments[_i];
            }
            var event = allArgs[0];
            if (_this.props.onChangeRaw) {
                _this.props.onChangeRaw.apply(_this, allArgs);
                if (!event ||
                    typeof event.isDefaultPrevented !== "function" ||
                    event.isDefaultPrevented()) {
                    return;
                }
            }
            _this.setState({
                inputValue: (event === null || event === void 0 ? void 0 : event.target) instanceof HTMLInputElement ? event.target.value : null,
                lastPreSelectChange: PRESELECT_CHANGE_VIA_INPUT,
            });
            var _c = _this.props, _d = _c.dateFormat, dateFormat = _d === void 0 ? DatePicker.defaultProps.dateFormat : _d, _e = _c.strictParsing, strictParsing = _e === void 0 ? DatePicker.defaultProps.strictParsing : _e, selectsRange = _c.selectsRange, startDate = _c.startDate, endDate = _c.endDate;
            var value = (event === null || event === void 0 ? void 0 : event.target) instanceof HTMLInputElement ? event.target.value : "";
            if (selectsRange) {
                var _f = value
                    .split("-", 2)
                    .map(function (val) { return val.trim(); }), valueStart = _f[0], valueEnd = _f[1];
                var startDateNew = parseDate(valueStart !== null && valueStart !== void 0 ? valueStart : "", dateFormat, _this.props.locale, strictParsing);
                var endDateNew = parseDate(valueEnd !== null && valueEnd !== void 0 ? valueEnd : "", dateFormat, _this.props.locale, strictParsing);
                var startChanged = (startDate === null || startDate === void 0 ? void 0 : startDate.getTime()) !== (startDateNew === null || startDateNew === void 0 ? void 0 : startDateNew.getTime());
                var endChanged = (endDate === null || endDate === void 0 ? void 0 : endDate.getTime()) !== (endDateNew === null || endDateNew === void 0 ? void 0 : endDateNew.getTime());
                if (!startChanged && !endChanged) {
                    return;
                }
                if (startDateNew && isDayDisabled(startDateNew, _this.props)) {
                    return;
                }
                if (endDateNew && isDayDisabled(endDateNew, _this.props)) {
                    return;
                }
                (_b = (_a = _this.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, [startDateNew, endDateNew], event);
            }
            else {
                // not selectsRange
                var date = parseDate(value, dateFormat, _this.props.locale, strictParsing, _this.props.minDate);
                // Use date from `selected` prop when manipulating only time for input value
                if (_this.props.showTimeSelectOnly &&
                    _this.props.selected &&
                    date &&
                    !isSameDay(date, _this.props.selected)) {
                    date = set(_this.props.selected, {
                        hours: getHours(date),
                        minutes: getMinutes(date),
                        seconds: getSeconds(date),
                    });
                }
                // Update selection if either (1) date was successfully parsed, or (2) input field is empty
                if (date || !value) {
                    _this.setSelected(date, event, true);
                }
            }
        };
        _this.handleSelect = function (date, event, monthSelectedIn) {
            if (_this.props.shouldCloseOnSelect && !_this.props.showTimeSelect) {
                // Preventing onFocus event to fix issue
                // https://github.com/Hacker0x01/react-datepicker/issues/628
                _this.sendFocusBackToInput();
            }
            if (_this.props.onChangeRaw) {
                _this.props.onChangeRaw(event);
            }
            _this.setSelected(date, event, false, monthSelectedIn);
            if (_this.props.showDateSelect) {
                _this.setState({ isRenderAriaLiveMessage: true });
            }
            if (!_this.props.shouldCloseOnSelect || _this.props.showTimeSelect) {
                _this.setPreSelection(date);
            }
            else if (!_this.props.inline) {
                if (!_this.props.selectsRange) {
                    _this.setOpen(false);
                }
                var _a = _this.props, startDate = _a.startDate, endDate = _a.endDate;
                if (startDate &&
                    !endDate &&
                    (_this.props.swapRange || !isDateBefore(date, startDate))) {
                    _this.setOpen(false);
                }
            }
        };
        // setSelected is called either from handleChange (user typed date into textbox and it was parsed) or handleSelect (user selected date from calendar using mouse or keyboard)
        _this.setSelected = function (date, event, keepInput, monthSelectedIn) {
            var _a, _b;
            var changedDate = date;
            // Early return if selected year/month/day is disabled
            if (_this.props.showYearPicker) {
                if (changedDate !== null &&
                    isYearDisabled(getYear$1(changedDate), _this.props)) {
                    return;
                }
            }
            else if (_this.props.showMonthYearPicker) {
                if (changedDate !== null && isMonthDisabled(changedDate, _this.props)) {
                    return;
                }
            }
            else {
                if (changedDate !== null && isDayDisabled(changedDate, _this.props)) {
                    return;
                }
            }
            var _c = _this.props, onChange = _c.onChange, selectsRange = _c.selectsRange, startDate = _c.startDate, endDate = _c.endDate, selectsMultiple = _c.selectsMultiple, selectedDates = _c.selectedDates, minTime = _c.minTime, swapRange = _c.swapRange;
            if (!isEqual(_this.props.selected, changedDate) ||
                _this.props.allowSameDay ||
                selectsRange ||
                selectsMultiple) {
                if (changedDate !== null) {
                    // Preserve previously selected time if only date is currently being changed
                    if (_this.props.selected &&
                        (!keepInput ||
                            (!_this.props.showTimeSelect &&
                                !_this.props.showTimeSelectOnly &&
                                !_this.props.showTimeInput))) {
                        changedDate = setTime(changedDate, {
                            hour: getHours(_this.props.selected),
                            minute: getMinutes(_this.props.selected),
                            second: getSeconds(_this.props.selected),
                        });
                    }
                    // If minTime is present then set the time to minTime
                    if (!keepInput &&
                        (_this.props.showTimeSelect || _this.props.showTimeSelectOnly)) {
                        if (minTime) {
                            changedDate = setTime(changedDate, {
                                hour: minTime.getHours(),
                                minute: minTime.getMinutes(),
                                second: minTime.getSeconds(),
                            });
                        }
                    }
                    if (!_this.props.inline) {
                        _this.setState({
                            preSelection: changedDate,
                        });
                    }
                    if (!_this.props.focusSelectedMonth) {
                        _this.setState({ monthSelectedIn: monthSelectedIn });
                    }
                }
                if (selectsRange) {
                    var noRanges = !startDate && !endDate;
                    var hasStartRange = startDate && !endDate;
                    var isRangeFilled = startDate && endDate;
                    if (noRanges) {
                        onChange === null || onChange === void 0 ? void 0 : onChange([changedDate, null], event);
                    }
                    else if (hasStartRange) {
                        if (changedDate === null) {
                            onChange === null || onChange === void 0 ? void 0 : onChange([null, null], event);
                        }
                        else if (isDateBefore(changedDate, startDate)) {
                            if (swapRange) {
                                onChange === null || onChange === void 0 ? void 0 : onChange([changedDate, startDate], event);
                            }
                            else {
                                onChange === null || onChange === void 0 ? void 0 : onChange([changedDate, null], event);
                            }
                        }
                        else {
                            onChange === null || onChange === void 0 ? void 0 : onChange([startDate, changedDate], event);
                        }
                    }
                    if (isRangeFilled) {
                        onChange === null || onChange === void 0 ? void 0 : onChange([changedDate, null], event);
                    }
                }
                else if (selectsMultiple) {
                    if (changedDate !== null) {
                        if (!(selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.length)) {
                            onChange === null || onChange === void 0 ? void 0 : onChange([changedDate], event);
                        }
                        else {
                            var isChangedDateAlreadySelected = selectedDates.some(function (selectedDate) { return isSameDay(selectedDate, changedDate); });
                            if (isChangedDateAlreadySelected) {
                                var nextDates = selectedDates.filter(function (selectedDate) { return !isSameDay(selectedDate, changedDate); });
                                onChange === null || onChange === void 0 ? void 0 : onChange(nextDates, event);
                            }
                            else {
                                onChange === null || onChange === void 0 ? void 0 : onChange(__spreadArray(__spreadArray([], selectedDates, true), [changedDate], false), event);
                            }
                        }
                    }
                }
                else {
                    onChange === null || onChange === void 0 ? void 0 : onChange(changedDate, event);
                }
            }
            if (!keepInput) {
                (_b = (_a = _this.props).onSelect) === null || _b === void 0 ? void 0 : _b.call(_a, changedDate, event);
                _this.setState({ inputValue: null });
            }
        };
        // When checking preSelection via min/maxDate, times need to be manipulated via getStartOfDay/getEndOfDay
        _this.setPreSelection = function (date) {
            var hasMinDate = isDate$1(_this.props.minDate);
            var hasMaxDate = isDate$1(_this.props.maxDate);
            var isValidDateSelection = true;
            if (date) {
                var dateStartOfDay = getStartOfDay(date);
                if (hasMinDate && hasMaxDate) {
                    // isDayInRange uses getStartOfDay internally, so not necessary to manipulate times here
                    isValidDateSelection = isDayInRange(date, _this.props.minDate, _this.props.maxDate);
                }
                else if (hasMinDate) {
                    var minDateStartOfDay = getStartOfDay(_this.props.minDate);
                    isValidDateSelection =
                        isAfter(date, minDateStartOfDay) ||
                            isEqual(dateStartOfDay, minDateStartOfDay);
                }
                else if (hasMaxDate) {
                    var maxDateEndOfDay = getEndOfDay(_this.props.maxDate);
                    isValidDateSelection =
                        isBefore(date, maxDateEndOfDay) ||
                            isEqual(dateStartOfDay, maxDateEndOfDay);
                }
            }
            if (isValidDateSelection) {
                _this.setState({
                    preSelection: date,
                });
            }
        };
        _this.toggleCalendar = function () {
            _this.setOpen(!_this.state.open);
        };
        _this.handleTimeChange = function (time) {
            var _a, _b;
            if (_this.props.selectsRange || _this.props.selectsMultiple) {
                return;
            }
            var selected = _this.props.selected
                ? _this.props.selected
                : _this.getPreSelection();
            var changedDate = _this.props.selected
                ? time
                : setTime(selected, {
                    hour: getHours(time),
                    minute: getMinutes(time),
                });
            _this.setState({
                preSelection: changedDate,
            });
            (_b = (_a = _this.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, changedDate);
            if (_this.props.shouldCloseOnSelect && !_this.props.showTimeInput) {
                _this.sendFocusBackToInput();
                _this.setOpen(false);
            }
            if (_this.props.showTimeInput) {
                _this.setOpen(true);
            }
            if (_this.props.showTimeSelectOnly || _this.props.showTimeSelect) {
                _this.setState({ isRenderAriaLiveMessage: true });
            }
            _this.setState({ inputValue: null });
        };
        _this.onInputClick = function () {
            var _a, _b;
            if (!_this.props.disabled && !_this.props.readOnly) {
                _this.setOpen(true);
            }
            (_b = (_a = _this.props).onInputClick) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        _this.onInputKeyDown = function (event) {
            var _a, _b, _c, _d, _e, _f;
            (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            var eventKey = event.key;
            if (!_this.state.open &&
                !_this.props.inline &&
                !_this.props.preventOpenOnFocus) {
                if (eventKey === KeyType.ArrowDown ||
                    eventKey === KeyType.ArrowUp ||
                    eventKey === KeyType.Enter) {
                    (_c = _this.onInputClick) === null || _c === void 0 ? void 0 : _c.call(_this);
                }
                return;
            }
            // if calendar is open, these keys will focus the selected item
            if (_this.state.open) {
                if (eventKey === KeyType.ArrowDown || eventKey === KeyType.ArrowUp) {
                    event.preventDefault();
                    var selectorString = _this.props.showTimeSelectOnly
                        ? ".react-datepicker__time-list-item[tabindex='0']"
                        : _this.props.showWeekPicker && _this.props.showWeekNumbers
                            ? '.react-datepicker__week-number[tabindex="0"]'
                            : _this.props.showFullMonthYearPicker ||
                                _this.props.showMonthYearPicker
                                ? '.react-datepicker__month-text[tabindex="0"]'
                                : '.react-datepicker__day[tabindex="0"]';
                    var selectedItem = ((_d = _this.calendar) === null || _d === void 0 ? void 0 : _d.containerRef.current) instanceof Element &&
                        _this.calendar.containerRef.current.querySelector(selectorString);
                    selectedItem instanceof HTMLElement &&
                        selectedItem.focus({ preventScroll: true });
                    return;
                }
                var copy = newDate(_this.state.preSelection);
                if (eventKey === KeyType.Enter) {
                    event.preventDefault();
                    event.target.blur();
                    if (_this.inputOk() &&
                        _this.state.lastPreSelectChange === PRESELECT_CHANGE_VIA_NAVIGATE) {
                        _this.handleSelect(copy, event);
                        !_this.props.shouldCloseOnSelect && _this.setPreSelection(copy);
                    }
                    else {
                        _this.setOpen(false);
                    }
                }
                else if (eventKey === KeyType.Escape) {
                    event.preventDefault();
                    event.target.blur();
                    _this.sendFocusBackToInput();
                    _this.setOpen(false);
                }
                else if (eventKey === KeyType.Tab) {
                    _this.setOpen(false);
                }
                if (!_this.inputOk()) {
                    (_f = (_e = _this.props).onInputError) === null || _f === void 0 ? void 0 : _f.call(_e, { code: 1, msg: INPUT_ERR_1 });
                }
            }
        };
        _this.onPortalKeyDown = function (event) {
            var eventKey = event.key;
            if (eventKey === KeyType.Escape) {
                event.preventDefault();
                _this.setState({
                    preventFocus: true,
                }, function () {
                    _this.setOpen(false);
                    setTimeout(function () {
                        _this.setFocus();
                        _this.setState({ preventFocus: false });
                    });
                });
            }
        };
        // keyDown events passed down to day.jsx
        _this.onDayKeyDown = function (event) {
            var _a, _b, _c, _d, _e, _f;
            var _g = _this.props, minDate = _g.minDate, maxDate = _g.maxDate, disabledKeyboardNavigation = _g.disabledKeyboardNavigation, showWeekPicker = _g.showWeekPicker, shouldCloseOnSelect = _g.shouldCloseOnSelect, locale = _g.locale, calendarStartDay = _g.calendarStartDay, adjustDateOnChange = _g.adjustDateOnChange, inline = _g.inline;
            (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            if (disabledKeyboardNavigation)
                return;
            var eventKey = event.key;
            var isShiftKeyActive = event.shiftKey;
            var copy = newDate(_this.state.preSelection);
            var calculateNewDate = function (eventKey, date) {
                var newCalculatedDate = date;
                switch (eventKey) {
                    case KeyType.ArrowRight:
                        newCalculatedDate = showWeekPicker
                            ? addWeeks(date, 1)
                            : addDays(date, 1);
                        break;
                    case KeyType.ArrowLeft:
                        newCalculatedDate = showWeekPicker
                            ? subWeeks(date)
                            : subDays(date);
                        break;
                    case KeyType.ArrowUp:
                        newCalculatedDate = subWeeks(date);
                        break;
                    case KeyType.ArrowDown:
                        newCalculatedDate = addWeeks(date, 1);
                        break;
                    case KeyType.PageUp:
                        newCalculatedDate = isShiftKeyActive
                            ? subYears(date, 1)
                            : subMonths(date, 1);
                        break;
                    case KeyType.PageDown:
                        newCalculatedDate = isShiftKeyActive
                            ? addYears(date, 1)
                            : addMonths(date, 1);
                        break;
                    case KeyType.Home:
                        newCalculatedDate = getStartOfWeek(date, locale, calendarStartDay);
                        break;
                    case KeyType.End:
                        newCalculatedDate = getEndOfWeek(date);
                        break;
                }
                return newCalculatedDate;
            };
            var getNewDate = function (eventKey, date) {
                var MAX_ITERATIONS = 40;
                var eventKeyCopy = eventKey;
                var validDateFound = false;
                var iterations = 0;
                var newSelection = calculateNewDate(eventKey, date);
                while (!validDateFound) {
                    if (iterations >= MAX_ITERATIONS) {
                        newSelection = date;
                        break;
                    }
                    // if minDate exists and the new selection is before the min date, get the nearest date that isn't disabled
                    if (minDate && newSelection < minDate) {
                        eventKeyCopy = KeyType.ArrowRight;
                        newSelection = isDayDisabled(minDate, _this.props)
                            ? calculateNewDate(eventKeyCopy, newSelection)
                            : minDate;
                    }
                    // if maxDate exists and the new selection is after the max date, get the nearest date that isn't disabled
                    if (maxDate && newSelection > maxDate) {
                        eventKeyCopy = KeyType.ArrowLeft;
                        newSelection = isDayDisabled(maxDate, _this.props)
                            ? calculateNewDate(eventKeyCopy, newSelection)
                            : maxDate;
                    }
                    if (isDayDisabled(newSelection, _this.props)) {
                        // if PageUp and Home is pressed to a disabled date, it will try to find the next available date after
                        if (eventKeyCopy === KeyType.PageUp ||
                            eventKeyCopy === KeyType.Home) {
                            eventKeyCopy = KeyType.ArrowRight;
                        }
                        // if PageDown and End is pressed to a disabled date, it will try to find the next available date before
                        if (eventKeyCopy === KeyType.PageDown ||
                            eventKeyCopy === KeyType.End) {
                            eventKeyCopy = KeyType.ArrowLeft;
                        }
                        newSelection = calculateNewDate(eventKeyCopy, newSelection);
                    }
                    else {
                        validDateFound = true;
                    }
                    iterations++;
                }
                return newSelection;
            };
            if (eventKey === KeyType.Enter) {
                event.preventDefault();
                _this.handleSelect(copy, event);
                !shouldCloseOnSelect && _this.setPreSelection(copy);
                return;
            }
            else if (eventKey === KeyType.Escape) {
                event.preventDefault();
                _this.setOpen(false);
                if (!_this.inputOk()) {
                    (_d = (_c = _this.props).onInputError) === null || _d === void 0 ? void 0 : _d.call(_c, { code: 1, msg: INPUT_ERR_1 });
                }
                return;
            }
            var newSelection = null;
            switch (eventKey) {
                case KeyType.ArrowLeft:
                case KeyType.ArrowRight:
                case KeyType.ArrowUp:
                case KeyType.ArrowDown:
                case KeyType.PageUp:
                case KeyType.PageDown:
                case KeyType.Home:
                case KeyType.End:
                    newSelection = getNewDate(eventKey, copy);
                    break;
            }
            if (!newSelection) {
                (_f = (_e = _this.props).onInputError) === null || _f === void 0 ? void 0 : _f.call(_e, { code: 1, msg: INPUT_ERR_1 });
                return;
            }
            event.preventDefault();
            _this.setState({ lastPreSelectChange: PRESELECT_CHANGE_VIA_NAVIGATE });
            if (adjustDateOnChange) {
                _this.setSelected(newSelection);
            }
            _this.setPreSelection(newSelection);
            // need to figure out whether month has changed to focus day in inline version
            if (inline) {
                var prevMonth = getMonth(copy);
                var newMonth = getMonth(newSelection);
                var prevYear = getYear$1(copy);
                var newYear = getYear$1(newSelection);
                if (prevMonth !== newMonth || prevYear !== newYear) {
                    // month has changed
                    _this.setState({ shouldFocusDayInline: true });
                }
                else {
                    // month hasn't changed
                    _this.setState({ shouldFocusDayInline: false });
                }
            }
        };
        // handle generic key down events in the popper that do not adjust or select dates
        // ex: while focusing prev and next month buttons
        _this.onPopperKeyDown = function (event) {
            var eventKey = event.key;
            if (eventKey === KeyType.Escape) {
                event.preventDefault();
                _this.sendFocusBackToInput();
            }
        };
        _this.onClearClick = function (event) {
            if (event) {
                if (event.preventDefault) {
                    event.preventDefault();
                }
            }
            _this.sendFocusBackToInput();
            var _a = _this.props, selectsRange = _a.selectsRange, onChange = _a.onChange;
            if (selectsRange) {
                onChange === null || onChange === void 0 ? void 0 : onChange([null, null], event);
            }
            else {
                onChange === null || onChange === void 0 ? void 0 : onChange(null, event);
            }
            _this.setState({ inputValue: null });
        };
        _this.clear = function () {
            _this.onClearClick();
        };
        _this.onScroll = function (event) {
            if (typeof _this.props.closeOnScroll === "boolean" &&
                _this.props.closeOnScroll) {
                if (event.target === document ||
                    event.target === document.documentElement ||
                    event.target === document.body) {
                    _this.setOpen(false);
                }
            }
            else if (typeof _this.props.closeOnScroll === "function") {
                if (_this.props.closeOnScroll(event)) {
                    _this.setOpen(false);
                }
            }
        };
        _this.renderCalendar = function () {
            var _a, _b;
            if (!_this.props.inline && !_this.isCalendarOpen()) {
                return null;
            }
            return (React__default.createElement(Calendar, _assign({ showMonthYearDropdown: undefined, ref: function (elem) {
                    _this.calendar = elem;
                } }, _this.props, _this.state, { setOpen: _this.setOpen, dateFormat: (_a = _this.props.dateFormatCalendar) !== null && _a !== void 0 ? _a : DatePicker.defaultProps.dateFormatCalendar, onSelect: _this.handleSelect, onClickOutside: _this.handleCalendarClickOutside, holidays: getHolidaysMap(_this.modifyHolidays()), outsideClickIgnoreClass: outsideClickIgnoreClass, onDropdownFocus: _this.handleDropdownFocus, onTimeChange: _this.handleTimeChange, className: _this.props.calendarClassName, container: _this.props.calendarContainer, handleOnKeyDown: _this.props.onKeyDown, handleOnDayKeyDown: _this.onDayKeyDown, setPreSelection: _this.setPreSelection, dropdownMode: (_b = _this.props.dropdownMode) !== null && _b !== void 0 ? _b : DatePicker.defaultProps.dropdownMode }), _this.props.children));
        };
        _this.renderAriaLiveRegion = function () {
            var _a = _this.props, _b = _a.dateFormat, dateFormat = _b === void 0 ? DatePicker.defaultProps.dateFormat : _b, locale = _a.locale;
            var isContainsTime = _this.props.showTimeInput || _this.props.showTimeSelect;
            var longDateFormat = isContainsTime ? "PPPPp" : "PPPP";
            var ariaLiveMessage;
            if (_this.props.selectsRange) {
                ariaLiveMessage = "Selected start date: ".concat(safeDateFormat(_this.props.startDate, {
                    dateFormat: longDateFormat,
                    locale: locale,
                }), ". ").concat(_this.props.endDate
                    ? "End date: " +
                        safeDateFormat(_this.props.endDate, {
                            dateFormat: longDateFormat,
                            locale: locale,
                        })
                    : "");
            }
            else {
                if (_this.props.showTimeSelectOnly) {
                    ariaLiveMessage = "Selected time: ".concat(safeDateFormat(_this.props.selected, { dateFormat: dateFormat, locale: locale }));
                }
                else if (_this.props.showYearPicker) {
                    ariaLiveMessage = "Selected year: ".concat(safeDateFormat(_this.props.selected, { dateFormat: "yyyy", locale: locale }));
                }
                else if (_this.props.showMonthYearPicker) {
                    ariaLiveMessage = "Selected month: ".concat(safeDateFormat(_this.props.selected, { dateFormat: "MMMM yyyy", locale: locale }));
                }
                else if (_this.props.showQuarterYearPicker) {
                    ariaLiveMessage = "Selected quarter: ".concat(safeDateFormat(_this.props.selected, {
                        dateFormat: "yyyy, QQQ",
                        locale: locale,
                    }));
                }
                else {
                    ariaLiveMessage = "Selected date: ".concat(safeDateFormat(_this.props.selected, {
                        dateFormat: longDateFormat,
                        locale: locale,
                    }));
                }
            }
            return (React__default.createElement("span", { role: "alert", "aria-live": "polite", className: "react-datepicker__aria-live" }, ariaLiveMessage));
        };
        _this.renderDateInput = function () {
            var _a, _b;
            var _c;
            var className = clsx(_this.props.className, (_a = {},
                _a[outsideClickIgnoreClass] = _this.state.open,
                _a));
            var customInput = _this.props.customInput || React__default.createElement("input", { type: "text" });
            var customInputRef = _this.props.customInputRef || "ref";
            var _d = _this.props, _e = _d.dateFormat, dateFormat = _e === void 0 ? DatePicker.defaultProps.dateFormat : _e, locale = _d.locale;
            var inputValue = typeof _this.props.value === "string"
                ? _this.props.value
                : typeof _this.state.inputValue === "string"
                    ? _this.state.inputValue
                    : _this.props.selectsRange
                        ? safeDateRangeFormat(_this.props.startDate, _this.props.endDate, {
                            dateFormat: dateFormat,
                            locale: locale,
                        })
                        : _this.props.selectsMultiple
                            ? safeMultipleDatesFormat((_c = _this.props.selectedDates) !== null && _c !== void 0 ? _c : [], {
                                dateFormat: dateFormat,
                                locale: locale,
                            })
                            : safeDateFormat(_this.props.selected, {
                                dateFormat: dateFormat,
                                locale: locale,
                            });
            return cloneElement(customInput, (_b = {},
                _b[customInputRef] = function (input) {
                    _this.input = input;
                },
                _b.value = inputValue,
                _b.onBlur = _this.handleBlur,
                _b.onChange = _this.handleChange,
                _b.onClick = _this.onInputClick,
                _b.onFocus = _this.handleFocus,
                _b.onKeyDown = _this.onInputKeyDown,
                _b.id = _this.props.id,
                _b.name = _this.props.name,
                _b.form = _this.props.form,
                _b.autoFocus = _this.props.autoFocus,
                _b.placeholder = _this.props.placeholderText,
                _b.disabled = _this.props.disabled,
                _b.autoComplete = _this.props.autoComplete,
                _b.className = clsx(customInput.props.className, className),
                _b.title = _this.props.title,
                _b.readOnly = _this.props.readOnly,
                _b.required = _this.props.required,
                _b.tabIndex = _this.props.tabIndex,
                _b["aria-describedby"] = _this.props.ariaDescribedBy,
                _b["aria-invalid"] = _this.props.ariaInvalid,
                _b["aria-labelledby"] = _this.props.ariaLabelledBy,
                _b["aria-required"] = _this.props.ariaRequired,
                _b));
        };
        _this.renderClearButton = function () {
            var _a = _this.props, isClearable = _a.isClearable, disabled = _a.disabled, selected = _a.selected, startDate = _a.startDate, endDate = _a.endDate, clearButtonTitle = _a.clearButtonTitle, _b = _a.clearButtonClassName, clearButtonClassName = _b === void 0 ? "" : _b, _c = _a.ariaLabelClose, ariaLabelClose = _c === void 0 ? "Close" : _c, selectedDates = _a.selectedDates;
            if (isClearable &&
                (selected != null ||
                    startDate != null ||
                    endDate != null ||
                    (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.length))) {
                return (React__default.createElement("button", { type: "button", className: clsx("react-datepicker__close-icon", clearButtonClassName, { "react-datepicker__close-icon--disabled": disabled }), disabled: disabled, "aria-label": ariaLabelClose, onClick: _this.onClearClick, title: clearButtonTitle, tabIndex: -1 }));
            }
            else {
                return null;
            }
        };
        _this.state = _this.calcInitialState();
        _this.preventFocusTimeout = undefined;
        return _this;
    }
    Object.defineProperty(DatePicker, "defaultProps", {
        get: function () {
            return {
                allowSameDay: false,
                dateFormat: "MM/dd/yyyy",
                dateFormatCalendar: "LLLL yyyy",
                disabled: false,
                disabledKeyboardNavigation: false,
                dropdownMode: "scroll",
                preventOpenOnFocus: false,
                monthsShown: 1,
                readOnly: false,
                withPortal: false,
                selectsDisabledDaysInRange: false,
                shouldCloseOnSelect: true,
                showTimeSelect: false,
                showTimeInput: false,
                showPreviousMonths: false,
                showMonthYearPicker: false,
                showFullMonthYearPicker: false,
                showTwoColumnMonthYearPicker: false,
                showFourColumnMonthYearPicker: false,
                showYearPicker: false,
                showQuarterYearPicker: false,
                showWeekPicker: false,
                strictParsing: false,
                swapRange: false,
                timeIntervals: 30,
                timeCaption: "Time",
                previousMonthAriaLabel: "Previous Month",
                previousMonthButtonLabel: "Previous Month",
                nextMonthAriaLabel: "Next Month",
                nextMonthButtonLabel: "Next Month",
                previousYearAriaLabel: "Previous Year",
                previousYearButtonLabel: "Previous Year",
                nextYearAriaLabel: "Next Year",
                nextYearButtonLabel: "Next Year",
                timeInputLabel: "Time",
                enableTabLoop: true,
                yearItemNumber: DEFAULT_YEAR_ITEM_NUMBER,
                focusSelectedMonth: false,
                showPopperArrow: true,
                excludeScrollbar: true,
                customTimeInput: null,
                calendarStartDay: undefined,
                toggleCalendarOnIconClick: false,
                usePointerEvent: false,
            };
        },
        enumerable: false,
        configurable: true
    });
    DatePicker.prototype.componentDidMount = function () {
        window.addEventListener("scroll", this.onScroll, true);
        document.addEventListener("visibilitychange", this.setHiddenStateOnVisibilityHidden);
    };
    DatePicker.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a, _b, _c, _d;
        if (prevProps.inline &&
            hasPreSelectionChanged(prevProps.selected, this.props.selected)) {
            this.setPreSelection(this.props.selected);
        }
        if (this.state.monthSelectedIn !== undefined &&
            prevProps.monthsShown !== this.props.monthsShown) {
            this.setState({ monthSelectedIn: 0 });
        }
        if (prevProps.highlightDates !== this.props.highlightDates) {
            this.setState({
                highlightDates: getHighLightDaysMap(this.props.highlightDates),
            });
        }
        if (!prevState.focused &&
            !isEqual(prevProps.selected, this.props.selected)) {
            this.setState({ inputValue: null });
        }
        if (prevState.open !== this.state.open) {
            if (prevState.open === false && this.state.open === true) {
                (_b = (_a = this.props).onCalendarOpen) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            if (prevState.open === true && this.state.open === false) {
                (_d = (_c = this.props).onCalendarClose) === null || _d === void 0 ? void 0 : _d.call(_c);
            }
        }
    };
    DatePicker.prototype.componentWillUnmount = function () {
        this.clearPreventFocusTimeout();
        window.removeEventListener("scroll", this.onScroll, true);
        document.removeEventListener("visibilitychange", this.setHiddenStateOnVisibilityHidden);
    };
    DatePicker.prototype.renderInputContainer = function () {
        var _a = this.props, showIcon = _a.showIcon, icon = _a.icon, calendarIconClassname = _a.calendarIconClassname, calendarIconClassName = _a.calendarIconClassName, toggleCalendarOnIconClick = _a.toggleCalendarOnIconClick;
        var open = this.state.open;
        if (calendarIconClassname) {
            console.warn("calendarIconClassname props is deprecated. should use calendarIconClassName props.");
        }
        return (React__default.createElement("div", { className: "react-datepicker__input-container".concat(showIcon ? " react-datepicker__view-calendar-icon" : "") },
            showIcon && (React__default.createElement(CalendarIcon, _assign({ icon: icon, className: clsx(calendarIconClassName, !calendarIconClassName && calendarIconClassname, open && "react-datepicker-ignore-onclickoutside") }, (toggleCalendarOnIconClick
                ? {
                    onClick: this.toggleCalendar,
                }
                : null)))),
            this.state.isRenderAriaLiveMessage && this.renderAriaLiveRegion(),
            this.renderDateInput(),
            this.renderClearButton()));
    };
    DatePicker.prototype.render = function () {
        var calendar = this.renderCalendar();
        if (this.props.inline)
            return calendar;
        if (this.props.withPortal) {
            var portalContainer = this.state.open ? (React__default.createElement(TabLoop, { enableTabLoop: this.props.enableTabLoop },
                React__default.createElement("div", { className: "react-datepicker__portal", tabIndex: -1, onKeyDown: this.onPortalKeyDown }, calendar))) : null;
            if (this.state.open && this.props.portalId) {
                portalContainer = (React__default.createElement(Portal$1, _assign({ portalId: this.props.portalId }, this.props), portalContainer));
            }
            return (React__default.createElement("div", null,
                this.renderInputContainer(),
                portalContainer));
        }
        return (React__default.createElement(PopperComponent$1, _assign({}, this.props, { className: this.props.popperClassName, hidePopper: !this.isCalendarOpen(), targetComponent: this.renderInputContainer(), popperComponent: calendar, popperOnKeyDown: this.onPopperKeyDown, showArrow: this.props.showPopperArrow })));
    };
    return DatePicker;
}(Component));
var PRESELECT_CHANGE_VIA_INPUT = "input";
var PRESELECT_CHANGE_VIA_NAVIGATE = "navigate";

var css_248z$J = "@charset \"UTF-8\";\n.react-datepicker__navigation-icon::before, .react-datepicker__year-read-view--down-arrow,\n.react-datepicker__month-read-view--down-arrow,\n.react-datepicker__month-year-read-view--down-arrow {\n  border-color: #ccc;\n  border-style: solid;\n  border-width: 3px 3px 0 0;\n  content: \"\";\n  display: block;\n  height: 9px;\n  position: absolute;\n  top: 6px;\n  width: 9px;\n}\n.react-datepicker-wrapper {\n  display: inline-block;\n  padding: 0;\n  border: 0;\n}\n\n.react-datepicker {\n  font-family: \"Helvetica Neue\", helvetica, arial, sans-serif;\n  font-size: 0.8rem;\n  background-color: #fff;\n  color: #000;\n  border: 1px solid #aeaeae;\n  border-radius: 0.3rem;\n  display: inline-block;\n  position: relative;\n  line-height: initial;\n}\n\n.react-datepicker--time-only .react-datepicker__time-container {\n  border-left: 0;\n}\n.react-datepicker--time-only .react-datepicker__time,\n.react-datepicker--time-only .react-datepicker__time-box {\n  border-bottom-left-radius: 0.3rem;\n  border-bottom-right-radius: 0.3rem;\n}\n\n.react-datepicker-popper {\n  z-index: 1;\n  line-height: 0;\n}\n.react-datepicker-popper .react-datepicker__triangle {\n  stroke: #aeaeae;\n}\n.react-datepicker-popper[data-placement^=bottom] .react-datepicker__triangle {\n  fill: #f0f0f0;\n  color: #f0f0f0;\n}\n.react-datepicker-popper[data-placement^=top] .react-datepicker__triangle {\n  fill: #fff;\n  color: #fff;\n}\n\n.react-datepicker__header {\n  text-align: center;\n  background-color: #f0f0f0;\n  border-bottom: 1px solid #aeaeae;\n  border-top-left-radius: 0.3rem;\n  padding: 8px 0;\n  position: relative;\n}\n.react-datepicker__header--time {\n  padding-bottom: 8px;\n  padding-left: 5px;\n  padding-right: 5px;\n}\n.react-datepicker__header--time:not(.react-datepicker__header--time--only) {\n  border-top-left-radius: 0;\n}\n.react-datepicker__header:not(.react-datepicker__header--has-time-select) {\n  border-top-right-radius: 0.3rem;\n}\n\n.react-datepicker__year-dropdown-container--select,\n.react-datepicker__month-dropdown-container--select,\n.react-datepicker__month-year-dropdown-container--select,\n.react-datepicker__year-dropdown-container--scroll,\n.react-datepicker__month-dropdown-container--scroll,\n.react-datepicker__month-year-dropdown-container--scroll {\n  display: inline-block;\n  margin: 0 15px;\n}\n\n.react-datepicker__current-month,\n.react-datepicker-time__header,\n.react-datepicker-year-header {\n  margin-top: 0;\n  color: #000;\n  font-weight: bold;\n  font-size: 0.944rem;\n}\n\nh2.react-datepicker__current-month {\n  padding: 0;\n  margin: 0;\n}\n\n.react-datepicker-time__header {\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\n.react-datepicker__navigation {\n  align-items: center;\n  background: none;\n  display: flex;\n  justify-content: center;\n  text-align: center;\n  cursor: pointer;\n  position: absolute;\n  top: 2px;\n  padding: 0;\n  border: none;\n  z-index: 1;\n  height: 32px;\n  width: 32px;\n  text-indent: -999em;\n  overflow: hidden;\n}\n.react-datepicker__navigation--previous {\n  left: 2px;\n}\n.react-datepicker__navigation--next {\n  right: 2px;\n}\n.react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {\n  right: 85px;\n}\n.react-datepicker__navigation--years {\n  position: relative;\n  top: 0;\n  display: block;\n  margin-left: auto;\n  margin-right: auto;\n}\n.react-datepicker__navigation--years-previous {\n  top: 4px;\n}\n.react-datepicker__navigation--years-upcoming {\n  top: -4px;\n}\n.react-datepicker__navigation:hover *::before {\n  border-color: rgb(165.75, 165.75, 165.75);\n}\n\n.react-datepicker__navigation-icon {\n  position: relative;\n  top: -1px;\n  font-size: 20px;\n  width: 0;\n}\n.react-datepicker__navigation-icon--next {\n  left: -2px;\n}\n.react-datepicker__navigation-icon--next::before {\n  transform: rotate(45deg);\n  left: -7px;\n}\n.react-datepicker__navigation-icon--previous {\n  right: -2px;\n}\n.react-datepicker__navigation-icon--previous::before {\n  transform: rotate(225deg);\n  right: -7px;\n}\n\n.react-datepicker__month-container {\n  float: left;\n}\n\n.react-datepicker__year {\n  margin: 0.4rem;\n  text-align: center;\n}\n.react-datepicker__year-wrapper {\n  display: flex;\n  flex-wrap: wrap;\n  max-width: 180px;\n}\n.react-datepicker__year .react-datepicker__year-text {\n  display: inline-block;\n  width: 4rem;\n  margin: 2px;\n}\n\n.react-datepicker__month {\n  margin: 0.4rem;\n  text-align: center;\n}\n.react-datepicker__month .react-datepicker__month-text,\n.react-datepicker__month .react-datepicker__quarter-text {\n  display: inline-block;\n  width: 4rem;\n  margin: 2px;\n}\n\n.react-datepicker__input-time-container {\n  clear: both;\n  width: 100%;\n  float: left;\n  margin: 5px 0 10px 15px;\n  text-align: left;\n}\n.react-datepicker__input-time-container .react-datepicker-time__caption {\n  display: inline-block;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container {\n  display: inline-block;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input {\n  display: inline-block;\n  margin-left: 10px;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input {\n  width: auto;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input[type=time]::-webkit-inner-spin-button,\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input[type=time]::-webkit-outer-spin-button {\n  -webkit-appearance: none;\n  margin: 0;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input[type=time] {\n  -moz-appearance: textfield;\n}\n.react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__delimiter {\n  margin-left: 5px;\n  display: inline-block;\n}\n\n.react-datepicker__time-container {\n  float: right;\n  border-left: 1px solid #aeaeae;\n  width: 85px;\n}\n.react-datepicker__time-container--with-today-button {\n  display: inline;\n  border: 1px solid #aeaeae;\n  border-radius: 0.3rem;\n  position: absolute;\n  right: -87px;\n  top: 0;\n}\n.react-datepicker__time-container .react-datepicker__time {\n  position: relative;\n  background: white;\n  border-bottom-right-radius: 0.3rem;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {\n  width: 85px;\n  overflow-x: hidden;\n  margin: 0 auto;\n  text-align: center;\n  border-bottom-right-radius: 0.3rem;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {\n  list-style: none;\n  margin: 0;\n  height: calc(195px + 1.7rem / 2);\n  overflow-y: scroll;\n  padding-right: 0;\n  padding-left: 0;\n  width: 100%;\n  box-sizing: content-box;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {\n  height: 30px;\n  padding: 5px 10px;\n  white-space: nowrap;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {\n  cursor: pointer;\n  background-color: #f0f0f0;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {\n  background-color: #216ba5;\n  color: white;\n  font-weight: bold;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected:hover {\n  background-color: #216ba5;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled {\n  color: #ccc;\n}\n.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled:hover {\n  cursor: default;\n  background-color: transparent;\n}\n\n.react-datepicker__week-number {\n  color: #ccc;\n  display: inline-block;\n  width: 1.7rem;\n  line-height: 1.7rem;\n  text-align: center;\n  margin: 0.166rem;\n}\n.react-datepicker__week-number.react-datepicker__week-number--clickable {\n  cursor: pointer;\n}\n.react-datepicker__week-number.react-datepicker__week-number--clickable:not(.react-datepicker__week-number--selected):hover {\n  border-radius: 0.3rem;\n  background-color: #f0f0f0;\n}\n.react-datepicker__week-number--selected {\n  border-radius: 0.3rem;\n  background-color: #216ba5;\n  color: #fff;\n}\n.react-datepicker__week-number--selected:hover {\n  background-color: rgb(28.75, 93.2196969697, 143.75);\n}\n\n.react-datepicker__day-names {\n  white-space: nowrap;\n  margin-bottom: -8px;\n}\n\n.react-datepicker__week {\n  white-space: nowrap;\n}\n\n.react-datepicker__day-name,\n.react-datepicker__day,\n.react-datepicker__time-name {\n  color: #000;\n  display: inline-block;\n  width: 1.7rem;\n  line-height: 1.7rem;\n  text-align: center;\n  margin: 0.166rem;\n}\n\n.react-datepicker__day,\n.react-datepicker__month-text,\n.react-datepicker__quarter-text,\n.react-datepicker__year-text {\n  cursor: pointer;\n}\n.react-datepicker__day:not([aria-disabled=true]):hover,\n.react-datepicker__month-text:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text:not([aria-disabled=true]):hover,\n.react-datepicker__year-text:not([aria-disabled=true]):hover {\n  border-radius: 0.3rem;\n  background-color: #f0f0f0;\n}\n.react-datepicker__day--today,\n.react-datepicker__month-text--today,\n.react-datepicker__quarter-text--today,\n.react-datepicker__year-text--today {\n  font-weight: bold;\n}\n.react-datepicker__day--highlighted,\n.react-datepicker__month-text--highlighted,\n.react-datepicker__quarter-text--highlighted,\n.react-datepicker__year-text--highlighted {\n  border-radius: 0.3rem;\n  background-color: #3dcc4a;\n  color: #fff;\n}\n.react-datepicker__day--highlighted:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--highlighted:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--highlighted:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--highlighted:not([aria-disabled=true]):hover {\n  background-color: rgb(49.8551020408, 189.6448979592, 62.5632653061);\n}\n.react-datepicker__day--highlighted-custom-1,\n.react-datepicker__month-text--highlighted-custom-1,\n.react-datepicker__quarter-text--highlighted-custom-1,\n.react-datepicker__year-text--highlighted-custom-1 {\n  color: magenta;\n}\n.react-datepicker__day--highlighted-custom-2,\n.react-datepicker__month-text--highlighted-custom-2,\n.react-datepicker__quarter-text--highlighted-custom-2,\n.react-datepicker__year-text--highlighted-custom-2 {\n  color: green;\n}\n.react-datepicker__day--holidays,\n.react-datepicker__month-text--holidays,\n.react-datepicker__quarter-text--holidays,\n.react-datepicker__year-text--holidays {\n  position: relative;\n  border-radius: 0.3rem;\n  background-color: #ff6803;\n  color: #fff;\n}\n.react-datepicker__day--holidays .overlay,\n.react-datepicker__month-text--holidays .overlay,\n.react-datepicker__quarter-text--holidays .overlay,\n.react-datepicker__year-text--holidays .overlay {\n  position: absolute;\n  bottom: 100%;\n  left: 50%;\n  transform: translateX(-50%);\n  background-color: #333;\n  color: #fff;\n  padding: 4px;\n  border-radius: 4px;\n  white-space: nowrap;\n  visibility: hidden;\n  opacity: 0;\n  transition: visibility 0s, opacity 0.3s ease-in-out;\n}\n.react-datepicker__day--holidays:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--holidays:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--holidays:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--holidays:not([aria-disabled=true]):hover {\n  background-color: rgb(207, 82.9642857143, 0);\n}\n.react-datepicker__day--holidays:hover .overlay,\n.react-datepicker__month-text--holidays:hover .overlay,\n.react-datepicker__quarter-text--holidays:hover .overlay,\n.react-datepicker__year-text--holidays:hover .overlay {\n  visibility: visible;\n  opacity: 1;\n}\n.react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range,\n.react-datepicker__month-text--selected,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__month-text--in-range,\n.react-datepicker__quarter-text--selected,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__quarter-text--in-range,\n.react-datepicker__year-text--selected,\n.react-datepicker__year-text--in-selecting-range,\n.react-datepicker__year-text--in-range {\n  border-radius: 0.3rem;\n  background-color: #216ba5;\n  color: #fff;\n}\n.react-datepicker__day--selected:not([aria-disabled=true]):hover, .react-datepicker__day--in-selecting-range:not([aria-disabled=true]):hover, .react-datepicker__day--in-range:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--selected:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--in-selecting-range:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--in-range:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--selected:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--in-selecting-range:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--in-range:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--selected:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--in-selecting-range:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--in-range:not([aria-disabled=true]):hover {\n  background-color: rgb(28.75, 93.2196969697, 143.75);\n}\n.react-datepicker__day--keyboard-selected,\n.react-datepicker__month-text--keyboard-selected,\n.react-datepicker__quarter-text--keyboard-selected,\n.react-datepicker__year-text--keyboard-selected {\n  border-radius: 0.3rem;\n  background-color: rgb(186.25, 217.0833333333, 241.25);\n  color: rgb(0, 0, 0);\n}\n.react-datepicker__day--keyboard-selected:not([aria-disabled=true]):hover,\n.react-datepicker__month-text--keyboard-selected:not([aria-disabled=true]):hover,\n.react-datepicker__quarter-text--keyboard-selected:not([aria-disabled=true]):hover,\n.react-datepicker__year-text--keyboard-selected:not([aria-disabled=true]):hover {\n  background-color: rgb(28.75, 93.2196969697, 143.75);\n}\n.react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range,\n.react-datepicker__month-text--in-range,\n.react-datepicker__quarter-text--in-range,\n.react-datepicker__year-text--in-range),\n.react-datepicker__month-text--in-selecting-range:not(.react-datepicker__day--in-range,\n.react-datepicker__month-text--in-range,\n.react-datepicker__quarter-text--in-range,\n.react-datepicker__year-text--in-range),\n.react-datepicker__quarter-text--in-selecting-range:not(.react-datepicker__day--in-range,\n.react-datepicker__month-text--in-range,\n.react-datepicker__quarter-text--in-range,\n.react-datepicker__year-text--in-range),\n.react-datepicker__year-text--in-selecting-range:not(.react-datepicker__day--in-range,\n.react-datepicker__month-text--in-range,\n.react-datepicker__quarter-text--in-range,\n.react-datepicker__year-text--in-range) {\n  background-color: rgba(33, 107, 165, 0.5);\n}\n.react-datepicker__month--selecting-range .react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range), .react-datepicker__year--selecting-range .react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__month--selecting-range .react-datepicker__month-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__year--selecting-range .react-datepicker__month-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__month--selecting-range .react-datepicker__quarter-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__year--selecting-range .react-datepicker__quarter-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__month--selecting-range .react-datepicker__year-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range),\n.react-datepicker__year--selecting-range .react-datepicker__year-text--in-range:not(.react-datepicker__day--in-selecting-range,\n.react-datepicker__month-text--in-selecting-range,\n.react-datepicker__quarter-text--in-selecting-range,\n.react-datepicker__year-text--in-selecting-range) {\n  background-color: #f0f0f0;\n  color: #000;\n}\n.react-datepicker__day--disabled,\n.react-datepicker__month-text--disabled,\n.react-datepicker__quarter-text--disabled,\n.react-datepicker__year-text--disabled {\n  cursor: default;\n  color: #ccc;\n}\n.react-datepicker__day--disabled .overlay,\n.react-datepicker__month-text--disabled .overlay,\n.react-datepicker__quarter-text--disabled .overlay,\n.react-datepicker__year-text--disabled .overlay {\n  position: absolute;\n  bottom: 70%;\n  left: 50%;\n  transform: translateX(-50%);\n  background-color: #333;\n  color: #fff;\n  padding: 4px;\n  border-radius: 4px;\n  white-space: nowrap;\n  visibility: hidden;\n  opacity: 0;\n  transition: visibility 0s, opacity 0.3s ease-in-out;\n}\n\n.react-datepicker__input-container {\n  position: relative;\n  display: inline-block;\n  width: 100%;\n}\n.react-datepicker__input-container .react-datepicker__calendar-icon {\n  position: absolute;\n  padding: 0.5rem;\n  box-sizing: content-box;\n}\n\n.react-datepicker__view-calendar-icon input {\n  padding: 6px 10px 5px 25px;\n}\n\n.react-datepicker__year-read-view,\n.react-datepicker__month-read-view,\n.react-datepicker__month-year-read-view {\n  border: 1px solid transparent;\n  border-radius: 0.3rem;\n  position: relative;\n}\n.react-datepicker__year-read-view:hover,\n.react-datepicker__month-read-view:hover,\n.react-datepicker__month-year-read-view:hover {\n  cursor: pointer;\n}\n.react-datepicker__year-read-view:hover .react-datepicker__year-read-view--down-arrow,\n.react-datepicker__year-read-view:hover .react-datepicker__month-read-view--down-arrow,\n.react-datepicker__month-read-view:hover .react-datepicker__year-read-view--down-arrow,\n.react-datepicker__month-read-view:hover .react-datepicker__month-read-view--down-arrow,\n.react-datepicker__month-year-read-view:hover .react-datepicker__year-read-view--down-arrow,\n.react-datepicker__month-year-read-view:hover .react-datepicker__month-read-view--down-arrow {\n  border-top-color: rgb(178.5, 178.5, 178.5);\n}\n.react-datepicker__year-read-view--down-arrow,\n.react-datepicker__month-read-view--down-arrow,\n.react-datepicker__month-year-read-view--down-arrow {\n  transform: rotate(135deg);\n  right: -16px;\n  top: 0;\n}\n\n.react-datepicker__year-dropdown,\n.react-datepicker__month-dropdown,\n.react-datepicker__month-year-dropdown {\n  background-color: #f0f0f0;\n  position: absolute;\n  width: 50%;\n  left: 25%;\n  top: 30px;\n  z-index: 1;\n  text-align: center;\n  border-radius: 0.3rem;\n  border: 1px solid #aeaeae;\n}\n.react-datepicker__year-dropdown:hover,\n.react-datepicker__month-dropdown:hover,\n.react-datepicker__month-year-dropdown:hover {\n  cursor: pointer;\n}\n.react-datepicker__year-dropdown--scrollable,\n.react-datepicker__month-dropdown--scrollable,\n.react-datepicker__month-year-dropdown--scrollable {\n  height: 150px;\n  overflow-y: scroll;\n}\n\n.react-datepicker__year-option,\n.react-datepicker__month-option,\n.react-datepicker__month-year-option {\n  line-height: 20px;\n  width: 100%;\n  display: block;\n  margin-left: auto;\n  margin-right: auto;\n}\n.react-datepicker__year-option:first-of-type,\n.react-datepicker__month-option:first-of-type,\n.react-datepicker__month-year-option:first-of-type {\n  border-top-left-radius: 0.3rem;\n  border-top-right-radius: 0.3rem;\n}\n.react-datepicker__year-option:last-of-type,\n.react-datepicker__month-option:last-of-type,\n.react-datepicker__month-year-option:last-of-type {\n  -webkit-user-select: none;\n  -moz-user-select: none;\n  -ms-user-select: none;\n  user-select: none;\n  border-bottom-left-radius: 0.3rem;\n  border-bottom-right-radius: 0.3rem;\n}\n.react-datepicker__year-option:hover,\n.react-datepicker__month-option:hover,\n.react-datepicker__month-year-option:hover {\n  background-color: #ccc;\n}\n.react-datepicker__year-option:hover .react-datepicker__navigation--years-upcoming,\n.react-datepicker__month-option:hover .react-datepicker__navigation--years-upcoming,\n.react-datepicker__month-year-option:hover .react-datepicker__navigation--years-upcoming {\n  border-bottom-color: rgb(178.5, 178.5, 178.5);\n}\n.react-datepicker__year-option:hover .react-datepicker__navigation--years-previous,\n.react-datepicker__month-option:hover .react-datepicker__navigation--years-previous,\n.react-datepicker__month-year-option:hover .react-datepicker__navigation--years-previous {\n  border-top-color: rgb(178.5, 178.5, 178.5);\n}\n.react-datepicker__year-option--selected,\n.react-datepicker__month-option--selected,\n.react-datepicker__month-year-option--selected {\n  position: absolute;\n  left: 15px;\n}\n\n.react-datepicker__close-icon {\n  cursor: pointer;\n  background-color: transparent;\n  border: 0;\n  outline: 0;\n  padding: 0 6px 0 0;\n  position: absolute;\n  top: 0;\n  right: 0;\n  height: 100%;\n  display: table-cell;\n  vertical-align: middle;\n}\n.react-datepicker__close-icon::after {\n  cursor: pointer;\n  background-color: #216ba5;\n  color: #fff;\n  border-radius: 50%;\n  height: 16px;\n  width: 16px;\n  padding: 2px;\n  font-size: 12px;\n  line-height: 1;\n  text-align: center;\n  display: table-cell;\n  vertical-align: middle;\n  content: \"×\";\n}\n.react-datepicker__close-icon--disabled {\n  cursor: default;\n}\n.react-datepicker__close-icon--disabled::after {\n  cursor: default;\n  background-color: #ccc;\n}\n\n.react-datepicker__today-button {\n  background: #f0f0f0;\n  border-top: 1px solid #aeaeae;\n  cursor: pointer;\n  text-align: center;\n  font-weight: bold;\n  padding: 5px 0;\n  clear: left;\n}\n\n.react-datepicker__portal {\n  position: fixed;\n  width: 100vw;\n  height: 100vh;\n  background-color: rgba(0, 0, 0, 0.8);\n  left: 0;\n  top: 0;\n  justify-content: center;\n  align-items: center;\n  display: flex;\n  z-index: 2147483647;\n}\n.react-datepicker__portal .react-datepicker__day-name,\n.react-datepicker__portal .react-datepicker__day,\n.react-datepicker__portal .react-datepicker__time-name {\n  width: 3rem;\n  line-height: 3rem;\n}\n@media (max-width: 400px), (max-height: 550px) {\n  .react-datepicker__portal .react-datepicker__day-name,\n  .react-datepicker__portal .react-datepicker__day,\n  .react-datepicker__portal .react-datepicker__time-name {\n    width: 2rem;\n    line-height: 2rem;\n  }\n}\n.react-datepicker__portal .react-datepicker__current-month,\n.react-datepicker__portal .react-datepicker-time__header {\n  font-size: 1.44rem;\n}\n\n.react-datepicker__children-container {\n  width: 13.8rem;\n  margin: 0.4rem;\n  padding-right: 0.2rem;\n  padding-left: 0.2rem;\n  height: auto;\n}\n\n.react-datepicker__aria-live {\n  position: absolute;\n  clip-path: circle(0);\n  border: 0;\n  height: 1px;\n  margin: -1px;\n  overflow: hidden;\n  padding: 0;\n  width: 1px;\n  white-space: nowrap;\n}\n\n.react-datepicker__calendar-icon {\n  width: 1em;\n  height: 1em;\n  vertical-align: -0.125em;\n}\n";
styleInject(css_248z$J);

var css_248z$I = ".DatePicker-module_date-picker__Ik7X7 {\n  width: 100%;\n  height: 100%;\n}\n\ndiv[class*='datepicker-wrapper'] {\n  width: 100%;\n}\n\n.DatePicker-module_popper__wAz-E[data-placement^='bottom'],\n.DatePicker-module_popper__wAz-E[data-placement^='top'] {\n  margin: 0;\n  padding: 0;\n}\n\ndiv[class*='react-datepicker-popper'] {\n  z-index: 99;\n}\n\n.DatePicker-module_calendar__wsqb3 {\n  background: var(--color-white);\n  box-shadow: var(--dropdown-box-shadow);\n  border-radius: 0 0 var(--input-border-radius) var(--input-border-radius);\n  border: none;\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__header {\n  background-color: var(--color-white);\n  border-bottom: none;\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__day-names {\n  font-size: var(--font-size-xxs);\n  border-bottom: 0.5px solid var(--color-grey-lighter);\n  padding-bottom: 0;\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__day-name {\n  color: var(--color-grey-light);\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__today-button {\n  background: var(--color-white);\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__day:hover {\n  background-color: var(--color-primary-light);\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__day--selected {\n  background-color: var(--color-primary);\n}\n\n.DatePicker-module_calendar__wsqb3 .react-datepicker__day--outside-month {\n  color: var(--color-grey-lighter);\n}\n\n.DatePicker-module_calendar__wsqb3 .DatePicker-module_today__VWXgL {\n  font-weight: normal;\n}\n\n.DatePicker-module_error-message__wHdhy {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n";
var styles$H = {"date-picker":"DatePicker-module_date-picker__Ik7X7","popper":"DatePicker-module_popper__wAz-E","calendar":"DatePicker-module_calendar__wsqb3","today":"DatePicker-module_today__VWXgL","error-message":"DatePicker-module_error-message__wHdhy"};
styleInject(css_248z$I);

/**
 * The {@link startOfISOWeek} function options.
 */

/**
 * @name startOfISOWeek
 * @category ISO Week Helpers
 * @summary Return the start of an ISO week for the given date.
 *
 * @description
 * Return the start of an ISO week for the given date.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of an ISO week
 *
 * @example
 * // The start of an ISO week for 2 September 2014 11:55:00:
 * const result = startOfISOWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfISOWeek(date, options) {
  return startOfWeek$1(date, { ...options, weekStartsOn: 1 });
}

/**
 * The {@link getISOWeekYear} function options.
 */

/**
 * @name getISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Get the ISO week-numbering year of the given date.
 *
 * @description
 * Get the ISO week-numbering year of the given date,
 * which always starts 3 days before the year's first Thursday.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @param date - The given date
 *
 * @returns The ISO week-numbering year
 *
 * @example
 * // Which ISO-week numbering year is 2 January 2005?
 * const result = getISOWeekYear(new Date(2005, 0, 2))
 * //=> 2004
 */
function getISOWeekYear(date, options) {
  const _date = toDate$1(date, options?.in);
  const year = _date.getFullYear();

  const fourthOfJanuaryOfNextYear = constructFrom$1(_date, 0);
  fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfISOWeek(fourthOfJanuaryOfNextYear);

  const fourthOfJanuaryOfThisYear = constructFrom$1(_date, 0);
  fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfISOWeek(fourthOfJanuaryOfThisYear);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  const _date = toDate$1(date);
  const utcDate = new Date(
    Date.UTC(
      _date.getFullYear(),
      _date.getMonth(),
      _date.getDate(),
      _date.getHours(),
      _date.getMinutes(),
      _date.getSeconds(),
      _date.getMilliseconds(),
    ),
  );
  utcDate.setUTCFullYear(_date.getFullYear());
  return +date - +utcDate;
}

/**
 * The {@link startOfDay} function options.
 */

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The original date
 * @param options - The options
 *
 * @returns The start of a day
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay(date, options) {
  const _date = toDate$1(date, options?.in);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * The {@link differenceInCalendarDays} function options.
 */

/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * @param laterDate - The later date
 * @param earlierDate - The earlier date
 * @param options - The options object
 *
 * @returns The number of calendar days
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */
function differenceInCalendarDays(laterDate, earlierDate, options) {
  const [laterDate_, earlierDate_] = normalizeDates(
    options?.in,
    laterDate,
    earlierDate,
  );

  const laterStartOfDay = startOfDay(laterDate_);
  const earlierStartOfDay = startOfDay(earlierDate_);

  const laterTimestamp =
    +laterStartOfDay - getTimezoneOffsetInMilliseconds(laterStartOfDay);
  const earlierTimestamp =
    +earlierStartOfDay - getTimezoneOffsetInMilliseconds(earlierStartOfDay);

  // Round the number of days to the nearest integer because the number of
  // milliseconds in a day is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round((laterTimestamp - earlierTimestamp) / millisecondsInDay$1);
}

/**
 * The {@link startOfISOWeekYear} function options.
 */

/**
 * @name startOfISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Return the start of an ISO week-numbering year for the given date.
 *
 * @description
 * Return the start of an ISO week-numbering year,
 * which always starts 3 days before the year's first Thursday.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of an ISO week-numbering year
 *
 * @example
 * // The start of an ISO week-numbering year for 2 July 2005:
 * const result = startOfISOWeekYear(new Date(2005, 6, 2))
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfISOWeekYear(date, options) {
  const year = getISOWeekYear(date, options);
  const fourthOfJanuary = constructFrom$1(date, 0);
  fourthOfJanuary.setFullYear(year, 0, 4);
  fourthOfJanuary.setHours(0, 0, 0, 0);
  return startOfISOWeek(fourthOfJanuary);
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param value - The value to check
 *
 * @returns True if the given value is a date
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */
function isDate(value) {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Date]")
  );
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @param date - The date to check
 *
 * @returns The date is valid
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertible into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */
function isValid(date) {
  return !((!isDate(date) && typeof date !== "number") || isNaN(+toDate$1(date)));
}

/**
 * The {@link startOfYear} function options.
 */

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The original date
 * @param options - The options
 *
 * @returns The start of a year
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear(date, options) {
  const date_ = toDate$1(date, options?.in);
  date_.setFullYear(date_.getFullYear(), 0, 1);
  date_.setHours(0, 0, 0, 0);
  return date_;
}

/**
 * The {@link getDayOfYear} function options.
 */

/**
 * @name getDayOfYear
 * @category Day Helpers
 * @summary Get the day of the year of the given date.
 *
 * @description
 * Get the day of the year of the given date.
 *
 * @param date - The given date
 * @param options - The options
 *
 * @returns The day of year
 *
 * @example
 * // Which day of the year is 2 July 2014?
 * const result = getDayOfYear(new Date(2014, 6, 2))
 * //=> 183
 */
function getDayOfYear(date, options) {
  const _date = toDate$1(date, options?.in);
  const diff = differenceInCalendarDays(_date, startOfYear(_date));
  const dayOfYear = diff + 1;
  return dayOfYear;
}

/**
 * The {@link getISOWeek} function options.
 */

/**
 * @name getISOWeek
 * @category ISO Week Helpers
 * @summary Get the ISO week of the given date.
 *
 * @description
 * Get the ISO week of the given date.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @param date - The given date
 * @param options - The options
 *
 * @returns The ISO week
 *
 * @example
 * // Which week of the ISO-week numbering year is 2 January 2005?
 * const result = getISOWeek(new Date(2005, 0, 2))
 * //=> 53
 */
function getISOWeek(date, options) {
  const _date = toDate$1(date, options?.in);
  const diff = +startOfISOWeek(_date) - +startOfISOWeekYear(_date);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek$1) + 1;
}

/**
 * The {@link getWeekYear} function options.
 */

/**
 * @name getWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Get the local week-numbering year of the given date.
 *
 * @description
 * Get the local week-numbering year of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @param date - The given date
 * @param options - An object with options.
 *
 * @returns The local week-numbering year
 *
 * @example
 * // Which week numbering year is 26 December 2004 with the default settings?
 * const result = getWeekYear(new Date(2004, 11, 26))
 * //=> 2005
 *
 * @example
 * // Which week numbering year is 26 December 2004 if week starts on Saturday?
 * const result = getWeekYear(new Date(2004, 11, 26), { weekStartsOn: 6 })
 * //=> 2004
 *
 * @example
 * // Which week numbering year is 26 December 2004 if the first week contains 4 January?
 * const result = getWeekYear(new Date(2004, 11, 26), { firstWeekContainsDate: 4 })
 * //=> 2004
 */
function getWeekYear(date, options) {
  const _date = toDate$1(date, options?.in);
  const year = _date.getFullYear();

  const defaultOptions = getDefaultOptions$2();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const firstWeekOfNextYear = constructFrom$1(options?.in || date, 0);
  firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfWeek$1(firstWeekOfNextYear, options);

  const firstWeekOfThisYear = constructFrom$1(options?.in || date, 0);
  firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfWeek$1(firstWeekOfThisYear, options);

  if (+_date >= +startOfNextYear) {
    return year + 1;
  } else if (+_date >= +startOfThisYear) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * The {@link startOfWeekYear} function options.
 */

/**
 * @name startOfWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Return the start of a local week-numbering year for the given date.
 *
 * @description
 * Return the start of a local week-numbering year.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type.
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week-numbering year
 *
 * @example
 * // The start of an a week-numbering year for 2 July 2005 with default settings:
 * const result = startOfWeekYear(new Date(2005, 6, 2))
 * //=> Sun Dec 26 2004 00:00:00
 *
 * @example
 * // The start of a week-numbering year for 2 July 2005
 * // if Monday is the first day of week
 * // and 4 January is always in the first week of the year:
 * const result = startOfWeekYear(new Date(2005, 6, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfWeekYear(date, options) {
  const defaultOptions = getDefaultOptions$2();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const year = getWeekYear(date, options);
  const firstWeek = constructFrom$1(options?.in || date, 0);
  firstWeek.setFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setHours(0, 0, 0, 0);
  const _date = startOfWeek$1(firstWeek, options);
  return _date;
}

/**
 * The {@link getWeek} function options.
 */

/**
 * @name getWeek
 * @category Week Helpers
 * @summary Get the local week index of the given date.
 *
 * @description
 * Get the local week index of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @param date - The given date
 * @param options - An object with options
 *
 * @returns The week
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005 with default options?
 * const result = getWeek(new Date(2005, 0, 2))
 * //=> 2
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005,
 * // if Monday is the first day of the week,
 * // and the first week of the year always contains 4 January?
 * const result = getWeek(new Date(2005, 0, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> 53
 */
function getWeek(date, options) {
  const _date = toDate$1(date, options?.in);
  const diff = +startOfWeek$1(_date, options) - +startOfWeekYear(_date, options);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek$1) + 1;
}

function addLeadingZeros(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  const output = Math.abs(number).toString().padStart(targetLength, "0");
  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

const lightFormatters = {
  // Year
  y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    const signedYear = date.getFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === "yy" ? year % 100 : year, token.length);
  },

  // Month
  M(date, token) {
    const month = date.getMonth();
    return token === "M" ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },

  // Day of the month
  d(date, token) {
    return addLeadingZeros(date.getDate(), token.length);
  },

  // AM or PM
  a(date, token) {
    const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return dayPeriodEnumValue.toUpperCase();
      case "aaa":
        return dayPeriodEnumValue;
      case "aaaaa":
        return dayPeriodEnumValue[0];
      case "aaaa":
      default:
        return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
    }
  },

  // Hour [1-12]
  h(date, token) {
    return addLeadingZeros(date.getHours() % 12 || 12, token.length);
  },

  // Hour [0-23]
  H(date, token) {
    return addLeadingZeros(date.getHours(), token.length);
  },

  // Minute
  m(date, token) {
    return addLeadingZeros(date.getMinutes(), token.length);
  },

  // Second
  s(date, token) {
    return addLeadingZeros(date.getSeconds(), token.length);
  },

  // Fraction of second
  S(date, token) {
    const numberOfDigits = token.length;
    const milliseconds = date.getMilliseconds();
    const fractionalSeconds = Math.trunc(
      milliseconds * Math.pow(10, numberOfDigits - 3),
    );
    return addLeadingZeros(fractionalSeconds, token.length);
  },
};

const dayPeriodEnum = {
  midnight: "midnight",
  noon: "noon",
  morning: "morning",
  afternoon: "afternoon",
  evening: "evening",
  night: "night",
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

const formatters = {
  // Era
  G: function (date, token, localize) {
    const era = date.getFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return localize.era(era, { width: "abbreviated" });
      // A, B
      case "GGGGG":
        return localize.era(era, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return localize.era(era, { width: "wide" });
    }
  },

  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === "yo") {
      const signedYear = date.getFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      const year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, { unit: "year" });
    }

    return lightFormatters.y(date, token);
  },

  // Local week-numbering year
  Y: function (date, token, localize, options) {
    const signedWeekYear = getWeekYear(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === "YY") {
      const twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    }

    // Ordinal number
    if (token === "Yo") {
      return localize.ordinalNumber(weekYear, { unit: "year" });
    }

    // Padding
    return addLeadingZeros(weekYear, token.length);
  },

  // ISO week-numbering year
  R: function (date, token) {
    const isoWeekYear = getISOWeekYear(date);

    // Padding
    return addLeadingZeros(isoWeekYear, token.length);
  },

  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    const year = date.getFullYear();
    return addLeadingZeros(year, token.length);
  },

  // Quarter
  Q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
        return String(quarter);
      // 01, 02, 03, 04
      case "QQ":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "formatting",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "formatting",
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone quarter
  q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "q":
        return String(quarter);
      // 01, 02, 03, 04
      case "qq":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "standalone",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "standalone",
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // Month
  M: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      case "M":
      case "MM":
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "MMM":
        return localize.month(month, {
          width: "abbreviated",
          context: "formatting",
        });
      // J, F, ..., D
      case "MMMMM":
        return localize.month(month, {
          width: "narrow",
          context: "formatting",
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return localize.month(month, { width: "wide", context: "formatting" });
    }
  },

  // Stand-alone month
  L: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return String(month + 1);
      // 01, 02, ..., 12
      case "LL":
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "LLL":
        return localize.month(month, {
          width: "abbreviated",
          context: "standalone",
        });
      // J, F, ..., D
      case "LLLLL":
        return localize.month(month, {
          width: "narrow",
          context: "standalone",
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return localize.month(month, { width: "wide", context: "standalone" });
    }
  },

  // Local week of year
  w: function (date, token, localize, options) {
    const week = getWeek(date, options);

    if (token === "wo") {
      return localize.ordinalNumber(week, { unit: "week" });
    }

    return addLeadingZeros(week, token.length);
  },

  // ISO week of year
  I: function (date, token, localize) {
    const isoWeek = getISOWeek(date);

    if (token === "Io") {
      return localize.ordinalNumber(isoWeek, { unit: "week" });
    }

    return addLeadingZeros(isoWeek, token.length);
  },

  // Day of the month
  d: function (date, token, localize) {
    if (token === "do") {
      return localize.ordinalNumber(date.getDate(), { unit: "date" });
    }

    return lightFormatters.d(date, token);
  },

  // Day of year
  D: function (date, token, localize) {
    const dayOfYear = getDayOfYear(date);

    if (token === "Do") {
      return localize.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },

  // Day of week
  E: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "EEEEE":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "EEEEEE":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "EEEE":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Local day of week
  e: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case "e":
        return String(localDayOfWeek);
      // Padded numerical value
      case "ee":
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case "eo":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "eee":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "eeeee":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "eeeeee":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "eeee":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case "c":
        return String(localDayOfWeek);
      // Padded numerical value
      case "cc":
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case "co":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "ccc":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "standalone",
        });
      // T
      case "ccccc":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "standalone",
        });
      // Tu
      case "cccccc":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "standalone",
        });
      // Tuesday
      case "cccc":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // ISO day of week
  i: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case "i":
        return String(isoDayOfWeek);
      // 02
      case "ii":
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd
      case "io":
        return localize.ordinalNumber(isoDayOfWeek, { unit: "day" });
      // Tue
      case "iii":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "iiiii":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "iiiiii":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "iiii":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM or PM
  a: function (date, token, localize) {
    const hours = date.getHours();
    const dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "aaa":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "aaaaa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "aaaa":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    }

    switch (token) {
      case "b":
      case "bb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "bbb":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "bbbbb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "bbbb":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "BBBBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "BBBB":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === "ho") {
      let hours = date.getHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return lightFormatters.h(date, token);
  },

  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === "Ho") {
      return localize.ordinalNumber(date.getHours(), { unit: "hour" });
    }

    return lightFormatters.H(date, token);
  },

  // Hour [0-11]
  K: function (date, token, localize) {
    const hours = date.getHours() % 12;

    if (token === "Ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Hour [1-24]
  k: function (date, token, localize) {
    let hours = date.getHours();
    if (hours === 0) hours = 24;

    if (token === "ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Minute
  m: function (date, token, localize) {
    if (token === "mo") {
      return localize.ordinalNumber(date.getMinutes(), { unit: "minute" });
    }

    return lightFormatters.m(date, token);
  },

  // Second
  s: function (date, token, localize) {
    if (token === "so") {
      return localize.ordinalNumber(date.getSeconds(), { unit: "second" });
    }

    return lightFormatters.s(date, token);
  },

  // Fraction of second
  S: function (date, token) {
    return lightFormatters.S(date, token);
  },

  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return "Z";
    }

    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (GMT)
  O: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (specific non-location)
  z: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "zzzz":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Seconds timestamp
  t: function (date, token, _localize) {
    const timestamp = Math.trunc(+date / 1000);
    return addLeadingZeros(timestamp, token.length);
  },

  // Milliseconds timestamp
  T: function (date, token, _localize) {
    return addLeadingZeros(+date, token.length);
  },
};

function formatTimezoneShort(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.trunc(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }
  return formatTimezone(offset, delimiter);
}

function formatTimezone(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros(Math.trunc(absOffset / 60), 2);
  const minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

const dateLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "P":
      return formatLong.date({ width: "short" });
    case "PP":
      return formatLong.date({ width: "medium" });
    case "PPP":
      return formatLong.date({ width: "long" });
    case "PPPP":
    default:
      return formatLong.date({ width: "full" });
  }
};

const timeLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "p":
      return formatLong.time({ width: "short" });
    case "pp":
      return formatLong.time({ width: "medium" });
    case "ppp":
      return formatLong.time({ width: "long" });
    case "pppp":
    default:
      return formatLong.time({ width: "full" });
  }
};

const dateTimeLongFormatter = (pattern, formatLong) => {
  const matchResult = pattern.match(/(P+)(p+)?/) || [];
  const datePattern = matchResult[1];
  const timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  let dateTimeFormat;

  switch (datePattern) {
    case "P":
      dateTimeFormat = formatLong.dateTime({ width: "short" });
      break;
    case "PP":
      dateTimeFormat = formatLong.dateTime({ width: "medium" });
      break;
    case "PPP":
      dateTimeFormat = formatLong.dateTime({ width: "long" });
      break;
    case "PPPP":
    default:
      dateTimeFormat = formatLong.dateTime({ width: "full" });
      break;
  }

  return dateTimeFormat
    .replace("{{date}}", dateLongFormatter(datePattern, formatLong))
    .replace("{{time}}", timeLongFormatter(timePattern, formatLong));
};

const longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter,
};

const dayOfYearTokenRE = /^D+$/;
const weekYearTokenRE = /^Y+$/;

const throwTokens = ["D", "DD", "YY", "YYYY"];

function isProtectedDayOfYearToken(token) {
  return dayOfYearTokenRE.test(token);
}

function isProtectedWeekYearToken(token) {
  return weekYearTokenRE.test(token);
}

function warnOrThrowProtectedError(token, format, input) {
  const _message = message(token, format, input);
  console.warn(_message);
  if (throwTokens.includes(token)) throw new RangeError(_message);
}

function message(token, format, input) {
  const subject = token[0] === "Y" ? "years" : "days of the month";
  return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
const formattingTokensRegExp =
  /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
const longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

const escapedStringRegExp = /^'([^]*?)'?$/;
const doubleQuoteRegExp = /''/g;
const unescapedLatinCharacterRegExp = /[a-zA-Z]/;

/**
 * The {@link format} function options.
 */

/**
 * @name format
 * @alias formatDate
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear](https://date-fns.org/docs/getISOWeekYear)
 *    and [getWeekYear](https://date-fns.org/docs/getWeekYear)).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @param date - The original date
 * @param format - The string of tokens
 * @param options - An object with options
 *
 * @returns The formatted date string
 *
 * @throws `date` must not be Invalid Date
 * @throws `options.locale` must contain `localize` property
 * @throws `options.locale` must contain `formatLong` property
 * @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */
function format(date, formatStr, options) {
  const defaultOptions = getDefaultOptions$2();
  const locale = options?.locale ?? defaultOptions.locale ?? enUS$1;

  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const originalDate = toDate$1(date, options?.in);

  if (!isValid(originalDate)) {
    throw new RangeError("Invalid time value");
  }

  let parts = formatStr
    .match(longFormattingTokensRegExp)
    .map((substring) => {
      const firstCharacter = substring[0];
      if (firstCharacter === "p" || firstCharacter === "P") {
        const longFormatter = longFormatters[firstCharacter];
        return longFormatter(substring, locale.formatLong);
      }
      return substring;
    })
    .join("")
    .match(formattingTokensRegExp)
    .map((substring) => {
      // Replace two single quote characters with one single quote character
      if (substring === "''") {
        return { isToken: false, value: "'" };
      }

      const firstCharacter = substring[0];
      if (firstCharacter === "'") {
        return { isToken: false, value: cleanEscapedString(substring) };
      }

      if (formatters[firstCharacter]) {
        return { isToken: true, value: substring };
      }

      if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
        throw new RangeError(
          "Format string contains an unescaped latin alphabet character `" +
            firstCharacter +
            "`",
        );
      }

      return { isToken: false, value: substring };
    });

  // invoke localize preprocessor (only for french locales at the moment)
  if (locale.localize.preprocessor) {
    parts = locale.localize.preprocessor(originalDate, parts);
  }

  const formatterOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale,
  };

  return parts
    .map((part) => {
      if (!part.isToken) return part.value;

      const token = part.value;

      if (
        (!options?.useAdditionalWeekYearTokens &&
          isProtectedWeekYearToken(token)) ||
        (!options?.useAdditionalDayOfYearTokens &&
          isProtectedDayOfYearToken(token))
      ) {
        warnOrThrowProtectedError(token, formatStr, String(date));
      }

      const formatter = formatters[token[0]];
      return formatter(originalDate, token, locale.localize, formatterOptions);
    })
    .join("");
}

function cleanEscapedString(input) {
  const matched = input.match(escapedStringRegExp);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp, "'");
}

/**
 * The {@link getYear} function options.
 */

/**
 * @name getYear
 * @category Year Helpers
 * @summary Get the year of the given date.
 *
 * @description
 * Get the year of the given date.
 *
 * @param date - The given date
 * @param options - An object with options
 *
 * @returns The year
 *
 * @example
 * // Which year is 2 July 2014?
 * const result = getYear(new Date(2014, 6, 2))
 * //=> 2014
 */
function getYear(date, options) {
  return toDate$1(date, options?.in).getFullYear();
}

var css_248z$H = ".DatePickerHeader-module_date-picker-header__MGjr- {\n  display: flex;\n  justify-content: space-between;\n  gap: 8px;\n  align-items: center;\n  background-color: var(--color-white);\n}\n\n.DatePickerHeader-module_date-picker-header__MGjr-.DatePickerHeader-module_simple__BQS3C {\n  padding: 0 16px;\n}\n\n.DatePickerHeader-module_date-picker-header__MGjr-.DatePickerHeader-module_select__6o-gq {\n  padding: 16px;\n}\n\n.DatePickerHeader-module_date-picker-header__MGjr-.DatePickerHeader-module_select__6o-gq .DatePickerHeader-module_month__ZnMTS {\n  width: 130px;\n  text-align: left;\n}\n\n.DatePickerHeader-module_date-picker-header__MGjr-.DatePickerHeader-module_select__6o-gq .DatePickerHeader-module_year__zwaES {\n  width: 100px;\n}\n";
var styles$G = {"date-picker-header":"DatePickerHeader-module_date-picker-header__MGjr-","simple":"DatePickerHeader-module_simple__BQS3C","select":"DatePickerHeader-module_select__6o-gq","month":"DatePickerHeader-module_month__ZnMTS","year":"DatePickerHeader-module_year__zwaES"};
styleInject(css_248z$H);

function range(start, end, step) {
    var value;
    if (step === void 0) { step = 1; }
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                value = start;
                _a.label = 1;
            case 1:
                if (!(value < end)) return [3 /*break*/, 4];
                return [4 /*yield*/, value];
            case 2:
                _a.sent();
                _a.label = 3;
            case 3:
                value += step;
                return [3 /*break*/, 1];
            case 4: return [2 /*return*/];
        }
    });
}

var _excluded = ["allowCreateWhileLoading", "createOptionPosition", "formatCreateLabel", "isValidNewOption", "getNewOptionData", "onCreateOption", "options", "onChange"];
var compareOption = function compareOption() {
  var inputValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var option = arguments.length > 1 ? arguments[1] : undefined;
  var accessors = arguments.length > 2 ? arguments[2] : undefined;
  var candidate = String(inputValue).toLowerCase();
  var optionValue = String(accessors.getOptionValue(option)).toLowerCase();
  var optionLabel = String(accessors.getOptionLabel(option)).toLowerCase();
  return optionValue === candidate || optionLabel === candidate;
};
var builtins = {
  formatCreateLabel: function formatCreateLabel(inputValue) {
    return "Create \"".concat(inputValue, "\"");
  },
  isValidNewOption: function isValidNewOption(inputValue, selectValue, selectOptions, accessors) {
    return !(!inputValue || selectValue.some(function (option) {
      return compareOption(inputValue, option, accessors);
    }) || selectOptions.some(function (option) {
      return compareOption(inputValue, option, accessors);
    }));
  },
  getNewOptionData: function getNewOptionData(inputValue, optionLabel) {
    return {
      label: optionLabel,
      value: inputValue,
      __isNew__: true
    };
  }
};
function useCreatable(_ref) {
  var _ref$allowCreateWhile = _ref.allowCreateWhileLoading,
    allowCreateWhileLoading = _ref$allowCreateWhile === void 0 ? false : _ref$allowCreateWhile,
    _ref$createOptionPosi = _ref.createOptionPosition,
    createOptionPosition = _ref$createOptionPosi === void 0 ? 'last' : _ref$createOptionPosi,
    _ref$formatCreateLabe = _ref.formatCreateLabel,
    formatCreateLabel = _ref$formatCreateLabe === void 0 ? builtins.formatCreateLabel : _ref$formatCreateLabe,
    _ref$isValidNewOption = _ref.isValidNewOption,
    isValidNewOption = _ref$isValidNewOption === void 0 ? builtins.isValidNewOption : _ref$isValidNewOption,
    _ref$getNewOptionData = _ref.getNewOptionData,
    getNewOptionData = _ref$getNewOptionData === void 0 ? builtins.getNewOptionData : _ref$getNewOptionData,
    onCreateOption = _ref.onCreateOption,
    _ref$options = _ref.options,
    propsOptions = _ref$options === void 0 ? [] : _ref$options,
    propsOnChange = _ref.onChange,
    restSelectProps = _objectWithoutProperties(_ref, _excluded);
  var _restSelectProps$getO = restSelectProps.getOptionValue,
    getOptionValue$1$1 = _restSelectProps$getO === void 0 ? getOptionValue$1 : _restSelectProps$getO,
    _restSelectProps$getO2 = restSelectProps.getOptionLabel,
    getOptionLabel$1$1 = _restSelectProps$getO2 === void 0 ? getOptionLabel$1 : _restSelectProps$getO2,
    inputValue = restSelectProps.inputValue,
    isLoading = restSelectProps.isLoading,
    isMulti = restSelectProps.isMulti,
    value = restSelectProps.value,
    name = restSelectProps.name;
  var newOption = useMemo(function () {
    return isValidNewOption(inputValue, cleanValue(value), propsOptions, {
      getOptionValue: getOptionValue$1$1,
      getOptionLabel: getOptionLabel$1$1
    }) ? getNewOptionData(inputValue, formatCreateLabel(inputValue)) : undefined;
  }, [formatCreateLabel, getNewOptionData, getOptionLabel$1$1, getOptionValue$1$1, inputValue, isValidNewOption, propsOptions, value]);
  var options = useMemo(function () {
    return (allowCreateWhileLoading || !isLoading) && newOption ? createOptionPosition === 'first' ? [newOption].concat(_toConsumableArray(propsOptions)) : [].concat(_toConsumableArray(propsOptions), [newOption]) : propsOptions;
  }, [allowCreateWhileLoading, createOptionPosition, isLoading, newOption, propsOptions]);
  var onChange = useCallback(function (newValue, actionMeta) {
    if (actionMeta.action !== 'select-option') {
      return propsOnChange(newValue, actionMeta);
    }
    var valueArray = Array.isArray(newValue) ? newValue : [newValue];
    if (valueArray[valueArray.length - 1] === newOption) {
      if (onCreateOption) onCreateOption(inputValue);else {
        var newOptionData = getNewOptionData(inputValue, inputValue);
        var newActionMeta = {
          action: 'create-option',
          name: name,
          option: newOptionData
        };
        propsOnChange(valueTernary(isMulti, [].concat(_toConsumableArray(cleanValue(value)), [newOptionData]), newOptionData), newActionMeta);
      }
      return;
    }
    propsOnChange(newValue, actionMeta);
  }, [getNewOptionData, inputValue, isMulti, name, newOption, onCreateOption, propsOnChange, value]);
  return _objectSpread2(_objectSpread2({}, restSelectProps), {}, {
    options: options,
    onChange: onChange
  });
}

var CreatableSelect = /*#__PURE__*/forwardRef(function (props, ref) {
  var creatableProps = useStateManager(props);
  var selectProps = useCreatable(creatableProps);
  return /*#__PURE__*/React.createElement(Select$2, _extends({
    ref: ref
  }, selectProps));
});
var CreatableSelect$1 = CreatableSelect;

var css_248z$G = "/*\n  There are multiple ways to style the react-select field.\n  See https://react-select.com/styles\n\n  Currently we use the CSS attribute selectors $= to get the\n  react select sub-components we want to style.\n  The $='' attribute selector can be used since all react-select\n  components are named in the same way (xyz-componentName).\n  This allows us to still have the CSS styles in our css-modules\n  files and there is no need to have CSS-in-JS styling.\n\n  This might be subject of change.\n*/\n.Select-module_select__X7aC6 {\n  width: 100%;\n  height: 100%;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-control'] {\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  height: 100%;\n  min-height: var(--input-height-mobile);\n  box-shadow: none;\n  transition: all 0.3s ease;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-ValueContainer'] {\n  padding: 0 8px;\n  min-height: var(--input-height-mobile);\n  height: fit-content;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-container'] {\n  padding: 0 8px;\n  min-height: var(--input-height-mobile);\n  height: fit-content;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_keep-height__llppm [class*='-ValueContainer'] {\n  height: var(--input-height-mobile);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_keep-height__llppm [class*='-container'] {\n  height: var(--input-height-mobile);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_filled__Tqn6D [class*='-control'] {\n  color: var(--input-text-color-filled);\n  border-color: var(--input-border-color-filled);\n  background-color: var(--input-bg-color-filled);\n}\n\n.Select-module_menu-list__AfB4E {\n  z-index: var(--z-index-dropdown);\n  background-color: var(--input-bg-color-default);\n  box-shadow: var(--dropdown-box-shadow);\n}\n\n.Select-module_menu__PQxZH {\n  max-height: 400px;\n  font-size: var(--font-size-s);\n  margin: 0;\n  background-color: var(--input-bg-color-default);\n  border-radius: 0 0 var(--input-border-radius) var(--input-border-radius);\n  position: absolute;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-placeholder'] {\n  color: var(--input-text-color-placeholder);\n  font-size: var(--font-size-s);\n  user-select: none;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-multiValue'] {\n  height: 100%;\n  background-color: transparent;\n  border: none;\n  margin: 0 8px 0 0;\n  padding: 4px 0;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-multiValue']:last-child {\n  display: none;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-indicatorSeparator'] {\n  display: none;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-indicatorContainer'] {\n  justify-content: center;\n  align-items: center;\n  padding: 0 8px;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-option'] {\n  border-radius: 4px;\n  padding: 4px 8px;\n  font-size: inherit;\n  cursor: pointer;\n}\n\n/* option and value */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f,\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-value__-TxXV {\n  display: flex;\n  align-items: center;\n  min-height: var(--input-height-mobile);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_success__Qq6mD {\n  color: var(--color-success);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_warning__n1O9p {\n  color: var(--color-warning);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_error__UvoaD {\n  color: var(--color-error);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_highlight-success__N96oF {\n  color: var(--color-white);\n  background-color: var(--color-success);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_highlight-warning__M4-i1 {\n  color: var(--color-white);\n  background-color: var(--color-warning);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_highlight-error__RoP5K {\n  color: var(--color-white);\n  background-color: var(--color-error);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f .Select-module_label__ptMNc,\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-value__-TxXV .Select-module_label__ptMNc {\n  white-space: nowrap;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f .Select-module_label__ptMNc.Select-module_with-icon__Cdn-1,\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-value__-TxXV .Select-module_label__ptMNc.Select-module_with-icon__Cdn-1 {\n  margin-left: calc(2rem + var(--spacing-1));\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_disabled__6PD-t {\n  color: var(--input-label-color-text-disabled);\n  background-color: var(--input-bg-color-disabled);\n  cursor: not-allowed;\n  pointer-events: none;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f.Select-module_disabled__6PD-t:hover {\n  background-color: var(--input-bg-color-disabled);\n}\n\n/* hovered */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-control']:hover {\n  border-color: var(--input-border-color-hover);\n  cursor: pointer;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk:hover [class*='-placeholder'] {\n  color: var(--input-text-color-hover);\n}\n\n/* focused */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-control']:focus-within {\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\n\n/* disabled */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_disabled__6PD-t [class*='-control'],\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_disabled__6PD-t .Select-module_tag__fCQk- {\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-text-color-disabled);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_disabled__6PD-t .Select-module_tag__fCQk- svg {\n  opacity: 0.3;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_disabled__6PD-t[class*='-container'] {\n  cursor: not-allowed;\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_disabled__6PD-t [class*='-placeholder'] {\n  color: var(--input-text-color-disabled);\n}\n\n/* read-only */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_read-only__hVcwP [class*='-control'] {\n  background-color: var(--color-grey-lightest);\n  border-color: var(--color-grey-lightest);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_read-only__hVcwP [class*='-singleValue'] {\n  color: var(--input-text-color-active);\n}\n\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_read-only__hVcwP .Select-module_tag__fCQk- {\n  color: var(--input-text-color-active);\n  background-color: var(--color-grey-lightest);\n}\n\n/* error */\n.Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_error__UvoaD [class*='-control'] {\n  border-color: var(--input-text-color-error);\n}\n\n.Select-module_error-message__YxHMi {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n\n/* contentBelowOpts */\n.Select-module_bottom__8PUFG {\n  font-size: inherit;\n  padding: 4px 8px;\n}\n\n.Select-module_icon__A1hFj {\n  position: absolute;\n  height: 2rem;\n  width: 2rem;\n  stroke-width: 2.5;\n}\n\n@media (width >= 580px) {\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-option__1ui0f,\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk .Select-module_custom-value__-TxXV {\n    min-height: calc(var(--input-height-desktop) - 8px);\n  }\n\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-control'] {\n    min-height: calc(var(--input-height-desktop) - 2px);\n  }\n\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-multiValue'] [class*='tag'] {\n    max-height: var(--spacing-4);\n  }\n\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk [class*='-container'] {\n    min-height: calc(var(--input-height-desktop) - 2px);\n    height: fit-content;\n  }\n\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_keep-height__llppm [class*='-ValueContainer'] {\n    height: calc(var(--input-height-desktop) - 2px);\n  }\n\n  .Select-module_select__X7aC6 .Select-module_input__3cgRk.Select-module_keep-height__llppm [class*='-container'] {\n    height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n";
var styles$F = {"select":"Select-module_select__X7aC6","input":"Select-module_input__3cgRk","keep-height":"Select-module_keep-height__llppm","filled":"Select-module_filled__Tqn6D","menu-list":"Select-module_menu-list__AfB4E","menu":"Select-module_menu__PQxZH","custom-option":"Select-module_custom-option__1ui0f","custom-value":"Select-module_custom-value__-TxXV","success":"Select-module_success__Qq6mD","warning":"Select-module_warning__n1O9p","error":"Select-module_error__UvoaD","highlight-success":"Select-module_highlight-success__N96oF","highlight-warning":"Select-module_highlight-warning__M4-i1","highlight-error":"Select-module_highlight-error__RoP5K","label":"Select-module_label__ptMNc","with-icon":"Select-module_with-icon__Cdn-1","disabled":"Select-module_disabled__6PD-t","tag":"Select-module_tag__fCQk-","read-only":"Select-module_read-only__hVcwP","error-message":"Select-module_error-message__YxHMi","bottom":"Select-module_bottom__8PUFG","icon":"Select-module_icon__A1hFj"};
styleInject(css_248z$G);

var css_248z$F = ".Tag-module_tag__-sLTT {\n  height: 100%;\n  border: 1px solid var(--color-grey-lighter);\n  color: var(--color-text);\n  background-color: var(--color-white);\n  font-size: var(--font-size-m);\n  display: inline-flex;\n  justify-content: center;\n  vertical-align: middle;\n  align-items: center;\n  transition: all 0.3s ease;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_big__PrwcZ {\n  max-height: var(--tag-height-big);\n  border-radius: var(--tag-height-big);\n}\n\n.Tag-module_tag__-sLTT.Tag-module_small__Fn5w4 {\n  max-height: var(--tag-height-small);\n  border-radius: var(--tag-height-small);\n}\n\n.Tag-module_tag__-sLTT.Tag-module_big__PrwcZ .Tag-module_button__ZtTnI {\n  border-radius: calc(var(--tag-height-big) / 2);\n}\n\n.Tag-module_tag__-sLTT.Tag-module_small__Fn5w4 .Tag-module_button__ZtTnI {\n  height: 100%;\n  border-radius: calc(var(--tag-height-small) / 2);\n  min-width: initial;\n  width: 16px;\n  margin: 0 2px 0 4px;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_big__PrwcZ .Tag-module_button__ZtTnI svg {\n  width: 20px;\n  height: 20px;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_small__Fn5w4 .Tag-module_button__ZtTnI svg {\n  width: 12px;\n  height: 12px;\n  stroke-width: 4;\n}\n\n.Tag-module_value__9wLOz {\n  display: inline-block;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_big__PrwcZ .Tag-module_value__9wLOz {\n  padding: 8px 12px;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_small__Fn5w4 .Tag-module_value__9wLOz {\n  padding: 4px 8px;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_big__PrwcZ.Tag-module_closable__VfAum .Tag-module_value__9wLOz {\n  padding: 8px 2px 8px 12px;\n}\n\n.Tag-module_tag__-sLTT.Tag-module_small__Fn5w4.Tag-module_closable__VfAum .Tag-module_value__9wLOz {\n  padding: 4px 2px 4px 8px;\n}\n";
var styles$E = {"tag":"Tag-module_tag__-sLTT","big":"Tag-module_big__PrwcZ","small":"Tag-module_small__Fn5w4","button":"Tag-module_button__ZtTnI","value":"Tag-module_value__9wLOz","closable":"Tag-module_closable__VfAum"};
styleInject(css_248z$F);

var Tag = function (_a, ref) {
    var _b;
    var value = _a.value, closable = _a.closable, onClose = _a.onClose, _c = _a.size, size = _c === void 0 ? 'big' : _c, className = _a.className;
    var classes = classNames$1(styles$E.tag, size === 'big' ? styles$E.big : styles$E.small, (_b = {},
        _b[styles$E.closable] = closable,
        _b), className);
    return (jsxs("div", { ref: ref, className: classes, children: [jsx$1("div", { className: styles$E.value, children: value }), closable && (jsx$1(Button$1, { hierarchy: 'tertiary', className: styles$E.button, icon: jsx$1(SvgCloseIcon, {}), onClick: onClose }))] }));
};
var Tag$1 = forwardRef(Tag);

var IconMap = {
    AccessIcon: SvgAccessIcon,
    AddIcon: SvgAddIcon,
    AllMeetingsIcon: SvgAllMeetingsIcon,
    ArrowDownIcon: SvgArrowDownIcon,
    ArrowHeadDownIcon: SvgArrowHeadDownIcon,
    ArrowHeadLeftIcon: SvgArrowHeadLeftIcon,
    ArrowHeadRightIcon: SvgArrowHeadRightIcon,
    ArrowHeadUpIcon: SvgArrowHeadUpIcon,
    ArrowLeftIcon: SvgArrowLeftIcon,
    ArrowRightIcon: SvgArrowRightIcon,
    ArrowUpIcon: SvgArrowUpIcon,
    BasedataIcon: SvgBasedataIcon,
    BirthdayIcon: SvgBirthdayIcon,
    BulkEditIcon: SvgBulkEditIcon,
    CalendarIcon: SvgCalendarIcon,
    CertificatesIcon: SvgCertificatesIcon,
    ChangeViewIcon: SvgChangeViewIcon,
    CheckIcon: SvgCheckIcon,
    ClassbookIcon: SvgClassbookIcon,
    ClockIcon: SvgClockIcon,
    CloseIcon: SvgCloseIcon,
    ControlIcon: SvgControlIcon,
    CoursesIcon: SvgCoursesIcon,
    DashboardIcon: SvgDashboardIcon,
    DeleteIcon: SvgDeleteIcon,
    DeputateIcon: SvgDeputateIcon,
    DotsHorizontalIcon: SvgDotsHorizontalIcon,
    DotsVerticalIcon: SvgDotsVerticalIcon,
    DoubleExclamationmarkIcon: SvgDoubleExclamationmarkIcon,
    DoublearrowLeftIcon: SvgDoublearrowLeftIcon,
    DoublearrowRightIcon: SvgDoublearrowRightIcon,
    DuplicateIcon: SvgDuplicateIcon,
    EditIcon: SvgEditIcon,
    EpochenIcon: SvgEpochenIcon,
    ExclamationmarkIcon: SvgExclamationmarkIcon,
    FachstundenIcon: SvgFachstundenIcon,
    FactorIcon: SvgFactorIcon,
    FileIcon: SvgFileIcon,
    FilterIcon: SvgFilterIcon,
    ForumIcon: SvgForumIcon,
    FullscreenActiveIcon: SvgFullscreenActiveIcon,
    FullscreenIcon: SvgFullscreenIcon,
    GenerateIcon: SvgGenerateIcon,
    GradeGroupsIcon: SvgGradeGroupsIcon,
    GridIcon: SvgGridIcon,
    GroupsIcon: SvgGroupsIcon,
    HandIcon: SvgHandIcon,
    HomeIcon: SvgHomeIcon,
    ImportIcon: SvgImportIcon,
    ImportantIcon: SvgImportantIcon,
    ImproveIcon: SvgImproveIcon,
    InfoIcon: SvgInfoIcon,
    InvisibleIcon: SvgInvisibleIcon,
    LessonIcon: SvgLessonIcon,
    LinkIcon: SvgLinkIcon,
    ListIcon: SvgListIcon,
    LockIcon: SvgLockIcon,
    LogOutIcon: SvgLogOutIcon,
    MailIcon: SvgMailIcon,
    MandatoryIcon: SvgMandatoryIcon,
    MaterialArchiveIcon: SvgMaterialArchiveIcon,
    MediathekIcon: SvgMediathekIcon,
    MeetingActiveIcon: SvgMeetingActiveIcon,
    MeetingDefaultIcon: SvgMeetingDefaultIcon,
    MenuIcon: SvgMenuIcon,
    MessagesActiveIcon: SvgMessagesActiveIcon,
    MessagesDefaultIcon: SvgMessagesDefaultIcon,
    MinusIcon: SvgMinusIcon,
    MobilePhoneIcon: SvgMobilePhoneIcon,
    NextcloudIcon: SvgNextcloudIcon,
    NoDataIcon: SvgNoDataIcon,
    NoTasksIcon: SvgNoTasksIcon,
    NotificationActiveIcon: SvgNotificationActiveIcon,
    NotificationDefaultIcon: SvgNotificationDefaultIcon,
    OptimizingIcon: SvgOptimizingIcon,
    PdfIcon: SvgPdfIcon,
    PinIcon: SvgPinIcon,
    PrintIcon: SvgPrintIcon,
    ProfileIcon: SvgProfileIcon,
    QuestionmarkCircleIcon: SvgQuestionmarkCircleIcon,
    QuestionmarkIcon: SvgQuestionmarkIcon,
    RoomIcon: SvgRoomIcon,
    ScenarioIcon: SvgScenarioIcon,
    SearchIcon: SvgSearchIcon,
    SendIcon: SvgSendIcon,
    SettingsIcon: SvgSettingsIcon,
    ShareAlternateIcon: SvgShareAlternateIcon,
    ShareIcon: SvgShareIcon,
    SpinnerIcon: SvgSpinnerIcon,
    SubstitutionsIcon: SvgSubstitutionsIcon,
    SwitchIcon: SvgSwitchIcon,
    TemplatesIcon: SvgTemplatesIcon,
    TestIcon: SvgTestIcon,
    ThreemaIcon: SvgThreemaIcon,
    TimetableIcon: SvgTimetableIcon,
    UndoIcon: SvgUndoIcon,
    UnlockIcon: SvgUnlockIcon,
    UnpinIcon: SvgUnpinIcon,
    UpdateIcon: SvgUpdateIcon,
    UploadIcon: SvgUploadIcon,
    VisibleIcon: SvgVisibleIcon,
};

/**
 *
 * Form input: Select
 *
 * General select field used for selects/dropdowns in forms.
 *
 * Based on React-Select (https://react-select.com/)
 *
 * Currently we only use the props that needed for our application. In case of any
 * additional props needed, checkout the react-select documentation to see what
 * props & types are available.
 *
 */
var Select = function (_a, ref) {
    var _b;
    var name = _a.name, options = _a.options, _c = _a.placement, placement = _c === void 0 ? 'bottom' : _c, _d = _a.menuPosition, menuPosition = _d === void 0 ? 'absolute' : _d, _e = _a.minMenuHeight, minMenuHeight = _e === void 0 ? 100 : _e, _f = _a.maxMenuHeight, maxMenuHeight = _f === void 0 ? 200 : _f, _g = _a.hasGroups, hasGroups = _g === void 0 ? false : _g, _h = _a.isMulti, isMulti = _h === void 0 ? false : _h, _j = _a.isClearable, isClearable = _j === void 0 ? false : _j, _k = _a.isSearchable, isSearchable = _k === void 0 ? false : _k, _l = _a.isTrigger, isTrigger = _l === void 0 ? false : _l, _m = _a.keepHeight, keepHeight = _m === void 0 ? false : _m, _o = _a.fitMenuToOptions, fitMenuToOptions = _o === void 0 ? false : _o, _p = _a.labelCanOverflow, labelCanOverflow = _p === void 0 ? false : _p, _q = _a.bigHeaderGap, bigHeaderGap = _q === void 0 ? false : _q, _r = _a.showIconOnly, showIconOnly = _r === void 0 ? false : _r, value = _a.value, defaultValue = _a.defaultValue, disabled = _a.disabled, readonly = _a.readonly, label = _a.label, placeholder = _a.placeholder, className = _a.className, error = _a.error, dense = _a.dense, autoFocus = _a.autoFocus, tooltipText = _a.tooltipText, _s = _a.noOptionsElement, noOptionsElement = _s === void 0 ? jsx$1(Fragment, { children: "Keine Auswahlm\u00F6glichkeiten gefunden" }) : _s, _t = _a.tabIndex, tabIndex = _t === void 0 ? 0 : _t, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, _u = _a.allowCreate, allowCreate = _u === void 0 ? false : _u, formatCreateLabel = _a.formatCreateLabel, contentBelowOpts = _a.contentBelowOpts;
    var _v = useState(), selectedOption = _v[0], setSelectedOption = _v[1];
    var inputClass = classNames$1(styles$F.input, (_b = {},
        _b[styles$F['error']] = error,
        _b[styles$F['disabled']] = disabled,
        _b[styles$F['read-only']] = readonly,
        _b[styles$F['keep-height']] = keepHeight,
        _b[styles$F['filled']] = !!selectedOption,
        _b));
    /**
     *
     * @param {MultiValue<SelectOptionType> | SingleValue<SelectOptionType>} option
     *
     * Event handler that fires when ReactSelect onChange event
     * is invoked.
     * Sets the internal state and update onChange prop
     */
    function handleChange(option) {
        var selectedOption = isMulti
            ? option
            : option;
        setSelectedOption(isTrigger ? null : selectedOption);
        onChange && onChange(selectedOption);
    }
    function handleRemove(option) {
        var options = selectedOption;
        if (!options.filter)
            handleChange([]);
        else
            handleChange(options.filter(function (o) { return o.value !== option.value; }));
    }
    var groupedOptions = useMemo(function () {
        var groupedOptions = undefined;
        if (hasGroups) {
            var groups_1 = [];
            options.forEach(function (option) {
                var _a;
                var label = (_a = option.group) !== null && _a !== void 0 ? _a : '';
                var isNew = true;
                groups_1.forEach(function (group) {
                    if (label === group.label) {
                        group.options.push(option);
                        isNew = false;
                    }
                });
                if (isNew)
                    groups_1.push({
                        label: label,
                        options: [option],
                    });
            });
            groupedOptions = groups_1;
        }
        return groupedOptions;
    }, [hasGroups, options]);
    var MenuList = function (props) {
        return (jsxs(components.MenuList, __assign({}, props, { className: styles$F['menu-list'], children: [props.children, contentBelowOpts && (jsx$1("div", { className: styles$F.bottom, children: contentBelowOpts }))] })));
    };
    var Menu = function (props) {
        return (jsx$1(components.Menu, __assign({}, props, { className: styles$F['menu'], children: props.children })));
    };
    var MultiValueContainer = function (_a) {
        var _b;
        _a.children; var props = __rest(_a, ["children"]);
        return (jsx$1(components.MultiValueContainer, __assign({}, props, { children: jsx$1(Tag$1, { className: styles$F['tag'], value: (_b = props.data.label) !== null && _b !== void 0 ? _b : '', closable: true, size: 'small', onClose: function () { return handleRemove(props.data); } }) })));
    };
    useEffect(function () {
        setSelectedOption(isTrigger ? null : value);
    }, [isTrigger, value]);
    function getIcon(type) {
        return React__default.createElement(IconMap[type], { className: styles$F.icon });
    }
    var CustomSelectOption = function (props) {
        var _a, _b;
        var data = props.data;
        return (jsxs(components.Option, __assign({}, props, { className: classNames$1(styles$F['custom-option'], (_a = {},
                _a[styles$F.disabled] = !!data.disabled,
                _a[styles$F.success] = data.color && data.color === 'success',
                _a[styles$F.warning] = data.color && data.color === 'warning',
                _a[styles$F.error] = data.color && data.color === 'error',
                _a[styles$F['highlight-success']] = data.highlight && data.highlight === 'success',
                _a[styles$F['highlight-warning']] = data.highlight && data.highlight === 'warning',
                _a[styles$F['highlight-error']] = data.highlight && data.highlight === 'error',
                _a), data.className), isDisabled: !!data.disabled, children: [data.icon && getIcon(data.icon), data.label && (jsx$1("div", { className: classNames$1(styles$F['label'], (_b = {},
                        _b[styles$F['with-icon']] = data.icon,
                        _b)), children: data.label }))] })));
    };
    var CustomSelectValue = function (props) {
        var _a;
        var data = props.data;
        return (jsxs(components.SingleValue, __assign({}, props, { className: styles$F['custom-value'], children: [data.icon && getIcon(data.icon), !showIconOnly && data.label && (jsx$1("div", { className: classNames$1(styles$F['label'], (_a = {},
                        _a[styles$F['with-icon']] = data.icon,
                        _a)), children: data.label }))] })));
    };
    return (jsxs("div", { ref: ref, className: classNames$1(styles$F.select, className), children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), React__default.createElement(allowCreate ? CreatableSelect$1 : StateManagedSelect$1, {
                menuPosition: menuPosition,
                minMenuHeight: minMenuHeight,
                maxMenuHeight: maxMenuHeight,
                menuPlacement: placement,
                isClearable: isClearable,
                className: inputClass,
                options: groupedOptions !== null && groupedOptions !== void 0 ? groupedOptions : options,
                isMulti: isMulti,
                isDisabled: disabled || readonly,
                isSearchable: isSearchable || allowCreate,
                placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '',
                noOptionsMessage: noOptionsElement ? function () { return noOptionsElement; } : undefined,
                value: selectedOption,
                defaultValue: defaultValue,
                blurInputOnSelect: true,
                onChange: function (option) {
                    return handleChange(option);
                },
                onBlur: onBlur,
                onFocus: onFocus,
                autoFocus: autoFocus,
                theme: function (theme) { return (__assign(__assign({}, theme), { colors: __assign(__assign({}, theme.colors), { primary25: 'var(--input-bg-color-active)', primary: 'var(--color-primary)' }) })); },
                formatCreateLabel: formatCreateLabel,
                styles: {
                    valueContainer: function (baseStyle, state) { return (__assign(__assign({}, baseStyle), { overflowY: 'auto', height: '100%', scrollbarColor: 'var(--color-grey-light) transparent', scrollbarWidth: 'thin' })); },
                    menu: function (baseStyle, state) { return ({
                        width: fitMenuToOptions ? 'fit-content' : '100%',
                    }); },
                },
                components: {
                    Menu: Menu,
                    MenuList: MenuList,
                    MultiValueContainer: MultiValueContainer,
                    Option: CustomSelectOption,
                    SingleValue: CustomSelectValue,
                },
                tabIndex: tabIndex,
            }), !dense && jsx$1("div", { className: styles$F['error-message'], children: error })] }));
};
var Select$1 = forwardRef(Select);

var DatePickerHeader = function (_a) {
    var _b;
    var date = _a.date, decreaseMonth = _a.decreaseMonth, increaseMonth = _a.increaseMonth, prevMonthButtonDisabled = _a.prevMonthButtonDisabled, nextMonthButtonDisabled = _a.nextMonthButtonDisabled, showMonthYearDropdown = _a.showMonthYearDropdown, changeMonth = _a.changeMonth, changeYear = _a.changeYear, _c = _a.startYear, startYear = _c === void 0 ? 100 : _c, _d = _a.endYear, endYear = _d === void 0 ? 3 : _d;
    var classes = classNames$1(styles$G['date-picker-header'], (_b = {},
        _b[styles$G['simple']] = !showMonthYearDropdown,
        _b[styles$G['select']] = showMonthYearDropdown,
        _b));
    var years = Array.from(range(getYear(new Date()) - startYear, getYear(new Date()) + endYear));
    var months = Array.from(range(0, 12, 1));
    var monthsOptions = useMemo(function () {
        return months.map(function (month) { return ({
            value: month,
            label: format(new Date(1970, month, 1), 'MMMM', { locale: de }),
        }); });
    }, [months]);
    var yearOptions = useMemo(function () {
        return years
            .sort()
            .reverse()
            .map(function (year) { return ({
            value: year,
            label: "".concat(year),
        }); });
    }, [years]);
    return (jsxs("header", { className: classes, children: [!showMonthYearDropdown && (jsxs(Fragment, { children: [jsx$1(Button$1, { hierarchy: 'tertiary', icon: jsx$1(SvgArrowHeadLeftIcon, { className: 'small' }), onClick: decreaseMonth, disabled: prevMonthButtonDisabled, type: 'button' }), "".concat(format(date, 'MMMM', { locale: de }), " ").concat(getYear(date)), jsx$1(Button$1, { hierarchy: 'tertiary', icon: jsx$1(SvgArrowHeadRightIcon, { className: 'small' }), onClick: increaseMonth, disabled: nextMonthButtonDisabled, type: 'button' })] })), showMonthYearDropdown && (jsxs(Fragment, { children: [jsx$1(Select$1, { menuPosition: 'absolute', name: 'month', dense: true, className: styles$G.month, options: monthsOptions, value: monthsOptions.find(function (month) {
                            return month.value === date.getMonth();
                        }), onChange: function (event) {
                            var e = event;
                            changeMonth(e === null || e === void 0 ? void 0 : e.value);
                        } }), jsx$1(Select$1, { value: yearOptions.find(function (year) {
                            return year.value === date.getFullYear();
                        }), menuPosition: 'absolute', name: 'year', dense: true, className: styles$G.year, options: yearOptions, onChange: function (event) {
                            var e = event;
                            changeYear(e === null || e === void 0 ? void 0 : e.value);
                        } })] }))] }));
};

var css_248z$E = ".Input-module_input__3JOBJ {\n  width: 100%;\n}\n\n.Input-module_input-wrapper__Zw7s9 {\n  display: flex;\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  height: var(--input-height-mobile);\n  width: 100%;\n  cursor: text;\n}\n\n.Input-module_wrapper__Szfnu {\n  border: none;\n  border-radius: var(--input-border-radius);\n  padding: 0 8px;\n  margin: 0;\n  width: 100%;\n  height: 100%;\n  line-height: 100%;\n}\n\n.Input-module_prefix__nwMRA,\n.Input-module_suffix__lLha6 {\n  color: var(--input-suffix-text-color);\n  font-size: var(--font-size-s);\n  height: 100%;\n  line-height: 100%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  background-color: var(--input-bg-color-default);\n  user-select: none;\n}\n\n.Input-module_prefix__nwMRA {\n  border-top-left-radius: var(--input-border-radius);\n  border-bottom-left-radius: var(--input-border-radius);\n  padding: 0 8px;\n}\n\n.Input-module_suffix__lLha6 {\n  border-top-right-radius: var(--input-border-radius);\n  border-bottom-right-radius: var(--input-border-radius);\n  padding: 0 8px;\n}\n\n@media (width >= 580px) {\n  .Input-module_input-wrapper__Zw7s9 {\n    height: calc(var(--input-height-desktop) - 2px);\n    line-height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_prevent-input__9HtZZ {\n  cursor: pointer;\n  caret-color: transparent;\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_filled__Hd19C {\n  color: var(--input-text-color-filled);\n  border-color: var(--input-border-color-filled);\n  background-color: var(--input-bg-color-filled);\n}\n\n.Input-module_input-wrapper__Zw7s9:hover {\n  color: var(--input-text-color-hover);\n  border-color: var(--input-border-color-hover);\n  background-color: var(--input-bg-color-hover);\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_has-focus__X3Zg9 {\n  color: var(--input-text-color-active);\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_has-menu__SBL8J.Input-module_has-focus__X3Zg9 {\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_read-only__j3L5b {\n  cursor: default;\n  color: var(--input-text-color-locked);\n  border-color: var(--input-border-color-locked);\n  background-color: var(--input-bg-color-locked);\n  caret-color: transparent;\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_disabled__oFXMQ {\n  cursor: not-allowed;\n  color: var(--input-text-color-disabled);\n  border-color: var(--input-border-color-disabled);\n  background-color: var(--input-bg-color-disabled);\n}\n\n.Input-module_input-wrapper__Zw7s9 .Input-module_prefix__nwMRA,\n.Input-module_input-wrapper__Zw7s9 .Input-module_suffix__lLha6,\n.Input-module_input-wrapper__Zw7s9 .Input-module_wrapper__Szfnu {\n  outline: none;\n  color: inherit;\n  border-color: inherit;\n  background-color: inherit;\n  cursor: inherit;\n}\n\n.Input-module_error-message__w8UXI {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n\n.Input-module_input-wrapper__Zw7s9.Input-module_error__2Em37 {\n  border-color: var(--input-text-color-error);\n}\n\ninput::-webkit-outer-spin-button,\ninput::-webkit-inner-spin-button {\n  appearance: none;\n  margin: 0;\n}\n\ninput[type='number'] {\n  appearance: textfield;\n}\n";
var styles$D = {"input":"Input-module_input__3JOBJ","input-wrapper":"Input-module_input-wrapper__Zw7s9","wrapper":"Input-module_wrapper__Szfnu","prefix":"Input-module_prefix__nwMRA","suffix":"Input-module_suffix__lLha6","prevent-input":"Input-module_prevent-input__9HtZZ","filled":"Input-module_filled__Hd19C","has-focus":"Input-module_has-focus__X3Zg9","has-menu":"Input-module_has-menu__SBL8J","read-only":"Input-module_read-only__j3L5b","disabled":"Input-module_disabled__oFXMQ","error-message":"Input-module_error-message__w8UXI","error":"Input-module_error__2Em37"};
styleInject(css_248z$E);

/**
 *
 * Input field with block label and optional tooltip info icon
 *
 * @param {string} name
 * @param {string} value
 * @param {string} label
 * @param {string} type
 * @param {boolean} disabled
 * @param {boolean} readonly
 * @param {boolean} required
 * @param {string} placeholder
 * @param {boolean} dense
 * @param {string} className
 * @param {string} error
 * @param {boolean} autoFocus
 * @param {string} tooltipText
 * @param {React.ReactNode} prefix
 * @param {React.ReactNode} suffix
 * @param {function} onChange
 * @param {function} onKeyDown
 * @param {function} onBlur
 * @param {function} onFocus
 * @param ref
 */
var Input = function (_a, ref) {
    var _b;
    var name = _a.name, value = _a.value, label = _a.label, _c = _a.type, type = _c === void 0 ? 'text' : _c, disabled = _a.disabled, readonly = _a.readonly, required = _a.required, placeholder = _a.placeholder, dense = _a.dense, className = _a.className, error = _a.error, autoFocus = _a.autoFocus, tooltipText = _a.tooltipText, _d = _a.labelCanOverflow, labelCanOverflow = _d === void 0 ? false : _d, _e = _a.bigHeaderGap, bigHeaderGap = _e === void 0 ? false : _e, _f = _a.tabIndex, tabIndex = _f === void 0 ? 0 : _f, prefix = _a.prefix, suffix = _a.suffix, _g = _a.hasMenu, hasMenu = _g === void 0 ? false : _g, _h = _a.preventInput, preventInput = _h === void 0 ? false : _h, _j = _a.selectOnFocus, selectOnFocus = _j === void 0 ? false : _j, min = _a.min, max = _a.max, step = _a.step, onChange = _a.onChange, onKeyDown = _a.onKeyDown, onBlur = _a.onBlur, onFocus = _a.onFocus, isFocused = _a.isFocused, _k = _a.autoComplete, autoComplete = _k === void 0 ? 'off' : _k;
    var inputRef = useRef(null);
    var _l = useState(false), isActive = _l[0], setIsActive = _l[1];
    var wrapperClass = classNames$1(styles$D['input-wrapper'], (_b = {},
        _b[styles$D['has-focus']] = isActive,
        _b[styles$D['disabled']] = disabled,
        _b[styles$D['read-only']] = readonly,
        _b[styles$D['filled']] = value,
        _b[styles$D['has-prefix']] = prefix,
        _b[styles$D['has-suffix']] = suffix,
        _b[styles$D['has-menu']] = hasMenu,
        _b[styles$D['prevent-input']] = preventInput,
        _b[styles$D['error']] = error,
        _b));
    useEffect(function () {
        var _a;
        if (isFocused)
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        setIsActive(isFocused !== null && isFocused !== void 0 ? isFocused : false);
    }, [inputRef, isFocused]);
    return (jsxs("div", { ref: ref, className: classNames$1(styles$D.input, className), onClick: function () {
            return !disabled && !readonly && inputRef.current && inputRef.current.focus();
        }, children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), jsxs("div", { className: wrapperClass, children: [prefix && jsx$1("div", { className: styles$D['prefix'], children: prefix }), jsx$1("input", { ref: inputRef, type: type, id: name, name: name, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '', disabled: disabled, className: styles$D.wrapper, required: required, onChange: onChange, onKeyDown: onKeyDown, onBlur: function (event) {
                            if (onBlur)
                                onBlur(event);
                            setIsActive(false);
                        }, onFocus: function (event) {
                            var _a;
                            if (onFocus)
                                onFocus(event);
                            if (selectOnFocus)
                                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.select();
                            setIsActive(true);
                        }, onWheel: function (e) { return e.target.blur(); }, value: value !== null && value !== void 0 ? value : '', autoFocus: autoFocus, readOnly: readonly, tabIndex: tabIndex, autoComplete: autoComplete !== null && autoComplete !== void 0 ? autoComplete : undefined, min: min, max: max, step: step }), suffix && jsx$1("div", { className: styles$D['suffix'], children: suffix })] }), !dense && jsx$1("div", { className: styles$D['error-message'], children: error })] }));
};
var Input$1 = forwardRef(Input);

registerLocale('de', de);
/**
 *
 * An input field with a date picker
 *
 * Base on React-Datepicker: https://reactdatepicker.com/

 * @param value
 * @param dateFormat
 * @param showMonthYearDropdown
 * @param showTodayButton
 * @param showIcon
 * @param labelCanOverflow
 * @param bigHeaderGap
 * @param locale
 * @param name
 * @param label
 * @param disabled
 * @param readonly
 * @param required
 * @param error
 * @param autoFocus
 * @param placeholder
 * @param dense
 * @param tooltipText
 * @param tabIndex
 * @param onChange
 * @param onBlur
 * @param onFocus
 * @param className
 * @param ref
 */
var DatePicker = function (_a, ref) {
    var _b;
    var value = _a.value, maxDate = _a.maxDate, minDate = _a.minDate, _c = _a.dateFormat, dateFormat = _c === void 0 ? 'dd.MM.yyyy' : _c, showMonthYearDropdown = _a.showMonthYearDropdown, _d = _a.showTodayButton, showTodayButton = _d === void 0 ? true : _d, _e = _a.showIcon, showIcon = _e === void 0 ? true : _e, _f = _a.labelCanOverflow, labelCanOverflow = _f === void 0 ? false : _f, _g = _a.bigHeaderGap, bigHeaderGap = _g === void 0 ? false : _g, _h = _a.locale, locale = _h === void 0 ? 'de' : _h, name = _a.name, label = _a.label, disabled = _a.disabled, readonly = _a.readonly, required = _a.required, error = _a.error, autoFocus = _a.autoFocus, placeholder = _a.placeholder, dense = _a.dense, tooltipText = _a.tooltipText, _j = _a.tabIndex, tabIndex = _j === void 0 ? 0 : _j, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, className = _a.className;
    var _k = useState(false), isOpen = _k[0], setIsOpen = _k[1];
    var inputClass = classNames$1(styles$H['input'], (_b = {},
        _b[styles$H['error']] = error,
        _b[styles$H['disabled']] = disabled,
        _b));
    return (jsxs("div", { ref: ref, className: classNames$1(styles$H['date-picker'], className), children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), jsx$1(DatePicker$2, { minDate: minDate, maxDate: maxDate, open: isOpen, locale: locale, dateFormat: dateFormat, autoFocus: autoFocus, disabled: disabled, required: required, selected: value ? value : null, onChange: function (date) { return onChange(date); }, onBlur: onBlur, onFocus: onFocus, onSelect: function (_date, event) {
                    if (event === null || event === void 0 ? void 0 : event.target)
                        setIsOpen(false);
                }, onClickOutside: function () { return setIsOpen(false); }, showPopperArrow: false, popperClassName: styles$H.popper, popperProps: {
                    strategy: 'fixed',
                }, closeOnScroll: function (e) { return e.target === document; }, disabledKeyboardNavigation: false, calendarClassName: styles$H.calendar, className: inputClass, todayButton: showTodayButton ? (jsxs("div", { className: styles$H.today, children: ["Heute ausw\u00E4hlen (", format(new Date(), dateFormat), ")"] })) : undefined, placeholderText: placeholder !== null && placeholder !== void 0 ? placeholder : '', customInput: jsx$1(Input$1, { name: name, hasMenu: isOpen, preventInput: true, selectOnFocus: true, dense: true, readonly: readonly, disabled: disabled, suffix: showIcon ? (jsx$1(SvgCalendarIcon, { strokeWidth: 2, onClick: function () {
                            if (!disabled && !readonly)
                                setIsOpen(!isOpen);
                        } })) : undefined, tabIndex: tabIndex }), renderCustomHeader: function (props) {
                    return (jsx$1(DatePickerHeader, __assign({}, props, { showMonthYearDropdown: showMonthYearDropdown })));
                }, enableTabLoop: false, adjustDateOnChange: true, preventOpenOnFocus: readonly }), !dense && jsx$1("div", { className: styles$H['error-message'], children: error })] }));
};
var DatePicker$1 = forwardRef(DatePicker);

function createCollection(name) {
  const PROVIDER_NAME = name + "CollectionProvider";
  const [createCollectionContext, createCollectionScope] = createContextScope(PROVIDER_NAME);
  const [CollectionProviderImpl, useCollectionContext] = createCollectionContext(
    PROVIDER_NAME,
    { collectionRef: { current: null }, itemMap: /* @__PURE__ */ new Map() }
  );
  const CollectionProvider = (props) => {
    const { scope, children } = props;
    const ref = React__default.useRef(null);
    const itemMap = React__default.useRef(/* @__PURE__ */ new Map()).current;
    return /* @__PURE__ */ jsx$1(CollectionProviderImpl, { scope, itemMap, collectionRef: ref, children });
  };
  CollectionProvider.displayName = PROVIDER_NAME;
  const COLLECTION_SLOT_NAME = name + "CollectionSlot";
  const CollectionSlot = React__default.forwardRef(
    (props, forwardedRef) => {
      const { scope, children } = props;
      const context = useCollectionContext(COLLECTION_SLOT_NAME, scope);
      const composedRefs = useComposedRefs(forwardedRef, context.collectionRef);
      return /* @__PURE__ */ jsx$1(Slot, { ref: composedRefs, children });
    }
  );
  CollectionSlot.displayName = COLLECTION_SLOT_NAME;
  const ITEM_SLOT_NAME = name + "CollectionItemSlot";
  const ITEM_DATA_ATTR = "data-radix-collection-item";
  const CollectionItemSlot = React__default.forwardRef(
    (props, forwardedRef) => {
      const { scope, children, ...itemData } = props;
      const ref = React__default.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      const context = useCollectionContext(ITEM_SLOT_NAME, scope);
      React__default.useEffect(() => {
        context.itemMap.set(ref, { ref, ...itemData });
        return () => void context.itemMap.delete(ref);
      });
      return /* @__PURE__ */ jsx$1(Slot, { ...{ [ITEM_DATA_ATTR]: "" }, ref: composedRefs, children });
    }
  );
  CollectionItemSlot.displayName = ITEM_SLOT_NAME;
  function useCollection(scope) {
    const context = useCollectionContext(name + "CollectionConsumer", scope);
    const getItems = React__default.useCallback(() => {
      const collectionNode = context.collectionRef.current;
      if (!collectionNode) return [];
      const orderedNodes = Array.from(collectionNode.querySelectorAll(`[${ITEM_DATA_ATTR}]`));
      const items = Array.from(context.itemMap.values());
      const orderedItems = items.sort(
        (a, b) => orderedNodes.indexOf(a.ref.current) - orderedNodes.indexOf(b.ref.current)
      );
      return orderedItems;
    }, [context.collectionRef, context.itemMap]);
    return getItems;
  }
  return [
    { Provider: CollectionProvider, Slot: CollectionSlot, ItemSlot: CollectionItemSlot },
    useCollection,
    createCollectionScope
  ];
}

// packages/react/direction/src/Direction.tsx
var DirectionContext = React.createContext(void 0);
function useDirection(localDir) {
  const globalDir = React.useContext(DirectionContext);
  return localDir || globalDir || "ltr";
}

var ENTRY_FOCUS = "rovingFocusGroup.onEntryFocus";
var EVENT_OPTIONS = { bubbles: false, cancelable: true };
var GROUP_NAME$3 = "RovingFocusGroup";
var [Collection$1, useCollection$1, createCollectionScope$1] = createCollection(GROUP_NAME$3);
var [createRovingFocusGroupContext, createRovingFocusGroupScope] = createContextScope(
  GROUP_NAME$3,
  [createCollectionScope$1]
);
var [RovingFocusProvider, useRovingFocusContext] = createRovingFocusGroupContext(GROUP_NAME$3);
var RovingFocusGroup = React.forwardRef(
  (props, forwardedRef) => {
    return /* @__PURE__ */ jsx$1(Collection$1.Provider, { scope: props.__scopeRovingFocusGroup, children: /* @__PURE__ */ jsx$1(Collection$1.Slot, { scope: props.__scopeRovingFocusGroup, children: /* @__PURE__ */ jsx$1(RovingFocusGroupImpl, { ...props, ref: forwardedRef }) }) });
  }
);
RovingFocusGroup.displayName = GROUP_NAME$3;
var RovingFocusGroupImpl = React.forwardRef((props, forwardedRef) => {
  const {
    __scopeRovingFocusGroup,
    orientation,
    loop = false,
    dir,
    currentTabStopId: currentTabStopIdProp,
    defaultCurrentTabStopId,
    onCurrentTabStopIdChange,
    onEntryFocus,
    preventScrollOnEntryFocus = false,
    ...groupProps
  } = props;
  const ref = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, ref);
  const direction = useDirection(dir);
  const [currentTabStopId = null, setCurrentTabStopId] = useControllableState({
    prop: currentTabStopIdProp,
    defaultProp: defaultCurrentTabStopId,
    onChange: onCurrentTabStopIdChange
  });
  const [isTabbingBackOut, setIsTabbingBackOut] = React.useState(false);
  const handleEntryFocus = useCallbackRef$1(onEntryFocus);
  const getItems = useCollection$1(__scopeRovingFocusGroup);
  const isClickFocusRef = React.useRef(false);
  const [focusableItemsCount, setFocusableItemsCount] = React.useState(0);
  React.useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener(ENTRY_FOCUS, handleEntryFocus);
      return () => node.removeEventListener(ENTRY_FOCUS, handleEntryFocus);
    }
  }, [handleEntryFocus]);
  return /* @__PURE__ */ jsx$1(
    RovingFocusProvider,
    {
      scope: __scopeRovingFocusGroup,
      orientation,
      dir: direction,
      loop,
      currentTabStopId,
      onItemFocus: React.useCallback(
        (tabStopId) => setCurrentTabStopId(tabStopId),
        [setCurrentTabStopId]
      ),
      onItemShiftTab: React.useCallback(() => setIsTabbingBackOut(true), []),
      onFocusableItemAdd: React.useCallback(
        () => setFocusableItemsCount((prevCount) => prevCount + 1),
        []
      ),
      onFocusableItemRemove: React.useCallback(
        () => setFocusableItemsCount((prevCount) => prevCount - 1),
        []
      ),
      children: /* @__PURE__ */ jsx$1(
        Primitive.div,
        {
          tabIndex: isTabbingBackOut || focusableItemsCount === 0 ? -1 : 0,
          "data-orientation": orientation,
          ...groupProps,
          ref: composedRefs,
          style: { outline: "none", ...props.style },
          onMouseDown: composeEventHandlers(props.onMouseDown, () => {
            isClickFocusRef.current = true;
          }),
          onFocus: composeEventHandlers(props.onFocus, (event) => {
            const isKeyboardFocus = !isClickFocusRef.current;
            if (event.target === event.currentTarget && isKeyboardFocus && !isTabbingBackOut) {
              const entryFocusEvent = new CustomEvent(ENTRY_FOCUS, EVENT_OPTIONS);
              event.currentTarget.dispatchEvent(entryFocusEvent);
              if (!entryFocusEvent.defaultPrevented) {
                const items = getItems().filter((item) => item.focusable);
                const activeItem = items.find((item) => item.active);
                const currentItem = items.find((item) => item.id === currentTabStopId);
                const candidateItems = [activeItem, currentItem, ...items].filter(
                  Boolean
                );
                const candidateNodes = candidateItems.map((item) => item.ref.current);
                focusFirst$1(candidateNodes, preventScrollOnEntryFocus);
              }
            }
            isClickFocusRef.current = false;
          }),
          onBlur: composeEventHandlers(props.onBlur, () => setIsTabbingBackOut(false))
        }
      )
    }
  );
});
var ITEM_NAME$3 = "RovingFocusGroupItem";
var RovingFocusGroupItem = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopeRovingFocusGroup,
      focusable = true,
      active = false,
      tabStopId,
      ...itemProps
    } = props;
    const autoId = useId$1();
    const id = tabStopId || autoId;
    const context = useRovingFocusContext(ITEM_NAME$3, __scopeRovingFocusGroup);
    const isCurrentTabStop = context.currentTabStopId === id;
    const getItems = useCollection$1(__scopeRovingFocusGroup);
    const { onFocusableItemAdd, onFocusableItemRemove } = context;
    React.useEffect(() => {
      if (focusable) {
        onFocusableItemAdd();
        return () => onFocusableItemRemove();
      }
    }, [focusable, onFocusableItemAdd, onFocusableItemRemove]);
    return /* @__PURE__ */ jsx$1(
      Collection$1.ItemSlot,
      {
        scope: __scopeRovingFocusGroup,
        id,
        focusable,
        active,
        children: /* @__PURE__ */ jsx$1(
          Primitive.span,
          {
            tabIndex: isCurrentTabStop ? 0 : -1,
            "data-orientation": context.orientation,
            ...itemProps,
            ref: forwardedRef,
            onMouseDown: composeEventHandlers(props.onMouseDown, (event) => {
              if (!focusable) event.preventDefault();
              else context.onItemFocus(id);
            }),
            onFocus: composeEventHandlers(props.onFocus, () => context.onItemFocus(id)),
            onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
              if (event.key === "Tab" && event.shiftKey) {
                context.onItemShiftTab();
                return;
              }
              if (event.target !== event.currentTarget) return;
              const focusIntent = getFocusIntent(event, context.orientation, context.dir);
              if (focusIntent !== void 0) {
                if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
                event.preventDefault();
                const items = getItems().filter((item) => item.focusable);
                let candidateNodes = items.map((item) => item.ref.current);
                if (focusIntent === "last") candidateNodes.reverse();
                else if (focusIntent === "prev" || focusIntent === "next") {
                  if (focusIntent === "prev") candidateNodes.reverse();
                  const currentIndex = candidateNodes.indexOf(event.currentTarget);
                  candidateNodes = context.loop ? wrapArray$1(candidateNodes, currentIndex + 1) : candidateNodes.slice(currentIndex + 1);
                }
                setTimeout(() => focusFirst$1(candidateNodes));
              }
            })
          }
        )
      }
    );
  }
);
RovingFocusGroupItem.displayName = ITEM_NAME$3;
var MAP_KEY_TO_FOCUS_INTENT = {
  ArrowLeft: "prev",
  ArrowUp: "prev",
  ArrowRight: "next",
  ArrowDown: "next",
  PageUp: "first",
  Home: "first",
  PageDown: "last",
  End: "last"
};
function getDirectionAwareKey(key, dir) {
  if (dir !== "rtl") return key;
  return key === "ArrowLeft" ? "ArrowRight" : key === "ArrowRight" ? "ArrowLeft" : key;
}
function getFocusIntent(event, orientation, dir) {
  const key = getDirectionAwareKey(event.key, dir);
  if (orientation === "vertical" && ["ArrowLeft", "ArrowRight"].includes(key)) return void 0;
  if (orientation === "horizontal" && ["ArrowUp", "ArrowDown"].includes(key)) return void 0;
  return MAP_KEY_TO_FOCUS_INTENT[key];
}
function focusFirst$1(candidates, preventScroll = false) {
  const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
  for (const candidate of candidates) {
    if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) return;
    candidate.focus({ preventScroll });
    if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) return;
  }
}
function wrapArray$1(array, startIndex) {
  return array.map((_, index) => array[(startIndex + index) % array.length]);
}
var Root = RovingFocusGroup;
var Item = RovingFocusGroupItem;

var SELECTION_KEYS = ["Enter", " "];
var FIRST_KEYS = ["ArrowDown", "PageUp", "Home"];
var LAST_KEYS = ["ArrowUp", "PageDown", "End"];
var FIRST_LAST_KEYS = [...FIRST_KEYS, ...LAST_KEYS];
var SUB_OPEN_KEYS = {
  ltr: [...SELECTION_KEYS, "ArrowRight"],
  rtl: [...SELECTION_KEYS, "ArrowLeft"]
};
var SUB_CLOSE_KEYS = {
  ltr: ["ArrowLeft"],
  rtl: ["ArrowRight"]
};
var MENU_NAME = "Menu";
var [Collection, useCollection, createCollectionScope] = createCollection(MENU_NAME);
var [createMenuContext, createMenuScope] = createContextScope(MENU_NAME, [
  createCollectionScope,
  createPopperScope,
  createRovingFocusGroupScope
]);
var usePopperScope = createPopperScope();
var useRovingFocusGroupScope$1 = createRovingFocusGroupScope();
var [MenuProvider, useMenuContext] = createMenuContext(MENU_NAME);
var [MenuRootProvider, useMenuRootContext] = createMenuContext(MENU_NAME);
var Menu = (props) => {
  const { __scopeMenu, open = false, children, dir, onOpenChange, modal = true } = props;
  const popperScope = usePopperScope(__scopeMenu);
  const [content, setContent] = React.useState(null);
  const isUsingKeyboardRef = React.useRef(false);
  const handleOpenChange = useCallbackRef$1(onOpenChange);
  const direction = useDirection(dir);
  React.useEffect(() => {
    const handleKeyDown = () => {
      isUsingKeyboardRef.current = true;
      document.addEventListener("pointerdown", handlePointer, { capture: true, once: true });
      document.addEventListener("pointermove", handlePointer, { capture: true, once: true });
    };
    const handlePointer = () => isUsingKeyboardRef.current = false;
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("pointerdown", handlePointer, { capture: true });
      document.removeEventListener("pointermove", handlePointer, { capture: true });
    };
  }, []);
  return /* @__PURE__ */ jsx$1(Root2$3, { ...popperScope, children: /* @__PURE__ */ jsx$1(
    MenuProvider,
    {
      scope: __scopeMenu,
      open,
      onOpenChange: handleOpenChange,
      content,
      onContentChange: setContent,
      children: /* @__PURE__ */ jsx$1(
        MenuRootProvider,
        {
          scope: __scopeMenu,
          onClose: React.useCallback(() => handleOpenChange(false), [handleOpenChange]),
          isUsingKeyboardRef,
          dir: direction,
          modal,
          children
        }
      )
    }
  ) });
};
Menu.displayName = MENU_NAME;
var ANCHOR_NAME = "MenuAnchor";
var MenuAnchor = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, ...anchorProps } = props;
    const popperScope = usePopperScope(__scopeMenu);
    return /* @__PURE__ */ jsx$1(Anchor, { ...popperScope, ...anchorProps, ref: forwardedRef });
  }
);
MenuAnchor.displayName = ANCHOR_NAME;
var PORTAL_NAME$2 = "MenuPortal";
var [PortalProvider, usePortalContext] = createMenuContext(PORTAL_NAME$2, {
  forceMount: void 0
});
var MenuPortal = (props) => {
  const { __scopeMenu, forceMount, children, container } = props;
  const context = useMenuContext(PORTAL_NAME$2, __scopeMenu);
  return /* @__PURE__ */ jsx$1(PortalProvider, { scope: __scopeMenu, forceMount, children: /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(Portal$4, { asChild: true, container, children }) }) });
};
MenuPortal.displayName = PORTAL_NAME$2;
var CONTENT_NAME$3 = "MenuContent";
var [MenuContentProvider, useMenuContentContext] = createMenuContext(CONTENT_NAME$3);
var MenuContent = React.forwardRef(
  (props, forwardedRef) => {
    const portalContext = usePortalContext(CONTENT_NAME$3, props.__scopeMenu);
    const { forceMount = portalContext.forceMount, ...contentProps } = props;
    const context = useMenuContext(CONTENT_NAME$3, props.__scopeMenu);
    const rootContext = useMenuRootContext(CONTENT_NAME$3, props.__scopeMenu);
    return /* @__PURE__ */ jsx$1(Collection.Provider, { scope: props.__scopeMenu, children: /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(Collection.Slot, { scope: props.__scopeMenu, children: rootContext.modal ? /* @__PURE__ */ jsx$1(MenuRootContentModal, { ...contentProps, ref: forwardedRef }) : /* @__PURE__ */ jsx$1(MenuRootContentNonModal, { ...contentProps, ref: forwardedRef }) }) }) });
  }
);
var MenuRootContentModal = React.forwardRef(
  (props, forwardedRef) => {
    const context = useMenuContext(CONTENT_NAME$3, props.__scopeMenu);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    React.useEffect(() => {
      const content = ref.current;
      if (content) return hideOthers(content);
    }, []);
    return /* @__PURE__ */ jsx$1(
      MenuContentImpl,
      {
        ...props,
        ref: composedRefs,
        trapFocus: context.open,
        disableOutsidePointerEvents: context.open,
        disableOutsideScroll: true,
        onFocusOutside: composeEventHandlers(
          props.onFocusOutside,
          (event) => event.preventDefault(),
          { checkForDefaultPrevented: false }
        ),
        onDismiss: () => context.onOpenChange(false)
      }
    );
  }
);
var MenuRootContentNonModal = React.forwardRef((props, forwardedRef) => {
  const context = useMenuContext(CONTENT_NAME$3, props.__scopeMenu);
  return /* @__PURE__ */ jsx$1(
    MenuContentImpl,
    {
      ...props,
      ref: forwardedRef,
      trapFocus: false,
      disableOutsidePointerEvents: false,
      disableOutsideScroll: false,
      onDismiss: () => context.onOpenChange(false)
    }
  );
});
var MenuContentImpl = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopeMenu,
      loop = false,
      trapFocus,
      onOpenAutoFocus,
      onCloseAutoFocus,
      disableOutsidePointerEvents,
      onEntryFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      onDismiss,
      disableOutsideScroll,
      ...contentProps
    } = props;
    const context = useMenuContext(CONTENT_NAME$3, __scopeMenu);
    const rootContext = useMenuRootContext(CONTENT_NAME$3, __scopeMenu);
    const popperScope = usePopperScope(__scopeMenu);
    const rovingFocusGroupScope = useRovingFocusGroupScope$1(__scopeMenu);
    const getItems = useCollection(__scopeMenu);
    const [currentItemId, setCurrentItemId] = React.useState(null);
    const contentRef = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, contentRef, context.onContentChange);
    const timerRef = React.useRef(0);
    const searchRef = React.useRef("");
    const pointerGraceTimerRef = React.useRef(0);
    const pointerGraceIntentRef = React.useRef(null);
    const pointerDirRef = React.useRef("right");
    const lastPointerXRef = React.useRef(0);
    const ScrollLockWrapper = disableOutsideScroll ? ReactRemoveScroll : React.Fragment;
    const scrollLockWrapperProps = disableOutsideScroll ? { as: Slot, allowPinchZoom: true } : void 0;
    const handleTypeaheadSearch = (key) => {
      const search = searchRef.current + key;
      const items = getItems().filter((item) => !item.disabled);
      const currentItem = document.activeElement;
      const currentMatch = items.find((item) => item.ref.current === currentItem)?.textValue;
      const values = items.map((item) => item.textValue);
      const nextMatch = getNextMatch(values, search, currentMatch);
      const newItem = items.find((item) => item.textValue === nextMatch)?.ref.current;
      (function updateSearch(value) {
        searchRef.current = value;
        window.clearTimeout(timerRef.current);
        if (value !== "") timerRef.current = window.setTimeout(() => updateSearch(""), 1e3);
      })(search);
      if (newItem) {
        setTimeout(() => newItem.focus());
      }
    };
    React.useEffect(() => {
      return () => window.clearTimeout(timerRef.current);
    }, []);
    useFocusGuards();
    const isPointerMovingToSubmenu = React.useCallback((event) => {
      const isMovingTowards = pointerDirRef.current === pointerGraceIntentRef.current?.side;
      return isMovingTowards && isPointerInGraceArea(event, pointerGraceIntentRef.current?.area);
    }, []);
    return /* @__PURE__ */ jsx$1(
      MenuContentProvider,
      {
        scope: __scopeMenu,
        searchRef,
        onItemEnter: React.useCallback(
          (event) => {
            if (isPointerMovingToSubmenu(event)) event.preventDefault();
          },
          [isPointerMovingToSubmenu]
        ),
        onItemLeave: React.useCallback(
          (event) => {
            if (isPointerMovingToSubmenu(event)) return;
            contentRef.current?.focus();
            setCurrentItemId(null);
          },
          [isPointerMovingToSubmenu]
        ),
        onTriggerLeave: React.useCallback(
          (event) => {
            if (isPointerMovingToSubmenu(event)) event.preventDefault();
          },
          [isPointerMovingToSubmenu]
        ),
        pointerGraceTimerRef,
        onPointerGraceIntentChange: React.useCallback((intent) => {
          pointerGraceIntentRef.current = intent;
        }, []),
        children: /* @__PURE__ */ jsx$1(ScrollLockWrapper, { ...scrollLockWrapperProps, children: /* @__PURE__ */ jsx$1(
          FocusScope,
          {
            asChild: true,
            trapped: trapFocus,
            onMountAutoFocus: composeEventHandlers(onOpenAutoFocus, (event) => {
              event.preventDefault();
              contentRef.current?.focus({ preventScroll: true });
            }),
            onUnmountAutoFocus: onCloseAutoFocus,
            children: /* @__PURE__ */ jsx$1(
              DismissableLayer,
              {
                asChild: true,
                disableOutsidePointerEvents,
                onEscapeKeyDown,
                onPointerDownOutside,
                onFocusOutside,
                onInteractOutside,
                onDismiss,
                children: /* @__PURE__ */ jsx$1(
                  Root,
                  {
                    asChild: true,
                    ...rovingFocusGroupScope,
                    dir: rootContext.dir,
                    orientation: "vertical",
                    loop,
                    currentTabStopId: currentItemId,
                    onCurrentTabStopIdChange: setCurrentItemId,
                    onEntryFocus: composeEventHandlers(onEntryFocus, (event) => {
                      if (!rootContext.isUsingKeyboardRef.current) event.preventDefault();
                    }),
                    preventScrollOnEntryFocus: true,
                    children: /* @__PURE__ */ jsx$1(
                      Content$1,
                      {
                        role: "menu",
                        "aria-orientation": "vertical",
                        "data-state": getOpenState(context.open),
                        "data-radix-menu-content": "",
                        dir: rootContext.dir,
                        ...popperScope,
                        ...contentProps,
                        ref: composedRefs,
                        style: { outline: "none", ...contentProps.style },
                        onKeyDown: composeEventHandlers(contentProps.onKeyDown, (event) => {
                          const target = event.target;
                          const isKeyDownInside = target.closest("[data-radix-menu-content]") === event.currentTarget;
                          const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
                          const isCharacterKey = event.key.length === 1;
                          if (isKeyDownInside) {
                            if (event.key === "Tab") event.preventDefault();
                            if (!isModifierKey && isCharacterKey) handleTypeaheadSearch(event.key);
                          }
                          const content = contentRef.current;
                          if (event.target !== content) return;
                          if (!FIRST_LAST_KEYS.includes(event.key)) return;
                          event.preventDefault();
                          const items = getItems().filter((item) => !item.disabled);
                          const candidateNodes = items.map((item) => item.ref.current);
                          if (LAST_KEYS.includes(event.key)) candidateNodes.reverse();
                          focusFirst(candidateNodes);
                        }),
                        onBlur: composeEventHandlers(props.onBlur, (event) => {
                          if (!event.currentTarget.contains(event.target)) {
                            window.clearTimeout(timerRef.current);
                            searchRef.current = "";
                          }
                        }),
                        onPointerMove: composeEventHandlers(
                          props.onPointerMove,
                          whenMouse((event) => {
                            const target = event.target;
                            const pointerXHasChanged = lastPointerXRef.current !== event.clientX;
                            if (event.currentTarget.contains(target) && pointerXHasChanged) {
                              const newDir = event.clientX > lastPointerXRef.current ? "right" : "left";
                              pointerDirRef.current = newDir;
                              lastPointerXRef.current = event.clientX;
                            }
                          })
                        )
                      }
                    )
                  }
                )
              }
            )
          }
        ) })
      }
    );
  }
);
MenuContent.displayName = CONTENT_NAME$3;
var GROUP_NAME$2 = "MenuGroup";
var MenuGroup = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, ...groupProps } = props;
    return /* @__PURE__ */ jsx$1(Primitive.div, { role: "group", ...groupProps, ref: forwardedRef });
  }
);
MenuGroup.displayName = GROUP_NAME$2;
var LABEL_NAME$2 = "MenuLabel";
var MenuLabel = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, ...labelProps } = props;
    return /* @__PURE__ */ jsx$1(Primitive.div, { ...labelProps, ref: forwardedRef });
  }
);
MenuLabel.displayName = LABEL_NAME$2;
var ITEM_NAME$2 = "MenuItem";
var ITEM_SELECT = "menu.itemSelect";
var MenuItem = React.forwardRef(
  (props, forwardedRef) => {
    const { disabled = false, onSelect, ...itemProps } = props;
    const ref = React.useRef(null);
    const rootContext = useMenuRootContext(ITEM_NAME$2, props.__scopeMenu);
    const contentContext = useMenuContentContext(ITEM_NAME$2, props.__scopeMenu);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    const isPointerDownRef = React.useRef(false);
    const handleSelect = () => {
      const menuItem = ref.current;
      if (!disabled && menuItem) {
        const itemSelectEvent = new CustomEvent(ITEM_SELECT, { bubbles: true, cancelable: true });
        menuItem.addEventListener(ITEM_SELECT, (event) => onSelect?.(event), { once: true });
        dispatchDiscreteCustomEvent(menuItem, itemSelectEvent);
        if (itemSelectEvent.defaultPrevented) {
          isPointerDownRef.current = false;
        } else {
          rootContext.onClose();
        }
      }
    };
    return /* @__PURE__ */ jsx$1(
      MenuItemImpl,
      {
        ...itemProps,
        ref: composedRefs,
        disabled,
        onClick: composeEventHandlers(props.onClick, handleSelect),
        onPointerDown: (event) => {
          props.onPointerDown?.(event);
          isPointerDownRef.current = true;
        },
        onPointerUp: composeEventHandlers(props.onPointerUp, (event) => {
          if (!isPointerDownRef.current) event.currentTarget?.click();
        }),
        onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
          const isTypingAhead = contentContext.searchRef.current !== "";
          if (disabled || isTypingAhead && event.key === " ") return;
          if (SELECTION_KEYS.includes(event.key)) {
            event.currentTarget.click();
            event.preventDefault();
          }
        })
      }
    );
  }
);
MenuItem.displayName = ITEM_NAME$2;
var MenuItemImpl = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, disabled = false, textValue, ...itemProps } = props;
    const contentContext = useMenuContentContext(ITEM_NAME$2, __scopeMenu);
    const rovingFocusGroupScope = useRovingFocusGroupScope$1(__scopeMenu);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    const [isFocused, setIsFocused] = React.useState(false);
    const [textContent, setTextContent] = React.useState("");
    React.useEffect(() => {
      const menuItem = ref.current;
      if (menuItem) {
        setTextContent((menuItem.textContent ?? "").trim());
      }
    }, [itemProps.children]);
    return /* @__PURE__ */ jsx$1(
      Collection.ItemSlot,
      {
        scope: __scopeMenu,
        disabled,
        textValue: textValue ?? textContent,
        children: /* @__PURE__ */ jsx$1(Item, { asChild: true, ...rovingFocusGroupScope, focusable: !disabled, children: /* @__PURE__ */ jsx$1(
          Primitive.div,
          {
            role: "menuitem",
            "data-highlighted": isFocused ? "" : void 0,
            "aria-disabled": disabled || void 0,
            "data-disabled": disabled ? "" : void 0,
            ...itemProps,
            ref: composedRefs,
            onPointerMove: composeEventHandlers(
              props.onPointerMove,
              whenMouse((event) => {
                if (disabled) {
                  contentContext.onItemLeave(event);
                } else {
                  contentContext.onItemEnter(event);
                  if (!event.defaultPrevented) {
                    const item = event.currentTarget;
                    item.focus({ preventScroll: true });
                  }
                }
              })
            ),
            onPointerLeave: composeEventHandlers(
              props.onPointerLeave,
              whenMouse((event) => contentContext.onItemLeave(event))
            ),
            onFocus: composeEventHandlers(props.onFocus, () => setIsFocused(true)),
            onBlur: composeEventHandlers(props.onBlur, () => setIsFocused(false))
          }
        ) })
      }
    );
  }
);
var CHECKBOX_ITEM_NAME$2 = "MenuCheckboxItem";
var MenuCheckboxItem = React.forwardRef(
  (props, forwardedRef) => {
    const { checked = false, onCheckedChange, ...checkboxItemProps } = props;
    return /* @__PURE__ */ jsx$1(ItemIndicatorProvider, { scope: props.__scopeMenu, checked, children: /* @__PURE__ */ jsx$1(
      MenuItem,
      {
        role: "menuitemcheckbox",
        "aria-checked": isIndeterminate(checked) ? "mixed" : checked,
        ...checkboxItemProps,
        ref: forwardedRef,
        "data-state": getCheckedState(checked),
        onSelect: composeEventHandlers(
          checkboxItemProps.onSelect,
          () => onCheckedChange?.(isIndeterminate(checked) ? true : !checked),
          { checkForDefaultPrevented: false }
        )
      }
    ) });
  }
);
MenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME$2;
var RADIO_GROUP_NAME$2 = "MenuRadioGroup";
var [RadioGroupProvider, useRadioGroupContext] = createMenuContext(
  RADIO_GROUP_NAME$2,
  { value: void 0, onValueChange: () => {
  } }
);
var MenuRadioGroup = React.forwardRef(
  (props, forwardedRef) => {
    const { value, onValueChange, ...groupProps } = props;
    const handleValueChange = useCallbackRef$1(onValueChange);
    return /* @__PURE__ */ jsx$1(RadioGroupProvider, { scope: props.__scopeMenu, value, onValueChange: handleValueChange, children: /* @__PURE__ */ jsx$1(MenuGroup, { ...groupProps, ref: forwardedRef }) });
  }
);
MenuRadioGroup.displayName = RADIO_GROUP_NAME$2;
var RADIO_ITEM_NAME$2 = "MenuRadioItem";
var MenuRadioItem = React.forwardRef(
  (props, forwardedRef) => {
    const { value, ...radioItemProps } = props;
    const context = useRadioGroupContext(RADIO_ITEM_NAME$2, props.__scopeMenu);
    const checked = value === context.value;
    return /* @__PURE__ */ jsx$1(ItemIndicatorProvider, { scope: props.__scopeMenu, checked, children: /* @__PURE__ */ jsx$1(
      MenuItem,
      {
        role: "menuitemradio",
        "aria-checked": checked,
        ...radioItemProps,
        ref: forwardedRef,
        "data-state": getCheckedState(checked),
        onSelect: composeEventHandlers(
          radioItemProps.onSelect,
          () => context.onValueChange?.(value),
          { checkForDefaultPrevented: false }
        )
      }
    ) });
  }
);
MenuRadioItem.displayName = RADIO_ITEM_NAME$2;
var ITEM_INDICATOR_NAME = "MenuItemIndicator";
var [ItemIndicatorProvider, useItemIndicatorContext] = createMenuContext(
  ITEM_INDICATOR_NAME,
  { checked: false }
);
var MenuItemIndicator = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, forceMount, ...itemIndicatorProps } = props;
    const indicatorContext = useItemIndicatorContext(ITEM_INDICATOR_NAME, __scopeMenu);
    return /* @__PURE__ */ jsx$1(
      Presence,
      {
        present: forceMount || isIndeterminate(indicatorContext.checked) || indicatorContext.checked === true,
        children: /* @__PURE__ */ jsx$1(
          Primitive.span,
          {
            ...itemIndicatorProps,
            ref: forwardedRef,
            "data-state": getCheckedState(indicatorContext.checked)
          }
        )
      }
    );
  }
);
MenuItemIndicator.displayName = ITEM_INDICATOR_NAME;
var SEPARATOR_NAME$2 = "MenuSeparator";
var MenuSeparator = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, ...separatorProps } = props;
    return /* @__PURE__ */ jsx$1(
      Primitive.div,
      {
        role: "separator",
        "aria-orientation": "horizontal",
        ...separatorProps,
        ref: forwardedRef
      }
    );
  }
);
MenuSeparator.displayName = SEPARATOR_NAME$2;
var ARROW_NAME$2 = "MenuArrow";
var MenuArrow = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeMenu, ...arrowProps } = props;
    const popperScope = usePopperScope(__scopeMenu);
    return /* @__PURE__ */ jsx$1(Arrow, { ...popperScope, ...arrowProps, ref: forwardedRef });
  }
);
MenuArrow.displayName = ARROW_NAME$2;
var SUB_NAME$1 = "MenuSub";
var [MenuSubProvider, useMenuSubContext] = createMenuContext(SUB_NAME$1);
var MenuSub = (props) => {
  const { __scopeMenu, children, open = false, onOpenChange } = props;
  const parentMenuContext = useMenuContext(SUB_NAME$1, __scopeMenu);
  const popperScope = usePopperScope(__scopeMenu);
  const [trigger, setTrigger] = React.useState(null);
  const [content, setContent] = React.useState(null);
  const handleOpenChange = useCallbackRef$1(onOpenChange);
  React.useEffect(() => {
    if (parentMenuContext.open === false) handleOpenChange(false);
    return () => handleOpenChange(false);
  }, [parentMenuContext.open, handleOpenChange]);
  return /* @__PURE__ */ jsx$1(Root2$3, { ...popperScope, children: /* @__PURE__ */ jsx$1(
    MenuProvider,
    {
      scope: __scopeMenu,
      open,
      onOpenChange: handleOpenChange,
      content,
      onContentChange: setContent,
      children: /* @__PURE__ */ jsx$1(
        MenuSubProvider,
        {
          scope: __scopeMenu,
          contentId: useId$1(),
          triggerId: useId$1(),
          trigger,
          onTriggerChange: setTrigger,
          children
        }
      )
    }
  ) });
};
MenuSub.displayName = SUB_NAME$1;
var SUB_TRIGGER_NAME$2 = "MenuSubTrigger";
var MenuSubTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const context = useMenuContext(SUB_TRIGGER_NAME$2, props.__scopeMenu);
    const rootContext = useMenuRootContext(SUB_TRIGGER_NAME$2, props.__scopeMenu);
    const subContext = useMenuSubContext(SUB_TRIGGER_NAME$2, props.__scopeMenu);
    const contentContext = useMenuContentContext(SUB_TRIGGER_NAME$2, props.__scopeMenu);
    const openTimerRef = React.useRef(null);
    const { pointerGraceTimerRef, onPointerGraceIntentChange } = contentContext;
    const scope = { __scopeMenu: props.__scopeMenu };
    const clearOpenTimer = React.useCallback(() => {
      if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }, []);
    React.useEffect(() => clearOpenTimer, [clearOpenTimer]);
    React.useEffect(() => {
      const pointerGraceTimer = pointerGraceTimerRef.current;
      return () => {
        window.clearTimeout(pointerGraceTimer);
        onPointerGraceIntentChange(null);
      };
    }, [pointerGraceTimerRef, onPointerGraceIntentChange]);
    return /* @__PURE__ */ jsx$1(MenuAnchor, { asChild: true, ...scope, children: /* @__PURE__ */ jsx$1(
      MenuItemImpl,
      {
        id: subContext.triggerId,
        "aria-haspopup": "menu",
        "aria-expanded": context.open,
        "aria-controls": subContext.contentId,
        "data-state": getOpenState(context.open),
        ...props,
        ref: composeRefs(forwardedRef, subContext.onTriggerChange),
        onClick: (event) => {
          props.onClick?.(event);
          if (props.disabled || event.defaultPrevented) return;
          event.currentTarget.focus();
          if (!context.open) context.onOpenChange(true);
        },
        onPointerMove: composeEventHandlers(
          props.onPointerMove,
          whenMouse((event) => {
            contentContext.onItemEnter(event);
            if (event.defaultPrevented) return;
            if (!props.disabled && !context.open && !openTimerRef.current) {
              contentContext.onPointerGraceIntentChange(null);
              openTimerRef.current = window.setTimeout(() => {
                context.onOpenChange(true);
                clearOpenTimer();
              }, 100);
            }
          })
        ),
        onPointerLeave: composeEventHandlers(
          props.onPointerLeave,
          whenMouse((event) => {
            clearOpenTimer();
            const contentRect = context.content?.getBoundingClientRect();
            if (contentRect) {
              const side = context.content?.dataset.side;
              const rightSide = side === "right";
              const bleed = rightSide ? -5 : 5;
              const contentNearEdge = contentRect[rightSide ? "left" : "right"];
              const contentFarEdge = contentRect[rightSide ? "right" : "left"];
              contentContext.onPointerGraceIntentChange({
                area: [
                  // Apply a bleed on clientX to ensure that our exit point is
                  // consistently within polygon bounds
                  { x: event.clientX + bleed, y: event.clientY },
                  { x: contentNearEdge, y: contentRect.top },
                  { x: contentFarEdge, y: contentRect.top },
                  { x: contentFarEdge, y: contentRect.bottom },
                  { x: contentNearEdge, y: contentRect.bottom }
                ],
                side
              });
              window.clearTimeout(pointerGraceTimerRef.current);
              pointerGraceTimerRef.current = window.setTimeout(
                () => contentContext.onPointerGraceIntentChange(null),
                300
              );
            } else {
              contentContext.onTriggerLeave(event);
              if (event.defaultPrevented) return;
              contentContext.onPointerGraceIntentChange(null);
            }
          })
        ),
        onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
          const isTypingAhead = contentContext.searchRef.current !== "";
          if (props.disabled || isTypingAhead && event.key === " ") return;
          if (SUB_OPEN_KEYS[rootContext.dir].includes(event.key)) {
            context.onOpenChange(true);
            context.content?.focus();
            event.preventDefault();
          }
        })
      }
    ) });
  }
);
MenuSubTrigger.displayName = SUB_TRIGGER_NAME$2;
var SUB_CONTENT_NAME$2 = "MenuSubContent";
var MenuSubContent = React.forwardRef(
  (props, forwardedRef) => {
    const portalContext = usePortalContext(CONTENT_NAME$3, props.__scopeMenu);
    const { forceMount = portalContext.forceMount, ...subContentProps } = props;
    const context = useMenuContext(CONTENT_NAME$3, props.__scopeMenu);
    const rootContext = useMenuRootContext(CONTENT_NAME$3, props.__scopeMenu);
    const subContext = useMenuSubContext(SUB_CONTENT_NAME$2, props.__scopeMenu);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    return /* @__PURE__ */ jsx$1(Collection.Provider, { scope: props.__scopeMenu, children: /* @__PURE__ */ jsx$1(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ jsx$1(Collection.Slot, { scope: props.__scopeMenu, children: /* @__PURE__ */ jsx$1(
      MenuContentImpl,
      {
        id: subContext.contentId,
        "aria-labelledby": subContext.triggerId,
        ...subContentProps,
        ref: composedRefs,
        align: "start",
        side: rootContext.dir === "rtl" ? "left" : "right",
        disableOutsidePointerEvents: false,
        disableOutsideScroll: false,
        trapFocus: false,
        onOpenAutoFocus: (event) => {
          if (rootContext.isUsingKeyboardRef.current) ref.current?.focus();
          event.preventDefault();
        },
        onCloseAutoFocus: (event) => event.preventDefault(),
        onFocusOutside: composeEventHandlers(props.onFocusOutside, (event) => {
          if (event.target !== subContext.trigger) context.onOpenChange(false);
        }),
        onEscapeKeyDown: composeEventHandlers(props.onEscapeKeyDown, (event) => {
          rootContext.onClose();
          event.preventDefault();
        }),
        onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
          const isKeyDownInside = event.currentTarget.contains(event.target);
          const isCloseKey = SUB_CLOSE_KEYS[rootContext.dir].includes(event.key);
          if (isKeyDownInside && isCloseKey) {
            context.onOpenChange(false);
            subContext.trigger?.focus();
            event.preventDefault();
          }
        })
      }
    ) }) }) });
  }
);
MenuSubContent.displayName = SUB_CONTENT_NAME$2;
function getOpenState(open) {
  return open ? "open" : "closed";
}
function isIndeterminate(checked) {
  return checked === "indeterminate";
}
function getCheckedState(checked) {
  return isIndeterminate(checked) ? "indeterminate" : checked ? "checked" : "unchecked";
}
function focusFirst(candidates) {
  const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
  for (const candidate of candidates) {
    if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) return;
    candidate.focus();
    if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) return;
  }
}
function wrapArray(array, startIndex) {
  return array.map((_, index) => array[(startIndex + index) % array.length]);
}
function getNextMatch(values, search, currentMatch) {
  const isRepeated = search.length > 1 && Array.from(search).every((char) => char === search[0]);
  const normalizedSearch = isRepeated ? search[0] : search;
  const currentMatchIndex = currentMatch ? values.indexOf(currentMatch) : -1;
  let wrappedValues = wrapArray(values, Math.max(currentMatchIndex, 0));
  const excludeCurrentMatch = normalizedSearch.length === 1;
  if (excludeCurrentMatch) wrappedValues = wrappedValues.filter((v) => v !== currentMatch);
  const nextMatch = wrappedValues.find(
    (value) => value.toLowerCase().startsWith(normalizedSearch.toLowerCase())
  );
  return nextMatch !== currentMatch ? nextMatch : void 0;
}
function isPointInPolygon(point, polygon) {
  const { x, y } = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect = yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
function isPointerInGraceArea(event, area) {
  if (!area) return false;
  const cursorPos = { x: event.clientX, y: event.clientY };
  return isPointInPolygon(cursorPos, area);
}
function whenMouse(handler) {
  return (event) => event.pointerType === "mouse" ? handler(event) : void 0;
}
var Root3 = Menu;
var Anchor2 = MenuAnchor;
var Portal = MenuPortal;
var Content2$2 = MenuContent;
var Group = MenuGroup;
var Label = MenuLabel;
var Item2$2 = MenuItem;
var CheckboxItem = MenuCheckboxItem;
var RadioGroup$2 = MenuRadioGroup;
var RadioItem = MenuRadioItem;
var ItemIndicator = MenuItemIndicator;
var Separator = MenuSeparator;
var Arrow2 = MenuArrow;
var Sub = MenuSub;
var SubTrigger = MenuSubTrigger;
var SubContent = MenuSubContent;

var DROPDOWN_MENU_NAME = "DropdownMenu";
var [createDropdownMenuContext, createDropdownMenuScope] = createContextScope(
  DROPDOWN_MENU_NAME,
  [createMenuScope]
);
var useMenuScope$1 = createMenuScope();
var [DropdownMenuProvider, useDropdownMenuContext] = createDropdownMenuContext(DROPDOWN_MENU_NAME);
var DropdownMenu$1 = (props) => {
  const {
    __scopeDropdownMenu,
    children,
    dir,
    open: openProp,
    defaultOpen,
    onOpenChange,
    modal = true
  } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  const triggerRef = React.useRef(null);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx$1(
    DropdownMenuProvider,
    {
      scope: __scopeDropdownMenu,
      triggerId: useId$1(),
      triggerRef,
      contentId: useId$1(),
      open,
      onOpenChange: setOpen,
      onOpenToggle: React.useCallback(() => setOpen((prevOpen) => !prevOpen), [setOpen]),
      modal,
      children: /* @__PURE__ */ jsx$1(Root3, { ...menuScope, open, onOpenChange: setOpen, dir, modal, children })
    }
  );
};
DropdownMenu$1.displayName = DROPDOWN_MENU_NAME;
var TRIGGER_NAME$2 = "DropdownMenuTrigger";
var DropdownMenuTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, disabled = false, ...triggerProps } = props;
    const context = useDropdownMenuContext(TRIGGER_NAME$2, __scopeDropdownMenu);
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    return /* @__PURE__ */ jsx$1(Anchor2, { asChild: true, ...menuScope, children: /* @__PURE__ */ jsx$1(
      Primitive.button,
      {
        type: "button",
        id: context.triggerId,
        "aria-haspopup": "menu",
        "aria-expanded": context.open,
        "aria-controls": context.open ? context.contentId : void 0,
        "data-state": context.open ? "open" : "closed",
        "data-disabled": disabled ? "" : void 0,
        disabled,
        ...triggerProps,
        ref: composeRefs(forwardedRef, context.triggerRef),
        onPointerDown: composeEventHandlers(props.onPointerDown, (event) => {
          if (!disabled && event.button === 0 && event.ctrlKey === false) {
            context.onOpenToggle();
            if (!context.open) event.preventDefault();
          }
        }),
        onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
          if (disabled) return;
          if (["Enter", " "].includes(event.key)) context.onOpenToggle();
          if (event.key === "ArrowDown") context.onOpenChange(true);
          if (["Enter", " ", "ArrowDown"].includes(event.key)) event.preventDefault();
        })
      }
    ) });
  }
);
DropdownMenuTrigger.displayName = TRIGGER_NAME$2;
var PORTAL_NAME$1 = "DropdownMenuPortal";
var DropdownMenuPortal = (props) => {
  const { __scopeDropdownMenu, ...portalProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(Portal, { ...menuScope, ...portalProps });
};
DropdownMenuPortal.displayName = PORTAL_NAME$1;
var CONTENT_NAME$2 = "DropdownMenuContent";
var DropdownMenuContent = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, ...contentProps } = props;
    const context = useDropdownMenuContext(CONTENT_NAME$2, __scopeDropdownMenu);
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    const hasInteractedOutsideRef = React.useRef(false);
    return /* @__PURE__ */ jsx$1(
      Content2$2,
      {
        id: context.contentId,
        "aria-labelledby": context.triggerId,
        ...menuScope,
        ...contentProps,
        ref: forwardedRef,
        onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
          if (!hasInteractedOutsideRef.current) context.triggerRef.current?.focus();
          hasInteractedOutsideRef.current = false;
          event.preventDefault();
        }),
        onInteractOutside: composeEventHandlers(props.onInteractOutside, (event) => {
          const originalEvent = event.detail.originalEvent;
          const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
          const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
          if (!context.modal || isRightClick) hasInteractedOutsideRef.current = true;
        }),
        style: {
          ...props.style,
          // re-namespace exposed content custom properties
          ...{
            "--radix-dropdown-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
            "--radix-dropdown-menu-content-available-width": "var(--radix-popper-available-width)",
            "--radix-dropdown-menu-content-available-height": "var(--radix-popper-available-height)",
            "--radix-dropdown-menu-trigger-width": "var(--radix-popper-anchor-width)",
            "--radix-dropdown-menu-trigger-height": "var(--radix-popper-anchor-height)"
          }
        }
      }
    );
  }
);
DropdownMenuContent.displayName = CONTENT_NAME$2;
var GROUP_NAME$1 = "DropdownMenuGroup";
var DropdownMenuGroup = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, ...groupProps } = props;
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    return /* @__PURE__ */ jsx$1(Group, { ...menuScope, ...groupProps, ref: forwardedRef });
  }
);
DropdownMenuGroup.displayName = GROUP_NAME$1;
var LABEL_NAME$1 = "DropdownMenuLabel";
var DropdownMenuLabel = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, ...labelProps } = props;
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    return /* @__PURE__ */ jsx$1(Label, { ...menuScope, ...labelProps, ref: forwardedRef });
  }
);
DropdownMenuLabel.displayName = LABEL_NAME$1;
var ITEM_NAME$1 = "DropdownMenuItem";
var DropdownMenuItem = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, ...itemProps } = props;
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    return /* @__PURE__ */ jsx$1(Item2$2, { ...menuScope, ...itemProps, ref: forwardedRef });
  }
);
DropdownMenuItem.displayName = ITEM_NAME$1;
var CHECKBOX_ITEM_NAME$1 = "DropdownMenuCheckboxItem";
var DropdownMenuCheckboxItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...checkboxItemProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(CheckboxItem, { ...menuScope, ...checkboxItemProps, ref: forwardedRef });
});
DropdownMenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME$1;
var RADIO_GROUP_NAME$1 = "DropdownMenuRadioGroup";
var DropdownMenuRadioGroup = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...radioGroupProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(RadioGroup$2, { ...menuScope, ...radioGroupProps, ref: forwardedRef });
});
DropdownMenuRadioGroup.displayName = RADIO_GROUP_NAME$1;
var RADIO_ITEM_NAME$1 = "DropdownMenuRadioItem";
var DropdownMenuRadioItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...radioItemProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(RadioItem, { ...menuScope, ...radioItemProps, ref: forwardedRef });
});
DropdownMenuRadioItem.displayName = RADIO_ITEM_NAME$1;
var INDICATOR_NAME$1 = "DropdownMenuItemIndicator";
var DropdownMenuItemIndicator = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...itemIndicatorProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(ItemIndicator, { ...menuScope, ...itemIndicatorProps, ref: forwardedRef });
});
DropdownMenuItemIndicator.displayName = INDICATOR_NAME$1;
var SEPARATOR_NAME$1 = "DropdownMenuSeparator";
var DropdownMenuSeparator = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...separatorProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(Separator, { ...menuScope, ...separatorProps, ref: forwardedRef });
});
DropdownMenuSeparator.displayName = SEPARATOR_NAME$1;
var ARROW_NAME$1 = "DropdownMenuArrow";
var DropdownMenuArrow = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeDropdownMenu, ...arrowProps } = props;
    const menuScope = useMenuScope$1(__scopeDropdownMenu);
    return /* @__PURE__ */ jsx$1(Arrow2, { ...menuScope, ...arrowProps, ref: forwardedRef });
  }
);
DropdownMenuArrow.displayName = ARROW_NAME$1;
var DropdownMenuSub = (props) => {
  const { __scopeDropdownMenu, children, open: openProp, onOpenChange, defaultOpen } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx$1(Sub, { ...menuScope, open, onOpenChange: setOpen, children });
};
var SUB_TRIGGER_NAME$1 = "DropdownMenuSubTrigger";
var DropdownMenuSubTrigger = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...subTriggerProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(SubTrigger, { ...menuScope, ...subTriggerProps, ref: forwardedRef });
});
DropdownMenuSubTrigger.displayName = SUB_TRIGGER_NAME$1;
var SUB_CONTENT_NAME$1 = "DropdownMenuSubContent";
var DropdownMenuSubContent = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...subContentProps } = props;
  const menuScope = useMenuScope$1(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx$1(
    SubContent,
    {
      ...menuScope,
      ...subContentProps,
      ref: forwardedRef,
      style: {
        ...props.style,
        // re-namespace exposed content custom properties
        ...{
          "--radix-dropdown-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
          "--radix-dropdown-menu-content-available-width": "var(--radix-popper-available-width)",
          "--radix-dropdown-menu-content-available-height": "var(--radix-popper-available-height)",
          "--radix-dropdown-menu-trigger-width": "var(--radix-popper-anchor-width)",
          "--radix-dropdown-menu-trigger-height": "var(--radix-popper-anchor-height)"
        }
      }
    }
  );
});
DropdownMenuSubContent.displayName = SUB_CONTENT_NAME$1;
var Root2$2 = DropdownMenu$1;
var Trigger$2 = DropdownMenuTrigger;
var Portal2$1 = DropdownMenuPortal;
var Content2$1 = DropdownMenuContent;
var Item2$1 = DropdownMenuItem;
var Separator2$1 = DropdownMenuSeparator;
var Sub2$1 = DropdownMenuSub;
var SubTrigger2$1 = DropdownMenuSubTrigger;
var SubContent2$1 = DropdownMenuSubContent;

var css_248z$D = ".Dropdown-module_content__zO--i {\n  max-width: 400px;\n  max-height: 100vh;\n  overflow: hidden auto;\n  scrollbar-width: none;\n  padding: 8px;\n  background-color: var(--color-white);\n  box-shadow: var(--dropdown-box-shadow);\n  border-radius: var(--border-radius-s);\n  font-size: var(--font-size-s);\n  z-index: var(--z-index-dropdown);\n}\n\n.Dropdown-module_content__zO--i::-webkit-scrollbar {\n  display: none;\n}\n\n.Dropdown-module_content__zO--i.Dropdown-module_height-s__UICJD {\n  max-height: 20vh;\n}\n\n.Dropdown-module_content__zO--i.Dropdown-module_height-m__RmL2E {\n  max-height: 60vh;\n}\n\n.Dropdown-module_content__zO--i.Dropdown-module_height-l__i-gve {\n  max-height: 85vh;\n}\n\n.Dropdown-module_content__zO--i.Dropdown-module_no-padding__CZ4cM {\n  padding: 0;\n}\n";
var styles$C = {"content":"Dropdown-module_content__zO--i","height-s":"Dropdown-module_height-s__UICJD","height-m":"Dropdown-module_height-m__RmL2E","height-l":"Dropdown-module_height-l__i-gve","no-padding":"Dropdown-module_no-padding__CZ4cM"};
styleInject(css_248z$D);

/**
 *
 * Dropdown:
 * Displays a menu to the user—such as a set of actions or functions—triggered by a button.
 *
 * Uses radix-ui as base component:
 * https://www.radix-ui.com/docs/primitives/components/dropdown-menu
 * @param {React.ReactNode} children - Dropdown Content
 * @param trigger
 * @param noPadding
 * @param disabled
 * @param maxHeight
 * @param triggerClass
 * @param triggerStyle
 * @param className
 * @param usePortal
 * @param ref
 */
var Dropdown = function (_a, ref) {
    var _b;
    var children = _a.children, trigger = _a.trigger, _c = _a.noPadding, noPadding = _c === void 0 ? true : _c, disabled = _a.disabled, _d = _a.defaultOpen, defaultOpen = _d === void 0 ? false : _d, _e = _a.maxHeight, maxHeight = _e === void 0 ? 'none' : _e, triggerClass = _a.triggerClass, triggerStyle = _a.triggerStyle, onOpenChange = _a.onOpenChange, className = _a.className, _f = _a.usePortal, usePortal = _f === void 0 ? true : _f;
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    useEffect(function () {
        if (open !== defaultOpen) {
            setOpen(defaultOpen);
        }
    }, [defaultOpen]);
    var classes = classNames$1(styles$C.content, styles$C['height-' + maxHeight], (_b = {},
        _b[styles$C['no-padding']] = noPadding,
        _b), className);
    var content = (jsx$1(Content2$1, { ref: ref, className: classes, sideOffset: 5, children: children }));
    return (jsxs(Root2$2, { open: open, onOpenChange: function (open) {
            setOpen(open);
            onOpenChange && onOpenChange(open);
        }, children: [jsx$1(Trigger$2, { asChild: true, disabled: disabled, className: triggerClass, style: triggerStyle, children: jsx$1("span", { children: trigger }) }), usePortal ? (jsx$1(Portal2$1, { children: content })) : (content)] }));
};
var Dropdown$1 = forwardRef(Dropdown);

var css_248z$C = ".EmptyState-module_empty-state__qCsvH {\n  margin: 0;\n  overflow: hidden;\n  display: flex;\n  justify-content: center;\n  user-select: none;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_padding-s__Vgk-x {\n  padding: 8px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_padding-m__My-UU {\n  padding: 16px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_padding-l__fXBN- {\n  padding: 32px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_padding-xl__Nsj2p {\n  padding: 64px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_border-radius-s__ULRiN {\n  border-radius: var(--border-radius-s);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_border-radius-m__QAiqE {\n  border-radius: var(--border-radius-m);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_border-radius-l__97rSq {\n  border-radius: var(--border-radius-l);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_border-radius-match-table__ZlKjw {\n  border-radius: var(--table-border-radius);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_position-left__qvgM- {\n  justify-content: flex-start;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_position-right__hXtfZ {\n  justify-content: flex-end;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_fit-parent__Orh3j {\n  width: 100%;\n  height: 100%;\n  align-items: center;\n}\n\n.EmptyState-module_empty-state__qCsvH:not(.EmptyState-module_fit-parent__Orh3j) .EmptyState-module_wrapper__dyIdd {\n  padding: 16px;\n}\n\n.EmptyState-module_icon__Nuru5 {\n  text-align: center;\n  height: 160px;\n}\n\n.EmptyState-module_icon__Nuru5 > svg {\n  height: 160px;\n  width: 160px;\n  stroke-width: 2;\n}\n\n.EmptyState-module_title__KzMiI {\n  text-align: center;\n  font-size: var(--font-size-xl);\n  font-weight: 500;\n  width: 100%;\n  min-height: 24px;\n  line-height: 24px;\n  padding: 0;\n  color: var(--color-grey-darker);\n}\n\n.EmptyState-module_info__8Zh6Z {\n  text-align: center;\n  font-size: var(--font-size-s);\n  width: 100%;\n  min-height: 20px;\n  line-height: 20px;\n  color: var(--color-grey);\n}\n\n.EmptyState-module_action__xZzuI {\n  margin-top: 24px;\n  text-align: center;\n  align-items: center;\n  align-content: center;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_is-small__zaYGk .EmptyState-module_icon__Nuru5 {\n  height: 64px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_is-small__zaYGk .EmptyState-module_icon__Nuru5 > svg {\n  height: 64px;\n  width: 64px;\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_is-small__zaYGk .EmptyState-module_title__KzMiI {\n  font-size: var(--font-size-l);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_is-small__zaYGk .EmptyState-module_info__8Zh6Z {\n  font-size: var(--font-size-s);\n}\n\n.EmptyState-module_empty-state__qCsvH.EmptyState-module_is-small__zaYGk .EmptyState-module_action__xZzuI {\n  margin-top: 12px;\n}\n";
var styles$B = {"empty-state":"EmptyState-module_empty-state__qCsvH","padding-s":"EmptyState-module_padding-s__Vgk-x","padding-m":"EmptyState-module_padding-m__My-UU","padding-l":"EmptyState-module_padding-l__fXBN-","padding-xl":"EmptyState-module_padding-xl__Nsj2p","border-radius-s":"EmptyState-module_border-radius-s__ULRiN","border-radius-m":"EmptyState-module_border-radius-m__QAiqE","border-radius-l":"EmptyState-module_border-radius-l__97rSq","border-radius-match-table":"EmptyState-module_border-radius-match-table__ZlKjw","position-left":"EmptyState-module_position-left__qvgM-","position-right":"EmptyState-module_position-right__hXtfZ","fit-parent":"EmptyState-module_fit-parent__Orh3j","wrapper":"EmptyState-module_wrapper__dyIdd","icon":"EmptyState-module_icon__Nuru5","title":"EmptyState-module_title__KzMiI","info":"EmptyState-module_info__8Zh6Z","action":"EmptyState-module_action__xZzuI","is-small":"EmptyState-module_is-small__zaYGk"};
styleInject(css_248z$C);

var EmptyState = function (_a, ref) {
    var _b;
    var _c = _a.size, size = _c === void 0 ? 'big' : _c, forcedWidth = _a.forcedWidth, forcedHeight = _a.forcedHeight, maxWidth = _a.maxWidth, title = _a.title, subtitle = _a.subtitle, _d = _a.backgroundColor, backgroundColor = _d === void 0 ? 'transparent' : _d, _e = _a.icon, icon = _e === void 0 ? jsx$1(SvgNoDataIcon, {}) : _e, _f = _a.iconColor, iconColor = _f === void 0 ? 'var(--color-primary-light)' : _f, _g = _a.hideIcon, hideIcon = _g === void 0 ? false : _g, buttonText = _a.buttonText, _h = _a.buttonIcon, buttonIcon = _h === void 0 ? jsx$1(SvgAddIcon, {}) : _h, _j = _a.showButton, showButton = _j === void 0 ? false : _j, _k = _a.padding, padding = _k === void 0 ? 'none' : _k, _l = _a.borderRadius, borderRadius = _l === void 0 ? 'match-table' : _l, _m = _a.position, position = _m === void 0 ? 'center' : _m, _o = _a.fitParent, fitParent = _o === void 0 ? true : _o, onButtonClick = _a.onButtonClick, className = _a.className;
    var classes = classNames$1(styles$B['empty-state'], (_b = {},
        _b[styles$B['is-small']] = size !== 'big',
        _b[styles$B['border-radius-' + borderRadius]] = borderRadius !== 'none',
        _b[styles$B['padding-' + padding]] = padding !== 'none',
        _b[styles$B['position-' + position]] = position !== 'center',
        _b[styles$B['fit-parent']] = fitParent,
        _b), className);
    return (jsx$1("div", { role: 'region', ref: ref, className: classes, style: {
            backgroundColor: backgroundColor,
            width: forcedWidth !== null && forcedWidth !== void 0 ? forcedWidth : undefined,
            height: forcedHeight !== null && forcedHeight !== void 0 ? forcedHeight : undefined,
        }, children: jsxs("div", { className: styles$B.wrapper, style: {
                maxWidth: maxWidth !== null && maxWidth !== void 0 ? maxWidth : undefined,
            }, children: [!hideIcon && icon && (jsx$1("div", { className: styles$B.icon, style: { color: iconColor }, children: icon })), title && title !== '' && jsx$1("div", { className: styles$B.title, children: title }), subtitle && subtitle !== '' && (jsx$1("div", { className: styles$B.info, children: subtitle })), showButton && (buttonText || buttonIcon) && (jsx$1("div", { className: styles$B.action, children: jsx$1(Button$1, { icon: buttonIcon !== null && buttonIcon !== void 0 ? buttonIcon : undefined, hierarchy: 'primary', onClick: function (event) {
                            if (onButtonClick) {
                                onButtonClick(event);
                            }
                        }, children: buttonText !== null && buttonText !== void 0 ? buttonText : '' }) }))] }) }));
};
var EmptyState$1 = forwardRef(EmptyState);

var css_248z$B = ".DropdownMenu-module_list__Dn5vR {\n  margin: 0;\n  padding: 4px;\n  text-align: left;\n}\n\n.DropdownMenu-module_list-item__J2Qjf {\n  padding: 4px 8px;\n  display: flex;\n  align-items: center;\n  width: 100%;\n  font-size: var(--font-size-s);\n  user-select: none;\n}\n\n.DropdownMenu-module_list-item__J2Qjf:first-child {\n  border-top-left-radius: var(--border-radius-s);\n  border-top-right-radius: var(--border-radius-s);\n}\n\n.DropdownMenu-module_list-item__J2Qjf:last-child {\n  border-bottom-left-radius: var(--border-radius-s);\n  border-bottom-right-radius: var(--border-radius-s);\n}\n\n.DropdownMenu-module_list-item__J2Qjf:hover,\n.DropdownMenu-module_list-item__J2Qjf:focus {\n  background-color: var(--color-grey-lightest);\n  cursor: pointer;\n  outline: none;\n}\n\n.DropdownMenu-module_list-item__J2Qjf.DropdownMenu-module_align-left__tHdiJ {\n  text-align: left;\n  justify-content: flex-start;\n}\n\n.DropdownMenu-module_list-item__J2Qjf.DropdownMenu-module_align-center__sFnFo {\n  text-align: center;\n  justify-content: center;\n}\n\n.DropdownMenu-module_list-item__J2Qjf.DropdownMenu-module_align-right__6l-G9 {\n  text-align: right;\n  justify-content: flex-end;\n}\n\n.DropdownMenu-module_error__5p9-n {\n  color: var(--color-error);\n}\n\n.DropdownMenu-module_warning__honoW {\n  color: var(--color-warning);\n}\n\n.DropdownMenu-module_primary__uSBNH {\n  color: var(--color-primary);\n}\n\n.DropdownMenu-module_primary__uSBNH:hover {\n  color: var(--color-white);\n  background-color: var(--color-primary);\n}\n\n.DropdownMenu-module_ruler__afL7l {\n  height: 1px;\n  background-color: var(--color-grey-light);\n  width: 100%;\n}\n\n.DropdownMenu-module_disabled__qnToo,\n.DropdownMenu-module_disabled__qnToo:hover {\n  cursor: not-allowed;\n  color: var(--color-grey-lighter);\n  background-color: transparent;\n}\n\n.DropdownMenu-module_sub-content__wlYVq {\n  max-width: 400px;\n  max-height: 100vh;\n  overflow: hidden auto;\n  scrollbar-width: none;\n  padding: 0;\n  background-color: var(--color-white);\n  box-shadow: var(--dropdown-box-shadow);\n  border-radius: var(--border-radius-s);\n  font-size: var(--font-size-s);\n  z-index: 1;\n}\n\n.DropdownMenu-module_sub-content__wlYVq::-webkit-scrollbar {\n  display: none;\n}\n\n.DropdownMenu-module_sub-content__wlYVq.DropdownMenu-module_height-s__lTBfR {\n  max-height: 20vh;\n}\n\n.DropdownMenu-module_sub-content__wlYVq.DropdownMenu-module_height-m__Z1Reb {\n  max-height: 60vh;\n}\n\n.DropdownMenu-module_sub-content__wlYVq.DropdownMenu-module_height-l__C6RMT {\n  max-height: 85vh;\n}\n\n.DropdownMenu-module_right-slot__jQ6ZK {\n  margin-left: auto;\n  padding-left: 20px;\n}\n\n.DropdownMenu-module_right-slot__jQ6ZK .DropdownMenu-module_arrow-icon__XuNPT {\n  width: 18px;\n  height: 18px;\n}\n\n.DropdownMenu-module_tooltip__rQAGY {\n  margin-left: 4px;\n  width: 18px;\n  height: 18px;\n}\n\n.DropdownMenu-module_tooltip__rQAGY span svg {\n  height: 100%;\n  width: 100%;\n}\n\n.DropdownMenu-module_disabled__qnToo .DropdownMenu-module_tooltip__rQAGY span svg {\n  color: var(--color-grey-lighter);\n}\n";
var styles$A = {"list":"DropdownMenu-module_list__Dn5vR","list-item":"DropdownMenu-module_list-item__J2Qjf","align-left":"DropdownMenu-module_align-left__tHdiJ","align-center":"DropdownMenu-module_align-center__sFnFo","align-right":"DropdownMenu-module_align-right__6l-G9","error":"DropdownMenu-module_error__5p9-n","warning":"DropdownMenu-module_warning__honoW","primary":"DropdownMenu-module_primary__uSBNH","ruler":"DropdownMenu-module_ruler__afL7l","disabled":"DropdownMenu-module_disabled__qnToo","sub-content":"DropdownMenu-module_sub-content__wlYVq","height-s":"DropdownMenu-module_height-s__lTBfR","height-m":"DropdownMenu-module_height-m__Z1Reb","height-l":"DropdownMenu-module_height-l__C6RMT","right-slot":"DropdownMenu-module_right-slot__jQ6ZK","arrow-icon":"DropdownMenu-module_arrow-icon__XuNPT","tooltip":"DropdownMenu-module_tooltip__rQAGY"};
styleInject(css_248z$B);

var css_248z$A = ".Switch-module_switch__6Lu4c {\n  width: fit-content;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  gap: var(--spacing-2);\n}\n\n.Switch-module_switch__6Lu4c .Switch-module_label__0fm-N {\n  font-size: var(--font-size-m);\n  color: var(--input-text-color-default);\n  display: inline-flex;\n  gap: var(--spacing-2);\n  user-select: none;\n  cursor: pointer;\n}\n\n.Switch-module_switch__6Lu4c .Switch-module_label__0fm-N .Switch-module_tooltip-icon__WqWvi {\n  color: var(--color-grey-dark);\n  width: 18px;\n  height: 18px;\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_left__oF1A9 {\n  flex-direction: row-reverse;\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_no-label__QQ-NA {\n  gap: 0;\n}\n\n.Switch-module_wrapper__nC6lr {\n  all: unset;\n  border-radius: var(--switch-border-radius);\n  display: inline-block;\n  width: var(--switch-width);\n  height: var(--switch-height);\n  position: relative;\n  flex: 0 0 var(--switch-width);\n}\n\n.Switch-module_wrapper__nC6lr .Switch-module_input__NBLLg {\n  opacity: 0;\n  width: 0;\n  height: 0;\n}\n\n.Switch-module_slider__LI8ha {\n  position: absolute;\n  cursor: pointer;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  transition: 0.4s;\n  border-radius: var(--switch-border-radius);\n  background-color: var(--switch-color-background);\n  -webkit-tap-highlight-color: var(--switch-color-background);\n}\n\n.Switch-module_slider__LI8ha::before {\n  position: absolute;\n  content: '';\n  width: var(--switch-thumb-size);\n  height: var(--switch-thumb-size);\n  left: 4px;\n  bottom: 3px;\n  background-color: white;\n  transition: transform 100ms ease 0s;\n  border-radius: 50%;\n  will-change: transform;\n}\n\n.Switch-module_input__NBLLg:checked + .Switch-module_slider__LI8ha {\n  background-color: var(--switch-color-background-active);\n}\n\n.Switch-module_input__NBLLg:focus + .Switch-module_slider__LI8ha {\n  box-shadow: 0 0 2px var(--switch-color-background-active);\n}\n\n.Switch-module_input__NBLLg:checked + .Switch-module_slider__LI8ha::before {\n  transform: translateX(calc(var(--switch-width) / 2 - 4px));\n}\n\n/* small sized switch */\n.Switch-module_switch__6Lu4c.Switch-module_small__DUxOd .Switch-module_label__0fm-N {\n  font-size: var(--font-size-s);\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_small__DUxOd .Switch-module_wrapper__nC6lr {\n  flex: 0 0 var(--switch-sm-width);\n  border-radius: var(--switch-sm-border-radius);\n  height: var(--switch-sm-height);\n  width: var(--switch-sm-width);\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_small__DUxOd .Switch-module_wrapper__nC6lr .Switch-module_slider__LI8ha::before {\n  height: var(--switch-sm-thumb-size);\n  width: var(--switch-sm-thumb-size);\n  left: 3px;\n  bottom: 3px;\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_small__DUxOd .Switch-module_wrapper__nC6lr .Switch-module_input__NBLLg:checked + .Switch-module_slider__LI8ha::before {\n  transform: translateX(calc(var(--switch-sm-width) / 2 - 2px));\n}\n\n/* disabled */\n.Switch-module_switch__6Lu4c.Switch-module_disabled__SWxhH .Switch-module_label__0fm-N {\n  cursor: not-allowed;\n  color: var(--input-text-color-disabled);\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_disabled__SWxhH .Switch-module_label__0fm-N .Switch-module_tooltip-icon__WqWvi {\n  color: var(--input-text-color-disabled);\n}\n\n.Switch-module_switch__6Lu4c.Switch-module_disabled__SWxhH .Switch-module_slider__LI8ha {\n  background-color: var(--switch-color-background-disabled);\n  cursor: not-allowed;\n}\n";
var styles$z = {"switch":"Switch-module_switch__6Lu4c","label":"Switch-module_label__0fm-N","tooltip-icon":"Switch-module_tooltip-icon__WqWvi","left":"Switch-module_left__oF1A9","no-label":"Switch-module_no-label__QQ-NA","wrapper":"Switch-module_wrapper__nC6lr","input":"Switch-module_input__NBLLg","slider":"Switch-module_slider__LI8ha","small":"Switch-module_small__DUxOd","disabled":"Switch-module_disabled__SWxhH"};
styleInject(css_248z$A);

/**
 *
 * Switch toggle:
 * A control that allows the user to toggle between checked and not checked.
 * Visually rendered as a switch.
 *
 * @param {string} name
 * @param {boolean} disabled
 * @param {boolean} required
 * @param {boolean} checked
 * @param {string} size
 * @param {string} value
 * @param {function} onChange
 */
var Switch = function (_a, ref) {
    var _b;
    var name = _a.name, label = _a.label, _c = _a.direction, direction = _c === void 0 ? 'left' : _c, disabled = _a.disabled, required = _a.required, checked = _a.checked, _d = _a.size, size = _d === void 0 ? 'large' : _d, tooltip = _a.tooltip, _e = _a.tabIndex, tabIndex = _e === void 0 ? 0 : _e, onChange = _a.onChange, className = _a.className;
    var classes = classNames$1(styles$z.switch, (_b = {},
        _b[styles$z.small] = size === 'small',
        _b[styles$z.disabled] = disabled,
        _b[styles$z.left] = direction === 'left',
        _b[styles$z['no-label']] = !label || label === '',
        _b), className);
    return (jsxs("div", { className: classes, ref: ref, children: [jsxs("label", { className: styles$z.wrapper, children: [jsx$1("input", { type: 'checkbox', name: name, id: name, disabled: disabled, required: required, onChange: onChange, className: styles$z.input, checked: checked, tabIndex: tabIndex }), jsx$1("span", { className: styles$z.slider })] }), jsxs("label", { htmlFor: name, className: styles$z.label, children: [label, tooltip && (jsx$1(Tooltip$1, { content: tooltip, children: jsx$1(SvgInfoIcon, { className: styles$z['tooltip-icon'] }) }))] })] }));
};
var Switch$1 = forwardRef(Switch);

/**
 * @param {Array} data - Dropdown Content
 * @param className
 */
var DropdownMenu = function (_a) {
    var _b = _a.usePortal, usePortal = _b === void 0 ? true : _b, data = _a.data, _c = _a.alignment, alignment = _c === void 0 ? 'left' : _c, _d = _a.maxHeight, maxHeight = _d === void 0 ? 'none' : _d;
    var singleItem = function (item, index) {
        var _a;
        var classes = classNames$1(styles$A['list-item'], [styles$A['align-' + alignment]], (_a = {},
            _a[styles$A['warning']] = item.color === 'warning',
            _a[styles$A['error']] = item.color === 'error',
            _a[styles$A['primary']] = item.color === 'primary',
            _a[styles$A['disabled']] = !item.subContent &&
                ((item.type !== 'switch' &&
                    item.type !== 'component' &&
                    !item.onClick) ||
                    item.disabled === true),
            _a), item.className);
        switch (item.type) {
            case 'ruler': {
                return jsx$1(Separator2$1, { className: styles$A.ruler }, index);
            }
            case 'switch': {
                return (jsxs(Item2$1, { className: classes, onSelect: function (e) {
                        e.preventDefault();
                        if (!item.disabled && item.onValueChange) {
                            item.onValueChange(!item.value);
                        }
                    }, children: [item.label, item.tooltip && (jsx$1("div", { className: styles$A.tooltip, children: jsx$1(Tooltip$1, { content: item.tooltip, children: jsx$1(SvgInfoIcon, { className: styles$A['tooltip-icon'] }) }) })), jsx$1("div", { className: styles$A['right-slot'], children: jsx$1(Switch$1, { checked: item.value, disabled: item.disabled === true, size: 'small', name: "".concat(item.label).concat(index), onChange: function (e) {
                                    e.preventDefault();
                                    if (!item.disabled && item.onValueChange) {
                                        item.onValueChange(item.value);
                                    }
                                } }) })] }, index));
            }
            case 'component': {
                return (jsx$1("div", { className: classes, onClick: function (e) { return item.onClick && item.onClick(e); }, role: 'menuitem', children: item.node }, index));
            }
            default: {
                return (jsxs(Item2$1, { onClick: function (event) { var _a; return !item.disabled && ((_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, event)); }, disabled: item.disabled === true, className: classes, children: [item.label, item.tooltip && (jsx$1("div", { className: styles$A.tooltip, children: jsx$1(Tooltip$1, { content: item.tooltip, children: jsx$1(SvgInfoIcon, { className: styles$A['tooltip-icon'] }) }) }))] }, index));
            }
        }
    };
    var multiItem = function (items, subIndex) {
        return items.map(function (item, index) {
            var _a;
            if (item.subContent) {
                return (jsxs(Sub2$1, { children: [jsxs(SubTrigger2$1, { disabled: item.disabled, className: classNames$1(styles$A['list-item'], (_a = {},
                                _a[styles$A['warning']] = item.color === 'warning',
                                _a[styles$A['error']] = item.color === 'error',
                                _a[styles$A['primary']] = item.color === 'primary',
                                _a[styles$A['disabled']] = item.disabled,
                                _a), item.className), children: [item.label, item.tooltip && (jsx$1("div", { className: styles$A.tooltip, children: jsx$1(Tooltip$1, { content: item.tooltip, children: jsx$1(SvgInfoIcon, { className: styles$A['tooltip-icon'] }) }) })), jsx$1("div", { className: styles$A['right-slot'], children: jsx$1(SvgArrowRightIcon, { className: styles$A['arrow-icon'] }) })] }), usePortal ? (jsx$1(Portal2$1, { children: jsx$1(SubContent2$1, { className: classNames$1(styles$A['sub-content'], styles$A['height-' + maxHeight]), sideOffset: subIndex, alignOffset: -5, children: multiItem(item.subContent, subIndex++) }) })) : (jsx$1(SubContent2$1, { className: classNames$1(styles$A['sub-content'], styles$A['height-' + maxHeight]), sideOffset: subIndex, alignOffset: -5, children: multiItem(item.subContent, subIndex++) }))] }, index));
            }
            return singleItem(item, index);
        });
    };
    return jsx$1(Fragment, { children: multiItem(data, 1) });
};

var css_248z$z = ".Logo-module_logo-typo__iyLXz {\n  opacity: 1;\n  transition: opacity var(--sidebar-easing);\n  will-change: opacity;\n}\n\n.Logo-module_collapsed__pCu9S .Logo-module_logo-typo__iyLXz {\n  opacity: 0;\n}\n\n.Logo-module_logo__-I8jh path {\n  fill: var(--color-primary);\n}\n";
var styles$y = {"logo-typo":"Logo-module_logo-typo__iyLXz","collapsed":"Logo-module_collapsed__pCu9S","logo":"Logo-module_logo__-I8jh"};
styleInject(css_248z$z);

var Logo = function (_a, ref) {
    var _b;
    var collapsed = _a.collapsed, type = _a.type, className = _a.className;
    var classes = classNames$1(styles$y.logo, (_b = {},
        _b[styles$y['collapsed']] = collapsed,
        _b), className);
    return (jsxs(Fragment, { children: [type === 'bildungsplattform' && (jsxs("svg", { viewBox: '0 0 261 68', className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', children: [jsx$1("path", { d: 'M24.0899 40.4698L13.3499 21.6798C13.1948 21.4347 12.983 21.2304 12.7324 21.0842C12.4818 20.938 12.1997 20.8542 11.9099 20.8398H0.909912C0.965587 20.9091 1.01574 20.9827 1.05991 21.0598L11.6499 40.6298C11.7763 40.8971 11.8419 41.1892 11.8419 41.4848C11.8419 41.7805 11.7763 42.0725 11.6499 42.3398L1.14991 61.7898H11.9099C12.2004 61.7736 12.4828 61.6879 12.7333 61.54C12.9839 61.3921 13.1953 61.1863 13.3499 60.9398L24.0899 42.1498C24.218 41.8883 24.2845 41.601 24.2845 41.3098C24.2845 41.0187 24.218 40.7313 24.0899 40.4698' }), jsx$1("path", { d: 'M16.2099 21.4411L26.9999 40.2311C27.1569 40.4746 27.3691 40.6776 27.6194 40.8235C27.8696 40.9695 28.1507 41.0543 28.4399 41.0711H39.4399C39.3779 41.0045 39.3241 40.9307 39.2799 40.8511L28.6499 21.2811C28.5235 21.0138 28.458 20.7218 28.458 20.4261C28.458 20.1304 28.5235 19.8384 28.6499 19.5711L39.15 0.121094H28.3899C28.1059 0.149865 27.8324 0.244358 27.5911 0.397094C27.3499 0.54983 27.1475 0.756618 26.9999 1.00109L16.2099 19.7511C16.0818 20.0143 16.0151 20.3033 16.0151 20.5961C16.0151 20.8889 16.0818 21.1779 16.2099 21.4411Z' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M40.1199 53.8983C40.5034 54.2636 40.8003 54.71 40.9889 55.2048C41.1775 55.6997 41.253 56.2305 41.2099 56.7583C41.2715 57.5258 41.1167 58.2953 40.7632 58.9793C40.4097 59.6633 39.8716 60.2346 39.2099 60.6283C37.537 61.4857 35.6672 61.8859 33.7899 61.7883H28.3899V44.4883H33.1099C34.9747 44.368 36.8403 44.7115 38.5399 45.4883C39.1414 45.8123 39.6373 46.302 39.9687 46.8993C40.3002 47.4967 40.4533 48.1766 40.4099 48.8583C40.4315 49.3085 40.362 49.7584 40.2057 50.1811C40.0493 50.6038 39.8093 50.9906 39.4999 51.3183C38.8865 51.951 38.1005 52.3892 37.2399 52.5783C38.3027 52.7461 39.299 53.2027 40.1199 53.8983V53.8983ZM32.1199 47.1283V51.5083H33.9199C34.6501 51.5676 35.3799 51.3886 35.9999 50.9983C36.2491 50.7927 36.4448 50.5297 36.5702 50.2319C36.6956 49.9341 36.7469 49.6103 36.7199 49.2883C36.7493 48.9726 36.6934 48.6547 36.558 48.368C36.4225 48.0812 36.2125 47.8361 35.9499 47.6583C35.1963 47.2871 34.358 47.1216 33.5199 47.1783L32.1199 47.1283ZM36.4999 58.5483C36.8181 58.3433 37.0717 58.0521 37.231 57.7087C37.3903 57.3653 37.4489 56.9837 37.3999 56.6083C37.3999 54.8783 36.3299 54.0083 34.1999 54.0083H32.1099V59.0583H33.8599C34.7654 59.1102 35.6694 58.9349 36.4899 58.5483H36.4999Z' }), jsx$1("path", { d: 'M47.7701 42.2983C47.9709 42.4931 48.1305 42.7263 48.2395 42.9839C48.3485 43.2416 48.4047 43.5185 48.4047 43.7983C48.4047 44.0781 48.3485 44.355 48.2395 44.6126C48.1305 44.8703 47.9709 45.1035 47.7701 45.2983C47.3488 45.694 46.7877 45.9062 46.2101 45.8883C45.9255 45.898 45.6418 45.8509 45.3756 45.7495C45.1094 45.6482 44.8662 45.4948 44.6601 45.2983C44.2668 44.8981 44.0464 44.3594 44.0464 43.7983C44.0464 43.2372 44.2668 42.6985 44.6601 42.2983C44.8662 42.1018 45.1094 41.9483 45.3756 41.847C45.6418 41.7457 45.9255 41.6985 46.2101 41.7083C46.7877 41.6904 47.3488 41.9026 47.7701 42.2983V42.2983ZM48.0001 48.5383V61.7883H44.4601V48.5383H48.0001Z' }), jsx$1("path", { d: 'M53.0001 61.2494C52.3613 60.516 52.0378 59.56 52.1001 58.5894V43.2594L55.6501 42.8594V58.4894C55.6501 59.0694 55.8901 59.3594 56.3801 59.3594C56.6219 59.3639 56.8614 59.3126 57.0801 59.2094L57.8001 61.7394C57.0832 62.0464 56.3099 62.1997 55.5301 62.1894C55.0663 62.2161 54.602 62.1464 54.1666 61.9846C53.7311 61.8228 53.3339 61.5724 53.0001 61.2494V61.2494Z' }), jsx$1("path", { d: 'M71.71 43.2603V61.7903H68.59L68.39 60.1603C67.9802 60.7905 67.4158 61.3052 66.7505 61.6554C66.0853 62.0055 65.3415 62.1793 64.59 62.1603C63.8563 62.1963 63.1258 62.041 62.4702 61.7096C61.8145 61.3783 61.2562 60.8823 60.85 60.2703C59.9139 58.7253 59.4644 56.9343 59.56 55.1303C59.5372 53.8853 59.7616 52.6481 60.22 51.4903C60.6018 50.4983 61.2572 49.6349 62.11 49.0003C62.929 48.3968 63.9227 48.0773 64.94 48.0903C65.538 48.088 66.1305 48.2043 66.6833 48.4326C67.236 48.6609 67.7379 48.9966 68.16 49.4203V42.8203L71.71 43.2603ZM67 59.1503C67.4604 58.828 67.8515 58.4165 68.15 57.9403V52.0003C67.8539 51.6278 67.4952 51.3098 67.09 51.0603C66.7123 50.8468 66.2837 50.7397 65.85 50.7503C65.4731 50.7539 65.1039 50.8565 64.7792 51.0478C64.4545 51.2391 64.1858 51.5124 64 51.8403C63.4684 52.8622 63.2296 54.0112 63.31 55.1603C63.223 56.3205 63.4329 57.4837 63.92 58.5403C64.0987 58.85 64.3571 59.1061 64.6684 59.282C64.9797 59.4579 65.3325 59.5471 65.69 59.5403C66.1546 59.5361 66.6087 59.401 67 59.1503Z' }), jsx$1("path", { d: 'M86.79 61.7891H83.71L83.54 59.9991C83.1208 60.6804 82.5359 61.2446 81.84 61.6391C81.1204 62.0103 80.3195 62.196 79.51 62.1791C78.9996 62.2112 78.4884 62.1302 78.013 61.9418C77.5376 61.7533 77.1097 61.4622 76.76 61.0891C76.0747 60.243 75.7194 59.1771 75.76 58.0891V48.5391H79.31V57.6091C79.2645 58.1304 79.3872 58.6525 79.66 59.0991C79.7968 59.2524 79.9674 59.372 80.1583 59.4483C80.3491 59.5247 80.5551 59.5557 80.76 59.5391C81.2843 59.5 81.788 59.3181 82.2164 59.0132C82.6447 58.7082 82.9814 58.2918 83.19 57.8091V48.5391H86.74L86.79 61.7891Z' }), jsx$1("path", { d: 'M101.14 49.2011C101.838 50.0127 102.196 51.0621 102.14 52.1311V61.7911H98.59V52.7911C98.6398 52.2586 98.5133 51.7246 98.23 51.2711C98.0928 51.1215 97.9233 51.0051 97.7345 50.9306C97.5457 50.8561 97.3424 50.8255 97.14 50.8411C96.6479 50.8372 96.1688 50.9993 95.78 51.3011C95.3032 51.6854 94.9001 52.153 94.59 52.6811V61.8411H91V48.5411H94.08L94.35 50.2111C94.8422 49.5773 95.458 49.0499 96.16 48.6611C96.8515 48.3023 97.621 48.1202 98.4 48.1311C98.9069 48.1013 99.4142 48.1814 99.8871 48.3661C100.36 48.5508 100.787 48.8357 101.14 49.2011V49.2011Z' }), jsx$1("path", { d: 'M118.06 49.2617C116.825 49.6462 115.533 49.8153 114.24 49.7617C114.963 50.0487 115.6 50.517 116.09 51.1217C116.499 51.7068 116.71 52.408 116.69 53.1217C116.698 53.9344 116.464 54.7312 116.02 55.4117C115.549 56.1098 114.89 56.6609 114.12 57.0017C113.213 57.3995 112.23 57.594 111.24 57.5717C110.718 57.5775 110.198 57.5204 109.69 57.4017C109.537 57.4898 109.411 57.6167 109.323 57.7695C109.235 57.9222 109.189 58.0955 109.19 58.2717C109.186 58.3956 109.212 58.5187 109.266 58.6302C109.32 58.7418 109.4 58.8384 109.5 58.9117C109.871 59.1093 110.292 59.1928 110.71 59.1517H112.71C113.594 59.1332 114.472 59.2966 115.29 59.6317C115.991 59.9197 116.599 60.3934 117.05 61.0017C117.464 61.585 117.678 62.2867 117.66 63.0017C117.676 63.6353 117.523 64.2617 117.217 64.8169C116.911 65.3721 116.464 65.8362 115.92 66.1617C114.396 67.0106 112.661 67.4061 110.92 67.3017C109.665 67.3426 108.412 67.177 107.21 66.8117C106.425 66.5799 105.748 66.0766 105.3 65.3917C104.908 64.6832 104.718 63.8808 104.75 63.0717H107.92C107.909 63.4218 107.995 63.7682 108.17 64.0717C108.393 64.3675 108.717 64.5705 109.08 64.6417C109.709 64.7907 110.354 64.8546 111 64.8317C111.826 64.8934 112.654 64.7491 113.41 64.4117C113.623 64.2926 113.799 64.1182 113.921 63.9069C114.042 63.6956 114.104 63.4554 114.1 63.2117C114.11 63.0017 114.066 62.7925 113.974 62.6038C113.881 62.415 113.742 62.2526 113.57 62.1317C113.103 61.8535 112.562 61.7244 112.02 61.7617H110.02C108.966 61.8472 107.913 61.5875 107.02 61.0217C106.713 60.8178 106.462 60.5414 106.287 60.217C106.113 59.8925 106.021 59.5301 106.02 59.1617C106.021 58.675 106.163 58.199 106.43 57.7917C106.716 57.336 107.107 56.9554 107.57 56.6817C106.844 56.3182 106.227 55.7698 105.78 55.0917C105.389 54.4336 105.194 53.677 105.22 52.9117C105.196 52.0129 105.443 51.1277 105.93 50.3717C106.418 49.6279 107.113 49.0437 107.93 48.6917C108.856 48.285 109.859 48.0837 110.87 48.1017C112.004 48.1631 113.141 48.0484 114.24 47.7617C115.225 47.4457 116.173 47.0267 117.07 46.5117L118.06 49.2617ZM109.47 51.1517C109.061 51.6322 108.853 52.2519 108.89 52.8817C108.852 53.527 109.059 54.1629 109.47 54.6617C109.91 55.0708 110.489 55.2982 111.09 55.2982C111.691 55.2982 112.27 55.0708 112.71 54.6617C113.113 54.1326 113.308 53.4748 113.26 52.8117C113.26 51.2617 112.53 50.4817 111.06 50.4817C110.762 50.4676 110.464 50.5205 110.189 50.6364C109.914 50.7524 109.668 50.9284 109.47 51.1517V51.1517Z' }), jsx$1("path", { d: 'M129.53 49.7405L128.18 51.7405C127.198 51.0998 126.053 50.7529 124.88 50.7405C124.407 50.7125 123.937 50.8275 123.53 51.0705C123.383 51.1623 123.262 51.2908 123.18 51.4433C123.097 51.5958 123.056 51.7672 123.06 51.9405C123.053 52.1842 123.126 52.4233 123.27 52.6205C123.492 52.8537 123.766 53.0318 124.07 53.1405C124.645 53.3738 125.232 53.5742 125.83 53.7405C126.993 53.9811 128.08 54.4991 129 55.2505C129.343 55.5936 129.609 56.0053 129.781 56.4586C129.953 56.9118 130.028 57.3964 130 57.8805C130.02 58.715 129.751 59.5308 129.24 60.1905C128.701 60.8622 127.988 61.3739 127.18 61.6705C126.255 62.0246 125.271 62.201 124.28 62.1905C123.24 62.2006 122.207 62.0244 121.23 61.6705C120.361 61.3599 119.563 60.8807 118.88 60.2605L120.63 58.3105C121.63 59.1365 122.883 59.5952 124.18 59.6105C124.737 59.64 125.29 59.5043 125.77 59.2205C125.952 59.1109 126.101 58.955 126.203 58.7688C126.305 58.5826 126.356 58.3727 126.35 58.1605C126.368 57.8705 126.291 57.5824 126.13 57.3405C125.906 57.0828 125.621 56.8866 125.3 56.7705C124.69 56.527 124.066 56.3232 123.43 56.1605C122.308 55.9188 121.272 55.38 120.43 54.6005C120.106 54.2686 119.851 53.876 119.679 53.4452C119.508 53.0145 119.423 52.5541 119.43 52.0905C119.43 51.372 119.658 50.672 120.08 50.0905C120.572 49.4395 121.237 48.9405 122 48.6505C122.875 48.2906 123.814 48.1136 124.76 48.1305C126.489 48.0949 128.176 48.6643 129.53 49.7405V49.7405Z' }), jsx$1("path", { d: 'M143.9 50.0015C144.789 51.5703 145.209 53.3612 145.11 55.1615C145.131 56.4004 144.917 57.632 144.48 58.7915C144.123 59.7808 143.496 60.6503 142.67 61.3015C141.865 61.9181 140.874 62.2425 139.86 62.2215C139.241 62.2415 138.626 62.1235 138.059 61.8762C137.492 61.6289 136.986 61.2583 136.58 60.7915V66.9115L133.03 67.3215V48.5415H136.16L136.33 50.0915C136.798 49.4706 137.404 48.9673 138.1 48.6215C138.751 48.3031 139.465 48.1357 140.19 48.1315C140.918 48.0907 141.644 48.242 142.295 48.5702C142.946 48.8984 143.5 49.392 143.9 50.0015ZM141.41 55.1915C141.495 54.0279 141.296 52.861 140.83 51.7915C140.664 51.4751 140.411 51.2128 140.101 51.0356C139.791 50.8583 139.437 50.7737 139.08 50.7915C138.582 50.7893 138.097 50.951 137.7 51.2515C137.248 51.5861 136.867 52.0077 136.58 52.4915V58.2715C136.806 58.6643 137.13 58.991 137.522 59.2191C137.913 59.4472 138.357 59.5687 138.81 59.5715C140.537 59.5515 141.403 58.0815 141.41 55.1615V55.1915Z' }), jsx$1("path", { d: 'M149.44 61.2494C149.12 60.8882 148.875 60.4671 148.719 60.0105C148.563 59.5539 148.498 59.0709 148.53 58.5894V43.2594L152.08 42.8594V58.4894C152.08 59.0694 152.33 59.3594 152.81 59.3594C153.052 59.3651 153.292 59.3137 153.51 59.2094L154.24 61.7394C153.519 62.0455 152.743 62.1987 151.96 62.1894C151.498 62.2165 151.035 62.147 150.601 61.9851C150.167 61.8233 149.772 61.5727 149.44 61.2494V61.2494Z' }), jsx$1("path", { d: 'M167 59.2503C167.179 59.5123 167.445 59.7034 167.75 59.7903L167 62.1303C166.331 62.0981 165.677 61.9134 165.09 61.5903C164.57 61.2579 164.17 60.7671 163.95 60.1903C163.521 60.8493 162.925 61.382 162.222 61.7335C161.519 62.0851 160.734 62.2428 159.95 62.1903C159.379 62.2197 158.807 62.134 158.269 61.9381C157.732 61.7423 157.239 61.4404 156.82 61.0503C156.428 60.6586 156.121 60.1897 155.92 59.6732C155.718 59.1568 155.627 58.6041 155.65 58.0503C155.616 57.4076 155.742 56.7666 156.018 56.1851C156.294 55.6037 156.711 55.1002 157.23 54.7203C158.578 53.8702 160.16 53.4677 161.75 53.5703H163.28V53.0003C163.316 52.6941 163.285 52.3838 163.19 52.0905C163.095 51.7971 162.938 51.5277 162.73 51.3003C162.193 50.9356 161.546 50.7694 160.9 50.8303C159.704 50.8789 158.523 51.1151 157.4 51.5303L156.6 49.1503C158.156 48.5108 159.818 48.1715 161.5 48.1503C162.933 48.0264 164.36 48.4438 165.5 49.3203C165.947 49.7725 166.292 50.3154 166.511 50.9123C166.73 51.5092 166.818 52.1463 166.77 52.7803V58.1303C166.747 58.5174 166.826 58.9036 167 59.2503V59.2503ZM163.3 58.1603V55.6303H162.12C160.21 55.6303 159.25 56.3503 159.25 57.7903C159.224 58.2824 159.381 58.7667 159.69 59.1503C159.853 59.3166 160.05 59.4457 160.267 59.5286C160.485 59.6114 160.718 59.6461 160.95 59.6303C161.442 59.6326 161.925 59.4958 162.342 59.2357C162.76 58.9756 163.095 58.6028 163.31 58.1603H163.3Z' }), jsx$1("path", { d: 'M179 61.1609C178.502 61.5014 177.951 61.7581 177.37 61.9209C176.779 62.0979 176.166 62.1888 175.55 62.1909C174.979 62.2229 174.408 62.1344 173.874 61.9311C173.34 61.7278 172.855 61.4142 172.45 61.0109C171.687 60.0568 171.312 58.8496 171.4 57.6309V51.0009H169.4V48.5009H171.4V45.6309L174.95 45.2109V48.5409H177.95L177.6 51.0409H174.9V57.5609C174.862 58.0442 174.977 58.5272 175.23 58.9409C175.351 59.0852 175.505 59.1985 175.679 59.2715C175.853 59.3445 176.042 59.3752 176.23 59.3609C176.76 59.3379 177.275 59.1832 177.73 58.9109L179 61.1609Z' }), jsx$1("path", { d: 'M189.21 61.1609C188.709 61.5031 188.155 61.76 187.57 61.9209C186.983 62.0979 186.373 62.1888 185.76 62.1909C185.189 62.2236 184.618 62.1353 184.084 61.932C183.55 61.7286 183.064 61.4148 182.66 61.0109C181.914 60.0496 181.558 58.8431 181.66 57.6309V51.0009H179.66V48.5009H181.66V45.6309L185.21 45.2109V48.5409H188.26L187.91 51.0409H185.21V57.5609C185.174 58.0428 185.286 58.5241 185.53 58.9409C185.651 59.0852 185.805 59.1985 185.979 59.2715C186.153 59.3445 186.342 59.3752 186.53 59.3609C187.06 59.3379 187.575 59.1832 188.03 58.9109L189.21 61.1609Z' }), jsx$1("path", { d: 'M195.9 45.9416C195.606 46.335 195.467 46.8226 195.51 47.3116V48.5416H198.66L198.26 51.0416H195.51V61.7916H192V51.0016H189.9V48.5016H192V47.2116C191.979 46.6293 192.08 46.0489 192.297 45.5081C192.514 44.9673 192.842 44.4779 193.26 44.0716C194.252 43.213 195.541 42.7785 196.85 42.8616C198.062 42.8597 199.259 43.1332 200.35 43.6616L199.35 46.0116C198.679 45.6894 197.945 45.5187 197.2 45.5116C196.965 45.484 196.727 45.5082 196.502 45.5825C196.278 45.6568 196.072 45.7794 195.9 45.9416V45.9416Z' }), jsx$1("path", { d: 'M211 49.9987C212.192 51.4449 212.792 53.2883 212.68 55.1587C212.704 56.4299 212.444 57.6904 211.92 58.8487C211.444 59.8671 210.679 60.7227 209.72 61.3087C208.693 61.9101 207.52 62.2147 206.33 62.1887C205.464 62.2321 204.6 62.0875 203.795 61.7646C202.991 61.4418 202.266 60.9485 201.67 60.3187C200.479 58.873 199.882 57.0283 200 55.1587C199.971 53.8871 200.231 52.6255 200.76 51.4687C201.233 50.4484 201.999 49.592 202.96 49.0087C203.994 48.4062 205.174 48.1016 206.37 48.1287C207.231 48.085 208.092 48.2297 208.892 48.5526C209.691 48.8756 210.411 49.3691 211 49.9987V49.9987ZM204.34 51.8487C203.83 52.8738 203.602 54.0163 203.68 55.1587C203.602 56.3011 203.83 57.4437 204.34 58.4687C204.557 58.8009 204.854 59.0737 205.203 59.2625C205.552 59.4514 205.943 59.5502 206.34 59.5502C206.737 59.5502 207.128 59.4514 207.477 59.2625C207.826 59.0737 208.123 58.8009 208.34 58.4687C208.85 57.4437 209.078 56.3011 209 55.1587C209.076 54.0176 208.852 52.8765 208.35 51.8487C208.147 51.5043 207.853 51.2229 207.5 51.0358C207.147 50.8486 206.749 50.7631 206.35 50.7887C205.947 50.762 205.544 50.8468 205.186 51.0338C204.828 51.2207 204.529 51.5027 204.32 51.8487H204.34Z' }), jsx$1("path", { d: 'M224.26 48.3602L223.66 51.7902C223.276 51.6824 222.879 51.6219 222.48 51.6102C222.132 51.5915 221.785 51.6573 221.468 51.8018C221.151 51.9463 220.874 52.1653 220.66 52.4402C220.151 53.1921 219.81 54.0448 219.66 54.9402V61.7902H216.11V48.5402H219.19L219.51 51.1102C219.766 50.2732 220.247 49.5226 220.9 48.9402C221.468 48.4367 222.201 48.1592 222.96 48.1602C223.4 48.1713 223.837 48.2385 224.26 48.3602Z' }), jsx$1("path", { d: 'M243.6 49.2114C244.289 50.0241 244.646 51.0669 244.6 52.1314V61.7914H241V52.7914C241 51.4914 240.56 50.7914 239.7 50.7914C239.248 50.7846 238.81 50.9448 238.47 51.2414C238.038 51.6417 237.676 52.1116 237.4 52.6314V61.7914H233.85V52.7914C233.85 51.4914 233.41 50.7914 232.55 50.7914C232.097 50.7834 231.657 50.9478 231.32 51.2514C230.899 51.6529 230.545 52.1187 230.27 52.6314V61.7914H226.72V48.5414H229.8L230.07 50.1914C230.52 49.569 231.096 49.0478 231.76 48.6614C232.422 48.3014 233.166 48.1189 233.92 48.1314C234.613 48.1143 235.295 48.3136 235.87 48.7014C236.432 49.0871 236.853 49.6452 237.07 50.2914C237.551 49.6357 238.16 49.0846 238.86 48.6714C239.53 48.3038 240.285 48.1176 241.05 48.1314C241.528 48.1125 242.005 48.1995 242.446 48.3862C242.887 48.5729 243.281 48.8547 243.6 49.2114V49.2114Z' }), jsx$1("path", { d: 'M257.6 43.75C258.605 44.332 259.435 45.1751 260 46.19C260.596 47.2377 260.903 48.4248 260.89 49.63C260.906 50.8324 260.598 52.0169 260 53.06C259.425 54.0637 258.588 54.8931 257.58 55.46C256.525 56.0377 255.342 56.3405 254.14 56.3405C252.938 56.3405 251.755 56.0377 250.7 55.46C249.692 54.8957 248.859 54.0655 248.29 53.06C247.695 52.0161 247.391 50.8315 247.41 49.63C247.397 48.4248 247.704 47.2377 248.3 46.19C248.872 45.1699 249.712 44.3261 250.73 43.75C251.767 43.1544 252.944 42.8471 254.14 42.86C255.352 42.8435 256.546 43.1507 257.6 43.75V43.75ZM256.72 54.12C257.461 53.6993 258.065 53.0745 258.46 52.32C258.886 51.4884 259.099 50.5641 259.08 49.63C259.095 48.6899 258.883 47.76 258.46 46.92C258.065 46.1544 257.457 45.519 256.71 45.09C255.934 44.6468 255.053 44.4221 254.16 44.44C253.27 44.4209 252.392 44.6457 251.62 45.09C250.877 45.5247 250.271 46.1586 249.87 46.92C249.436 47.7567 249.216 48.6877 249.23 49.63C249.216 50.5627 249.436 51.4841 249.87 52.31C250.271 53.0659 250.878 53.6933 251.62 54.12C252.404 54.5402 253.28 54.76 254.17 54.76C255.06 54.76 255.936 54.5402 256.72 54.12V54.12ZM256.56 49.29C256.268 49.6345 255.888 49.8936 255.46 50.04L257.31 53H255.36L253.88 50.4H253.33V53H251.63V46H253.71C254.557 45.9424 255.403 46.1262 256.15 46.53C256.416 46.709 256.631 46.9545 256.773 47.2422C256.915 47.5299 256.98 47.8498 256.96 48.17C256.951 48.5767 256.81 48.9694 256.56 49.29V49.29ZM253.33 49H254.06C254.82 49 255.21 48.68 255.21 48.05C255.21 47.42 254.82 47.2 254.03 47.2H253.33V49Z' })] })] })), type === 'kleo' && (jsxs("svg", { viewBox: '0 0 47 41', className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', children: [jsx$1("path", { d: 'M15.6567 26.1822L8.42011 13.9863C8.3133 13.8275 8.1687 13.6953 7.99821 13.6006C7.82772 13.5058 7.63622 13.4513 7.43946 13.4414H0C0.0398789 13.4847 0.0739582 13.5327 0.101447 13.5841L7.24333 26.2796C7.32778 26.4521 7.37156 26.6404 7.37156 26.831C7.37156 27.0216 7.32778 27.2098 7.24333 27.3824L0.148789 40H7.41917C7.61942 39.9931 7.815 39.94 7.98926 39.8452C8.16353 39.7503 8.31134 39.6165 8.42011 39.455L15.6567 27.2656C15.7469 27.097 15.7939 26.9102 15.7939 26.7207C15.7939 26.5312 15.7469 26.3444 15.6567 26.1758' }), jsx$1("path", { d: 'M10.3274 13.8566L17.591 26.0526C17.6979 26.2114 17.8425 26.3436 18.0129 26.4383C18.1834 26.5331 18.3749 26.5876 18.5717 26.5975H26.0044C25.966 26.553 25.9321 26.5053 25.9029 26.4548L18.7408 13.7204C18.6563 13.5479 18.6125 13.3596 18.6125 13.169C18.6125 12.9784 18.6563 12.7901 18.7408 12.6176L25.8421 0H18.5649C18.3682 0.00987726 18.1767 0.0644284 18.0062 0.159164C17.8357 0.2539 17.6911 0.386113 17.5843 0.544924L10.3274 12.7344C10.2408 12.904 10.1958 13.0904 10.1958 13.2793C10.1958 13.4682 10.2408 13.6546 10.3274 13.8242' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M21.2664 37.8026L21.2714 35.2621H21.5797L24.0257 32.3636H26.4568L23.1706 36.2017H22.6684L21.2664 37.8026ZM19.3474 40V29.8182H21.4653V40H19.3474ZM24.1201 40L21.873 36.674L23.2849 35.1776L26.601 40H24.1201Z' }), jsx$1("path", { d: 'M29.6137 29.8182V40H27.4958V29.8182H29.6137Z' }), jsx$1("path", { d: 'M34.7904 40.1491C34.0049 40.1491 33.3288 39.9901 32.762 39.6719C32.1986 39.3504 31.7644 38.8963 31.4594 38.3097C31.1545 37.7197 31.0021 37.022 31.0021 36.2166C31.0021 35.4311 31.1545 34.7417 31.4594 34.1484C31.7644 33.5552 32.1936 33.0928 32.7471 32.7614C33.3039 32.4299 33.9568 32.2642 34.7059 32.2642C35.2097 32.2642 35.6787 32.3454 36.1129 32.5078C36.5504 32.6669 36.9315 32.9072 37.2563 33.2287C37.5844 33.5502 37.8397 33.9545 38.0219 34.4418C38.2042 34.9257 38.2954 35.4924 38.2954 36.142V36.7237H31.8472V35.4112H36.3018C36.3018 35.1063 36.2355 34.8362 36.1029 34.6008C35.9703 34.3655 35.7864 34.1816 35.5511 34.049C35.3191 33.9131 35.0489 33.8452 34.7407 33.8452C34.4192 33.8452 34.1342 33.9197 33.8856 34.0689C33.6403 34.2147 33.4481 34.4119 33.3089 34.6605C33.1697 34.9058 33.0984 35.1792 33.0951 35.4808V36.7287C33.0951 37.1065 33.1647 37.433 33.3039 37.7081C33.4464 37.9832 33.647 38.1953 33.9055 38.3445C34.164 38.4936 34.4706 38.5682 34.8252 38.5682C35.0605 38.5682 35.276 38.535 35.4715 38.4687C35.6671 38.4025 35.8344 38.303 35.9737 38.1704C36.1129 38.0379 36.2189 37.8755 36.2918 37.6832L38.2506 37.8125C38.1512 38.2831 37.9474 38.6941 37.6391 39.0455C37.3342 39.3935 36.9398 39.6652 36.4559 39.8608C35.9753 40.053 35.4202 40.1491 34.7904 40.1491Z' }), jsx$1("path", { d: 'M43.1278 40.1491C42.3555 40.1491 41.6877 39.9851 41.1242 39.657C40.5641 39.3255 40.1316 38.8648 39.8266 38.2749C39.5217 37.6816 39.3693 36.9938 39.3693 36.2116C39.3693 35.4228 39.5217 34.7334 39.8266 34.1435C40.1316 33.5502 40.5641 33.0895 41.1242 32.7614C41.6877 32.4299 42.3555 32.2642 43.1278 32.2642C43.9 32.2642 44.5662 32.4299 45.1264 32.7614C45.6898 33.0895 46.124 33.5502 46.4289 34.1435C46.7338 34.7334 46.8863 35.4228 46.8863 36.2116C46.8863 36.9938 46.7338 37.6816 46.4289 38.2749C46.124 38.8648 45.6898 39.3255 45.1264 39.657C44.5662 39.9851 43.9 40.1491 43.1278 40.1491ZM43.1377 38.5085C43.489 38.5085 43.7824 38.4091 44.0177 38.2102C44.253 38.008 44.4303 37.7329 44.5496 37.3849C44.6723 37.0369 44.7336 36.6409 44.7336 36.1967C44.7336 35.7526 44.6723 35.3565 44.5496 35.0085C44.4303 34.6605 44.253 34.3854 44.0177 34.1832C43.7824 33.9811 43.489 33.88 43.1377 33.88C42.7831 33.88 42.4848 33.9811 42.2428 34.1832C42.0042 34.3854 41.8236 34.6605 41.7009 35.0085C41.5816 35.3565 41.5219 35.7526 41.5219 36.1967C41.5219 36.6409 41.5816 37.0369 41.7009 37.3849C41.8236 37.7329 42.0042 38.008 42.2428 38.2102C42.4848 38.4091 42.7831 38.5085 43.1377 38.5085Z' })] })] })), type === 'planung' && (jsxs("svg", { className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 123.94 71.41', children: [jsx$1("path", { d: 'M25.89,42.6L15.16,23.81c-.27-.46-.91-.84-1.45-.84H2.74c.06,.07,.11,.14,.15,.22l10.56,19.57c.25,.47,.25,1.23,0,1.7L2.96,63.92H13.71c.53,0,1.18-.38,1.45-.84l10.73-18.79c.27-.46,.27-1.22,0-1.68' }), jsx$1("path", { d: 'M18.01,23.57l10.73,18.79c.27,.46,.91,.84,1.45,.84h10.97c-.06-.07-.11-.14-.15-.22l-10.56-19.57c-.25-.47-.25-1.23,0-1.7L40.95,2.25h-10.75c-.53,0-1.18,.38-1.45,.84l-10.73,18.79c-.27,.46-.27,1.22,0,1.68' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M30.19,47.3c1.12-.2,2.65-.35,4.7-.35,2.25,0,3.87,.47,4.95,1.38,1,.82,1.65,2.15,1.65,3.72s-.5,2.9-1.42,3.8c-1.25,1.22-3.15,1.83-5.32,1.83-.57,0-1.07-.03-1.5-.12v6.37h-3.05v-16.62Zm3.05,7.82c.4,.12,.88,.15,1.5,.15,2.3,0,3.7-1.15,3.7-3.1s-1.3-2.85-3.42-2.85c-.85,0-1.45,.08-1.77,.15v5.65Z' }), jsx$1("path", { d: 'M44.76,46.17h3.07v17.75h-3.07v-17.75Z' }), jsx$1("path", { d: 'M61.36,60.99c0,1.1,.05,2.17,.2,2.92h-2.77l-.23-1.35h-.07c-.73,.95-2,1.62-3.6,1.62-2.45,0-3.82-1.78-3.82-3.62,0-3.05,2.72-4.62,7.22-4.6v-.2c0-.8-.32-2.12-2.47-2.12-1.2,0-2.45,.38-3.27,.9l-.6-2c.9-.55,2.47-1.08,4.4-1.08,3.9,0,5.02,2.47,5.02,5.15v4.37Zm-3-3.02c-2.17-.03-4.25,.42-4.25,2.27,0,1.2,.77,1.75,1.75,1.75,1.25,0,2.12-.8,2.42-1.67,.08-.22,.08-.45,.08-.67v-1.67Z' }), jsx$1("path", { d: 'M65.34,55.37c0-1.4-.05-2.57-.1-3.62h2.67l.15,1.82h.07c.55-.95,1.87-2.1,3.9-2.1,2.12,0,4.32,1.38,4.32,5.22v7.22h-3.07v-6.87c0-1.75-.65-3.07-2.32-3.07-1.23,0-2.07,.87-2.4,1.8-.1,.25-.15,.62-.15,.98v7.17h-3.07v-8.55Z' }), jsx$1("path", { d: 'M91.26,60.27c0,1.45,.05,2.65,.1,3.65h-2.67l-.15-1.82h-.08c-.52,.85-1.72,2.1-3.92,2.1s-4.22-1.33-4.22-5.3v-7.15h3.07v6.62c0,2.02,.65,3.32,2.27,3.32,1.25,0,2.05-.85,2.35-1.65,.12-.25,.17-.6,.17-.95v-7.35h3.07v8.52Z' }), jsx$1("path", { d: 'M95.31,55.37c0-1.4-.05-2.57-.1-3.62h2.67l.15,1.82h.07c.55-.95,1.87-2.1,3.9-2.1,2.12,0,4.32,1.38,4.32,5.22v7.22h-3.07v-6.87c0-1.75-.65-3.07-2.32-3.07-1.23,0-2.07,.87-2.4,1.8-.1,.25-.15,.62-.15,.98v7.17h-3.07v-8.55Z' }), jsx$1("path", { d: 'M121.36,62.14c0,2.55-.52,4.37-1.8,5.52-1.25,1.12-3.02,1.47-4.7,1.47-1.55,0-3.2-.35-4.25-.97l.68-2.32c.77,.45,2.07,.92,3.55,.92,2,0,3.5-1.05,3.5-3.67v-1.05h-.05c-.7,1.08-1.92,1.8-3.55,1.8-3.02,0-5.17-2.5-5.17-5.95,0-4,2.6-6.42,5.52-6.42,1.87,0,2.95,.9,3.52,1.9h.05l.12-1.62h2.67c-.05,.82-.1,1.82-.1,3.5v6.9Zm-3.07-5.52c0-.3-.02-.58-.1-.83-.32-1.12-1.2-1.97-2.5-1.97-1.72,0-3,1.5-3,3.97,0,2.07,1.05,3.75,2.97,3.75,1.15,0,2.15-.75,2.5-1.88,.1-.3,.12-.72,.12-1.07v-1.97Z' })] })] })), type === 'zeugnis' && (jsxs("svg", { className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 123.99 73.57', children: [jsx$1("path", { d: 'M26.68,43.63L15.95,24.83c-.27-.46-.91-.84-1.45-.84H3.53c.06,.07,.11,.14,.15,.22l10.56,19.57c.25,.47,.25,1.23,0,1.7L3.74,64.94H14.5c.53,0,1.18-.38,1.45-.84l10.73-18.79c.27-.46,.27-1.22,0-1.68' }), jsx$1("path", { d: 'M18.8,24.59l10.73,18.79c.27,.46,.91,.84,1.45,.84h10.97c-.06-.07-.11-.14-.15-.22l-10.56-19.57c-.25-.47-.25-1.23,0-1.7L41.74,3.28h-10.75c-.53,0-1.18,.38-1.45,.84l-10.73,18.79c-.27,.46-.27,1.22,0,1.68' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M29.85,63.24l8.6-12.52v-.08h-7.85v-2.55h11.85v1.8l-8.5,12.42v.07h8.62v2.55h-12.72v-1.7Z' }), jsx$1("path", { d: 'M47.9,59.72c.07,2.2,1.77,3.15,3.75,3.15,1.42,0,2.42-.23,3.37-.55l.45,2.1c-1.05,.45-2.5,.77-4.25,.77-3.95,0-6.27-2.42-6.27-6.15,0-3.37,2.05-6.55,5.95-6.55s5.25,3.25,5.25,5.92c0,.58-.05,1.02-.1,1.3h-8.15Zm5.35-2.15c.02-1.12-.48-2.97-2.52-2.97-1.9,0-2.7,1.72-2.82,2.97h5.35Z' }), jsx$1("path", { d: 'M70.25,61.29c0,1.45,.05,2.65,.1,3.65h-2.67l-.15-1.82h-.08c-.52,.85-1.72,2.1-3.92,2.1s-4.22-1.33-4.22-5.3v-7.15h3.07v6.62c0,2.02,.65,3.32,2.27,3.32,1.25,0,2.05-.85,2.35-1.65,.12-.25,.17-.6,.17-.95v-7.35h3.07v8.52Z' }), jsx$1("path", { d: 'M85.32,63.17c0,2.55-.52,4.37-1.8,5.52-1.25,1.12-3.02,1.47-4.7,1.47-1.55,0-3.2-.35-4.25-.97l.68-2.32c.77,.45,2.07,.92,3.55,.92,2,0,3.5-1.05,3.5-3.67v-1.05h-.05c-.7,1.08-1.92,1.8-3.55,1.8-3.02,0-5.17-2.5-5.17-5.95,0-4,2.6-6.42,5.52-6.42,1.87,0,2.95,.9,3.52,1.9h.05l.12-1.62h2.67c-.05,.82-.1,1.82-.1,3.5v6.9Zm-3.07-5.52c0-.3-.02-.58-.1-.83-.32-1.12-1.2-1.97-2.5-1.97-1.72,0-3,1.5-3,3.97,0,2.07,1.05,3.75,2.97,3.75,1.15,0,2.15-.75,2.5-1.88,.1-.3,.12-.72,.12-1.07v-1.97Z' }), jsx$1("path", { d: 'M89.35,56.39c0-1.4-.05-2.57-.1-3.62h2.67l.15,1.82h.07c.55-.95,1.87-2.1,3.9-2.1,2.12,0,4.32,1.38,4.32,5.22v7.22h-3.07v-6.87c0-1.75-.65-3.07-2.32-3.07-1.23,0-2.07,.87-2.4,1.8-.1,.25-.15,.62-.15,.98v7.17h-3.07v-8.55Z' }), jsx$1("path", { d: 'M107.65,49.37c0,.9-.67,1.65-1.75,1.65s-1.7-.75-1.7-1.65c0-.95,.7-1.67,1.72-1.67s1.7,.73,1.72,1.67Zm-3.27,15.57v-12.17h3.07v12.17h-3.07Z' }), jsx$1("path", { d: 'M111.38,62.09c.68,.42,2,.9,3.1,.9,1.35,0,1.95-.58,1.95-1.38s-.5-1.22-2-1.77c-2.37-.8-3.35-2.1-3.35-3.55,0-2.15,1.77-3.8,4.6-3.8,1.35,0,2.52,.33,3.25,.73l-.62,2.15c-.5-.3-1.5-.7-2.57-.7s-1.7,.55-1.7,1.3,.58,1.12,2.12,1.7c2.22,.78,3.22,1.9,3.25,3.7,0,2.22-1.72,3.82-4.95,3.82-1.48,0-2.8-.35-3.7-.85l.62-2.25Z' })] })] })), type === 'tk' && (jsxs("svg", { className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 70.87 70.87', children: [jsx$1("path", { d: 'M31.03,44.49l-10.73-18.79c-.27-.46-.91-.84-1.45-.84H7.87c.06,.07,.11,.14,.15,.22l10.56,19.57c.25,.47,.25,1.23,0,1.7l-10.5,19.45h10.75c.53,0,1.18-.38,1.45-.84l10.73-18.79c.27-.46,.27-1.22,0-1.68' }), jsx$1("path", { d: 'M23.15,25.46l10.73,18.79c.27,.46,.91,.84,1.45,.84h10.97c-.06-.07-.11-.14-.15-.22l-10.56-19.57c-.25-.47-.25-1.23,0-1.7L46.09,4.14h-10.75c-.53,0-1.18,.38-1.45,.84l-10.73,18.79c-.27,.46-.27,1.22,0,1.68' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M38.66,52.1h-4.61v-2.47h12.22v2.47h-4.66v13.71h-2.95v-13.71Z' }), jsx$1("path", { d: 'M48.86,49.63h2.93v7.44h.07c.38-.62,.79-1.2,1.18-1.75l4.25-5.69h3.62l-5.57,6.89,5.93,9.29h-3.46l-4.56-7.42-1.46,1.73v5.69h-2.93v-16.18Z' })] })] })), type === 'admin' && (jsxs("svg", { className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 83.93 54.13', children: [jsx$1("path", { d: 'M20.53,34.33l-8.52-14.91c-.21-.37-.73-.67-1.15-.67H2.16c.05,.06,.09,.11,.12,.17l8.38,15.53c.2,.37,.2,.98,0,1.35L2.34,51.24H10.87c.42,0,.94-.3,1.15-.67l8.52-14.91c.21-.37,.21-.97,0-1.33' }), jsx$1("path", { d: 'M14.28,19.23l8.52,14.91c.21,.37,.73,.67,1.15,.67h8.7c-.05-.06-.09-.11-.12-.17l-8.38-15.53c-.2-.37-.2-.98,0-1.35L32.48,2.32h-8.53c-.42,0-.94,.3-1.15,.67l-8.52,14.91c-.21,.37-.21,.97,0,1.33' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M26.62,47.46l-1.15,3.79h-2.52l4.28-13.37h3.11l4.34,13.37h-2.64l-1.21-3.79h-4.23Zm3.83-1.85l-1.05-3.29c-.26-.81-.48-1.73-.67-2.5h-.04c-.2,.77-.42,1.71-.63,2.5l-1.03,3.29h3.43Z' }), jsx$1("path", { d: 'M45.94,37.16v11.37c0,.99,.04,2.06,.08,2.72h-2.16l-.12-1.53h-.04c-.58,1.07-1.75,1.75-3.13,1.75-2.32,0-4.15-1.96-4.15-4.94-.02-3.23,2-5.16,4.32-5.16,1.33,0,2.28,.54,2.72,1.25h.04v-5.46h2.44Zm-2.44,8.45c0-.2-.02-.46-.06-.65-.22-.93-.99-1.71-2.1-1.71-1.57,0-2.44,1.39-2.44,3.17s.87,3.07,2.42,3.07c.99,0,1.86-.67,2.1-1.73,.06-.22,.08-.46,.08-.71v-1.45Z' }), jsx$1("path", { d: 'M49.14,44.46c0-1.11-.04-2.04-.08-2.88h2.08l.12,1.43h.06c.46-.75,1.33-1.65,2.98-1.65,1.27,0,2.26,.71,2.68,1.79h.04c.34-.54,.73-.93,1.17-1.21,.56-.38,1.17-.58,1.98-.58,1.59,0,3.21,1.09,3.21,4.19v5.69h-2.38v-5.34c0-1.61-.56-2.56-1.73-2.56-.83,0-1.45,.6-1.71,1.29-.08,.22-.12,.54-.12,.81v5.79h-2.38v-5.59c0-1.35-.54-2.3-1.67-2.3-.91,0-1.53,.71-1.77,1.37-.08,.26-.12,.54-.12,.81v5.71h-2.38v-6.78Z' }), jsx$1("path", { d: 'M69.13,38.89c0,.71-.54,1.31-1.39,1.31s-1.35-.6-1.35-1.31c0-.75,.56-1.33,1.37-1.33s1.35,.58,1.37,1.33Zm-2.6,12.36v-9.66h2.44v9.66h-2.44Z' }), jsx$1("path", { d: 'M72.19,44.46c0-1.11-.04-2.04-.08-2.88h2.12l.12,1.45h.06c.44-.75,1.49-1.67,3.09-1.67,1.69,0,3.43,1.09,3.43,4.15v5.73h-2.44v-5.46c0-1.39-.52-2.44-1.84-2.44-.97,0-1.65,.69-1.9,1.43-.08,.2-.12,.5-.12,.77v5.69h-2.44v-6.78Z' })] })] })), type === 'pim' && (jsxs("svg", { className: classes, ref: ref, xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 39.5 52.01', children: [jsx$1("path", { d: 'M19.82,33.51l-8.52-14.91c-.21-.37-.73-.67-1.15-.67H1.45c.05,.06,.09,.11,.12,.17l8.38,15.53c.2,.37,.2,.98,0,1.35L1.62,50.42H10.15c.42,0,.94-.3,1.15-.67l8.52-14.91c.21-.37,.21-.97,0-1.33' }), jsx$1("path", { d: 'M13.57,18.4l8.52,14.91c.21,.37,.73,.67,1.15,.67h8.7c-.05-.06-.09-.11-.12-.17l-8.38-15.53c-.2-.37-.2-.98,0-1.35L31.77,1.49h-8.53c-.42,0-.94,.3-1.15,.67l-8.52,14.91c-.21,.37-.21,.97,0,1.33' }), jsxs("g", { className: styles$y['logo-typo'], children: [jsx$1("path", { d: 'M25.73,38.06c0,.71-.54,1.31-1.39,1.31s-1.35-.6-1.35-1.31c0-.75,.56-1.33,1.37-1.33s1.35,.58,1.37,1.33Zm-2.6,12.36v-9.66h2.44v9.66h-2.44Z' }), jsx$1("path", { d: 'M37.69,36.33v11.37c0,.99,.04,2.06,.08,2.72h-2.16l-.12-1.53h-.04c-.58,1.07-1.75,1.75-3.13,1.75-2.32,0-4.15-1.96-4.15-4.94-.02-3.23,2-5.16,4.32-5.16,1.33,0,2.28,.54,2.72,1.25h.04v-5.46h2.44Zm-2.44,8.45c0-.2-.02-.46-.06-.65-.22-.93-.99-1.71-2.1-1.71-1.57,0-2.44,1.39-2.44,3.17s.87,3.07,2.42,3.07c.99,0,1.86-.67,2.1-1.73,.06-.22,.08-.46,.08-.71v-1.45Z' })] })] }))] }));
};
var Logo$1 = forwardRef(Logo);

var css_248z$y = ".Sidebar-module_wrapper__bJIQW {\n  height: 100%;\n}\n\n.Sidebar-module_sidebar__WD1-h {\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  min-height: 100%;\n  /* stylelint-disable */\n  min-height: -webkit-fill-available;\n  background-color: var(--color-white);\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  flex: 1;\n  align-items: center;\n  position: fixed;\n  z-index: var(--z-index-sidebar);\n  box-shadow: var(--box-shadow-light);\n  overflow: hidden;\n  overflow-y: auto;\n  transform: translate3d(0, 101%, 0);\n  transition: transform var(--sidebar-easing-mobile);\n  will-change: transform;\n}\n\n@media (width >= 770px) {\n  .Sidebar-module_sidebar__WD1-h {\n    height: 100%;\n    width: var(--sidebar-width);\n    transform: translate3d(0, 0, 0);\n    transition: width var(--sidebar-easing);\n    will-change: width;\n  }\n}\n\n.Sidebar-module_logo-wrapper__KpBUO {\n  position: absolute;\n  top: 48px;\n  left: 50%;\n  transform: translate3d(-50%, 0, 0);\n}\n\n@media (width >= 770px) {\n  .Sidebar-module_logo-wrapper__KpBUO {\n    position: absolute;\n    top: 24px;\n    left: 28px;\n    transform: translate3d(0, 0, 0);\n  }\n}\n\n.Sidebar-module_logo-wrapper__KpBUO svg {\n  height: 38px;\n}\n\n.Sidebar-module_nav-title-arrow__ppQ93 {\n  position: absolute;\n  right: 24px;\n  opacity: 0;\n  transform: translate3d(-2px, 0, 0);\n  transition:\n    transform var(--sidebar-easing),\n    opacity var(--sidebar-easing);\n  will-change: transform, opacity;\n}\n\n.Sidebar-module_nav-title__RzyKH {\n  position: absolute;\n  left: var(--sidebar-width);\n  display: flex;\n  justify-content: space-between;\n  white-space: nowrap;\n  opacity: 0;\n  transform: translate3d(-10px, 0, 0);\n  max-width: calc(var(--sidebar-width-expanded) - 128px);\n  transition:\n    transform var(--sidebar-easing),\n    opacity var(--sidebar-easing);\n  will-change: transform, opacity;\n  overflow: hidden;\n  white-space: nowrap;\n  text-overflow: ellipsis;\n}\n\n@media (width >= 770px) {\n  .Sidebar-module_nav-title__RzyKH {\n    display: initial;\n    transform: translate3d(-2px, 0, 0);\n  }\n}\n\n/* expanded */\n.Sidebar-module_expanded__MDvb1 {\n  transform: translate3d(0, 0, 0);\n}\n\n@media (width >= 770px) {\n  .Sidebar-module_expanded__MDvb1 {\n    transform: translate3d(0, 0, 0);\n    width: var(--sidebar-width-expanded);\n    box-shadow: var(--box-shadow);\n  }\n}\n\n.Sidebar-module_expanded__MDvb1 .Sidebar-module_nav-title__RzyKH {\n  opacity: 1;\n  transform: translate3d(-10px, 0, 0);\n}\n\n.Sidebar-module_expanded__MDvb1 .Sidebar-module_nav-title-arrow__ppQ93 {\n  opacity: 1;\n}\n\n.Sidebar-module_expanded__MDvb1 .Sidebar-module_category-title__4G1Vr {\n  opacity: 1;\n}\n\nnav.Sidebar-module_main-nav__lr0MV {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  width: 100%;\n  height: 100%;\n  padding-top: 140px;\n  margin-bottom: calc(var(--outer-gutter) * 2 + var(--burger-height));\n}\n\n@media (width >= 770px) {\n  nav.Sidebar-module_main-nav__lr0MV {\n    margin-bottom: 0;\n    height: 100%;\n  }\n}\n\nnav.Sidebar-module_main-nav__lr0MV ul {\n  margin: 0;\n}\n\n.Sidebar-module_category-title__4G1Vr {\n  font-size: var(--font-size-xs);\n  display: block;\n  margin-top: 16px;\n  color: var(--color-grey-light);\n  padding: 0 0 4px 16px;\n  white-space: nowrap;\n  opacity: 0;\n  transition: opacity var(--sidebar-easing);\n  will-change: opacity;\n}\n\n.Sidebar-module_category-title__4G1Vr:first-child {\n  margin-top: 0;\n}\n\n.Sidebar-module_category-line__hnoqh {\n  width: 100%;\n  height: 1px;\n  display: block;\n  margin-bottom: 8px;\n  background-color: var(--color-grey-lighter);\n}\n\n.Sidebar-module_nav-item__QgvD5 {\n  list-style: none;\n}\n\n.Sidebar-module_nav-icon__7t9wi {\n  height: var(--nav-icon-wrapper-height);\n  width: 100%;\n  min-width: var(--nav-icon-wrapper-height);\n  margin-left: calc(\n    (var(--sidebar-width) - var(--nav-icon-wrapper-height)) / 2\n  );\n  margin-right: calc(\n    (var(--sidebar-width) - var(--nav-icon-wrapper-height)) / 2\n  );\n  border-radius: 6px;\n  display: flex;\n  justify-content: flex-start;\n  padding-left: calc(\n    (var(--nav-icon-wrapper-height) - var(--nav-icon-size)) / 2\n  );\n  align-items: center;\n  transition: background-color var(--sidebar-easing);\n  will-change: background-color;\n}\n\n.Sidebar-module_nav-icon__7t9wi svg {\n  height: var(--nav-icon-size);\n  width: var(--nav-icon-size);\n}\n\n.Sidebar-module_nav-link__XXbf5 {\n  margin-bottom: 8px;\n  display: flex;\n  align-items: center;\n  justify-content: flex-start;\n  text-decoration: none;\n  color: var(--color-text);\n  position: relative;\n  font-size: var(--font-size-md);\n}\n\n.Sidebar-module_nav-link__XXbf5:hover .Sidebar-module_nav-icon__7t9wi,\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB .Sidebar-module_nav-icon__7t9wi {\n  background-color: var(--color-background);\n}\n\n.Sidebar-module_nav-link__XXbf5::before {\n  content: '';\n  position: absolute;\n  display: block;\n  left: 0;\n  top: 50%;\n  height: 40%;\n  width: 5px;\n  opacity: 0;\n  border-radius: 0 10px 10px 0;\n  transform: translate3d(0, -50%, 0);\n  transition: height var(--sidebar-easing);\n  will-change: height;\n}\n\n.Sidebar-module_list-item__tniZ2 {\n  position: relative;\n}\n\n/* active */\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB {\n  color: var(--color-grey-dark);\n}\n\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB::before {\n  height: 64%;\n  opacity: 1;\n  background-color: var(--color-primary);\n}\n\n/* disabled */\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_disabled__sND1O {\n  cursor: not-allowed;\n  pointer-events: none;\n  opacity: 0.5;\n}\n\n/* sublist */\n.Sidebar-module_sublist__FWYAE {\n  padding: 18px 0 24px 60px;\n}\n\n.Sidebar-module_sublist-item__dqJ2b {\n  margin-bottom: 20px;\n  white-space: nowrap;\n  list-style: none;\n}\n\n.Sidebar-module_sublist-item__dqJ2b:last-child {\n  margin: 0;\n}\n\n.Sidebar-module_sublist-indicator__P4-ke {\n  position: absolute;\n  right: 20px;\n}\n\n.Sidebar-module_subnav-link__kcrCQ {\n  text-decoration: none;\n  color: var(--color-text-light);\n  display: block;\n  font-size: var(--font-size-md);\n  position: relative;\n  transition:\n    color 0.4s ease,\n    opacity 0.4s ease,\n    transform 0.4s ease;\n  will-change: color;\n}\n\n.Sidebar-module_subnav-link__kcrCQ:hover {\n  color: var(--color-text);\n}\n\n.Sidebar-module_subnav-link__kcrCQ::before {\n  content: '';\n  position: absolute;\n  width: 4px;\n  height: 100%;\n  border-radius: 2px;\n  left: -15px;\n  top: 2px;\n  margin-top: -2px;\n  opacity: 0;\n  transform: scale(0);\n  transition:\n    opacity 0.4s ease,\n    transform 0.4s ease;\n  will-change: opacity, transform;\n}\n\n.Sidebar-module_subnav-link__kcrCQ.Sidebar-module_active__6c3QB {\n  color: var(--sidebar-active-color);\n}\n\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ::before {\n  background-color: var(--color-grey-lighter);\n  opacity: 0.5;\n  transform: scale(1);\n}\n\n.Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ.Sidebar-module_active__6c3QB::before {\n  background-color: var(--color-primary);\n  opacity: 0.5;\n  transform: scale(1);\n}\n\n:not(.Sidebar-module_expanded__MDvb1) .Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ {\n  transform: translateX(20px);\n}\n\n.Sidebar-module_expanded__MDvb1 .Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ {\n  transform: translateX(0);\n  opacity: 1;\n}\n\n:not(.Sidebar-module_expanded__MDvb1) .Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ::before {\n  transform: translateX(-28px);\n}\n\n.Sidebar-module_expanded__MDvb1 .Sidebar-module_nav-link__XXbf5.Sidebar-module_active__6c3QB + .Sidebar-module_sublist__FWYAE .Sidebar-module_subnav-link__kcrCQ::before {\n  transform: translateX(0);\n  opacity: 1;\n}\n\n.Sidebar-module_fixed-sidebar-toggle__bcv2S {\n  border: none;\n  display: none;\n  border-top: 1px solid var(--color-grey-lighter);\n  width: 100%;\n  height: 40px;\n  min-height: 40px;\n  background-color: transparent;\n  transition: background-color 0.3s ease;\n}\n\n@media (width >= 770px) {\n  .Sidebar-module_fixed-sidebar-toggle__bcv2S {\n    display: block;\n  }\n}\n\n.Sidebar-module_fixed-sidebar-toggle__bcv2S:hover {\n  cursor: pointer;\n  background-color: var(--color-grey-lightest);\n}\n";
var styles$x = {"wrapper":"Sidebar-module_wrapper__bJIQW","sidebar":"Sidebar-module_sidebar__WD1-h","logo-wrapper":"Sidebar-module_logo-wrapper__KpBUO","nav-title-arrow":"Sidebar-module_nav-title-arrow__ppQ93","nav-title":"Sidebar-module_nav-title__RzyKH","expanded":"Sidebar-module_expanded__MDvb1","category-title":"Sidebar-module_category-title__4G1Vr","main-nav":"Sidebar-module_main-nav__lr0MV","category-line":"Sidebar-module_category-line__hnoqh","nav-icon":"Sidebar-module_nav-icon__7t9wi","nav-link":"Sidebar-module_nav-link__XXbf5","active":"Sidebar-module_active__6c3QB","list-item":"Sidebar-module_list-item__tniZ2","disabled":"Sidebar-module_disabled__sND1O","sublist":"Sidebar-module_sublist__FWYAE","sublist-item":"Sidebar-module_sublist-item__dqJ2b","subnav-link":"Sidebar-module_subnav-link__kcrCQ","fixed-sidebar-toggle":"Sidebar-module_fixed-sidebar-toggle__bcv2S"};
styleInject(css_248z$y);

var SidebarItem = function (_a, ref) {
    var _b;
    var icon = _a.icon, link = _a.link, title = _a.title, sublist = _a.sublist, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.isExpanded, isExpanded = _d === void 0 ? false : _d, currentLocationPath = _a.currentLocationPath, onNavigate = _a.onNavigate, onOpenSidebar = _a.onOpenSidebar, onCloseSidebar = _a.onCloseSidebar, className = _a.className;
    var _e = useState(''), currentMainPath = _e[0], setCurrentMainPath = _e[1];
    var _f = useState(''), currentSubPath = _f[0], setCurrentSubPath = _f[1];
    var _g = useState(false), subMenuOpen = _g[0], setSubMenuOpen = _g[1];
    var classes = classNames$1(styles$x['list-item'], className);
    var navLinkClass = classNames$1(styles$x['nav-link'], (_b = {},
        _b[styles$x.active] = currentMainPath === link,
        _b[styles$x.disabled] = disabled,
        _b));
    useEffect(function () {
        // Split pathname to manage the active state of menu & submenu items
        var split = currentLocationPath.split('/');
        setCurrentMainPath(split[1]);
        setCurrentSubPath(split[2]);
        // Close submenu when route changes
        onCloseSidebar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocationPath]);
    useEffect(function () {
        if (!isExpanded && !currentLocationPath.includes(link)) {
            setSubMenuOpen(false);
        }
        if (isExpanded && currentLocationPath.includes(link)) {
            setSubMenuOpen(true);
        }
    }, [isExpanded, link, currentLocationPath]);
    function handleMouseOver() {
        onOpenSidebar();
    }
    return (jsxs("li", { ref: ref, className: classes, children: [jsxs(Link, { to: sublist ? '#' : link, className: navLinkClass, onMouseEnter: function () { return handleMouseOver(); }, onClick: function (e) {
                    e.stopPropagation();
                    if (!sublist) {
                        onCloseSidebar();
                        onNavigate();
                    }
                    else {
                        setSubMenuOpen(!subMenuOpen);
                    }
                }, children: [jsx$1("div", { className: styles$x['nav-icon'], children: icon }), jsx$1("span", { className: styles$x['nav-title'], children: title }), sublist && (jsx$1("div", { className: styles$x['nav-title-arrow'], children: subMenuOpen ? jsx$1(SvgArrowUpIcon, {}) : jsx$1(SvgArrowDownIcon, {}) }))] }), sublist && subMenuOpen && (jsx$1("ul", { className: styles$x.sublist, onClick: function (e) { return e.stopPropagation(); }, children: sublist.map(function (item, key) {
                    var _a;
                    return (jsx$1("li", { className: styles$x['sublist-item'], onClick: function (e) { return e.stopPropagation(); }, children: jsx$1(Link, { to: "".concat(link, "/").concat(item.link), className: classNames$1(styles$x['subnav-link'], (_a = {},
                                _a[styles$x.active] = currentMainPath === link && currentSubPath === item.link,
                                _a)), onClick: function (e) {
                                e.stopPropagation();
                                onCloseSidebar();
                                onNavigate();
                            }, children: item.title }) }, key));
                }) }))] }));
};
SidebarItem.displayName = 'SidebarItem';
var SidebarItem$1 = React__default.memo(forwardRef(SidebarItem));

var SidebarList = forwardRef(
// eslint-disable-next-line react/prop-types
function (_a, ref) {
    var children = _a.children, title = _a.title, className = _a.className;
    return (jsxs(Fragment, { children: [title && (jsxs(Fragment, { children: [jsx$1("span", { className: styles$x['category-title'], children: title }), jsx$1("span", { className: styles$x['category-line'] })] })), jsx$1("ul", { ref: ref, className: className, children: children })] }));
});
SidebarList.displayName = 'SidebarList';
var SidebarList$1 = React__default.memo(SidebarList);

var Sidebar = function (_a, ref) {
    var _b;
    var isExpanded = _a.isExpanded, isFixed = _a.isFixed, sidebarContent = _a.sidebarContent, sidebarFooterItems = _a.sidebarFooterItems, logoType = _a.logoType, currentLocationPath = _a.currentLocationPath, onNavigate = _a.onNavigate, setIsExpanded = _a.setIsExpanded, setIsFixed = _a.setIsFixed, className = _a.className;
    var classes = classNames$1(styles$x.wrapper, className);
    var sidebarClass = classNames$1(styles$x.sidebar, (_b = {},
        _b[styles$x.expanded] = isExpanded,
        _b));
    function handleIsFixed() {
        setIsFixed(!isFixed);
    }
    function openSidebar() {
        setIsExpanded(true);
    }
    function closeSidebar() {
        if (!isFixed) {
            setIsExpanded(false);
        }
    }
    useEffect(function () {
        if (isFixed) {
            setIsExpanded(true);
        }
        else {
            setIsExpanded(false);
        }
    }, [currentLocationPath, setIsExpanded, isFixed]);
    return (jsx$1("div", { ref: ref, className: classes, "data-cy": 'layoutSidebar', children: jsxs("aside", { className: sidebarClass, onFocus: function () { return openSidebar(); }, onMouseEnter: function () { return openSidebar(); }, onMouseLeave: function () { return closeSidebar(); }, onClick: function () { return closeSidebar(); }, children: [jsx$1("div", { className: styles$x['logo-wrapper'], children: jsx$1(Link, { to: '/', children: jsx$1(Logo$1, { collapsed: !isExpanded, type: logoType }) }) }), jsxs("nav", { className: styles$x['main-nav'], children: [jsx$1("div", { children: sidebarContent.map(function (content, key) { return (jsx$1(SidebarList$1, { title: content.title, children: jsx$1(Fragment, { children: content.items.map(function (item, key) { return (jsx$1(SidebarItem$1, { currentLocationPath: currentLocationPath, icon: item.icon, link: item.link, title: item.title, sublist: item.sublist, isExpanded: isExpanded, onNavigate: function () { return onNavigate && onNavigate(); }, onOpenSidebar: function () { return openSidebar(); }, onCloseSidebar: function () { return closeSidebar(); } }, key)); }) }) }, key)); }) }), jsxs("div", { className: styles$x['sidebar-footer'], "data-cy": 'sidebarFooter', children: [jsx$1("ul", { children: sidebarFooterItems &&
                                        sidebarFooterItems.map(function (item, key) { return (jsx$1(SidebarItem$1, { currentLocationPath: currentLocationPath, icon: item.icon, link: item.link, title: item.title, onNavigate: function () { return onNavigate && onNavigate(); }, onOpenSidebar: function () { return openSidebar(); }, onCloseSidebar: function () { return closeSidebar(); } }, key)); }) }), jsx$1("button", { onClick: function () { return handleIsFixed(); }, className: styles$x['fixed-sidebar-toggle'], children: isFixed ? jsx$1(SvgDoublearrowLeftIcon, {}) : jsx$1(SvgDoublearrowRightIcon, {}) })] })] })] }) }));
};
var Sidebar$1 = forwardRef(Sidebar);

var TABS_NAME = "Tabs";
var [createTabsContext, createTabsScope] = createContextScope(TABS_NAME, [
  createRovingFocusGroupScope
]);
var useRovingFocusGroupScope = createRovingFocusGroupScope();
var [TabsProvider, useTabsContext] = createTabsContext(TABS_NAME);
var Tabs = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopeTabs,
      value: valueProp,
      onValueChange,
      defaultValue,
      orientation = "horizontal",
      dir,
      activationMode = "automatic",
      ...tabsProps
    } = props;
    const direction = useDirection(dir);
    const [value, setValue] = useControllableState({
      prop: valueProp,
      onChange: onValueChange,
      defaultProp: defaultValue
    });
    return /* @__PURE__ */ jsx$1(
      TabsProvider,
      {
        scope: __scopeTabs,
        baseId: useId$1(),
        value,
        onValueChange: setValue,
        orientation,
        dir: direction,
        activationMode,
        children: /* @__PURE__ */ jsx$1(
          Primitive.div,
          {
            dir: direction,
            "data-orientation": orientation,
            ...tabsProps,
            ref: forwardedRef
          }
        )
      }
    );
  }
);
Tabs.displayName = TABS_NAME;
var TAB_LIST_NAME = "TabsList";
var TabsList = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeTabs, loop = true, ...listProps } = props;
    const context = useTabsContext(TAB_LIST_NAME, __scopeTabs);
    const rovingFocusGroupScope = useRovingFocusGroupScope(__scopeTabs);
    return /* @__PURE__ */ jsx$1(
      Root,
      {
        asChild: true,
        ...rovingFocusGroupScope,
        orientation: context.orientation,
        dir: context.dir,
        loop,
        children: /* @__PURE__ */ jsx$1(
          Primitive.div,
          {
            role: "tablist",
            "aria-orientation": context.orientation,
            ...listProps,
            ref: forwardedRef
          }
        )
      }
    );
  }
);
TabsList.displayName = TAB_LIST_NAME;
var TRIGGER_NAME$1 = "TabsTrigger";
var TabsTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeTabs, value, disabled = false, ...triggerProps } = props;
    const context = useTabsContext(TRIGGER_NAME$1, __scopeTabs);
    const rovingFocusGroupScope = useRovingFocusGroupScope(__scopeTabs);
    const triggerId = makeTriggerId(context.baseId, value);
    const contentId = makeContentId(context.baseId, value);
    const isSelected = value === context.value;
    return /* @__PURE__ */ jsx$1(
      Item,
      {
        asChild: true,
        ...rovingFocusGroupScope,
        focusable: !disabled,
        active: isSelected,
        children: /* @__PURE__ */ jsx$1(
          Primitive.button,
          {
            type: "button",
            role: "tab",
            "aria-selected": isSelected,
            "aria-controls": contentId,
            "data-state": isSelected ? "active" : "inactive",
            "data-disabled": disabled ? "" : void 0,
            disabled,
            id: triggerId,
            ...triggerProps,
            ref: forwardedRef,
            onMouseDown: composeEventHandlers(props.onMouseDown, (event) => {
              if (!disabled && event.button === 0 && event.ctrlKey === false) {
                context.onValueChange(value);
              } else {
                event.preventDefault();
              }
            }),
            onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
              if ([" ", "Enter"].includes(event.key)) context.onValueChange(value);
            }),
            onFocus: composeEventHandlers(props.onFocus, () => {
              const isAutomaticActivation = context.activationMode !== "manual";
              if (!isSelected && !disabled && isAutomaticActivation) {
                context.onValueChange(value);
              }
            })
          }
        )
      }
    );
  }
);
TabsTrigger.displayName = TRIGGER_NAME$1;
var CONTENT_NAME$1 = "TabsContent";
var TabsContent = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeTabs, value, forceMount, children, ...contentProps } = props;
    const context = useTabsContext(CONTENT_NAME$1, __scopeTabs);
    const triggerId = makeTriggerId(context.baseId, value);
    const contentId = makeContentId(context.baseId, value);
    const isSelected = value === context.value;
    const isMountAnimationPreventedRef = React.useRef(isSelected);
    React.useEffect(() => {
      const rAF = requestAnimationFrame(() => isMountAnimationPreventedRef.current = false);
      return () => cancelAnimationFrame(rAF);
    }, []);
    return /* @__PURE__ */ jsx$1(Presence, { present: forceMount || isSelected, children: ({ present }) => /* @__PURE__ */ jsx$1(
      Primitive.div,
      {
        "data-state": isSelected ? "active" : "inactive",
        "data-orientation": context.orientation,
        role: "tabpanel",
        "aria-labelledby": triggerId,
        hidden: !present,
        id: contentId,
        tabIndex: 0,
        ...contentProps,
        ref: forwardedRef,
        style: {
          ...props.style,
          animationDuration: isMountAnimationPreventedRef.current ? "0s" : void 0
        },
        children: present && children
      }
    ) });
  }
);
TabsContent.displayName = CONTENT_NAME$1;
function makeTriggerId(baseId, value) {
  return `${baseId}-trigger-${value}`;
}
function makeContentId(baseId, value) {
  return `${baseId}-content-${value}`;
}
var Root2$1 = Tabs;
var List = TabsList;
var Trigger$1 = TabsTrigger;
var Content = TabsContent;

var css_248z$x = ".Tab-module_tab-list__NN5Hv {\n  position: relative;\n  border-bottom: 1px solid var(--color-grey-lighter);\n  display: flex;\n  flex: auto;\n  align-self: stretch;\n  overflow: auto hidden;\n  white-space: nowrap;\n  margin-bottom: 1em;\n  scrollbar-width: none;\n}\n\n.Tab-module_tab-list__NN5Hv::-webkit-scrollbar {\n  display: none;\n}\n\n.Tab-module_button__GsB2I {\n  border: none;\n  background: transparent;\n  border-bottom: 2px solid transparent;\n  font-size: var(--font-size-m);\n  padding: 0 0 10px;\n  margin-right: 16px;\n  position: relative;\n}\n\n.Tab-module_button__GsB2I[data-state='active'] {\n  color: var(--tab-active-text-color);\n}\n\n.Tab-module_button__GsB2I[data-state='inactive'] {\n  color: var(--tab-inactive-text-color);\n}\n\n.Tab-module_button__GsB2I[data-disabled] {\n  color: var(--tab-disabled-text-color);\n}\n\n.Tab-module_large__OBaMU {\n  font-size: var(--font-size-xxl);\n  margin-right: 40px;\n}\n\n.Tab-module_list-margin__dZtU1 {\n  margin-bottom: 40px;\n}\n\n.Tab-module_tab-list__NN5Hv button:last-child {\n  margin-right: 0;\n}\n\n.Tab-module_tab-list__NN5Hv button::after {\n  content: '';\n  position: absolute;\n  display: block;\n  width: 100%;\n  height: 0;\n  bottom: -2px;\n  opacity: 0;\n  border-radius: 5px 5px 0 0;\n  background: var(--color-primary);\n  transition:\n    height 0.3s ease,\n    opacity 0.3s ease;\n  will-change: height, opacity;\n}\n\n.Tab-module_tab-list__NN5Hv button[data-state='active']::after {\n  height: 4px;\n  opacity: 1;\n}\n";
var styles$w = {"tab-list":"Tab-module_tab-list__NN5Hv","button":"Tab-module_button__GsB2I","large":"Tab-module_large__OBaMU","list-margin":"Tab-module_list-margin__dZtU1"};
styleInject(css_248z$x);

var Tab = function (_a, ref) {
    var _b, _c;
    var tabs = _a.tabs, variant = _a.variant, defaultValue = _a.defaultValue, value = _a.value, _d = _a.tabIndex, tabIndex = _d === void 0 ? 0 : _d, onValueChange = _a.onValueChange, className = _a.className;
    var buttonStyle = classNames$1(styles$w.button, (_b = {},
        _b[styles$w.large] = variant === 'large',
        _b));
    var classes = classNames$1(styles$w['tab-list'], (_c = {},
        _c[styles$w['list-margin']] = variant === 'large',
        _c), className);
    return (jsxs(Root2$1, { defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : tabs[0].value, value: value, onValueChange: onValueChange, orientation: 'vertical', children: [jsx$1(List, { ref: ref, className: classes, "aria-label": 'tabs', children: tabs.map(function (tab, key) {
                    return (jsx$1(Trigger$1, { className: buttonStyle, value: tab.value, disabled: tab.disabled, tabIndex: tabIndex, children: tab.title }, key));
                }) }), tabs.map(function (tab, key) {
                return (jsx$1(Content, { value: tab.value, children: tab.content }, key));
            })] }));
};
var Tab$1 = forwardRef(Tab);

/**
   * table-core
   *
   * Copyright (c) TanStack
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE.md file in the root directory of this source tree.
   *
   * @license MIT
   */
// type Person = {
//   firstName: string
//   lastName: string
//   age: number
//   visits: number
//   status: string
//   progress: number
//   createdAt: Date
//   nested: {
//     foo: [
//       {
//         bar: 'bar'
//       }
//     ]
//     bar: { subBar: boolean }[]
//     baz: {
//       foo: 'foo'
//       bar: {
//         baz: 'baz'
//       }
//     }
//   }
// }


// Is this type a tuple?

// If this type is a tuple, what indices are allowed?

///

function functionalUpdate(updater, input) {
  return typeof updater === 'function' ? updater(input) : updater;
}
function makeStateUpdater(key, instance) {
  return updater => {
    instance.setState(old => {
      return {
        ...old,
        [key]: functionalUpdate(updater, old[key])
      };
    });
  };
}
function isFunction(d) {
  return d instanceof Function;
}
function isNumberArray(d) {
  return Array.isArray(d) && d.every(val => typeof val === 'number');
}
function flattenBy(arr, getChildren) {
  const flat = [];
  const recurse = subArr => {
    subArr.forEach(item => {
      flat.push(item);
      const children = getChildren(item);
      if (children != null && children.length) {
        recurse(children);
      }
    });
  };
  recurse(arr);
  return flat;
}
function memo(getDeps, fn, opts) {
  let deps = [];
  let result;
  return depArgs => {
    let depTime;
    if (opts.key && opts.debug) depTime = Date.now();
    const newDeps = getDeps(depArgs);
    const depsChanged = newDeps.length !== deps.length || newDeps.some((dep, index) => deps[index] !== dep);
    if (!depsChanged) {
      return result;
    }
    deps = newDeps;
    let resultTime;
    if (opts.key && opts.debug) resultTime = Date.now();
    result = fn(...newDeps);
    opts == null || opts.onChange == null || opts.onChange(result);
    if (opts.key && opts.debug) {
      if (opts != null && opts.debug()) {
        const depEndTime = Math.round((Date.now() - depTime) * 100) / 100;
        const resultEndTime = Math.round((Date.now() - resultTime) * 100) / 100;
        const resultFpsPercentage = resultEndTime / 16;
        const pad = (str, num) => {
          str = String(str);
          while (str.length < num) {
            str = ' ' + str;
          }
          return str;
        };
        console.info(`%c⏱ ${pad(resultEndTime, 5)} /${pad(depEndTime, 5)} ms`, `
            font-size: .6rem;
            font-weight: bold;
            color: hsl(${Math.max(0, Math.min(120 - 120 * resultFpsPercentage, 120))}deg 100% 31%);`, opts == null ? void 0 : opts.key);
      }
    }
    return result;
  };
}
function getMemoOptions(tableOptions, debugLevel, key, onChange) {
  return {
    debug: () => {
      var _tableOptions$debugAl;
      return (_tableOptions$debugAl = tableOptions == null ? void 0 : tableOptions.debugAll) != null ? _tableOptions$debugAl : tableOptions[debugLevel];
    },
    key: process.env.NODE_ENV === 'development' && key,
    onChange
  };
}

function createCell(table, row, column, columnId) {
  const getRenderValue = () => {
    var _cell$getValue;
    return (_cell$getValue = cell.getValue()) != null ? _cell$getValue : table.options.renderFallbackValue;
  };
  const cell = {
    id: `${row.id}_${column.id}`,
    row,
    column,
    getValue: () => row.getValue(columnId),
    renderValue: getRenderValue,
    getContext: memo(() => [table, column, row, cell], (table, column, row, cell) => ({
      table,
      column,
      row,
      cell: cell,
      getValue: cell.getValue,
      renderValue: cell.renderValue
    }), getMemoOptions(table.options, 'debugCells', 'cell.getContext'))
  };
  table._features.forEach(feature => {
    feature.createCell == null || feature.createCell(cell, column, row, table);
  }, {});
  return cell;
}

function createColumn(table, columnDef, depth, parent) {
  var _ref, _resolvedColumnDef$id;
  const defaultColumn = table._getDefaultColumnDef();
  const resolvedColumnDef = {
    ...defaultColumn,
    ...columnDef
  };
  const accessorKey = resolvedColumnDef.accessorKey;
  let id = (_ref = (_resolvedColumnDef$id = resolvedColumnDef.id) != null ? _resolvedColumnDef$id : accessorKey ? typeof String.prototype.replaceAll === 'function' ? accessorKey.replaceAll('.', '_') : accessorKey.replace(/\./g, '_') : undefined) != null ? _ref : typeof resolvedColumnDef.header === 'string' ? resolvedColumnDef.header : undefined;
  let accessorFn;
  if (resolvedColumnDef.accessorFn) {
    accessorFn = resolvedColumnDef.accessorFn;
  } else if (accessorKey) {
    // Support deep accessor keys
    if (accessorKey.includes('.')) {
      accessorFn = originalRow => {
        let result = originalRow;
        for (const key of accessorKey.split('.')) {
          var _result;
          result = (_result = result) == null ? void 0 : _result[key];
          if (process.env.NODE_ENV !== 'production' && result === undefined) {
            console.warn(`"${key}" in deeply nested key "${accessorKey}" returned undefined.`);
          }
        }
        return result;
      };
    } else {
      accessorFn = originalRow => originalRow[resolvedColumnDef.accessorKey];
    }
  }
  if (!id) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(resolvedColumnDef.accessorFn ? `Columns require an id when using an accessorFn` : `Columns require an id when using a non-string header`);
    }
    throw new Error();
  }
  let column = {
    id: `${String(id)}`,
    accessorFn,
    parent: parent,
    depth,
    columnDef: resolvedColumnDef,
    columns: [],
    getFlatColumns: memo(() => [true], () => {
      var _column$columns;
      return [column, ...((_column$columns = column.columns) == null ? void 0 : _column$columns.flatMap(d => d.getFlatColumns()))];
    }, getMemoOptions(table.options, 'debugColumns', 'column.getFlatColumns')),
    getLeafColumns: memo(() => [table._getOrderColumnsFn()], orderColumns => {
      var _column$columns2;
      if ((_column$columns2 = column.columns) != null && _column$columns2.length) {
        let leafColumns = column.columns.flatMap(column => column.getLeafColumns());
        return orderColumns(leafColumns);
      }
      return [column];
    }, getMemoOptions(table.options, 'debugColumns', 'column.getLeafColumns'))
  };
  for (const feature of table._features) {
    feature.createColumn == null || feature.createColumn(column, table);
  }

  // Yes, we have to convert table to unknown, because we know more than the compiler here.
  return column;
}

const debug = 'debugHeaders';
//

function createHeader(table, column, options) {
  var _options$id;
  const id = (_options$id = options.id) != null ? _options$id : column.id;
  let header = {
    id,
    column,
    index: options.index,
    isPlaceholder: !!options.isPlaceholder,
    placeholderId: options.placeholderId,
    depth: options.depth,
    subHeaders: [],
    colSpan: 0,
    rowSpan: 0,
    headerGroup: null,
    getLeafHeaders: () => {
      const leafHeaders = [];
      const recurseHeader = h => {
        if (h.subHeaders && h.subHeaders.length) {
          h.subHeaders.map(recurseHeader);
        }
        leafHeaders.push(h);
      };
      recurseHeader(header);
      return leafHeaders;
    },
    getContext: () => ({
      table,
      header: header,
      column
    })
  };
  table._features.forEach(feature => {
    feature.createHeader == null || feature.createHeader(header, table);
  });
  return header;
}
const Headers = {
  createTable: table => {
    // Header Groups

    table.getHeaderGroups = memo(() => [table.getAllColumns(), table.getVisibleLeafColumns(), table.getState().columnPinning.left, table.getState().columnPinning.right], (allColumns, leafColumns, left, right) => {
      var _left$map$filter, _right$map$filter;
      const leftColumns = (_left$map$filter = left == null ? void 0 : left.map(columnId => leafColumns.find(d => d.id === columnId)).filter(Boolean)) != null ? _left$map$filter : [];
      const rightColumns = (_right$map$filter = right == null ? void 0 : right.map(columnId => leafColumns.find(d => d.id === columnId)).filter(Boolean)) != null ? _right$map$filter : [];
      const centerColumns = leafColumns.filter(column => !(left != null && left.includes(column.id)) && !(right != null && right.includes(column.id)));
      const headerGroups = buildHeaderGroups(allColumns, [...leftColumns, ...centerColumns, ...rightColumns], table);
      return headerGroups;
    }, getMemoOptions(table.options, debug, 'getHeaderGroups'));
    table.getCenterHeaderGroups = memo(() => [table.getAllColumns(), table.getVisibleLeafColumns(), table.getState().columnPinning.left, table.getState().columnPinning.right], (allColumns, leafColumns, left, right) => {
      leafColumns = leafColumns.filter(column => !(left != null && left.includes(column.id)) && !(right != null && right.includes(column.id)));
      return buildHeaderGroups(allColumns, leafColumns, table, 'center');
    }, getMemoOptions(table.options, debug, 'getCenterHeaderGroups'));
    table.getLeftHeaderGroups = memo(() => [table.getAllColumns(), table.getVisibleLeafColumns(), table.getState().columnPinning.left], (allColumns, leafColumns, left) => {
      var _left$map$filter2;
      const orderedLeafColumns = (_left$map$filter2 = left == null ? void 0 : left.map(columnId => leafColumns.find(d => d.id === columnId)).filter(Boolean)) != null ? _left$map$filter2 : [];
      return buildHeaderGroups(allColumns, orderedLeafColumns, table, 'left');
    }, getMemoOptions(table.options, debug, 'getLeftHeaderGroups'));
    table.getRightHeaderGroups = memo(() => [table.getAllColumns(), table.getVisibleLeafColumns(), table.getState().columnPinning.right], (allColumns, leafColumns, right) => {
      var _right$map$filter2;
      const orderedLeafColumns = (_right$map$filter2 = right == null ? void 0 : right.map(columnId => leafColumns.find(d => d.id === columnId)).filter(Boolean)) != null ? _right$map$filter2 : [];
      return buildHeaderGroups(allColumns, orderedLeafColumns, table, 'right');
    }, getMemoOptions(table.options, debug, 'getRightHeaderGroups'));

    // Footer Groups

    table.getFooterGroups = memo(() => [table.getHeaderGroups()], headerGroups => {
      return [...headerGroups].reverse();
    }, getMemoOptions(table.options, debug, 'getFooterGroups'));
    table.getLeftFooterGroups = memo(() => [table.getLeftHeaderGroups()], headerGroups => {
      return [...headerGroups].reverse();
    }, getMemoOptions(table.options, debug, 'getLeftFooterGroups'));
    table.getCenterFooterGroups = memo(() => [table.getCenterHeaderGroups()], headerGroups => {
      return [...headerGroups].reverse();
    }, getMemoOptions(table.options, debug, 'getCenterFooterGroups'));
    table.getRightFooterGroups = memo(() => [table.getRightHeaderGroups()], headerGroups => {
      return [...headerGroups].reverse();
    }, getMemoOptions(table.options, debug, 'getRightFooterGroups'));

    // Flat Headers

    table.getFlatHeaders = memo(() => [table.getHeaderGroups()], headerGroups => {
      return headerGroups.map(headerGroup => {
        return headerGroup.headers;
      }).flat();
    }, getMemoOptions(table.options, debug, 'getFlatHeaders'));
    table.getLeftFlatHeaders = memo(() => [table.getLeftHeaderGroups()], left => {
      return left.map(headerGroup => {
        return headerGroup.headers;
      }).flat();
    }, getMemoOptions(table.options, debug, 'getLeftFlatHeaders'));
    table.getCenterFlatHeaders = memo(() => [table.getCenterHeaderGroups()], left => {
      return left.map(headerGroup => {
        return headerGroup.headers;
      }).flat();
    }, getMemoOptions(table.options, debug, 'getCenterFlatHeaders'));
    table.getRightFlatHeaders = memo(() => [table.getRightHeaderGroups()], left => {
      return left.map(headerGroup => {
        return headerGroup.headers;
      }).flat();
    }, getMemoOptions(table.options, debug, 'getRightFlatHeaders'));

    // Leaf Headers

    table.getCenterLeafHeaders = memo(() => [table.getCenterFlatHeaders()], flatHeaders => {
      return flatHeaders.filter(header => {
        var _header$subHeaders;
        return !((_header$subHeaders = header.subHeaders) != null && _header$subHeaders.length);
      });
    }, getMemoOptions(table.options, debug, 'getCenterLeafHeaders'));
    table.getLeftLeafHeaders = memo(() => [table.getLeftFlatHeaders()], flatHeaders => {
      return flatHeaders.filter(header => {
        var _header$subHeaders2;
        return !((_header$subHeaders2 = header.subHeaders) != null && _header$subHeaders2.length);
      });
    }, getMemoOptions(table.options, debug, 'getLeftLeafHeaders'));
    table.getRightLeafHeaders = memo(() => [table.getRightFlatHeaders()], flatHeaders => {
      return flatHeaders.filter(header => {
        var _header$subHeaders3;
        return !((_header$subHeaders3 = header.subHeaders) != null && _header$subHeaders3.length);
      });
    }, getMemoOptions(table.options, debug, 'getRightLeafHeaders'));
    table.getLeafHeaders = memo(() => [table.getLeftHeaderGroups(), table.getCenterHeaderGroups(), table.getRightHeaderGroups()], (left, center, right) => {
      var _left$0$headers, _left$, _center$0$headers, _center$, _right$0$headers, _right$;
      return [...((_left$0$headers = (_left$ = left[0]) == null ? void 0 : _left$.headers) != null ? _left$0$headers : []), ...((_center$0$headers = (_center$ = center[0]) == null ? void 0 : _center$.headers) != null ? _center$0$headers : []), ...((_right$0$headers = (_right$ = right[0]) == null ? void 0 : _right$.headers) != null ? _right$0$headers : [])].map(header => {
        return header.getLeafHeaders();
      }).flat();
    }, getMemoOptions(table.options, debug, 'getLeafHeaders'));
  }
};
function buildHeaderGroups(allColumns, columnsToGroup, table, headerFamily) {
  var _headerGroups$0$heade, _headerGroups$;
  // Find the max depth of the columns:
  // build the leaf column row
  // build each buffer row going up
  //    placeholder for non-existent level
  //    real column for existing level

  let maxDepth = 0;
  const findMaxDepth = function (columns, depth) {
    if (depth === void 0) {
      depth = 1;
    }
    maxDepth = Math.max(maxDepth, depth);
    columns.filter(column => column.getIsVisible()).forEach(column => {
      var _column$columns;
      if ((_column$columns = column.columns) != null && _column$columns.length) {
        findMaxDepth(column.columns, depth + 1);
      }
    }, 0);
  };
  findMaxDepth(allColumns);
  let headerGroups = [];
  const createHeaderGroup = (headersToGroup, depth) => {
    // The header group we are creating
    const headerGroup = {
      depth,
      id: [headerFamily, `${depth}`].filter(Boolean).join('_'),
      headers: []
    };

    // The parent columns we're going to scan next
    const pendingParentHeaders = [];

    // Scan each column for parents
    headersToGroup.forEach(headerToGroup => {
      // What is the latest (last) parent column?

      const latestPendingParentHeader = [...pendingParentHeaders].reverse()[0];
      const isLeafHeader = headerToGroup.column.depth === headerGroup.depth;
      let column;
      let isPlaceholder = false;
      if (isLeafHeader && headerToGroup.column.parent) {
        // The parent header is new
        column = headerToGroup.column.parent;
      } else {
        // The parent header is repeated
        column = headerToGroup.column;
        isPlaceholder = true;
      }
      if (latestPendingParentHeader && (latestPendingParentHeader == null ? void 0 : latestPendingParentHeader.column) === column) {
        // This column is repeated. Add it as a sub header to the next batch
        latestPendingParentHeader.subHeaders.push(headerToGroup);
      } else {
        // This is a new header. Let's create it
        const header = createHeader(table, column, {
          id: [headerFamily, depth, column.id, headerToGroup == null ? void 0 : headerToGroup.id].filter(Boolean).join('_'),
          isPlaceholder,
          placeholderId: isPlaceholder ? `${pendingParentHeaders.filter(d => d.column === column).length}` : undefined,
          depth,
          index: pendingParentHeaders.length
        });

        // Add the headerToGroup as a subHeader of the new header
        header.subHeaders.push(headerToGroup);
        // Add the new header to the pendingParentHeaders to get grouped
        // in the next batch
        pendingParentHeaders.push(header);
      }
      headerGroup.headers.push(headerToGroup);
      headerToGroup.headerGroup = headerGroup;
    });
    headerGroups.push(headerGroup);
    if (depth > 0) {
      createHeaderGroup(pendingParentHeaders, depth - 1);
    }
  };
  const bottomHeaders = columnsToGroup.map((column, index) => createHeader(table, column, {
    depth: maxDepth,
    index
  }));
  createHeaderGroup(bottomHeaders, maxDepth - 1);
  headerGroups.reverse();

  // headerGroups = headerGroups.filter(headerGroup => {
  //   return !headerGroup.headers.every(header => header.isPlaceholder)
  // })

  const recurseHeadersForSpans = headers => {
    const filteredHeaders = headers.filter(header => header.column.getIsVisible());
    return filteredHeaders.map(header => {
      let colSpan = 0;
      let rowSpan = 0;
      let childRowSpans = [0];
      if (header.subHeaders && header.subHeaders.length) {
        childRowSpans = [];
        recurseHeadersForSpans(header.subHeaders).forEach(_ref => {
          let {
            colSpan: childColSpan,
            rowSpan: childRowSpan
          } = _ref;
          colSpan += childColSpan;
          childRowSpans.push(childRowSpan);
        });
      } else {
        colSpan = 1;
      }
      const minChildRowSpan = Math.min(...childRowSpans);
      rowSpan = rowSpan + minChildRowSpan;
      header.colSpan = colSpan;
      header.rowSpan = rowSpan;
      return {
        colSpan,
        rowSpan
      };
    });
  };
  recurseHeadersForSpans((_headerGroups$0$heade = (_headerGroups$ = headerGroups[0]) == null ? void 0 : _headerGroups$.headers) != null ? _headerGroups$0$heade : []);
  return headerGroups;
}

const createRow = (table, id, original, rowIndex, depth, subRows, parentId) => {
  let row = {
    id,
    index: rowIndex,
    original,
    depth,
    parentId,
    _valuesCache: {},
    _uniqueValuesCache: {},
    getValue: columnId => {
      if (row._valuesCache.hasOwnProperty(columnId)) {
        return row._valuesCache[columnId];
      }
      const column = table.getColumn(columnId);
      if (!(column != null && column.accessorFn)) {
        return undefined;
      }
      row._valuesCache[columnId] = column.accessorFn(row.original, rowIndex);
      return row._valuesCache[columnId];
    },
    getUniqueValues: columnId => {
      if (row._uniqueValuesCache.hasOwnProperty(columnId)) {
        return row._uniqueValuesCache[columnId];
      }
      const column = table.getColumn(columnId);
      if (!(column != null && column.accessorFn)) {
        return undefined;
      }
      if (!column.columnDef.getUniqueValues) {
        row._uniqueValuesCache[columnId] = [row.getValue(columnId)];
        return row._uniqueValuesCache[columnId];
      }
      row._uniqueValuesCache[columnId] = column.columnDef.getUniqueValues(row.original, rowIndex);
      return row._uniqueValuesCache[columnId];
    },
    renderValue: columnId => {
      var _row$getValue;
      return (_row$getValue = row.getValue(columnId)) != null ? _row$getValue : table.options.renderFallbackValue;
    },
    subRows: [],
    getLeafRows: () => flattenBy(row.subRows, d => d.subRows),
    getParentRow: () => row.parentId ? table.getRow(row.parentId, true) : undefined,
    getParentRows: () => {
      let parentRows = [];
      let currentRow = row;
      while (true) {
        const parentRow = currentRow.getParentRow();
        if (!parentRow) break;
        parentRows.push(parentRow);
        currentRow = parentRow;
      }
      return parentRows.reverse();
    },
    getAllCells: memo(() => [table.getAllLeafColumns()], leafColumns => {
      return leafColumns.map(column => {
        return createCell(table, row, column, column.id);
      });
    }, getMemoOptions(table.options, 'debugRows', 'getAllCells')),
    _getAllCellsByColumnId: memo(() => [row.getAllCells()], allCells => {
      return allCells.reduce((acc, cell) => {
        acc[cell.column.id] = cell;
        return acc;
      }, {});
    }, getMemoOptions(table.options, 'debugRows', 'getAllCellsByColumnId'))
  };
  for (let i = 0; i < table._features.length; i++) {
    const feature = table._features[i];
    feature == null || feature.createRow == null || feature.createRow(row, table);
  }
  return row;
};

//

const ColumnFaceting = {
  createColumn: (column, table) => {
    column._getFacetedRowModel = table.options.getFacetedRowModel && table.options.getFacetedRowModel(table, column.id);
    column.getFacetedRowModel = () => {
      if (!column._getFacetedRowModel) {
        return table.getPreFilteredRowModel();
      }
      return column._getFacetedRowModel();
    };
    column._getFacetedUniqueValues = table.options.getFacetedUniqueValues && table.options.getFacetedUniqueValues(table, column.id);
    column.getFacetedUniqueValues = () => {
      if (!column._getFacetedUniqueValues) {
        return new Map();
      }
      return column._getFacetedUniqueValues();
    };
    column._getFacetedMinMaxValues = table.options.getFacetedMinMaxValues && table.options.getFacetedMinMaxValues(table, column.id);
    column.getFacetedMinMaxValues = () => {
      if (!column._getFacetedMinMaxValues) {
        return undefined;
      }
      return column._getFacetedMinMaxValues();
    };
  }
};

const includesString = (row, columnId, filterValue) => {
  var _filterValue$toString, _row$getValue;
  const search = filterValue == null || (_filterValue$toString = filterValue.toString()) == null ? void 0 : _filterValue$toString.toLowerCase();
  return Boolean((_row$getValue = row.getValue(columnId)) == null || (_row$getValue = _row$getValue.toString()) == null || (_row$getValue = _row$getValue.toLowerCase()) == null ? void 0 : _row$getValue.includes(search));
};
includesString.autoRemove = val => testFalsey(val);
const includesStringSensitive = (row, columnId, filterValue) => {
  var _row$getValue2;
  return Boolean((_row$getValue2 = row.getValue(columnId)) == null || (_row$getValue2 = _row$getValue2.toString()) == null ? void 0 : _row$getValue2.includes(filterValue));
};
includesStringSensitive.autoRemove = val => testFalsey(val);
const equalsString = (row, columnId, filterValue) => {
  var _row$getValue3;
  return ((_row$getValue3 = row.getValue(columnId)) == null || (_row$getValue3 = _row$getValue3.toString()) == null ? void 0 : _row$getValue3.toLowerCase()) === (filterValue == null ? void 0 : filterValue.toLowerCase());
};
equalsString.autoRemove = val => testFalsey(val);
const arrIncludes = (row, columnId, filterValue) => {
  var _row$getValue4;
  return (_row$getValue4 = row.getValue(columnId)) == null ? void 0 : _row$getValue4.includes(filterValue);
};
arrIncludes.autoRemove = val => testFalsey(val);
const arrIncludesAll = (row, columnId, filterValue) => {
  return !filterValue.some(val => {
    var _row$getValue5;
    return !((_row$getValue5 = row.getValue(columnId)) != null && _row$getValue5.includes(val));
  });
};
arrIncludesAll.autoRemove = val => testFalsey(val) || !(val != null && val.length);
const arrIncludesSome = (row, columnId, filterValue) => {
  return filterValue.some(val => {
    var _row$getValue6;
    return (_row$getValue6 = row.getValue(columnId)) == null ? void 0 : _row$getValue6.includes(val);
  });
};
arrIncludesSome.autoRemove = val => testFalsey(val) || !(val != null && val.length);
const equals$1 = (row, columnId, filterValue) => {
  return row.getValue(columnId) === filterValue;
};
equals$1.autoRemove = val => testFalsey(val);
const weakEquals = (row, columnId, filterValue) => {
  return row.getValue(columnId) == filterValue;
};
weakEquals.autoRemove = val => testFalsey(val);
const inNumberRange = (row, columnId, filterValue) => {
  let [min, max] = filterValue;
  const rowValue = row.getValue(columnId);
  return rowValue >= min && rowValue <= max;
};
inNumberRange.resolveFilterValue = val => {
  let [unsafeMin, unsafeMax] = val;
  let parsedMin = typeof unsafeMin !== 'number' ? parseFloat(unsafeMin) : unsafeMin;
  let parsedMax = typeof unsafeMax !== 'number' ? parseFloat(unsafeMax) : unsafeMax;
  let min = unsafeMin === null || Number.isNaN(parsedMin) ? -Infinity : parsedMin;
  let max = unsafeMax === null || Number.isNaN(parsedMax) ? Infinity : parsedMax;
  if (min > max) {
    const temp = min;
    min = max;
    max = temp;
  }
  return [min, max];
};
inNumberRange.autoRemove = val => testFalsey(val) || testFalsey(val[0]) && testFalsey(val[1]);

// Export

const filterFns = {
  includesString,
  includesStringSensitive,
  equalsString,
  arrIncludes,
  arrIncludesAll,
  arrIncludesSome,
  equals: equals$1,
  weakEquals,
  inNumberRange
};
// Utils

function testFalsey(val) {
  return val === undefined || val === null || val === '';
}

//

const ColumnFiltering = {
  getDefaultColumnDef: () => {
    return {
      filterFn: 'auto'
    };
  },
  getInitialState: state => {
    return {
      columnFilters: [],
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onColumnFiltersChange: makeStateUpdater('columnFilters', table),
      filterFromLeafRows: false,
      maxLeafRowFilterDepth: 100
    };
  },
  createColumn: (column, table) => {
    column.getAutoFilterFn = () => {
      const firstRow = table.getCoreRowModel().flatRows[0];
      const value = firstRow == null ? void 0 : firstRow.getValue(column.id);
      if (typeof value === 'string') {
        return filterFns.includesString;
      }
      if (typeof value === 'number') {
        return filterFns.inNumberRange;
      }
      if (typeof value === 'boolean') {
        return filterFns.equals;
      }
      if (value !== null && typeof value === 'object') {
        return filterFns.equals;
      }
      if (Array.isArray(value)) {
        return filterFns.arrIncludes;
      }
      return filterFns.weakEquals;
    };
    column.getFilterFn = () => {
      var _table$options$filter, _table$options$filter2;
      return isFunction(column.columnDef.filterFn) ? column.columnDef.filterFn : column.columnDef.filterFn === 'auto' ? column.getAutoFilterFn() : // @ts-ignore
      (_table$options$filter = (_table$options$filter2 = table.options.filterFns) == null ? void 0 : _table$options$filter2[column.columnDef.filterFn]) != null ? _table$options$filter : filterFns[column.columnDef.filterFn];
    };
    column.getCanFilter = () => {
      var _column$columnDef$ena, _table$options$enable, _table$options$enable2;
      return ((_column$columnDef$ena = column.columnDef.enableColumnFilter) != null ? _column$columnDef$ena : true) && ((_table$options$enable = table.options.enableColumnFilters) != null ? _table$options$enable : true) && ((_table$options$enable2 = table.options.enableFilters) != null ? _table$options$enable2 : true) && !!column.accessorFn;
    };
    column.getIsFiltered = () => column.getFilterIndex() > -1;
    column.getFilterValue = () => {
      var _table$getState$colum;
      return (_table$getState$colum = table.getState().columnFilters) == null || (_table$getState$colum = _table$getState$colum.find(d => d.id === column.id)) == null ? void 0 : _table$getState$colum.value;
    };
    column.getFilterIndex = () => {
      var _table$getState$colum2, _table$getState$colum3;
      return (_table$getState$colum2 = (_table$getState$colum3 = table.getState().columnFilters) == null ? void 0 : _table$getState$colum3.findIndex(d => d.id === column.id)) != null ? _table$getState$colum2 : -1;
    };
    column.setFilterValue = value => {
      table.setColumnFilters(old => {
        const filterFn = column.getFilterFn();
        const previousFilter = old == null ? void 0 : old.find(d => d.id === column.id);
        const newFilter = functionalUpdate(value, previousFilter ? previousFilter.value : undefined);

        //
        if (shouldAutoRemoveFilter(filterFn, newFilter, column)) {
          var _old$filter;
          return (_old$filter = old == null ? void 0 : old.filter(d => d.id !== column.id)) != null ? _old$filter : [];
        }
        const newFilterObj = {
          id: column.id,
          value: newFilter
        };
        if (previousFilter) {
          var _old$map;
          return (_old$map = old == null ? void 0 : old.map(d => {
            if (d.id === column.id) {
              return newFilterObj;
            }
            return d;
          })) != null ? _old$map : [];
        }
        if (old != null && old.length) {
          return [...old, newFilterObj];
        }
        return [newFilterObj];
      });
    };
  },
  createRow: (row, _table) => {
    row.columnFilters = {};
    row.columnFiltersMeta = {};
  },
  createTable: table => {
    table.setColumnFilters = updater => {
      const leafColumns = table.getAllLeafColumns();
      const updateFn = old => {
        var _functionalUpdate;
        return (_functionalUpdate = functionalUpdate(updater, old)) == null ? void 0 : _functionalUpdate.filter(filter => {
          const column = leafColumns.find(d => d.id === filter.id);
          if (column) {
            const filterFn = column.getFilterFn();
            if (shouldAutoRemoveFilter(filterFn, filter.value, column)) {
              return false;
            }
          }
          return true;
        });
      };
      table.options.onColumnFiltersChange == null || table.options.onColumnFiltersChange(updateFn);
    };
    table.resetColumnFilters = defaultState => {
      var _table$initialState$c, _table$initialState;
      table.setColumnFilters(defaultState ? [] : (_table$initialState$c = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.columnFilters) != null ? _table$initialState$c : []);
    };
    table.getPreFilteredRowModel = () => table.getCoreRowModel();
    table.getFilteredRowModel = () => {
      if (!table._getFilteredRowModel && table.options.getFilteredRowModel) {
        table._getFilteredRowModel = table.options.getFilteredRowModel(table);
      }
      if (table.options.manualFiltering || !table._getFilteredRowModel) {
        return table.getPreFilteredRowModel();
      }
      return table._getFilteredRowModel();
    };
  }
};
function shouldAutoRemoveFilter(filterFn, value, column) {
  return (filterFn && filterFn.autoRemove ? filterFn.autoRemove(value, column) : false) || typeof value === 'undefined' || typeof value === 'string' && !value;
}

const sum = (columnId, _leafRows, childRows) => {
  // It's faster to just add the aggregations together instead of
  // process leaf nodes individually
  return childRows.reduce((sum, next) => {
    const nextValue = next.getValue(columnId);
    return sum + (typeof nextValue === 'number' ? nextValue : 0);
  }, 0);
};
const min = (columnId, _leafRows, childRows) => {
  let min;
  childRows.forEach(row => {
    const value = row.getValue(columnId);
    if (value != null && (min > value || min === undefined && value >= value)) {
      min = value;
    }
  });
  return min;
};
const max = (columnId, _leafRows, childRows) => {
  let max;
  childRows.forEach(row => {
    const value = row.getValue(columnId);
    if (value != null && (max < value || max === undefined && value >= value)) {
      max = value;
    }
  });
  return max;
};
const extent = (columnId, _leafRows, childRows) => {
  let min;
  let max;
  childRows.forEach(row => {
    const value = row.getValue(columnId);
    if (value != null) {
      if (min === undefined) {
        if (value >= value) min = max = value;
      } else {
        if (min > value) min = value;
        if (max < value) max = value;
      }
    }
  });
  return [min, max];
};
const mean = (columnId, leafRows) => {
  let count = 0;
  let sum = 0;
  leafRows.forEach(row => {
    let value = row.getValue(columnId);
    if (value != null && (value = +value) >= value) {
      ++count, sum += value;
    }
  });
  if (count) return sum / count;
  return;
};
const median = (columnId, leafRows) => {
  if (!leafRows.length) {
    return;
  }
  const values = leafRows.map(row => row.getValue(columnId));
  if (!isNumberArray(values)) {
    return;
  }
  if (values.length === 1) {
    return values[0];
  }
  const mid = Math.floor(values.length / 2);
  const nums = values.sort((a, b) => a - b);
  return values.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};
const unique = (columnId, leafRows) => {
  return Array.from(new Set(leafRows.map(d => d.getValue(columnId))).values());
};
const uniqueCount = (columnId, leafRows) => {
  return new Set(leafRows.map(d => d.getValue(columnId))).size;
};
const count = (_columnId, leafRows) => {
  return leafRows.length;
};
const aggregationFns = {
  sum,
  min,
  max,
  extent,
  mean,
  median,
  unique,
  uniqueCount,
  count
};

//

const ColumnGrouping = {
  getDefaultColumnDef: () => {
    return {
      aggregatedCell: props => {
        var _toString, _props$getValue;
        return (_toString = (_props$getValue = props.getValue()) == null || _props$getValue.toString == null ? void 0 : _props$getValue.toString()) != null ? _toString : null;
      },
      aggregationFn: 'auto'
    };
  },
  getInitialState: state => {
    return {
      grouping: [],
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onGroupingChange: makeStateUpdater('grouping', table),
      groupedColumnMode: 'reorder'
    };
  },
  createColumn: (column, table) => {
    column.toggleGrouping = () => {
      table.setGrouping(old => {
        // Find any existing grouping for this column
        if (old != null && old.includes(column.id)) {
          return old.filter(d => d !== column.id);
        }
        return [...(old != null ? old : []), column.id];
      });
    };
    column.getCanGroup = () => {
      var _column$columnDef$ena, _table$options$enable;
      return ((_column$columnDef$ena = column.columnDef.enableGrouping) != null ? _column$columnDef$ena : true) && ((_table$options$enable = table.options.enableGrouping) != null ? _table$options$enable : true) && (!!column.accessorFn || !!column.columnDef.getGroupingValue);
    };
    column.getIsGrouped = () => {
      var _table$getState$group;
      return (_table$getState$group = table.getState().grouping) == null ? void 0 : _table$getState$group.includes(column.id);
    };
    column.getGroupedIndex = () => {
      var _table$getState$group2;
      return (_table$getState$group2 = table.getState().grouping) == null ? void 0 : _table$getState$group2.indexOf(column.id);
    };
    column.getToggleGroupingHandler = () => {
      const canGroup = column.getCanGroup();
      return () => {
        if (!canGroup) return;
        column.toggleGrouping();
      };
    };
    column.getAutoAggregationFn = () => {
      const firstRow = table.getCoreRowModel().flatRows[0];
      const value = firstRow == null ? void 0 : firstRow.getValue(column.id);
      if (typeof value === 'number') {
        return aggregationFns.sum;
      }
      if (Object.prototype.toString.call(value) === '[object Date]') {
        return aggregationFns.extent;
      }
    };
    column.getAggregationFn = () => {
      var _table$options$aggreg, _table$options$aggreg2;
      if (!column) {
        throw new Error();
      }
      return isFunction(column.columnDef.aggregationFn) ? column.columnDef.aggregationFn : column.columnDef.aggregationFn === 'auto' ? column.getAutoAggregationFn() : (_table$options$aggreg = (_table$options$aggreg2 = table.options.aggregationFns) == null ? void 0 : _table$options$aggreg2[column.columnDef.aggregationFn]) != null ? _table$options$aggreg : aggregationFns[column.columnDef.aggregationFn];
    };
  },
  createTable: table => {
    table.setGrouping = updater => table.options.onGroupingChange == null ? void 0 : table.options.onGroupingChange(updater);
    table.resetGrouping = defaultState => {
      var _table$initialState$g, _table$initialState;
      table.setGrouping(defaultState ? [] : (_table$initialState$g = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.grouping) != null ? _table$initialState$g : []);
    };
    table.getPreGroupedRowModel = () => table.getFilteredRowModel();
    table.getGroupedRowModel = () => {
      if (!table._getGroupedRowModel && table.options.getGroupedRowModel) {
        table._getGroupedRowModel = table.options.getGroupedRowModel(table);
      }
      if (table.options.manualGrouping || !table._getGroupedRowModel) {
        return table.getPreGroupedRowModel();
      }
      return table._getGroupedRowModel();
    };
  },
  createRow: (row, table) => {
    row.getIsGrouped = () => !!row.groupingColumnId;
    row.getGroupingValue = columnId => {
      if (row._groupingValuesCache.hasOwnProperty(columnId)) {
        return row._groupingValuesCache[columnId];
      }
      const column = table.getColumn(columnId);
      if (!(column != null && column.columnDef.getGroupingValue)) {
        return row.getValue(columnId);
      }
      row._groupingValuesCache[columnId] = column.columnDef.getGroupingValue(row.original);
      return row._groupingValuesCache[columnId];
    };
    row._groupingValuesCache = {};
  },
  createCell: (cell, column, row, table) => {
    cell.getIsGrouped = () => column.getIsGrouped() && column.id === row.groupingColumnId;
    cell.getIsPlaceholder = () => !cell.getIsGrouped() && column.getIsGrouped();
    cell.getIsAggregated = () => {
      var _row$subRows;
      return !cell.getIsGrouped() && !cell.getIsPlaceholder() && !!((_row$subRows = row.subRows) != null && _row$subRows.length);
    };
  }
};
function orderColumns(leafColumns, grouping, groupedColumnMode) {
  if (!(grouping != null && grouping.length) || !groupedColumnMode) {
    return leafColumns;
  }
  const nonGroupingColumns = leafColumns.filter(col => !grouping.includes(col.id));
  if (groupedColumnMode === 'remove') {
    return nonGroupingColumns;
  }
  const groupingColumns = grouping.map(g => leafColumns.find(col => col.id === g)).filter(Boolean);
  return [...groupingColumns, ...nonGroupingColumns];
}

//

const ColumnOrdering = {
  getInitialState: state => {
    return {
      columnOrder: [],
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onColumnOrderChange: makeStateUpdater('columnOrder', table)
    };
  },
  createColumn: (column, table) => {
    column.getIndex = memo(position => [_getVisibleLeafColumns(table, position)], columns => columns.findIndex(d => d.id === column.id), getMemoOptions(table.options, 'debugColumns', 'getIndex'));
    column.getIsFirstColumn = position => {
      var _columns$;
      const columns = _getVisibleLeafColumns(table, position);
      return ((_columns$ = columns[0]) == null ? void 0 : _columns$.id) === column.id;
    };
    column.getIsLastColumn = position => {
      var _columns;
      const columns = _getVisibleLeafColumns(table, position);
      return ((_columns = columns[columns.length - 1]) == null ? void 0 : _columns.id) === column.id;
    };
  },
  createTable: table => {
    table.setColumnOrder = updater => table.options.onColumnOrderChange == null ? void 0 : table.options.onColumnOrderChange(updater);
    table.resetColumnOrder = defaultState => {
      var _table$initialState$c;
      table.setColumnOrder(defaultState ? [] : (_table$initialState$c = table.initialState.columnOrder) != null ? _table$initialState$c : []);
    };
    table._getOrderColumnsFn = memo(() => [table.getState().columnOrder, table.getState().grouping, table.options.groupedColumnMode], (columnOrder, grouping, groupedColumnMode) => columns => {
      // Sort grouped columns to the start of the column list
      // before the headers are built
      let orderedColumns = [];

      // If there is no order, return the normal columns
      if (!(columnOrder != null && columnOrder.length)) {
        orderedColumns = columns;
      } else {
        const columnOrderCopy = [...columnOrder];

        // If there is an order, make a copy of the columns
        const columnsCopy = [...columns];

        // And make a new ordered array of the columns

        // Loop over the columns and place them in order into the new array
        while (columnsCopy.length && columnOrderCopy.length) {
          const targetColumnId = columnOrderCopy.shift();
          const foundIndex = columnsCopy.findIndex(d => d.id === targetColumnId);
          if (foundIndex > -1) {
            orderedColumns.push(columnsCopy.splice(foundIndex, 1)[0]);
          }
        }

        // If there are any columns left, add them to the end
        orderedColumns = [...orderedColumns, ...columnsCopy];
      }
      return orderColumns(orderedColumns, grouping, groupedColumnMode);
    }, getMemoOptions(table.options, 'debugTable', '_getOrderColumnsFn'));
  }
};

//

const getDefaultColumnPinningState = () => ({
  left: [],
  right: []
});
const ColumnPinning = {
  getInitialState: state => {
    return {
      columnPinning: getDefaultColumnPinningState(),
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onColumnPinningChange: makeStateUpdater('columnPinning', table)
    };
  },
  createColumn: (column, table) => {
    column.pin = position => {
      const columnIds = column.getLeafColumns().map(d => d.id).filter(Boolean);
      table.setColumnPinning(old => {
        var _old$left3, _old$right3;
        if (position === 'right') {
          var _old$left, _old$right;
          return {
            left: ((_old$left = old == null ? void 0 : old.left) != null ? _old$left : []).filter(d => !(columnIds != null && columnIds.includes(d))),
            right: [...((_old$right = old == null ? void 0 : old.right) != null ? _old$right : []).filter(d => !(columnIds != null && columnIds.includes(d))), ...columnIds]
          };
        }
        if (position === 'left') {
          var _old$left2, _old$right2;
          return {
            left: [...((_old$left2 = old == null ? void 0 : old.left) != null ? _old$left2 : []).filter(d => !(columnIds != null && columnIds.includes(d))), ...columnIds],
            right: ((_old$right2 = old == null ? void 0 : old.right) != null ? _old$right2 : []).filter(d => !(columnIds != null && columnIds.includes(d)))
          };
        }
        return {
          left: ((_old$left3 = old == null ? void 0 : old.left) != null ? _old$left3 : []).filter(d => !(columnIds != null && columnIds.includes(d))),
          right: ((_old$right3 = old == null ? void 0 : old.right) != null ? _old$right3 : []).filter(d => !(columnIds != null && columnIds.includes(d)))
        };
      });
    };
    column.getCanPin = () => {
      const leafColumns = column.getLeafColumns();
      return leafColumns.some(d => {
        var _d$columnDef$enablePi, _ref, _table$options$enable;
        return ((_d$columnDef$enablePi = d.columnDef.enablePinning) != null ? _d$columnDef$enablePi : true) && ((_ref = (_table$options$enable = table.options.enableColumnPinning) != null ? _table$options$enable : table.options.enablePinning) != null ? _ref : true);
      });
    };
    column.getIsPinned = () => {
      const leafColumnIds = column.getLeafColumns().map(d => d.id);
      const {
        left,
        right
      } = table.getState().columnPinning;
      const isLeft = leafColumnIds.some(d => left == null ? void 0 : left.includes(d));
      const isRight = leafColumnIds.some(d => right == null ? void 0 : right.includes(d));
      return isLeft ? 'left' : isRight ? 'right' : false;
    };
    column.getPinnedIndex = () => {
      var _table$getState$colum, _table$getState$colum2;
      const position = column.getIsPinned();
      return position ? (_table$getState$colum = (_table$getState$colum2 = table.getState().columnPinning) == null || (_table$getState$colum2 = _table$getState$colum2[position]) == null ? void 0 : _table$getState$colum2.indexOf(column.id)) != null ? _table$getState$colum : -1 : 0;
    };
  },
  createRow: (row, table) => {
    row.getCenterVisibleCells = memo(() => [row._getAllVisibleCells(), table.getState().columnPinning.left, table.getState().columnPinning.right], (allCells, left, right) => {
      const leftAndRight = [...(left != null ? left : []), ...(right != null ? right : [])];
      return allCells.filter(d => !leftAndRight.includes(d.column.id));
    }, getMemoOptions(table.options, 'debugRows', 'getCenterVisibleCells'));
    row.getLeftVisibleCells = memo(() => [row._getAllVisibleCells(), table.getState().columnPinning.left], (allCells, left) => {
      const cells = (left != null ? left : []).map(columnId => allCells.find(cell => cell.column.id === columnId)).filter(Boolean).map(d => ({
        ...d,
        position: 'left'
      }));
      return cells;
    }, getMemoOptions(table.options, 'debugRows', 'getLeftVisibleCells'));
    row.getRightVisibleCells = memo(() => [row._getAllVisibleCells(), table.getState().columnPinning.right], (allCells, right) => {
      const cells = (right != null ? right : []).map(columnId => allCells.find(cell => cell.column.id === columnId)).filter(Boolean).map(d => ({
        ...d,
        position: 'right'
      }));
      return cells;
    }, getMemoOptions(table.options, 'debugRows', 'getRightVisibleCells'));
  },
  createTable: table => {
    table.setColumnPinning = updater => table.options.onColumnPinningChange == null ? void 0 : table.options.onColumnPinningChange(updater);
    table.resetColumnPinning = defaultState => {
      var _table$initialState$c, _table$initialState;
      return table.setColumnPinning(defaultState ? getDefaultColumnPinningState() : (_table$initialState$c = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.columnPinning) != null ? _table$initialState$c : getDefaultColumnPinningState());
    };
    table.getIsSomeColumnsPinned = position => {
      var _pinningState$positio;
      const pinningState = table.getState().columnPinning;
      if (!position) {
        var _pinningState$left, _pinningState$right;
        return Boolean(((_pinningState$left = pinningState.left) == null ? void 0 : _pinningState$left.length) || ((_pinningState$right = pinningState.right) == null ? void 0 : _pinningState$right.length));
      }
      return Boolean((_pinningState$positio = pinningState[position]) == null ? void 0 : _pinningState$positio.length);
    };
    table.getLeftLeafColumns = memo(() => [table.getAllLeafColumns(), table.getState().columnPinning.left], (allColumns, left) => {
      return (left != null ? left : []).map(columnId => allColumns.find(column => column.id === columnId)).filter(Boolean);
    }, getMemoOptions(table.options, 'debugColumns', 'getLeftLeafColumns'));
    table.getRightLeafColumns = memo(() => [table.getAllLeafColumns(), table.getState().columnPinning.right], (allColumns, right) => {
      return (right != null ? right : []).map(columnId => allColumns.find(column => column.id === columnId)).filter(Boolean);
    }, getMemoOptions(table.options, 'debugColumns', 'getRightLeafColumns'));
    table.getCenterLeafColumns = memo(() => [table.getAllLeafColumns(), table.getState().columnPinning.left, table.getState().columnPinning.right], (allColumns, left, right) => {
      const leftAndRight = [...(left != null ? left : []), ...(right != null ? right : [])];
      return allColumns.filter(d => !leftAndRight.includes(d.id));
    }, getMemoOptions(table.options, 'debugColumns', 'getCenterLeafColumns'));
  }
};

//

//

const defaultColumnSizing = {
  size: 150,
  minSize: 20,
  maxSize: Number.MAX_SAFE_INTEGER
};
const getDefaultColumnSizingInfoState = () => ({
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  columnSizingStart: []
});
const ColumnSizing = {
  getDefaultColumnDef: () => {
    return defaultColumnSizing;
  },
  getInitialState: state => {
    return {
      columnSizing: {},
      columnSizingInfo: getDefaultColumnSizingInfoState(),
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      columnResizeMode: 'onEnd',
      columnResizeDirection: 'ltr',
      onColumnSizingChange: makeStateUpdater('columnSizing', table),
      onColumnSizingInfoChange: makeStateUpdater('columnSizingInfo', table)
    };
  },
  createColumn: (column, table) => {
    column.getSize = () => {
      var _column$columnDef$min, _ref, _column$columnDef$max;
      const columnSize = table.getState().columnSizing[column.id];
      return Math.min(Math.max((_column$columnDef$min = column.columnDef.minSize) != null ? _column$columnDef$min : defaultColumnSizing.minSize, (_ref = columnSize != null ? columnSize : column.columnDef.size) != null ? _ref : defaultColumnSizing.size), (_column$columnDef$max = column.columnDef.maxSize) != null ? _column$columnDef$max : defaultColumnSizing.maxSize);
    };
    column.getStart = memo(position => [position, _getVisibleLeafColumns(table, position), table.getState().columnSizing], (position, columns) => columns.slice(0, column.getIndex(position)).reduce((sum, column) => sum + column.getSize(), 0), getMemoOptions(table.options, 'debugColumns', 'getStart'));
    column.getAfter = memo(position => [position, _getVisibleLeafColumns(table, position), table.getState().columnSizing], (position, columns) => columns.slice(column.getIndex(position) + 1).reduce((sum, column) => sum + column.getSize(), 0), getMemoOptions(table.options, 'debugColumns', 'getAfter'));
    column.resetSize = () => {
      table.setColumnSizing(_ref2 => {
        let {
          [column.id]: _,
          ...rest
        } = _ref2;
        return rest;
      });
    };
    column.getCanResize = () => {
      var _column$columnDef$ena, _table$options$enable;
      return ((_column$columnDef$ena = column.columnDef.enableResizing) != null ? _column$columnDef$ena : true) && ((_table$options$enable = table.options.enableColumnResizing) != null ? _table$options$enable : true);
    };
    column.getIsResizing = () => {
      return table.getState().columnSizingInfo.isResizingColumn === column.id;
    };
  },
  createHeader: (header, table) => {
    header.getSize = () => {
      let sum = 0;
      const recurse = header => {
        if (header.subHeaders.length) {
          header.subHeaders.forEach(recurse);
        } else {
          var _header$column$getSiz;
          sum += (_header$column$getSiz = header.column.getSize()) != null ? _header$column$getSiz : 0;
        }
      };
      recurse(header);
      return sum;
    };
    header.getStart = () => {
      if (header.index > 0) {
        const prevSiblingHeader = header.headerGroup.headers[header.index - 1];
        return prevSiblingHeader.getStart() + prevSiblingHeader.getSize();
      }
      return 0;
    };
    header.getResizeHandler = _contextDocument => {
      const column = table.getColumn(header.column.id);
      const canResize = column == null ? void 0 : column.getCanResize();
      return e => {
        if (!column || !canResize) {
          return;
        }
        e.persist == null || e.persist();
        if (isTouchStartEvent(e)) {
          // lets not respond to multiple touches (e.g. 2 or 3 fingers)
          if (e.touches && e.touches.length > 1) {
            return;
          }
        }
        const startSize = header.getSize();
        const columnSizingStart = header ? header.getLeafHeaders().map(d => [d.column.id, d.column.getSize()]) : [[column.id, column.getSize()]];
        const clientX = isTouchStartEvent(e) ? Math.round(e.touches[0].clientX) : e.clientX;
        const newColumnSizing = {};
        const updateOffset = (eventType, clientXPos) => {
          if (typeof clientXPos !== 'number') {
            return;
          }
          table.setColumnSizingInfo(old => {
            var _old$startOffset, _old$startSize;
            const deltaDirection = table.options.columnResizeDirection === 'rtl' ? -1 : 1;
            const deltaOffset = (clientXPos - ((_old$startOffset = old == null ? void 0 : old.startOffset) != null ? _old$startOffset : 0)) * deltaDirection;
            const deltaPercentage = Math.max(deltaOffset / ((_old$startSize = old == null ? void 0 : old.startSize) != null ? _old$startSize : 0), -0.999999);
            old.columnSizingStart.forEach(_ref3 => {
              let [columnId, headerSize] = _ref3;
              newColumnSizing[columnId] = Math.round(Math.max(headerSize + headerSize * deltaPercentage, 0) * 100) / 100;
            });
            return {
              ...old,
              deltaOffset,
              deltaPercentage
            };
          });
          if (table.options.columnResizeMode === 'onChange' || eventType === 'end') {
            table.setColumnSizing(old => ({
              ...old,
              ...newColumnSizing
            }));
          }
        };
        const onMove = clientXPos => updateOffset('move', clientXPos);
        const onEnd = clientXPos => {
          updateOffset('end', clientXPos);
          table.setColumnSizingInfo(old => ({
            ...old,
            isResizingColumn: false,
            startOffset: null,
            startSize: null,
            deltaOffset: null,
            deltaPercentage: null,
            columnSizingStart: []
          }));
        };
        const contextDocument = _contextDocument || typeof document !== 'undefined' ? document : null;
        const mouseEvents = {
          moveHandler: e => onMove(e.clientX),
          upHandler: e => {
            contextDocument == null || contextDocument.removeEventListener('mousemove', mouseEvents.moveHandler);
            contextDocument == null || contextDocument.removeEventListener('mouseup', mouseEvents.upHandler);
            onEnd(e.clientX);
          }
        };
        const touchEvents = {
          moveHandler: e => {
            if (e.cancelable) {
              e.preventDefault();
              e.stopPropagation();
            }
            onMove(e.touches[0].clientX);
            return false;
          },
          upHandler: e => {
            var _e$touches$;
            contextDocument == null || contextDocument.removeEventListener('touchmove', touchEvents.moveHandler);
            contextDocument == null || contextDocument.removeEventListener('touchend', touchEvents.upHandler);
            if (e.cancelable) {
              e.preventDefault();
              e.stopPropagation();
            }
            onEnd((_e$touches$ = e.touches[0]) == null ? void 0 : _e$touches$.clientX);
          }
        };
        const passiveIfSupported = passiveEventSupported() ? {
          passive: false
        } : false;
        if (isTouchStartEvent(e)) {
          contextDocument == null || contextDocument.addEventListener('touchmove', touchEvents.moveHandler, passiveIfSupported);
          contextDocument == null || contextDocument.addEventListener('touchend', touchEvents.upHandler, passiveIfSupported);
        } else {
          contextDocument == null || contextDocument.addEventListener('mousemove', mouseEvents.moveHandler, passiveIfSupported);
          contextDocument == null || contextDocument.addEventListener('mouseup', mouseEvents.upHandler, passiveIfSupported);
        }
        table.setColumnSizingInfo(old => ({
          ...old,
          startOffset: clientX,
          startSize,
          deltaOffset: 0,
          deltaPercentage: 0,
          columnSizingStart,
          isResizingColumn: column.id
        }));
      };
    };
  },
  createTable: table => {
    table.setColumnSizing = updater => table.options.onColumnSizingChange == null ? void 0 : table.options.onColumnSizingChange(updater);
    table.setColumnSizingInfo = updater => table.options.onColumnSizingInfoChange == null ? void 0 : table.options.onColumnSizingInfoChange(updater);
    table.resetColumnSizing = defaultState => {
      var _table$initialState$c;
      table.setColumnSizing(defaultState ? {} : (_table$initialState$c = table.initialState.columnSizing) != null ? _table$initialState$c : {});
    };
    table.resetHeaderSizeInfo = defaultState => {
      var _table$initialState$c2;
      table.setColumnSizingInfo(defaultState ? getDefaultColumnSizingInfoState() : (_table$initialState$c2 = table.initialState.columnSizingInfo) != null ? _table$initialState$c2 : getDefaultColumnSizingInfoState());
    };
    table.getTotalSize = () => {
      var _table$getHeaderGroup, _table$getHeaderGroup2;
      return (_table$getHeaderGroup = (_table$getHeaderGroup2 = table.getHeaderGroups()[0]) == null ? void 0 : _table$getHeaderGroup2.headers.reduce((sum, header) => {
        return sum + header.getSize();
      }, 0)) != null ? _table$getHeaderGroup : 0;
    };
    table.getLeftTotalSize = () => {
      var _table$getLeftHeaderG, _table$getLeftHeaderG2;
      return (_table$getLeftHeaderG = (_table$getLeftHeaderG2 = table.getLeftHeaderGroups()[0]) == null ? void 0 : _table$getLeftHeaderG2.headers.reduce((sum, header) => {
        return sum + header.getSize();
      }, 0)) != null ? _table$getLeftHeaderG : 0;
    };
    table.getCenterTotalSize = () => {
      var _table$getCenterHeade, _table$getCenterHeade2;
      return (_table$getCenterHeade = (_table$getCenterHeade2 = table.getCenterHeaderGroups()[0]) == null ? void 0 : _table$getCenterHeade2.headers.reduce((sum, header) => {
        return sum + header.getSize();
      }, 0)) != null ? _table$getCenterHeade : 0;
    };
    table.getRightTotalSize = () => {
      var _table$getRightHeader, _table$getRightHeader2;
      return (_table$getRightHeader = (_table$getRightHeader2 = table.getRightHeaderGroups()[0]) == null ? void 0 : _table$getRightHeader2.headers.reduce((sum, header) => {
        return sum + header.getSize();
      }, 0)) != null ? _table$getRightHeader : 0;
    };
  }
};
let passiveSupported = null;
function passiveEventSupported() {
  if (typeof passiveSupported === 'boolean') return passiveSupported;
  let supported = false;
  try {
    const options = {
      get passive() {
        supported = true;
        return false;
      }
    };
    const noop = () => {};
    window.addEventListener('test', noop, options);
    window.removeEventListener('test', noop);
  } catch (err) {
    supported = false;
  }
  passiveSupported = supported;
  return passiveSupported;
}
function isTouchStartEvent(e) {
  return e.type === 'touchstart';
}

//

const ColumnVisibility = {
  getInitialState: state => {
    return {
      columnVisibility: {},
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onColumnVisibilityChange: makeStateUpdater('columnVisibility', table)
    };
  },
  createColumn: (column, table) => {
    column.toggleVisibility = value => {
      if (column.getCanHide()) {
        table.setColumnVisibility(old => ({
          ...old,
          [column.id]: value != null ? value : !column.getIsVisible()
        }));
      }
    };
    column.getIsVisible = () => {
      var _ref, _table$getState$colum;
      const childColumns = column.columns;
      return (_ref = childColumns.length ? childColumns.some(c => c.getIsVisible()) : (_table$getState$colum = table.getState().columnVisibility) == null ? void 0 : _table$getState$colum[column.id]) != null ? _ref : true;
    };
    column.getCanHide = () => {
      var _column$columnDef$ena, _table$options$enable;
      return ((_column$columnDef$ena = column.columnDef.enableHiding) != null ? _column$columnDef$ena : true) && ((_table$options$enable = table.options.enableHiding) != null ? _table$options$enable : true);
    };
    column.getToggleVisibilityHandler = () => {
      return e => {
        column.toggleVisibility == null || column.toggleVisibility(e.target.checked);
      };
    };
  },
  createRow: (row, table) => {
    row._getAllVisibleCells = memo(() => [row.getAllCells(), table.getState().columnVisibility], cells => {
      return cells.filter(cell => cell.column.getIsVisible());
    }, getMemoOptions(table.options, 'debugRows', '_getAllVisibleCells'));
    row.getVisibleCells = memo(() => [row.getLeftVisibleCells(), row.getCenterVisibleCells(), row.getRightVisibleCells()], (left, center, right) => [...left, ...center, ...right], getMemoOptions(table.options, 'debugRows', 'getVisibleCells'));
  },
  createTable: table => {
    const makeVisibleColumnsMethod = (key, getColumns) => {
      return memo(() => [getColumns(), getColumns().filter(d => d.getIsVisible()).map(d => d.id).join('_')], columns => {
        return columns.filter(d => d.getIsVisible == null ? void 0 : d.getIsVisible());
      }, getMemoOptions(table.options, 'debugColumns', key));
    };
    table.getVisibleFlatColumns = makeVisibleColumnsMethod('getVisibleFlatColumns', () => table.getAllFlatColumns());
    table.getVisibleLeafColumns = makeVisibleColumnsMethod('getVisibleLeafColumns', () => table.getAllLeafColumns());
    table.getLeftVisibleLeafColumns = makeVisibleColumnsMethod('getLeftVisibleLeafColumns', () => table.getLeftLeafColumns());
    table.getRightVisibleLeafColumns = makeVisibleColumnsMethod('getRightVisibleLeafColumns', () => table.getRightLeafColumns());
    table.getCenterVisibleLeafColumns = makeVisibleColumnsMethod('getCenterVisibleLeafColumns', () => table.getCenterLeafColumns());
    table.setColumnVisibility = updater => table.options.onColumnVisibilityChange == null ? void 0 : table.options.onColumnVisibilityChange(updater);
    table.resetColumnVisibility = defaultState => {
      var _table$initialState$c;
      table.setColumnVisibility(defaultState ? {} : (_table$initialState$c = table.initialState.columnVisibility) != null ? _table$initialState$c : {});
    };
    table.toggleAllColumnsVisible = value => {
      var _value;
      value = (_value = value) != null ? _value : !table.getIsAllColumnsVisible();
      table.setColumnVisibility(table.getAllLeafColumns().reduce((obj, column) => ({
        ...obj,
        [column.id]: !value ? !(column.getCanHide != null && column.getCanHide()) : value
      }), {}));
    };
    table.getIsAllColumnsVisible = () => !table.getAllLeafColumns().some(column => !(column.getIsVisible != null && column.getIsVisible()));
    table.getIsSomeColumnsVisible = () => table.getAllLeafColumns().some(column => column.getIsVisible == null ? void 0 : column.getIsVisible());
    table.getToggleAllColumnsVisibilityHandler = () => {
      return e => {
        var _target;
        table.toggleAllColumnsVisible((_target = e.target) == null ? void 0 : _target.checked);
      };
    };
  }
};
function _getVisibleLeafColumns(table, position) {
  return !position ? table.getVisibleLeafColumns() : position === 'center' ? table.getCenterVisibleLeafColumns() : position === 'left' ? table.getLeftVisibleLeafColumns() : table.getRightVisibleLeafColumns();
}

//

const GlobalFaceting = {
  createTable: table => {
    table._getGlobalFacetedRowModel = table.options.getFacetedRowModel && table.options.getFacetedRowModel(table, '__global__');
    table.getGlobalFacetedRowModel = () => {
      if (table.options.manualFiltering || !table._getGlobalFacetedRowModel) {
        return table.getPreFilteredRowModel();
      }
      return table._getGlobalFacetedRowModel();
    };
    table._getGlobalFacetedUniqueValues = table.options.getFacetedUniqueValues && table.options.getFacetedUniqueValues(table, '__global__');
    table.getGlobalFacetedUniqueValues = () => {
      if (!table._getGlobalFacetedUniqueValues) {
        return new Map();
      }
      return table._getGlobalFacetedUniqueValues();
    };
    table._getGlobalFacetedMinMaxValues = table.options.getFacetedMinMaxValues && table.options.getFacetedMinMaxValues(table, '__global__');
    table.getGlobalFacetedMinMaxValues = () => {
      if (!table._getGlobalFacetedMinMaxValues) {
        return;
      }
      return table._getGlobalFacetedMinMaxValues();
    };
  }
};

//

const GlobalFiltering = {
  getInitialState: state => {
    return {
      globalFilter: undefined,
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onGlobalFilterChange: makeStateUpdater('globalFilter', table),
      globalFilterFn: 'auto',
      getColumnCanGlobalFilter: column => {
        var _table$getCoreRowMode;
        const value = (_table$getCoreRowMode = table.getCoreRowModel().flatRows[0]) == null || (_table$getCoreRowMode = _table$getCoreRowMode._getAllCellsByColumnId()[column.id]) == null ? void 0 : _table$getCoreRowMode.getValue();
        return typeof value === 'string' || typeof value === 'number';
      }
    };
  },
  createColumn: (column, table) => {
    column.getCanGlobalFilter = () => {
      var _column$columnDef$ena, _table$options$enable, _table$options$enable2, _table$options$getCol;
      return ((_column$columnDef$ena = column.columnDef.enableGlobalFilter) != null ? _column$columnDef$ena : true) && ((_table$options$enable = table.options.enableGlobalFilter) != null ? _table$options$enable : true) && ((_table$options$enable2 = table.options.enableFilters) != null ? _table$options$enable2 : true) && ((_table$options$getCol = table.options.getColumnCanGlobalFilter == null ? void 0 : table.options.getColumnCanGlobalFilter(column)) != null ? _table$options$getCol : true) && !!column.accessorFn;
    };
  },
  createTable: table => {
    table.getGlobalAutoFilterFn = () => {
      return filterFns.includesString;
    };
    table.getGlobalFilterFn = () => {
      var _table$options$filter, _table$options$filter2;
      const {
        globalFilterFn: globalFilterFn
      } = table.options;
      return isFunction(globalFilterFn) ? globalFilterFn : globalFilterFn === 'auto' ? table.getGlobalAutoFilterFn() : (_table$options$filter = (_table$options$filter2 = table.options.filterFns) == null ? void 0 : _table$options$filter2[globalFilterFn]) != null ? _table$options$filter : filterFns[globalFilterFn];
    };
    table.setGlobalFilter = updater => {
      table.options.onGlobalFilterChange == null || table.options.onGlobalFilterChange(updater);
    };
    table.resetGlobalFilter = defaultState => {
      table.setGlobalFilter(defaultState ? undefined : table.initialState.globalFilter);
    };
  }
};

//

const RowExpanding = {
  getInitialState: state => {
    return {
      expanded: {},
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onExpandedChange: makeStateUpdater('expanded', table),
      paginateExpandedRows: true
    };
  },
  createTable: table => {
    let registered = false;
    let queued = false;
    table._autoResetExpanded = () => {
      var _ref, _table$options$autoRe;
      if (!registered) {
        table._queue(() => {
          registered = true;
        });
        return;
      }
      if ((_ref = (_table$options$autoRe = table.options.autoResetAll) != null ? _table$options$autoRe : table.options.autoResetExpanded) != null ? _ref : !table.options.manualExpanding) {
        if (queued) return;
        queued = true;
        table._queue(() => {
          table.resetExpanded();
          queued = false;
        });
      }
    };
    table.setExpanded = updater => table.options.onExpandedChange == null ? void 0 : table.options.onExpandedChange(updater);
    table.toggleAllRowsExpanded = expanded => {
      if (expanded != null ? expanded : !table.getIsAllRowsExpanded()) {
        table.setExpanded(true);
      } else {
        table.setExpanded({});
      }
    };
    table.resetExpanded = defaultState => {
      var _table$initialState$e, _table$initialState;
      table.setExpanded(defaultState ? {} : (_table$initialState$e = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.expanded) != null ? _table$initialState$e : {});
    };
    table.getCanSomeRowsExpand = () => {
      return table.getPrePaginationRowModel().flatRows.some(row => row.getCanExpand());
    };
    table.getToggleAllRowsExpandedHandler = () => {
      return e => {
        e.persist == null || e.persist();
        table.toggleAllRowsExpanded();
      };
    };
    table.getIsSomeRowsExpanded = () => {
      const expanded = table.getState().expanded;
      return expanded === true || Object.values(expanded).some(Boolean);
    };
    table.getIsAllRowsExpanded = () => {
      const expanded = table.getState().expanded;

      // If expanded is true, save some cycles and return true
      if (typeof expanded === 'boolean') {
        return expanded === true;
      }
      if (!Object.keys(expanded).length) {
        return false;
      }

      // If any row is not expanded, return false
      if (table.getRowModel().flatRows.some(row => !row.getIsExpanded())) {
        return false;
      }

      // They must all be expanded :shrug:
      return true;
    };
    table.getExpandedDepth = () => {
      let maxDepth = 0;
      const rowIds = table.getState().expanded === true ? Object.keys(table.getRowModel().rowsById) : Object.keys(table.getState().expanded);
      rowIds.forEach(id => {
        const splitId = id.split('.');
        maxDepth = Math.max(maxDepth, splitId.length);
      });
      return maxDepth;
    };
    table.getPreExpandedRowModel = () => table.getSortedRowModel();
    table.getExpandedRowModel = () => {
      if (!table._getExpandedRowModel && table.options.getExpandedRowModel) {
        table._getExpandedRowModel = table.options.getExpandedRowModel(table);
      }
      if (table.options.manualExpanding || !table._getExpandedRowModel) {
        return table.getPreExpandedRowModel();
      }
      return table._getExpandedRowModel();
    };
  },
  createRow: (row, table) => {
    row.toggleExpanded = expanded => {
      table.setExpanded(old => {
        var _expanded;
        const exists = old === true ? true : !!(old != null && old[row.id]);
        let oldExpanded = {};
        if (old === true) {
          Object.keys(table.getRowModel().rowsById).forEach(rowId => {
            oldExpanded[rowId] = true;
          });
        } else {
          oldExpanded = old;
        }
        expanded = (_expanded = expanded) != null ? _expanded : !exists;
        if (!exists && expanded) {
          return {
            ...oldExpanded,
            [row.id]: true
          };
        }
        if (exists && !expanded) {
          const {
            [row.id]: _,
            ...rest
          } = oldExpanded;
          return rest;
        }
        return old;
      });
    };
    row.getIsExpanded = () => {
      var _table$options$getIsR;
      const expanded = table.getState().expanded;
      return !!((_table$options$getIsR = table.options.getIsRowExpanded == null ? void 0 : table.options.getIsRowExpanded(row)) != null ? _table$options$getIsR : expanded === true || (expanded == null ? void 0 : expanded[row.id]));
    };
    row.getCanExpand = () => {
      var _table$options$getRow, _table$options$enable, _row$subRows;
      return (_table$options$getRow = table.options.getRowCanExpand == null ? void 0 : table.options.getRowCanExpand(row)) != null ? _table$options$getRow : ((_table$options$enable = table.options.enableExpanding) != null ? _table$options$enable : true) && !!((_row$subRows = row.subRows) != null && _row$subRows.length);
    };
    row.getIsAllParentsExpanded = () => {
      let isFullyExpanded = true;
      let currentRow = row;
      while (isFullyExpanded && currentRow.parentId) {
        currentRow = table.getRow(currentRow.parentId, true);
        isFullyExpanded = currentRow.getIsExpanded();
      }
      return isFullyExpanded;
    };
    row.getToggleExpandedHandler = () => {
      const canExpand = row.getCanExpand();
      return () => {
        if (!canExpand) return;
        row.toggleExpanded();
      };
    };
  }
};

//

const defaultPageIndex = 0;
const defaultPageSize = 10;
const getDefaultPaginationState = () => ({
  pageIndex: defaultPageIndex,
  pageSize: defaultPageSize
});
const RowPagination = {
  getInitialState: state => {
    return {
      ...state,
      pagination: {
        ...getDefaultPaginationState(),
        ...(state == null ? void 0 : state.pagination)
      }
    };
  },
  getDefaultOptions: table => {
    return {
      onPaginationChange: makeStateUpdater('pagination', table)
    };
  },
  createTable: table => {
    let registered = false;
    let queued = false;
    table._autoResetPageIndex = () => {
      var _ref, _table$options$autoRe;
      if (!registered) {
        table._queue(() => {
          registered = true;
        });
        return;
      }
      if ((_ref = (_table$options$autoRe = table.options.autoResetAll) != null ? _table$options$autoRe : table.options.autoResetPageIndex) != null ? _ref : !table.options.manualPagination) {
        if (queued) return;
        queued = true;
        table._queue(() => {
          table.resetPageIndex();
          queued = false;
        });
      }
    };
    table.setPagination = updater => {
      const safeUpdater = old => {
        let newState = functionalUpdate(updater, old);
        return newState;
      };
      return table.options.onPaginationChange == null ? void 0 : table.options.onPaginationChange(safeUpdater);
    };
    table.resetPagination = defaultState => {
      var _table$initialState$p;
      table.setPagination(defaultState ? getDefaultPaginationState() : (_table$initialState$p = table.initialState.pagination) != null ? _table$initialState$p : getDefaultPaginationState());
    };
    table.setPageIndex = updater => {
      table.setPagination(old => {
        let pageIndex = functionalUpdate(updater, old.pageIndex);
        const maxPageIndex = typeof table.options.pageCount === 'undefined' || table.options.pageCount === -1 ? Number.MAX_SAFE_INTEGER : table.options.pageCount - 1;
        pageIndex = Math.max(0, Math.min(pageIndex, maxPageIndex));
        return {
          ...old,
          pageIndex
        };
      });
    };
    table.resetPageIndex = defaultState => {
      var _table$initialState$p2, _table$initialState;
      table.setPageIndex(defaultState ? defaultPageIndex : (_table$initialState$p2 = (_table$initialState = table.initialState) == null || (_table$initialState = _table$initialState.pagination) == null ? void 0 : _table$initialState.pageIndex) != null ? _table$initialState$p2 : defaultPageIndex);
    };
    table.resetPageSize = defaultState => {
      var _table$initialState$p3, _table$initialState2;
      table.setPageSize(defaultState ? defaultPageSize : (_table$initialState$p3 = (_table$initialState2 = table.initialState) == null || (_table$initialState2 = _table$initialState2.pagination) == null ? void 0 : _table$initialState2.pageSize) != null ? _table$initialState$p3 : defaultPageSize);
    };
    table.setPageSize = updater => {
      table.setPagination(old => {
        const pageSize = Math.max(1, functionalUpdate(updater, old.pageSize));
        const topRowIndex = old.pageSize * old.pageIndex;
        const pageIndex = Math.floor(topRowIndex / pageSize);
        return {
          ...old,
          pageIndex,
          pageSize
        };
      });
    };
    //deprecated
    table.setPageCount = updater => table.setPagination(old => {
      var _table$options$pageCo;
      let newPageCount = functionalUpdate(updater, (_table$options$pageCo = table.options.pageCount) != null ? _table$options$pageCo : -1);
      if (typeof newPageCount === 'number') {
        newPageCount = Math.max(-1, newPageCount);
      }
      return {
        ...old,
        pageCount: newPageCount
      };
    });
    table.getPageOptions = memo(() => [table.getPageCount()], pageCount => {
      let pageOptions = [];
      if (pageCount && pageCount > 0) {
        pageOptions = [...new Array(pageCount)].fill(null).map((_, i) => i);
      }
      return pageOptions;
    }, getMemoOptions(table.options, 'debugTable', 'getPageOptions'));
    table.getCanPreviousPage = () => table.getState().pagination.pageIndex > 0;
    table.getCanNextPage = () => {
      const {
        pageIndex
      } = table.getState().pagination;
      const pageCount = table.getPageCount();
      if (pageCount === -1) {
        return true;
      }
      if (pageCount === 0) {
        return false;
      }
      return pageIndex < pageCount - 1;
    };
    table.previousPage = () => {
      return table.setPageIndex(old => old - 1);
    };
    table.nextPage = () => {
      return table.setPageIndex(old => {
        return old + 1;
      });
    };
    table.firstPage = () => {
      return table.setPageIndex(0);
    };
    table.lastPage = () => {
      return table.setPageIndex(table.getPageCount() - 1);
    };
    table.getPrePaginationRowModel = () => table.getExpandedRowModel();
    table.getPaginationRowModel = () => {
      if (!table._getPaginationRowModel && table.options.getPaginationRowModel) {
        table._getPaginationRowModel = table.options.getPaginationRowModel(table);
      }
      if (table.options.manualPagination || !table._getPaginationRowModel) {
        return table.getPrePaginationRowModel();
      }
      return table._getPaginationRowModel();
    };
    table.getPageCount = () => {
      var _table$options$pageCo2;
      return (_table$options$pageCo2 = table.options.pageCount) != null ? _table$options$pageCo2 : Math.ceil(table.getRowCount() / table.getState().pagination.pageSize);
    };
    table.getRowCount = () => {
      var _table$options$rowCou;
      return (_table$options$rowCou = table.options.rowCount) != null ? _table$options$rowCou : table.getPrePaginationRowModel().rows.length;
    };
  }
};

//

const getDefaultRowPinningState = () => ({
  top: [],
  bottom: []
});
const RowPinning = {
  getInitialState: state => {
    return {
      rowPinning: getDefaultRowPinningState(),
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onRowPinningChange: makeStateUpdater('rowPinning', table)
    };
  },
  createRow: (row, table) => {
    row.pin = (position, includeLeafRows, includeParentRows) => {
      const leafRowIds = includeLeafRows ? row.getLeafRows().map(_ref => {
        let {
          id
        } = _ref;
        return id;
      }) : [];
      const parentRowIds = includeParentRows ? row.getParentRows().map(_ref2 => {
        let {
          id
        } = _ref2;
        return id;
      }) : [];
      const rowIds = new Set([...parentRowIds, row.id, ...leafRowIds]);
      table.setRowPinning(old => {
        var _old$top3, _old$bottom3;
        if (position === 'bottom') {
          var _old$top, _old$bottom;
          return {
            top: ((_old$top = old == null ? void 0 : old.top) != null ? _old$top : []).filter(d => !(rowIds != null && rowIds.has(d))),
            bottom: [...((_old$bottom = old == null ? void 0 : old.bottom) != null ? _old$bottom : []).filter(d => !(rowIds != null && rowIds.has(d))), ...Array.from(rowIds)]
          };
        }
        if (position === 'top') {
          var _old$top2, _old$bottom2;
          return {
            top: [...((_old$top2 = old == null ? void 0 : old.top) != null ? _old$top2 : []).filter(d => !(rowIds != null && rowIds.has(d))), ...Array.from(rowIds)],
            bottom: ((_old$bottom2 = old == null ? void 0 : old.bottom) != null ? _old$bottom2 : []).filter(d => !(rowIds != null && rowIds.has(d)))
          };
        }
        return {
          top: ((_old$top3 = old == null ? void 0 : old.top) != null ? _old$top3 : []).filter(d => !(rowIds != null && rowIds.has(d))),
          bottom: ((_old$bottom3 = old == null ? void 0 : old.bottom) != null ? _old$bottom3 : []).filter(d => !(rowIds != null && rowIds.has(d)))
        };
      });
    };
    row.getCanPin = () => {
      var _ref3;
      const {
        enableRowPinning,
        enablePinning
      } = table.options;
      if (typeof enableRowPinning === 'function') {
        return enableRowPinning(row);
      }
      return (_ref3 = enableRowPinning != null ? enableRowPinning : enablePinning) != null ? _ref3 : true;
    };
    row.getIsPinned = () => {
      const rowIds = [row.id];
      const {
        top,
        bottom
      } = table.getState().rowPinning;
      const isTop = rowIds.some(d => top == null ? void 0 : top.includes(d));
      const isBottom = rowIds.some(d => bottom == null ? void 0 : bottom.includes(d));
      return isTop ? 'top' : isBottom ? 'bottom' : false;
    };
    row.getPinnedIndex = () => {
      var _ref4, _visiblePinnedRowIds$;
      const position = row.getIsPinned();
      if (!position) return -1;
      const visiblePinnedRowIds = (_ref4 = position === 'top' ? table.getTopRows() : table.getBottomRows()) == null ? void 0 : _ref4.map(_ref5 => {
        let {
          id
        } = _ref5;
        return id;
      });
      return (_visiblePinnedRowIds$ = visiblePinnedRowIds == null ? void 0 : visiblePinnedRowIds.indexOf(row.id)) != null ? _visiblePinnedRowIds$ : -1;
    };
  },
  createTable: table => {
    table.setRowPinning = updater => table.options.onRowPinningChange == null ? void 0 : table.options.onRowPinningChange(updater);
    table.resetRowPinning = defaultState => {
      var _table$initialState$r, _table$initialState;
      return table.setRowPinning(defaultState ? getDefaultRowPinningState() : (_table$initialState$r = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.rowPinning) != null ? _table$initialState$r : getDefaultRowPinningState());
    };
    table.getIsSomeRowsPinned = position => {
      var _pinningState$positio;
      const pinningState = table.getState().rowPinning;
      if (!position) {
        var _pinningState$top, _pinningState$bottom;
        return Boolean(((_pinningState$top = pinningState.top) == null ? void 0 : _pinningState$top.length) || ((_pinningState$bottom = pinningState.bottom) == null ? void 0 : _pinningState$bottom.length));
      }
      return Boolean((_pinningState$positio = pinningState[position]) == null ? void 0 : _pinningState$positio.length);
    };
    table._getPinnedRows = (visibleRows, pinnedRowIds, position) => {
      var _table$options$keepPi;
      const rows = ((_table$options$keepPi = table.options.keepPinnedRows) != null ? _table$options$keepPi : true) ?
      //get all rows that are pinned even if they would not be otherwise visible
      //account for expanded parent rows, but not pagination or filtering
      (pinnedRowIds != null ? pinnedRowIds : []).map(rowId => {
        const row = table.getRow(rowId, true);
        return row.getIsAllParentsExpanded() ? row : null;
      }) :
      //else get only visible rows that are pinned
      (pinnedRowIds != null ? pinnedRowIds : []).map(rowId => visibleRows.find(row => row.id === rowId));
      return rows.filter(Boolean).map(d => ({
        ...d,
        position
      }));
    };
    table.getTopRows = memo(() => [table.getRowModel().rows, table.getState().rowPinning.top], (allRows, topPinnedRowIds) => table._getPinnedRows(allRows, topPinnedRowIds, 'top'), getMemoOptions(table.options, 'debugRows', 'getTopRows'));
    table.getBottomRows = memo(() => [table.getRowModel().rows, table.getState().rowPinning.bottom], (allRows, bottomPinnedRowIds) => table._getPinnedRows(allRows, bottomPinnedRowIds, 'bottom'), getMemoOptions(table.options, 'debugRows', 'getBottomRows'));
    table.getCenterRows = memo(() => [table.getRowModel().rows, table.getState().rowPinning.top, table.getState().rowPinning.bottom], (allRows, top, bottom) => {
      const topAndBottom = new Set([...(top != null ? top : []), ...(bottom != null ? bottom : [])]);
      return allRows.filter(d => !topAndBottom.has(d.id));
    }, getMemoOptions(table.options, 'debugRows', 'getCenterRows'));
  }
};

//

const RowSelection = {
  getInitialState: state => {
    return {
      rowSelection: {},
      ...state
    };
  },
  getDefaultOptions: table => {
    return {
      onRowSelectionChange: makeStateUpdater('rowSelection', table),
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableSubRowSelection: true
      // enableGroupingRowSelection: false,
      // isAdditiveSelectEvent: (e: unknown) => !!e.metaKey,
      // isInclusiveSelectEvent: (e: unknown) => !!e.shiftKey,
    };
  },
  createTable: table => {
    table.setRowSelection = updater => table.options.onRowSelectionChange == null ? void 0 : table.options.onRowSelectionChange(updater);
    table.resetRowSelection = defaultState => {
      var _table$initialState$r;
      return table.setRowSelection(defaultState ? {} : (_table$initialState$r = table.initialState.rowSelection) != null ? _table$initialState$r : {});
    };
    table.toggleAllRowsSelected = value => {
      table.setRowSelection(old => {
        value = typeof value !== 'undefined' ? value : !table.getIsAllRowsSelected();
        const rowSelection = {
          ...old
        };
        const preGroupedFlatRows = table.getPreGroupedRowModel().flatRows;

        // We don't use `mutateRowIsSelected` here for performance reasons.
        // All of the rows are flat already, so it wouldn't be worth it
        if (value) {
          preGroupedFlatRows.forEach(row => {
            if (!row.getCanSelect()) {
              return;
            }
            rowSelection[row.id] = true;
          });
        } else {
          preGroupedFlatRows.forEach(row => {
            delete rowSelection[row.id];
          });
        }
        return rowSelection;
      });
    };
    table.toggleAllPageRowsSelected = value => table.setRowSelection(old => {
      const resolvedValue = typeof value !== 'undefined' ? value : !table.getIsAllPageRowsSelected();
      const rowSelection = {
        ...old
      };
      table.getRowModel().rows.forEach(row => {
        mutateRowIsSelected(rowSelection, row.id, resolvedValue, true, table);
      });
      return rowSelection;
    });

    // addRowSelectionRange: rowId => {
    //   const {
    //     rows,
    //     rowsById,
    //     options: { selectGroupingRows, selectSubRows },
    //   } = table

    //   const findSelectedRow = (rows: Row[]) => {
    //     let found
    //     rows.find(d => {
    //       if (d.getIsSelected()) {
    //         found = d
    //         return true
    //       }
    //       const subFound = findSelectedRow(d.subRows || [])
    //       if (subFound) {
    //         found = subFound
    //         return true
    //       }
    //       return false
    //     })
    //     return found
    //   }

    //   const firstRow = findSelectedRow(rows) || rows[0]
    //   const lastRow = rowsById[rowId]

    //   let include = false
    //   const selectedRowIds = {}

    //   const addRow = (row: Row) => {
    //     mutateRowIsSelected(selectedRowIds, row.id, true, {
    //       rowsById,
    //       selectGroupingRows: selectGroupingRows!,
    //       selectSubRows: selectSubRows!,
    //     })
    //   }

    //   table.rows.forEach(row => {
    //     const isFirstRow = row.id === firstRow.id
    //     const isLastRow = row.id === lastRow.id

    //     if (isFirstRow || isLastRow) {
    //       if (!include) {
    //         include = true
    //       } else if (include) {
    //         addRow(row)
    //         include = false
    //       }
    //     }

    //     if (include) {
    //       addRow(row)
    //     }
    //   })

    //   table.setRowSelection(selectedRowIds)
    // },
    table.getPreSelectedRowModel = () => table.getCoreRowModel();
    table.getSelectedRowModel = memo(() => [table.getState().rowSelection, table.getCoreRowModel()], (rowSelection, rowModel) => {
      if (!Object.keys(rowSelection).length) {
        return {
          rows: [],
          flatRows: [],
          rowsById: {}
        };
      }
      return selectRowsFn(table, rowModel);
    }, getMemoOptions(table.options, 'debugTable', 'getSelectedRowModel'));
    table.getFilteredSelectedRowModel = memo(() => [table.getState().rowSelection, table.getFilteredRowModel()], (rowSelection, rowModel) => {
      if (!Object.keys(rowSelection).length) {
        return {
          rows: [],
          flatRows: [],
          rowsById: {}
        };
      }
      return selectRowsFn(table, rowModel);
    }, getMemoOptions(table.options, 'debugTable', 'getFilteredSelectedRowModel'));
    table.getGroupedSelectedRowModel = memo(() => [table.getState().rowSelection, table.getSortedRowModel()], (rowSelection, rowModel) => {
      if (!Object.keys(rowSelection).length) {
        return {
          rows: [],
          flatRows: [],
          rowsById: {}
        };
      }
      return selectRowsFn(table, rowModel);
    }, getMemoOptions(table.options, 'debugTable', 'getGroupedSelectedRowModel'));

    ///

    // getGroupingRowCanSelect: rowId => {
    //   const row = table.getRow(rowId)

    //   if (!row) {
    //     throw new Error()
    //   }

    //   if (typeof table.options.enableGroupingRowSelection === 'function') {
    //     return table.options.enableGroupingRowSelection(row)
    //   }

    //   return table.options.enableGroupingRowSelection ?? false
    // },

    table.getIsAllRowsSelected = () => {
      const preGroupedFlatRows = table.getFilteredRowModel().flatRows;
      const {
        rowSelection
      } = table.getState();
      let isAllRowsSelected = Boolean(preGroupedFlatRows.length && Object.keys(rowSelection).length);
      if (isAllRowsSelected) {
        if (preGroupedFlatRows.some(row => row.getCanSelect() && !rowSelection[row.id])) {
          isAllRowsSelected = false;
        }
      }
      return isAllRowsSelected;
    };
    table.getIsAllPageRowsSelected = () => {
      const paginationFlatRows = table.getPaginationRowModel().flatRows.filter(row => row.getCanSelect());
      const {
        rowSelection
      } = table.getState();
      let isAllPageRowsSelected = !!paginationFlatRows.length;
      if (isAllPageRowsSelected && paginationFlatRows.some(row => !rowSelection[row.id])) {
        isAllPageRowsSelected = false;
      }
      return isAllPageRowsSelected;
    };
    table.getIsSomeRowsSelected = () => {
      var _table$getState$rowSe;
      const totalSelected = Object.keys((_table$getState$rowSe = table.getState().rowSelection) != null ? _table$getState$rowSe : {}).length;
      return totalSelected > 0 && totalSelected < table.getFilteredRowModel().flatRows.length;
    };
    table.getIsSomePageRowsSelected = () => {
      const paginationFlatRows = table.getPaginationRowModel().flatRows;
      return table.getIsAllPageRowsSelected() ? false : paginationFlatRows.filter(row => row.getCanSelect()).some(d => d.getIsSelected() || d.getIsSomeSelected());
    };
    table.getToggleAllRowsSelectedHandler = () => {
      return e => {
        table.toggleAllRowsSelected(e.target.checked);
      };
    };
    table.getToggleAllPageRowsSelectedHandler = () => {
      return e => {
        table.toggleAllPageRowsSelected(e.target.checked);
      };
    };
  },
  createRow: (row, table) => {
    row.toggleSelected = (value, opts) => {
      const isSelected = row.getIsSelected();
      table.setRowSelection(old => {
        var _opts$selectChildren;
        value = typeof value !== 'undefined' ? value : !isSelected;
        if (row.getCanSelect() && isSelected === value) {
          return old;
        }
        const selectedRowIds = {
          ...old
        };
        mutateRowIsSelected(selectedRowIds, row.id, value, (_opts$selectChildren = opts == null ? void 0 : opts.selectChildren) != null ? _opts$selectChildren : true, table);
        return selectedRowIds;
      });
    };
    row.getIsSelected = () => {
      const {
        rowSelection
      } = table.getState();
      return isRowSelected(row, rowSelection);
    };
    row.getIsSomeSelected = () => {
      const {
        rowSelection
      } = table.getState();
      return isSubRowSelected(row, rowSelection) === 'some';
    };
    row.getIsAllSubRowsSelected = () => {
      const {
        rowSelection
      } = table.getState();
      return isSubRowSelected(row, rowSelection) === 'all';
    };
    row.getCanSelect = () => {
      var _table$options$enable;
      if (typeof table.options.enableRowSelection === 'function') {
        return table.options.enableRowSelection(row);
      }
      return (_table$options$enable = table.options.enableRowSelection) != null ? _table$options$enable : true;
    };
    row.getCanSelectSubRows = () => {
      var _table$options$enable2;
      if (typeof table.options.enableSubRowSelection === 'function') {
        return table.options.enableSubRowSelection(row);
      }
      return (_table$options$enable2 = table.options.enableSubRowSelection) != null ? _table$options$enable2 : true;
    };
    row.getCanMultiSelect = () => {
      var _table$options$enable3;
      if (typeof table.options.enableMultiRowSelection === 'function') {
        return table.options.enableMultiRowSelection(row);
      }
      return (_table$options$enable3 = table.options.enableMultiRowSelection) != null ? _table$options$enable3 : true;
    };
    row.getToggleSelectedHandler = () => {
      const canSelect = row.getCanSelect();
      return e => {
        var _target;
        if (!canSelect) return;
        row.toggleSelected((_target = e.target) == null ? void 0 : _target.checked);
      };
    };
  }
};
const mutateRowIsSelected = (selectedRowIds, id, value, includeChildren, table) => {
  var _row$subRows;
  const row = table.getRow(id, true);

  // const isGrouped = row.getIsGrouped()

  // if ( // TODO: enforce grouping row selection rules
  //   !isGrouped ||
  //   (isGrouped && table.options.enableGroupingRowSelection)
  // ) {
  if (value) {
    if (!row.getCanMultiSelect()) {
      Object.keys(selectedRowIds).forEach(key => delete selectedRowIds[key]);
    }
    if (row.getCanSelect()) {
      selectedRowIds[id] = true;
    }
  } else {
    delete selectedRowIds[id];
  }
  // }

  if (includeChildren && (_row$subRows = row.subRows) != null && _row$subRows.length && row.getCanSelectSubRows()) {
    row.subRows.forEach(row => mutateRowIsSelected(selectedRowIds, row.id, value, includeChildren, table));
  }
};
function selectRowsFn(table, rowModel) {
  const rowSelection = table.getState().rowSelection;
  const newSelectedFlatRows = [];
  const newSelectedRowsById = {};

  // Filters top level and nested rows
  const recurseRows = function (rows, depth) {
    return rows.map(row => {
      var _row$subRows2;
      const isSelected = isRowSelected(row, rowSelection);
      if (isSelected) {
        newSelectedFlatRows.push(row);
        newSelectedRowsById[row.id] = row;
      }
      if ((_row$subRows2 = row.subRows) != null && _row$subRows2.length) {
        row = {
          ...row,
          subRows: recurseRows(row.subRows)
        };
      }
      if (isSelected) {
        return row;
      }
    }).filter(Boolean);
  };
  return {
    rows: recurseRows(rowModel.rows),
    flatRows: newSelectedFlatRows,
    rowsById: newSelectedRowsById
  };
}
function isRowSelected(row, selection) {
  var _selection$row$id;
  return (_selection$row$id = selection[row.id]) != null ? _selection$row$id : false;
}
function isSubRowSelected(row, selection, table) {
  var _row$subRows3;
  if (!((_row$subRows3 = row.subRows) != null && _row$subRows3.length)) return false;
  let allChildrenSelected = true;
  let someSelected = false;
  row.subRows.forEach(subRow => {
    // Bail out early if we know both of these
    if (someSelected && !allChildrenSelected) {
      return;
    }
    if (subRow.getCanSelect()) {
      if (isRowSelected(subRow, selection)) {
        someSelected = true;
      } else {
        allChildrenSelected = false;
      }
    }

    // Check row selection of nested subrows
    if (subRow.subRows && subRow.subRows.length) {
      const subRowChildrenSelected = isSubRowSelected(subRow, selection);
      if (subRowChildrenSelected === 'all') {
        someSelected = true;
      } else if (subRowChildrenSelected === 'some') {
        someSelected = true;
        allChildrenSelected = false;
      } else {
        allChildrenSelected = false;
      }
    }
  });
  return allChildrenSelected ? 'all' : someSelected ? 'some' : false;
}

const reSplitAlphaNumeric = /([0-9]+)/gm;
const alphanumeric = (rowA, rowB, columnId) => {
  return compareAlphanumeric(toString(rowA.getValue(columnId)).toLowerCase(), toString(rowB.getValue(columnId)).toLowerCase());
};
const alphanumericCaseSensitive = (rowA, rowB, columnId) => {
  return compareAlphanumeric(toString(rowA.getValue(columnId)), toString(rowB.getValue(columnId)));
};

// The text filter is more basic (less numeric support)
// but is much faster
const text = (rowA, rowB, columnId) => {
  return compareBasic(toString(rowA.getValue(columnId)).toLowerCase(), toString(rowB.getValue(columnId)).toLowerCase());
};

// The text filter is more basic (less numeric support)
// but is much faster
const textCaseSensitive = (rowA, rowB, columnId) => {
  return compareBasic(toString(rowA.getValue(columnId)), toString(rowB.getValue(columnId)));
};
const datetime = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  // Can handle nullish values
  // Use > and < because == (and ===) doesn't work with
  // Date objects (would require calling getTime()).
  return a > b ? 1 : a < b ? -1 : 0;
};
const basic = (rowA, rowB, columnId) => {
  return compareBasic(rowA.getValue(columnId), rowB.getValue(columnId));
};

// Utils

function compareBasic(a, b) {
  return a === b ? 0 : a > b ? 1 : -1;
}
function toString(a) {
  if (typeof a === 'number') {
    if (isNaN(a) || a === Infinity || a === -Infinity) {
      return '';
    }
    return String(a);
  }
  if (typeof a === 'string') {
    return a;
  }
  return '';
}

// Mixed sorting is slow, but very inclusive of many edge cases.
// It handles numbers, mixed alphanumeric combinations, and even
// null, undefined, and Infinity
function compareAlphanumeric(aStr, bStr) {
  // Split on number groups, but keep the delimiter
  // Then remove falsey split values
  const a = aStr.split(reSplitAlphaNumeric).filter(Boolean);
  const b = bStr.split(reSplitAlphaNumeric).filter(Boolean);

  // While
  while (a.length && b.length) {
    const aa = a.shift();
    const bb = b.shift();
    const an = parseInt(aa, 10);
    const bn = parseInt(bb, 10);
    const combo = [an, bn].sort();

    // Both are string
    if (isNaN(combo[0])) {
      if (aa > bb) {
        return 1;
      }
      if (bb > aa) {
        return -1;
      }
      continue;
    }

    // One is a string, one is a number
    if (isNaN(combo[1])) {
      return isNaN(an) ? -1 : 1;
    }

    // Both are numbers
    if (an > bn) {
      return 1;
    }
    if (bn > an) {
      return -1;
    }
  }
  return a.length - b.length;
}

// Exports

const sortingFns = {
  alphanumeric,
  alphanumericCaseSensitive,
  text,
  textCaseSensitive,
  datetime,
  basic
};

//

const RowSorting = {
  getInitialState: state => {
    return {
      sorting: [],
      ...state
    };
  },
  getDefaultColumnDef: () => {
    return {
      sortingFn: 'auto',
      sortUndefined: 1
    };
  },
  getDefaultOptions: table => {
    return {
      onSortingChange: makeStateUpdater('sorting', table),
      isMultiSortEvent: e => {
        return e.shiftKey;
      }
    };
  },
  createColumn: (column, table) => {
    column.getAutoSortingFn = () => {
      const firstRows = table.getFilteredRowModel().flatRows.slice(10);
      let isString = false;
      for (const row of firstRows) {
        const value = row == null ? void 0 : row.getValue(column.id);
        if (Object.prototype.toString.call(value) === '[object Date]') {
          return sortingFns.datetime;
        }
        if (typeof value === 'string') {
          isString = true;
          if (value.split(reSplitAlphaNumeric).length > 1) {
            return sortingFns.alphanumeric;
          }
        }
      }
      if (isString) {
        return sortingFns.text;
      }
      return sortingFns.basic;
    };
    column.getAutoSortDir = () => {
      const firstRow = table.getFilteredRowModel().flatRows[0];
      const value = firstRow == null ? void 0 : firstRow.getValue(column.id);
      if (typeof value === 'string') {
        return 'asc';
      }
      return 'desc';
    };
    column.getSortingFn = () => {
      var _table$options$sortin, _table$options$sortin2;
      if (!column) {
        throw new Error();
      }
      return isFunction(column.columnDef.sortingFn) ? column.columnDef.sortingFn : column.columnDef.sortingFn === 'auto' ? column.getAutoSortingFn() : (_table$options$sortin = (_table$options$sortin2 = table.options.sortingFns) == null ? void 0 : _table$options$sortin2[column.columnDef.sortingFn]) != null ? _table$options$sortin : sortingFns[column.columnDef.sortingFn];
    };
    column.toggleSorting = (desc, multi) => {
      // if (column.columns.length) {
      //   column.columns.forEach((c, i) => {
      //     if (c.id) {
      //       table.toggleColumnSorting(c.id, undefined, multi || !!i)
      //     }
      //   })
      //   return
      // }

      // this needs to be outside of table.setSorting to be in sync with rerender
      const nextSortingOrder = column.getNextSortingOrder();
      const hasManualValue = typeof desc !== 'undefined' && desc !== null;
      table.setSorting(old => {
        // Find any existing sorting for this column
        const existingSorting = old == null ? void 0 : old.find(d => d.id === column.id);
        const existingIndex = old == null ? void 0 : old.findIndex(d => d.id === column.id);
        let newSorting = [];

        // What should we do with this sort action?
        let sortAction;
        let nextDesc = hasManualValue ? desc : nextSortingOrder === 'desc';

        // Multi-mode
        if (old != null && old.length && column.getCanMultiSort() && multi) {
          if (existingSorting) {
            sortAction = 'toggle';
          } else {
            sortAction = 'add';
          }
        } else {
          // Normal mode
          if (old != null && old.length && existingIndex !== old.length - 1) {
            sortAction = 'replace';
          } else if (existingSorting) {
            sortAction = 'toggle';
          } else {
            sortAction = 'replace';
          }
        }

        // Handle toggle states that will remove the sorting
        if (sortAction === 'toggle') {
          // If we are "actually" toggling (not a manual set value), should we remove the sorting?
          if (!hasManualValue) {
            // Is our intention to remove?
            if (!nextSortingOrder) {
              sortAction = 'remove';
            }
          }
        }
        if (sortAction === 'add') {
          var _table$options$maxMul;
          newSorting = [...old, {
            id: column.id,
            desc: nextDesc
          }];
          // Take latest n columns
          newSorting.splice(0, newSorting.length - ((_table$options$maxMul = table.options.maxMultiSortColCount) != null ? _table$options$maxMul : Number.MAX_SAFE_INTEGER));
        } else if (sortAction === 'toggle') {
          // This flips (or sets) the
          newSorting = old.map(d => {
            if (d.id === column.id) {
              return {
                ...d,
                desc: nextDesc
              };
            }
            return d;
          });
        } else if (sortAction === 'remove') {
          newSorting = old.filter(d => d.id !== column.id);
        } else {
          newSorting = [{
            id: column.id,
            desc: nextDesc
          }];
        }
        return newSorting;
      });
    };
    column.getFirstSortDir = () => {
      var _ref, _column$columnDef$sor;
      const sortDescFirst = (_ref = (_column$columnDef$sor = column.columnDef.sortDescFirst) != null ? _column$columnDef$sor : table.options.sortDescFirst) != null ? _ref : column.getAutoSortDir() === 'desc';
      return sortDescFirst ? 'desc' : 'asc';
    };
    column.getNextSortingOrder = multi => {
      var _table$options$enable, _table$options$enable2;
      const firstSortDirection = column.getFirstSortDir();
      const isSorted = column.getIsSorted();
      if (!isSorted) {
        return firstSortDirection;
      }
      if (isSorted !== firstSortDirection && ((_table$options$enable = table.options.enableSortingRemoval) != null ? _table$options$enable : true) && (
      // If enableSortRemove, enable in general
      multi ? (_table$options$enable2 = table.options.enableMultiRemove) != null ? _table$options$enable2 : true : true) // If multi, don't allow if enableMultiRemove))
      ) {
        return false;
      }
      return isSorted === 'desc' ? 'asc' : 'desc';
    };
    column.getCanSort = () => {
      var _column$columnDef$ena, _table$options$enable3;
      return ((_column$columnDef$ena = column.columnDef.enableSorting) != null ? _column$columnDef$ena : true) && ((_table$options$enable3 = table.options.enableSorting) != null ? _table$options$enable3 : true) && !!column.accessorFn;
    };
    column.getCanMultiSort = () => {
      var _ref2, _column$columnDef$ena2;
      return (_ref2 = (_column$columnDef$ena2 = column.columnDef.enableMultiSort) != null ? _column$columnDef$ena2 : table.options.enableMultiSort) != null ? _ref2 : !!column.accessorFn;
    };
    column.getIsSorted = () => {
      var _table$getState$sorti;
      const columnSort = (_table$getState$sorti = table.getState().sorting) == null ? void 0 : _table$getState$sorti.find(d => d.id === column.id);
      return !columnSort ? false : columnSort.desc ? 'desc' : 'asc';
    };
    column.getSortIndex = () => {
      var _table$getState$sorti2, _table$getState$sorti3;
      return (_table$getState$sorti2 = (_table$getState$sorti3 = table.getState().sorting) == null ? void 0 : _table$getState$sorti3.findIndex(d => d.id === column.id)) != null ? _table$getState$sorti2 : -1;
    };
    column.clearSorting = () => {
      //clear sorting for just 1 column
      table.setSorting(old => old != null && old.length ? old.filter(d => d.id !== column.id) : []);
    };
    column.getToggleSortingHandler = () => {
      const canSort = column.getCanSort();
      return e => {
        if (!canSort) return;
        e.persist == null || e.persist();
        column.toggleSorting == null || column.toggleSorting(undefined, column.getCanMultiSort() ? table.options.isMultiSortEvent == null ? void 0 : table.options.isMultiSortEvent(e) : false);
      };
    };
  },
  createTable: table => {
    table.setSorting = updater => table.options.onSortingChange == null ? void 0 : table.options.onSortingChange(updater);
    table.resetSorting = defaultState => {
      var _table$initialState$s, _table$initialState;
      table.setSorting(defaultState ? [] : (_table$initialState$s = (_table$initialState = table.initialState) == null ? void 0 : _table$initialState.sorting) != null ? _table$initialState$s : []);
    };
    table.getPreSortedRowModel = () => table.getGroupedRowModel();
    table.getSortedRowModel = () => {
      if (!table._getSortedRowModel && table.options.getSortedRowModel) {
        table._getSortedRowModel = table.options.getSortedRowModel(table);
      }
      if (table.options.manualSorting || !table._getSortedRowModel) {
        return table.getPreSortedRowModel();
      }
      return table._getSortedRowModel();
    };
  }
};

const builtInFeatures = [Headers, ColumnVisibility, ColumnOrdering, ColumnPinning, ColumnFaceting, ColumnFiltering, GlobalFaceting,
//depends on ColumnFaceting
GlobalFiltering,
//depends on ColumnFiltering
RowSorting, ColumnGrouping,
//depends on RowSorting
RowExpanding, RowPagination, RowPinning, RowSelection, ColumnSizing];

//

function createTable(options) {
  var _options$_features, _options$initialState;
  if (process.env.NODE_ENV !== 'production' && (options.debugAll || options.debugTable)) {
    console.info('Creating Table Instance...');
  }
  const _features = [...builtInFeatures, ...((_options$_features = options._features) != null ? _options$_features : [])];
  let table = {
    _features
  };
  const defaultOptions = table._features.reduce((obj, feature) => {
    return Object.assign(obj, feature.getDefaultOptions == null ? void 0 : feature.getDefaultOptions(table));
  }, {});
  const mergeOptions = options => {
    if (table.options.mergeOptions) {
      return table.options.mergeOptions(defaultOptions, options);
    }
    return {
      ...defaultOptions,
      ...options
    };
  };
  const coreInitialState = {};
  let initialState = {
    ...coreInitialState,
    ...((_options$initialState = options.initialState) != null ? _options$initialState : {})
  };
  table._features.forEach(feature => {
    var _feature$getInitialSt;
    initialState = (_feature$getInitialSt = feature.getInitialState == null ? void 0 : feature.getInitialState(initialState)) != null ? _feature$getInitialSt : initialState;
  });
  const queued = [];
  let queuedTimeout = false;
  const coreInstance = {
    _features,
    options: {
      ...defaultOptions,
      ...options
    },
    initialState,
    _queue: cb => {
      queued.push(cb);
      if (!queuedTimeout) {
        queuedTimeout = true;

        // Schedule a microtask to run the queued callbacks after
        // the current call stack (render, etc) has finished.
        Promise.resolve().then(() => {
          while (queued.length) {
            queued.shift()();
          }
          queuedTimeout = false;
        }).catch(error => setTimeout(() => {
          throw error;
        }));
      }
    },
    reset: () => {
      table.setState(table.initialState);
    },
    setOptions: updater => {
      const newOptions = functionalUpdate(updater, table.options);
      table.options = mergeOptions(newOptions);
    },
    getState: () => {
      return table.options.state;
    },
    setState: updater => {
      table.options.onStateChange == null || table.options.onStateChange(updater);
    },
    _getRowId: (row, index, parent) => {
      var _table$options$getRow;
      return (_table$options$getRow = table.options.getRowId == null ? void 0 : table.options.getRowId(row, index, parent)) != null ? _table$options$getRow : `${parent ? [parent.id, index].join('.') : index}`;
    },
    getCoreRowModel: () => {
      if (!table._getCoreRowModel) {
        table._getCoreRowModel = table.options.getCoreRowModel(table);
      }
      return table._getCoreRowModel();
    },
    // The final calls start at the bottom of the model,
    // expanded rows, which then work their way up

    getRowModel: () => {
      return table.getPaginationRowModel();
    },
    //in next version, we should just pass in the row model as the optional 2nd arg
    getRow: (id, searchAll) => {
      let row = (searchAll ? table.getPrePaginationRowModel() : table.getRowModel()).rowsById[id];
      if (!row) {
        row = table.getCoreRowModel().rowsById[id];
        if (!row) {
          if (process.env.NODE_ENV !== 'production') {
            throw new Error(`getRow could not find row with ID: ${id}`);
          }
          throw new Error();
        }
      }
      return row;
    },
    _getDefaultColumnDef: memo(() => [table.options.defaultColumn], defaultColumn => {
      var _defaultColumn;
      defaultColumn = (_defaultColumn = defaultColumn) != null ? _defaultColumn : {};
      return {
        header: props => {
          const resolvedColumnDef = props.header.column.columnDef;
          if (resolvedColumnDef.accessorKey) {
            return resolvedColumnDef.accessorKey;
          }
          if (resolvedColumnDef.accessorFn) {
            return resolvedColumnDef.id;
          }
          return null;
        },
        // footer: props => props.header.column.id,
        cell: props => {
          var _props$renderValue$to, _props$renderValue;
          return (_props$renderValue$to = (_props$renderValue = props.renderValue()) == null || _props$renderValue.toString == null ? void 0 : _props$renderValue.toString()) != null ? _props$renderValue$to : null;
        },
        ...table._features.reduce((obj, feature) => {
          return Object.assign(obj, feature.getDefaultColumnDef == null ? void 0 : feature.getDefaultColumnDef());
        }, {}),
        ...defaultColumn
      };
    }, getMemoOptions(options, 'debugColumns', '_getDefaultColumnDef')),
    _getColumnDefs: () => table.options.columns,
    getAllColumns: memo(() => [table._getColumnDefs()], columnDefs => {
      const recurseColumns = function (columnDefs, parent, depth) {
        if (depth === void 0) {
          depth = 0;
        }
        return columnDefs.map(columnDef => {
          const column = createColumn(table, columnDef, depth, parent);
          const groupingColumnDef = columnDef;
          column.columns = groupingColumnDef.columns ? recurseColumns(groupingColumnDef.columns, column, depth + 1) : [];
          return column;
        });
      };
      return recurseColumns(columnDefs);
    }, getMemoOptions(options, 'debugColumns', 'getAllColumns')),
    getAllFlatColumns: memo(() => [table.getAllColumns()], allColumns => {
      return allColumns.flatMap(column => {
        return column.getFlatColumns();
      });
    }, getMemoOptions(options, 'debugColumns', 'getAllFlatColumns')),
    _getAllFlatColumnsById: memo(() => [table.getAllFlatColumns()], flatColumns => {
      return flatColumns.reduce((acc, column) => {
        acc[column.id] = column;
        return acc;
      }, {});
    }, getMemoOptions(options, 'debugColumns', 'getAllFlatColumnsById')),
    getAllLeafColumns: memo(() => [table.getAllColumns(), table._getOrderColumnsFn()], (allColumns, orderColumns) => {
      let leafColumns = allColumns.flatMap(column => column.getLeafColumns());
      return orderColumns(leafColumns);
    }, getMemoOptions(options, 'debugColumns', 'getAllLeafColumns')),
    getColumn: columnId => {
      const column = table._getAllFlatColumnsById()[columnId];
      if (process.env.NODE_ENV !== 'production' && !column) {
        console.error(`[Table] Column with id '${columnId}' does not exist.`);
      }
      return column;
    }
  };
  Object.assign(table, coreInstance);
  for (let index = 0; index < table._features.length; index++) {
    const feature = table._features[index];
    feature == null || feature.createTable == null || feature.createTable(table);
  }
  return table;
}

function getCoreRowModel() {
  return table => memo(() => [table.options.data], data => {
    const rowModel = {
      rows: [],
      flatRows: [],
      rowsById: {}
    };
    const accessRows = function (originalRows, depth, parentRow) {
      if (depth === void 0) {
        depth = 0;
      }
      const rows = [];
      for (let i = 0; i < originalRows.length; i++) {
        // This could be an expensive check at scale, so we should move it somewhere else, but where?
        // if (!id) {
        //   if (process.env.NODE_ENV !== 'production') {
        //     throw new Error(`getRowId expected an ID, but got ${id}`)
        //   }
        // }

        // Make the row
        const row = createRow(table, table._getRowId(originalRows[i], i, parentRow), originalRows[i], i, depth, undefined, parentRow == null ? void 0 : parentRow.id);

        // Keep track of every row in a flat array
        rowModel.flatRows.push(row);
        // Also keep track of every row by its ID
        rowModel.rowsById[row.id] = row;
        // Push table row into parent
        rows.push(row);

        // Get the original subrows
        if (table.options.getSubRows) {
          var _row$originalSubRows;
          row.originalSubRows = table.options.getSubRows(originalRows[i], i);

          // Then recursively access them
          if ((_row$originalSubRows = row.originalSubRows) != null && _row$originalSubRows.length) {
            row.subRows = accessRows(row.originalSubRows, depth + 1, row);
          }
        }
      }
      return rows;
    };
    rowModel.rows = accessRows(data);
    return rowModel;
  }, getMemoOptions(table.options, 'debugTable', 'getRowModel', () => table._autoResetPageIndex()));
}
function expandRows(rowModel) {
  const expandedRows = [];
  const handleRow = row => {
    var _row$subRows;
    expandedRows.push(row);
    if ((_row$subRows = row.subRows) != null && _row$subRows.length && row.getIsExpanded()) {
      row.subRows.forEach(handleRow);
    }
  };
  rowModel.rows.forEach(handleRow);
  return {
    rows: expandedRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById
  };
}

function filterRows(rows, filterRowImpl, table) {
  if (table.options.filterFromLeafRows) {
    return filterRowModelFromLeafs(rows, filterRowImpl, table);
  }
  return filterRowModelFromRoot(rows, filterRowImpl, table);
}
function filterRowModelFromLeafs(rowsToFilter, filterRow, table) {
  var _table$options$maxLea;
  const newFilteredFlatRows = [];
  const newFilteredRowsById = {};
  const maxDepth = (_table$options$maxLea = table.options.maxLeafRowFilterDepth) != null ? _table$options$maxLea : 100;
  const recurseFilterRows = function (rowsToFilter, depth) {
    if (depth === void 0) {
      depth = 0;
    }
    const rows = [];

    // Filter from children up first
    for (let i = 0; i < rowsToFilter.length; i++) {
      var _row$subRows;
      let row = rowsToFilter[i];
      const newRow = createRow(table, row.id, row.original, row.index, row.depth, undefined, row.parentId);
      newRow.columnFilters = row.columnFilters;
      if ((_row$subRows = row.subRows) != null && _row$subRows.length && depth < maxDepth) {
        newRow.subRows = recurseFilterRows(row.subRows, depth + 1);
        row = newRow;
        if (filterRow(row) && !newRow.subRows.length) {
          rows.push(row);
          newFilteredRowsById[row.id] = row;
          newFilteredFlatRows.push(row);
          continue;
        }
        if (filterRow(row) || newRow.subRows.length) {
          rows.push(row);
          newFilteredRowsById[row.id] = row;
          newFilteredFlatRows.push(row);
          continue;
        }
      } else {
        row = newRow;
        if (filterRow(row)) {
          rows.push(row);
          newFilteredRowsById[row.id] = row;
          newFilteredFlatRows.push(row);
        }
      }
    }
    return rows;
  };
  return {
    rows: recurseFilterRows(rowsToFilter),
    flatRows: newFilteredFlatRows,
    rowsById: newFilteredRowsById
  };
}
function filterRowModelFromRoot(rowsToFilter, filterRow, table) {
  var _table$options$maxLea2;
  const newFilteredFlatRows = [];
  const newFilteredRowsById = {};
  const maxDepth = (_table$options$maxLea2 = table.options.maxLeafRowFilterDepth) != null ? _table$options$maxLea2 : 100;

  // Filters top level and nested rows
  const recurseFilterRows = function (rowsToFilter, depth) {
    if (depth === void 0) {
      depth = 0;
    }
    // Filter from parents downward first

    const rows = [];

    // Apply the filter to any subRows
    for (let i = 0; i < rowsToFilter.length; i++) {
      let row = rowsToFilter[i];
      const pass = filterRow(row);
      if (pass) {
        var _row$subRows2;
        if ((_row$subRows2 = row.subRows) != null && _row$subRows2.length && depth < maxDepth) {
          const newRow = createRow(table, row.id, row.original, row.index, row.depth, undefined, row.parentId);
          newRow.subRows = recurseFilterRows(row.subRows, depth + 1);
          row = newRow;
        }
        rows.push(row);
        newFilteredFlatRows.push(row);
        newFilteredRowsById[row.id] = row;
      }
    }
    return rows;
  };
  return {
    rows: recurseFilterRows(rowsToFilter),
    flatRows: newFilteredFlatRows,
    rowsById: newFilteredRowsById
  };
}

function getFilteredRowModel() {
  return table => memo(() => [table.getPreFilteredRowModel(), table.getState().columnFilters, table.getState().globalFilter], (rowModel, columnFilters, globalFilter) => {
    if (!rowModel.rows.length || !(columnFilters != null && columnFilters.length) && !globalFilter) {
      for (let i = 0; i < rowModel.flatRows.length; i++) {
        rowModel.flatRows[i].columnFilters = {};
        rowModel.flatRows[i].columnFiltersMeta = {};
      }
      return rowModel;
    }
    const resolvedColumnFilters = [];
    const resolvedGlobalFilters = [];
    (columnFilters != null ? columnFilters : []).forEach(d => {
      var _filterFn$resolveFilt;
      const column = table.getColumn(d.id);
      if (!column) {
        return;
      }
      const filterFn = column.getFilterFn();
      if (!filterFn) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`Could not find a valid 'column.filterFn' for column with the ID: ${column.id}.`);
        }
        return;
      }
      resolvedColumnFilters.push({
        id: d.id,
        filterFn,
        resolvedValue: (_filterFn$resolveFilt = filterFn.resolveFilterValue == null ? void 0 : filterFn.resolveFilterValue(d.value)) != null ? _filterFn$resolveFilt : d.value
      });
    });
    const filterableIds = (columnFilters != null ? columnFilters : []).map(d => d.id);
    const globalFilterFn = table.getGlobalFilterFn();
    const globallyFilterableColumns = table.getAllLeafColumns().filter(column => column.getCanGlobalFilter());
    if (globalFilter && globalFilterFn && globallyFilterableColumns.length) {
      filterableIds.push('__global__');
      globallyFilterableColumns.forEach(column => {
        var _globalFilterFn$resol;
        resolvedGlobalFilters.push({
          id: column.id,
          filterFn: globalFilterFn,
          resolvedValue: (_globalFilterFn$resol = globalFilterFn.resolveFilterValue == null ? void 0 : globalFilterFn.resolveFilterValue(globalFilter)) != null ? _globalFilterFn$resol : globalFilter
        });
      });
    }
    let currentColumnFilter;
    let currentGlobalFilter;

    // Flag the prefiltered row model with each filter state
    for (let j = 0; j < rowModel.flatRows.length; j++) {
      const row = rowModel.flatRows[j];
      row.columnFilters = {};
      if (resolvedColumnFilters.length) {
        for (let i = 0; i < resolvedColumnFilters.length; i++) {
          currentColumnFilter = resolvedColumnFilters[i];
          const id = currentColumnFilter.id;

          // Tag the row with the column filter state
          row.columnFilters[id] = currentColumnFilter.filterFn(row, id, currentColumnFilter.resolvedValue, filterMeta => {
            row.columnFiltersMeta[id] = filterMeta;
          });
        }
      }
      if (resolvedGlobalFilters.length) {
        for (let i = 0; i < resolvedGlobalFilters.length; i++) {
          currentGlobalFilter = resolvedGlobalFilters[i];
          const id = currentGlobalFilter.id;
          // Tag the row with the first truthy global filter state
          if (currentGlobalFilter.filterFn(row, id, currentGlobalFilter.resolvedValue, filterMeta => {
            row.columnFiltersMeta[id] = filterMeta;
          })) {
            row.columnFilters.__global__ = true;
            break;
          }
        }
        if (row.columnFilters.__global__ !== true) {
          row.columnFilters.__global__ = false;
        }
      }
    }
    const filterRowsImpl = row => {
      // Horizontally filter rows through each column
      for (let i = 0; i < filterableIds.length; i++) {
        if (row.columnFilters[filterableIds[i]] === false) {
          return false;
        }
      }
      return true;
    };

    // Filter final rows using all of the active filters
    return filterRows(rowModel.rows, filterRowsImpl, table);
  }, getMemoOptions(table.options, 'debugTable', 'getFilteredRowModel', () => table._autoResetPageIndex()));
}

function getPaginationRowModel(opts) {
  return table => memo(() => [table.getState().pagination, table.getPrePaginationRowModel(), table.options.paginateExpandedRows ? undefined : table.getState().expanded], (pagination, rowModel) => {
    if (!rowModel.rows.length) {
      return rowModel;
    }
    const {
      pageSize,
      pageIndex
    } = pagination;
    let {
      rows,
      flatRows,
      rowsById
    } = rowModel;
    const pageStart = pageSize * pageIndex;
    const pageEnd = pageStart + pageSize;
    rows = rows.slice(pageStart, pageEnd);
    let paginatedRowModel;
    if (!table.options.paginateExpandedRows) {
      paginatedRowModel = expandRows({
        rows,
        flatRows,
        rowsById
      });
    } else {
      paginatedRowModel = {
        rows,
        flatRows,
        rowsById
      };
    }
    paginatedRowModel.flatRows = [];
    const handleRow = row => {
      paginatedRowModel.flatRows.push(row);
      if (row.subRows.length) {
        row.subRows.forEach(handleRow);
      }
    };
    paginatedRowModel.rows.forEach(handleRow);
    return paginatedRowModel;
  }, getMemoOptions(table.options, 'debugTable', 'getPaginationRowModel'));
}

function getSortedRowModel() {
  return table => memo(() => [table.getState().sorting, table.getPreSortedRowModel()], (sorting, rowModel) => {
    if (!rowModel.rows.length || !(sorting != null && sorting.length)) {
      return rowModel;
    }
    const sortingState = table.getState().sorting;
    const sortedFlatRows = [];

    // Filter out sortings that correspond to non existing columns
    const availableSorting = sortingState.filter(sort => {
      var _table$getColumn;
      return (_table$getColumn = table.getColumn(sort.id)) == null ? void 0 : _table$getColumn.getCanSort();
    });
    const columnInfoById = {};
    availableSorting.forEach(sortEntry => {
      const column = table.getColumn(sortEntry.id);
      if (!column) return;
      columnInfoById[sortEntry.id] = {
        sortUndefined: column.columnDef.sortUndefined,
        invertSorting: column.columnDef.invertSorting,
        sortingFn: column.getSortingFn()
      };
    });
    const sortData = rows => {
      // This will also perform a stable sorting using the row index
      // if needed.
      const sortedData = rows.map(row => ({
        ...row
      }));
      sortedData.sort((rowA, rowB) => {
        for (let i = 0; i < availableSorting.length; i += 1) {
          var _sortEntry$desc;
          const sortEntry = availableSorting[i];
          const columnInfo = columnInfoById[sortEntry.id];
          const sortUndefined = columnInfo.sortUndefined;
          const isDesc = (_sortEntry$desc = sortEntry == null ? void 0 : sortEntry.desc) != null ? _sortEntry$desc : false;
          let sortInt = 0;

          // All sorting ints should always return in ascending order
          if (sortUndefined) {
            const aValue = rowA.getValue(sortEntry.id);
            const bValue = rowB.getValue(sortEntry.id);
            const aUndefined = aValue === undefined;
            const bUndefined = bValue === undefined;
            if (aUndefined || bUndefined) {
              if (sortUndefined === 'first') return aUndefined ? -1 : 1;
              if (sortUndefined === 'last') return aUndefined ? 1 : -1;
              sortInt = aUndefined && bUndefined ? 0 : aUndefined ? sortUndefined : -sortUndefined;
            }
          }
          if (sortInt === 0) {
            sortInt = columnInfo.sortingFn(rowA, rowB, sortEntry.id);
          }

          // If sorting is non-zero, take care of desc and inversion
          if (sortInt !== 0) {
            if (isDesc) {
              sortInt *= -1;
            }
            if (columnInfo.invertSorting) {
              sortInt *= -1;
            }
            return sortInt;
          }
        }
        return rowA.index - rowB.index;
      });

      // If there are sub-rows, sort them
      sortedData.forEach(row => {
        var _row$subRows;
        sortedFlatRows.push(row);
        if ((_row$subRows = row.subRows) != null && _row$subRows.length) {
          row.subRows = sortData(row.subRows);
        }
      });
      return sortedData;
    };
    return {
      rows: sortData(rowModel.rows),
      flatRows: sortedFlatRows,
      rowsById: rowModel.rowsById
    };
  }, getMemoOptions(table.options, 'debugTable', 'getSortedRowModel', () => table._autoResetPageIndex()));
}

/**
   * react-table
   *
   * Copyright (c) TanStack
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE.md file in the root directory of this source tree.
   *
   * @license MIT
   */

//

/**
 * If rendering headers, cells, or footers with custom markup, use flexRender instead of `cell.getValue()` or `cell.renderValue()`.
 */
function flexRender(Comp, props) {
  return !Comp ? null : isReactComponent(Comp) ? /*#__PURE__*/React.createElement(Comp, props) : Comp;
}
function isReactComponent(component) {
  return isClassComponent(component) || typeof component === 'function' || isExoticComponent(component);
}
function isClassComponent(component) {
  return typeof component === 'function' && (() => {
    const proto = Object.getPrototypeOf(component);
    return proto.prototype && proto.prototype.isReactComponent;
  })();
}
function isExoticComponent(component) {
  return typeof component === 'object' && typeof component.$$typeof === 'symbol' && ['react.memo', 'react.forward_ref'].includes(component.$$typeof.description);
}
function useReactTable(options) {
  // Compose in the generic options to the user options
  const resolvedOptions = {
    state: {},
    // Dummy state
    onStateChange: () => {},
    // noop
    renderFallbackValue: null,
    ...options
  };

  // Create a new table and store it in state
  const [tableRef] = React.useState(() => ({
    current: createTable(resolvedOptions)
  }));

  // By default, manage table state here using the table's initial state
  const [state, setState] = React.useState(() => tableRef.current.initialState);

  // Compose the default state above with any user state. This will allow the user
  // to only control a subset of the state if desired.
  tableRef.current.setOptions(prev => ({
    ...prev,
    ...options,
    state: {
      ...state,
      ...options.state
    },
    // Similarly, we'll maintain both our internal state and any user-provided
    // state.
    onStateChange: updater => {
      setState(updater);
      options.onStateChange == null || options.onStateChange(updater);
    }
  }));
  return tableRef.current;
}

var css_248z$w = ".Table-module_table__tQfK6 {\n  width: 100%;\n  height: 100%;\n}\n\n.Table-module_table__tQfK6 .Table-module_wrapper__cEuxl {\n  max-width: 100%;\n  background-color: var(--color-white);\n  position: relative;\n}\n\n.Table-module_table__tQfK6.Table-module_on-white__7sFwD .Table-module_action-bar__B4K1G {\n  background-color: var(--color-white);\n}\n\n.Table-module_table__tQfK6.Table-module_with-border-radius__vV3so .Table-module_wrapper__cEuxl {\n  border-radius: var(--table-border-radius);\n}\n\n.Table-module_table__tQfK6.Table-module_with-shadow__sjPjy .Table-module_wrapper__cEuxl {\n  box-shadow: var(--table-shadow);\n}\n\n.Table-module_table__tQfK6 .Table-module_wrapper__cEuxl .Table-module_content__fuZtJ {\n  overflow: hidden auto;\n  max-height: 100%;\n  width: fit-content;\n  min-width: 100%;\n  color: var(--color-grey-darker);\n}\n\n.Table-module_table__tQfK6.Table-module_hide-gutter__6-WWf .Table-module_wrapper__cEuxl .Table-module_content__fuZtJ {\n  scrollbar-gutter: none;\n}\n\n.Table-module_table__tQfK6 .Table-module_wrapper__cEuxl .Table-module_content__fuZtJ .Table-module_row__NZofV:not(:first-child) {\n  border-top: 1px solid var(--table-row-divider-color);\n}\n\n.Table-module_table__tQfK6 .Table-module_wrapper__cEuxl .Table-module_select-all__pKu0R {\n  padding: 0 24px;\n  height: calc(var(--table-header-height-mobile) * 2);\n  display: flex;\n  align-items: center;\n  border-bottom: 1px solid var(--table-row-divider-color);\n}\n\n.Table-module_loader__PIhm1 {\n  position: absolute;\n  z-index: var(--z-index-toast);\n}\n\n.Table-module_loader__PIhm1[class*='lazy-loader'] {\n  background-color: rgb(255 255 255 / 80%);\n}\n";
var styles$v = {"table":"Table-module_table__tQfK6","wrapper":"Table-module_wrapper__cEuxl","on-white":"Table-module_on-white__7sFwD","action-bar":"Table-module_action-bar__B4K1G","with-border-radius":"Table-module_with-border-radius__vV3so","with-shadow":"Table-module_with-shadow__sjPjy","content":"Table-module_content__fuZtJ","hide-gutter":"Table-module_hide-gutter__6-WWf","row":"Table-module_row__NZofV","select-all":"Table-module_select-all__pKu0R","loader":"Table-module_loader__PIhm1"};
styleInject(css_248z$w);

var greaterThan = function (row, id, filterValue) {
    return !isNaN(+filterValue) && !isNaN(+row.getValue(id))
        ? +row.getValue(id) > +filterValue
        : row.getValue(id).toString().toLowerCase().trim() >
            filterValue.toString().toLowerCase().trim();
};
greaterThan.autoRemove = function (val) { return !val; };
var lessThan = function (row, id, filterValue) {
    return !isNaN(+filterValue) &&
        !isNaN(+row.getValue(id))
        ? +row.getValue(id) < +filterValue
        : row.getValue(id).toString().toLowerCase().trim() <
            filterValue.toString().toLowerCase().trim();
};
lessThan.autoRemove = function (val) { return !val; };
var lessThanOrEqualTo = function (row, id, filterValue) {
    return equals(row, id, filterValue) || lessThan(row, id, filterValue);
};
lessThanOrEqualTo.autoRemove = function (val) { return !val; };
var greaterThanOrEqualTo = function (row, id, filterValue) {
    return equals(row, id, filterValue) || greaterThan(row, id, filterValue);
};
greaterThanOrEqualTo.autoRemove = function (val) { return !val; };
var between = function (row, id, filterValues) {
    return ((['', undefined].includes(filterValues[0]) ||
        greaterThan(row, id, filterValues[0])) &&
        ((!isNaN(+filterValues[0]) &&
            !isNaN(+filterValues[1]) &&
            +filterValues[0] > +filterValues[1]) ||
            ['', undefined].includes(filterValues[1]) ||
            lessThan(row, id, filterValues[1])));
};
between.autoRemove = function (val) { return !val; };
var equals = function (row, id, filterValue) {
    var value = row.getValue(id);
    if (typeof value === 'boolean' && typeof filterValue === 'boolean') {
        return value === filterValue;
    }
    if (value === undefined && typeof filterValue === 'boolean') {
        return !filterValue;
    }
    return (value.toString().toLowerCase().trim() ===
        filterValue.toString().toLowerCase().trim());
};
equals.autoRemove = function (val) { return testFalsely(val); };
var contains = function (row, id, filterValue) {
    return row
        .getValue(id)
        .toString()
        .toLowerCase()
        .trim()
        .includes(filterValue.toString().toLowerCase().trim());
};
contains.autoRemove = function (val) { return !val; };
var filters = __assign(__assign({}, filterFns), { greaterThan: greaterThan, lessThan: lessThan, lessThanOrEqualTo: lessThanOrEqualTo, greaterThanOrEqualTo: greaterThanOrEqualTo, between: between, contains: contains, equals: equals });
function testFalsely(val) {
    return val === undefined || val === null || val === '';
}

var css_248z$v = ".TableRow-module_table-row__sd51r {\n  width: 100%;\n  min-width: 100%;\n  min-height: var(--table-row-height);\n  max-height: var(--table-row-height);\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: var(--table-cell-gap);\n  overflow: hidden;\n  position: relative;\n}\n\n.TableRow-module_table-row__sd51r::after {\n  content: '';\n  position: absolute;\n  display: block;\n  right: 0;\n  top: 50%;\n  height: 75%;\n  width: 0;\n  border-radius: 8px 0 0 8px;\n  transform: translate3d(0, -50%, 0);\n  background-color: var(--color-primary);\n  transition: width 0.2s ease-in-out;\n}\n\n.TableRow-module_table-row__sd51r.TableRow-module_hover__zygiV:hover {\n  background: var(--table-row-hover-bg);\n  color: var(--table-row-hover-color);\n}\n\n.TableRow-module_table-row__sd51r.TableRow-module_action__7goJX {\n  cursor: pointer;\n}\n\n.TableRow-module_table-row__sd51r.TableRow-module_marked__Uq3hG {\n  background-color: var(--table-row-marked-bg);\n  color: var(--table-row-marked-color);\n}\n\n.TableRow-module_table-row__sd51r.TableRow-module_selected__D4LPs {\n  background-color: var(--table-row-selected-bg);\n  color: var(--table-row-selected-color);\n}\n\n.TableRow-module_table-row__sd51r.TableRow-module_selected__D4LPs::after {\n  width: 4px;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_checkbox__UAhJH {\n  padding-left: 24px;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_wrapper__gl5Wr {\n  flex: 1 1 auto;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: var(--table-cell-gap);\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_wrapper__gl5Wr.TableRow-module_padding-left__G6dkg {\n  padding-left: 24px;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_wrapper__gl5Wr.TableRow-module_padding-right__tLbDP {\n  padding-right: 24px;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT {\n  padding: 8px 0;\n  min-width: 0;\n  flex-grow: 0;\n  flex-shrink: 1;\n  text-align: left;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  font-size: var(--font-size-m);\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT.TableRow-module_align-center__mUhWd {\n  text-align: center;\n  justify-content: center;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT.TableRow-module_align-right__f-D6Z {\n  text-align: right;\n  justify-content: flex-end;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT .TableRow-module_color-hint__DPqyC {\n  display: flex;\n  text-align: left;\n  justify-content: flex-start;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT.TableRow-module_align-center__mUhWd .TableRow-module_color-hint__DPqyC {\n  text-align: center;\n  justify-content: center;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT.TableRow-module_align-right__f-D6Z .TableRow-module_color-hint__DPqyC {\n  text-align: right;\n  justify-content: flex-end;\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_table-cell__fDbJT .TableRow-module_color-hint__DPqyC .TableRow-module_color-dot__SfSnr {\n  display: block;\n  border-radius: 50%;\n  width: 16px;\n  height: 16px;\n  border: 1px solid var(--color-grey-lighter);\n}\n\n.TableRow-module_table-row__sd51r .TableRow-module_last-col__qNYuN {\n  font-size: var(--font-size-m);\n  height: 100%;\n  margin-right: 24px;\n  text-align: right;\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  gap: 8px;\n  min-width: 0;\n}\n";
var styles$u = {"table-row":"TableRow-module_table-row__sd51r","hover":"TableRow-module_hover__zygiV","action":"TableRow-module_action__7goJX","marked":"TableRow-module_marked__Uq3hG","selected":"TableRow-module_selected__D4LPs","checkbox":"TableRow-module_checkbox__UAhJH","wrapper":"TableRow-module_wrapper__gl5Wr","padding-left":"TableRow-module_padding-left__G6dkg","padding-right":"TableRow-module_padding-right__tLbDP","table-cell":"TableRow-module_table-cell__fDbJT","align-center":"TableRow-module_align-center__mUhWd","align-right":"TableRow-module_align-right__f-D6Z","color-hint":"TableRow-module_color-hint__DPqyC","color-dot":"TableRow-module_color-dot__SfSnr","last-col":"TableRow-module_last-col__qNYuN"};
styleInject(css_248z$v);

/**
 *
 * Custom Cell component to display either a Chip with a
 * boolean value or the actual cell value as string.
 */
var CustomCell = function (_a) {
    var _b, _c;
    var cell = _a.cell, columns = _a.columns;
    // Search for columns with overwritten cell
    var current = columns.find(function (col) { return col.id === cell.getContext().column.id && col.cell; });
    if (current) {
        return jsx$1(Fragment, { children: flexRender(current.cell, cell.getContext()) });
    }
    if (cell.id) {
        var type = (_c = (_b = columns.find(function (col) { return col.id === cell.column.id; })) === null || _b === void 0 ? void 0 : _b.type) !== null && _c !== void 0 ? _c : 'string';
        if (type === 'boolean' || type === 'active') {
            var isTrue = cell.getContext().getValue();
            var title = '';
            if (type === 'boolean') {
                title = isTrue ? 'An' : 'Aus';
            }
            else {
                title = isTrue ? 'Aktiv' : 'Inaktiv';
            }
            return (jsx$1(Chip$1, { title: title, isSmall: true, value: isTrue ? jsx$1(Fragment, { children: "\u2713" }) : jsx$1(Fragment, { children: "\u2014" }), color: isTrue ? 'var(--color-white)' : 'var(--color-grey-light)', bgColor: type === 'active'
                    ? isTrue
                        ? 'var(--color-success)'
                        : 'var(--color-grey-lighter)'
                    : isTrue
                        ? 'var(--color-primary)'
                        : 'var(--color-grey-lighter)' }));
        }
        else if (type === 'date' &&
            cell.getContext().getValue() instanceof Date) {
            var date = cell.getContext().getValue().toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
            return jsx$1(Fragment, { children: date });
        }
        else if (type === 'color') {
            var config = cell.getContext().getValue();
            return (jsx$1("div", { className: styles$u['color-hint'], children: jsx$1(Tooltip$1, { usePortal: true, content: config && config.colorLabel
                        ? config.colorLabel
                        : config && config.color
                            ? config.color.toUpperCase()
                            : 'Keine Farbe', children: jsx$1("span", { className: styles$u['color-dot'], style: {
                            backgroundColor: config && config.color ? config.color : '#FFF',
                        } }) }) }));
        }
    }
    return jsx$1(Fragment, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) });
};

// calculate classes, flexBasis, maxWidth, flexGrow and type for given cell based on type/custom width/...
function getClassesAndStyle(columns, cell) {
    var _a, _b, _c;
    var columnData = columns.find(function (col) { return col.id === cell.column.id; });
    var classes = ['table-cell'];
    if (columnData === null || columnData === void 0 ? void 0 : columnData.alignment)
        classes.push('align-' + columnData.alignment);
    var type = (_a = columnData === null || columnData === void 0 ? void 0 : columnData.type) !== null && _a !== void 0 ? _a : 'string';
    var customWidth = (_b = columnData === null || columnData === void 0 ? void 0 : columnData.size) !== null && _b !== void 0 ? _b : null;
    var canExpand = (_c = columnData === null || columnData === void 0 ? void 0 : columnData.canExpand) !== null && _c !== void 0 ? _c : false;
    var width = '';
    if (customWidth)
        width = "".concat(customWidth, "px");
    else if (type === 'boolean' || type === 'active')
        width = '48px';
    else if (type === 'color')
        width = '60px';
    else if (type === 'date')
        width = '110px';
    else
        width = '125px';
    return {
        classes: classes,
        flexBasis: width,
        maxWidth: canExpand ? 'initial' : width,
        flexGrow: canExpand ? 1 : 0,
        canOverflow: !!(columnData === null || columnData === void 0 ? void 0 : columnData.canOverflow),
        isMultiline: !!(columnData === null || columnData === void 0 ? void 0 : columnData.isMultiline),
        type: type,
    };
}

function TableRow(_a) {
    var _b, _c;
    var showSelect = _a.showSelect, showVisibility = _a.showVisibility, columns = _a.columns, row = _a.row, rowSelection = _a.rowSelection, rowMarker = _a.rowMarker, showRowHover = _a.showRowHover, selectOnClick = _a.selectOnClick, showSelected = _a.showSelected, lastColWidth = _a.lastColWidth, lastCol = _a.lastCol, customPadding = _a.customPadding, customLastColSpacing = _a.customLastColSpacing, customHeight = _a.customHeight, showMultiline = _a.showMultiline, onRowClick = _a.onRowClick, className = _a.className;
    var cells = row.getVisibleCells();
    var rowClasses = classNames$1(styles$u['table-row'], (_b = {},
        _b[styles$u.hover] = showRowHover,
        _b[styles$u.action] = onRowClick || selectOnClick,
        _b[styles$u.marked] = rowMarker && rowMarker[row.id],
        _b[styles$u.selected] = showSelected && rowSelection && rowSelection[row.id],
        _b), className);
    var wrapperClasses = classNames$1(styles$u.wrapper, (_c = {},
        _c[styles$u['padding-left']] = !showSelect,
        _c[styles$u['padding-right']] = !showVisibility && !lastCol,
        _c));
    return (jsxs("div", { className: rowClasses, style: {
            height: customHeight !== null && customHeight !== void 0 ? customHeight : undefined,
            minHeight: customHeight !== null && customHeight !== void 0 ? customHeight : undefined,
            maxHeight: showMultiline ? 'initial' : (customHeight !== null && customHeight !== void 0 ? customHeight : undefined),
        }, onClick: function (e) {
            if (onRowClick)
                onRowClick(e, row);
            if (selectOnClick)
                row.toggleSelected();
        }, children: [showSelect && (jsx$1(Checkbox$1, { className: styles$u.checkbox, style: { paddingLeft: customPadding !== null && customPadding !== void 0 ? customPadding : undefined }, name: "checkbox-".concat(row.id), checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler() })), jsx$1("div", { className: wrapperClasses, style: {
                    paddingLeft: !showSelect && customPadding ? customPadding : undefined,
                    paddingRight: !showVisibility && !lastCol && customLastColSpacing
                        ? customLastColSpacing
                        : !showVisibility && !lastCol && customPadding
                            ? customPadding
                            : undefined,
                    maxWidth: columns.length <= 1 && lastCol
                        ? "calc(100% - ".concat(lastColWidth, " - var(--table-cell-gap) * 2)")
                        : '100%',
                }, children: cells.map(function (cell) {
                    var _a = getClassesAndStyle(columns, cell), classes = _a.classes, flexBasis = _a.flexBasis, maxWidth = _a.maxWidth, flexGrow = _a.flexGrow, canOverflow = _a.canOverflow, isMultiline = _a.isMultiline;
                    var cellClasses = classNames$1(classes.map(function (c) { return styles$u[c]; }));
                    return (jsx$1("div", { className: cellClasses, style: {
                            flexBasis: flexBasis !== null && flexBasis !== void 0 ? flexBasis : undefined,
                            maxWidth: maxWidth !== null && maxWidth !== void 0 ? maxWidth : undefined,
                            flexGrow: flexGrow,
                            overflow: canOverflow ? 'initial' : undefined,
                            whiteSpace: isMultiline ? 'initial' : undefined,
                        }, children: flexRender(CustomCell, {
                            cell: cell,
                            columns: columns,
                        }) }, cell.id));
                }) }), (showVisibility || lastCol) && (jsx$1("div", { className: styles$u['last-col'], style: {
                    flexBasis: lastColWidth,
                    minWidth: lastColWidth,
                    maxWidth: lastColWidth,
                    marginRight: customLastColSpacing
                        ? customLastColSpacing
                        : (customPadding !== null && customPadding !== void 0 ? customPadding : undefined),
                }, children: lastCol && lastCol(row) }))] }));
}

var css_248z$u = ".TableHeader-module_table-header__ym65G {\n  width: fit-content;\n  min-width: 100%;\n  height: var(--table-header-height);\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: var(--table-cell-gap);\n  overflow: hidden;\n  border-bottom: 1px solid var(--table-row-divider-color);\n  scrollbar-width: thin;\n}\n\n.TableHeader-module_table-header__ym65G.TableHeader-module_can-scroll__YJvnC {\n  scrollbar-gutter: stable;\n  overflow-y: auto;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_checkbox__hDnMk {\n  padding-left: 24px;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_wrapper__NzpgV {\n  flex: 1 1 auto;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: var(--table-cell-gap);\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_wrapper__NzpgV.TableHeader-module_padding-left__DI-Bq {\n  padding-left: 24px;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_wrapper__NzpgV.TableHeader-module_padding-right__3YTVW {\n  padding-right: 24px;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy {\n  flex-grow: 0;\n  flex-shrink: 1;\n  text-align: left;\n  justify-content: flex-start;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  font-size: var(--font-size-xs);\n  color: var(--color-grey);\n  user-select: none;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy .TableHeader-module_content__gi-Vt {\n  display: flex;\n  align-items: center;\n  gap: 4px;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy.TableHeader-module_sortable__5nkGc,\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy .TableHeader-module_sortable__5nkGc {\n  cursor: pointer;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy.TableHeader-module_align-center__QY1jv .TableHeader-module_content__gi-Vt {\n  text-align: center;\n  justify-content: center;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy.TableHeader-module_align-right__R2sqz .TableHeader-module_content__gi-Vt {\n  text-align: right;\n  justify-content: flex-end;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy .TableHeader-module_icon__T7E-s {\n  cursor: pointer;\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy:hover .TableHeader-module_icon__T7E-s,\n.TableHeader-module_table-header__ym65G .TableHeader-module_table-cell__4euIy:hover .TableHeader-module_icon__T7E-s svg {\n  color: var(--color-grey);\n}\n\n.TableHeader-module_table-header__ym65G .TableHeader-module_last-col__YTv1B {\n  margin-right: 24px;\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n}\n";
var styles$t = {"table-header":"TableHeader-module_table-header__ym65G","can-scroll":"TableHeader-module_can-scroll__YJvnC","checkbox":"TableHeader-module_checkbox__hDnMk","wrapper":"TableHeader-module_wrapper__NzpgV","padding-left":"TableHeader-module_padding-left__DI-Bq","padding-right":"TableHeader-module_padding-right__3YTVW","table-cell":"TableHeader-module_table-cell__4euIy","content":"TableHeader-module_content__gi-Vt","sortable":"TableHeader-module_sortable__5nkGc","align-center":"TableHeader-module_align-center__QY1jv","align-right":"TableHeader-module_align-right__R2sqz","icon":"TableHeader-module_icon__T7E-s","last-col":"TableHeader-module_last-col__YTv1B"};
styleInject(css_248z$u);

var css_248z$t = ".SortIcon-module_sort-icon__FAqPm {\n  display: inline;\n  margin-left: 2px;\n  color: var(--color-grey-lighter);\n}\n\n.SortIcon-module_sort-icon__FAqPm .SortIcon-module_icon__Uqdqh {\n  height: 8px;\n  width: 8px;\n  margin-bottom: 2px;\n  color: var(--color-grey-light);\n  stroke-width: 4;\n}\n";
var styles$s = {"sort-icon":"SortIcon-module_sort-icon__FAqPm","icon":"SortIcon-module_icon__Uqdqh"};
styleInject(css_248z$t);

/**
 * Sort Icon Component for the Table Component's header.
 *
 * Displays an arrow icon depending on the sorting direction.
 *
 * @param {SortIconProps}
 */
var SortIcon = function (_a) {
    var direction = _a.direction, order = _a.order, className = _a.className;
    return (jsxs("div", { className: classNames$1(styles$s['sort-icon'], className), children: [direction === 'down' ? (jsx$1(SvgArrowHeadUpIcon, { className: styles$s['icon'] })) : (jsx$1(SvgArrowHeadDownIcon, { className: styles$s['icon'] })), order !== undefined ? order + 1 : jsx$1(Fragment, {})] }));
};

function TableHeader(_a) {
    var _b, _c;
    var showSelect = _a.showSelect, showSort = _a.showSort, showVisibility = _a.showVisibility, columns = _a.columns, table = _a.table, tableCanScroll = _a.tableCanScroll, hasLastCol = _a.hasLastCol, lastColWidth = _a.lastColWidth, customPadding = _a.customPadding, customHeight = _a.customHeight, customLastColSpacing = _a.customLastColSpacing, width = _a.width;
    var cells = table
        .getHeaderGroups()
        .map(function (group) { return group.headers.map(function (header) { return header; }); })
        .flat(2);
    function getCell(cell, type) {
        var _a, _b, _c;
        return (jsxs("div", { className: styles$t.content, children: [((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.tooltip) ? (jsx$1(Tooltip$1, { usePortal: true, content: (_b = cell.column.columnDef.meta) === null || _b === void 0 ? void 0 : _b.tooltip, children: getContent(cell, type) })) : (getContent(cell, type)), (_c = (showSort &&
                    {
                        asc: (jsx$1(SortIcon, { className: styles$t.icon, direction: 'down', order: cell.column.getSortIndex() })),
                        desc: (jsx$1(SortIcon, { className: styles$t.icon, direction: 'up', order: cell.column.getSortIndex() })),
                    }[cell.column.getIsSorted()])) !== null && _c !== void 0 ? _c : null] }));
    }
    function getContent(cell, type) {
        return (jsx$1("div", { className: showSort && type !== 'color' ? styles$t.sortable : undefined, onClick: showSort && type !== 'color'
                ? cell.column.getToggleSortingHandler()
                : undefined, children: flexRender(cell.column.columnDef.header, cell.getContext()) }));
    }
    var classes = classNames$1(styles$t['table-header'], (_b = {},
        _b[styles$t['can-scroll']] = tableCanScroll,
        _b));
    var wrapperClasses = classNames$1(styles$t.wrapper, (_c = {},
        _c[styles$t['padding-left']] = !showSelect,
        _c[styles$t['padding-right']] = !showVisibility && !hasLastCol,
        _c));
    return (jsxs("div", { className: classes, style: { height: customHeight !== null && customHeight !== void 0 ? customHeight : undefined, width: width }, children: [showSelect && (jsx$1(Checkbox$1, { className: styles$t.checkbox, style: { paddingLeft: customPadding !== null && customPadding !== void 0 ? customPadding : undefined }, name: 'all-checked', checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler() })), jsx$1("div", { className: wrapperClasses, style: {
                    paddingLeft: !showSelect && customPadding ? customPadding : undefined,
                    paddingRight: !showVisibility && !hasLastCol && customLastColSpacing
                        ? customLastColSpacing
                        : !showVisibility && !hasLastCol && customPadding
                            ? customPadding
                            : undefined,
                }, children: cells.map(function (cell) {
                    var _a = getClassesAndStyle(columns, cell), classes = _a.classes, flexBasis = _a.flexBasis, maxWidth = _a.maxWidth, flexGrow = _a.flexGrow, type = _a.type;
                    var cellClasses = classNames$1(classes.map(function (c) { return styles$t[c]; }));
                    return (jsx$1("div", { className: cellClasses, style: {
                            flexBasis: flexBasis !== null && flexBasis !== void 0 ? flexBasis : undefined,
                            maxWidth: maxWidth !== null && maxWidth !== void 0 ? maxWidth : undefined,
                            flexGrow: flexGrow,
                        }, children: getCell(cell, type) }, cell.id));
                }) }), (showVisibility || hasLastCol) && (jsx$1("div", { className: styles$t['last-col'], style: {
                    width: lastColWidth,
                    marginRight: customLastColSpacing
                        ? customLastColSpacing
                        : customPadding !== null && customPadding !== void 0 ? customPadding : undefined,
                }, children: showVisibility && (jsx$1(Dropdown$1, { trigger: jsx$1(Button$1, { hierarchy: 'ghost', icon: jsx$1(SvgDotsHorizontalIcon, { className: 'small' }) }), children: jsx$1(DropdownMenu, { data: table
                            .getAllColumns()
                            .filter(function (column) { return column.getCanHide(); })
                            .map(function (column) {
                            var _a, _b, _c;
                            return {
                                label: (_c = (_b = (_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.filterName) !== null && _b !== void 0 ? _b : column.columnDef.header) !== null && _c !== void 0 ? _c : '',
                                type: 'switch',
                                value: column.getIsVisible(),
                                onValueChange: function (value) {
                                    return column.toggleVisibility(value);
                                },
                            };
                        }) }) })) }))] }));
}

function useCalculateTableHeight() {
    var _a = useState(0), parentOffsetY = _a[0], setParentOffsetY = _a[1];
    var _b = useState({
        height: 0,
        width: 0,
    }), dimensions = _b[0], setDimensions = _b[1];
    var wrapperRef = useRef(null);
    useLayoutEffect(function () {
        function handleResize() {
            if (wrapperRef.current) {
                setParentOffsetY(wrapperRef.current.getBoundingClientRect().top);
            }
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth,
            });
        }
        window.addEventListener('resize', handleResize);
        handleResize();
    }, []);
    return {
        calculatedHeight: "calc(100vh - ".concat(parentOffsetY, "px - 16px)"),
        wrapperRef: wrapperRef,
        dimensions: dimensions,
    };
}

var lib = {exports: {}};

var Modal$2 = {};

var propTypes = {exports: {}};

var reactIs = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min;

function requireReactIs_production_min () {
	if (hasRequiredReactIs_production_min) return reactIs_production_min;
	hasRequiredReactIs_production_min = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
	reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
	reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
	reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;
	return reactIs_production_min;
}

var reactIs_development = {};

var hasRequiredReactIs_development;

function requireReactIs_development () {
	if (hasRequiredReactIs_development) return reactIs_development;
	hasRequiredReactIs_development = 1;



	if (browser$1.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development.AsyncMode = AsyncMode;
	reactIs_development.ConcurrentMode = ConcurrentMode;
	reactIs_development.ContextConsumer = ContextConsumer;
	reactIs_development.ContextProvider = ContextProvider;
	reactIs_development.Element = Element;
	reactIs_development.ForwardRef = ForwardRef;
	reactIs_development.Fragment = Fragment;
	reactIs_development.Lazy = Lazy;
	reactIs_development.Memo = Memo;
	reactIs_development.Portal = Portal;
	reactIs_development.Profiler = Profiler;
	reactIs_development.StrictMode = StrictMode;
	reactIs_development.Suspense = Suspense;
	reactIs_development.isAsyncMode = isAsyncMode;
	reactIs_development.isConcurrentMode = isConcurrentMode;
	reactIs_development.isContextConsumer = isContextConsumer;
	reactIs_development.isContextProvider = isContextProvider;
	reactIs_development.isElement = isElement;
	reactIs_development.isForwardRef = isForwardRef;
	reactIs_development.isFragment = isFragment;
	reactIs_development.isLazy = isLazy;
	reactIs_development.isMemo = isMemo;
	reactIs_development.isPortal = isPortal;
	reactIs_development.isProfiler = isProfiler;
	reactIs_development.isStrictMode = isStrictMode;
	reactIs_development.isSuspense = isSuspense;
	reactIs_development.isValidElementType = isValidElementType;
	reactIs_development.typeOf = typeOf;
	  })();
	}
	return reactIs_development;
}

var hasRequiredReactIs;

function requireReactIs () {
	if (hasRequiredReactIs) return reactIs.exports;
	hasRequiredReactIs = 1;

	if (browser$1.env.NODE_ENV === 'production') {
	  reactIs.exports = requireReactIs_production_min();
	} else {
	  reactIs.exports = requireReactIs_development();
	}
	return reactIs.exports;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/

var objectAssign;
var hasRequiredObjectAssign;

function requireObjectAssign () {
	if (hasRequiredObjectAssign) return objectAssign;
	hasRequiredObjectAssign = 1;
	/* eslint-disable no-unused-vars */
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	function shouldUseNative() {
		try {
			if (!Object.assign) {
				return false;
			}

			// Detect buggy property enumeration order in older V8 versions.

			// https://bugs.chromium.org/p/v8/issues/detail?id=4118
			var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
			test1[5] = 'de';
			if (Object.getOwnPropertyNames(test1)[0] === '5') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test2 = {};
			for (var i = 0; i < 10; i++) {
				test2['_' + String.fromCharCode(i)] = i;
			}
			var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
				return test2[n];
			});
			if (order2.join('') !== '0123456789') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test3 = {};
			'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
				test3[letter] = letter;
			});
			if (Object.keys(Object.assign({}, test3)).join('') !==
					'abcdefghijklmnopqrst') {
				return false;
			}

			return true;
		} catch (err) {
			// We don't expect any of the above to throw, but better to be safe.
			return false;
		}
	}

	objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (getOwnPropertySymbols) {
				symbols = getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	return objectAssign;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;

function requireReactPropTypesSecret () {
	if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
	hasRequiredReactPropTypesSecret = 1;

	var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

	ReactPropTypesSecret_1 = ReactPropTypesSecret;
	return ReactPropTypesSecret_1;
}

var has;
var hasRequiredHas;

function requireHas () {
	if (hasRequiredHas) return has;
	hasRequiredHas = 1;
	has = Function.call.bind(Object.prototype.hasOwnProperty);
	return has;
}

var checkPropTypes_1;
var hasRequiredCheckPropTypes;

function requireCheckPropTypes () {
	if (hasRequiredCheckPropTypes) return checkPropTypes_1;
	hasRequiredCheckPropTypes = 1;

	var printWarning = function() {};

	if (browser$1.env.NODE_ENV !== 'production') {
	  var ReactPropTypesSecret = /*@__PURE__*/ requireReactPropTypesSecret();
	  var loggedTypeFailures = {};
	  var has = /*@__PURE__*/ requireHas();

	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) { /**/ }
	  };
	}

	/**
	 * Assert that the values match with the type specs.
	 * Error messages are memorized and will only be shown once.
	 *
	 * @param {object} typeSpecs Map of name to a ReactPropType
	 * @param {object} values Runtime values that need to be type-checked
	 * @param {string} location e.g. "prop", "context", "child context"
	 * @param {string} componentName Name of the component for error messages.
	 * @param {?Function} getStack Returns the component stack.
	 * @private
	 */
	function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
	  if (browser$1.env.NODE_ENV !== 'production') {
	    for (var typeSpecName in typeSpecs) {
	      if (has(typeSpecs, typeSpecName)) {
	        var error;
	        // Prop type validation may throw. In case they do, we don't want to
	        // fail the render phase where it didn't fail before. So we log it.
	        // After these have been cleaned up, we'll let them throw.
	        try {
	          // This is intentionally an invariant that gets caught. It's the same
	          // behavior as without this statement except with a better message.
	          if (typeof typeSpecs[typeSpecName] !== 'function') {
	            var err = Error(
	              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
	              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
	              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
	            );
	            err.name = 'Invariant Violation';
	            throw err;
	          }
	          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret);
	        } catch (ex) {
	          error = ex;
	        }
	        if (error && !(error instanceof Error)) {
	          printWarning(
	            (componentName || 'React class') + ': type specification of ' +
	            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
	            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
	            'You may have forgotten to pass an argument to the type checker ' +
	            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
	            'shape all require an argument).'
	          );
	        }
	        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
	          // Only monitor this failure once because there tends to be a lot of the
	          // same error.
	          loggedTypeFailures[error.message] = true;

	          var stack = getStack ? getStack() : '';

	          printWarning(
	            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
	          );
	        }
	      }
	    }
	  }
	}

	/**
	 * Resets warning cache when testing.
	 *
	 * @private
	 */
	checkPropTypes.resetWarningCache = function() {
	  if (browser$1.env.NODE_ENV !== 'production') {
	    loggedTypeFailures = {};
	  }
	};

	checkPropTypes_1 = checkPropTypes;
	return checkPropTypes_1;
}

var factoryWithTypeCheckers;
var hasRequiredFactoryWithTypeCheckers;

function requireFactoryWithTypeCheckers () {
	if (hasRequiredFactoryWithTypeCheckers) return factoryWithTypeCheckers;
	hasRequiredFactoryWithTypeCheckers = 1;

	var ReactIs = requireReactIs();
	var assign = requireObjectAssign();

	var ReactPropTypesSecret = /*@__PURE__*/ requireReactPropTypesSecret();
	var has = /*@__PURE__*/ requireHas();
	var checkPropTypes = /*@__PURE__*/ requireCheckPropTypes();

	var printWarning = function() {};

	if (browser$1.env.NODE_ENV !== 'production') {
	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) {}
	  };
	}

	function emptyFunctionThatReturnsNull() {
	  return null;
	}

	factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
	  /* global Symbol */
	  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
	  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

	  /**
	   * Returns the iterator method function contained on the iterable object.
	   *
	   * Be sure to invoke the function with the iterable as context:
	   *
	   *     var iteratorFn = getIteratorFn(myIterable);
	   *     if (iteratorFn) {
	   *       var iterator = iteratorFn.call(myIterable);
	   *       ...
	   *     }
	   *
	   * @param {?object} maybeIterable
	   * @return {?function}
	   */
	  function getIteratorFn(maybeIterable) {
	    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
	    if (typeof iteratorFn === 'function') {
	      return iteratorFn;
	    }
	  }

	  /**
	   * Collection of methods that allow declaration and validation of props that are
	   * supplied to React components. Example usage:
	   *
	   *   var Props = require('ReactPropTypes');
	   *   var MyArticle = React.createClass({
	   *     propTypes: {
	   *       // An optional string prop named "description".
	   *       description: Props.string,
	   *
	   *       // A required enum prop named "category".
	   *       category: Props.oneOf(['News','Photos']).isRequired,
	   *
	   *       // A prop named "dialog" that requires an instance of Dialog.
	   *       dialog: Props.instanceOf(Dialog).isRequired
	   *     },
	   *     render: function() { ... }
	   *   });
	   *
	   * A more formal specification of how these methods are used:
	   *
	   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
	   *   decl := ReactPropTypes.{type}(.isRequired)?
	   *
	   * Each and every declaration produces a function with the same signature. This
	   * allows the creation of custom validation functions. For example:
	   *
	   *  var MyLink = React.createClass({
	   *    propTypes: {
	   *      // An optional string or URI prop named "href".
	   *      href: function(props, propName, componentName) {
	   *        var propValue = props[propName];
	   *        if (propValue != null && typeof propValue !== 'string' &&
	   *            !(propValue instanceof URI)) {
	   *          return new Error(
	   *            'Expected a string or an URI for ' + propName + ' in ' +
	   *            componentName
	   *          );
	   *        }
	   *      }
	   *    },
	   *    render: function() {...}
	   *  });
	   *
	   * @internal
	   */

	  var ANONYMOUS = '<<anonymous>>';

	  // Important!
	  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
	  var ReactPropTypes = {
	    array: createPrimitiveTypeChecker('array'),
	    bigint: createPrimitiveTypeChecker('bigint'),
	    bool: createPrimitiveTypeChecker('boolean'),
	    func: createPrimitiveTypeChecker('function'),
	    number: createPrimitiveTypeChecker('number'),
	    object: createPrimitiveTypeChecker('object'),
	    string: createPrimitiveTypeChecker('string'),
	    symbol: createPrimitiveTypeChecker('symbol'),

	    any: createAnyTypeChecker(),
	    arrayOf: createArrayOfTypeChecker,
	    element: createElementTypeChecker(),
	    elementType: createElementTypeTypeChecker(),
	    instanceOf: createInstanceTypeChecker,
	    node: createNodeChecker(),
	    objectOf: createObjectOfTypeChecker,
	    oneOf: createEnumTypeChecker,
	    oneOfType: createUnionTypeChecker,
	    shape: createShapeTypeChecker,
	    exact: createStrictShapeTypeChecker,
	  };

	  /**
	   * inlined Object.is polyfill to avoid requiring consumers ship their own
	   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
	   */
	  /*eslint-disable no-self-compare*/
	  function is(x, y) {
	    // SameValue algorithm
	    if (x === y) {
	      // Steps 1-5, 7-10
	      // Steps 6.b-6.e: +0 != -0
	      return x !== 0 || 1 / x === 1 / y;
	    } else {
	      // Step 6.a: NaN == NaN
	      return x !== x && y !== y;
	    }
	  }
	  /*eslint-enable no-self-compare*/

	  /**
	   * We use an Error-like object for backward compatibility as people may call
	   * PropTypes directly and inspect their output. However, we don't use real
	   * Errors anymore. We don't inspect their stack anyway, and creating them
	   * is prohibitively expensive if they are created too often, such as what
	   * happens in oneOfType() for any type before the one that matched.
	   */
	  function PropTypeError(message, data) {
	    this.message = message;
	    this.data = data && typeof data === 'object' ? data: {};
	    this.stack = '';
	  }
	  // Make `instanceof Error` still work for returned errors.
	  PropTypeError.prototype = Error.prototype;

	  function createChainableTypeChecker(validate) {
	    if (browser$1.env.NODE_ENV !== 'production') {
	      var manualPropTypeCallCache = {};
	      var manualPropTypeWarningCount = 0;
	    }
	    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
	      componentName = componentName || ANONYMOUS;
	      propFullName = propFullName || propName;

	      if (secret !== ReactPropTypesSecret) {
	        if (throwOnDirectAccess) {
	          // New behavior only for users of `prop-types` package
	          var err = new Error(
	            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	            'Use `PropTypes.checkPropTypes()` to call them. ' +
	            'Read more at http://fb.me/use-check-prop-types'
	          );
	          err.name = 'Invariant Violation';
	          throw err;
	        } else if (browser$1.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
	          // Old behavior for people using React.PropTypes
	          var cacheKey = componentName + ':' + propName;
	          if (
	            !manualPropTypeCallCache[cacheKey] &&
	            // Avoid spamming the console because they are often not actionable except for lib authors
	            manualPropTypeWarningCount < 3
	          ) {
	            printWarning(
	              'You are manually calling a React.PropTypes validation ' +
	              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
	              'and will throw in the standalone `prop-types` package. ' +
	              'You may be seeing this warning due to a third-party PropTypes ' +
	              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
	            );
	            manualPropTypeCallCache[cacheKey] = true;
	            manualPropTypeWarningCount++;
	          }
	        }
	      }
	      if (props[propName] == null) {
	        if (isRequired) {
	          if (props[propName] === null) {
	            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
	          }
	          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
	        }
	        return null;
	      } else {
	        return validate(props, propName, componentName, location, propFullName);
	      }
	    }

	    var chainedCheckType = checkType.bind(null, false);
	    chainedCheckType.isRequired = checkType.bind(null, true);

	    return chainedCheckType;
	  }

	  function createPrimitiveTypeChecker(expectedType) {
	    function validate(props, propName, componentName, location, propFullName, secret) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== expectedType) {
	        // `propValue` being instance of, say, date/regexp, pass the 'object'
	        // check, but we can offer a more precise error message here rather than
	        // 'of type `object`'.
	        var preciseType = getPreciseType(propValue);

	        return new PropTypeError(
	          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
	          {expectedType: expectedType}
	        );
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createAnyTypeChecker() {
	    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
	  }

	  function createArrayOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
	      }
	      var propValue = props[propName];
	      if (!Array.isArray(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
	      }
	      for (var i = 0; i < propValue.length; i++) {
	        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret);
	        if (error instanceof Error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!isValidElement(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!ReactIs.isValidElementType(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createInstanceTypeChecker(expectedClass) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!(props[propName] instanceof expectedClass)) {
	        var expectedClassName = expectedClass.name || ANONYMOUS;
	        var actualClassName = getClassName(props[propName]);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createEnumTypeChecker(expectedValues) {
	    if (!Array.isArray(expectedValues)) {
	      if (browser$1.env.NODE_ENV !== 'production') {
	        if (arguments.length > 1) {
	          printWarning(
	            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
	            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
	          );
	        } else {
	          printWarning('Invalid argument supplied to oneOf, expected an array.');
	        }
	      }
	      return emptyFunctionThatReturnsNull;
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      for (var i = 0; i < expectedValues.length; i++) {
	        if (is(propValue, expectedValues[i])) {
	          return null;
	        }
	      }

	      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
	        var type = getPreciseType(value);
	        if (type === 'symbol') {
	          return String(value);
	        }
	        return value;
	      });
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createObjectOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
	      }
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
	      }
	      for (var key in propValue) {
	        if (has(propValue, key)) {
	          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	          if (error instanceof Error) {
	            return error;
	          }
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createUnionTypeChecker(arrayOfTypeCheckers) {
	    if (!Array.isArray(arrayOfTypeCheckers)) {
	      browser$1.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
	      return emptyFunctionThatReturnsNull;
	    }

	    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	      var checker = arrayOfTypeCheckers[i];
	      if (typeof checker !== 'function') {
	        printWarning(
	          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
	          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
	        );
	        return emptyFunctionThatReturnsNull;
	      }
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var expectedTypes = [];
	      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	        var checker = arrayOfTypeCheckers[i];
	        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret);
	        if (checkerResult == null) {
	          return null;
	        }
	        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
	          expectedTypes.push(checkerResult.data.expectedType);
	        }
	      }
	      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createNodeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!isNode(props[propName])) {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function invalidValidatorError(componentName, location, propFullName, key, type) {
	    return new PropTypeError(
	      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
	      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
	    );
	  }

	  function createShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      for (var key in shapeTypes) {
	        var checker = shapeTypes[key];
	        if (typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createStrictShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      // We need to check all keys in case some are required but missing from props.
	      var allKeys = assign({}, props[propName], shapeTypes);
	      for (var key in allKeys) {
	        var checker = shapeTypes[key];
	        if (has(shapeTypes, key) && typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        if (!checker) {
	          return new PropTypeError(
	            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
	            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
	            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
	          );
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }

	    return createChainableTypeChecker(validate);
	  }

	  function isNode(propValue) {
	    switch (typeof propValue) {
	      case 'number':
	      case 'string':
	      case 'undefined':
	        return true;
	      case 'boolean':
	        return !propValue;
	      case 'object':
	        if (Array.isArray(propValue)) {
	          return propValue.every(isNode);
	        }
	        if (propValue === null || isValidElement(propValue)) {
	          return true;
	        }

	        var iteratorFn = getIteratorFn(propValue);
	        if (iteratorFn) {
	          var iterator = iteratorFn.call(propValue);
	          var step;
	          if (iteratorFn !== propValue.entries) {
	            while (!(step = iterator.next()).done) {
	              if (!isNode(step.value)) {
	                return false;
	              }
	            }
	          } else {
	            // Iterator will provide entry [k,v] tuples rather than values.
	            while (!(step = iterator.next()).done) {
	              var entry = step.value;
	              if (entry) {
	                if (!isNode(entry[1])) {
	                  return false;
	                }
	              }
	            }
	          }
	        } else {
	          return false;
	        }

	        return true;
	      default:
	        return false;
	    }
	  }

	  function isSymbol(propType, propValue) {
	    // Native Symbol.
	    if (propType === 'symbol') {
	      return true;
	    }

	    // falsy value can't be a Symbol
	    if (!propValue) {
	      return false;
	    }

	    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
	    if (propValue['@@toStringTag'] === 'Symbol') {
	      return true;
	    }

	    // Fallback for non-spec compliant Symbols which are polyfilled.
	    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
	      return true;
	    }

	    return false;
	  }

	  // Equivalent of `typeof` but with special handling for array and regexp.
	  function getPropType(propValue) {
	    var propType = typeof propValue;
	    if (Array.isArray(propValue)) {
	      return 'array';
	    }
	    if (propValue instanceof RegExp) {
	      // Old webkits (at least until Android 4.0) return 'function' rather than
	      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
	      // passes PropTypes.object.
	      return 'object';
	    }
	    if (isSymbol(propType, propValue)) {
	      return 'symbol';
	    }
	    return propType;
	  }

	  // This handles more types than `getPropType`. Only used for error messages.
	  // See `createPrimitiveTypeChecker`.
	  function getPreciseType(propValue) {
	    if (typeof propValue === 'undefined' || propValue === null) {
	      return '' + propValue;
	    }
	    var propType = getPropType(propValue);
	    if (propType === 'object') {
	      if (propValue instanceof Date) {
	        return 'date';
	      } else if (propValue instanceof RegExp) {
	        return 'regexp';
	      }
	    }
	    return propType;
	  }

	  // Returns a string that is postfixed to a warning about an invalid type.
	  // For example, "undefined" or "of type array"
	  function getPostfixForTypeWarning(value) {
	    var type = getPreciseType(value);
	    switch (type) {
	      case 'array':
	      case 'object':
	        return 'an ' + type;
	      case 'boolean':
	      case 'date':
	      case 'regexp':
	        return 'a ' + type;
	      default:
	        return type;
	    }
	  }

	  // Returns class name of the object, if any.
	  function getClassName(propValue) {
	    if (!propValue.constructor || !propValue.constructor.name) {
	      return ANONYMOUS;
	    }
	    return propValue.constructor.name;
	  }

	  ReactPropTypes.checkPropTypes = checkPropTypes;
	  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithTypeCheckers;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;

function requireFactoryWithThrowingShims () {
	if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
	hasRequiredFactoryWithThrowingShims = 1;

	var ReactPropTypesSecret = /*@__PURE__*/ requireReactPropTypesSecret();

	function emptyFunction() {}
	function emptyFunctionWithReset() {}
	emptyFunctionWithReset.resetWarningCache = emptyFunction;

	factoryWithThrowingShims = function() {
	  function shim(props, propName, componentName, location, propFullName, secret) {
	    if (secret === ReactPropTypesSecret) {
	      // It is still safe when called from React.
	      return;
	    }
	    var err = new Error(
	      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	      'Use PropTypes.checkPropTypes() to call them. ' +
	      'Read more at http://fb.me/use-check-prop-types'
	    );
	    err.name = 'Invariant Violation';
	    throw err;
	  }	  shim.isRequired = shim;
	  function getShim() {
	    return shim;
	  }	  // Important!
	  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
	  var ReactPropTypes = {
	    array: shim,
	    bigint: shim,
	    bool: shim,
	    func: shim,
	    number: shim,
	    object: shim,
	    string: shim,
	    symbol: shim,

	    any: shim,
	    arrayOf: getShim,
	    element: shim,
	    elementType: shim,
	    instanceOf: getShim,
	    node: shim,
	    objectOf: getShim,
	    oneOf: getShim,
	    oneOfType: getShim,
	    shape: getShim,
	    exact: getShim,

	    checkPropTypes: emptyFunctionWithReset,
	    resetWarningCache: emptyFunction
	  };

	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithThrowingShims;
}

var hasRequiredPropTypes;

function requirePropTypes () {
	if (hasRequiredPropTypes) return propTypes.exports;
	hasRequiredPropTypes = 1;
	if (browser$1.env.NODE_ENV !== 'production') {
	  var ReactIs = requireReactIs();

	  // By explicitly using `prop-types` you are opting into new development behavior.
	  // http://fb.me/prop-types-in-prod
	  var throwOnDirectAccess = true;
	  propTypes.exports = /*@__PURE__*/ requireFactoryWithTypeCheckers()(ReactIs.isElement, throwOnDirectAccess);
	} else {
	  // By explicitly using `prop-types` you are opting into new production behavior.
	  // http://fb.me/prop-types-in-prod
	  propTypes.exports = /*@__PURE__*/ requireFactoryWithThrowingShims()();
	}
	return propTypes.exports;
}

var ModalPortal = {exports: {}};

var focusManager = {};

var tabbable = {exports: {}};

var hasRequiredTabbable;

function requireTabbable () {
	if (hasRequiredTabbable) return tabbable.exports;
	hasRequiredTabbable = 1;
	(function (module, exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		exports.default = findTabbableDescendants;
		/*!
		 * Adapted from jQuery UI core
		 *
		 * http://jqueryui.com
		 *
		 * Copyright 2014 jQuery Foundation and other contributors
		 * Released under the MIT license.
		 * http://jquery.org/license
		 *
		 * http://api.jqueryui.com/category/ui-core/
		 */

		var DISPLAY_NONE = "none";
		var DISPLAY_CONTENTS = "contents";

		// match the whole word to prevent fuzzy searching
		var tabbableNode = /^(input|select|textarea|button|object|iframe)$/;

		function isNotOverflowing(element, style) {
		  return style.getPropertyValue("overflow") !== "visible" ||
		  // if 'overflow: visible' set, check if there is actually any overflow
		  element.scrollWidth <= 0 && element.scrollHeight <= 0;
		}

		function hidesContents(element) {
		  var zeroSize = element.offsetWidth <= 0 && element.offsetHeight <= 0;

		  // If the node is empty, this is good enough
		  if (zeroSize && !element.innerHTML) return true;

		  try {
		    // Otherwise we need to check some styles
		    var style = window.getComputedStyle(element);
		    var displayValue = style.getPropertyValue("display");
		    return zeroSize ? displayValue !== DISPLAY_CONTENTS && isNotOverflowing(element, style) : displayValue === DISPLAY_NONE;
		  } catch (exception) {
		    // eslint-disable-next-line no-console
		    console.warn("Failed to inspect element style");
		    return false;
		  }
		}

		function visible(element) {
		  var parentElement = element;
		  var rootNode = element.getRootNode && element.getRootNode();
		  while (parentElement) {
		    if (parentElement === document.body) break;

		    // if we are not hidden yet, skip to checking outside the Web Component
		    if (rootNode && parentElement === rootNode) parentElement = rootNode.host.parentNode;

		    if (hidesContents(parentElement)) return false;
		    parentElement = parentElement.parentNode;
		  }
		  return true;
		}

		function focusable(element, isTabIndexNotNaN) {
		  var nodeName = element.nodeName.toLowerCase();
		  var res = tabbableNode.test(nodeName) && !element.disabled || (nodeName === "a" ? element.href || isTabIndexNotNaN : isTabIndexNotNaN);
		  return res && visible(element);
		}

		function tabbable(element) {
		  var tabIndex = element.getAttribute("tabindex");
		  if (tabIndex === null) tabIndex = undefined;
		  var isTabIndexNaN = isNaN(tabIndex);
		  return (isTabIndexNaN || tabIndex >= 0) && focusable(element, !isTabIndexNaN);
		}

		function findTabbableDescendants(element) {
		  var descendants = [].slice.call(element.querySelectorAll("*"), 0).reduce(function (finished, el) {
		    return finished.concat(!el.shadowRoot ? [el] : findTabbableDescendants(el.shadowRoot));
		  }, []);
		  return descendants.filter(tabbable);
		}
		module.exports = exports["default"]; 
	} (tabbable, tabbable.exports));
	return tabbable.exports;
}

var hasRequiredFocusManager;

function requireFocusManager () {
	if (hasRequiredFocusManager) return focusManager;
	hasRequiredFocusManager = 1;

	Object.defineProperty(focusManager, "__esModule", {
	  value: true
	});
	focusManager.resetState = resetState;
	focusManager.log = log;
	focusManager.handleBlur = handleBlur;
	focusManager.handleFocus = handleFocus;
	focusManager.markForFocusLater = markForFocusLater;
	focusManager.returnFocus = returnFocus;
	focusManager.popWithoutFocus = popWithoutFocus;
	focusManager.setupScopedFocus = setupScopedFocus;
	focusManager.teardownScopedFocus = teardownScopedFocus;

	var _tabbable = requireTabbable();

	var _tabbable2 = _interopRequireDefault(_tabbable);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	var focusLaterElements = [];
	var modalElement = null;
	var needToFocus = false;

	/* eslint-disable no-console */
	/* istanbul ignore next */
	function resetState() {
	  focusLaterElements = [];
	}

	/* istanbul ignore next */
	function log() {
	  if (browser$1.env.NODE_ENV !== "production") {
	    console.log("focusManager ----------");
	    focusLaterElements.forEach(function (f) {
	      var check = f || {};
	      console.log(check.nodeName, check.className, check.id);
	    });
	    console.log("end focusManager ----------");
	  }
	}
	/* eslint-enable no-console */

	function handleBlur() {
	  needToFocus = true;
	}

	function handleFocus() {
	  if (needToFocus) {
	    needToFocus = false;
	    if (!modalElement) {
	      return;
	    }
	    // need to see how jQuery shims document.on('focusin') so we don't need the
	    // setTimeout, firefox doesn't support focusin, if it did, we could focus
	    // the element outside of a setTimeout. Side-effect of this implementation
	    // is that the document.body gets focus, and then we focus our element right
	    // after, seems fine.
	    setTimeout(function () {
	      if (modalElement.contains(document.activeElement)) {
	        return;
	      }
	      var el = (0, _tabbable2.default)(modalElement)[0] || modalElement;
	      el.focus();
	    }, 0);
	  }
	}

	function markForFocusLater() {
	  focusLaterElements.push(document.activeElement);
	}

	/* eslint-disable no-console */
	function returnFocus() {
	  var preventScroll = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

	  var toFocus = null;
	  try {
	    if (focusLaterElements.length !== 0) {
	      toFocus = focusLaterElements.pop();
	      toFocus.focus({ preventScroll: preventScroll });
	    }
	    return;
	  } catch (e) {
	    console.warn(["You tried to return focus to", toFocus, "but it is not in the DOM anymore"].join(" "));
	  }
	}
	/* eslint-enable no-console */

	function popWithoutFocus() {
	  focusLaterElements.length > 0 && focusLaterElements.pop();
	}

	function setupScopedFocus(element) {
	  modalElement = element;

	  if (window.addEventListener) {
	    window.addEventListener("blur", handleBlur, false);
	    document.addEventListener("focus", handleFocus, true);
	  } else {
	    window.attachEvent("onBlur", handleBlur);
	    document.attachEvent("onFocus", handleFocus);
	  }
	}

	function teardownScopedFocus() {
	  modalElement = null;

	  if (window.addEventListener) {
	    window.removeEventListener("blur", handleBlur);
	    document.removeEventListener("focus", handleFocus);
	  } else {
	    window.detachEvent("onBlur", handleBlur);
	    document.detachEvent("onFocus", handleFocus);
	  }
	}
	return focusManager;
}

var scopeTab = {exports: {}};

var hasRequiredScopeTab;

function requireScopeTab () {
	if (hasRequiredScopeTab) return scopeTab.exports;
	hasRequiredScopeTab = 1;
	(function (module, exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		exports.default = scopeTab;

		var _tabbable = requireTabbable();

		var _tabbable2 = _interopRequireDefault(_tabbable);

		function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

		function getActiveElement() {
		  var el = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;

		  return el.activeElement.shadowRoot ? getActiveElement(el.activeElement.shadowRoot) : el.activeElement;
		}

		function scopeTab(node, event) {
		  var tabbable = (0, _tabbable2.default)(node);

		  if (!tabbable.length) {
		    // Do nothing, since there are no elements that can receive focus.
		    event.preventDefault();
		    return;
		  }

		  var target = void 0;

		  var shiftKey = event.shiftKey;
		  var head = tabbable[0];
		  var tail = tabbable[tabbable.length - 1];
		  var activeElement = getActiveElement();

		  // proceed with default browser behavior on tab.
		  // Focus on last element on shift + tab.
		  if (node === activeElement) {
		    if (!shiftKey) return;
		    target = tail;
		  }

		  if (tail === activeElement && !shiftKey) {
		    target = head;
		  }

		  if (head === activeElement && shiftKey) {
		    target = tail;
		  }

		  if (target) {
		    event.preventDefault();
		    target.focus();
		    return;
		  }

		  // Safari radio issue.
		  //
		  // Safari does not move the focus to the radio button,
		  // so we need to force it to really walk through all elements.
		  //
		  // This is very error prone, since we are trying to guess
		  // if it is a safari browser from the first occurence between
		  // chrome or safari.
		  //
		  // The chrome user agent contains the first ocurrence
		  // as the 'chrome/version' and later the 'safari/version'.
		  var checkSafari = /(\bChrome\b|\bSafari\b)\//.exec(navigator.userAgent);
		  var isSafariDesktop = checkSafari != null && checkSafari[1] != "Chrome" && /\biPod\b|\biPad\b/g.exec(navigator.userAgent) == null;

		  // If we are not in safari desktop, let the browser control
		  // the focus
		  if (!isSafariDesktop) return;

		  var x = tabbable.indexOf(activeElement);

		  if (x > -1) {
		    x += shiftKey ? -1 : 1;
		  }

		  target = tabbable[x];

		  // If the tabbable element does not exist,
		  // focus head/tail based on shiftKey
		  if (typeof target === "undefined") {
		    event.preventDefault();
		    target = shiftKey ? tail : head;
		    target.focus();
		    return;
		  }

		  event.preventDefault();

		  target.focus();
		}
		module.exports = exports["default"]; 
	} (scopeTab, scopeTab.exports));
	return scopeTab.exports;
}

var ariaAppHider = {};

var warning_1;
var hasRequiredWarning;

function requireWarning () {
	if (hasRequiredWarning) return warning_1;
	hasRequiredWarning = 1;

	/**
	 * Similar to invariant but only logs a warning if the condition is not met.
	 * This can be used to log issues in development environments in critical
	 * paths. Removing the logging code for production environments will keep the
	 * same logic and follow the same code paths.
	 */

	var __DEV__ = browser$1.env.NODE_ENV !== 'production';

	var warning = function() {};

	if (__DEV__) {
	  var printWarning = function printWarning(format, args) {
	    var len = arguments.length;
	    args = new Array(len > 1 ? len - 1 : 0);
	    for (var key = 1; key < len; key++) {
	      args[key - 1] = arguments[key];
	    }
	    var argIndex = 0;
	    var message = 'Warning: ' +
	      format.replace(/%s/g, function() {
	        return args[argIndex++];
	      });
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) {}
	  };

	  warning = function(condition, format, args) {
	    var len = arguments.length;
	    args = new Array(len > 2 ? len - 2 : 0);
	    for (var key = 2; key < len; key++) {
	      args[key - 2] = arguments[key];
	    }
	    if (format === undefined) {
	      throw new Error(
	          '`warning(condition, format, ...args)` requires a warning ' +
	          'message argument'
	      );
	    }
	    if (!condition) {
	      printWarning.apply(null, [format].concat(args));
	    }
	  };
	}

	warning_1 = warning;
	return warning_1;
}

var safeHTMLElement = {};

var exenv = {exports: {}};

/*!
  Copyright (c) 2015 Jed Watson.
  Based on code that is Copyright 2013-2015, Facebook, Inc.
  All rights reserved.
*/

var hasRequiredExenv;

function requireExenv () {
	if (hasRequiredExenv) return exenv.exports;
	hasRequiredExenv = 1;
	(function (module) {
		/* global define */

		(function () {

			var canUseDOM = !!(
				typeof window !== 'undefined' &&
				window.document &&
				window.document.createElement
			);

			var ExecutionEnvironment = {

				canUseDOM: canUseDOM,

				canUseWorkers: typeof Worker !== 'undefined',

				canUseEventListeners:
					canUseDOM && !!(window.addEventListener || window.attachEvent),

				canUseViewport: canUseDOM && !!window.screen

			};

			if (module.exports) {
				module.exports = ExecutionEnvironment;
			} else {
				window.ExecutionEnvironment = ExecutionEnvironment;
			}

		}()); 
	} (exenv));
	return exenv.exports;
}

var hasRequiredSafeHTMLElement;

function requireSafeHTMLElement () {
	if (hasRequiredSafeHTMLElement) return safeHTMLElement;
	hasRequiredSafeHTMLElement = 1;

	Object.defineProperty(safeHTMLElement, "__esModule", {
	  value: true
	});
	safeHTMLElement.canUseDOM = safeHTMLElement.SafeNodeList = safeHTMLElement.SafeHTMLCollection = undefined;

	var _exenv = requireExenv();

	var _exenv2 = _interopRequireDefault(_exenv);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	var EE = _exenv2.default;

	var SafeHTMLElement = EE.canUseDOM ? window.HTMLElement : {};

	safeHTMLElement.SafeHTMLCollection = EE.canUseDOM ? window.HTMLCollection : {};

	safeHTMLElement.SafeNodeList = EE.canUseDOM ? window.NodeList : {};

	safeHTMLElement.canUseDOM = EE.canUseDOM;

	safeHTMLElement.default = SafeHTMLElement;
	return safeHTMLElement;
}

var hasRequiredAriaAppHider;

function requireAriaAppHider () {
	if (hasRequiredAriaAppHider) return ariaAppHider;
	hasRequiredAriaAppHider = 1;

	Object.defineProperty(ariaAppHider, "__esModule", {
	  value: true
	});
	ariaAppHider.resetState = resetState;
	ariaAppHider.log = log;
	ariaAppHider.assertNodeList = assertNodeList;
	ariaAppHider.setElement = setElement;
	ariaAppHider.validateElement = validateElement;
	ariaAppHider.hide = hide;
	ariaAppHider.show = show;
	ariaAppHider.documentNotReadyOrSSRTesting = documentNotReadyOrSSRTesting;

	var _warning = requireWarning();

	var _warning2 = _interopRequireDefault(_warning);

	var _safeHTMLElement = requireSafeHTMLElement();

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	var globalElement = null;

	/* eslint-disable no-console */
	/* istanbul ignore next */
	function resetState() {
	  if (globalElement) {
	    if (globalElement.removeAttribute) {
	      globalElement.removeAttribute("aria-hidden");
	    } else if (globalElement.length != null) {
	      globalElement.forEach(function (element) {
	        return element.removeAttribute("aria-hidden");
	      });
	    } else {
	      document.querySelectorAll(globalElement).forEach(function (element) {
	        return element.removeAttribute("aria-hidden");
	      });
	    }
	  }
	  globalElement = null;
	}

	/* istanbul ignore next */
	function log() {
	  if (browser$1.env.NODE_ENV !== "production") {
	    var check = globalElement || {};
	    console.log("ariaAppHider ----------");
	    console.log(check.nodeName, check.className, check.id);
	    console.log("end ariaAppHider ----------");
	  }
	}
	/* eslint-enable no-console */

	function assertNodeList(nodeList, selector) {
	  if (!nodeList || !nodeList.length) {
	    throw new Error("react-modal: No elements were found for selector " + selector + ".");
	  }
	}

	function setElement(element) {
	  var useElement = element;
	  if (typeof useElement === "string" && _safeHTMLElement.canUseDOM) {
	    var el = document.querySelectorAll(useElement);
	    assertNodeList(el, useElement);
	    useElement = el;
	  }
	  globalElement = useElement || globalElement;
	  return globalElement;
	}

	function validateElement(appElement) {
	  var el = appElement || globalElement;
	  if (el) {
	    return Array.isArray(el) || el instanceof HTMLCollection || el instanceof NodeList ? el : [el];
	  } else {
	    (0, _warning2.default)(false, ["react-modal: App element is not defined.", "Please use `Modal.setAppElement(el)` or set `appElement={el}`.", "This is needed so screen readers don't see main content", "when modal is opened. It is not recommended, but you can opt-out", "by setting `ariaHideApp={false}`."].join(" "));

	    return [];
	  }
	}

	function hide(appElement) {
	  var _iteratorNormalCompletion = true;
	  var _didIteratorError = false;
	  var _iteratorError = undefined;

	  try {
	    for (var _iterator = validateElement(appElement)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
	      var el = _step.value;

	      el.setAttribute("aria-hidden", "true");
	    }
	  } catch (err) {
	    _didIteratorError = true;
	    _iteratorError = err;
	  } finally {
	    try {
	      if (!_iteratorNormalCompletion && _iterator.return) {
	        _iterator.return();
	      }
	    } finally {
	      if (_didIteratorError) {
	        throw _iteratorError;
	      }
	    }
	  }
	}

	function show(appElement) {
	  var _iteratorNormalCompletion2 = true;
	  var _didIteratorError2 = false;
	  var _iteratorError2 = undefined;

	  try {
	    for (var _iterator2 = validateElement(appElement)[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
	      var el = _step2.value;

	      el.removeAttribute("aria-hidden");
	    }
	  } catch (err) {
	    _didIteratorError2 = true;
	    _iteratorError2 = err;
	  } finally {
	    try {
	      if (!_iteratorNormalCompletion2 && _iterator2.return) {
	        _iterator2.return();
	      }
	    } finally {
	      if (_didIteratorError2) {
	        throw _iteratorError2;
	      }
	    }
	  }
	}

	function documentNotReadyOrSSRTesting() {
	  globalElement = null;
	}
	return ariaAppHider;
}

var classList = {};

var hasRequiredClassList;

function requireClassList () {
	if (hasRequiredClassList) return classList;
	hasRequiredClassList = 1;

	Object.defineProperty(classList, "__esModule", {
	  value: true
	});
	classList.resetState = resetState;
	classList.log = log;
	var htmlClassList = {};
	var docBodyClassList = {};

	/* eslint-disable no-console */
	/* istanbul ignore next */
	function removeClass(at, cls) {
	  at.classList.remove(cls);
	}

	/* istanbul ignore next */
	function resetState() {
	  var htmlElement = document.getElementsByTagName("html")[0];
	  for (var cls in htmlClassList) {
	    removeClass(htmlElement, htmlClassList[cls]);
	  }

	  var body = document.body;
	  for (var _cls in docBodyClassList) {
	    removeClass(body, docBodyClassList[_cls]);
	  }

	  htmlClassList = {};
	  docBodyClassList = {};
	}

	/* istanbul ignore next */
	function log() {
	  if (browser$1.env.NODE_ENV !== "production") {
	    var classes = document.getElementsByTagName("html")[0].className;
	    var buffer = "Show tracked classes:\n\n";

	    buffer += "<html /> (" + classes + "):\n  ";
	    for (var x in htmlClassList) {
	      buffer += "  " + x + " " + htmlClassList[x] + "\n  ";
	    }

	    classes = document.body.className;

	    buffer += "\n\ndoc.body (" + classes + "):\n  ";
	    for (var _x in docBodyClassList) {
	      buffer += "  " + _x + " " + docBodyClassList[_x] + "\n  ";
	    }

	    buffer += "\n";

	    console.log(buffer);
	  }
	}
	/* eslint-enable no-console */

	/**
	 * Track the number of reference of a class.
	 * @param {object} poll The poll to receive the reference.
	 * @param {string} className The class name.
	 * @return {string}
	 */
	var incrementReference = function incrementReference(poll, className) {
	  if (!poll[className]) {
	    poll[className] = 0;
	  }
	  poll[className] += 1;
	  return className;
	};

	/**
	 * Drop the reference of a class.
	 * @param {object} poll The poll to receive the reference.
	 * @param {string} className The class name.
	 * @return {string}
	 */
	var decrementReference = function decrementReference(poll, className) {
	  if (poll[className]) {
	    poll[className] -= 1;
	  }
	  return className;
	};

	/**
	 * Track a class and add to the given class list.
	 * @param {Object} classListRef A class list of an element.
	 * @param {Object} poll         The poll to be used.
	 * @param {Array}  classes      The list of classes to be tracked.
	 */
	var trackClass = function trackClass(classListRef, poll, classes) {
	  classes.forEach(function (className) {
	    incrementReference(poll, className);
	    classListRef.add(className);
	  });
	};

	/**
	 * Untrack a class and remove from the given class list if the reference
	 * reaches 0.
	 * @param {Object} classListRef A class list of an element.
	 * @param {Object} poll         The poll to be used.
	 * @param {Array}  classes      The list of classes to be untracked.
	 */
	var untrackClass = function untrackClass(classListRef, poll, classes) {
	  classes.forEach(function (className) {
	    decrementReference(poll, className);
	    poll[className] === 0 && classListRef.remove(className);
	  });
	};

	/**
	 * Public inferface to add classes to the document.body.
	 * @param {string} bodyClass The class string to be added.
	 *                           It may contain more then one class
	 *                           with ' ' as separator.
	 */
	classList.add = function add(element, classString) {
	  return trackClass(element.classList, element.nodeName.toLowerCase() == "html" ? htmlClassList : docBodyClassList, classString.split(" "));
	};

	/**
	 * Public inferface to remove classes from the document.body.
	 * @param {string} bodyClass The class string to be added.
	 *                           It may contain more then one class
	 *                           with ' ' as separator.
	 */
	classList.remove = function remove(element, classString) {
	  return untrackClass(element.classList, element.nodeName.toLowerCase() == "html" ? htmlClassList : docBodyClassList, classString.split(" "));
	};
	return classList;
}

var portalOpenInstances = {};

var hasRequiredPortalOpenInstances;

function requirePortalOpenInstances () {
	if (hasRequiredPortalOpenInstances) return portalOpenInstances;
	hasRequiredPortalOpenInstances = 1;

	Object.defineProperty(portalOpenInstances, "__esModule", {
	  value: true
	});
	portalOpenInstances.log = log;
	portalOpenInstances.resetState = resetState;

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	// Tracks portals that are open and emits events to subscribers

	var PortalOpenInstances = function PortalOpenInstances() {
	  var _this = this;

	  _classCallCheck(this, PortalOpenInstances);

	  this.register = function (openInstance) {
	    if (_this.openInstances.indexOf(openInstance) !== -1) {
	      if (browser$1.env.NODE_ENV !== "production") {
	        // eslint-disable-next-line no-console
	        console.warn("React-Modal: Cannot register modal instance that's already open");
	      }
	      return;
	    }
	    _this.openInstances.push(openInstance);
	    _this.emit("register");
	  };

	  this.deregister = function (openInstance) {
	    var index = _this.openInstances.indexOf(openInstance);
	    if (index === -1) {
	      if (browser$1.env.NODE_ENV !== "production") {
	        // eslint-disable-next-line no-console
	        console.warn("React-Modal: Unable to deregister " + openInstance + " as " + "it was never registered");
	      }
	      return;
	    }
	    _this.openInstances.splice(index, 1);
	    _this.emit("deregister");
	  };

	  this.subscribe = function (callback) {
	    _this.subscribers.push(callback);
	  };

	  this.emit = function (eventType) {
	    _this.subscribers.forEach(function (subscriber) {
	      return subscriber(eventType,
	      // shallow copy to avoid accidental mutation
	      _this.openInstances.slice());
	    });
	  };

	  this.openInstances = [];
	  this.subscribers = [];
	};

	var portalOpenInstances$1 = new PortalOpenInstances();

	/* eslint-disable no-console */
	/* istanbul ignore next */
	function log() {
	  console.log("portalOpenInstances ----------");
	  console.log(portalOpenInstances$1.openInstances.length);
	  portalOpenInstances$1.openInstances.forEach(function (p) {
	    return console.log(p);
	  });
	  console.log("end portalOpenInstances ----------");
	}

	/* istanbul ignore next */
	function resetState() {
	  portalOpenInstances$1 = new PortalOpenInstances();
	}
	/* eslint-enable no-console */

	portalOpenInstances.default = portalOpenInstances$1;
	return portalOpenInstances;
}

var bodyTrap = {};

var hasRequiredBodyTrap;

function requireBodyTrap () {
	if (hasRequiredBodyTrap) return bodyTrap;
	hasRequiredBodyTrap = 1;

	Object.defineProperty(bodyTrap, "__esModule", {
	  value: true
	});
	bodyTrap.resetState = resetState;
	bodyTrap.log = log;

	var _portalOpenInstances = requirePortalOpenInstances();

	var _portalOpenInstances2 = _interopRequireDefault(_portalOpenInstances);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	// Body focus trap see Issue #742

	var before = void 0,
	    after = void 0,
	    instances = [];

	/* eslint-disable no-console */
	/* istanbul ignore next */
	function resetState() {
	  var _arr = [before, after];

	  for (var _i = 0; _i < _arr.length; _i++) {
	    var item = _arr[_i];
	    if (!item) continue;
	    item.parentNode && item.parentNode.removeChild(item);
	  }
	  before = after = null;
	  instances = [];
	}

	/* istanbul ignore next */
	function log() {
	  console.log("bodyTrap ----------");
	  console.log(instances.length);
	  var _arr2 = [before, after];
	  for (var _i2 = 0; _i2 < _arr2.length; _i2++) {
	    var item = _arr2[_i2];
	    var check = item || {};
	    console.log(check.nodeName, check.className, check.id);
	  }
	  console.log("edn bodyTrap ----------");
	}
	/* eslint-enable no-console */

	function focusContent() {
	  if (instances.length === 0) {
	    if (browser$1.env.NODE_ENV !== "production") {
	      // eslint-disable-next-line no-console
	      console.warn("React-Modal: Open instances > 0 expected");
	    }
	    return;
	  }
	  instances[instances.length - 1].focusContent();
	}

	function bodyTrap$1(eventType, openInstances) {
	  if (!before && !after) {
	    before = document.createElement("div");
	    before.setAttribute("data-react-modal-body-trap", "");
	    before.style.position = "absolute";
	    before.style.opacity = "0";
	    before.setAttribute("tabindex", "0");
	    before.addEventListener("focus", focusContent);
	    after = before.cloneNode();
	    after.addEventListener("focus", focusContent);
	  }

	  instances = openInstances;

	  if (instances.length > 0) {
	    // Add focus trap
	    if (document.body.firstChild !== before) {
	      document.body.insertBefore(before, document.body.firstChild);
	    }
	    if (document.body.lastChild !== after) {
	      document.body.appendChild(after);
	    }
	  } else {
	    // Remove focus trap
	    if (before.parentElement) {
	      before.parentElement.removeChild(before);
	    }
	    if (after.parentElement) {
	      after.parentElement.removeChild(after);
	    }
	  }
	}

	_portalOpenInstances2.default.subscribe(bodyTrap$1);
	return bodyTrap;
}

var hasRequiredModalPortal;

function requireModalPortal () {
	if (hasRequiredModalPortal) return ModalPortal.exports;
	hasRequiredModalPortal = 1;
	(function (module, exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});

		var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

		var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

		var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

		var _react = React__default;

		var _propTypes = /*@__PURE__*/ requirePropTypes();

		var _propTypes2 = _interopRequireDefault(_propTypes);

		var _focusManager = requireFocusManager();

		var focusManager = _interopRequireWildcard(_focusManager);

		var _scopeTab = requireScopeTab();

		var _scopeTab2 = _interopRequireDefault(_scopeTab);

		var _ariaAppHider = requireAriaAppHider();

		var ariaAppHider = _interopRequireWildcard(_ariaAppHider);

		var _classList = requireClassList();

		var classList = _interopRequireWildcard(_classList);

		var _safeHTMLElement = requireSafeHTMLElement();

		var _safeHTMLElement2 = _interopRequireDefault(_safeHTMLElement);

		var _portalOpenInstances = requirePortalOpenInstances();

		var _portalOpenInstances2 = _interopRequireDefault(_portalOpenInstances);

		requireBodyTrap();

		function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj.default = obj; return newObj; } }

		function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

		function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

		function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

		function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

		// so that our CSS is statically analyzable
		var CLASS_NAMES = {
		  overlay: "ReactModal__Overlay",
		  content: "ReactModal__Content"
		};

		/**
		 * We need to support the deprecated `KeyboardEvent.keyCode` in addition to
		 * `KeyboardEvent.code` for apps that still support IE11. Can be removed when
		 * `react-modal` only supports React >18 (which dropped IE support).
		 */
		var isTabKey = function isTabKey(event) {
		  return event.code === "Tab" || event.keyCode === 9;
		};
		var isEscKey = function isEscKey(event) {
		  return event.code === "Escape" || event.keyCode === 27;
		};

		var ariaHiddenInstances = 0;

		var ModalPortal = function (_Component) {
		  _inherits(ModalPortal, _Component);

		  function ModalPortal(props) {
		    _classCallCheck(this, ModalPortal);

		    var _this = _possibleConstructorReturn(this, (ModalPortal.__proto__ || Object.getPrototypeOf(ModalPortal)).call(this, props));

		    _this.setOverlayRef = function (overlay) {
		      _this.overlay = overlay;
		      _this.props.overlayRef && _this.props.overlayRef(overlay);
		    };

		    _this.setContentRef = function (content) {
		      _this.content = content;
		      _this.props.contentRef && _this.props.contentRef(content);
		    };

		    _this.afterClose = function () {
		      var _this$props = _this.props,
		          appElement = _this$props.appElement,
		          ariaHideApp = _this$props.ariaHideApp,
		          htmlOpenClassName = _this$props.htmlOpenClassName,
		          bodyOpenClassName = _this$props.bodyOpenClassName,
		          parentSelector = _this$props.parentSelector;


		      var parentDocument = parentSelector && parentSelector().ownerDocument || document;

		      // Remove classes.
		      bodyOpenClassName && classList.remove(parentDocument.body, bodyOpenClassName);

		      htmlOpenClassName && classList.remove(parentDocument.getElementsByTagName("html")[0], htmlOpenClassName);

		      // Reset aria-hidden attribute if all modals have been removed
		      if (ariaHideApp && ariaHiddenInstances > 0) {
		        ariaHiddenInstances -= 1;

		        if (ariaHiddenInstances === 0) {
		          ariaAppHider.show(appElement);
		        }
		      }

		      if (_this.props.shouldFocusAfterRender) {
		        if (_this.props.shouldReturnFocusAfterClose) {
		          focusManager.returnFocus(_this.props.preventScroll);
		          focusManager.teardownScopedFocus();
		        } else {
		          focusManager.popWithoutFocus();
		        }
		      }

		      if (_this.props.onAfterClose) {
		        _this.props.onAfterClose();
		      }

		      _portalOpenInstances2.default.deregister(_this);
		    };

		    _this.open = function () {
		      _this.beforeOpen();
		      if (_this.state.afterOpen && _this.state.beforeClose) {
		        clearTimeout(_this.closeTimer);
		        _this.setState({ beforeClose: false });
		      } else {
		        if (_this.props.shouldFocusAfterRender) {
		          focusManager.setupScopedFocus(_this.node);
		          focusManager.markForFocusLater();
		        }

		        _this.setState({ isOpen: true }, function () {
		          _this.openAnimationFrame = requestAnimationFrame(function () {
		            _this.setState({ afterOpen: true });

		            if (_this.props.isOpen && _this.props.onAfterOpen) {
		              _this.props.onAfterOpen({
		                overlayEl: _this.overlay,
		                contentEl: _this.content
		              });
		            }
		          });
		        });
		      }
		    };

		    _this.close = function () {
		      if (_this.props.closeTimeoutMS > 0) {
		        _this.closeWithTimeout();
		      } else {
		        _this.closeWithoutTimeout();
		      }
		    };

		    _this.focusContent = function () {
		      return _this.content && !_this.contentHasFocus() && _this.content.focus({ preventScroll: true });
		    };

		    _this.closeWithTimeout = function () {
		      var closesAt = Date.now() + _this.props.closeTimeoutMS;
		      _this.setState({ beforeClose: true, closesAt: closesAt }, function () {
		        _this.closeTimer = setTimeout(_this.closeWithoutTimeout, _this.state.closesAt - Date.now());
		      });
		    };

		    _this.closeWithoutTimeout = function () {
		      _this.setState({
		        beforeClose: false,
		        isOpen: false,
		        afterOpen: false,
		        closesAt: null
		      }, _this.afterClose);
		    };

		    _this.handleKeyDown = function (event) {
		      if (isTabKey(event)) {
		        (0, _scopeTab2.default)(_this.content, event);
		      }

		      if (_this.props.shouldCloseOnEsc && isEscKey(event)) {
		        event.stopPropagation();
		        _this.requestClose(event);
		      }
		    };

		    _this.handleOverlayOnClick = function (event) {
		      if (_this.shouldClose === null) {
		        _this.shouldClose = true;
		      }

		      if (_this.shouldClose && _this.props.shouldCloseOnOverlayClick) {
		        if (_this.ownerHandlesClose()) {
		          _this.requestClose(event);
		        } else {
		          _this.focusContent();
		        }
		      }
		      _this.shouldClose = null;
		    };

		    _this.handleContentOnMouseUp = function () {
		      _this.shouldClose = false;
		    };

		    _this.handleOverlayOnMouseDown = function (event) {
		      if (!_this.props.shouldCloseOnOverlayClick && event.target == _this.overlay) {
		        event.preventDefault();
		      }
		    };

		    _this.handleContentOnClick = function () {
		      _this.shouldClose = false;
		    };

		    _this.handleContentOnMouseDown = function () {
		      _this.shouldClose = false;
		    };

		    _this.requestClose = function (event) {
		      return _this.ownerHandlesClose() && _this.props.onRequestClose(event);
		    };

		    _this.ownerHandlesClose = function () {
		      return _this.props.onRequestClose;
		    };

		    _this.shouldBeClosed = function () {
		      return !_this.state.isOpen && !_this.state.beforeClose;
		    };

		    _this.contentHasFocus = function () {
		      return document.activeElement === _this.content || _this.content.contains(document.activeElement);
		    };

		    _this.buildClassName = function (which, additional) {
		      var classNames = (typeof additional === "undefined" ? "undefined" : _typeof(additional)) === "object" ? additional : {
		        base: CLASS_NAMES[which],
		        afterOpen: CLASS_NAMES[which] + "--after-open",
		        beforeClose: CLASS_NAMES[which] + "--before-close"
		      };
		      var className = classNames.base;
		      if (_this.state.afterOpen) {
		        className = className + " " + classNames.afterOpen;
		      }
		      if (_this.state.beforeClose) {
		        className = className + " " + classNames.beforeClose;
		      }
		      return typeof additional === "string" && additional ? className + " " + additional : className;
		    };

		    _this.attributesFromObject = function (prefix, items) {
		      return Object.keys(items).reduce(function (acc, name) {
		        acc[prefix + "-" + name] = items[name];
		        return acc;
		      }, {});
		    };

		    _this.state = {
		      afterOpen: false,
		      beforeClose: false
		    };

		    _this.shouldClose = null;
		    _this.moveFromContentToOverlay = null;
		    return _this;
		  }

		  _createClass(ModalPortal, [{
		    key: "componentDidMount",
		    value: function componentDidMount() {
		      if (this.props.isOpen) {
		        this.open();
		      }
		    }
		  }, {
		    key: "componentDidUpdate",
		    value: function componentDidUpdate(prevProps, prevState) {
		      if (browser$1.env.NODE_ENV !== "production") {
		        if (prevProps.bodyOpenClassName !== this.props.bodyOpenClassName) {
		          // eslint-disable-next-line no-console
		          console.warn('React-Modal: "bodyOpenClassName" prop has been modified. ' + "This may cause unexpected behavior when multiple modals are open.");
		        }
		        if (prevProps.htmlOpenClassName !== this.props.htmlOpenClassName) {
		          // eslint-disable-next-line no-console
		          console.warn('React-Modal: "htmlOpenClassName" prop has been modified. ' + "This may cause unexpected behavior when multiple modals are open.");
		        }
		      }

		      if (this.props.isOpen && !prevProps.isOpen) {
		        this.open();
		      } else if (!this.props.isOpen && prevProps.isOpen) {
		        this.close();
		      }

		      // Focus only needs to be set once when the modal is being opened
		      if (this.props.shouldFocusAfterRender && this.state.isOpen && !prevState.isOpen) {
		        this.focusContent();
		      }
		    }
		  }, {
		    key: "componentWillUnmount",
		    value: function componentWillUnmount() {
		      if (this.state.isOpen) {
		        this.afterClose();
		      }
		      clearTimeout(this.closeTimer);
		      cancelAnimationFrame(this.openAnimationFrame);
		    }
		  }, {
		    key: "beforeOpen",
		    value: function beforeOpen() {
		      var _props = this.props,
		          appElement = _props.appElement,
		          ariaHideApp = _props.ariaHideApp,
		          htmlOpenClassName = _props.htmlOpenClassName,
		          bodyOpenClassName = _props.bodyOpenClassName,
		          parentSelector = _props.parentSelector;


		      var parentDocument = parentSelector && parentSelector().ownerDocument || document;

		      // Add classes.
		      bodyOpenClassName && classList.add(parentDocument.body, bodyOpenClassName);

		      htmlOpenClassName && classList.add(parentDocument.getElementsByTagName("html")[0], htmlOpenClassName);

		      if (ariaHideApp) {
		        ariaHiddenInstances += 1;
		        ariaAppHider.hide(appElement);
		      }

		      _portalOpenInstances2.default.register(this);
		    }

		    // Don't steal focus from inner elements

		  }, {
		    key: "render",
		    value: function render() {
		      var _props2 = this.props,
		          id = _props2.id,
		          className = _props2.className,
		          overlayClassName = _props2.overlayClassName,
		          defaultStyles = _props2.defaultStyles,
		          children = _props2.children;

		      var contentStyles = className ? {} : defaultStyles.content;
		      var overlayStyles = overlayClassName ? {} : defaultStyles.overlay;

		      if (this.shouldBeClosed()) {
		        return null;
		      }

		      var overlayProps = {
		        ref: this.setOverlayRef,
		        className: this.buildClassName("overlay", overlayClassName),
		        style: _extends({}, overlayStyles, this.props.style.overlay),
		        onClick: this.handleOverlayOnClick,
		        onMouseDown: this.handleOverlayOnMouseDown
		      };

		      var contentProps = _extends({
		        id: id,
		        ref: this.setContentRef,
		        style: _extends({}, contentStyles, this.props.style.content),
		        className: this.buildClassName("content", className),
		        tabIndex: "-1",
		        onKeyDown: this.handleKeyDown,
		        onMouseDown: this.handleContentOnMouseDown,
		        onMouseUp: this.handleContentOnMouseUp,
		        onClick: this.handleContentOnClick,
		        role: this.props.role,
		        "aria-label": this.props.contentLabel
		      }, this.attributesFromObject("aria", _extends({ modal: true }, this.props.aria)), this.attributesFromObject("data", this.props.data || {}), {
		        "data-testid": this.props.testId
		      });

		      var contentElement = this.props.contentElement(contentProps, children);
		      return this.props.overlayElement(overlayProps, contentElement);
		    }
		  }]);

		  return ModalPortal;
		}(_react.Component);

		ModalPortal.defaultProps = {
		  style: {
		    overlay: {},
		    content: {}
		  },
		  defaultStyles: {}
		};
		ModalPortal.propTypes = {
		  isOpen: _propTypes2.default.bool.isRequired,
		  defaultStyles: _propTypes2.default.shape({
		    content: _propTypes2.default.object,
		    overlay: _propTypes2.default.object
		  }),
		  style: _propTypes2.default.shape({
		    content: _propTypes2.default.object,
		    overlay: _propTypes2.default.object
		  }),
		  className: _propTypes2.default.oneOfType([_propTypes2.default.string, _propTypes2.default.object]),
		  overlayClassName: _propTypes2.default.oneOfType([_propTypes2.default.string, _propTypes2.default.object]),
		  parentSelector: _propTypes2.default.func,
		  bodyOpenClassName: _propTypes2.default.string,
		  htmlOpenClassName: _propTypes2.default.string,
		  ariaHideApp: _propTypes2.default.bool,
		  appElement: _propTypes2.default.oneOfType([_propTypes2.default.instanceOf(_safeHTMLElement2.default), _propTypes2.default.instanceOf(_safeHTMLElement.SafeHTMLCollection), _propTypes2.default.instanceOf(_safeHTMLElement.SafeNodeList), _propTypes2.default.arrayOf(_propTypes2.default.instanceOf(_safeHTMLElement2.default))]),
		  onAfterOpen: _propTypes2.default.func,
		  onAfterClose: _propTypes2.default.func,
		  onRequestClose: _propTypes2.default.func,
		  closeTimeoutMS: _propTypes2.default.number,
		  shouldFocusAfterRender: _propTypes2.default.bool,
		  shouldCloseOnOverlayClick: _propTypes2.default.bool,
		  shouldReturnFocusAfterClose: _propTypes2.default.bool,
		  preventScroll: _propTypes2.default.bool,
		  role: _propTypes2.default.string,
		  contentLabel: _propTypes2.default.string,
		  aria: _propTypes2.default.object,
		  data: _propTypes2.default.object,
		  children: _propTypes2.default.node,
		  shouldCloseOnEsc: _propTypes2.default.bool,
		  overlayRef: _propTypes2.default.func,
		  contentRef: _propTypes2.default.func,
		  id: _propTypes2.default.string,
		  overlayElement: _propTypes2.default.func,
		  contentElement: _propTypes2.default.func,
		  testId: _propTypes2.default.string
		};
		exports.default = ModalPortal;
		module.exports = exports["default"]; 
	} (ModalPortal, ModalPortal.exports));
	return ModalPortal.exports;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

function componentWillMount() {
  // Call this.constructor.gDSFP to support sub-classes.
  var state = this.constructor.getDerivedStateFromProps(this.props, this.state);
  if (state !== null && state !== undefined) {
    this.setState(state);
  }
}

function componentWillReceiveProps(nextProps) {
  // Call this.constructor.gDSFP to support sub-classes.
  // Use the setState() updater to ensure state isn't stale in certain edge cases.
  function updater(prevState) {
    var state = this.constructor.getDerivedStateFromProps(nextProps, prevState);
    return state !== null && state !== undefined ? state : null;
  }
  // Binding "this" is important for shallow renderer support.
  this.setState(updater.bind(this));
}

function componentWillUpdate(nextProps, nextState) {
  try {
    var prevProps = this.props;
    var prevState = this.state;
    this.props = nextProps;
    this.state = nextState;
    this.__reactInternalSnapshotFlag = true;
    this.__reactInternalSnapshot = this.getSnapshotBeforeUpdate(
      prevProps,
      prevState
    );
  } finally {
    this.props = prevProps;
    this.state = prevState;
  }
}

// React may warn about cWM/cWRP/cWU methods being deprecated.
// Add a flag to suppress these warnings for this special case.
componentWillMount.__suppressDeprecationWarning = true;
componentWillReceiveProps.__suppressDeprecationWarning = true;
componentWillUpdate.__suppressDeprecationWarning = true;

function polyfill(Component) {
  var prototype = Component.prototype;

  if (!prototype || !prototype.isReactComponent) {
    throw new Error('Can only polyfill class components');
  }

  if (
    typeof Component.getDerivedStateFromProps !== 'function' &&
    typeof prototype.getSnapshotBeforeUpdate !== 'function'
  ) {
    return Component;
  }

  // If new component APIs are defined, "unsafe" lifecycles won't be called.
  // Error if any of these lifecycles are present,
  // Because they would work differently between older and newer (16.3+) versions of React.
  var foundWillMountName = null;
  var foundWillReceivePropsName = null;
  var foundWillUpdateName = null;
  if (typeof prototype.componentWillMount === 'function') {
    foundWillMountName = 'componentWillMount';
  } else if (typeof prototype.UNSAFE_componentWillMount === 'function') {
    foundWillMountName = 'UNSAFE_componentWillMount';
  }
  if (typeof prototype.componentWillReceiveProps === 'function') {
    foundWillReceivePropsName = 'componentWillReceiveProps';
  } else if (typeof prototype.UNSAFE_componentWillReceiveProps === 'function') {
    foundWillReceivePropsName = 'UNSAFE_componentWillReceiveProps';
  }
  if (typeof prototype.componentWillUpdate === 'function') {
    foundWillUpdateName = 'componentWillUpdate';
  } else if (typeof prototype.UNSAFE_componentWillUpdate === 'function') {
    foundWillUpdateName = 'UNSAFE_componentWillUpdate';
  }
  if (
    foundWillMountName !== null ||
    foundWillReceivePropsName !== null ||
    foundWillUpdateName !== null
  ) {
    var componentName = Component.displayName || Component.name;
    var newApiName =
      typeof Component.getDerivedStateFromProps === 'function'
        ? 'getDerivedStateFromProps()'
        : 'getSnapshotBeforeUpdate()';

    throw Error(
      'Unsafe legacy lifecycles will not be called for components using new component APIs.\n\n' +
        componentName +
        ' uses ' +
        newApiName +
        ' but also contains the following legacy lifecycles:' +
        (foundWillMountName !== null ? '\n  ' + foundWillMountName : '') +
        (foundWillReceivePropsName !== null
          ? '\n  ' + foundWillReceivePropsName
          : '') +
        (foundWillUpdateName !== null ? '\n  ' + foundWillUpdateName : '') +
        '\n\nThe above lifecycles should be removed. Learn more about this warning here:\n' +
        'https://fb.me/react-async-component-lifecycle-hooks'
    );
  }

  // React <= 16.2 does not support static getDerivedStateFromProps.
  // As a workaround, use cWM and cWRP to invoke the new static lifecycle.
  // Newer versions of React will ignore these lifecycles if gDSFP exists.
  if (typeof Component.getDerivedStateFromProps === 'function') {
    prototype.componentWillMount = componentWillMount;
    prototype.componentWillReceiveProps = componentWillReceiveProps;
  }

  // React <= 16.2 does not support getSnapshotBeforeUpdate.
  // As a workaround, use cWU to invoke the new lifecycle.
  // Newer versions of React will ignore that lifecycle if gSBU exists.
  if (typeof prototype.getSnapshotBeforeUpdate === 'function') {
    if (typeof prototype.componentDidUpdate !== 'function') {
      throw new Error(
        'Cannot polyfill getSnapshotBeforeUpdate() for components that do not define componentDidUpdate() on the prototype'
      );
    }

    prototype.componentWillUpdate = componentWillUpdate;

    var componentDidUpdate = prototype.componentDidUpdate;

    prototype.componentDidUpdate = function componentDidUpdatePolyfill(
      prevProps,
      prevState,
      maybeSnapshot
    ) {
      // 16.3+ will not execute our will-update method;
      // It will pass a snapshot value to did-update though.
      // Older versions will require our polyfilled will-update value.
      // We need to handle both cases, but can't just check for the presence of "maybeSnapshot",
      // Because for <= 15.x versions this might be a "prevContext" object.
      // We also can't just check "__reactInternalSnapshot",
      // Because get-snapshot might return a falsy value.
      // So check for the explicit __reactInternalSnapshotFlag flag to determine behavior.
      var snapshot = this.__reactInternalSnapshotFlag
        ? this.__reactInternalSnapshot
        : maybeSnapshot;

      componentDidUpdate.call(this, prevProps, prevState, snapshot);
    };
  }

  return Component;
}

var reactLifecyclesCompat_es = /*#__PURE__*/Object.freeze({
  __proto__: null,
  polyfill: polyfill
});

var require$$6 = /*@__PURE__*/getAugmentedNamespace(reactLifecyclesCompat_es);

var hasRequiredModal;

function requireModal () {
	if (hasRequiredModal) return Modal$2;
	hasRequiredModal = 1;

	Object.defineProperty(Modal$2, "__esModule", {
	  value: true
	});
	Modal$2.bodyOpenClassName = Modal$2.portalClassName = undefined;

	var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

	var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

	var _react = React__default;

	var _react2 = _interopRequireDefault(_react);

	var _reactDom = ReactDOM__default;

	var _reactDom2 = _interopRequireDefault(_reactDom);

	var _propTypes = /*@__PURE__*/ requirePropTypes();

	var _propTypes2 = _interopRequireDefault(_propTypes);

	var _ModalPortal = requireModalPortal();

	var _ModalPortal2 = _interopRequireDefault(_ModalPortal);

	var _ariaAppHider = requireAriaAppHider();

	var ariaAppHider = _interopRequireWildcard(_ariaAppHider);

	var _safeHTMLElement = requireSafeHTMLElement();

	var _safeHTMLElement2 = _interopRequireDefault(_safeHTMLElement);

	var _reactLifecyclesCompat = require$$6;

	function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj.default = obj; return newObj; } }

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

	var portalClassName = Modal$2.portalClassName = "ReactModalPortal";
	var bodyOpenClassName = Modal$2.bodyOpenClassName = "ReactModal__Body--open";

	var isReact16 = _safeHTMLElement.canUseDOM && _reactDom2.default.createPortal !== undefined;

	var createHTMLElement = function createHTMLElement(name) {
	  return document.createElement(name);
	};

	var getCreatePortal = function getCreatePortal() {
	  return isReact16 ? _reactDom2.default.createPortal : _reactDom2.default.unstable_renderSubtreeIntoContainer;
	};

	function getParentElement(parentSelector) {
	  return parentSelector();
	}

	var Modal = function (_Component) {
	  _inherits(Modal, _Component);

	  function Modal() {
	    var _ref;

	    var _temp, _this, _ret;

	    _classCallCheck(this, Modal);

	    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
	      args[_key] = arguments[_key];
	    }

	    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = Modal.__proto__ || Object.getPrototypeOf(Modal)).call.apply(_ref, [this].concat(args))), _this), _this.removePortal = function () {
	      !isReact16 && _reactDom2.default.unmountComponentAtNode(_this.node);
	      var parent = getParentElement(_this.props.parentSelector);
	      if (parent && parent.contains(_this.node)) {
	        parent.removeChild(_this.node);
	      } else {
	        // eslint-disable-next-line no-console
	        console.warn('React-Modal: "parentSelector" prop did not returned any DOM ' + "element. Make sure that the parent element is unmounted to " + "avoid any memory leaks.");
	      }
	    }, _this.portalRef = function (ref) {
	      _this.portal = ref;
	    }, _this.renderPortal = function (props) {
	      var createPortal = getCreatePortal();
	      var portal = createPortal(_this, _react2.default.createElement(_ModalPortal2.default, _extends({ defaultStyles: Modal.defaultStyles }, props)), _this.node);
	      _this.portalRef(portal);
	    }, _temp), _possibleConstructorReturn(_this, _ret);
	  }

	  _createClass(Modal, [{
	    key: "componentDidMount",
	    value: function componentDidMount() {
	      if (!_safeHTMLElement.canUseDOM) return;

	      if (!isReact16) {
	        this.node = createHTMLElement("div");
	      }
	      this.node.className = this.props.portalClassName;

	      var parent = getParentElement(this.props.parentSelector);
	      parent.appendChild(this.node);

	      !isReact16 && this.renderPortal(this.props);
	    }
	  }, {
	    key: "getSnapshotBeforeUpdate",
	    value: function getSnapshotBeforeUpdate(prevProps) {
	      var prevParent = getParentElement(prevProps.parentSelector);
	      var nextParent = getParentElement(this.props.parentSelector);
	      return { prevParent: prevParent, nextParent: nextParent };
	    }
	  }, {
	    key: "componentDidUpdate",
	    value: function componentDidUpdate(prevProps, _, snapshot) {
	      if (!_safeHTMLElement.canUseDOM) return;
	      var _props = this.props,
	          isOpen = _props.isOpen,
	          portalClassName = _props.portalClassName;


	      if (prevProps.portalClassName !== portalClassName) {
	        this.node.className = portalClassName;
	      }

	      var prevParent = snapshot.prevParent,
	          nextParent = snapshot.nextParent;

	      if (nextParent !== prevParent) {
	        prevParent.removeChild(this.node);
	        nextParent.appendChild(this.node);
	      }

	      // Stop unnecessary renders if modal is remaining closed
	      if (!prevProps.isOpen && !isOpen) return;

	      !isReact16 && this.renderPortal(this.props);
	    }
	  }, {
	    key: "componentWillUnmount",
	    value: function componentWillUnmount() {
	      if (!_safeHTMLElement.canUseDOM || !this.node || !this.portal) return;

	      var state = this.portal.state;
	      var now = Date.now();
	      var closesAt = state.isOpen && this.props.closeTimeoutMS && (state.closesAt || now + this.props.closeTimeoutMS);

	      if (closesAt) {
	        if (!state.beforeClose) {
	          this.portal.closeWithTimeout();
	        }

	        setTimeout(this.removePortal, closesAt - now);
	      } else {
	        this.removePortal();
	      }
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      if (!_safeHTMLElement.canUseDOM || !isReact16) {
	        return null;
	      }

	      if (!this.node && isReact16) {
	        this.node = createHTMLElement("div");
	      }

	      var createPortal = getCreatePortal();
	      return createPortal(_react2.default.createElement(_ModalPortal2.default, _extends({
	        ref: this.portalRef,
	        defaultStyles: Modal.defaultStyles
	      }, this.props)), this.node);
	    }
	  }], [{
	    key: "setAppElement",
	    value: function setAppElement(element) {
	      ariaAppHider.setElement(element);
	    }

	    /* eslint-disable react/no-unused-prop-types */

	    /* eslint-enable react/no-unused-prop-types */

	  }]);

	  return Modal;
	}(_react.Component);

	Modal.propTypes = {
	  isOpen: _propTypes2.default.bool.isRequired,
	  style: _propTypes2.default.shape({
	    content: _propTypes2.default.object,
	    overlay: _propTypes2.default.object
	  }),
	  portalClassName: _propTypes2.default.string,
	  bodyOpenClassName: _propTypes2.default.string,
	  htmlOpenClassName: _propTypes2.default.string,
	  className: _propTypes2.default.oneOfType([_propTypes2.default.string, _propTypes2.default.shape({
	    base: _propTypes2.default.string.isRequired,
	    afterOpen: _propTypes2.default.string.isRequired,
	    beforeClose: _propTypes2.default.string.isRequired
	  })]),
	  overlayClassName: _propTypes2.default.oneOfType([_propTypes2.default.string, _propTypes2.default.shape({
	    base: _propTypes2.default.string.isRequired,
	    afterOpen: _propTypes2.default.string.isRequired,
	    beforeClose: _propTypes2.default.string.isRequired
	  })]),
	  appElement: _propTypes2.default.oneOfType([_propTypes2.default.instanceOf(_safeHTMLElement2.default), _propTypes2.default.instanceOf(_safeHTMLElement.SafeHTMLCollection), _propTypes2.default.instanceOf(_safeHTMLElement.SafeNodeList), _propTypes2.default.arrayOf(_propTypes2.default.instanceOf(_safeHTMLElement2.default))]),
	  onAfterOpen: _propTypes2.default.func,
	  onRequestClose: _propTypes2.default.func,
	  closeTimeoutMS: _propTypes2.default.number,
	  ariaHideApp: _propTypes2.default.bool,
	  shouldFocusAfterRender: _propTypes2.default.bool,
	  shouldCloseOnOverlayClick: _propTypes2.default.bool,
	  shouldReturnFocusAfterClose: _propTypes2.default.bool,
	  preventScroll: _propTypes2.default.bool,
	  parentSelector: _propTypes2.default.func,
	  aria: _propTypes2.default.object,
	  data: _propTypes2.default.object,
	  role: _propTypes2.default.string,
	  contentLabel: _propTypes2.default.string,
	  shouldCloseOnEsc: _propTypes2.default.bool,
	  overlayRef: _propTypes2.default.func,
	  contentRef: _propTypes2.default.func,
	  id: _propTypes2.default.string,
	  overlayElement: _propTypes2.default.func,
	  contentElement: _propTypes2.default.func
	};
	Modal.defaultProps = {
	  isOpen: false,
	  portalClassName: portalClassName,
	  bodyOpenClassName: bodyOpenClassName,
	  role: "dialog",
	  ariaHideApp: true,
	  closeTimeoutMS: 0,
	  shouldFocusAfterRender: true,
	  shouldCloseOnEsc: true,
	  shouldCloseOnOverlayClick: true,
	  shouldReturnFocusAfterClose: true,
	  preventScroll: false,
	  parentSelector: function parentSelector() {
	    return document.body;
	  },
	  overlayElement: function overlayElement(props, contentEl) {
	    return _react2.default.createElement(
	      "div",
	      props,
	      contentEl
	    );
	  },
	  contentElement: function contentElement(props, children) {
	    return _react2.default.createElement(
	      "div",
	      props,
	      children
	    );
	  }
	};
	Modal.defaultStyles = {
	  overlay: {
	    position: "fixed",
	    top: 0,
	    left: 0,
	    right: 0,
	    bottom: 0,
	    backgroundColor: "rgba(255, 255, 255, 0.75)"
	  },
	  content: {
	    position: "absolute",
	    top: "40px",
	    left: "40px",
	    right: "40px",
	    bottom: "40px",
	    border: "1px solid #ccc",
	    background: "#fff",
	    overflow: "auto",
	    WebkitOverflowScrolling: "touch",
	    borderRadius: "4px",
	    outline: "none",
	    padding: "20px"
	  }
	};


	(0, _reactLifecyclesCompat.polyfill)(Modal);

	if (browser$1.env.NODE_ENV !== "production") {
	  Modal.setCreateHTMLElement = function (fn) {
	    return createHTMLElement = fn;
	  };
	}

	Modal$2.default = Modal;
	return Modal$2;
}

var hasRequiredLib;

function requireLib () {
	if (hasRequiredLib) return lib.exports;
	hasRequiredLib = 1;
	(function (module, exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});

		var _Modal = requireModal();

		var _Modal2 = _interopRequireDefault(_Modal);

		function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

		exports.default = _Modal2.default;
		module.exports = exports["default"]; 
	} (lib, lib.exports));
	return lib.exports;
}

var libExports = requireLib();
var ReactModal = /*@__PURE__*/getDefaultExportFromCjs(libExports);

var css_248z$s = ".Modal-module_overlay__4M2wS {\n  background-color: rgb(0 0 0 / 50%);\n  position: fixed;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  z-index: var(--z-index-modal);\n  display: flex;\n  justify-content: center;\n  align-items: center;\n}\n\n.Modal-module_modal__42I6x {\n  background-color: var(--color-white);\n  width: calc(100% - 16px);\n  height: fit-content;\n  max-height: 70vh;\n  box-shadow: var(--box-shadow);\n  border-radius: var(--border-radius-m);\n  overflow: hidden;\n  position: relative;\n  display: flex;\n  flex-direction: column;\n}\n\n.Modal-module_modal__42I6x:focus-visible {\n  outline: none;\n}\n\n@media (width >= 580px) {\n  .Modal-module_modal__42I6x {\n    width: 90vw;\n    max-width: 1200px;\n    border-radius: var(--border-radius-m);\n  }\n}\n\n.Modal-module_header__0jl-d {\n  padding: 36px 36px 0;\n  width: 100%;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: 16px;\n  margin-bottom: 32px;\n  user-select: none;\n}\n\n.Modal-module_header__0jl-d button {\n  align-self: flex-start;\n}\n\n.Modal-module_headline__jrske {\n  display: inline-flex;\n  align-items: baseline;\n  margin-bottom: 0;\n}\n\n.Modal-module_title__XS4tA {\n  font-size: var(--font-size-xxl);\n  word-break: break-word;\n}\n\n.Modal-module_subtitle__eKKhw {\n  font-size: var(--font-size-s);\n  margin-left: 16px;\n  color: var(--color-grey);\n}\n\n.Modal-module_actions__NPA4U {\n  display: flex;\n  gap: 8px;\n}\n\n.Modal-module_content__-hF6V {\n  padding: 0 36px;\n  font-size: var(--font-size-m);\n  overflow-y: auto;\n}\n\n.Modal-module_footer__S0Kd6 {\n  padding: 0 36px 36px;\n  margin-top: 32px;\n  display: flex;\n  justify-content: flex-end;\n}\n\n.Modal-module_footer__S0Kd6 button:not(:last-child) {\n  margin-right: 12px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_width-s__PpPDw {\n  max-width: 600px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_width-l__iW5gT {\n  max-width: 1600px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_full-width__qWjhL {\n  max-width: none;\n}\n\n.Modal-module_modal__42I6x.Modal-module_height-s__69vkK {\n  max-height: 50vh;\n}\n\n.Modal-module_modal__42I6x.Modal-module_height-l__pVhEU {\n  max-height: 85vh;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-header__pRi1T .Modal-module_content__-hF6V {\n  padding-top: 36px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-footer__IQoAp .Modal-module_content__-hF6V {\n  padding-bottom: 36px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-s__6PxxV .Modal-module_header__0jl-d {\n  padding: 16px 16px 0;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-s__6PxxV .Modal-module_content__-hF6V {\n  padding: 0 16px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-s__6PxxV .Modal-module_footer__S0Kd6 {\n  padding: 0 16px 16px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-l__RSFtf .Modal-module_header__0jl-d {\n  padding: 64px 64px 0;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-l__RSFtf .Modal-module_content__-hF6V {\n  padding: 0 64px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_padding-l__RSFtf .Modal-module_footer__S0Kd6 {\n  padding: 0 64px 64px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-header__pRi1T.Modal-module_padding-s__6PxxV .Modal-module_content__-hF6V {\n  padding-top: 16px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-header__pRi1T.Modal-module_padding-l__RSFtf .Modal-module_content__-hF6V {\n  padding-top: 64px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-footer__IQoAp.Modal-module_padding-s__6PxxV .Modal-module_content__-hF6V {\n  padding-bottom: 16px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_no-footer__IQoAp.Modal-module_padding-l__RSFtf .Modal-module_content__-hF6V {\n  padding-bottom: 64px;\n}\n\n.Modal-module_modal__42I6x.Modal-module_form-modal__hg5nr.Modal-module_no-header__pRi1T .Modal-module_content__-hF6V {\n  padding-top: 0;\n}\n\n.Modal-module_loader__XNTPn {\n  position: absolute;\n  z-index: var(--z-index-toast);\n}\n\n.Modal-module_loader__XNTPn[class*='lazy-loader'] {\n  background-color: rgb(255 255 255 / 80%);\n}\n";
var styles$r = {"overlay":"Modal-module_overlay__4M2wS","modal":"Modal-module_modal__42I6x","header":"Modal-module_header__0jl-d","headline":"Modal-module_headline__jrske","title":"Modal-module_title__XS4tA","subtitle":"Modal-module_subtitle__eKKhw","actions":"Modal-module_actions__NPA4U","content":"Modal-module_content__-hF6V","footer":"Modal-module_footer__S0Kd6","width-s":"Modal-module_width-s__PpPDw","width-l":"Modal-module_width-l__iW5gT","full-width":"Modal-module_full-width__qWjhL","height-s":"Modal-module_height-s__69vkK","height-l":"Modal-module_height-l__pVhEU","no-header":"Modal-module_no-header__pRi1T","no-footer":"Modal-module_no-footer__IQoAp","padding-s":"Modal-module_padding-s__6PxxV","padding-l":"Modal-module_padding-l__RSFtf","form-modal":"Modal-module_form-modal__hg5nr","loader":"Modal-module_loader__XNTPn"};
styleInject(css_248z$s);

/**
 *
 * Modal:
 * A modal that interrupts the user with important content. Can have an optional fixed footer.
 *
 * Uses react-modal as base component:
 * http://reactcommunity.org/react-modal/
 *
 * @param {React.ReactNode} children - Modal content
 * @param {string} title - Modal headline
 * @param subtitle
 * @param {boolean} isOpen
 * @param {React.ReactNode} footer - Optional footer component
 * @param {boolean} shouldCloseOnEsc - Close modal on escape key press
 * @param {boolean} shouldCloseOnOverlayClick - Close modal when backdrop is clicked
 * @param actions
 * @param customHeader
 * @param hideHeader
 * @param hideFooter
 * @param hideCloseButton
 * @param isFormModal
 * @param width
 * @param height
 * @param padding
 * @param parent
 * @param {boolean} onRequestClose - Function to close the modal
 *
 * The react-modal props are passed through and can be used.
 * @param className
 * @param props
 * @param ref
 */
var Modal = function (_a, ref) {
    var _b;
    var _c, _d;
    var isOpen = _a.isOpen, _e = _a.shouldCloseOnOverlayClick, shouldCloseOnOverlayClick = _e === void 0 ? false : _e, _f = _a.shouldCloseOnEsc, shouldCloseOnEsc = _f === void 0 ? true : _f, title = _a.title, subtitle = _a.subtitle, children = _a.children, actions = _a.actions, customHeader = _a.customHeader, footer = _a.footer, _g = _a.hideHeader, hideHeader = _g === void 0 ? false : _g, _h = _a.hideFooter, hideFooter = _h === void 0 ? true : _h, _j = _a.hideCloseButton, hideCloseButton = _j === void 0 ? false : _j, _k = _a.isFormModal, isFormModal = _k === void 0 ? false : _k, _l = _a.width, width = _l === void 0 ? 'm' : _l, _m = _a.fullWidth, fullWidth = _m === void 0 ? false : _m, _o = _a.height, height = _o === void 0 ? 'l' : _o, _p = _a.padding, padding = _p === void 0 ? 'm' : _p, loading = _a.loading, _q = _a.parent, parent = _q === void 0 ? document.getElementById('root') : _q, onRequestClose = _a.onRequestClose, className = _a.className, props = __rest(_a, ["isOpen", "shouldCloseOnOverlayClick", "shouldCloseOnEsc", "title", "subtitle", "children", "actions", "customHeader", "footer", "hideHeader", "hideFooter", "hideCloseButton", "isFormModal", "width", "fullWidth", "height", "padding", "loading", "parent", "onRequestClose", "className"]);
    var classes = classNames$1(styles$r.modal, styles$r['height-' + height], styles$r['padding-' + padding], (_b = {},
        _b[styles$r['width-' + width]] = !fullWidth,
        _b[styles$r['full-width']] = fullWidth,
        _b[styles$r['no-header']] = hideHeader,
        _b[styles$r['no-footer']] = hideFooter,
        _b[styles$r['form-modal']] = isFormModal,
        _b), className);
    return (jsx$1(ReactModal, __assign({}, props, { isOpen: isOpen, onRequestClose: onRequestClose, contentLabel: title, className: classes, overlayClassName: styles$r.overlay, shouldCloseOnEsc: shouldCloseOnEsc && !loading, shouldCloseOnOverlayClick: shouldCloseOnOverlayClick && !loading, parentSelector: function () { return parent !== null && parent !== void 0 ? parent : document.body; }, appElement: (_d = (_c = document.getElementById('root')) !== null && _c !== void 0 ? _c : document.documentElement) !== null && _d !== void 0 ? _d : undefined, children: isOpen && (jsxs(Fragment, { children: [loading && (jsx$1(LazyLoader$1, { className: styles$r.loader, embedded: true, size: 'xxl' })), !hideHeader && (jsx$1("div", { className: styles$r.header, children: customHeader ? (customHeader) : (jsxs(Fragment, { children: [jsxs("div", { className: styles$r.headline, children: [jsx$1("div", { className: styles$r.title, children: title }), subtitle && (jsx$1("div", { className: styles$r.subtitle, children: subtitle }))] }), jsxs("div", { className: styles$r.actions, children: [actions && actions, !hideCloseButton && (jsx$1(Button$1, { hierarchy: 'tertiary', onClick: function (event) {
                                            return onRequestClose(event);
                                        }, icon: jsx$1(SvgCloseIcon, {}) }))] })] })) })), jsx$1("div", { ref: ref, className: classNames$1(styles$r.content, 'has-scrollbar'), children: children }), !hideFooter && (jsx$1("div", { className: styles$r.footer, children: footer && footer }))] })) })));
};
var Modal$1 = forwardRef(Modal);

var css_248z$r = ".Printer-module_pdf-wrapper__zU9kk {\n  width: 100%;\n  height: 100vh;\n  min-height: 450px;\n  max-height: 750px;\n}\n";
var styles$q = {"pdf-wrapper":"Printer-module_pdf-wrapper__zU9kk"};
styleInject(css_248z$r);

function PrinterTable(_a) {
    var config = _a.config, table = _a.table;
    var fallback = '---';
    var styling = StyleSheet$1.create({
        header: {
            backgroundColor: '#d4d6df',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '8px',
        },
        row: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '4px 8px',
        },
        cell: {
            flex: '1 1 0',
            padding: '0 12px',
        },
        fixedCellSmall: {
            flex: '0 0 35px',
        },
        fixedCellMedium: {
            flex: '0 0 60px',
        },
        rowGrey: {
            backgroundColor: '#f2f2f8',
        },
        rowWhite: {
            backgroundColor: '#ffffff',
        },
    });
    function getBackground(index) {
        if (config.showHeader) {
            return index % 2 === 0 ? styling.rowWhite : styling.rowGrey;
        }
        else {
            return index % 2 !== 0 ? styling.rowWhite : styling.rowGrey;
        }
    }
    function getCellStyles(type, size) {
        var _a, _b, _c, _d;
        var styles = [styling.cell];
        if (size) {
            styles.push({ flex: "0 0 ".concat(size, "px") });
        }
        else if ((type === 'boolean' && ((_a = config.fixed) === null || _a === void 0 ? void 0 : _a.boolean)) ||
            type === 'active' ||
            (type === 'color' && ((_b = config.fixed) === null || _b === void 0 ? void 0 : _b.color) && ((_c = config.print) === null || _c === void 0 ? void 0 : _c.color))) {
            styles.push(styling.fixedCellSmall);
        }
        else if (type === 'date' && ((_d = config.fixed) === null || _d === void 0 ? void 0 : _d.date)) {
            styles.push(styling.fixedCellMedium);
        }
        return styles;
    }
    function getType(id) {
        var _a;
        /* eslint-disable */
        // cast to any because TS will not find .type otherwise for some reason
        return ((_a = table.getAllColumns().find(function (col) { return col.id === id; })) === null || _a === void 0 ? void 0 : _a.columnDef).type;
        /* eslint-enable */
    }
    function getSize(id) {
        var _a;
        /* eslint-disable */
        // cast to any because TS will not find .size otherwise for some reason
        return ((_a = table.getAllColumns().find(function (col) { return col.id === id; })) === null || _a === void 0 ? void 0 : _a.columnDef).size;
        /* eslint-enable */
    }
    function getColorIndicator(style, key, color) {
        return (jsx$1(Canvas, { style: style, paint: function (painterObject) {
                return painterObject
                    .circle(4.5, 4.5, 4.5)
                    .fill('#d4d6df')
                    .circle(4.5, 4.5, 4)
                    .fill(color);
            } }, key));
    }
    function getBooleanIcon(style, key, color, isTrue) {
        return (jsx$1(Canvas, { style: style, paint: function (painterObject) {
                var background = painterObject
                    .roundedRect(0, 0, 18, 9, 5)
                    .fill(isTrue ? color : '#d4d6df');
                var icon = isTrue
                    ? background
                        .moveTo(6, 5)
                        .lineTo(8, 7)
                        .lineTo(11.5, 2.5)
                        .stroke('white')
                    : background.moveTo(6, 4.5).lineTo(12, 4.5).stroke('#57576b');
                return icon;
            } }, key));
    }
    function getHeaders() {
        var headers = table
            .getHeaderGroups()
            .map(function (group) {
            return group.headers
                .filter(function (header) { return header.id !== 'select'; })
                .map(function (header) {
                var _a, _b;
                return {
                    type: getType(header.column.id),
                    size: getSize(header.column.id),
                    value: (_b = (_a = header.column.columnDef.header) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '',
                };
            });
        })
            .flat();
        return headers;
    }
    function getCellData(cell, index) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var value = cell.getContext().getValue();
        var type = getType(cell.column.id);
        var size = getSize(cell.column.id);
        var cellStyles = getCellStyles(type, size);
        if (type === 'date') {
            return (jsx$1(Text, { style: cellStyles, children: (_a = new Date(value).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })) !== null && _a !== void 0 ? _a : fallback }, index));
        }
        else if (type === 'boolean') {
            if ((_b = config.print) === null || _b === void 0 ? void 0 : _b.boolean) {
                return getBooleanIcon(cellStyles, index, '#fcd05f', value);
            }
            else {
                return (jsx$1(Text, { style: cellStyles, children: value ? 'O' : 'X' }, index));
            }
        }
        else if (type === 'active') {
            if ((_c = config.print) === null || _c === void 0 ? void 0 : _c.active) {
                return getBooleanIcon(cellStyles, index, '#84ac71', value);
            }
            else {
                return (jsx$1(Text, { style: cellStyles, children: value ? 'O' : 'X' }, index));
            }
        }
        else if (type === 'color') {
            var color = value;
            var asLabel = !color ? fallback : (_d = color.colorLabel) !== null && _d !== void 0 ? _d : fallback;
            var asColor = !color ? fallback : (_e = color.color) !== null && _e !== void 0 ? _e : fallback;
            if (((_f = config.print) === null || _f === void 0 ? void 0 : _f.color) && asColor !== fallback) {
                return getColorIndicator(cellStyles, index, asColor);
            }
            else {
                return (jsx$1(Text, { style: cellStyles, children: asLabel }, index));
            }
        }
        else if (typeof value === 'object') {
            return (jsx$1(Text, { style: cellStyles, children: (_g = JSON.stringify(value, null, 4)) !== null && _g !== void 0 ? _g : fallback }, index));
        }
        else if (typeof value === 'string') {
            return (jsx$1(Text, { style: cellStyles, children: (_h = value) !== null && _h !== void 0 ? _h : fallback }, index));
        }
        else {
            return (jsx$1(Text, { style: cellStyles, children: fallback }, index));
        }
    }
    return (jsxs(View, { children: [config.showHeader && (jsx$1(View, { style: styling.header, fixed: config.fixedHeader, children: getHeaders().map(function (header, index) {
                    return (jsx$1(Text, { style: getCellStyles(header.type, header.size), children: header.value }, index));
                }) })), jsx$1(View, { children: table.getRowModel().rows.map(function (row, rowIndex) {
                    return (jsx$1(View, { break: false, wrap: false, style: [styling.row, getBackground(rowIndex)], children: row
                            .getVisibleCells()
                            .filter(function (cell) { return !cell.id.includes('select'); })
                            .map(function (cell, cellIndex) {
                            return getCellData(cell, cellIndex);
                        }) }, rowIndex));
                }) })] }));
}

function Printer(_a) {
    var _b = _a.orientation, orientation = _b === void 0 ? 'portrait' : _b, _c = _a.size, size = _c === void 0 ? 'A4' : _c, headline = _a.headline, subline = _a.subline, _d = _a.showHeader, showHeader = _d === void 0 ? true : _d, _e = _a.showHeadline, showHeadline = _e === void 0 ? true : _e, _f = _a.showSubline, showSubline = _f === void 0 ? true : _f, _g = _a.showTableHeader, showTableHeader = _g === void 0 ? true : _g, _h = _a.fixedTableHeader, fixedTableHeader = _h === void 0 ? true : _h, _j = _a.showFooter, showFooter = _j === void 0 ? true : _j, _k = _a.showDate, showDate = _k === void 0 ? true : _k, _l = _a.showPageNumber, showPageNumber = _l === void 0 ? true : _l, _m = _a.fixedDate, fixedDate = _m === void 0 ? true : _m, _o = _a.fixedColor, fixedColor = _o === void 0 ? true : _o, _p = _a.fixedBoolean, fixedBoolean = _p === void 0 ? false : _p, _q = _a.printColor, printColor = _q === void 0 ? true : _q, _r = _a.printBoolean, printBoolean = _r === void 0 ? true : _r, _s = _a.printActive, printActive = _s === void 0 ? true : _s, table = _a.table, filename = _a.filename, setFile = _a.setFile;
    var headerHeight = 40;
    var headerPadding = 24;
    var footerHeight = 8;
    var footerPadding = 8;
    var contentMargin = 24;
    var spacing = 32;
    var styling = StyleSheet$1.create({
        page: {
            color: '#57576b',
            paddingVertical: spacing + 'px',
            paddingBottom: (showFooter ? footerHeight + 2 * footerPadding + spacing : spacing) +
                'px',
        },
        header: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: headerHeight + 2 * headerPadding + 'px',
            paddingVertical: headerPadding + 'px',
            backgroundColor: '#f2f2f8',
            alignItems: 'center',
            textAlign: 'center',
        },
        headline: {
            fontSize: '16px',
            marginBottom: '8px',
        },
        subline: {
            fontSize: '12px',
            color: '#8e949f',
        },
        content: {
            marginHorizontal: contentMargin + 'px',
            marginTop: (showHeader ? headerHeight + 2 * headerPadding : contentMargin) + 'px',
        },
        footer: {
            fontSize: '6px',
            paddingHorizontal: contentMargin + 'px',
            paddingVertical: footerPadding + 'px',
            height: footerHeight + 2 * footerPadding + 'px',
            backgroundColor: '#f2f2f8',
            gap: '8px',
            flexDirection: 'row',
            alignItems: 'center',
            position: 'absolute',
            bottom: '0',
            left: '0',
            right: '0',
        },
        dateWrapper: {
            marginLeft: 'auto',
            flexDirection: 'row',
            gap: '8px',
        },
        table: {
            fontSize: '8px',
        },
    });
    var PDFFile = function () {
        return (jsx$1(Document, { title: filename !== null && filename !== void 0 ? filename : undefined, children: jsxs(Page, { size: size, orientation: orientation, style: styling.page, children: [showHeader && (jsxs(View, { style: styling.header, children: [showHeadline && headline && (jsx$1(Text, { style: styling.headline, children: headline })), showSubline && subline && (jsx$1(Text, { style: styling.subline, children: subline }))] })), jsx$1(View, { style: styling.content, children: table && (jsx$1(View, { style: styling.table, children: jsx$1(PrinterTable, { config: {
                                    showHeader: showTableHeader,
                                    fixedHeader: fixedTableHeader,
                                    fixed: {
                                        date: fixedDate,
                                        boolean: fixedBoolean,
                                        color: fixedColor,
                                    },
                                    print: {
                                        color: printColor,
                                        boolean: printBoolean,
                                        active: printActive,
                                    },
                                }, table: table }) })) }), showFooter && (jsxs(View, { style: styling.footer, fixed: true, children: [headline && showHeader && jsx$1(Text, { children: headline }), subline && showSubline && jsx$1(Text, { children: subline }), jsxs(View, { style: styling.dateWrapper, children: [showDate && (jsx$1(Text, { children: new Date().toLocaleDateString('de-DE', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        }) })), showPageNumber && (jsx$1(Text, { render: function (_a) {
                                            var pageNumber = _a.pageNumber, totalPages = _a.totalPages;
                                            return "Seite ".concat(String(pageNumber).padStart(2, '0'), " / ").concat(String(totalPages).padStart(2, '0'));
                                        } }))] })] }))] }) }));
    };
    useEffect(function () {
        setFile(jsx$1(PDFFile, {}));
    }, []);
    return (jsx$1(PDFViewer, { className: styles$q['pdf-wrapper'], showToolbar: false, children: jsx$1(PDFFile, {}) }));
}

var PrinterModal = function (_a) {
    var _b = _a.headline, headline = _b === void 0 ? '' : _b, _c = _a.subline, subline = _c === void 0 ? '' : _c, _d = _a.filename, filename = _d === void 0 ? '' : _d, _e = _a.showPrintButton, showPrintButton = _e === void 0 ? true : _e, _f = _a.showSettingsButton, showSettingsButton = _f === void 0 ? true : _f, isOpen = _a.isOpen, table = _a.table, handleClose = _a.handleClose;
    var _g = useState('portrait'), orientation = _g[0], setOrientation = _g[1];
    var _h = useState('A4'), size = _h[0], setSize = _h[1];
    var _j = useState(true), showHeader = _j[0], setShowHeader = _j[1];
    var _k = useState(true), showHeadline = _k[0], setShowHeadline = _k[1];
    var _l = useState(true), showSubline = _l[0], setShowsubline = _l[1];
    var _m = useState(true), showTableHeader = _m[0], setShowTableHeader = _m[1];
    var _o = useState(true), fixedTableHeader = _o[0], setFixedTableHeader = _o[1];
    var _p = useState(true), showFooter = _p[0], setShowFooter = _p[1];
    var _q = useState(true), showDate = _q[0], setShowDate = _q[1];
    var _r = useState(true), showPageNumber = _r[0], setShowPageNumber = _r[1];
    var _s = useState(false), sameColumnWidth = _s[0], setSameColumnWidth = _s[1];
    var _t = useState(true), fixedDate = _t[0], setFixedDate = _t[1];
    var _u = useState(false), fixedBoolean = _u[0], setFixedBoolean = _u[1];
    var _v = useState(true), fixedColor = _v[0], setFixedColor = _v[1];
    var _w = useState(true), showColors = _w[0], setShowColors = _w[1];
    var _x = useState(true), printColor = _x[0], setPrintColor = _x[1];
    var _y = useState(true), printBoolean = _y[0], setPrintBoolean = _y[1];
    var _z = useState(true), printActive = _z[0], setPrintActive = _z[1];
    var _0 = useState(), DownloadFile = _0[0], setDownloadFile = _0[1];
    var orientationeDropdown = [
        {
            label: 'Hochformat',
            color: orientation === 'portrait' ? 'primary' : 'default',
            onClick: function () { return setOrientation('portrait'); },
        },
        {
            label: 'Querformat',
            color: orientation === 'landscape' ? 'primary' : 'default',
            onClick: function () { return setOrientation('landscape'); },
        },
    ];
    var sizeDropdown = [
        {
            label: 'A5',
            color: size === 'A5' ? 'primary' : 'default',
            onClick: function () { return setSize('A5'); },
        },
        {
            label: 'A4',
            color: size === 'A4' ? 'primary' : 'default',
            onClick: function () { return setSize('A4'); },
        },
        {
            label: 'A3',
            color: size === 'A3' ? 'primary' : 'default',
            onClick: function () { return setSize('A3'); },
        },
    ];
    var layoutDropdown = [
        {
            label: 'Kopfzeile einblenden',
            type: 'switch',
            value: showHeader,
            onValueChange: function (value) { return setShowHeader(value); },
        },
        {
            label: 'Schultitel einblenden',
            type: 'switch',
            value: !showHeader ? false : showHeadline,
            disabled: !showHeader,
            onValueChange: function (value) { return setShowHeadline(value); },
        },
        {
            label: 'Tabellenbezeichnung einblenden',
            type: 'switch',
            value: !showHeader ? false : showSubline,
            disabled: !showHeader,
            onValueChange: function (value) { return setShowsubline(value); },
        },
        {
            type: 'ruler',
        },
        {
            label: 'Tabellenkopf einblenden',
            type: 'switch',
            value: showTableHeader,
            onValueChange: function (value) { return setShowTableHeader(value); },
        },
        {
            label: 'Tabellenkopf fixieren',
            tooltip: 'Druckt den Tabellenkopf am Start jeder Seite',
            type: 'switch',
            value: !showTableHeader ? false : fixedTableHeader,
            disabled: !showTableHeader,
            onValueChange: function (value) { return setFixedTableHeader(value); },
        },
        {
            type: 'ruler',
        },
        {
            label: 'Fußzeile einblenden',
            type: 'switch',
            value: showFooter,
            onValueChange: function (value) { return setShowFooter(value); },
        },
        {
            label: 'Datum einblenden',
            type: 'switch',
            value: !showFooter ? false : showDate,
            disabled: !showFooter,
            onValueChange: function (value) { return setShowDate(value); },
        },
        {
            label: 'Seitenzahl einblenden',
            type: 'switch',
            value: !showFooter ? false : showPageNumber,
            disabled: !showFooter,
            onValueChange: function (value) { return setShowPageNumber(value); },
        },
        {
            type: 'ruler',
        },
        {
            label: 'Gleiche Breite für alle Spalten',
            tooltip: 'Druckt alle Spalten (bis auf "Aktiv") in der gleichen Breite',
            type: 'switch',
            value: sameColumnWidth,
            onValueChange: function (value) { return setSameColumnWidth(value); },
        },
        {
            label: 'Feste Breite für Datumsspalten',
            type: 'switch',
            value: sameColumnWidth ? false : fixedDate,
            disabled: sameColumnWidth,
            onValueChange: function (value) { return setFixedDate(value); },
        },
        {
            label: 'Feste Breite für Wahrheitswerte',
            type: 'switch',
            value: sameColumnWidth ? false : fixedBoolean,
            disabled: sameColumnWidth,
            onValueChange: function (value) { return setFixedBoolean(value); },
        },
        {
            label: 'Feste Breite für Farbwerte',
            type: 'switch',
            value: sameColumnWidth ? false : fixedColor,
            disabled: sameColumnWidth || !showColors || !printColor,
            onValueChange: function (value) { return setFixedColor(value); },
        },
    ];
    var contentDropdown = [
        {
            label: 'Farbmodus',
            tooltip: "Ersetzt Farbindikatoren mit dem jeweiligen Namen und\nWahrheitswerte mit einer kontrastreicheren Alternative",
            type: 'switch',
            value: showColors,
            onValueChange: function (value) { return setShowColors(value); },
        },
        {
            label: 'Farben als Indikator drucken',
            type: 'switch',
            tooltip: 'Druckt einen Farbindikator anstelle des Farbnamens',
            value: !showColors ? false : printColor,
            disabled: !showColors,
            onValueChange: function (value) { return setPrintColor(value); },
        },
        {
            label: 'Warheitswerte als Icon drucken',
            type: 'switch',
            value: !showColors ? false : printBoolean,
            disabled: !showColors,
            onValueChange: function (value) { return setPrintBoolean(value); },
        },
        {
            label: '"Aktiv" Werte als Icon drucken',
            type: 'switch',
            value: !showColors ? false : printActive,
            disabled: !showColors,
            onValueChange: function (value) { return setPrintActive(value); },
        },
    ];
    var settingsDropdown = [
        {
            label: 'Format',
            subContent: orientationeDropdown,
        },
        {
            label: 'Größe',
            subContent: sizeDropdown,
        },
        {
            label: 'Layout',
            subContent: layoutDropdown,
        },
        {
            label: 'Inhalt',
            subContent: contentDropdown,
        },
    ];
    return (jsx$1(Modal$1, { style: { content: { whiteSpace: 'pre-wrap' } }, title: 'Druckvorschau', isOpen: isOpen, onRequestClose: function () { return handleClose(); }, actions: jsxs(Fragment, { children: [showPrintButton && DownloadFile && (jsx$1(PDFDownloadLink, { document: DownloadFile, fileName: "".concat(filename, ".pdf"), children: function (_a) {
                        var loading = _a.loading;
                        return (jsx$1(Button$1, { disabled: loading, icon: loading ? (jsx$1(SvgSpinnerIcon, { className: 'spinning' })) : (jsx$1(SvgImportIcon, {})), hierarchy: 'tertiary', children: "Download" }));
                    } })), showSettingsButton && (jsx$1(Dropdown$1, { usePortal: false, trigger: jsx$1(Button$1, { name: 'size', hierarchy: 'tertiary', icon: jsx$1(SvgSettingsIcon, {}), children: "Druckeinstellungen" }), children: jsx$1(DropdownMenu, { usePortal: false, data: settingsDropdown }) }))] }), children: jsx$1(Printer, { orientation: orientation, size: size, headline: headline, subline: subline, showHeader: showHeader, showHeadline: showHeadline, showSubline: showSubline, showTableHeader: showTableHeader, fixedTableHeader: fixedTableHeader, showFooter: showFooter, showDate: showDate, showPageNumber: showPageNumber, fixedDate: sameColumnWidth ? false : fixedDate, fixedColor: sameColumnWidth ? false : fixedColor, fixedBoolean: sameColumnWidth ? false : fixedBoolean, printColor: !showColors ? false : printColor, printBoolean: !showColors ? false : printBoolean, printActive: !showColors ? false : printActive, table: table, filename: filename, setFile: function (file) { return setDownloadFile(file); } }) }));
};

var css_248z$q = ".ActionBar-module_action-bar__tn6aY {\n  width: 100%;\n  min-height: var(--action-bar-height);\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: center;\n  gap: var(--table-cell-gap);\n}\n\n.ActionBar-module_action-bar__tn6aY .ActionBar-module_left__KyGK1,\n.ActionBar-module_action-bar__tn6aY .ActionBar-module_right__oeCFr {\n  width: 100%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  flex-wrap: wrap;\n  gap: var(--table-cell-gap);\n}\n\n.ActionBar-module_action-bar__tn6aY .ActionBar-module_left__KyGK1 .ActionBar-module_count__ZfQxG {\n  margin-top: 2px;\n  font-size: var(--font-size-s);\n  color: var(--color-grey-light);\n  user-select: none;\n  text-align: center;\n}\n\n.ActionBar-module_action-bar__tn6aY.ActionBar-module_is-mobile__a-Bre .ActionBar-module_global-filter__Q2599 {\n  width: 50%;\n}\n\n@media screen and (width >= 580px) {\n  .ActionBar-module_action-bar__tn6aY {\n    flex-direction: row;\n  }\n\n  .ActionBar-module_action-bar__tn6aY .ActionBar-module_left__KyGK1,\n  .ActionBar-module_action-bar__tn6aY .ActionBar-module_right__oeCFr {\n    width: fit-content;\n    flex-direction: row;\n  }\n\n  .ActionBar-module_action-bar__tn6aY .ActionBar-module_left__KyGK1 {\n    justify-content: flex-start;\n  }\n\n  .ActionBar-module_action-bar__tn6aY .ActionBar-module_right__oeCFr {\n    justify-content: flex-end;\n  }\n}\n";
var styles$p = {"action-bar":"ActionBar-module_action-bar__tn6aY","left":"ActionBar-module_left__KyGK1","right":"ActionBar-module_right__oeCFr","count":"ActionBar-module_count__ZfQxG","is-mobile":"ActionBar-module_is-mobile__a-Bre","global-filter":"ActionBar-module_global-filter__Q2599"};
styleInject(css_248z$q);

var css_248z$p = ".ExpertFilter-module_expert-filter__4VxfV {\n  position: absolute;\n  background-color: #fff;\n  margin-top: 8px;\n  width: 750px;\n  min-height: 120px;\n  border: 1px solid var(--color-grey-light);\n  box-shadow: 0 12px 34px rgb(15 15 47 / 6%);\n  border-radius: 4px;\n  padding: 24px 24px 8px;\n  z-index: var(--z-index-overlay);\n}\n\n.ExpertFilter-module_form-row__1gfL- {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  margin-bottom: 24px;\n}\n\n.ExpertFilter-module_operation__jrlyt {\n  width: 100px;\n}\n\n.ExpertFilter-module_column__HfamV {\n  width: 160px;\n}\n\n.ExpertFilter-module_condition__aiwKD {\n  width: 100px;\n}\n\n.ExpertFilter-module_value__h9oEE {\n  width: 260px;\n}\n\n.ExpertFilter-module_actions__5CNOh {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: 16px;\n}\n\n.ExpertFilter-module_actions__5CNOh button:first-child {\n  margin-right: auto;\n}\n\n.ExpertFilter-module_results__J7f1Y {\n  font-size: var(--font-size-s);\n  color: var(--color-grey-light);\n  margin-left: 8px;\n}\n";
var styles$o = {"expert-filter":"ExpertFilter-module_expert-filter__4VxfV","form-row":"ExpertFilter-module_form-row__1gfL-","operation":"ExpertFilter-module_operation__jrlyt","column":"ExpertFilter-module_column__HfamV","condition":"ExpertFilter-module_condition__aiwKD","value":"ExpertFilter-module_value__h9oEE","actions":"ExpertFilter-module_actions__5CNOh"};
styleInject(css_248z$p);

function ExpertFilter(_a) {
    var columns = _a.columns, setOptions = _a.setOptions, setColumnFilters = _a.setColumnFilters, onFilter = _a.onFilter, filterFormRows = _a.filterFormRows, setFilterFormRows = _a.setFilterFormRows, initialFormState = _a.initialFormState;
    function applyFilter() {
        var columnFilters = [];
        setOptions(function (prev) { return (__assign(__assign({}, prev), { columns: columns.map(function (column) {
                var current = filterFormRows.find(function (filterRow) {
                    return column.id === filterRow.column.value;
                });
                if (current) {
                    column.filterFn =
                        column.type === 'boolean' || column.type === 'active'
                            ? 'equals'
                            : current.condition.value;
                    if (current.condition.value === 'between') {
                        columnFilters.push({
                            value: [current.filterValue, current.filterValue2],
                            id: current.column.value,
                        });
                    }
                    else {
                        columnFilters.push({
                            value: current.filterValue,
                            id: current.column.value,
                        });
                    }
                }
                return column;
            }) })); });
        setColumnFilters(function () { return columnFilters; });
    }
    /**
     *
     * @param {number} index
     * @param {MultiValue<SelectOptionType> | SingleValue<SelectOptionType>} selectedOption
     *
     * Event handler that is invoked when the column select in the expert filter
     * form row is changed.
     *
     * Updates the form state and applies the data to filter the table.
     */
    function handleColumnChange(index, selectedOption) {
        var formData = __spreadArray$1([], filterFormRows, true);
        formData[index].column = selectedOption;
        // Check if selected column is of type boolean:
        // This would change the condition to "exact", disables the condition selection and
        // changes the filter value field to a select with "yes/no" instead of an input field.
        // Also set "true" as default value for the filter value select.
        if (selectedOption.type === 'boolean' ||
            selectedOption.type === 'active') {
            formData[index].type = 'boolean';
            formData[index].condition = getConditionOptions()[1];
            formData[index].filterValue = getBooleanOptions()[0].value;
        }
        else if (selectedOption.type === 'date') {
            formData[index].type = 'date';
            formData[index].condition = getDateConditionOptions()[0];
            formData[index].filterValue = new Date();
        }
        else {
            formData[index].type = 'string';
            formData[index].condition = getConditionOptions()[0];
            formData[index].filterValue = '';
        }
        setFilterFormRows(formData);
    }
    /**
     *
     * @param {number} index
     * @param {MultiValue<SelectOptionType> | SingleValue<SelectOptionType>} selectedOption
     *
     * Event handler that is invoked when the condition select in the expert filter
     * form row is changed.
     *
     * Updates the form state and applies the data to filter the table.
     */
    function handleConditionChange(index, selectedOption) {
        var formData = __spreadArray$1([], filterFormRows, true);
        formData[index].condition = selectedOption;
        setFilterFormRows(formData);
    }
    /**
     *
     * @param {number} index
     * @param {ChangeEvent<HTMLInputElement>} event
     *
     * Event handler that is invoked when the filter value input field in the expert filter
     * form row is changed.
     *
     * Updates the form state and applies the data to filter the table.
     */
    function handleFilterValueChange(index, event) {
        var formData = __spreadArray$1([], filterFormRows, true);
        formData[index].filterValue = event.target.value;
        setFilterFormRows(formData);
    }
    /**
     *
     * @param {number} index
     * @param {MultiValue<SelectOptionType> | SingleValue<SelectOptionType>} selectedOption
     *
     * Event handler that is invoked when the boolean filter select (true/false) in the expert filter
     * form row is changed.
     *
     * Updates the form state and applies the data to filter the table.
     */
    function handleBooleanFilterValueChange(index, selectedOption) {
        var formData = __spreadArray$1([], filterFormRows, true);
        formData[index].filterValue = selectedOption
            .value;
        setFilterFormRows(formData);
    }
    function handleDateFilterValueChange(index, date, field) {
        var formData = __spreadArray$1([], filterFormRows, true);
        if (date) {
            formData[index][field] = date;
            setFilterFormRows(formData);
        }
    }
    /**
     *
     * @param index
     * @param event
     *
     * CURRENTLY NOT USED YET
     *
     * Handler when the operation select is changed
     */
    function handleOperationChange(index, event) {
        // what is this for?
        // console.log('Operation change');
    }
    /**
     * Adds a new form row to the filter form
     */
    function addRow() {
        setFilterFormRows(__spreadArray$1(__spreadArray$1([], filterFormRows, true), [initialFormState], false));
    }
    /**
     *
     * @param {number} index
     *
     * Removes the selected row from the filter form
     */
    function removeRow(index) {
        if (filterFormRows.length <= 1) {
            return;
        }
        var data = __spreadArray$1([], filterFormRows, true);
        data.splice(index, 1);
        setFilterFormRows(data);
        // Manually trigger Escape event when user clicks in DropdownMenu item
        // because Radix Dropdown does not close automatically when link is clicked.
        // Can be improved
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    }
    /**
     *
     * @param {number} index
     *
     * Duplicates the selected filter row
     */
    function duplicateRow(index) {
        var data = __spreadArray$1([], filterFormRows, true);
        data[index + 1] = __assign({}, data[index]);
        setFilterFormRows(data);
        // Manually trigger Escape event when user clicks in DropdownMenu item
        // because Radix Dropdown does not close automatically when link is clicked.
        // Can be improved
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    }
    /**
     *
     * @returns {ColumnSelectOptionType[]}
     *
     * Maps all table columns to the Select data
     * format.
     */
    function mapTableColumns() {
        return columns
            .filter(function (col) { return col.type !== 'color'; })
            .filter(function (col) { return col.enableColumnFilter !== false; })
            .map(function (col) {
            var _a, _b, _c;
            return ({
                label: (_b = (_a = col.meta) === null || _a === void 0 ? void 0 : _a.filterName) !== null && _b !== void 0 ? _b : col.header,
                value: col.id,
                type: (_c = col.type) !== null && _c !== void 0 ? _c : 'string',
            });
        });
    }
    /**
     *
     * @returns {SelectOptionType[]}
     *
     * Create data for operation Select
     */
    function getOperations() {
        return [
            { label: 'Und', value: 'and' },
            { label: 'Oder', value: 'or' },
        ];
    }
    /**
     *
     * @returns {SelectOptionType[]}
     *
     * Create data for condition Select
     */
    function getConditionOptions() {
        return [
            { label: 'enthält', value: 'contains' },
            { label: 'ist', value: 'equals' },
        ];
    }
    /**
     *
     * @returns {SelectOptionType[]}
     *
     * Create data for condition Select
     */
    function getBooleanOptions() {
        return [
            { label: 'Ja', value: true },
            { label: 'Nein', value: false },
        ];
    }
    /**
     *
     * @returns {SelectOptionType[]}
     *
     * Create data for condition Select
     */
    function getDateConditionOptions() {
        return [
            { label: 'zwischen', value: 'between' },
            { label: 'kleiner oder gleich', value: 'lessThanOrEqualTo' },
            { label: 'größer oder gleich', value: 'greaterThanOrEqualTo' },
            { label: 'kleiner', value: 'lessThan' },
            { label: 'größer', value: 'greaterThan' },
        ];
    }
    /**
     * Resets form fields and clears all filters.
     */
    function clearFilters() {
        var columnFilters = [];
        setFilterFormRows([initialFormState]);
        setOptions(function (prev) { return (__assign(__assign({}, prev), { columns: columns })); });
        setColumnFilters(function () { return columnFilters; });
        onFilter();
    }
    return (jsxs("div", { className: styles$o['expert-filter'], children: [jsx$1("form", { children: filterFormRows.map(function (filterFormRow, index) {
                    return (jsxs("div", { className: styles$o['form-row'], children: [index === 0 ? (jsx$1("label", { className: styles$o.operation, children: "Filtern nach" })) : (jsx$1(Select$1, { dense: true, name: 'operation', options: getOperations(), isMulti: false, className: styles$o.operation, disabled: true, onChange: function (event) { return handleOperationChange(); }, value: filterFormRow.operation })), jsx$1(Select$1, { dense: true, name: 'column', options: mapTableColumns(), isMulti: false, className: styles$o.column, onChange: function (event) { return handleColumnChange(index, event); }, value: filterFormRow.column }), filterFormRow.type !== 'date' && (jsx$1(Select$1, { dense: true, name: 'condition', options: getConditionOptions(), isMulti: false, className: styles$o.condition, onChange: function (event) { return handleConditionChange(index, event); }, value: filterFormRow.type !== 'boolean'
                                    ? filterFormRow.condition
                                    : getConditionOptions()[1], disabled: filterFormRow.type === 'boolean' })), filterFormRow.type === 'date' && (jsx$1(Select$1, { dense: true, name: 'condition', options: getDateConditionOptions(), isMulti: false, className: styles$o.condition, onChange: function (event) { return handleConditionChange(index, event); }, value: filterFormRow.condition })), filterFormRow.type === 'string' && (jsx$1(Input$1, { dense: true, name: 'filterValue', className: styles$o.value, onChange: function (event) { return handleFilterValueChange(index, event); }, value: filterFormRow.filterValue })), (filterFormRow.type === 'boolean' ||
                                filterFormRow.type === 'active') && (jsx$1(Select$1, { dense: true, name: 'filterValue', options: getBooleanOptions(), isMulti: false, className: styles$o.value, onChange: function (event) {
                                    return handleBooleanFilterValueChange(index, event);
                                }, value: filterFormRow.filterValue
                                    ? getBooleanOptions()[0]
                                    : getBooleanOptions()[1] })), filterFormRow.type === 'date' && (jsxs(Fragment, { children: [jsx$1(DatePicker$1, { dense: true, name: 'filterValue', value: filterFormRows[index].filterValue instanceof Date
                                            ? filterFormRows[index].filterValue
                                            : new Date(), onChange: function (date) {
                                            return handleDateFilterValueChange(index, date, 'filterValue');
                                        }, showMonthYearDropdown: true }), filterFormRows[index].condition.value === 'between' && (jsx$1(DatePicker$1, { dense: true, name: 'filterValue2', value: filterFormRows[index].filterValue2 instanceof Date
                                            ? filterFormRows[index].filterValue2
                                            : new Date(), onChange: function (date) {
                                            return handleDateFilterValueChange(index, date, 'filterValue2');
                                        }, showMonthYearDropdown: true }))] })), jsx$1(Dropdown$1, { usePortal: false, trigger: jsx$1(Button$1, { hierarchy: 'tertiary', icon: jsx$1(SvgDotsHorizontalIcon, {}) }), children: jsx$1(DropdownMenu, { data: [
                                        {
                                            type: 'default',
                                            label: 'Duplizieren',
                                            onClick: function () { return duplicateRow(index); },
                                        },
                                        {
                                            label: 'Löschen',
                                            color: 'error',
                                            onClick: function () { return removeRow(index); },
                                        },
                                    ] }) })] }, index));
                }) }), jsxs("footer", { className: styles$o.actions, children: [jsx$1(Button$1, { className: styles$o.add, hierarchy: 'tertiary', icon: jsx$1(SvgAddIcon, {}), onClick: addRow, children: "Filter hinzuf\u00FCgen" }), jsx$1(Button$1, { hierarchy: 'tertiary', onClick: function () {
                            clearFilters();
                        }, children: "Filter l\u00F6schen" }), jsx$1(Button$1, { hierarchy: 'primary', onClick: function () {
                            applyFilter();
                            onFilter();
                        }, children: "Filtern" })] })] }));
}

var css_248z$o = ".SearchInput-module_search-input__LcK-p {\n  position: relative;\n  cursor: text;\n}\n\n.SearchInput-module_input__YiP1K {\n  background-color: var(--input-bg-color-default);\n  border: 1px solid transparent;\n  border-radius: var(--search-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  height: var(--input-height-mobile);\n  outline: none;\n  padding-left: 12px;\n  padding-right: 48px;\n  transition: 0.3s ease all;\n  width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.SearchInput-module_input__YiP1K::placeholder {\n  color: var(--input-text-color-placeholder);\n}\n\n/* clears the 'X' from Internet Explorer */\n.SearchInput-module_input__YiP1K::-ms-clear {\n  display: none;\n  width: 0;\n  height: 0;\n}\n\n.SearchInput-module_input__YiP1K::-ms-reveal {\n  display: none;\n  width: 0;\n  height: 0;\n}\n\n/* clears the 'X' from Chrome */\n.SearchInput-module_input__YiP1K::-webkit-search-decoration,\n.SearchInput-module_input__YiP1K::-webkit-search-cancel-button,\n.SearchInput-module_input__YiP1K::-webkit-search-results-button,\n.SearchInput-module_input__YiP1K::-webkit-search-results-decoration {\n  display: none;\n}\n\n@media (width >= 580px) {\n  .SearchInput-module_input__YiP1K {\n    height: calc(var(--input-height-desktop) - 2px);\n    line-height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n/* hovered */\n.SearchInput-module_search-input__LcK-p:hover .SearchInput-module_input__YiP1K {\n  border-color: var(--color-grey-lighter);\n}\n\n/* filled */\n.SearchInput-module_search-input__LcK-p.SearchInput-module_filled__Fvl6A .SearchInput-module_input__YiP1K {\n  border-color: var(--input-border-color-filled);\n  color: var(--input-text-color-filled);\n}\n\n/* focused */\n.SearchInput-module_search-input__LcK-p .SearchInput-module_input__YiP1K:focus-within {\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n}\n\n/* disabled */\n.SearchInput-module_search-input__LcK-p .SearchInput-module_input__YiP1K:disabled {\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-text-color-disabled);\n}\n\n/* on white background */\n.SearchInput-module_search-input__LcK-p.SearchInput-module_on-white__iG5cI .SearchInput-module_input__YiP1K {\n  border-color: var(--input-border-color-disabled);\n}\n\n.SearchInput-module_search-input__LcK-p.SearchInput-module_on-white__iG5cI .SearchInput-module_input__YiP1K:hover {\n  border-color: var(--input-border-color-filled);\n}\n\n.SearchInput-module_icon__aefQV {\n  position: absolute;\n  right: 8px;\n  top: 50%;\n  transform: translateY(-50%);\n  color: var(--color-grey-light);\n  transition: color 0.3s ease;\n}\n\n.SearchInput-module_search-input__LcK-p:hover .SearchInput-module_icon__aefQV,\n.SearchInput-module_search-input__LcK-p.SearchInput-module_filled__Fvl6A .SearchInput-module_icon__aefQV {\n  color: var(--color-grey-dark);\n}\n";
var styles$n = {"search-input":"SearchInput-module_search-input__LcK-p","input":"SearchInput-module_input__YiP1K","filled":"SearchInput-module_filled__Fvl6A","on-white":"SearchInput-module_on-white__iG5cI","icon":"SearchInput-module_icon__aefQV"};
styleInject(css_248z$o);

/**
 *
 * Search Input Field
 *
 * @param {string} name
 * @param {string} value
 * @param {boolean} disabled
 * @param {boolean} required
 * @param {string} placeholder
 * @param {string} className
 * @param {boolean} autoFocus
 * @param {function} onChange
 * @param {function} onBlur
 * @param {function} onFocus
 * @param ref
 */
var SearchInput = function (_a, ref) {
    var _b;
    var name = _a.name, value = _a.value, disabled = _a.disabled, required = _a.required, placeholder = _a.placeholder, className = _a.className, autoFocus = _a.autoFocus, isOnWhite = _a.isOnWhite, _c = _a.tabIndex, tabIndex = _c === void 0 ? 0 : _c, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus;
    var inputRef = useRef(null);
    var classes = classNames$1(styles$n['search-input'], (_b = {},
        _b[styles$n['filled']] = value,
        _b[styles$n['on-white']] = isOnWhite,
        _b), className);
    return (jsxs("div", { ref: ref, className: classes, children: [jsx$1("input", { ref: inputRef, type: 'search', id: name, name: name, placeholder: placeholder, disabled: disabled, className: styles$n.input, required: required, onChange: onChange, onBlur: onBlur, onFocus: onFocus, value: value !== null && value !== void 0 ? value : '', autoFocus: autoFocus, tabIndex: tabIndex }), jsx$1("div", { className: styles$n.icon, onClick: function () { return inputRef.current && inputRef.current.focus(); }, children: jsx$1(SvgSearchIcon, {}) })] }));
};
var SearchInput$1 = forwardRef(SearchInput);

var DebouncedInput = function (_a) {
    var initialValue = _a.value, _b = _a.debounce, debounce = _b === void 0 ? 200 : _b, isOnWhite = _a.isOnWhite, _c = _a.tabIndex, tabIndex = _c === void 0 ? 0 : _c, _d = _a.placeholder, placeholder = _d === void 0 ? '' : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, onChange = _a.onChange;
    var _f = React__default.useState(initialValue), value = _f[0], setValue = _f[1];
    React__default.useEffect(function () {
        setValue(initialValue);
    }, [initialValue]);
    React__default.useEffect(function () {
        var timeout = setTimeout(function () {
            onChange(value);
        }, debounce);
        return function () { return clearTimeout(timeout); };
    }, [debounce, value, onChange]);
    return (jsx$1(SearchInput$1, { value: value || '', onChange: function (e) {
            setValue(e.target.value);
        }, placeholder: placeholder, name: 'globalFilter', isOnWhite: isOnWhite, tabIndex: tabIndex, disabled: disabled }));
};

function ActionBar(_a) {
    var _b;
    var _c, _d;
    var table = _a.table, columns = _a.columns, selection = _a.selection, isMobile = _a.isMobile, isOnWhite = _a.isOnWhite, _e = _a.showExpertFilter, showExpertFilter = _e === void 0 ? false : _e, _f = _a.disableExpertFilter, disableExpertFilter = _f === void 0 ? false : _f, _g = _a.showGlobalFilter, showGlobalFilter = _g === void 0 ? true : _g, _h = _a.disableGlobalFilter, disableGlobalFilter = _h === void 0 ? false : _h, _j = _a.globalFilterValue, globalFilterValue = _j === void 0 ? '' : _j, globalFilterPlaceholder = _a.globalFilterPlaceholder, _k = _a.showFilteredRowCount, showFilteredRowCount = _k === void 0 ? false : _k, _l = _a.showSelectedRowsCount, showSelectedRowsCount = _l === void 0 ? false : _l, _m = _a.showBulkEdit, showBulkEdit = _m === void 0 ? false : _m, _o = _a.disableBulkEdit, disableBulkEdit = _o === void 0 ? false : _o, _p = _a.bulkEditText, bulkEditText = _p === void 0 ? 'Aktion für Auswahl' : _p, _q = _a.showAddButton, showAddButton = _q === void 0 ? false : _q, _r = _a.disableAddButton, disableAddButton = _r === void 0 ? false : _r, _s = _a.addButtonText, addButtonText = _s === void 0 ? 'Hinzufügen' : _s, _t = _a.showPrintButton, showPrintButton = _t === void 0 ? false : _t, _u = _a.disablePrintButton, disablePrintButton = _u === void 0 ? false : _u, _v = _a.printButtonText, printButtonText = _v === void 0 ? 'Drucken' : _v, _w = _a.marginBottom, marginBottom = _w === void 0 ? 'var(--table-action-bar-gap)' : _w, _x = _a.tabIndex, tabIndex = _x === void 0 ? 0 : _x, extendedActionsLeft = _a.extendedActionsLeft, actionsLeftSpacing = _a.actionsLeftSpacing, extendedActionsRight = _a.extendedActionsRight, actionsRightSpacing = _a.actionsRightSpacing, bulkEditDropdownContent = _a.bulkEditDropdownContent, onGlobalFilterChange = _a.onGlobalFilterChange, onAddClick = _a.onAddClick, onPrintClick = _a.onPrintClick, className = _a.className;
    var _y = useState(false), openExpertFilter = _y[0], setOpenExpertFilter = _y[1];
    var initialFormState = {
        operation: { value: 'and', label: 'Und' },
        column: { value: '', label: '', type: 'string' },
        condition: { label: 'enthält', value: 'contains' },
        filterValue: '',
        type: 'string',
    };
    var _z = useState([initialFormState]), filterFormRows = _z[0], setFilterFormRows = _z[1];
    var placeholder = globalFilterPlaceholder
        ? globalFilterPlaceholder
        : columns && columns[0]
            ? "z.B. nach ".concat((_d = (_c = columns[0].meta) === null || _c === void 0 ? void 0 : _c.filterName) !== null && _d !== void 0 ? _d : columns[0].header)
            : 'Suchen';
    var actionBarClasses = classNames$1(styles$p['action-bar'], (_b = {}, _b[styles$p['is-mobile']] = isMobile, _b), className);
    return (jsxs("div", { className: actionBarClasses, style: { marginBottom: marginBottom }, children: [jsxs("div", { className: styles$p.left, style: { gap: actionsLeftSpacing !== null && actionsLeftSpacing !== void 0 ? actionsLeftSpacing : 'var(--table-cell-gap)' }, children: [showExpertFilter && (jsxs("div", { className: styles$p['expert-filter'], children: [jsx$1(Button$1, { icon: jsx$1(SvgFilterIcon, {}), hierarchy: 'tertiary', onClick: function () { return setOpenExpertFilter(!openExpertFilter); }, alwaysShowBorder: true, disabled: disableExpertFilter || !table || !columns, tabIndex: tabIndex, children: filterFormRows.length > 0 &&
                                    filterFormRows[0].column.value !== ''
                                    ? "".concat(filterFormRows.length, " Filter angewendet")
                                    : 'Filtern' }), openExpertFilter && table && columns && (jsx$1(ExpertFilter, { initialFormState: initialFormState, filterFormRows: filterFormRows, setFilterFormRows: setFilterFormRows, columns: columns, setOptions: table.setOptions, setColumnFilters: table.setColumnFilters, onFilter: function () {
                                    setOpenExpertFilter(!openExpertFilter);
                                } }))] })), showGlobalFilter && (jsx$1("div", { className: styles$p['global-filter'], children: jsx$1(DebouncedInput, { placeholder: placeholder, value: globalFilterValue !== null && globalFilterValue !== void 0 ? globalFilterValue : '', onChange: function (value) { return onGlobalFilterChange(String(value)); }, isOnWhite: isOnWhite, tabIndex: tabIndex, disabled: disableGlobalFilter }) })), showFilteredRowCount && table && (jsx$1("div", { className: styles$p.count, children: "".concat(table.getPrePaginationRowModel().rows.length, " von ").concat(table.getPreFilteredRowModel().rows.length, " angezeigt") })), showSelectedRowsCount && table && (jsx$1("div", { className: styles$p.count, children: "".concat(table.getSelectedRowModel().rows.length, " von ").concat(table.getPreFilteredRowModel().rows.length, " ausgew\u00E4hlt") })), extendedActionsLeft && extendedActionsLeft] }), jsxs("div", { className: styles$p.right, style: {
                    gap: actionsRightSpacing !== null && actionsRightSpacing !== void 0 ? actionsRightSpacing : 'calc(var(--table-cell-gap) / 2)',
                }, children: [extendedActionsRight && extendedActionsRight, showBulkEdit && table && bulkEditDropdownContent && (jsx$1(Dropdown$1, { disabled: disableBulkEdit ||
                            !table ||
                            !table.getSelectedRowModel().flatRows ||
                            table.getSelectedRowModel().flatRows.length === 0, trigger: jsx$1(Button$1, { icon: jsx$1(SvgBulkEditIcon, {}), hierarchy: 'tertiary', disabled: disableBulkEdit ||
                                !table ||
                                !table.getSelectedRowModel().flatRows ||
                                table.getSelectedRowModel().flatRows.length === 0, tabIndex: tabIndex, children: !isMobile && bulkEditText }), children: bulkEditDropdownContent &&
                            bulkEditDropdownContent(table
                                ? table.getSelectedRowModel().flatRows
                                : selection
                                    ? selection
                                    : []) })), showAddButton && onAddClick && (jsx$1(Button$1, { icon: jsx$1(SvgAddIcon, {}), hierarchy: 'tertiary', onClick: function (event) {
                            return onAddClick(event);
                        }, tabIndex: tabIndex, disabled: disableAddButton, children: !isMobile && addButtonText })), showPrintButton && onPrintClick && (jsx$1(Button$1, { icon: jsx$1(SvgPrintIcon, {}), hierarchy: 'tertiary', onClick: function (event) {
                            return onPrintClick(event);
                        }, tabIndex: tabIndex, disabled: disablePrintButton, children: !isMobile && printButtonText }))] })] }));
}

var css_248z$n = ".MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q {\n  padding: 16px 24px;\n}\n\n.MobileTable-module_mobile-table__SDFao.MobileTable-module_single-column__4cD7D .MobileTable-module_row__Pmo9q {\n  padding: 8px 24px;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q:not(:last-child) {\n  border-bottom: 1px solid var(--table-row-divider-color);\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_header__q5NJi {\n  height: var(--table-header-height-mobile);\n  margin-bottom: 8px;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  gap: 8px;\n}\n\n.MobileTable-module_mobile-table__SDFao.MobileTable-module_single-column__4cD7D .MobileTable-module_row__Pmo9q .MobileTable-module_header__q5NJi {\n  margin-bottom: 0;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_header__q5NJi .MobileTable-module_headline__Bd3M3 {\n  font-size: var(--font-size-m);\n  flex: 1 1 100%;\n  max-width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_header__q5NJi .MobileTable-module_last-col__sa8Ea {\n  font-size: var(--font-size-m);\n  flex: 0 0 auto;\n  gap: 8px;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_cell__NQ864 {\n  width: 100%;\n  display: flex;\n  align-items: center;\n  gap: var(--table-cell-gap);\n  height: var(--table-row-height-mobile);\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_cell__NQ864 .MobileTable-module_headline__Bd3M3 {\n  flex: 0 0 calc(50% - var(--table-cell-gap) / 2);\n  color: var(--color-grey-light);\n  font-size: var(--font-size-m);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_cell__NQ864 .MobileTable-module_content__yv9K9 {\n  flex: 0 0 calc(50% - var(--table-cell-gap) / 2);\n  color: var(--color-grey-darker);\n  font-size: var(--font-size-m);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.MobileTable-module_mobile-table__SDFao .MobileTable-module_row__Pmo9q .MobileTable-module_cell__NQ864 .MobileTable-module_content__yv9K9 div[class*='color-hint'] {\n  display: block;\n  width: 16px;\n  height: 16px;\n}\n\n.MobileTable-module_mobile-table__SDFao\n  .MobileTable-module_row__Pmo9q\n  .MobileTable-module_cell__NQ864\n  .MobileTable-module_content__yv9K9\n  div[class*='color-hint']\n  span[class*='color-dot'] {\n  display: block;\n  width: 16px;\n  height: 16px;\n  border: 1px solid var(--color-grey-lighter);\n  border-radius: 50%;\n}\n";
var styles$m = {"mobile-table":"MobileTable-module_mobile-table__SDFao","row":"MobileTable-module_row__Pmo9q","single-column":"MobileTable-module_single-column__4cD7D","header":"MobileTable-module_header__q5NJi","headline":"MobileTable-module_headline__Bd3M3","last-col":"MobileTable-module_last-col__sa8Ea","cell":"MobileTable-module_cell__NQ864","content":"MobileTable-module_content__yv9K9"};
styleInject(css_248z$n);

function MobileTable(_a) {
    var _b;
    var columns = _a.columns, table = _a.table, showSelect = _a.showSelect, lastCol = _a.lastCol, lastColWidth = _a.lastColWidth, customPadding = _a.customPadding, leftColWidth = _a.leftColWidth, rightColWidth = _a.rightColWidth;
    var headers = table
        .getHeaderGroups()
        .map(function (group) { return group.headers.map(function (header) { return header; }); })
        .flat(2);
    var classes = classNames$1(styles$m['mobile-table'], (_b = {},
        _b[styles$m['single-column']] = columns.length <= 1,
        _b));
    return (jsx$1("div", { className: classes, children: table.getRowModel().rows.map(function (row) {
            return (jsx$1("div", { className: styles$m.row, style: {
                    padding: customPadding !== null && customPadding !== void 0 ? customPadding : undefined,
                }, children: row.getVisibleCells().map(function (cell, index) {
                    return index === 0 ? (
                    // row-header
                    jsxs("div", { className: styles$m.header, children: [showSelect && (jsx$1(Checkbox$1, { className: styles$m.checkbox, name: "checkbox-".concat(row.id), checked: row.getIsSelected(),
                                indeterminate: row.getIsSomeSelected(),
                                onChange: row.getToggleSelectedHandler() })), jsx$1("div", { className: styles$m.headline, children: flexRender(CustomCell, {
                                    cell: cell,
                                    columns: columns,
                                }) }), lastCol && (jsx$1("div", { className: styles$m['last-col'], style: { flex: "1 1 ".concat(lastColWidth) }, children: lastCol(row) }))] }, cell.id)) : (
                    // row-content
                    jsxs("div", { className: styles$m.cell, children: [jsx$1("div", { className: styles$m.headline, style: {
                                    flex: leftColWidth ? "0 0 ".concat(leftColWidth) : undefined,
                                }, children: flexRender(headers[index].column.columnDef.header, headers[index].getContext()) }), jsx$1("div", { className: styles$m.content, style: {
                                    flex: rightColWidth
                                        ? "0 0 ".concat(rightColWidth)
                                        : leftColWidth
                                            ? "1 1 auto"
                                            : undefined,
                                }, children: flexRender(CustomCell, {
                                    cell: cell,
                                    columns: columns,
                                }) })] }, cell.id));
                }) }, row.id));
        }) }));
}

var css_248z$m = ".Pagination-module_pagination__1tbBZ {\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    padding: 16px 0;\n    border-top: 1px solid var(--table-row-divider-color);\n    font-size: 14px;\n}\n\n.Pagination-module_pagination-info__fxFdB {\n    margin-left: 16px;\n}\n";
var styles$l = {"pagination":"Pagination-module_pagination__1tbBZ"};
styleInject(css_248z$m);

var Pagination = function (_a) {
    var firstPage = _a.firstPage, getCanPreviousPage = _a.getCanPreviousPage, getCanNextPage = _a.getCanNextPage, previousPage = _a.previousPage, currentPage = _a.currentPage, pageCount = _a.pageCount, nextPage = _a.nextPage, lastPage = _a.lastPage, setPageIndex = _a.setPageIndex, className = _a.className;
    var pageMenu = useMemo(function () {
        var menu = [];
        var _loop_1 = function (i) {
            menu.push({
                label: (i + 1).toString(),
                onClick: function () { return setPageIndex(i); },
            });
        };
        for (var i = 0; i < pageCount; i++) {
            _loop_1(i);
        }
        return menu;
    }, [pageCount]);
    return (jsx$1("div", { className: className, children: jsx$1("div", { className: styles$l.pagination, children: jsxs(ButtonGroup$1, { isSmall: true, children: [jsx$1(Button$1, { hierarchy: 'secondary', onClick: function () { return firstPage(); }, disabled: !getCanPreviousPage(), icon: jsx$1(SvgDoublearrowLeftIcon, {}) }), jsx$1(Button$1, { hierarchy: 'secondary', onClick: function () { return previousPage(); }, disabled: !getCanPreviousPage(), icon: jsx$1(SvgArrowLeftIcon, {}) }), jsx$1(Dropdown$1, { className: styles$l['pagination-menu'], triggerStyle: { width: 'fit-content' }, trigger: jsx$1(Button$1, { style: { width: 'fit-content' }, hierarchy: 'secondary', children: "".concat(currentPage + 1, " von ").concat(pageCount.toLocaleString()) }), children: jsx$1(DropdownMenu, { maxHeight: 's', data: pageMenu }) }), jsx$1(Button$1, { hierarchy: 'secondary', onClick: function () { return nextPage(); }, disabled: !getCanNextPage(), icon: jsx$1(SvgArrowRightIcon, {}) }), jsx$1(Button$1, { hierarchy: 'secondary', onClick: function () { return lastPage(); }, disabled: !getCanNextPage(), icon: jsx$1(SvgDoublearrowRightIcon, {}) })] }) }) }));
};

function Table(_a) {
    var _b;
    var _c, _d, _e, _f, _g;
    var columns = _a.columns, data = _a.data, _h = _a.showSelect, showSelect = _h === void 0 ? false : _h, _j = _a.showSort, showSort = _j === void 0 ? false : _j, sorting = _a.sorting, onSortingChange = _a.onSortingChange, _k = _a.showVisibility, showVisibility = _k === void 0 ? false : _k, columnVisibility = _a.columnVisibility, onColumnVisibilityChange = _a.onColumnVisibilityChange, _l = _a.showRowHover, showRowHover = _l === void 0 ? false : _l, _m = _a.selectOnClick, selectOnClick = _m === void 0 ? false : _m, _o = _a.showSelected, showSelected = _o === void 0 ? false : _o, rowSelection = _a.rowSelection, rowMarker = _a.rowMarker, onRowSelectionChange = _a.onRowSelectionChange, lastColWidth = _a.lastColWidth, lastCol = _a.lastCol, _p = _a.canScroll, canScroll = _p === void 0 ? false : _p, _q = _a.hideScrollbarGutter, hideScrollbarGutter = _q === void 0 ? false : _q, maxHeight = _a.maxHeight, minHeight = _a.minHeight, customPadding = _a.customPadding, customLastColSpacing = _a.customLastColSpacing, customRowHeight = _a.customRowHeight, customHeaderHeight = _a.customHeaderHeight, _r = _a.hideHeader, hideHeader = _r === void 0 ? false : _r, emptyStateSettings = _a.emptyStateSettings, printerSettings = _a.printerSettings, _s = _a.showActionBar, showActionBar = _s === void 0 ? false : _s, actionBarSettings = _a.actionBarSettings, _t = _a.isOnWhite, isOnWhite = _t === void 0 ? true : _t, _u = _a.showShadow, showShadow = _u === void 0 ? false : _u, _v = _a.showBorderRadius, showBorderRadius = _v === void 0 ? false : _v, globalFilter = _a.globalFilter, _w = _a.breakpoint, breakpoint = _w === void 0 ? null : _w, mobileSettings = _a.mobileSettings, _x = _a.loading, loading = _x === void 0 ? false : _x, showPagination = _a.showPagination, _y = _a.pageSize, pageSize = _y === void 0 ? 100000 : _y, bulkEditDropdownContent = _a.bulkEditDropdownContent, onRowClick = _a.onRowClick, onAddClick = _a.onAddClick, className = _a.className;
    var ref = useRef(null);
    var _z = useCalculateTableHeight(), calculatedHeight = _z.calculatedHeight, contentRef = _z.wrapperRef, dimensions = _z.dimensions;
    var _0 = useState(null), parentHeight = _0[0], setParentHeight = _0[1];
    var _1 = useState(false), showPrintModal = _1[0], setShowPrintModal = _1[1];
    var _2 = useState(false), isMobile = _2[0], setIsMobile = _2[1];
    var _3 = useState(), headerWidth = _3[0], setHeaderWidth = _3[1];
    var _4 = useState(), maxCalculatedHeight = _4[0], setMaxCalculatedHeight = _4[1];
    var _5 = useState(false), calculatedCanScroll = _5[0], setCalculatedCanScroll = _5[1];
    var _6 = useState({
        pageIndex: 0,
        pageSize: pageSize,
    }), pagination = _6[0], setPagination = _6[1];
    var filterFns = useMemo(function () { return (__assign({}, filters)); }, []);
    var _7 = useState(), filter = _7[0], setFilter = _7[1];
    var table = useReactTable({
        data: data,
        columns: columns,
        isMultiSortEvent: function () { return true; },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            columnVisibility: columnVisibility,
            sorting: sorting,
            rowSelection: rowSelection !== null && rowSelection !== void 0 ? rowSelection : {},
            globalFilter: globalFilter && globalFilter !== '' ? globalFilter : filter,
            pagination: pagination,
        },
        onGlobalFilterChange: setFilter,
        onRowSelectionChange: onRowSelectionChange,
        onSortingChange: onSortingChange,
        onColumnVisibilityChange: onColumnVisibilityChange,
        getSortedRowModel: getSortedRowModel(),
        filterFns: filterFns,
        globalFilterFn: filters.contains,
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
    });
    var lastColWidthCalculated = lastColWidth
        ? lastColWidth
        : lastCol
            ? 'var(--table-last-col-width)'
            : 'var(--input-height-desktop)';
    var tableClasses = classNames$1(styles$v.table, (_b = {},
        _b[styles$v['on-white']] = isOnWhite,
        _b[styles$v['with-border-radius']] = showBorderRadius,
        _b[styles$v['with-shadow']] = showShadow,
        _b[styles$v['can-scroll']] = calculatedCanScroll,
        _b[styles$v['hide-gutter']] = hideScrollbarGutter,
        _b), className);
    useLayoutEffect(function () {
        var height = undefined;
        if (canScroll && parentHeight) {
            if (minHeight && dimensions.height < minHeight) {
                setCalculatedCanScroll(false);
                setMaxCalculatedHeight(undefined);
                return;
            }
            if (maxHeight) {
                height = "calc(".concat(maxHeight, "px - 90px)");
                if (hideHeader)
                    height = "calc(".concat(height, " + var(--table-header-height))");
            }
            else
                height = "calc(".concat(calculatedHeight, " - var(--table-header-height))");
            setCalculatedCanScroll(true);
            setMaxCalculatedHeight(height);
        }
    }, [dimensions.height, parentHeight, calculatedHeight]);
    useLayoutEffect(function () {
        setCalculatedCanScroll(canScroll);
    }, [canScroll]);
    useLayoutEffect(function () {
        var _a, _b;
        if (ref.current && calculatedCanScroll) {
            setParentHeight((_b = (_a = ref.current.parentElement) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : null);
        }
    }, [calculatedCanScroll]);
    useLayoutEffect(function () {
        if (!breakpoint || breakpoint === '')
            return;
        var breaknumber = parseInt(breakpoint.slice(0, -2));
        setIsMobile(breaknumber > dimensions.width);
    }, [dimensions.width, breakpoint]);
    useLayoutEffect(function () {
        var _a;
        setHeaderWidth((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth);
    }, [
        dimensions.width,
        (_c = contentRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth,
        columnVisibility,
        contentRef,
    ]);
    useLayoutEffect(function () {
        setFilter(globalFilter);
    }, [globalFilter]);
    return (jsxs("div", { className: tableClasses, ref: ref, children: [showActionBar && (jsx$1("div", { className: styles$v['action-bar'], style: {
                    paddingLeft: customPadding !== null && customPadding !== void 0 ? customPadding : undefined,
                    paddingRight: customPadding !== null && customPadding !== void 0 ? customPadding : undefined,
                }, children: jsx$1(ActionBar, __assign({}, actionBarSettings, { table: table, columns: columns, onGlobalFilterChange: function (filterValue) { return setFilter(filterValue); }, bulkEditDropdownContent: bulkEditDropdownContent, onAddClick: onAddClick, onPrintClick: function () { return setShowPrintModal(true); }, isMobile: isMobile, isOnWhite: isOnWhite })) })), jsxs("div", { className: classNames$1(styles$v.wrapper, 'has-scrollbar hide-gutter'), children: [loading && (jsx$1(LazyLoader$1, { className: styles$v.loader, embedded: true, size: 'xxl' })), !hideHeader && !isMobile && (jsx$1(TableHeader, { showSelect: showSelect, showSort: showSort, columns: columns, table: table, tableCanScroll: calculatedCanScroll && !hideScrollbarGutter, showVisibility: showVisibility, hasLastCol: !!lastCol, lastColWidth: lastColWidthCalculated, customPadding: customPadding !== null && customPadding !== void 0 ? customPadding : null, customLastColSpacing: customLastColSpacing !== null && customLastColSpacing !== void 0 ? customLastColSpacing : null, customHeight: customHeaderHeight !== null && customHeaderHeight !== void 0 ? customHeaderHeight : null, width: "".concat(headerWidth, "px") })), isMobile && showSelect && (jsx$1("div", { className: styles$v['select-all'], children: jsx$1(Checkbox$1, { className: styles$v.checkbox, label: 'Alle ausw\u00E4hlen', name: 'all-checked', checked: table.getIsAllRowsSelected(),
                            indeterminate: table.getIsSomeRowsSelected(),
                            onChange: table.getToggleAllRowsSelectedHandler() }) })), jsx$1("div", { className: classNames$1(styles$v.content, {
                            'has-scrollbar': calculatedCanScroll && !hideScrollbarGutter,
                        }), style: {
                            maxHeight: maxCalculatedHeight !== null && maxCalculatedHeight !== void 0 ? maxCalculatedHeight : undefined,
                        }, ref: contentRef, children: !data || table.getFilteredRowModel().flatRows.length === 0 ? (jsx$1(EmptyState$1, __assign({}, emptyStateSettings, { padding: (_d = emptyStateSettings === null || emptyStateSettings === void 0 ? void 0 : emptyStateSettings.padding) !== null && _d !== void 0 ? _d : 'l', title: (_e = emptyStateSettings === null || emptyStateSettings === void 0 ? void 0 : emptyStateSettings.title) !== null && _e !== void 0 ? _e : 'Diese Liste ist leer', subtitle: data.length === 0
                                ? ((_f = emptyStateSettings === null || emptyStateSettings === void 0 ? void 0 : emptyStateSettings.subtitle) !== null && _f !== void 0 ? _f : 'Bitte fügen Sie Ihren ersten Eintrag hinzu.')
                                : table.getFilteredRowModel().flatRows.length === 0
                                    ? 'Es wurde kein passender Eintrag gefunden'
                                    : '', iconColor: (_g = emptyStateSettings === null || emptyStateSettings === void 0 ? void 0 : emptyStateSettings.backgroundColor) !== null && _g !== void 0 ? _g : 'var(--color-primary-light)' }))) : isMobile ? (jsx$1(MobileTable, { columns: columns, table: table, showSelect: showSelect, lastCol: lastCol, lastColWidth: lastColWidthCalculated, customPadding: customPadding, leftColWidth: mobileSettings === null || mobileSettings === void 0 ? void 0 : mobileSettings.leftColWidth, rightColWidth: mobileSettings === null || mobileSettings === void 0 ? void 0 : mobileSettings.rightColWidth })) : (table.getRowModel().rows.map(function (row) {
                            return (jsx$1(TableRow, { className: styles$v.row, showSelect: showSelect, columns: columns, row: row, rowMarker: rowMarker !== null && rowMarker !== void 0 ? rowMarker : {}, rowSelection: rowSelection !== null && rowSelection !== void 0 ? rowSelection : {}, showVisibility: showVisibility, showRowHover: showRowHover, selectOnClick: selectOnClick, showSelected: showSelected, lastColWidth: lastColWidthCalculated, lastCol: lastCol !== null && lastCol !== void 0 ? lastCol : null, customPadding: customPadding !== null && customPadding !== void 0 ? customPadding : null, customLastColSpacing: customLastColSpacing !== null && customLastColSpacing !== void 0 ? customLastColSpacing : null, customHeight: customRowHeight !== null && customRowHeight !== void 0 ? customRowHeight : null, showMultiline: columns.some(function (cell) { return cell.isMultiline; }), onRowClick: onRowClick }, row.id));
                        })) })] }), showPagination && table.getRowCount() > pageSize && (jsx$1(Pagination, __assign({ currentPage: table.getState().pagination.pageIndex, firstPage: table.firstPage, getCanNextPage: table.getCanNextPage, getCanPreviousPage: table.getCanPreviousPage, lastPage: table.lastPage, nextPage: table.nextPage, pageCount: table.getPageCount(), previousPage: table.previousPage, setPageIndex: table.setPageIndex }, table.getState().pagination))), jsx$1(PrinterModal, __assign({}, printerSettings, { isOpen: showPrintModal, table: table, handleClose: function () { return setShowPrintModal(false); } }))] }));
}

const c=e=>"number"==typeof e&&!isNaN(e),d=e=>"string"==typeof e,u=e=>"function"==typeof e,p=e=>d(e)||u(e)?e:null,m=e=>isValidElement(e)||d(e)||u(e)||c(e);function f(e,t,n){ void 0===n&&(n=300);const{scrollHeight:o,style:s}=e;requestAnimationFrame(()=>{s.minHeight="initial",s.height=o+"px",s.transition=`all ${n}ms`,requestAnimationFrame(()=>{s.height="0",s.padding="0",s.margin="0",setTimeout(t,n);});});}function g(t){let{enter:a,exit:r,appendPosition:i=false,collapse:l=true,collapseDuration:c=300}=t;return function(t){let{children:d,position:u,preventExitTransition:p,done:m,nodeRef:g,isIn:y,playToast:v}=t;const h=i?`${a}--${u}`:a,T=i?`${r}--${u}`:r,E=useRef(0);return useLayoutEffect(()=>{const e=g.current,t=h.split(" "),n=o=>{o.target===g.current&&(v(),e.removeEventListener("animationend",n),e.removeEventListener("animationcancel",n),0===E.current&&"animationcancel"!==o.type&&e.classList.remove(...t));};e.classList.add(...t),e.addEventListener("animationend",n),e.addEventListener("animationcancel",n);},[]),useEffect(()=>{const e=g.current,t=()=>{e.removeEventListener("animationend",t),l?f(e,m,c):m();};y||(p?t():(E.current=1,e.className+=` ${T}`,e.addEventListener("animationend",t)));},[y]),React__default.createElement(React__default.Fragment,null,d)}}function y(e,t){return null!=e?{content:e.content,containerId:e.props.containerId,id:e.props.toastId,theme:e.props.theme,type:e.props.type,data:e.props.data||{},isLoading:e.props.isLoading,icon:e.props.icon,status:t}:{}}const v=new Map;let h=[];const T=new Set,E=e=>T.forEach(t=>t(e)),b=()=>v.size>0;function I(e,t){var n;if(t)return !(null==(n=v.get(t))||!n.isToastActive(e));let o=false;return v.forEach(t=>{t.isToastActive(e)&&(o=true);}),o}function _(e,t){m(e)&&(b()||h.push({content:e,options:t}),v.forEach(n=>{n.buildToast(e,t);}));}function C(e,t){v.forEach(n=>{null!=t&&null!=t&&t.containerId?(null==t?void 0:t.containerId)===n.id&&n.toggle(e,null==t?void 0:t.id):n.toggle(e,null==t?void 0:t.id);});}function L(e){const{subscribe:o,getSnapshot:s,setProps:i}=useRef(function(e){const n=e.containerId||1;return {subscribe(o){const s=function(e,n,o){let s=1,r=0,i=[],l=[],f=[],g=n;const v=new Map,h=new Set,T=()=>{f=Array.from(v.values()),h.forEach(e=>e());},E=e=>{l=null==e?[]:l.filter(t=>t!==e),T();},b=e=>{const{toastId:n,onOpen:s,updateId:a,children:r}=e.props,i=null==a;e.staleId&&v.delete(e.staleId),v.set(n,e),l=[...l,e.props.toastId].filter(t=>t!==e.staleId),T(),o(y(e,i?"added":"updated")),i&&u(s)&&s(isValidElement(r)&&r.props);};return {id:e,props:g,observe:e=>(h.add(e),()=>h.delete(e)),toggle:(e,t)=>{v.forEach(n=>{null!=t&&t!==n.props.toastId||u(n.toggle)&&n.toggle(e);});},removeToast:E,toasts:v,clearQueue:()=>{r-=i.length,i=[];},buildToast:(n,l)=>{if((t=>{let{containerId:n,toastId:o,updateId:s}=t;const a=n?n!==e:1!==e,r=v.has(o)&&null==s;return a||r})(l))return;const{toastId:f,updateId:h,data:I,staleId:_,delay:C}=l,L=()=>{E(f);},N=null==h;N&&r++;const $={...g,style:g.toastStyle,key:s++,...Object.fromEntries(Object.entries(l).filter(e=>{let[t,n]=e;return null!=n})),toastId:f,updateId:h,data:I,closeToast:L,isIn:false,className:p(l.className||g.toastClassName),bodyClassName:p(l.bodyClassName||g.bodyClassName),progressClassName:p(l.progressClassName||g.progressClassName),autoClose:!l.isLoading&&(w=l.autoClose,k=g.autoClose,false===w||c(w)&&w>0?w:k),deleteToast(){const e=v.get(f),{onClose:n,children:s}=e.props;u(n)&&n(isValidElement(s)&&s.props),o(y(e,"removed")),v.delete(f),r--,r<0&&(r=0),i.length>0?b(i.shift()):T();}};var w,k;$.closeButton=g.closeButton,false===l.closeButton||m(l.closeButton)?$.closeButton=l.closeButton:true===l.closeButton&&($.closeButton=!m(g.closeButton)||g.closeButton);let P=n;isValidElement(n)&&!d(n.type)?P=cloneElement(n,{closeToast:L,toastProps:$,data:I}):u(n)&&(P=n({closeToast:L,toastProps:$,data:I}));const M={content:P,props:$,staleId:_};g.limit&&g.limit>0&&r>g.limit&&N?i.push(M):c(C)?setTimeout(()=>{b(M);},C):b(M);},setProps(e){g=e;},setToggle:(e,t)=>{v.get(e).toggle=t;},isToastActive:e=>l.some(t=>t===e),getSnapshot:()=>f}}(n,e,E);v.set(n,s);const r=s.observe(o);return h.forEach(e=>_(e.content,e.options)),h=[],()=>{r(),v.delete(n);}},setProps(e){var t;null==(t=v.get(n))||t.setProps(e);},getSnapshot(){var e;return null==(e=v.get(n))?void 0:e.getSnapshot()}}}(e)).current;i(e);const l=useSyncExternalStore(o,s,s);return {getToastToRender:function(t){if(!l)return [];const n=new Map;return e.newestOnTop&&l.reverse(),l.forEach(e=>{const{position:t}=e.props;n.has(t)||n.set(t,[]),n.get(t).push(e);}),Array.from(n,e=>t(e[0],e[1]))},isToastActive:I,count:null==l?void 0:l.length}}function N(e){const[t,o]=useState(false),[a,r]=useState(false),l=useRef(null),c=useRef({start:0,delta:0,removalDistance:0,canCloseOnClick:true,canDrag:false,didMove:false}).current,{autoClose:d,pauseOnHover:u,closeToast:p,onClick:m,closeOnClick:f}=e;var g,y;function h(){o(true);}function T(){o(false);}function E(n){const o=l.current;c.canDrag&&o&&(c.didMove=true,t&&T(),c.delta="x"===e.draggableDirection?n.clientX-c.start:n.clientY-c.start,c.start!==n.clientX&&(c.canCloseOnClick=false),o.style.transform=`translate3d(${"x"===e.draggableDirection?`${c.delta}px, var(--y)`:`0, calc(${c.delta}px + var(--y))`},0)`,o.style.opacity=""+(1-Math.abs(c.delta/c.removalDistance)));}function b(){document.removeEventListener("pointermove",E),document.removeEventListener("pointerup",b);const t=l.current;if(c.canDrag&&c.didMove&&t){if(c.canDrag=false,Math.abs(c.delta)>c.removalDistance)return r(true),e.closeToast(),void e.collapseAll();t.style.transition="transform 0.2s, opacity 0.2s",t.style.removeProperty("transform"),t.style.removeProperty("opacity");}}null==(y=v.get((g={id:e.toastId,containerId:e.containerId,fn:o}).containerId||1))||y.setToggle(g.id,g.fn),useEffect(()=>{if(e.pauseOnFocusLoss)return document.hasFocus()||T(),window.addEventListener("focus",h),window.addEventListener("blur",T),()=>{window.removeEventListener("focus",h),window.removeEventListener("blur",T);}},[e.pauseOnFocusLoss]);const I={onPointerDown:function(t){if(true===e.draggable||e.draggable===t.pointerType){c.didMove=false,document.addEventListener("pointermove",E),document.addEventListener("pointerup",b);const n=l.current;c.canCloseOnClick=true,c.canDrag=true,n.style.transition="none","x"===e.draggableDirection?(c.start=t.clientX,c.removalDistance=n.offsetWidth*(e.draggablePercent/100)):(c.start=t.clientY,c.removalDistance=n.offsetHeight*(80===e.draggablePercent?1.5*e.draggablePercent:e.draggablePercent)/100);}},onPointerUp:function(t){const{top:n,bottom:o,left:s,right:a}=l.current.getBoundingClientRect();"touchend"!==t.nativeEvent.type&&e.pauseOnHover&&t.clientX>=s&&t.clientX<=a&&t.clientY>=n&&t.clientY<=o?T():h();}};return d&&u&&(I.onMouseEnter=T,e.stacked||(I.onMouseLeave=h)),f&&(I.onClick=e=>{m&&m(e),c.canCloseOnClick&&p();}),{playToast:h,pauseToast:T,isRunning:t,preventExitTransition:a,toastRef:l,eventHandlers:I}}function $(t){let{delay:n,isRunning:o,closeToast:s,type:a="default",hide:r,className:i,style:c,controlledProgress:d,progress:p,rtl:m,isIn:f,theme:g}=t;const y=r||d&&0===p,v={...c,animationDuration:`${n}ms`,animationPlayState:o?"running":"paused"};d&&(v.transform=`scaleX(${p})`);const h=clsx("Toastify__progress-bar",d?"Toastify__progress-bar--controlled":"Toastify__progress-bar--animated",`Toastify__progress-bar-theme--${g}`,`Toastify__progress-bar--${a}`,{"Toastify__progress-bar--rtl":m}),T=u(i)?i({rtl:m,type:a,defaultClassName:h}):clsx(h,i),E={[d&&p>=1?"onTransitionEnd":"onAnimationEnd"]:d&&p<1?null:()=>{f&&s();}};return React__default.createElement("div",{className:"Toastify__progress-bar--wrp","data-hidden":y},React__default.createElement("div",{className:`Toastify__progress-bar--bg Toastify__progress-bar-theme--${g} Toastify__progress-bar--${a}`}),React__default.createElement("div",{role:"progressbar","aria-hidden":y?"true":"false","aria-label":"notification timer",className:T,style:v,...E}))}let w=1;const k=()=>""+w++;function P(e){return e&&(d(e.toastId)||c(e.toastId))?e.toastId:k()}function M(e,t){return _(e,t),t.toastId}function x(e,t){return {...t,type:t&&t.type||e,toastId:P(t)}}function A(e){return (t,n)=>M(t,x(e,n))}function B(e,t){return M(e,x("default",t))}B.loading=(e,t)=>M(e,x("default",{isLoading:true,autoClose:false,closeOnClick:false,closeButton:false,draggable:false,...t})),B.promise=function(e,t,n){let o,{pending:s,error:a,success:r}=t;s&&(o=d(s)?B.loading(s,n):B.loading(s.render,{...n,...s}));const i={isLoading:null,autoClose:null,closeOnClick:null,closeButton:null,draggable:null},l=(e,t,s)=>{if(null==t)return void B.dismiss(o);const a={type:e,...i,...n,data:s},r=d(t)?{render:t}:t;return o?B.update(o,{...a,...r}):B(r.render,{...a,...r}),s},c=u(e)?e():e;return c.then(e=>l("success",r,e)).catch(e=>l("error",a,e)),c},B.success=A("success"),B.info=A("info"),B.error=A("error"),B.warning=A("warning"),B.warn=B.warning,B.dark=(e,t)=>M(e,x("default",{theme:"dark",...t})),B.dismiss=function(e){!function(e){var t;if(b()){if(null==e||d(t=e)||c(t))v.forEach(t=>{t.removeToast(e);});else if(e&&("containerId"in e||"id"in e)){const t=v.get(e.containerId);t?t.removeToast(e.id):v.forEach(t=>{t.removeToast(e.id);});}}else h=h.filter(t=>null!=e&&t.options.toastId!==e);}(e);},B.clearWaitingQueue=function(e){ void 0===e&&(e={}),v.forEach(t=>{!t.props.limit||e.containerId&&t.id!==e.containerId||t.clearQueue();});},B.isActive=I,B.update=function(e,t){ void 0===t&&(t={});const n=((e,t)=>{var n;let{containerId:o}=t;return null==(n=v.get(o||1))?void 0:n.toasts.get(e)})(e,t);if(n){const{props:o,content:s}=n,a={delay:100,...o,...t,toastId:t.toastId||e,updateId:k()};a.toastId!==e&&(a.staleId=e);const r=a.render||s;delete a.render,M(r,a);}},B.done=e=>{B.update(e,{progress:1});},B.onChange=function(e){return T.add(e),()=>{T.delete(e);}},B.play=e=>C(true,e),B.pause=e=>C(false,e);const O="undefined"!=typeof window?useLayoutEffect:useEffect,D=t=>{let{theme:n,type:o,isLoading:s,...a}=t;return React__default.createElement("svg",{viewBox:"0 0 24 24",width:"100%",height:"100%",fill:"colored"===n?"currentColor":`var(--toastify-icon-color-${o})`,...a})},z={info:function(t){return React__default.createElement(D,{...t},React__default.createElement("path",{d:"M12 0a12 12 0 1012 12A12.013 12.013 0 0012 0zm.25 5a1.5 1.5 0 11-1.5 1.5 1.5 1.5 0 011.5-1.5zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75a.25.25 0 00.25.25h.75a1 1 0 110 2z"}))},warning:function(t){return React__default.createElement(D,{...t},React__default.createElement("path",{d:"M23.32 17.191L15.438 2.184C14.728.833 13.416 0 11.996 0c-1.42 0-2.733.833-3.443 2.184L.533 17.448a4.744 4.744 0 000 4.368C1.243 23.167 2.555 24 3.975 24h16.05C22.22 24 24 22.044 24 19.632c0-.904-.251-1.746-.68-2.44zm-9.622 1.46c0 1.033-.724 1.823-1.698 1.823s-1.698-.79-1.698-1.822v-.043c0-1.028.724-1.822 1.698-1.822s1.698.79 1.698 1.822v.043zm.039-12.285l-.84 8.06c-.057.581-.408.943-.897.943-.49 0-.84-.367-.896-.942l-.84-8.065c-.057-.624.25-1.095.779-1.095h1.91c.528.005.84.476.784 1.1z"}))},success:function(t){return React__default.createElement(D,{...t},React__default.createElement("path",{d:"M12 0a12 12 0 1012 12A12.014 12.014 0 0012 0zm6.927 8.2l-6.845 9.289a1.011 1.011 0 01-1.43.188l-4.888-3.908a1 1 0 111.25-1.562l4.076 3.261 6.227-8.451a1 1 0 111.61 1.183z"}))},error:function(t){return React__default.createElement(D,{...t},React__default.createElement("path",{d:"M11.983 0a12.206 12.206 0 00-8.51 3.653A11.8 11.8 0 000 12.207 11.779 11.779 0 0011.8 24h.214A12.111 12.111 0 0024 11.791 11.766 11.766 0 0011.983 0zM10.5 16.542a1.476 1.476 0 011.449-1.53h.027a1.527 1.527 0 011.523 1.47 1.475 1.475 0 01-1.449 1.53h-.027a1.529 1.529 0 01-1.523-1.47zM11 12.5v-6a1 1 0 012 0v6a1 1 0 11-2 0z"}))},spinner:function(){return React__default.createElement("div",{className:"Toastify__spinner"})}},R=n=>{const{isRunning:o,preventExitTransition:s,toastRef:r,eventHandlers:i,playToast:c}=N(n),{closeButton:d,children:p,autoClose:m,onClick:f,type:g,hideProgressBar:y,closeToast:v,transition:h,position:T,className:E,style:b,bodyClassName:I,bodyStyle:_,progressClassName:C,progressStyle:L,updateId:w,role:k,progress:P,rtl:M,toastId:x,deleteToast:A,isIn:B,isLoading:O,closeOnClick:D,theme:R}=n,S=clsx("Toastify__toast",`Toastify__toast-theme--${R}`,`Toastify__toast--${g}`,{"Toastify__toast--rtl":M},{"Toastify__toast--close-on-click":D}),H=u(E)?E({rtl:M,position:T,type:g,defaultClassName:S}):clsx(S,E),F=function(e){let{theme:n,type:o,isLoading:s,icon:r}=e,i=null;const l={theme:n,type:o};return  false===r||(u(r)?i=r({...l,isLoading:s}):isValidElement(r)?i=cloneElement(r,l):s?i=z.spinner():(e=>e in z)(o)&&(i=z[o](l))),i}(n),X=!!P||!m,Y={closeToast:v,type:g,theme:R};let q=null;return  false===d||(q=u(d)?d(Y):isValidElement(d)?cloneElement(d,Y):function(t){let{closeToast:n,theme:o,ariaLabel:s="close"}=t;return React__default.createElement("button",{className:`Toastify__close-button Toastify__close-button--${o}`,type:"button",onClick:e=>{e.stopPropagation(),n(e);},"aria-label":s},React__default.createElement("svg",{"aria-hidden":"true",viewBox:"0 0 14 16"},React__default.createElement("path",{fillRule:"evenodd",d:"M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75L1 11.98l3.75-3.75L1 4.48 2.48 3l3.75 3.75L9.98 3l1.48 1.48-3.75 3.75z"})))}(Y)),React__default.createElement(h,{isIn:B,done:A,position:T,preventExitTransition:s,nodeRef:r,playToast:c},React__default.createElement("div",{id:x,onClick:f,"data-in":B,className:H,...i,style:b,ref:r},React__default.createElement("div",{...B&&{role:k},className:u(I)?I({type:g}):clsx("Toastify__toast-body",I),style:_},null!=F&&React__default.createElement("div",{className:clsx("Toastify__toast-icon",{"Toastify--animate-icon Toastify__zoom-enter":!O})},F),React__default.createElement("div",null,p)),q,React__default.createElement($,{...w&&!X?{key:`pb-${w}`}:{},rtl:M,theme:R,delay:m,isRunning:o,isIn:B,closeToast:v,hide:y,type:g,style:L,className:C,controlledProgress:X,progress:P||0})))},S=function(e,t){return void 0===t&&(t=false),{enter:`Toastify--animate Toastify__${e}-enter`,exit:`Toastify--animate Toastify__${e}-exit`,appendPosition:t}},H=g(S("bounce",true));g(S("slide",true));g(S("zoom"));g(S("flip"));const q={position:"top-right",transition:H,autoClose:5e3,closeButton:true,pauseOnHover:true,pauseOnFocusLoss:true,draggable:"touch",draggablePercent:80,draggableDirection:"x",role:"alert",theme:"light"};function Q(t){let o={...q,...t};const s=t.stacked,[a,r]=useState(true),c=useRef(null),{getToastToRender:d,isToastActive:m,count:f}=L(o),{className:g,style:y,rtl:v,containerId:h}=o;function T(e){const t=clsx("Toastify__toast-container",`Toastify__toast-container--${e}`,{"Toastify__toast-container--rtl":v});return u(g)?g({position:e,rtl:v,defaultClassName:t}):clsx(t,p(g))}function E(){s&&(r(true),B.play());}return O(()=>{if(s){var e;const t=c.current.querySelectorAll('[data-in="true"]'),n=12,s=null==(e=o.position)?void 0:e.includes("top");let r=0,i=0;Array.from(t).reverse().forEach((e,t)=>{const o=e;o.classList.add("Toastify__toast--stacked"),t>0&&(o.dataset.collapsed=`${a}`),o.dataset.pos||(o.dataset.pos=s?"top":"bot");const l=r*(a?.2:1)+(a?0:n*t);o.style.setProperty("--y",`${s?l:-1*l}px`),o.style.setProperty("--g",`${n}`),o.style.setProperty("--s",""+(1-(a?i:0))),r+=o.offsetHeight,i+=.025;});}},[a,f,s]),React__default.createElement("div",{ref:c,className:"Toastify",id:h,onMouseEnter:()=>{s&&(r(false),B.pause());},onMouseLeave:E},d((t,n)=>{const o=n.length?{...y}:{...y,pointerEvents:"none"};return React__default.createElement("div",{className:T(t),style:o,key:`container-${t}`},n.map(t=>{let{content:n,props:o}=t;return React__default.createElement(R,{...o,stacked:s,collapseAll:E,isIn:m(o.toastId,o.containerId),style:o.style,key:`toast-${o.key}`},n)}))}))}

var css_248z$l = ":root {\n  --toastify-color-light: #fff;\n  --toastify-color-dark: #121212;\n  --toastify-color-info: #3498db;\n  --toastify-color-success: #07bc0c;\n  --toastify-color-warning: #f1c40f;\n  --toastify-color-error: #e74c3c;\n  --toastify-color-transparent: rgba(255, 255, 255, 0.7);\n  --toastify-icon-color-info: var(--toastify-color-info);\n  --toastify-icon-color-success: var(--toastify-color-success);\n  --toastify-icon-color-warning: var(--toastify-color-warning);\n  --toastify-icon-color-error: var(--toastify-color-error);\n  --toastify-toast-width: 320px;\n  --toastify-toast-offset: 16px;\n  --toastify-toast-top: max(var(--toastify-toast-offset), env(safe-area-inset-top));\n  --toastify-toast-right: max(var(--toastify-toast-offset), env(safe-area-inset-right));\n  --toastify-toast-left: max(var(--toastify-toast-offset), env(safe-area-inset-left));\n  --toastify-toast-bottom: max(var(--toastify-toast-offset), env(safe-area-inset-bottom));\n  --toastify-toast-background: #fff;\n  --toastify-toast-min-height: 64px;\n  --toastify-toast-max-height: 800px;\n  --toastify-toast-bd-radius: 6px;\n  --toastify-font-family: sans-serif;\n  --toastify-z-index: 9999;\n  --toastify-text-color-light: #757575;\n  --toastify-text-color-dark: #fff;\n  --toastify-text-color-info: #fff;\n  --toastify-text-color-success: #fff;\n  --toastify-text-color-warning: #fff;\n  --toastify-text-color-error: #fff;\n  --toastify-spinner-color: #616161;\n  --toastify-spinner-color-empty-area: #e0e0e0;\n  --toastify-color-progress-light: linear-gradient(\n    to right,\n    #4cd964,\n    #5ac8fa,\n    #007aff,\n    #34aadc,\n    #5856d6,\n    #ff2d55\n  );\n  --toastify-color-progress-dark: #bb86fc;\n  --toastify-color-progress-info: var(--toastify-color-info);\n  --toastify-color-progress-success: var(--toastify-color-success);\n  --toastify-color-progress-warning: var(--toastify-color-warning);\n  --toastify-color-progress-error: var(--toastify-color-error);\n  --toastify-color-progress-bgo: 0.2;\n}\n\n.Toastify__toast-container {\n  z-index: var(--toastify-z-index);\n  -webkit-transform: translate3d(0, 0, var(--toastify-z-index));\n  position: fixed;\n  padding: 4px;\n  width: var(--toastify-toast-width);\n  box-sizing: border-box;\n  color: #fff;\n}\n.Toastify__toast-container--top-left {\n  top: var(--toastify-toast-top);\n  left: var(--toastify-toast-left);\n}\n.Toastify__toast-container--top-center {\n  top: var(--toastify-toast-top);\n  left: 50%;\n  transform: translateX(-50%);\n}\n.Toastify__toast-container--top-right {\n  top: var(--toastify-toast-top);\n  right: var(--toastify-toast-right);\n}\n.Toastify__toast-container--bottom-left {\n  bottom: var(--toastify-toast-bottom);\n  left: var(--toastify-toast-left);\n}\n.Toastify__toast-container--bottom-center {\n  bottom: var(--toastify-toast-bottom);\n  left: 50%;\n  transform: translateX(-50%);\n}\n.Toastify__toast-container--bottom-right {\n  bottom: var(--toastify-toast-bottom);\n  right: var(--toastify-toast-right);\n}\n\n@media only screen and (max-width : 480px) {\n  .Toastify__toast-container {\n    width: 100vw;\n    padding: 0;\n    left: env(safe-area-inset-left);\n    margin: 0;\n  }\n  .Toastify__toast-container--top-left, .Toastify__toast-container--top-center, .Toastify__toast-container--top-right {\n    top: env(safe-area-inset-top);\n    transform: translateX(0);\n  }\n  .Toastify__toast-container--bottom-left, .Toastify__toast-container--bottom-center, .Toastify__toast-container--bottom-right {\n    bottom: env(safe-area-inset-bottom);\n    transform: translateX(0);\n  }\n  .Toastify__toast-container--rtl {\n    right: env(safe-area-inset-right);\n    left: initial;\n  }\n}\n.Toastify__toast {\n  --y: 0;\n  position: relative;\n  -ms-touch-action: none;\n      touch-action: none;\n  min-height: var(--toastify-toast-min-height);\n  box-sizing: border-box;\n  margin-bottom: 1rem;\n  padding: 8px;\n  border-radius: var(--toastify-toast-bd-radius);\n  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);\n  display: -ms-flexbox;\n  display: flex;\n  -ms-flex-pack: justify;\n      justify-content: space-between;\n  max-height: var(--toastify-toast-max-height);\n  font-family: var(--toastify-font-family);\n  cursor: default;\n  direction: ltr;\n  /* webkit only issue #791 */\n  z-index: 0;\n  overflow: hidden;\n}\n.Toastify__toast--stacked {\n  position: absolute;\n  width: 100%;\n  transform: translate3d(0, var(--y), 0) scale(var(--s));\n  transition: transform 0.3s;\n}\n.Toastify__toast--stacked[data-collapsed] .Toastify__toast-body, .Toastify__toast--stacked[data-collapsed] .Toastify__close-button {\n  transition: opacity 0.1s;\n}\n.Toastify__toast--stacked[data-collapsed=false] {\n  overflow: visible;\n}\n.Toastify__toast--stacked[data-collapsed=true]:not(:last-child) > * {\n  opacity: 0;\n}\n.Toastify__toast--stacked:after {\n  content: \"\";\n  position: absolute;\n  left: 0;\n  right: 0;\n  height: calc(var(--g) * 1px);\n  bottom: 100%;\n}\n.Toastify__toast--stacked[data-pos=top] {\n  top: 0;\n}\n.Toastify__toast--stacked[data-pos=bot] {\n  bottom: 0;\n}\n.Toastify__toast--stacked[data-pos=bot].Toastify__toast--stacked:before {\n  transform-origin: top;\n}\n.Toastify__toast--stacked[data-pos=top].Toastify__toast--stacked:before {\n  transform-origin: bottom;\n}\n.Toastify__toast--stacked:before {\n  content: \"\";\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  height: 100%;\n  transform: scaleY(3);\n  z-index: -1;\n}\n.Toastify__toast--rtl {\n  direction: rtl;\n}\n.Toastify__toast--close-on-click {\n  cursor: pointer;\n}\n.Toastify__toast-body {\n  margin: auto 0;\n  -ms-flex: 1 1 auto;\n      flex: 1 1 auto;\n  padding: 6px;\n  display: -ms-flexbox;\n  display: flex;\n  -ms-flex-align: center;\n      align-items: center;\n}\n.Toastify__toast-body > div:last-child {\n  word-break: break-word;\n  -ms-flex: 1;\n      flex: 1;\n}\n.Toastify__toast-icon {\n  -webkit-margin-end: 10px;\n          margin-inline-end: 10px;\n  width: 20px;\n  -ms-flex-negative: 0;\n      flex-shrink: 0;\n  display: -ms-flexbox;\n  display: flex;\n}\n\n.Toastify--animate {\n  animation-fill-mode: both;\n  animation-duration: 0.5s;\n}\n\n.Toastify--animate-icon {\n  animation-fill-mode: both;\n  animation-duration: 0.3s;\n}\n\n@media only screen and (max-width : 480px) {\n  .Toastify__toast {\n    margin-bottom: 0;\n    border-radius: 0;\n  }\n}\n.Toastify__toast-theme--dark {\n  background: var(--toastify-color-dark);\n  color: var(--toastify-text-color-dark);\n}\n.Toastify__toast-theme--light {\n  background: var(--toastify-color-light);\n  color: var(--toastify-text-color-light);\n}\n.Toastify__toast-theme--colored.Toastify__toast--default {\n  background: var(--toastify-color-light);\n  color: var(--toastify-text-color-light);\n}\n.Toastify__toast-theme--colored.Toastify__toast--info {\n  color: var(--toastify-text-color-info);\n  background: var(--toastify-color-info);\n}\n.Toastify__toast-theme--colored.Toastify__toast--success {\n  color: var(--toastify-text-color-success);\n  background: var(--toastify-color-success);\n}\n.Toastify__toast-theme--colored.Toastify__toast--warning {\n  color: var(--toastify-text-color-warning);\n  background: var(--toastify-color-warning);\n}\n.Toastify__toast-theme--colored.Toastify__toast--error {\n  color: var(--toastify-text-color-error);\n  background: var(--toastify-color-error);\n}\n\n.Toastify__progress-bar-theme--light {\n  background: var(--toastify-color-progress-light);\n}\n.Toastify__progress-bar-theme--dark {\n  background: var(--toastify-color-progress-dark);\n}\n.Toastify__progress-bar--info {\n  background: var(--toastify-color-progress-info);\n}\n.Toastify__progress-bar--success {\n  background: var(--toastify-color-progress-success);\n}\n.Toastify__progress-bar--warning {\n  background: var(--toastify-color-progress-warning);\n}\n.Toastify__progress-bar--error {\n  background: var(--toastify-color-progress-error);\n}\n.Toastify__progress-bar-theme--colored.Toastify__progress-bar--info, .Toastify__progress-bar-theme--colored.Toastify__progress-bar--success, .Toastify__progress-bar-theme--colored.Toastify__progress-bar--warning, .Toastify__progress-bar-theme--colored.Toastify__progress-bar--error {\n  background: var(--toastify-color-transparent);\n}\n\n.Toastify__close-button {\n  color: #fff;\n  background: transparent;\n  outline: none;\n  border: none;\n  padding: 0;\n  cursor: pointer;\n  opacity: 0.7;\n  transition: 0.3s ease;\n  -ms-flex-item-align: start;\n      align-self: flex-start;\n  z-index: 1;\n}\n.Toastify__close-button--light {\n  color: #000;\n  opacity: 0.3;\n}\n.Toastify__close-button > svg {\n  fill: currentColor;\n  height: 16px;\n  width: 14px;\n}\n.Toastify__close-button:hover, .Toastify__close-button:focus {\n  opacity: 1;\n}\n\n@keyframes Toastify__trackProgress {\n  0% {\n    transform: scaleX(1);\n  }\n  100% {\n    transform: scaleX(0);\n  }\n}\n.Toastify__progress-bar {\n  position: absolute;\n  bottom: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  z-index: var(--toastify-z-index);\n  opacity: 0.7;\n  transform-origin: left;\n  border-bottom-left-radius: var(--toastify-toast-bd-radius);\n}\n.Toastify__progress-bar--animated {\n  animation: Toastify__trackProgress linear 1 forwards;\n}\n.Toastify__progress-bar--controlled {\n  transition: transform 0.2s;\n}\n.Toastify__progress-bar--rtl {\n  right: 0;\n  left: initial;\n  transform-origin: right;\n  border-bottom-left-radius: initial;\n  border-bottom-right-radius: var(--toastify-toast-bd-radius);\n}\n.Toastify__progress-bar--wrp {\n  position: absolute;\n  bottom: 0;\n  left: 0;\n  width: 100%;\n  height: 5px;\n  border-bottom-left-radius: var(--toastify-toast-bd-radius);\n}\n.Toastify__progress-bar--wrp[data-hidden=true] {\n  opacity: 0;\n}\n.Toastify__progress-bar--bg {\n  opacity: var(--toastify-color-progress-bgo);\n  width: 100%;\n  height: 100%;\n}\n\n.Toastify__spinner {\n  width: 20px;\n  height: 20px;\n  box-sizing: border-box;\n  border: 2px solid;\n  border-radius: 100%;\n  border-color: var(--toastify-spinner-color-empty-area);\n  border-right-color: var(--toastify-spinner-color);\n  animation: Toastify__spin 0.65s linear infinite;\n}\n\n@keyframes Toastify__bounceInRight {\n  from, 60%, 75%, 90%, to {\n    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n  }\n  from {\n    opacity: 0;\n    transform: translate3d(3000px, 0, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(-25px, 0, 0);\n  }\n  75% {\n    transform: translate3d(10px, 0, 0);\n  }\n  90% {\n    transform: translate3d(-5px, 0, 0);\n  }\n  to {\n    transform: none;\n  }\n}\n@keyframes Toastify__bounceOutRight {\n  20% {\n    opacity: 1;\n    transform: translate3d(-20px, var(--y), 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(2000px, var(--y), 0);\n  }\n}\n@keyframes Toastify__bounceInLeft {\n  from, 60%, 75%, 90%, to {\n    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n  }\n  0% {\n    opacity: 0;\n    transform: translate3d(-3000px, 0, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(25px, 0, 0);\n  }\n  75% {\n    transform: translate3d(-10px, 0, 0);\n  }\n  90% {\n    transform: translate3d(5px, 0, 0);\n  }\n  to {\n    transform: none;\n  }\n}\n@keyframes Toastify__bounceOutLeft {\n  20% {\n    opacity: 1;\n    transform: translate3d(20px, var(--y), 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(-2000px, var(--y), 0);\n  }\n}\n@keyframes Toastify__bounceInUp {\n  from, 60%, 75%, 90%, to {\n    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n  }\n  from {\n    opacity: 0;\n    transform: translate3d(0, 3000px, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(0, -20px, 0);\n  }\n  75% {\n    transform: translate3d(0, 10px, 0);\n  }\n  90% {\n    transform: translate3d(0, -5px, 0);\n  }\n  to {\n    transform: translate3d(0, 0, 0);\n  }\n}\n@keyframes Toastify__bounceOutUp {\n  20% {\n    transform: translate3d(0, calc(var(--y) - 10px), 0);\n  }\n  40%, 45% {\n    opacity: 1;\n    transform: translate3d(0, calc(var(--y) + 20px), 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(0, -2000px, 0);\n  }\n}\n@keyframes Toastify__bounceInDown {\n  from, 60%, 75%, 90%, to {\n    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n  }\n  0% {\n    opacity: 0;\n    transform: translate3d(0, -3000px, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(0, 25px, 0);\n  }\n  75% {\n    transform: translate3d(0, -10px, 0);\n  }\n  90% {\n    transform: translate3d(0, 5px, 0);\n  }\n  to {\n    transform: none;\n  }\n}\n@keyframes Toastify__bounceOutDown {\n  20% {\n    transform: translate3d(0, calc(var(--y) - 10px), 0);\n  }\n  40%, 45% {\n    opacity: 1;\n    transform: translate3d(0, calc(var(--y) + 20px), 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(0, 2000px, 0);\n  }\n}\n.Toastify__bounce-enter--top-left, .Toastify__bounce-enter--bottom-left {\n  animation-name: Toastify__bounceInLeft;\n}\n.Toastify__bounce-enter--top-right, .Toastify__bounce-enter--bottom-right {\n  animation-name: Toastify__bounceInRight;\n}\n.Toastify__bounce-enter--top-center {\n  animation-name: Toastify__bounceInDown;\n}\n.Toastify__bounce-enter--bottom-center {\n  animation-name: Toastify__bounceInUp;\n}\n\n.Toastify__bounce-exit--top-left, .Toastify__bounce-exit--bottom-left {\n  animation-name: Toastify__bounceOutLeft;\n}\n.Toastify__bounce-exit--top-right, .Toastify__bounce-exit--bottom-right {\n  animation-name: Toastify__bounceOutRight;\n}\n.Toastify__bounce-exit--top-center {\n  animation-name: Toastify__bounceOutUp;\n}\n.Toastify__bounce-exit--bottom-center {\n  animation-name: Toastify__bounceOutDown;\n}\n\n@keyframes Toastify__zoomIn {\n  from {\n    opacity: 0;\n    transform: scale3d(0.3, 0.3, 0.3);\n  }\n  50% {\n    opacity: 1;\n  }\n}\n@keyframes Toastify__zoomOut {\n  from {\n    opacity: 1;\n  }\n  50% {\n    opacity: 0;\n    transform: translate3d(0, var(--y), 0) scale3d(0.3, 0.3, 0.3);\n  }\n  to {\n    opacity: 0;\n  }\n}\n.Toastify__zoom-enter {\n  animation-name: Toastify__zoomIn;\n}\n\n.Toastify__zoom-exit {\n  animation-name: Toastify__zoomOut;\n}\n\n@keyframes Toastify__flipIn {\n  from {\n    transform: perspective(400px) rotate3d(1, 0, 0, 90deg);\n    animation-timing-function: ease-in;\n    opacity: 0;\n  }\n  40% {\n    transform: perspective(400px) rotate3d(1, 0, 0, -20deg);\n    animation-timing-function: ease-in;\n  }\n  60% {\n    transform: perspective(400px) rotate3d(1, 0, 0, 10deg);\n    opacity: 1;\n  }\n  80% {\n    transform: perspective(400px) rotate3d(1, 0, 0, -5deg);\n  }\n  to {\n    transform: perspective(400px);\n  }\n}\n@keyframes Toastify__flipOut {\n  from {\n    transform: translate3d(0, var(--y), 0) perspective(400px);\n  }\n  30% {\n    transform: translate3d(0, var(--y), 0) perspective(400px) rotate3d(1, 0, 0, -20deg);\n    opacity: 1;\n  }\n  to {\n    transform: translate3d(0, var(--y), 0) perspective(400px) rotate3d(1, 0, 0, 90deg);\n    opacity: 0;\n  }\n}\n.Toastify__flip-enter {\n  animation-name: Toastify__flipIn;\n}\n\n.Toastify__flip-exit {\n  animation-name: Toastify__flipOut;\n}\n\n@keyframes Toastify__slideInRight {\n  from {\n    transform: translate3d(110%, 0, 0);\n    visibility: visible;\n  }\n  to {\n    transform: translate3d(0, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideInLeft {\n  from {\n    transform: translate3d(-110%, 0, 0);\n    visibility: visible;\n  }\n  to {\n    transform: translate3d(0, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideInUp {\n  from {\n    transform: translate3d(0, 110%, 0);\n    visibility: visible;\n  }\n  to {\n    transform: translate3d(0, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideInDown {\n  from {\n    transform: translate3d(0, -110%, 0);\n    visibility: visible;\n  }\n  to {\n    transform: translate3d(0, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideOutRight {\n  from {\n    transform: translate3d(0, var(--y), 0);\n  }\n  to {\n    visibility: hidden;\n    transform: translate3d(110%, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideOutLeft {\n  from {\n    transform: translate3d(0, var(--y), 0);\n  }\n  to {\n    visibility: hidden;\n    transform: translate3d(-110%, var(--y), 0);\n  }\n}\n@keyframes Toastify__slideOutDown {\n  from {\n    transform: translate3d(0, var(--y), 0);\n  }\n  to {\n    visibility: hidden;\n    transform: translate3d(0, 500px, 0);\n  }\n}\n@keyframes Toastify__slideOutUp {\n  from {\n    transform: translate3d(0, var(--y), 0);\n  }\n  to {\n    visibility: hidden;\n    transform: translate3d(0, -500px, 0);\n  }\n}\n.Toastify__slide-enter--top-left, .Toastify__slide-enter--bottom-left {\n  animation-name: Toastify__slideInLeft;\n}\n.Toastify__slide-enter--top-right, .Toastify__slide-enter--bottom-right {\n  animation-name: Toastify__slideInRight;\n}\n.Toastify__slide-enter--top-center {\n  animation-name: Toastify__slideInDown;\n}\n.Toastify__slide-enter--bottom-center {\n  animation-name: Toastify__slideInUp;\n}\n\n.Toastify__slide-exit--top-left, .Toastify__slide-exit--bottom-left {\n  animation-name: Toastify__slideOutLeft;\n  animation-timing-function: ease-in;\n  animation-duration: 0.3s;\n}\n.Toastify__slide-exit--top-right, .Toastify__slide-exit--bottom-right {\n  animation-name: Toastify__slideOutRight;\n  animation-timing-function: ease-in;\n  animation-duration: 0.3s;\n}\n.Toastify__slide-exit--top-center {\n  animation-name: Toastify__slideOutUp;\n  animation-timing-function: ease-in;\n  animation-duration: 0.3s;\n}\n.Toastify__slide-exit--bottom-center {\n  animation-name: Toastify__slideOutDown;\n  animation-timing-function: ease-in;\n  animation-duration: 0.3s;\n}\n\n@keyframes Toastify__spin {\n  from {\n    transform: rotate(0deg);\n  }\n  to {\n    transform: rotate(360deg);\n  }\n}";
styleInject(css_248z$l);

var css_248z$k = ":root {\n  --toastify-color-light: var(--color-grey-darker);\n  --toastify-color-dark: #121212;\n  --toastify-color-info: var(--color-primary-lighter);\n  --toastify-color-success: var(--color-success);\n  --toastify-color-warning: var(--color-warning);\n  --toastify-color-error: var(--color-error);\n  --toastify-color-transparent: rgb(255 255 255 / 70%);\n  --toastify-icon-color-info: var(--toastify-color-info);\n  --toastify-icon-color-success: var(--toastify-color-success);\n  --toastify-icon-color-warning: var(--toastify-color-warning);\n  --toastify-icon-color-error: var(--toastify-color-error);\n  --toastify-toast-width: 400px;\n  --toastify-toast-background: #fff;\n  --toastify-toast-min-height: 40px;\n  --toastify-toast-max-height: 800px;\n  --toastify-font-family: sans-serif;\n  --toastify-z-index: var(--z-index-toast);\n  --toastify-text-color-light: #fff;\n  --toastify-text-color-dark: #fff;\n  --toastify-text-color-info: #fff;\n  --toastify-text-color-success: #fff;\n  --toastify-text-color-warning: #fff;\n  --toastify-text-color-error: #fff;\n  --toastify-spinner-color: #616161;\n  --toastify-spinner-color-empty-area: #e0e0e0;\n  --toastify-color-progress-light: var(--color-primary-lighter);\n  --toastify-color-progress-dark: #bb86fc;\n  --toastify-color-progress-info: var(--toastify-color-info);\n  --toastify-color-progress-success: var(--toastify-color-success);\n  --toastify-color-progress-warning: var(--toastify-color-warning);\n  --toastify-color-progress-error: var(--toastify-color-error);\n}\n";
var styles$k = {};
styleInject(css_248z$k);

/**
 *
 * Toast component:
 * A toast/snackbar component based on react-toastify
 * https://fkhadra.github.io/react-toastify
 *
 * To use it in the application, import the <Toast /> component somewhere in
 * your application (e.g. in <App /> component).
 *
 * To open a toast, import the showToast() method and pass the 'content' and optional
 * options. The options are the exact same options of the react-toastify library (ToastOptions).
 *
 * See here:
 * https://fkhadra.github.io/react-toastify/introduction
 */
var showToast = function (content, 
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
options) {
    /**
     *
     * @param type
     * @returns
     *
     * Use our own icons for the toast
     */
    function getIcon(type) {
        switch (type) {
            case 'error':
                return jsx$1(SvgCloseIcon, { color: 'var(--color-error)', strokeWidth: 5 });
            case 'warning':
                return jsx$1(SvgInfoIcon, { color: 'var(--color-warning)', strokeWidth: 5 });
            case 'success':
                return jsx$1(SvgCheckIcon, { color: 'var(--color-success)', strokeWidth: 5 });
        }
        return false;
    }
    return B(content, __assign({ icon: (options === null || options === void 0 ? void 0 : options.isLoading) ? jsx$1(LazyLoader$1, {}) : getIcon(options === null || options === void 0 ? void 0 : options.type) }, options));
};
var Toast = function (_a) {
    var className = _a.className;
    var classes = classNames$1(className);
    return (jsx$1(Q, { className: classes, toastClassName: styles$k.toast, closeButton: jsx$1(Button$1, { hierarchy: 'tertiary', icon: jsx$1(SvgCloseIcon, {}) }) }));
};

var css_248z$j = ".Skeleton-module_skeleton__DqOd3 ul {\n  margin: 0;\n  padding: 0;\n}\n\n.Skeleton-module_skeleton__DqOd3 ul li {\n  width: 100%;\n  height: var(--spacing-4);\n  list-style: none;\n  background: var(--color-grey-lighter);\n  border-radius: var(--spacing-1);\n  margin-top: 16px;\n}\n\n.Skeleton-module_skeleton__DqOd3 ul li:first-child {\n  margin: 0;\n  margin-bottom: 20px;\n  width: 40%;\n}\n\n.Skeleton-module_skeleton__DqOd3 ul li:last-child {\n  width: 60%;\n}\n";
var styles$j = {"skeleton":"Skeleton-module_skeleton__DqOd3"};
styleInject(css_248z$j);

var Skeleton = function (_a, ref) {
    var className = _a.className;
    var classes = classNames$1(styles$j.skeleton, className);
    return (jsx$1("span", { ref: ref, className: classes, children: jsxs("ul", { children: [jsx$1("li", {}), jsx$1("li", {}), jsx$1("li", {}), jsx$1("li", {})] }) }));
};
var Skeleton$1 = forwardRef(Skeleton);

var css_248z$i = ".Autocomplete-module_autocomplete-wrapper__CLwMN {\n  position: relative;\n}\n\n.Autocomplete-module_suggestions__S5mjZ {\n  position: absolute;\n  z-index: 101;\n  width: 100%;\n  background-color: var(--input-bg-color-default);\n  border-radius: 0 0 var(--input-border-radius) var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-s);\n  list-style: none;\n  margin: 0;\n  padding: 2px 0;\n  overflow-y: auto;\n  box-shadow: var(--dropdown-box-shadow);\n  left: 0;\n  top: calc(var(--input-height-mobile) + 16px);\n}\n\n@media (width >= 580px) {\n  .Autocomplete-module_suggestions__S5mjZ {\n    top: calc(var(--input-height-desktop) + 16px);\n  }\n}\n\n.Autocomplete-module_suggestions__S5mjZ li {\n  padding: 4px 8px;\n  margin: 2px;\n  cursor: pointer;\n  border-radius: var(--input-border-radius);\n}\n\n.Autocomplete-module_suggestion-selected__Adlrx {\n  background-color: var(--color-primary);\n}\n\n.Autocomplete-module_suggestion-active__JuwRK,\n.Autocomplete-module_suggestions__S5mjZ li:hover {\n  background-color: var(--color-primary-light);\n}\n";
var styles$i = {"autocomplete-wrapper":"Autocomplete-module_autocomplete-wrapper__CLwMN","suggestions":"Autocomplete-module_suggestions__S5mjZ","suggestion-selected":"Autocomplete-module_suggestion-selected__Adlrx","suggestion-active":"Autocomplete-module_suggestion-active__JuwRK"};
styleInject(css_248z$i);

var Autocomplete = function (_a, ref) {
    var suggestions = _a.suggestions, name = _a.name, defaultValue = _a.defaultValue, _b = _a.openOnActive, openOnActive = _b === void 0 ? false : _b, onChange = _a.onChange, onBlur = _a.onBlur, inputProps = _a.inputProps, className = _a.className;
    var _c = useState(null), currentSuggestion = _c[0], setCurrentSuggestion = _c[1];
    var _d = useState([]), filteredSuggestions = _d[0], setFilteredSuggestions = _d[1];
    var _e = useState(false), showSuggestions = _e[0], setShowSuggestions = _e[1];
    var _f = useState(null), value = _f[0], setValue = _f[1];
    var classes = classNames$1(styles$i['autocomplete-wrapper'], className);
    var onKeyDown = function (event) {
        if (event.key === 'Enter') {
            setCurrentSuggestion(null);
            setShowSuggestions(false);
            setValue(filteredSuggestions[currentSuggestion !== null && currentSuggestion !== void 0 ? currentSuggestion : 0]);
            event.currentTarget.blur();
            if (onChange) {
                onChange(filteredSuggestions[currentSuggestion !== null && currentSuggestion !== void 0 ? currentSuggestion : 0]);
            }
        }
        else if (event.key === 'ArrowUp' && currentSuggestion !== 0) {
            setCurrentSuggestion(currentSuggestion === null ? 0 : currentSuggestion - 1);
        }
        else if (event.key === 'ArrowDown' &&
            currentSuggestion !== filteredSuggestions.length - 1) {
            setCurrentSuggestion(currentSuggestion === null ? 0 : currentSuggestion + 1);
        }
    };
    var handleChange = function (event) {
        var userInput = event.currentTarget.value;
        var filteredSuggestions = __spreadArray$1([], suggestions, true).filter(function (suggestion) {
            return suggestion.label.toLowerCase().indexOf(userInput.toLowerCase()) > -1;
        });
        setFilteredSuggestions(filteredSuggestions);
        setShowSuggestions(!!filteredSuggestions.length);
        setValue({ label: event.currentTarget.value });
        if (onChange) {
            onChange({ label: event.currentTarget.value });
        }
    };
    var handleBlur = function (event) {
        // hack hack hack onBlur is always triggered before onClick
        setTimeout(function () { return setShowSuggestions(false); }, 100);
        if (onBlur) {
            onBlur(event);
        }
    };
    var handleFocus = function () {
        if (!filteredSuggestions.length) {
            setFilteredSuggestions(suggestions);
        }
        setShowSuggestions(openOnActive);
    };
    useEffect(function () {
        if (defaultValue && !value) {
            setValue(defaultValue);
        }
    }, [defaultValue]);
    return (jsxs("div", { ref: ref, className: classes, children: [jsx$1(Input$1, __assign({ type: 'text', name: name }, inputProps, { hasMenu: true, onChange: handleChange, onKeyDown: onKeyDown, onBlur: handleBlur, onFocus: handleFocus, value: value ? value.label : '' })), showSuggestions && value && (jsx$1("ul", { className: styles$i.suggestions, children: filteredSuggestions.map(function (suggestion, index) {
                    var _a;
                    return (jsx$1("li", { className: classNames$1((_a = {},
                            _a[styles$i['suggestion-selected']] = suggestion === value,
                            _a[styles$i['suggestion-active']] = index === currentSuggestion,
                            _a)), onClick: function () {
                            setValue(suggestion);
                            setCurrentSuggestion(null);
                            setFilteredSuggestions([]);
                            setShowSuggestions(false);
                            if (onChange) {
                                onChange(suggestion);
                            }
                        }, children: suggestion.label }, suggestion.label));
                }) }))] }));
};
var Autocomplete$1 = forwardRef(Autocomplete);

var css_248z$h = "/*\n  There are multiple ways to style the react-select field.\n  See https://react-select.com/styles\n\n  Currently we use the CSS attribute selectors $= to get the\n  react select sub-components we want to style.\n  The $='' attribute selector can be used since all react-select\n  components are named in the same way (xyz-componentName).\n  This allows us to still have the CSS styles in our css-modules\n  files and there is no need to have CSS-in-JS styling.\n\n  This might be subject of change.\n*/\n.GroupPicker-module_group-picker__csUux [class$='-control'] {\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  height: 100%;\n  min-height: 100%;\n  box-shadow: none;\n  transition: 0.3s ease all;\n  overflow-y: auto;\n  padding: 1px 0 2px;\n}\n\n.GroupPicker-module_group-picker__csUux {\n  min-height: var(--input-height-mobile);\n  height: fit-content;\n}\n\n.GroupPicker-module_group-picker__csUux.GroupPicker-module_keep-height__hmLlr {\n  height: var(--input-height-mobile);\n}\n\n@media (width >= 580px) {\n  .GroupPicker-module_group-picker__csUux {\n    min-height: calc(var(--input-height-desktop) - 2px);\n    height: fit-content;\n  }\n\n  .GroupPicker-module_group-picker__csUux.GroupPicker-module_keep-height__hmLlr {\n    height: calc(var(--input-height-desktop) - 2px);\n  }\n}\n\n/* hovered */\n.GroupPicker-module_group-picker__csUux [class$='-control']:hover {\n  border-color: var(--input-border-color-hover);\n  cursor: pointer;\n}\n\n/* focused */\n.GroupPicker-module_group-picker__csUux [class$='-control']:focus-within {\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\n\n.GroupPicker-module_group-picker__csUux [class*='-ValueContainer'] {\n  padding: 0 12px;\n}\n\n.GroupPicker-module_group-picker__csUux [class$='-indicatorSeparator'] {\n  display: none;\n}\n\n.GroupPicker-module_group-picker__csUux [class$='-indicatorContainer'] {\n  justify-content: center;\n  align-items: center;\n  padding: 0 8px;\n}\n\n.GroupPicker-module_group-picker__csUux [class$='-menu'] {\n  font-size: var(--font-size-s);\n  padding: 0 2px;\n  margin: 0;\n  box-shadow: var(--dropdown-box-shadow);\n  background-color: var(--input-bg-color-default);\n  border-radius: 0 0 var(--input-border-radius) var(--input-border-radius);\n}\n\n/* disabled */\n.GroupPicker-module_group-picker__csUux.GroupPicker-module_disabled__y1MWJ [class$='-control'] {\n  border-color: var(--input-border-color-disabled);\n  color: var(--input-text-color-disabled);\n  cursor: not-allowed;\n}\n\n/* error */\n.GroupPicker-module_group-picker__csUux.GroupPicker-module_error__GVOAG [class$='-control'] {\n  border-color: var(--input-text-color-error);\n}\n\n.GroupPicker-module_group-picker__csUux [class$='-Group']:not(:last-child) {\n  padding-bottom: 0;\n}\n\n.GroupPicker-module_group-picker__csUux [class*='menu-list'] [class*='group'] {\n  grid-auto-columns: auto;\n}\n\n.GroupPicker-module_error-message__kH9Xs {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n";
var styles$h = {"group-picker":"GroupPicker-module_group-picker__csUux","keep-height":"GroupPicker-module_keep-height__hmLlr","disabled":"GroupPicker-module_disabled__y1MWJ","error":"GroupPicker-module_error__GVOAG","error-message":"GroupPicker-module_error-message__kH9Xs"};
styleInject(css_248z$h);

var css_248z$g = ".GroupPickerGroup-module_group__auQNi {\n  display: grid;\n  gap: 4px;\n  grid-auto-columns: minmax(0, 1fr);\n  grid-auto-flow: column;\n}\n";
var styles$g = {"group":"GroupPickerGroup-module_group__auQNi"};
styleInject(css_248z$g);

var ColorPickerGroup = function (_a) {
    var innerProps = _a.innerProps, children = _a.children;
    return (jsx$1("div", __assign({ className: classNames$1(styles$g.group) }, innerProps, { children: children })));
};

var GroupPickerGroupHeading = function () {
    return jsx$1(Fragment, {});
};

var css_248z$f = ".GroupPickerMenuList-module_menu__zBYtT {\n  display: grid;\n  gap: 16px;\n  padding: 16px 16px 20px;\n}\n\n.GroupPickerMenuList-module_menu-list__AK7CY {\n  display: grid;\n  gap: 4px;\n}\n";
var styles$f = {"menu":"GroupPickerMenuList-module_menu__zBYtT","menu-list":"GroupPickerMenuList-module_menu-list__AK7CY"};
styleInject(css_248z$f);

var GroupPickerMenuList = function (_a) {
    var innerProps = _a.innerProps, children = _a.children, dropdownHeader = _a.dropdownHeader, dropdownFooter = _a.dropdownFooter;
    return (jsxs("div", { className: styles$f.menu, children: [dropdownHeader, jsx$1("div", __assign({}, innerProps, { className: styles$f['menu-list'], children: children })), dropdownFooter] }));
};

var css_248z$e = ".GroupPickerOption-module_option__Pkjnc {\n  display: block;\n  font-size: var(--option-font-size);\n  height: var(--option-height);\n  line-height: var(--option-height);\n  color: var(--option-text-color);\n  background-color: var(--option-background-color);\n  border-radius: var(--option-border-radius);\n  padding: 0 12px;\n  position: relative;\n  overflow: hidden;\n  text-align: center;\n  width: 100%;\n  cursor: pointer;\n  min-width: 40px;\n}\n\n.GroupPickerOption-module_option__Pkjnc:hover:not(.GroupPickerOption-module_selected__GZ8pI, .GroupPickerOption-module_disabled__WoMY0) {\n  background-color: var(--option-background-color-hover);\n}\n\n.GroupPickerOption-module_selected__GZ8pI {\n  background-color: var(--option-background-color-seleced);\n}\n\n.GroupPickerOption-module_disabled__WoMY0 {\n  background-color: var(--option-background-color-disabled);\n  color: var(--option-text-color-disabled);\n  cursor: default;\n}\n";
var styles$e = {"option":"GroupPickerOption-module_option__Pkjnc","selected":"GroupPickerOption-module_selected__GZ8pI","disabled":"GroupPickerOption-module_disabled__WoMY0"};
styleInject(css_248z$e);

var GroupPickerOption = function (_a) {
    var _b;
    var innerProps = _a.innerProps, isDisabled = _a.isDisabled, isSelected = _a.isSelected, label = _a.label;
    return (jsx$1("div", __assign({}, innerProps, { className: classNames$1(styles$e.option, (_b = {},
            _b[styles$e.selected] = isSelected,
            _b[styles$e.disabled] = isDisabled,
            _b)), children: label })));
};

/**
 *
 * Form input: GroupPicker
 *
 *
 * Based on React-Select (https://react-select.com/)
 *
 *
 * @param {string} name
 * @param {Array<GroupedGroupOption>} options
 * @param {string} placement
 * @param {string|number} menuWidth
 * @param {number} minMenuHeight
 * @param maxMenuHeight
 * @param defaultValue
 * @param disabled
 * @param label
 * @param noOptionsMessage
 * @param placeholder
 * @param className
 * @param error
 * @param tooltipText
 * @param onChange
 * @param onBlur
 * @param onFocus
 * @param allowCrossGroupSelect
 * @param dropdownHeader
 * @param dropdownFooter
 * @param menuIsOpen
 * @param closeMenuOnSelect
 * @param ref
 *  */
var GroupPicker = function (_a, ref) {
    var _b;
    var name = _a.name, options = _a.options, _c = _a.placement, placement = _c === void 0 ? 'bottom' : _c, _d = _a.fitMenuToOptions, fitMenuToOptions = _d === void 0 ? true : _d, _e = _a.minMenuHeight, minMenuHeight = _e === void 0 ? 100 : _e, _f = _a.maxMenuHeight, maxMenuHeight = _f === void 0 ? 500 : _f, _g = _a.keepHeight, keepHeight = _g === void 0 ? false : _g, _h = _a.labelCanOverflow, labelCanOverflow = _h === void 0 ? false : _h, _j = _a.bigHeaderGap, bigHeaderGap = _j === void 0 ? false : _j, defaultValue = _a.defaultValue, value = _a.value, disabled = _a.disabled, label = _a.label, noOptionsMessage = _a.noOptionsMessage, placeholder = _a.placeholder, className = _a.className, error = _a.error, dense = _a.dense, tooltipText = _a.tooltipText, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, _k = _a.allowCrossGroupSelect, allowCrossGroupSelect = _k === void 0 ? false : _k, dropdownHeader = _a.dropdownHeader, dropdownFooter = _a.dropdownFooter, menuIsOpen = _a.menuIsOpen, _l = _a.closeMenuOnSelect, closeMenuOnSelect = _l === void 0 ? false : _l, _m = _a.tabIndex, tabIndex = _m === void 0 ? 0 : _m;
    var inputClass = classNames$1(styles$h['group-picker'], (_b = {},
        _b[styles$h['error']] = error,
        _b[styles$h['disabled']] = disabled,
        _b[styles$h['keep-height']] = keepHeight,
        _b));
    var customStyles = {
        menu: function (provided) { return (__assign(__assign({}, provided), { width: fitMenuToOptions ? 'fit-content' : '100%' })); },
    };
    /**
     *
     * @param {MultiValue<GroupOption>} option
     *
     * Event handler that fires when ReactColorPicker onChange event
     * is invoked.
     * Sets the internal state and update onChange prop
     */
    function handleChange(option) {
        onChange(option);
    }
    function handleDisabled(option, selectValue) {
        if (selectValue[0]) {
            var firstSelectedGroupKey = options.findIndex(function (value) {
                return !!value.options.find(function (v) { return v.value === selectValue[0].value; });
            });
            var currentOptionGroupKey = options.findIndex(function (value) {
                return !!value.options.find(function (v) { return v.value === option.value; });
            });
            return firstSelectedGroupKey !== currentOptionGroupKey;
        }
        return false;
    }
    return (jsxs("div", { ref: ref, className: className, children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), jsx$1(StateManagedSelect$1, __assign({ menuPosition: 'absolute', minMenuHeight: minMenuHeight, maxMenuHeight: maxMenuHeight, menuPlacement: placement }, (!allowCrossGroupSelect && { isOptionDisabled: handleDisabled }), { styles: customStyles, components: {
                    // eslint-disable-next-line react/prop-types
                    MenuList: function (_a, props) {
                        var children = _a.children;
                        return (jsx$1(GroupPickerMenuList, __assign({}, props, { dropdownFooter: dropdownFooter, dropdownHeader: dropdownHeader, children: children })));
                    },
                    GroupHeading: GroupPickerGroupHeading,
                    Group: ColorPickerGroup,
                    Option: GroupPickerOption,
                }, className: inputClass, options: options, isMulti: true, isDisabled: disabled, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '', onChange: function (option) { return handleChange(option); }, onBlur: onBlur, onFocus: onFocus, value: value, defaultValue: defaultValue, hideSelectedOptions: false, closeMenuOnSelect: closeMenuOnSelect, autoFocus: false, noOptionsMessage: noOptionsMessage, menuIsOpen: menuIsOpen, tabIndex: tabIndex })), !dense && jsx$1("div", { className: styles$h['error-message'], children: error })] }));
};
var GroupPicker$1 = forwardRef(GroupPicker);

function useDefaultSorting(defaultValue) {
    if (defaultValue === void 0) { defaultValue = []; }
    var _a = useState(defaultValue), sorting = _a[0], setSorting = _a[1];
    var onSortingChange = function (value) {
        setSorting(value);
    };
    return { sorting: sorting, onSortingChange: onSortingChange };
}

function useDefaultColumnVisibility(defaultValue) {
    if (defaultValue === void 0) { defaultValue = {}; }
    var _a = useState(defaultValue), columnVisibility = _a[0], setColumnVisibility = _a[1];
    var onColumnVisibilityChange = function (value) {
        setColumnVisibility(value);
    };
    return { columnVisibility: columnVisibility, onColumnVisibilityChange: onColumnVisibilityChange };
}

function useDefaultSelecting(defaultValue) {
    if (defaultValue === void 0) { defaultValue = {}; }
    var _a = useState(defaultValue), rowSelection = _a[0], setRowSelection = _a[1];
    var onRowSelectionChange = function (value) {
        setRowSelection(value);
    };
    var clearSelection = function () {
        setRowSelection({});
    };
    return { rowSelection: rowSelection, onRowSelectionChange: onRowSelectionChange, clearSelection: clearSelection };
}

var css_248z$d = ".TextArea-module_text-area__f2VX0 {\n  width: 100%;\n  height: 100%;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc {\n  background-color: var(--input-bg-color-default);\n  border: 1px solid var(--input-border-color-default);\n  border-radius: var(--input-border-radius);\n  color: var(--input-text-color-default);\n  font-size: var(--font-size-m);\n  font-family: inherit;\n  transition: all 0.3s ease;\n  cursor: text;\n  padding: 6px 8px;\n  margin: 0;\n  height: 100%;\n  width: 100%;\n  max-width: 100%;\n  line-height: 1.5rem;\n  min-height: var(--input-height-mobile);\n  resize: none;\n  overflow-y: hidden;\n}\n\n@media (width >= 580px) {\n  .TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc {\n    line-height: 1.4rem;\n    min-height: var(--input-height-desktop);\n  }\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_resizable__IHQ4-,\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_expandable__Sp-gK {\n  transition: none;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_resizable__IHQ4- {\n  resize: vertical;\n  overflow: auto;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_show-scrollbar__N-53B {\n  overflow-y: auto;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_filled__1RdVd {\n  color: var(--input-text-color-filled);\n  border-color: var(--input-border-color-filled);\n  background-color: var(--input-bg-color-filled);\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc:hover {\n  color: var(--input-text-color-hover);\n  border-color: var(--input-border-color-hover);\n  background-color: var(--input-bg-color-hover);\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc:focus-visible {\n  outline: 0;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_has-focus__k4cKN {\n  color: var(--input-text-color-active);\n  border-color: var(--input-border-color-active);\n  background-color: var(--input-bg-color-active);\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_read-only__6fdjk {\n  cursor: default;\n  color: var(--input-text-color-locked);\n  border-color: var(--input-border-color-locked);\n  background-color: var(--input-bg-color-locked);\n  caret-color: transparent;\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_disabled__vlHNm {\n  cursor: not-allowed;\n  color: var(--input-text-color-disabled);\n  border-color: var(--input-border-color-disabled);\n  background-color: var(--input-bg-color-disabled);\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_error-message__G9R-7 {\n  display: block;\n  height: 12px;\n  margin-top: 2px;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n}\n\n.TextArea-module_text-area__f2VX0 .TextArea-module_input__TZAlc.TextArea-module_error__qPpHU {\n  border-color: var(--input-text-color-error);\n}\n";
var styles$d = {"text-area":"TextArea-module_text-area__f2VX0","input":"TextArea-module_input__TZAlc","resizable":"TextArea-module_resizable__IHQ4-","expandable":"TextArea-module_expandable__Sp-gK","show-scrollbar":"TextArea-module_show-scrollbar__N-53B","filled":"TextArea-module_filled__1RdVd","has-focus":"TextArea-module_has-focus__k4cKN","read-only":"TextArea-module_read-only__6fdjk","disabled":"TextArea-module_disabled__vlHNm","error-message":"TextArea-module_error-message__G9R-7","error":"TextArea-module_error__qPpHU"};
styleInject(css_248z$d);

var TextArea = function (props, ref) {
    var _a;
    var name = props.name, label = props.label, placeholder = props.placeholder, value = props.value, _b = props.rows, rows = _b === void 0 ? 2 : _b, _c = props.isResizable, isResizable = _c === void 0 ? false : _c, _d = props.isExpandable, isExpandable = _d === void 0 ? false : _d, _e = props.isScrollable, isScrollable = _e === void 0 ? false : _e, className = props.className, dense = props.dense, disabled = props.disabled, readonly = props.readonly, required = props.required, error = props.error, tooltipText = props.tooltipText, _f = props.bigHeaderGap, bigHeaderGap = _f === void 0 ? false : _f, _g = props.labelCanOverflow, labelCanOverflow = _g === void 0 ? false : _g, selectOnFocus = props.selectOnFocus, autoFocus = props.autoFocus, _h = props.tabIndex, tabIndex = _h === void 0 ? 0 : _h;
    var inputRef = useRef(null);
    var _j = useState(false), isActive = _j[0], setIsActive = _j[1];
    var classes = classNames$1(styles$d['input'], 'has-scrollbar', (_a = {},
        _a[styles$d['resizable']] = isResizable,
        _a[styles$d['expandable']] = isExpandable,
        _a[styles$d['show-scrollbar']] = isScrollable,
        _a[styles$d['multi-line']] = rows > 1,
        _a[styles$d['has-focus']] = isActive,
        _a[styles$d['disabled']] = disabled,
        _a[styles$d['read-only']] = readonly,
        _a[styles$d['filled']] = value,
        _a[styles$d['error']] = error,
        _a));
    var height = useMemo(function () {
        var _a;
        return isExpandable
            ? "".concat((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight, "px")
            : "calc((1.3rem ".concat(rows > 1 ? '' : '- 2px', ") * ").concat(rows <= 1 ? 0 : rows + 1, ")");
    }, [isExpandable, rows]);
    return (jsxs("div", { ref: ref, className: classNames$1(styles$d['text-area'], className), onClick: function () {
            return !disabled && !readonly && inputRef.current && inputRef.current.focus();
        }, children: [!dense && (jsx$1(InputHeader$1, { name: name, label: label, disabled: disabled, canOverflow: labelCanOverflow, tooltipText: tooltipText, bigGap: bigHeaderGap })), jsx$1("textarea", { ref: inputRef, id: name, name: name, className: classes, style: { height: height }, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '', onChange: !readonly ? props.onChange : undefined, onKeyDown: !readonly ? props.onKeyDown : undefined, onBlur: !readonly
                    ? function (event) {
                        if (props.onBlur)
                            props.onBlur(event);
                        setIsActive(false);
                    }
                    : undefined, onFocus: !readonly
                    ? function (event) {
                        var _a;
                        if (props.onFocus)
                            props.onFocus(event);
                        if (selectOnFocus)
                            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.select();
                        setIsActive(true);
                    }
                    : undefined, autoFocus: autoFocus, disabled: disabled, readOnly: readonly, required: required, defaultValue: value !== null && value !== void 0 ? value : '', tabIndex: tabIndex }), !dense && jsx$1("div", { className: styles$d['error-message'], children: error })] }));
};
var TextArea$1 = forwardRef(TextArea);

var css_248z$c = ".InplaceEdit-module_inplace-edit__eBQSC {\n  max-width: 100%;\n  width: fit-content;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht {\n  display: inline-flex;\n  align-items: center;\n  gap: 4px;\n  max-width: 100%;\n  background-color: var(--input-bg-color-default);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_background__6PeZy > .InplaceEdit-module_wrapper__Kk9ht {\n  background-color: var(--color-background);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC > span {\n  visibility: hidden;\n  position: absolute;\n  font-family: Inter, sans-serif;\n  top: -1000px;\n  max-width: 100%;\n  white-space: pre;\n  overflow: hidden;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-family: Inter, sans-serif;\n  flex: 1 0 auto;\n  padding: 0 var(--spacing-1);\n  margin: 0;\n  height: 100%;\n  font-size: var(--font-size-l);\n  white-space: pre;\n  min-width: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  color: var(--input-color-text);\n  border-radius: var(--input-border-radius);\n  background-color: transparent;\n}\n\n/* S T A T E S */\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9::placeholder {\n  color: var(--input-text-color-placeholder);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9,\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:active,\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus,\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus-visible,\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus-within {\n  border: none;\n  outline: none;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  color: var(--input-text-color-placeholder);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC:hover {\n  cursor: text;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC:hover > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9,\n.InplaceEdit-module_inplace-edit__eBQSC:hover > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9::placeholder,\n.InplaceEdit-module_inplace-edit__eBQSC:hover > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  color: var(--input-text-color-hover);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_readonly__llr58,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_readonly__llr58 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9::placeholder,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_readonly__llr58 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  color: var(--input-text-color);\n  cursor: default;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_readonly__llr58 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  cursor: initial;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_disabled__MV8Xx,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_disabled__MV8Xx > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_disabled__MV8Xx > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9::placeholder,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_disabled__MV8Xx > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  color: var(--input-text-color-disabled);\n  cursor: not-allowed;\n}\n\n/* S I Z E S */\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-s__IngGF:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 41px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-s__IngGF > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-s__IngGF > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-s);\n  max-width: calc(100% - 20px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-s__IngGF > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 20px;\n  height: 20px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-m__Opocq:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 44px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-m__Opocq > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-m__Opocq > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-m);\n  max-width: calc(100% - 28px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-m__Opocq > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 28px;\n  height: 28px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-l__zayjY:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 48px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-l__zayjY > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-l__zayjY > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-l);\n  max-width: calc(100% - 32px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-l__zayjY > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 32px;\n  height: 32px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xl__YEjb1:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 52px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xl__YEjb1 > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xl__YEjb1 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-xl);\n  max-width: calc(100% - 36px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xl__YEjb1 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 36px;\n  height: 36px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxl__AlSVy:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 64px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxl__AlSVy > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxl__AlSVy > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-xxl);\n  max-width: calc(100% - 48px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxl__AlSVy > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 48px;\n  height: 48px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxxl__mksm1:not(.InplaceEdit-module_dense__PZjdP) {\n  height: 68px;\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxxl__mksm1 > span,\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxxl__mksm1 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9 {\n  font-size: var(--font-size-xxxl);\n  max-width: calc(100% - 52px);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC.InplaceEdit-module_size-xxxl__mksm1 > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_icon__DqS16 > svg {\n  width: 52px;\n  height: 52px;\n}\n\n/* S T A T E S (CONTINUED) */\n.InplaceEdit-module_inplace-edit__eBQSC > .InplaceEdit-module_error__kSH9H {\n  width: 100%;\n  color: var(--color-error);\n  font-size: var(--font-size-xs);\n}\n\n.InplaceEdit-module_inplace-edit__eBQSC:not(.InplaceEdit-module_readonly__llr58) > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:active,\n.InplaceEdit-module_inplace-edit__eBQSC:not(.InplaceEdit-module_readonly__llr58) > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus,\n.InplaceEdit-module_inplace-edit__eBQSC:not(.InplaceEdit-module_readonly__llr58) > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus-visible,\n.InplaceEdit-module_inplace-edit__eBQSC:not(.InplaceEdit-module_readonly__llr58) > .InplaceEdit-module_wrapper__Kk9ht > .InplaceEdit-module_input__stSX9:focus-within {\n  background-color: var(--input-bg-color-active);\n}\n";
var styles$c = {"inplace-edit":"InplaceEdit-module_inplace-edit__eBQSC","wrapper":"InplaceEdit-module_wrapper__Kk9ht","background":"InplaceEdit-module_background__6PeZy","input":"InplaceEdit-module_input__stSX9","icon":"InplaceEdit-module_icon__DqS16","readonly":"InplaceEdit-module_readonly__llr58","disabled":"InplaceEdit-module_disabled__MV8Xx","size-s":"InplaceEdit-module_size-s__IngGF","dense":"InplaceEdit-module_dense__PZjdP","size-m":"InplaceEdit-module_size-m__Opocq","size-l":"InplaceEdit-module_size-l__zayjY","size-xl":"InplaceEdit-module_size-xl__YEjb1","size-xxl":"InplaceEdit-module_size-xxl__AlSVy","size-xxxl":"InplaceEdit-module_size-xxxl__mksm1","error":"InplaceEdit-module_error__kSH9H"};
styleInject(css_248z$c);

var InplaceEdit = function (_a, ref) {
    var _b;
    var _c = _a.size, size = _c === void 0 ? 'm' : _c, _d = _a.icon, icon = _d === void 0 ? jsx$1(SvgEditIcon, {}) : _d, _e = _a.iconPosition, iconPosition = _e === void 0 ? 'right' : _e, _f = _a.backgroundColor, backgroundColor = _f === void 0 ? 'background' : _f, defaultValue = _a.defaultValue, name = _a.name, placeholder = _a.placeholder, autoFocus = _a.autoFocus, disabled = _a.disabled, error = _a.error, readonly = _a.readonly, required = _a.required, _g = _a.tabIndex, tabIndex = _g === void 0 ? 0 : _g, dense = _a.dense, onChange = _a.onChange, onBlur = _a.onBlur, className = _a.className;
    var inputRef = useRef(null);
    var spanRef = useRef(null);
    var _h = useState(''), value = _h[0], setValue = _h[1];
    var _j = useState(0), width = _j[0], setWidth = _j[1];
    useLayoutEffect(function () {
        var _a, _b;
        setWidth((_b = (_a = spanRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) !== null && _b !== void 0 ? _b : 0);
    }, [value]);
    useEffect(function () {
        if (defaultValue && !value) {
            setValue(defaultValue);
        }
    }, [defaultValue]);
    var wrappedIcon = (jsx$1("div", { className: styles$c.icon, onClick: focus, children: icon }));
    function focus() {
        if (inputRef && inputRef.current)
            inputRef.current.focus();
    }
    var classes = classNames$1(styles$c['inplace-edit'], styles$c["size-".concat(size)], (_b = {},
        _b[styles$c.background] = backgroundColor === 'background',
        _b[styles$c.disabled] = disabled,
        _b[styles$c.readonly] = readonly,
        _b[styles$c.dense] = dense,
        _b), className);
    return (jsxs("div", { className: classes, onClick: focus, ref: ref, children: [jsx$1("span", { ref: spanRef, children: value !== '' ? value : (placeholder !== null && placeholder !== void 0 ? placeholder : '') }), jsxs("div", { className: styles$c.wrapper, children: [iconPosition === 'left' && wrappedIcon, jsx$1("input", { style: { width: "".concat(width + 10, "px") }, ref: inputRef, className: styles$c.input, type: 'text', name: name, placeholder: placeholder, value: value, autoFocus: autoFocus, disabled: disabled, readOnly: readonly, required: required, tabIndex: tabIndex, onInput: function (e) {
                            var value = e.target.value;
                            setValue(value);
                            onChange && onChange(value);
                        }, onKeyDown: function (e) {
                            var _a;
                            if (e.key === 'Enter' || e.key === 'Tabulator') {
                                e.stopPropagation();
                                onChange && onChange(value);
                                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                            }
                        }, onBlur: function () { return onBlur && onBlur(value); } }), iconPosition === 'right' && wrappedIcon] }), error && jsx$1("div", { className: styles$c.error, children: error })] }));
};
var InplaceEdit$1 = forwardRef(InplaceEdit);

var css_248z$b = ".RadioGroup-module_radio-button-wrapper__z5ScT {\n  width: fit-content;\n  margin: 0;\n  padding: 0;\n  border: none;\n}\n\n.RadioGroup-module_item__bmplD {\n  height: var(--input-height-mobile);\n  width: fit-content;\n  display: flex;\n  gap: 8px;\n  align-items: center;\n}\n\n.RadioGroup-module_button__9JqTY {\n  appearance: none;\n  background-color: var(--input-bg-color-default);\n  box-shadow: none;\n  border: 1px solid var(--input-border-color-default);\n  border-radius: 64px;\n  width: var(--checkbox-input-size-mobile);\n  height: var(--checkbox-input-size-mobile);\n  transition: 0.3s all;\n  cursor: pointer;\n}\n\n.RadioGroup-module_label__XKDc3 {\n  display: flex;\n  font-size: var(--checkbox-text-size-mobile);\n  color: var(--input-text-color-default);\n  line-height: var(--input-height-mobile);\n  cursor: pointer;\n  user-select: none;\n  transition: 0.3s all;\n  max-width: 320px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.RadioGroup-module_tooltip-icon__SQ0VO {\n  width: 20px;\n  height: 20px;\n  margin-bottom: 2px;\n}\n\n.RadioGroup-module_error-message__EKxHs {\n  height: 1rem;\n  width: fit-content;\n  color: var(--input-text-color-error);\n  font-size: var(--font-size-xs);\n  max-width: 320px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/* checked */\n.RadioGroup-module_button__9JqTY:checked {\n  background-color: var(--color-white);\n  box-shadow: inset 0 0 0 6px var(--input-bg-color-checked);\n  border: 1px solid var(--input-border-color-checked);\n}\n\n/* disabled */\n.RadioGroup-module_label__XKDc3.RadioGroup-module_disabled__ANyS7 {\n  color: var(--input-text-color-disabled);\n  cursor: not-allowed;\n}\n\n.RadioGroup-module_tooltip-icon__SQ0VO.RadioGroup-module_disabled__ANyS7 {\n  color: var(--input-text-color-disabled);\n}\n\n.RadioGroup-module_button__9JqTY:disabled {\n  border-color: var(--input-border-color-disabled);\n  cursor: not-allowed;\n}\n\n.RadioGroup-module_button__9JqTY:disabled.RadioGroup-module_button__9JqTY:disabled:checked {\n  border-color: var(--input-border-color-disabled);\n  box-shadow: inset 0 0 0 6px var(--input-border-color-disabled);\n}\n\n/* readonly */\n.RadioGroup-module_label__XKDc3.RadioGroup-module_readonly__AwOq-:not(.RadioGroup-module_disabled__ANyS7) {\n  color: var(--input-text-color-readonly);\n  cursor: initial;\n}\n\n.RadioGroup-module_tooltip-icon__SQ0VO.RadioGroup-module_readonly__AwOq- {\n  color: var(--input-text-color-readonly);\n}\n\n.RadioGroup-module_button__9JqTY.RadioGroup-module_readonly__AwOq- {\n  cursor: initial;\n}\n\n.RadioGroup-module_button__9JqTY.RadioGroup-module_readonly__AwOq-:checked {\n  background-color: var(--input-bg-color-readonly);\n}\n\n/* hover  */\n.RadioGroup-module_button__9JqTY:hover:not(:disabled, .RadioGroup-module_readonly__AwOq-) {\n  border-color: var(--input-border-color-hover);\n}\n\n.RadioGroup-module_button__9JqTY:checked:hover:not(:disabled, .RadioGroup-module_readonly__AwOq-) {\n  border-color: var(--input-border-color-checked);\n}\n\n.RadioGroup-module_label__XKDc3:hover:not(.RadioGroup-module_disabled__ANyS7, .RadioGroup-module_readonly__AwOq-) {\n  color: var(--input-text-color-hover);\n}\n\n@media (width >= 580px) {\n  .RadioGroup-module_button__9JqTY {\n    width: var(--checkbox-input-size-desktop);\n    height: var(--checkbox-input-size-desktop);\n  }\n\n  .RadioGroup-module_label__XKDc3 {\n    font-size: var(--checkbox-text-size-desktop);\n    line-height: var(--input-height-desktop);\n  }\n\n  .RadioGroup-module_button__9JqTY::before {\n    width: var(--checkbox-input-size-desktop);\n    height: var(--checkbox-input-size-desktop);\n  }\n\n  .RadioGroup-module_button__9JqTY:checked {\n    border-color: var(--input-border-color-checked);\n    box-shadow: inset 0 0 0 4px var(--input-bg-color-checked);\n  }\n\n  .RadioGroup-module_button__9JqTY:disabled {\n    border-color: var(--input-border-color-disabled);\n  }\n\n  .RadioGroup-module_button__9JqTY:disabled.RadioGroup-module_button__9JqTY:disabled:checked {\n    border-color: var(--input-border-color-disabled);\n    box-shadow: inset 0 0 0 4px var(--input-border-color-disabled);\n  }\n}\n";
var styles$b = {"radio-button-wrapper":"RadioGroup-module_radio-button-wrapper__z5ScT","item":"RadioGroup-module_item__bmplD","button":"RadioGroup-module_button__9JqTY","label":"RadioGroup-module_label__XKDc3","tooltip-icon":"RadioGroup-module_tooltip-icon__SQ0VO","error-message":"RadioGroup-module_error-message__EKxHs","disabled":"RadioGroup-module_disabled__ANyS7","readonly":"RadioGroup-module_readonly__AwOq-"};
styleInject(css_248z$b);

var RadioGroup = function (_a, ref) {
    var options = _a.options, value = _a.value, name = _a.name, disabled = _a.disabled, readonly = _a.readonly, required = _a.required, error = _a.error, _b = _a.tabIndex, tabIndex = _b === void 0 ? 0 : _b, onChange = _a.onChange, className = _a.className, style = _a.style;
    var wrapperClass = classNames$1(styles$b['radio-button-wrapper'], className);
    return (jsxs("fieldset", { ref: ref, style: style, className: wrapperClass, children: [options.length > 0 &&
                options.map(function (option) {
                    var _a, _b, _c;
                    return (jsxs("div", { className: styles$b.item, children: [jsx$1("input", { value: option.value, type: 'radio', className: classNames$1(styles$b.button, (_a = {},
                                    _a[styles$b['disabled']] = disabled || option.disabled,
                                    _a[styles$b['readonly']] = readonly || option.readonly,
                                    _a)), name: name, id: option.value, title: option.label, disabled: disabled ? true : option.disabled, readOnly: readonly ? true : option.readonly, required: required, checked: option.value === value, onChange: function (event) {
                                    !disabled &&
                                        !option.disabled &&
                                        !readonly &&
                                        !option.readonly &&
                                        onChange &&
                                        onChange(event.target.value);
                                }, tabIndex: tabIndex }), option.label && (jsx$1("label", { htmlFor: disabled || option.disabled || readonly || option.readonly
                                    ? undefined
                                    : option.value, className: classNames$1(styles$b.label, (_b = {},
                                    _b[styles$b['disabled']] = disabled || option.disabled,
                                    _b[styles$b['readonly']] = readonly || option.readonly,
                                    _b)), children: option.label })), option.tooltipText && (jsx$1(Tooltip$1, { usePortal: true, content: option.tooltipText, children: jsx$1(SvgInfoIcon, { className: classNames$1(styles$b['tooltip-icon'], (_c = {},
                                        _c[styles$b['disabled']] = disabled || option.disabled,
                                        _c[styles$b['readonly']] = readonly || option.readonly,
                                        _c)) }) }))] }, option.value));
                }), error && jsx$1("div", { className: styles$b['error-message'], children: error })] }));
};
var RadioGroup$1 = forwardRef(RadioGroup);

var css_248z$a = ".ContextMenu-module_list__1WuV3 {\n  margin: 0;\n  padding: 4px;\n  text-align: left;\n}\n\n.ContextMenu-module_list-item__kzfnM {\n  padding: 4px 8px;\n  display: flex;\n  align-items: center;\n  font-size: var(--font-size-s);\n  width: 100%;\n  overflow: hidden;\n}\n\n.ContextMenu-module_list-item__kzfnM:first-child {\n  border-top-left-radius: 4px;\n  border-top-right-radius: 4px;\n}\n\n.ContextMenu-module_list-item__kzfnM:last-child {\n  border-bottom-left-radius: 4px;\n  border-bottom-right-radius: 4px;\n}\n\n.ContextMenu-module_list-item__kzfnM:hover,\n.ContextMenu-module_list-item__kzfnM:focus {\n  background-color: var(--color-grey-lightest);\n  cursor: pointer;\n  outline: none;\n}\n\n.ContextMenu-module_header__IDGDA {\n  font-size: var(--context-menu-font-size-header);\n  padding: 8px;\n}\n\n.ContextMenu-module_header__IDGDA:hover {\n  background-color: var(--color-white);\n  cursor: initial;\n}\n\n.ContextMenu-module_error__9TEK5 {\n  color: var(--color-error);\n}\n\n.ContextMenu-module_warning__uliAX {\n  color: var(--color-warning);\n}\n\n.ContextMenu-module_primary__7KMQC {\n  color: var(--color-primary);\n}\n\n.ContextMenu-module_primary__7KMQC:hover {\n  color: var(--color-white);\n  background-color: var(--color-primary);\n}\n\n.ContextMenu-module_ruler__-ldCo {\n  height: 1px;\n  background-color: var(--color-grey-light);\n  width: 100%;\n}\n\n.ContextMenu-module_disabled__BPl7w,\n.ContextMenu-module_disabled__BPl7w:hover {\n  cursor: not-allowed;\n  color: var(--color-grey-lighter);\n  background-color: transparent;\n}\n\n.ContextMenu-module_right-slot__F6ST7 {\n  margin-left: auto;\n  padding-left: 20px;\n}\n\n.ContextMenu-module_tooltip__7Q-pC {\n  margin-left: 4px;\n  width: 18px;\n  height: 18px;\n}\n\n.ContextMenu-module_disabled__BPl7w svg {\n  color: var(--color-grey-lighter);\n}\n\n.ContextMenu-module_tooltip__7Q-pC span svg {\n  height: 100%;\n  width: 100%;\n}\n\n.ContextMenu-module_content__kkPVR {\n  color: var(--color-text);\n  max-width: var(--context-menu-max-width);\n  padding: 8px;\n  background-color: var(--color-white);\n  box-shadow: var(--dropdown-box-shadow);\n  border-radius: var(--border-radius-s);\n  font-size: var(--context-menu-font-size);\n  z-index: var(--z-index-dropdown);\n  user-select: none;\n}\n\n.ContextMenu-module_content__kkPVR.ContextMenu-module_no-padding__Gg6zo {\n  padding: 0;\n}\n";
var styles$a = {"list-item":"ContextMenu-module_list-item__kzfnM","header":"ContextMenu-module_header__IDGDA","error":"ContextMenu-module_error__9TEK5","warning":"ContextMenu-module_warning__uliAX","primary":"ContextMenu-module_primary__7KMQC","ruler":"ContextMenu-module_ruler__-ldCo","disabled":"ContextMenu-module_disabled__BPl7w","right-slot":"ContextMenu-module_right-slot__F6ST7","tooltip":"ContextMenu-module_tooltip__7Q-pC","content":"ContextMenu-module_content__kkPVR","no-padding":"ContextMenu-module_no-padding__Gg6zo"};
styleInject(css_248z$a);

var CONTEXT_MENU_NAME = "ContextMenu";
var [createContextMenuContext, createContextMenuScope] = createContextScope(CONTEXT_MENU_NAME, [
  createMenuScope
]);
var useMenuScope = createMenuScope();
var [ContextMenuProvider, useContextMenuContext] = createContextMenuContext(CONTEXT_MENU_NAME);
var ContextMenu$1 = (props) => {
  const { __scopeContextMenu, children, onOpenChange, dir, modal = true } = props;
  const [open, setOpen] = React.useState(false);
  const menuScope = useMenuScope(__scopeContextMenu);
  const handleOpenChangeProp = useCallbackRef$1(onOpenChange);
  const handleOpenChange = React.useCallback(
    (open2) => {
      setOpen(open2);
      handleOpenChangeProp(open2);
    },
    [handleOpenChangeProp]
  );
  return /* @__PURE__ */ jsx$1(
    ContextMenuProvider,
    {
      scope: __scopeContextMenu,
      open,
      onOpenChange: handleOpenChange,
      modal,
      children: /* @__PURE__ */ jsx$1(
        Root3,
        {
          ...menuScope,
          dir,
          open,
          onOpenChange: handleOpenChange,
          modal,
          children
        }
      )
    }
  );
};
ContextMenu$1.displayName = CONTEXT_MENU_NAME;
var TRIGGER_NAME = "ContextMenuTrigger";
var ContextMenuTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, disabled = false, ...triggerProps } = props;
    const context = useContextMenuContext(TRIGGER_NAME, __scopeContextMenu);
    const menuScope = useMenuScope(__scopeContextMenu);
    const pointRef = React.useRef({ x: 0, y: 0 });
    const virtualRef = React.useRef({
      getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...pointRef.current })
    });
    const longPressTimerRef = React.useRef(0);
    const clearLongPress = React.useCallback(
      () => window.clearTimeout(longPressTimerRef.current),
      []
    );
    const handleOpen = (event) => {
      pointRef.current = { x: event.clientX, y: event.clientY };
      context.onOpenChange(true);
    };
    React.useEffect(() => clearLongPress, [clearLongPress]);
    React.useEffect(() => void (disabled && clearLongPress()), [disabled, clearLongPress]);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx$1(Anchor2, { ...menuScope, virtualRef }),
      /* @__PURE__ */ jsx$1(
        Primitive.span,
        {
          "data-state": context.open ? "open" : "closed",
          "data-disabled": disabled ? "" : void 0,
          ...triggerProps,
          ref: forwardedRef,
          style: { WebkitTouchCallout: "none", ...props.style },
          onContextMenu: disabled ? props.onContextMenu : composeEventHandlers(props.onContextMenu, (event) => {
            clearLongPress();
            handleOpen(event);
            event.preventDefault();
          }),
          onPointerDown: disabled ? props.onPointerDown : composeEventHandlers(
            props.onPointerDown,
            whenTouchOrPen((event) => {
              clearLongPress();
              longPressTimerRef.current = window.setTimeout(() => handleOpen(event), 700);
            })
          ),
          onPointerMove: disabled ? props.onPointerMove : composeEventHandlers(props.onPointerMove, whenTouchOrPen(clearLongPress)),
          onPointerCancel: disabled ? props.onPointerCancel : composeEventHandlers(props.onPointerCancel, whenTouchOrPen(clearLongPress)),
          onPointerUp: disabled ? props.onPointerUp : composeEventHandlers(props.onPointerUp, whenTouchOrPen(clearLongPress))
        }
      )
    ] });
  }
);
ContextMenuTrigger.displayName = TRIGGER_NAME;
var PORTAL_NAME = "ContextMenuPortal";
var ContextMenuPortal = (props) => {
  const { __scopeContextMenu, ...portalProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(Portal, { ...menuScope, ...portalProps });
};
ContextMenuPortal.displayName = PORTAL_NAME;
var CONTENT_NAME = "ContextMenuContent";
var ContextMenuContent = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, ...contentProps } = props;
    const context = useContextMenuContext(CONTENT_NAME, __scopeContextMenu);
    const menuScope = useMenuScope(__scopeContextMenu);
    const hasInteractedOutsideRef = React.useRef(false);
    return /* @__PURE__ */ jsx$1(
      Content2$2,
      {
        ...menuScope,
        ...contentProps,
        ref: forwardedRef,
        side: "right",
        sideOffset: 2,
        align: "start",
        onCloseAutoFocus: (event) => {
          props.onCloseAutoFocus?.(event);
          if (!event.defaultPrevented && hasInteractedOutsideRef.current) {
            event.preventDefault();
          }
          hasInteractedOutsideRef.current = false;
        },
        onInteractOutside: (event) => {
          props.onInteractOutside?.(event);
          if (!event.defaultPrevented && !context.modal) hasInteractedOutsideRef.current = true;
        },
        style: {
          ...props.style,
          // re-namespace exposed content custom properties
          ...{
            "--radix-context-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
            "--radix-context-menu-content-available-width": "var(--radix-popper-available-width)",
            "--radix-context-menu-content-available-height": "var(--radix-popper-available-height)",
            "--radix-context-menu-trigger-width": "var(--radix-popper-anchor-width)",
            "--radix-context-menu-trigger-height": "var(--radix-popper-anchor-height)"
          }
        }
      }
    );
  }
);
ContextMenuContent.displayName = CONTENT_NAME;
var GROUP_NAME = "ContextMenuGroup";
var ContextMenuGroup = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, ...groupProps } = props;
    const menuScope = useMenuScope(__scopeContextMenu);
    return /* @__PURE__ */ jsx$1(Group, { ...menuScope, ...groupProps, ref: forwardedRef });
  }
);
ContextMenuGroup.displayName = GROUP_NAME;
var LABEL_NAME = "ContextMenuLabel";
var ContextMenuLabel = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, ...labelProps } = props;
    const menuScope = useMenuScope(__scopeContextMenu);
    return /* @__PURE__ */ jsx$1(Label, { ...menuScope, ...labelProps, ref: forwardedRef });
  }
);
ContextMenuLabel.displayName = LABEL_NAME;
var ITEM_NAME = "ContextMenuItem";
var ContextMenuItem = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, ...itemProps } = props;
    const menuScope = useMenuScope(__scopeContextMenu);
    return /* @__PURE__ */ jsx$1(Item2$2, { ...menuScope, ...itemProps, ref: forwardedRef });
  }
);
ContextMenuItem.displayName = ITEM_NAME;
var CHECKBOX_ITEM_NAME = "ContextMenuCheckboxItem";
var ContextMenuCheckboxItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...checkboxItemProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(CheckboxItem, { ...menuScope, ...checkboxItemProps, ref: forwardedRef });
});
ContextMenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME;
var RADIO_GROUP_NAME = "ContextMenuRadioGroup";
var ContextMenuRadioGroup = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...radioGroupProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(RadioGroup$2, { ...menuScope, ...radioGroupProps, ref: forwardedRef });
});
ContextMenuRadioGroup.displayName = RADIO_GROUP_NAME;
var RADIO_ITEM_NAME = "ContextMenuRadioItem";
var ContextMenuRadioItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...radioItemProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(RadioItem, { ...menuScope, ...radioItemProps, ref: forwardedRef });
});
ContextMenuRadioItem.displayName = RADIO_ITEM_NAME;
var INDICATOR_NAME = "ContextMenuItemIndicator";
var ContextMenuItemIndicator = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...itemIndicatorProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(ItemIndicator, { ...menuScope, ...itemIndicatorProps, ref: forwardedRef });
});
ContextMenuItemIndicator.displayName = INDICATOR_NAME;
var SEPARATOR_NAME = "ContextMenuSeparator";
var ContextMenuSeparator = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...separatorProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(Separator, { ...menuScope, ...separatorProps, ref: forwardedRef });
});
ContextMenuSeparator.displayName = SEPARATOR_NAME;
var ARROW_NAME = "ContextMenuArrow";
var ContextMenuArrow = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeContextMenu, ...arrowProps } = props;
    const menuScope = useMenuScope(__scopeContextMenu);
    return /* @__PURE__ */ jsx$1(Arrow2, { ...menuScope, ...arrowProps, ref: forwardedRef });
  }
);
ContextMenuArrow.displayName = ARROW_NAME;
var SUB_NAME = "ContextMenuSub";
var ContextMenuSub = (props) => {
  const { __scopeContextMenu, children, onOpenChange, open: openProp, defaultOpen } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx$1(Sub, { ...menuScope, open, onOpenChange: setOpen, children });
};
ContextMenuSub.displayName = SUB_NAME;
var SUB_TRIGGER_NAME = "ContextMenuSubTrigger";
var ContextMenuSubTrigger = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...triggerItemProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(SubTrigger, { ...menuScope, ...triggerItemProps, ref: forwardedRef });
});
ContextMenuSubTrigger.displayName = SUB_TRIGGER_NAME;
var SUB_CONTENT_NAME = "ContextMenuSubContent";
var ContextMenuSubContent = React.forwardRef((props, forwardedRef) => {
  const { __scopeContextMenu, ...subContentProps } = props;
  const menuScope = useMenuScope(__scopeContextMenu);
  return /* @__PURE__ */ jsx$1(
    SubContent,
    {
      ...menuScope,
      ...subContentProps,
      ref: forwardedRef,
      style: {
        ...props.style,
        // re-namespace exposed content custom properties
        ...{
          "--radix-context-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
          "--radix-context-menu-content-available-width": "var(--radix-popper-available-width)",
          "--radix-context-menu-content-available-height": "var(--radix-popper-available-height)",
          "--radix-context-menu-trigger-width": "var(--radix-popper-anchor-width)",
          "--radix-context-menu-trigger-height": "var(--radix-popper-anchor-height)"
        }
      }
    }
  );
});
ContextMenuSubContent.displayName = SUB_CONTENT_NAME;
function whenTouchOrPen(handler) {
  return (event) => event.pointerType !== "mouse" ? handler(event) : void 0;
}
var Root2 = ContextMenu$1;
var Trigger = ContextMenuTrigger;
var Portal2 = ContextMenuPortal;
var Content2 = ContextMenuContent;
var Item2 = ContextMenuItem;
var Separator2 = ContextMenuSeparator;
var Sub2 = ContextMenuSub;
var SubTrigger2 = ContextMenuSubTrigger;
var SubContent2 = ContextMenuSubContent;

/**
 * @param usePortal
 * @param {Array} data - Dropdown Content
 * @param children
 * @param noPadding
 * @param disabled
 * @param parent
 * @param onClose
 * @param className
 * @param onOpenChange
 */
var ContextMenu = function (_a) {
    var _b;
    var data = _a.data, children = _a.children, _c = _a.noPadding, noPadding = _c === void 0 ? true : _c, disabled = _a.disabled, _d = _a.usePortal, usePortal = _d === void 0 ? true : _d, _e = _a.parent, parent = _e === void 0 ? document.getElementById('root') : _e, onClose = _a.onClose, className = _a.className, onOpenChange = _a.onOpenChange;
    var contentClasses = classNames$1(styles$a.content, (_b = {},
        _b[styles$a['no-padding']] = noPadding,
        _b), className);
    var singleItem = function (item, index) {
        var _a, _b, _c;
        switch (item.type) {
            case 'ruler': {
                return (jsx$1(Separator2, { className: styles$a.ruler }, index));
            }
            case 'switch': {
                return (jsxs(Item2, { className: classNames$1(styles$a['list-item'], (_a = {}, _a[styles$a['disabled']] = item.disabled, _a), item.className), onSelect: function (e) {
                        e.preventDefault();
                        if (!item.disabled && item.onValueChange) {
                            item.onValueChange(!item.value);
                        }
                    }, children: [item.label, item.tooltip && (jsx$1("div", { className: styles$a.tooltip, children: jsx$1(Tooltip$1, { content: item.tooltip, children: jsx$1(SvgInfoIcon, { className: styles$a['tooltip-icon'] }) }) })), jsx$1("div", { className: styles$a['right-slot'], children: jsx$1(Switch$1, { checked: item.value, disabled: item.disabled === true, size: 'small', name: "".concat(item.label).concat(index), onChange: function (e) {
                                    e.preventDefault();
                                    if (!item.disabled && item.onValueChange) {
                                        item.onValueChange(item.value);
                                    }
                                } }) })] }, index));
            }
            case 'component': {
                return (jsx$1("div", { className: classNames$1(styles$a['list-item'], (_b = {},
                        _b[styles$a['disabled']] = item.disabled,
                        _b), item.className), role: 'menuitem', children: item.node }, index));
            }
            case 'header': {
                return (jsx$1(Item2, { className: classNames$1(styles$a['list-item'], styles$a.header, item.className), children: item.label }, index));
            }
            default: {
                return (jsx$1(Item2, { onClick: !item.disabled ? function (event) { var _a; return (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, event); } : undefined, className: classNames$1(styles$a['list-item'], (_c = {},
                        _c[styles$a['warning']] = item.type === 'warning',
                        _c[styles$a['error']] = item.type === 'error',
                        _c[styles$a['primary']] = item.type === 'primary',
                        _c[styles$a['disabled']] = item.disabled,
                        _c), item.className), children: item.label }, index));
            }
        }
    };
    var multiItem = function (items, subIndex) {
        return items.map(function (item, index) {
            var _a;
            if (item.subContent) {
                return (jsxs(Sub2, { defaultOpen: item.defaultOpen, children: [jsxs(SubTrigger2, { disabled: item.disabled || item.subContent.length === 0, className: classNames$1(styles$a['list-item'], (_a = {},
                                _a[styles$a['warning']] = item.type === 'warning',
                                _a[styles$a['error']] = item.type === 'error',
                                _a[styles$a['primary']] = item.type === 'primary',
                                _a[styles$a['disabled']] = item.disabled || item.subContent.length === 0,
                                _a), item.className), children: [item.label, jsx$1("div", { className: styles$a['right-slot'], children: jsx$1(SvgArrowRightIcon, { className: 'no-size', fontSize: '16px', strokeWidth: 5 }) })] }), usePortal ? (jsx$1(Portal2, { container: parent !== null && parent !== void 0 ? parent : document.body, children: jsx$1(SubContent2, { className: contentClasses, sideOffset: -5, alignOffset: 0, children: multiItem(item.subContent) }) })) : (jsx$1(SubContent2, { className: contentClasses, sideOffset: -5, alignOffset: 0, children: multiItem(item.subContent) }))] }, index));
            }
            return singleItem(item, index);
        });
    };
    var content = (jsx$1(Content2, { onInteractOutside: function (e) {
            if (onClose)
                onClose(e);
        }, onEscapeKeyDown: function (e) {
            if (onClose)
                onClose(e);
        }, className: contentClasses, children: multiItem(data) }));
    return (jsxs(Root2, { onOpenChange: onOpenChange, children: [jsx$1(Trigger, { asChild: true, disabled: disabled, children: children }), usePortal ? (jsx$1(Portal2, { container: parent !== null && parent !== void 0 ? parent : document.body, children: content })) : (content)] }));
};

var ErrorState = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var _j = _a.type, type = _j === void 0 ? 'error' : _j, settings = _a.settings, title = _a.title, pageTitle = _a.pageTitle, subtitle = _a.subtitle, showSubtitle = _a.showSubtitle, useLightColor = _a.useLightColor, showBackButton = _a.showBackButton, onNavigateBack = _a.onNavigateBack, className = _a.className;
    var autoTitle = type === 'notFound'
        ? 'Diese Seite wurde nicht gefunden'
        : type === 'forbidden'
            ? "Sie haben keine Berechtigung, um auf ".concat(pageTitle !== null && pageTitle !== void 0 ? pageTitle : 'diesen Bereich', " zugreifen zu k\u00F6nnen")
            : 'Etwas ist schief gelaufen';
    var autoSubtitle = type === 'notFound'
        ? 'Bitte überprüfen Sie die URL'
        : type === 'forbidden'
            ? 'Wenden Sie sich an Ihre Administration, um Zugang zu erlangen'
            : 'Das tut uns sehr leid';
    var icon = type === 'notFound' ? (jsx$1(SvgQuestionmarkIcon, {})) : type === 'forbidden' ? (jsx$1(SvgInvisibleIcon, {})) : (jsx$1(SvgExclamationmarkIcon, {}));
    return (jsx$1(EmptyState$1, { className: className, icon: icon, iconColor: useLightColor ? 'var(--color-error-light)' : 'var(--color-error)', size: 'big', title: title !== null && title !== void 0 ? title : autoTitle, subtitle: showSubtitle ? (subtitle !== null && subtitle !== void 0 ? subtitle : autoSubtitle) : undefined, forcedHeight: (_b = settings === null || settings === void 0 ? void 0 : settings.forcedHeight) !== null && _b !== void 0 ? _b : undefined, forcedWidth: (_c = settings === null || settings === void 0 ? void 0 : settings.forcedWidth) !== null && _c !== void 0 ? _c : undefined, maxWidth: (_d = settings === null || settings === void 0 ? void 0 : settings.maxWidth) !== null && _d !== void 0 ? _d : '400px', backgroundColor: (_e = settings === null || settings === void 0 ? void 0 : settings.backgroundColor) !== null && _e !== void 0 ? _e : undefined, showButton: showBackButton, buttonIcon: jsx$1(SvgArrowHeadLeftIcon, {}), buttonText: 'Zur\u00FCck', onButtonClick: onNavigateBack, padding: (_f = settings === null || settings === void 0 ? void 0 : settings.padding) !== null && _f !== void 0 ? _f : undefined, position: (_g = settings === null || settings === void 0 ? void 0 : settings.position) !== null && _g !== void 0 ? _g : undefined, fitParent: (_h = settings === null || settings === void 0 ? void 0 : settings.fitParent) !== null && _h !== void 0 ? _h : true }));
};

var css_248z$9 = ".Grid-module_grid__-FAfV {\n  width: 100%;\n  height: 100%;\n  display: flex;\n  flex-direction: column;\n}\n\n@media screen and (width >= 580px) {\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-phone']\n    > div[class*='row']\n    > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-phone']\n    > div[class*='row']\n    > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-phone']\n    > div[class*='row']\n    > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n}\n\n@media screen and (width >= 770px) {\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-tablet']\n    > div[class*='row']\n    > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-tablet']\n    > div[class*='row']\n    > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row'][class*='-tablet']\n    > div[class*='row']\n    > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n}\n\n@media screen and (width >= 1024px) {\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column'][class*='padding-right'] {\n    padding-right: calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column'][class*='padding-left'] {\n    padding-left: calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column'][class*='offset-right'] {\n    padding-right: calc(var(--grid-form-gap) / 2);\n  }\n\n  .Grid-module_grid__-FAfV.Grid-module_form-gap__tuCdw\n    div[class*='grid-row']\n    > div[class*='row']\n    > div[class*='grid-column'][class*='offset-left'] {\n    padding-left: calc(var(--grid-form-gap) / 2);\n  }\n}\n";
var styles$9 = {"grid":"Grid-module_grid__-FAfV","form-gap":"Grid-module_form-gap__tuCdw"};
styleInject(css_248z$9);

/**
 * Outer wrapper for a Grid
 *
 * @component
 *
 * @param {boolean} [useFormGap] - will use the predefined var(--grid-form-gap) instead of default var(--grid-column-gap) to space columns
 * @param {ReactNode} children - it is highly recommended that all children used are GridRow components
 * @param {CSSProperties | undefined} [style] - optional inline styling
 * @param {string | undefined} [className] - an optional className to add styling
 *
 * @example
 *  <Grid>
 *    <GridRow>
 *      ...
 *    </GridRow>
 *  <Grid>
 **/
var Grid = function (_a) {
    var _b;
    var useFormGap = _a.useFormGap, children = _a.children, style = _a.style, className = _a.className;
    var classes = classNames$1(styles$9.grid, (_b = {}, _b[styles$9['form-gap']] = useFormGap, _b), className);
    return (jsx$1("div", { className: classes, style: style, children: children }));
};

var css_248z$8 = ".GridRow-module_grid-row__t5gOT {\n  width: 100%;\n  margin: calc(var(--grid-row-gap) / 2) 0;\n}\n\n.GridRow-module_grid-row__t5gOT:first-child {\n  margin-bottom: calc(var(--grid-row-gap) / 2);\n  margin-top: 0;\n}\n\n.GridRow-module_grid-row__t5gOT:last-child {\n  margin-top: calc(var(--grid-row-gap) / 2);\n  margin-bottom: 0;\n}\n\n.GridRow-module_grid-row__t5gOT:only-child {\n  margin: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-top-s__X8Q-v {\n  margin-top: 8px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-top-m__2Sg-P {\n  margin-top: 24px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-top-l__DBEr8 {\n  margin-top: 36px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-top-none__dsY7G {\n  margin-top: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-bottom-s__twxr- {\n  margin-bottom: 8px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-bottom-m__KZwcx {\n  margin-bottom: 24px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-bottom-l__tw-pt {\n  margin-bottom: 36px;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_spacing-bottom-none__8pNhS {\n  margin-bottom: 0;\n}\n\n.GridRow-module_grid-row__t5gOT .GridRow-module_row__Dmo-w {\n  display: flex;\n  flex-direction: column;\n}\n\n.GridRow-module_grid-row__t5gOT .GridRow-module_headline__lpaFx {\n  font-size: var(--font-size-l);\n  color: var(--color-text);\n  margin-bottom: 24px;\n  user-select: none;\n}\n\n.GridRow-module_grid-row__t5gOT .GridRow-module_headline__lpaFx .GridRow-module_headline-wrapper__hsWEt {\n  display: flex;\n  align-items: center;\n  gap: 4px;\n}\n\n.GridRow-module_grid-row__t5gOT .GridRow-module_divider__WntWS {\n  display: block;\n  line-height: 0;\n  border: 0;\n  border-bottom: 1px solid var(--color-grey-lighter);\n  margin: 0 calc(var(--grid-margin) * -1);\n}\n\n.GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n  width: 100%;\n  max-width: 100%;\n  padding: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n  width: 100%;\n  max-width: 100%;\n  padding: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n  width: 100%;\n  max-width: 100%;\n  padding: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n  padding-left: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n  padding-right: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n  padding-left: 0;\n}\n\n.GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n  padding-right: 0;\n}\n\n@media screen and (width >= 580px) {\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w {\n    flex-direction: row;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-1'] {\n    width: 8.3333%;\n    max-width: 8.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-2'] {\n    width: 16.6666%;\n    max-width: 16.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-3'] {\n    width: 25%;\n    max-width: 25%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-4'] {\n    width: 33.3333%;\n    max-width: 33.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-5'] {\n    width: 41.6666%;\n    max-width: 41.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-6'] {\n    width: 50%;\n    max-width: 50%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-7'] {\n    width: 58.3333%;\n    max-width: 58.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-8'] {\n    width: 66.6666%;\n    max-width: 66.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-9'] {\n    width: 75%;\n    max-width: 75%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-10'] {\n    width: 83.3333%;\n    max-width: 83.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-11'] {\n    width: 91.6666%;\n    max-width: 91.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-phone__ieqGr\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-12'] {\n    width: 100%;\n    max-width: 100%;\n  }\n}\n\n@media screen and (width >= 770px) {\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w {\n    flex-direction: row;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-1'] {\n    width: 8.3333%;\n    max-width: 8.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-2'] {\n    width: 16.6666%;\n    max-width: 16.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-3'] {\n    width: 25%;\n    max-width: 25%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-4'] {\n    width: 33.3333%;\n    max-width: 33.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-5'] {\n    width: 41.6666%;\n    max-width: 41.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-6'] {\n    width: 50%;\n    max-width: 50%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-7'] {\n    width: 58.3333%;\n    max-width: 58.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-8'] {\n    width: 66.6666%;\n    max-width: 66.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-9'] {\n    width: 75%;\n    max-width: 75%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-10'] {\n    width: 83.3333%;\n    max-width: 83.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-11'] {\n    width: 91.6666%;\n    max-width: 91.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod\n    > .GridRow-module_row__Dmo-w\n    > div[class*='grid-column'][class*='width-12'] {\n    width: 100%;\n    max-width: 100%;\n  }\n}\n\n@media screen and (width >= 1024px) {\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w {\n    flex-direction: row;\n    gap: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='padding-right'] {\n    padding-right: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='padding-left'] {\n    padding-left: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='padding-none'] {\n    padding: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='offset-right'] {\n    padding-right: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='offset-left'] {\n    padding-left: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-1'] {\n    width: 8.3333%;\n    max-width: 8.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-2'] {\n    width: 16.6666%;\n    max-width: 16.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-3'] {\n    width: 25%;\n    max-width: 25%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-4'] {\n    width: 33.3333%;\n    max-width: 33.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-5'] {\n    width: 41.6666%;\n    max-width: 41.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-6'] {\n    width: 50%;\n    max-width: 50%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-7'] {\n    width: 58.3333%;\n    max-width: 58.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-8'] {\n    width: 66.6666%;\n    max-width: 66.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-9'] {\n    width: 75%;\n    max-width: 75%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-10'] {\n    width: 83.3333%;\n    max-width: 83.3333%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-11'] {\n    width: 91.6666%;\n    max-width: 91.6666%;\n  }\n\n  .GridRow-module_grid-row__t5gOT > .GridRow-module_row__Dmo-w > div[class*='grid-column'][class*='width-12'] {\n    width: 100%;\n    max-width: 100%;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column'] {\n    padding: 0 calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:first-child {\n    padding-left: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_breakpoint-tablet__PzKod > .GridRow-module_row__Dmo-w > div[class*='grid-column']:last-child {\n    padding-right: 0;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_direction-row__5Qqqj > .GridRow-module_row__Dmo-w {\n    flex-direction: row;\n  }\n\n  .GridRow-module_grid-row__t5gOT.GridRow-module_direction-column__l0iRH > .GridRow-module_row__Dmo-w {\n    flex-direction: column;\n  }\n}\n";
var styles$8 = {"grid-row":"GridRow-module_grid-row__t5gOT","spacing-top-s":"GridRow-module_spacing-top-s__X8Q-v","spacing-top-m":"GridRow-module_spacing-top-m__2Sg-P","spacing-top-l":"GridRow-module_spacing-top-l__DBEr8","spacing-top-none":"GridRow-module_spacing-top-none__dsY7G","spacing-bottom-s":"GridRow-module_spacing-bottom-s__twxr-","spacing-bottom-m":"GridRow-module_spacing-bottom-m__KZwcx","spacing-bottom-l":"GridRow-module_spacing-bottom-l__tw-pt","spacing-bottom-none":"GridRow-module_spacing-bottom-none__8pNhS","row":"GridRow-module_row__Dmo-w","headline":"GridRow-module_headline__lpaFx","headline-wrapper":"GridRow-module_headline-wrapper__hsWEt","divider":"GridRow-module_divider__WntWS","breakpoint-phone":"GridRow-module_breakpoint-phone__ieqGr","breakpoint-tablet":"GridRow-module_breakpoint-tablet__PzKod","direction-row":"GridRow-module_direction-row__5Qqqj","direction-column":"GridRow-module_direction-column__l0iRH"};
styleInject(css_248z$8);

function useWindowDimensions() {
    var _a = useState({
        height: 0,
        width: 0,
    }), dimensions = _a[0], setDimensions = _a[1];
    useEffect(function () {
        function handleResize() {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth,
            });
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    return {
        dimensions: dimensions,
    };
}

/**
 * GridRow used in a Grid, wrapper for GridColumns
 *
 * @component
 *
 * @param {string} [breakpoint] - controls when the row should break (default is 'laptop' so the row will wrap < 1024px, with value 'tablet' it will wrap < 770px and with value 'phone' it will only wrap on screens < 580px)
 * @param {string} [spacingTop] - marginTop of the row (default is 'm' which uses the --grid-row-gap variable)
 * @param {string} [spacingBottom] - marginBottom of the row (default is 'm' which uses the --grid-row-gap variable)
 * @param {string} [direction] - changes the flexDirection of the row (should only be relevant in certain edge-cases)
 * @param {string} [headline] - if this string is set, the row will show a headline above
 * @param {string} [tooltip] - if this string is set, the row will show a tooltip besides the headline (only visible if 'headline' is also set)
 * @param {string} [mobileGap] - used to finetune or disable the gap between elements on mobile layouts (should only be relevant in certain edge-cases)
 * @param {ReactNode} children - it is highly recommended that all children used are GridColumn components
 * @param {CSSProperties | undefined} [style] - optional inline styling
 * @param {string | undefined} [className] - an optional className to add styling
 *
 * @example
 *  <Grid>
 *    <GridRow>
 *      <GridColumn>
 *        ...
 *      </GridColumn>
 *    </GridRow>
 *  <Grid>
 **/
var GridRow = function (_a) {
    var _b;
    var _c = _a.breakpoint, breakpoint = _c === void 0 ? 'laptop' : _c, spacingTop = _a.spacingTop, spacingBottom = _a.spacingBottom, direction = _a.direction, headline = _a.headline, tooltip = _a.tooltip, mobileGap = _a.mobileGap, children = _a.children, style = _a.style, className = _a.className;
    var dimensions = useWindowDimensions().dimensions;
    var gridRowClasses = classNames$1(styles$8['grid-row'], (_b = {},
        _b[styles$8['spacing-top-' + spacingTop]] = spacingTop,
        _b[styles$8['spacing-bottom-' + spacingBottom]] = spacingBottom,
        _b[styles$8['breakpoint-' + breakpoint]] = breakpoint !== 'laptop',
        _b[styles$8['direction-' + direction]] = direction,
        _b), className);
    var rowStyle = useMemo(function () {
        return {
            gap: mobileGap && dimensions.width < 1024
                ? mobileGap === 'none'
                    ? 0
                    : mobileGap
                : undefined,
        };
    }, [dimensions.width]);
    return (jsxs("div", { className: gridRowClasses, style: style, children: [headline && (jsxs("div", { className: styles$8.headline, children: [jsxs("div", { className: styles$8['headline-wrapper'], children: [headline, tooltip && (jsx$1(Tooltip$1, { content: tooltip, children: jsx$1(SvgInfoIcon, { className: 'small' }) }))] }), jsx$1("div", { className: styles$8.divider })] })), jsx$1("div", { className: styles$8.row, style: rowStyle, children: children })] }));
};

var css_248z$7 = ".GridColumn-module_grid-column__cHubn {\n  flex-grow: 1;\n  flex-basis: auto;\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  align-items: flex-start;\n  padding: 0;\n  width: 100%;\n  max-width: 100%;\n  min-width: 0;\n}\n\n.GridColumn-module_grid-column__cHubn.GridColumn-module_justify-center__3NUJl {\n  justify-content: center;\n}\n\n.GridColumn-module_grid-column__cHubn.GridColumn-module_justify-end__fuhLZ {\n  justify-content: flex-end;\n}\n\n.GridColumn-module_grid-column__cHubn.GridColumn-module_align-center__4IiPv {\n  align-items: center;\n}\n\n.GridColumn-module_grid-column__cHubn.GridColumn-module_align-end__xPQ-Q {\n  align-items: flex-end;\n}\n\n@media screen and (width >= 580px) {\n  .GridColumn-module_grid-column__cHubn {\n    width: initial;\n    max-width: initial;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_padding-right__bk7JZ {\n    padding-right: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_padding-left__9yTFH {\n    padding-left: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_padding-none__6yw-k {\n    padding: 0;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-1__LjmYB {\n    width: 8.3333%;\n    max-width: 8.3333%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_offset-left__sGLc9 {\n    margin-left: auto;\n    padding-left: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_offset-right__1Nzt9 {\n    margin-right: auto;\n    padding-right: calc(var(--grid-column-gap) / 2);\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-2__anCg- {\n    width: 16.6666%;\n    max-width: 16.6666%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-3__xoXLK {\n    width: 25%;\n    max-width: 25%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-4__wPcSm {\n    width: 33.3333%;\n    max-width: 33.3333%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-5__YmE-m {\n    width: 41.6666%;\n    max-width: 41.6666%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-6__5qVPg {\n    width: 50%;\n    max-width: 50%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-7__orcOu {\n    width: 58.3333%;\n    max-width: 58.3333%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-8__RS--k {\n    width: 66.6666%;\n    max-width: 66.6666%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-9__umBrv {\n    width: 75%;\n    max-width: 75%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-10__ktP8i {\n    width: 83.3333%;\n    max-width: 83.3333%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-11__WXnyU {\n    width: 91.6666%;\n    max-width: 91.6666%;\n  }\n\n  .GridColumn-module_grid-column__cHubn.GridColumn-module_width-12__ZpXE9 {\n    width: 100%;\n    max-width: 100%;\n  }\n}\n";
var styles$7 = {"grid-column":"GridColumn-module_grid-column__cHubn","justify-center":"GridColumn-module_justify-center__3NUJl","justify-end":"GridColumn-module_justify-end__fuhLZ","align-center":"GridColumn-module_align-center__4IiPv","align-end":"GridColumn-module_align-end__xPQ-Q","padding-right":"GridColumn-module_padding-right__bk7JZ","padding-left":"GridColumn-module_padding-left__9yTFH","padding-none":"GridColumn-module_padding-none__6yw-k","width-1":"GridColumn-module_width-1__LjmYB","offset-left":"GridColumn-module_offset-left__sGLc9","offset-right":"GridColumn-module_offset-right__1Nzt9","width-2":"GridColumn-module_width-2__anCg-","width-3":"GridColumn-module_width-3__xoXLK","width-4":"GridColumn-module_width-4__wPcSm","width-5":"GridColumn-module_width-5__YmE-m","width-6":"GridColumn-module_width-6__5qVPg","width-7":"GridColumn-module_width-7__orcOu","width-8":"GridColumn-module_width-8__RS--k","width-9":"GridColumn-module_width-9__umBrv","width-10":"GridColumn-module_width-10__ktP8i","width-11":"GridColumn-module_width-11__WXnyU","width-12":"GridColumn-module_width-12__ZpXE9"};
styleInject(css_248z$7);

/**
 * GridColumn used in a GridRow
 *
 * @component
 *
 * @param {GridDivisions} width - a value between 1 and 12, which will determine the width of the column in percentage (so a width of 6 equals 50%, 3 equals 25%, etc.). Columns will maintain this width if the parent GridRow did not wrap (see breakpoint property of {@see GridRow})
 * @param {string} [fixedHeight] - if this string is set, the column will maintain the given height, no matter the content or neighboring GridColumns
 * @param {boolean} [fitRowHeight] - if true, the column will expand its height to fit the height of the parent GridRow
 * @param {string} [offset] - adds auto-margin to the given direction ('left' or 'right', should only be relevant in certain edge-cases)
 * @param {string} [justify] - justifies children within the column (default is 'start')
 * @param {string} [align] - aligns children within the column (default is 'start')
 * @param {string} [padding] - used to finetune padding (add on 'left' or 'right' side or remove completely with 'none', should only be relevant in certain edge-cases)
 * @param {ReactNode} children - you can use multiple items per column, in some cases it is advisable to wrap every child in its own GridColumn component
 * @param {CSSProperties | undefined} [style] - optional inline styling
 * @param {string | undefined} [className] - an optional className to add styling
 *
 * @example
 *  <Grid>
 *    <GridRow>
 *      <GridColumn width={6}>
 *        <Input />
 *      </GridColumn>
 *      <GridColumn width={6}>
 *        <Input />
 *      </GridColumn>
 *    </GridRow>
 *  <Grid>
 **/
var GridColumn = function (_a) {
    var _b;
    var width = _a.width, fixedHeight = _a.fixedHeight, _c = _a.fitRowHeight, fitRowHeight = _c === void 0 ? false : _c, offset = _a.offset, _d = _a.justify, justify = _d === void 0 ? 'start' : _d, _e = _a.align, align = _e === void 0 ? 'start' : _e, padding = _a.padding, children = _a.children, style = _a.style, className = _a.className;
    var columnStyle = useMemo(function () {
        return __assign(__assign({}, style), { height: fitRowHeight ? undefined : fixedHeight ? fixedHeight : '100%' });
    }, []);
    var gridColumnClasses = classNames$1(styles$7['grid-column'], styles$7['width-' + width], styles$7['justify-' + justify], styles$7['align-' + align], (_b = {},
        _b[styles$7['offset-right']] = offset && offset === 'right',
        _b[styles$7['offset-left']] = offset && offset === 'left',
        _b[styles$7['padding-right']] = padding && padding === 'right',
        _b[styles$7['padding-left']] = padding && padding === 'left',
        _b[styles$7['padding-none']] = padding && padding === 'none',
        _b), className);
    return (jsx$1("div", { className: gridColumnClasses, style: columnStyle, children: children }));
};

var css_248z$6 = ".SliderModal-module_slider-modal__e2LsS {\n  max-height: 60vh;\n  overflow: hidden;\n}\n\n.SliderModal-module_slider-modal__e2LsS .SliderModal-module_slider__Toq5I {\n  min-height: 24px;\n  max-height: 60vh;\n  width: 100%;\n  position: relative;\n  overflow: hidden auto;\n}\n\n.SliderModal-module_slider-modal__e2LsS .SliderModal-module_slide__TFIwy {\n  width: 100%;\n  display: flex;\n  align-items: center;\n  flex-wrap: wrap;\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  overflow: hidden auto;\n  transition: left 0.5s;\n}\n\n.SliderModal-module_slider-modal__e2LsS .SliderModal-module_slide__TFIwy > * {\n  width: 100%;\n  display: flex;\n  align-items: center;\n  flex-wrap: wrap;\n}\n";
var styles$6 = {"slider-modal":"SliderModal-module_slider-modal__e2LsS","slider":"SliderModal-module_slider__Toq5I","slide":"SliderModal-module_slide__TFIwy"};
styleInject(css_248z$6);

var css_248z$5 = ".ModalBottomButtons-module_modal-bottom-buttons__XFA1U {\n  width: 100%;\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  gap: var(--spacing-2);\n  margin-top: var(--spacing-8);\n}\n\n.ModalBottomButtons-module_modal-bottom-buttons__XFA1U .ModalBottomButtons-module_error-message__Gd2Vf {\n  margin-right: auto;\n  color: var(--color-error);\n  font-size: var(--font-size-s);\n  max-width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.ModalBottomButtons-module_modal-bottom-buttons__XFA1U .ModalBottomButtons-module_button__8Q0Mu {\n  flex: 0 0 auto;\n  min-width: 0;\n}\n";
var styles$5 = {"modal-bottom-buttons":"ModalBottomButtons-module_modal-bottom-buttons__XFA1U","error-message":"ModalBottomButtons-module_error-message__Gd2Vf","button":"ModalBottomButtons-module_button__8Q0Mu"};
styleInject(css_248z$5);

var ModalBottomButtons = function (_a) {
    var _b, _c;
    var closeButton = _a.closeButton, submitButton = _a.submitButton, additionalButtons = _a.additionalButtons, _d = _a.isLoading, isLoading = _d === void 0 ? false : _d, errors = _a.errors, errorMessage = _a.errorMessage, className = _a.className;
    var classes = classNames$1(styles$5['modal-bottom-buttons'], className);
    return (jsxs("div", { className: classes, children: [errorMessage && (jsx$1("div", { className: styles$5['error-message'], children: errorMessage })), jsx$1(Button$1, { hierarchy: 'tertiary', onClick: closeButton.callback, onKeyUp: function (e) { return e.key === 'Escape'; }, type: 'button', className: styles$5.button, children: (_b = closeButton.text) !== null && _b !== void 0 ? _b : 'Abbrechen' }), additionalButtons === null || additionalButtons === void 0 ? void 0 : additionalButtons.map(function (button, index) {
                var _a, _b;
                return (jsx$1(Button$1, { disabled: button.disabled, icon: button.icon, iconPosition: (_a = button.iconPosition) !== null && _a !== void 0 ? _a : 'left', hierarchy: (_b = button.hierarchy) !== null && _b !== void 0 ? _b : 'secondary', onClick: button.callback, type: 'button', className: styles$5.button, children: button.text }, index));
            }), jsx$1(Button$1, __assign({ isLoading: isLoading, type: 'submit', hierarchy: 'primary', disabled: (errors ? Object.values(errors).length > 0 : false) ||
                    (submitButton === null || submitButton === void 0 ? void 0 : submitButton.disabled) ||
                    !!errorMessage }, ((submitButton === null || submitButton === void 0 ? void 0 : submitButton.callback) ? { onClick: submitButton.callback } : {}), { onKeyUp: function (e) { return e.key === 'Enter'; }, className: styles$5.button, children: (_c = submitButton === null || submitButton === void 0 ? void 0 : submitButton.text) !== null && _c !== void 0 ? _c : 'Bestätigen' }))] }));
};

var SliderModal = function (_a) {
    var _b;
    var slides = _a.slides, startIndex = _a.startIndex, defaultIndex = _a.defaultIndex, isOpen = _a.isOpen, title = _a.title, _c = _a.submitDisabled, submitDisabled = _c === void 0 ? false : _c, nextDisabled = _a.nextDisabled, previousDisabled = _a.previousDisabled, _d = _a.hideControls, hideControls = _d === void 0 ? false : _d, _e = _a.loading, loading = _e === void 0 ? false : _e, modalSettings = _a.modalSettings, sliderClassName = _a.sliderClassName, sliderStyle = _a.sliderStyle, slideClassName = _a.slideClassName, slideStyle = _a.slideStyle, onPrevious = _a.onPrevious, onNext = _a.onNext, onChange = _a.onChange, onClose = _a.onClose, onSubmit = _a.onSubmit, className = _a.className;
    var sliderRef = useRef(null);
    var _f = useState(null), slideRef = _f[0], setSlideRef = _f[1];
    var _g = useState(0), index = _g[0], setIndex = _g[1];
    var wrapperClasses = classNames$1(styles$6['slider-modal'], className);
    var sliderClasses = classNames$1(styles$6.slider, sliderClassName);
    var slideClasses = classNames$1(styles$6.slide, slideClassName);
    function slideLeft(nextIndex) {
        if (onChange) {
            onChange(nextIndex);
        }
        else {
            onPrevious && onPrevious(nextIndex);
            updateIndex(nextIndex);
        }
    }
    function slideRight(nextIndex) {
        if (onChange) {
            onChange(nextIndex);
        }
        else {
            onNext && onNext(nextIndex);
            updateIndex(nextIndex);
        }
    }
    function submit() {
        onSubmit && onSubmit(index);
        close();
    }
    function close() {
        updateIndex(startIndex !== null && startIndex !== void 0 ? startIndex : 0);
        onClose(index);
    }
    function updateIndex(newIndex) {
        setIndex(newIndex
            ? newIndex > slides.length - 1
                ? slides.length - 1
                : newIndex < 0
                    ? 0
                    : newIndex
            : 0);
    }
    useEffect(function () {
        if (sliderRef.current)
            sliderRef.current.style.height = (slideRef === null || slideRef === void 0 ? void 0 : slideRef.scrollHeight) + 'px';
    }, [slideRef]);
    useEffect(function () {
        updateIndex(defaultIndex);
    }, [defaultIndex]);
    useEffect(function () {
        updateIndex(startIndex);
    }, [startIndex]);
    return (jsxs(Modal$1, __assign({}, modalSettings, { isOpen: isOpen, title: title, onRequestClose: close, loading: loading, children: [jsx$1("div", { className: wrapperClasses, children: jsx$1("div", { className: sliderClasses, style: sliderStyle, ref: sliderRef, children: slides.map(function (slide, i) {
                        return (jsx$1("div", { className: slideClasses, ref: function (ref) { return setSlideRef(ref); }, style: __assign(__assign({}, slideStyle), { left: index === i
                                    ? '0'
                                    : index > i
                                        ? "".concat((index + 1) * -100, "%")
                                        : "".concat((index + 1) * 100, "%") }), children: slide }, i));
                    }) }) }), !hideControls && (jsx$1(ModalBottomButtons, { closeButton: {
                    callback: close,
                }, submitButton: {
                    disabled: submitDisabled,
                    callback: submit,
                }, additionalButtons: [
                    {
                        text: 'Zurück',
                        icon: jsx$1(SvgArrowLeftIcon, { className: 'svg-icon small' }),
                        disabled: index === 0 || (nextDisabled === null || nextDisabled === void 0 ? void 0 : nextDisabled.includes(index)),
                        callback: function () { return slideLeft(index - 1); },
                    },
                    {
                        text: 'Weiter',
                        icon: jsx$1(SvgArrowRightIcon, { className: 'svg-icon small' }),
                        iconPosition: 'right',
                        disabled: index === ((_b = slides.length) !== null && _b !== void 0 ? _b : 1) - 1 ||
                            (previousDisabled === null || previousDisabled === void 0 ? void 0 : previousDisabled.includes(index)),
                        callback: function () { return slideRight(index + 1); },
                    },
                ] }))] })));
};

var css_248z$4 = ".LoadingBar-module_loading-bar__Nu7T2 {\n  width: 100%;\n  height: 100%;\n  user-select: none;\n}\n\n.LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_bar__tyCJG {\n  position: relative;\n  margin-bottom: 8px;\n  width: 100%;\n  height: var(--checkbox-input-size-mobile);\n  border: 1px solid var(--color-grey-light);\n  border-radius: var(--border-radius-m);\n  overflow: hidden;\n  background-color: var(--color-grey-lightest);\n}\n\n.LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_bar__tyCJG .LoadingBar-module_hint__HYtha {\n  position: absolute;\n  width: 100%;\n  text-align: center;\n  font-size: var(--font-size-s);\n  color: var(--color-grey-light);\n  line-height: 1.4rem;\n  transition: color 0.6s;\n}\n\n.LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_bar__tyCJG .LoadingBar-module_progress__SLr74 {\n  position: absolute;\n  background-color: var(--color-primary-light);\n  height: 100%;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  transition:\n    right 0.6s,\n    background-color 0.6s;\n}\n\n.LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_title__bWPx5 {\n  width: 100%;\n  text-align: center;\n  font-size: var(--font-size-m);\n  color: var(--color-text);\n}\n\n.LoadingBar-module_loading-bar__Nu7T2.LoadingBar-module_height-l__leX1a .LoadingBar-module_bar__tyCJG {\n  height: var(--input-height-mobile);\n}\n\n.LoadingBar-module_loading-bar__Nu7T2.LoadingBar-module_finished__UXbGQ .LoadingBar-module_bar__tyCJG .LoadingBar-module_hint__HYtha {\n  color: var(--color-white);\n}\n\n.LoadingBar-module_loading-bar__Nu7T2.LoadingBar-module_finished__UXbGQ .LoadingBar-module_bar__tyCJG .LoadingBar-module_progress__SLr74 {\n  background-color: var(--color-primary);\n}\n\n@media (width >= 580px) {\n  .LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_bar__tyCJG {\n    height: var(--checkbox-input-size-desktop);\n    margin-bottom: 4px;\n  }\n\n  .LoadingBar-module_loading-bar__Nu7T2.LoadingBar-module_height-l__leX1a .LoadingBar-module_bar__tyCJG {\n    height: calc(var(--input-height-desktop) - 2px);\n  }\n\n  .LoadingBar-module_loading-bar__Nu7T2 .LoadingBar-module_bar__tyCJG .LoadingBar-module_hint__HYtha {\n    line-height: 0.9rem;\n  }\n\n  .LoadingBar-module_loading-bar__Nu7T2.LoadingBar-module_height-l__leX1a .LoadingBar-module_bar__tyCJG .LoadingBar-module_hint__HYtha {\n    line-height: 1.9rem;\n  }\n}\n";
var styles$4 = {"loading-bar":"LoadingBar-module_loading-bar__Nu7T2","bar":"LoadingBar-module_bar__tyCJG","hint":"LoadingBar-module_hint__HYtha","progress":"LoadingBar-module_progress__SLr74","title":"LoadingBar-module_title__bWPx5","height-l":"LoadingBar-module_height-l__leX1a","finished":"LoadingBar-module_finished__UXbGQ"};
styleInject(css_248z$4);

var LoadingBar = function (_a) {
    var _b;
    var title = _a.title, progress = _a.progress, _c = _a.height, height = _c === void 0 ? 'm' : _c, _d = _a.hideProgressHint, hideProgressHint = _d === void 0 ? false : _d, backgroundColor = _a.backgroundColor, progressColor = _a.progressColor, hintColor = _a.hintColor, finishedColor = _a.finishedColor, finishedHintColor = _a.finishedHintColor, className = _a.className;
    var classes = classNames$1(styles$4['loading-bar'], (_b = {},
        _b[styles$4['height-l']] = height !== 'm',
        _b[styles$4.finished] = progress >= 100,
        _b), className);
    return (jsxs("div", { className: classes, children: [jsxs("div", { className: styles$4.bar, style: { backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : undefined }, children: [jsx$1("div", { className: styles$4.progress, style: {
                            right: "".concat(progress < 0 ? 100 : 100 - progress, "%"),
                            backgroundColor: finishedColor && progress >= 100
                                ? finishedColor
                                : progressColor !== null && progressColor !== void 0 ? progressColor : undefined,
                        } }), !hideProgressHint && (jsxs("div", { className: styles$4.hint, style: {
                            color: finishedHintColor && progress >= 100
                                ? finishedHintColor
                                : hintColor !== null && hintColor !== void 0 ? hintColor : undefined,
                        }, children: [progress > 100 ? 100 : progress, "%"] }))] }), title && jsx$1("div", { className: styles$4.title, children: title })] }));
};

var css_248z$3 = ".TimetableView-module_timetable-view__0saWd {\n  display: flex;\n  gap: var(--spacing-2);\n}\n\n.TimetableView-module_timetable-view__0saWd .TimetableView-module_timegrid__FozmO {\n  flex: 0 0 var(--spacing-8);\n}\n\n.TimetableView-module_timetable-view__0saWd .TimetableView-module_hours__eTkFx {\n  flex: 1 1 auto;\n}\n\n.TimetableView-module_timetable-view__0saWd .TimetableView-module_hours__eTkFx .TimetableView-module_hour__M010- {\n  position: relative;\n  height: var(--spacing-8);\n  background-color: var(--color-white);\n  border-top: 1px solid var(--color-grey-lighter);\n}\n\n.TimetableView-module_timetable-view__0saWd .TimetableView-module_hours__eTkFx .TimetableView-module_hour__M010-:last-child {\n  border-bottom: 1px solid var(--color-grey-lighter);\n}\n";
var styles$3 = {"timetable-view":"TimetableView-module_timetable-view__0saWd","timegrid":"TimetableView-module_timegrid__FozmO","hours":"TimetableView-module_hours__eTkFx","hour":"TimetableView-module_hour__M010-"};
styleInject(css_248z$3);

var css_248z$2 = ".TimegridEntry-module_timegrid-entry__W7LXB {\n  height: var(--spacing-8);\n  width: var(--spacing-8);\n  background-color: var(--color-white);\n  border-top: 1px solid var(--color-grey-lighter);\n  user-select: none;\n}\n\n.TimegridEntry-module_timegrid-entry__W7LXB:last-child {\n  border-bottom: 1px solid var(--color-grey-lighter);\n}\n\n.TimegridEntry-module_timegrid-entry__W7LXB .TimegridEntry-module_hour__hY-3n {\n  width: 100%;\n  font-size: var(--font-size-s);\n  color: var(--color-grey-light);\n  text-align: center;\n}\n";
var styles$2 = {"timegrid-entry":"TimegridEntry-module_timegrid-entry__W7LXB","hour":"TimegridEntry-module_hour__hY-3n"};
styleInject(css_248z$2);

var TimegridEntry = function (_a) {
    var height = _a.height, hour = _a.hour;
    return (jsx$1("div", { className: styles$2['timegrid-entry'], style: { height: "".concat(height, "px") }, children: jsx$1("div", { className: styles$2.hour, children: "".concat(String(hour).padStart(2, '0'), ":00") }) }));
};

var dayjs_min$1 = {exports: {}};

var dayjs_min = dayjs_min$1.exports;

var hasRequiredDayjs_min;

function requireDayjs_min () {
	if (hasRequiredDayjs_min) return dayjs_min$1.exports;
	hasRequiredDayjs_min = 1;
	(function (module, exports) {
		!function(t,e){module.exports=e();}(dayjs_min,(function(){var t=1e3,e=6e4,n=36e5,r="millisecond",i="second",s="minute",u="hour",a="day",o="week",c="month",f="quarter",h="year",d="date",l="Invalid Date",$=/^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/,y=/\[([^\]]+)]|Y{1,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS/g,M={name:"en",weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),ordinal:function(t){var e=["th","st","nd","rd"],n=t%100;return "["+t+(e[(n-20)%10]||e[n]||e[0])+"]"}},m=function(t,e,n){var r=String(t);return !r||r.length>=e?t:""+Array(e+1-r.length).join(n)+t},v={s:m,z:function(t){var e=-t.utcOffset(),n=Math.abs(e),r=Math.floor(n/60),i=n%60;return (e<=0?"+":"-")+m(r,2,"0")+":"+m(i,2,"0")},m:function t(e,n){if(e.date()<n.date())return -t(n,e);var r=12*(n.year()-e.year())+(n.month()-e.month()),i=e.clone().add(r,c),s=n-i<0,u=e.clone().add(r+(s?-1:1),c);return +(-(r+(n-i)/(s?i-u:u-i))||0)},a:function(t){return t<0?Math.ceil(t)||0:Math.floor(t)},p:function(t){return {M:c,y:h,w:o,d:a,D:d,h:u,m:s,s:i,ms:r,Q:f}[t]||String(t||"").toLowerCase().replace(/s$/,"")},u:function(t){return void 0===t}},g="en",D={};D[g]=M;var p="$isDayjsObject",S=function(t){return t instanceof _||!(!t||!t[p])},w=function t(e,n,r){var i;if(!e)return g;if("string"==typeof e){var s=e.toLowerCase();D[s]&&(i=s),n&&(D[s]=n,i=s);var u=e.split("-");if(!i&&u.length>1)return t(u[0])}else {var a=e.name;D[a]=e,i=a;}return !r&&i&&(g=i),i||!r&&g},O=function(t,e){if(S(t))return t.clone();var n="object"==typeof e?e:{};return n.date=t,n.args=arguments,new _(n)},b=v;b.l=w,b.i=S,b.w=function(t,e){return O(t,{locale:e.$L,utc:e.$u,x:e.$x,$offset:e.$offset})};var _=function(){function M(t){this.$L=w(t.locale,null,true),this.parse(t),this.$x=this.$x||t.x||{},this[p]=true;}var m=M.prototype;return m.parse=function(t){this.$d=function(t){var e=t.date,n=t.utc;if(null===e)return new Date(NaN);if(b.u(e))return new Date;if(e instanceof Date)return new Date(e);if("string"==typeof e&&!/Z$/i.test(e)){var r=e.match($);if(r){var i=r[2]-1||0,s=(r[7]||"0").substring(0,3);return n?new Date(Date.UTC(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)):new Date(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)}}return new Date(e)}(t),this.init();},m.init=function(){var t=this.$d;this.$y=t.getFullYear(),this.$M=t.getMonth(),this.$D=t.getDate(),this.$W=t.getDay(),this.$H=t.getHours(),this.$m=t.getMinutes(),this.$s=t.getSeconds(),this.$ms=t.getMilliseconds();},m.$utils=function(){return b},m.isValid=function(){return !(this.$d.toString()===l)},m.isSame=function(t,e){var n=O(t);return this.startOf(e)<=n&&n<=this.endOf(e)},m.isAfter=function(t,e){return O(t)<this.startOf(e)},m.isBefore=function(t,e){return this.endOf(e)<O(t)},m.$g=function(t,e,n){return b.u(t)?this[e]:this.set(n,t)},m.unix=function(){return Math.floor(this.valueOf()/1e3)},m.valueOf=function(){return this.$d.getTime()},m.startOf=function(t,e){var n=this,r=!!b.u(e)||e,f=b.p(t),l=function(t,e){var i=b.w(n.$u?Date.UTC(n.$y,e,t):new Date(n.$y,e,t),n);return r?i:i.endOf(a)},$=function(t,e){return b.w(n.toDate()[t].apply(n.toDate("s"),(r?[0,0,0,0]:[23,59,59,999]).slice(e)),n)},y=this.$W,M=this.$M,m=this.$D,v="set"+(this.$u?"UTC":"");switch(f){case h:return r?l(1,0):l(31,11);case c:return r?l(1,M):l(0,M+1);case o:var g=this.$locale().weekStart||0,D=(y<g?y+7:y)-g;return l(r?m-D:m+(6-D),M);case a:case d:return $(v+"Hours",0);case u:return $(v+"Minutes",1);case s:return $(v+"Seconds",2);case i:return $(v+"Milliseconds",3);default:return this.clone()}},m.endOf=function(t){return this.startOf(t,false)},m.$set=function(t,e){var n,o=b.p(t),f="set"+(this.$u?"UTC":""),l=(n={},n[a]=f+"Date",n[d]=f+"Date",n[c]=f+"Month",n[h]=f+"FullYear",n[u]=f+"Hours",n[s]=f+"Minutes",n[i]=f+"Seconds",n[r]=f+"Milliseconds",n)[o],$=o===a?this.$D+(e-this.$W):e;if(o===c||o===h){var y=this.clone().set(d,1);y.$d[l]($),y.init(),this.$d=y.set(d,Math.min(this.$D,y.daysInMonth())).$d;}else l&&this.$d[l]($);return this.init(),this},m.set=function(t,e){return this.clone().$set(t,e)},m.get=function(t){return this[b.p(t)]()},m.add=function(r,f){var d,l=this;r=Number(r);var $=b.p(f),y=function(t){var e=O(l);return b.w(e.date(e.date()+Math.round(t*r)),l)};if($===c)return this.set(c,this.$M+r);if($===h)return this.set(h,this.$y+r);if($===a)return y(1);if($===o)return y(7);var M=(d={},d[s]=e,d[u]=n,d[i]=t,d)[$]||1,m=this.$d.getTime()+r*M;return b.w(m,this)},m.subtract=function(t,e){return this.add(-1*t,e)},m.format=function(t){var e=this,n=this.$locale();if(!this.isValid())return n.invalidDate||l;var r=t||"YYYY-MM-DDTHH:mm:ssZ",i=b.z(this),s=this.$H,u=this.$m,a=this.$M,o=n.weekdays,c=n.months,f=n.meridiem,h=function(t,n,i,s){return t&&(t[n]||t(e,r))||i[n].slice(0,s)},d=function(t){return b.s(s%12||12,t,"0")},$=f||function(t,e,n){var r=t<12?"AM":"PM";return n?r.toLowerCase():r};return r.replace(y,(function(t,r){return r||function(t){switch(t){case "YY":return String(e.$y).slice(-2);case "YYYY":return b.s(e.$y,4,"0");case "M":return a+1;case "MM":return b.s(a+1,2,"0");case "MMM":return h(n.monthsShort,a,c,3);case "MMMM":return h(c,a);case "D":return e.$D;case "DD":return b.s(e.$D,2,"0");case "d":return String(e.$W);case "dd":return h(n.weekdaysMin,e.$W,o,2);case "ddd":return h(n.weekdaysShort,e.$W,o,3);case "dddd":return o[e.$W];case "H":return String(s);case "HH":return b.s(s,2,"0");case "h":return d(1);case "hh":return d(2);case "a":return $(s,u,true);case "A":return $(s,u,false);case "m":return String(u);case "mm":return b.s(u,2,"0");case "s":return String(e.$s);case "ss":return b.s(e.$s,2,"0");case "SSS":return b.s(e.$ms,3,"0");case "Z":return i}return null}(t)||i.replace(":","")}))},m.utcOffset=function(){return 15*-Math.round(this.$d.getTimezoneOffset()/15)},m.diff=function(r,d,l){var $,y=this,M=b.p(d),m=O(r),v=(m.utcOffset()-this.utcOffset())*e,g=this-m,D=function(){return b.m(y,m)};switch(M){case h:$=D()/12;break;case c:$=D();break;case f:$=D()/3;break;case o:$=(g-v)/6048e5;break;case a:$=(g-v)/864e5;break;case u:$=g/n;break;case s:$=g/e;break;case i:$=g/t;break;default:$=g;}return l?$:b.a($)},m.daysInMonth=function(){return this.endOf(c).$D},m.$locale=function(){return D[this.$L]},m.locale=function(t,e){if(!t)return this.$L;var n=this.clone(),r=w(t,e,true);return r&&(n.$L=r),n},m.clone=function(){return b.w(this.$d,this)},m.toDate=function(){return new Date(this.valueOf())},m.toJSON=function(){return this.isValid()?this.toISOString():null},m.toISOString=function(){return this.$d.toISOString()},m.toString=function(){return this.$d.toUTCString()},M}(),k=_.prototype;return O.prototype=k,[["$ms",r],["$s",i],["$m",s],["$H",u],["$W",a],["$M",c],["$y",h],["$D",d]].forEach((function(t){k[t[1]]=function(e){return this.$g(e,t[0],t[1])};})),O.extend=function(t,e){return t.$i||(t(e,_,O),t.$i=true),O},O.locale=w,O.isDayjs=S,O.unix=function(t){return O(1e3*t)},O.en=D[g],O.Ls=D,O.p={},O})); 
	} (dayjs_min$1));
	return dayjs_min$1.exports;
}

var dayjs_minExports = requireDayjs_min();
var dayjs = /*@__PURE__*/getDefaultExportFromCjs(dayjs_minExports);

var weekOfYear$2 = {exports: {}};

var weekOfYear$1 = weekOfYear$2.exports;

var hasRequiredWeekOfYear;

function requireWeekOfYear () {
	if (hasRequiredWeekOfYear) return weekOfYear$2.exports;
	hasRequiredWeekOfYear = 1;
	(function (module, exports) {
		!function(e,t){module.exports=t();}(weekOfYear$1,(function(){var e="week",t="year";return function(i,n,r){var f=n.prototype;f.week=function(i){if(void 0===i&&(i=null),null!==i)return this.add(7*(i-this.week()),"day");var n=this.$locale().yearStart||1;if(11===this.month()&&this.date()>25){var f=r(this).startOf(t).add(1,t).date(n),s=r(this).endOf(e);if(f.isBefore(s))return 1}var a=r(this).startOf(t).date(n).startOf(e).subtract(1,"millisecond"),o=this.diff(a,e,true);return o<0?r(this).startOf("week").week():Math.ceil(o)},f.weeks=function(e){return void 0===e&&(e=null),this.week(e)};}})); 
	} (weekOfYear$2));
	return weekOfYear$2.exports;
}

var weekOfYearExports = requireWeekOfYear();
var weekOfYear = /*@__PURE__*/getDefaultExportFromCjs(weekOfYearExports);

var css_248z$1 = ".TimetableViewEntry-module_timetable-view-entry__HUXUA {\n  color: var(--color-grey-darker);\n  padding: var(--spacing-2) 0;\n  display: flex;\n  align-items: center;\n  font-size: var(--font-size-s);\n  border-radius: 6px;\n  border: 1px solid var(--color-grey-light);\n  background-color: var(--color-grey-lightest);\n  position: absolute;\n  left: 0;\n  right: 0;\n  z-index: 1;\n  user-select: none;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_small-height__8q6VV {\n  padding-top: 2px;\n  padding-bottom: 2px;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_padding-s__Wr7q6 {\n  padding-left: var(--spacing-1);\n  padding-right: var(--spacing-1);\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_light__5XNJC {\n  color: var(--color-white);\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_interactive__PU3yG {\n  cursor: pointer;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_left__gnfgo,\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_right__DteY4 {\n  flex: 0 0 50%;\n  min-width: 0;\n  height: 100%;\n  padding: 0 var(--spacing-2);\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_left__gnfgo {\n  border-right: 1px solid currentcolor;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_padding-s__Wr7q6 .TimetableViewEntry-module_left__gnfgo {\n  padding: 0 var(--spacing-1) 0 0;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA.TimetableViewEntry-module_padding-s__Wr7q6 .TimetableViewEntry-module_right__DteY4 {\n  padding: 0 0 0 var(--spacing-1);\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_field__APaf5 {\n  flex: 0 0 50%;\n  min-width: 0;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_field__APaf5:nth-child(2) {\n  text-align: right;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_left__gnfgo.TimetableViewEntry-module_single__X0ZKF .TimetableViewEntry-module_field__APaf5,\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_right__DteY4.TimetableViewEntry-module_single__X0ZKF .TimetableViewEntry-module_field__APaf5 {\n  flex: 0 0 100%;\n}\n\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_left__gnfgo.TimetableViewEntry-module_align-right__U4pmj .TimetableViewEntry-module_field__APaf5,\n.TimetableViewEntry-module_timetable-view-entry__HUXUA .TimetableViewEntry-module_right__DteY4.TimetableViewEntry-module_align-right__U4pmj .TimetableViewEntry-module_field__APaf5 {\n  text-align: right;\n}\n";
var styles$1 = {"timetable-view-entry":"TimetableViewEntry-module_timetable-view-entry__HUXUA","small-height":"TimetableViewEntry-module_small-height__8q6VV","padding-s":"TimetableViewEntry-module_padding-s__Wr7q6","light":"TimetableViewEntry-module_light__5XNJC","interactive":"TimetableViewEntry-module_interactive__PU3yG","left":"TimetableViewEntry-module_left__gnfgo","right":"TimetableViewEntry-module_right__DteY4","field":"TimetableViewEntry-module_field__APaf5","single":"TimetableViewEntry-module_single__X0ZKF","align-right":"TimetableViewEntry-module_align-right__U4pmj"};
styleInject(css_248z$1);

function hexToRgb(hex) {
    var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (r, g, b) {
        return r + r + g + g + b + b;
    });
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : {
            r: 0,
            g: 0,
            b: 0,
        };
}
function luminance(r, g, b) {
    var a = [r, g, b].map(function (v) {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}
function isLight(hex1, hex2) {
    var color1rgb = hexToRgb(hex1);
    var color2rgb = hexToRgb(hex2);
    var color1luminance = luminance(color1rgb.r, color1rgb.g, color1rgb.b);
    var color2luminance = luminance(color2rgb.r, color2rgb.g, color2rgb.b);
    var ratio = color1luminance > color2luminance
        ? (color2luminance + 0.05) / (color1luminance + 0.05)
        : (color1luminance + 0.05) / (color2luminance + 0.05);
    return ratio < 1 / 3;
}

var TimetableViewEntry = function (_a) {
    var _b, _c, _d;
    var padding = _a.padding, height = _a.height, top = _a.top, width = _a.width, left = _a.left, field1 = _a.field1, field2 = _a.field2, field3 = _a.field3, field4 = _a.field4, tooltip = _a.tooltip, color = _a.color, bgColor = _a.bgColor, border = _a.border, onClick = _a.onClick, className = _a.className;
    var lightColored = isLight('#FFFFFF', bgColor && (bgColor.length === 7 || bgColor.length === 4)
        ? bgColor
        : '#FFFFFF');
    var classes = classNames$1(styles$1['timetable-view-entry'], (_b = {},
        _b[styles$1['small-height']] = height <= 24,
        _b[styles$1['padding-s']] = padding === 's',
        _b[styles$1.light] = lightColored,
        _b[styles$1.interactive] = !!onClick,
        _b), className);
    var title = '';
    if (tooltip) {
        if (tooltip.field1)
            title += tooltip.field1;
        if (tooltip.field2) {
            if (tooltip.field1)
                title += '\n';
            title += tooltip.field2;
        }
        if (tooltip.field3) {
            if (tooltip.field1 || tooltip.field2)
                title += '\n';
            title += tooltip.field3;
        }
        if (tooltip.field4) {
            if (tooltip.field1 || tooltip.field2 || tooltip.field3)
                title += '\n';
            title += tooltip.field4;
        }
    }
    return (jsxs("div", { className: classes, style: {
            height: "".concat(height, "px"),
            top: "".concat(top, "px"),
            width: "calc(".concat(width, "% - 2px)"),
            left: "".concat(left, "%"),
            color: color !== null && color !== void 0 ? color : undefined,
            backgroundColor: bgColor !== null && bgColor !== void 0 ? bgColor : 'var(--color-grey-lightest)',
            border: border ? border : "1px solid ".concat(bgColor),
        }, onClick: onClick && onClick, title: title !== null && title !== void 0 ? title : undefined, children: [jsxs("div", { className: classNames$1(styles$1.left, (_c = {},
                    _c[styles$1.single] = (!field1 && field2) || (field1 && !field2),
                    _c[styles$1['align-right']] = !field1 && field2,
                    _c)), children: [field1 && jsx$1("div", { className: styles$1.field, children: field1 }), field2 && jsx$1("div", { className: styles$1.field, children: field2 })] }), jsxs("div", { className: classNames$1(styles$1.right, (_d = {},
                    _d[styles$1.single] = (!field3 && field4) || (field3 && !field4),
                    _d[styles$1['align-right']] = !field3 && field4,
                    _d)), children: [field3 && jsx$1("div", { className: styles$1.field, children: field3 }), field4 && jsx$1("div", { className: styles$1.field, children: field4 })] })] }));
};

dayjs.extend(weekOfYear);
var TimetableView = function (_a, ref) {
    var start = _a.start, end = _a.end, showTimegrid = _a.showTimegrid, _b = _a.gridHeight, gridHeight = _b === void 0 ? 'm' : _b, _c = _a.padding, padding = _c === void 0 ? 'm' : _c, entries = _a.entries, className = _a.className;
    var entryHeight = gridHeight === 'xs'
        ? 24
        : gridHeight === 's'
            ? 36
            : gridHeight === 'm'
                ? 48
                : 64;
    var startHour = dayjs(start).hour();
    var endHour = dayjs(end).hour();
    var hours = [];
    var timegridEntries = [];
    for (var i = startHour; i < endHour + 1; i++) {
        hours.push(dayjs().set('hour', i).startOf('hour'));
        if (showTimegrid) {
            timegridEntries.push(jsx$1(TimegridEntry, { height: entryHeight, hour: i }, i));
        }
    }
    var normalizedDate = function (date, relativeTo) {
        var relative = dayjs(relativeTo);
        return dayjs(date)
            .set('year', relative.year())
            .set('month', relative.month())
            .week(relative.week())
            .set('day', relative.day());
    };
    function getEntry(hour, entry) {
        var start = normalizedDate(entry.start, hour);
        var end = normalizedDate(entry.end, hour);
        var hasBlockAbove = entries.some(function (e) {
            var entryEnd = normalizedDate(e.end, hour);
            return (dayjs(entryEnd).isSame(start, 'hour') &&
                dayjs(entryEnd).isSame(start, 'minutes'));
        });
        var hasBlockBelow = entries.some(function (e) {
            var entryStart = normalizedDate(e.start, hour);
            return (dayjs(entryStart).isSame(end, 'hour') &&
                dayjs(entryStart).isSame(end, 'minutes'));
        });
        var duration = end.diff(start, 'minutes');
        var difference = start.diff(hour, 'minutes');
        var realHeight = Math.floor((entryHeight / 60) * duration);
        var forceHeight = hasBlockAbove && hasBlockBelow && duration % 60 === 0;
        var height = forceHeight ? realHeight - 2 : realHeight;
        var gap = hasBlockAbove ? 2 : 0;
        var offset = Math.floor((entryHeight / 60) * difference) + gap;
        return {
            height: height,
            offset: offset,
        };
    }
    var classes = classNames$1(styles$3['timetable-view'], className);
    return (jsxs("div", { ref: ref, className: classes, children: [showTimegrid && (jsx$1("div", { className: styles$3.timegrid, children: timegridEntries.map(function (entry) {
                    return entry;
                }) })), jsx$1("div", { className: styles$3.hours, children: hours.map(function (hour, hourIndex) {
                    var hourEntries = entries.filter(function (e) { return dayjs(e.start).hour() === hour.hour(); });
                    return (jsx$1("div", { className: styles$3.hour, style: { height: "".concat(entryHeight, "px") }, children: hourEntries.map(function (entry, entryIndex) {
                            var _a, _b, _c, _d;
                            var _e = getEntry(hour, entry), height = _e.height, offset = _e.offset;
                            var width = 100 / ((_b = (_a = entry.group) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 1);
                            var left = width * ((_d = (_c = entry.group) === null || _c === void 0 ? void 0 : _c.index) !== null && _d !== void 0 ? _d : 0);
                            return (jsx$1(TimetableViewEntry, __assign({ padding: padding, height: height, top: offset, width: width, left: left }, entry), entryIndex));
                        }) }, hourIndex));
                }) })] }));
};
var TimetableView$1 = forwardRef(TimetableView);

var css_248z = ".ValueField-module_value-field__7ldLf {\n  display: block;\n  width: fit-content;\n  max-width: 100%;\n}\n\n.ValueField-module_value-field__7ldLf .ValueField-module_title__b4oW0 {\n  max-width: 100%;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/* mode  */\n.ValueField-module_value-field__7ldLf.ValueField-module_mode-hint__okZtU .ValueField-module_title__b4oW0 {\n  color: var(--color-grey);\n  font-size: var(--font-size-s);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_mode-headline__YovAV .ValueField-module_title__b4oW0 {\n  color: var(--color-text);\n  font-size: var(--font-size-l);\n}\n\n/* gap */\n.ValueField-module_value-field__7ldLf.ValueField-module_position-bottom__bUTup.ValueField-module_gap-none__3zzvt .ValueField-module_title__b4oW0 {\n  margin-top: 0;\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-bottom__bUTup.ValueField-module_gap-s__rlajR .ValueField-module_title__b4oW0 {\n  margin-top: var(--spacing-1);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-bottom__bUTup.ValueField-module_gap-m__JPaKD .ValueField-module_title__b4oW0 {\n  margin-top: var(--spacing-2);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-bottom__bUTup.ValueField-module_gap-l__9FbWu .ValueField-module_title__b4oW0 {\n  margin-top: var(--spacing-3);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-top__XXDQZ.ValueField-module_gap-none__3zzvt .ValueField-module_title__b4oW0 {\n  margin-bottom: 0;\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-top__XXDQZ.ValueField-module_gap-s__rlajR .ValueField-module_title__b4oW0 {\n  margin-bottom: var(--spacing-1);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-top__XXDQZ.ValueField-module_gap-m__JPaKD .ValueField-module_title__b4oW0 {\n  margin-bottom: var(--spacing-2);\n}\n\n.ValueField-module_value-field__7ldLf.ValueField-module_position-top__XXDQZ.ValueField-module_gap-l__9FbWu .ValueField-module_title__b4oW0 {\n  margin-bottom: var(--spacing-3);\n}\n";
var styles = {"value-field":"ValueField-module_value-field__7ldLf","title":"ValueField-module_title__b4oW0","mode-hint":"ValueField-module_mode-hint__okZtU","mode-headline":"ValueField-module_mode-headline__YovAV","position-bottom":"ValueField-module_position-bottom__bUTup","gap-none":"ValueField-module_gap-none__3zzvt","gap-s":"ValueField-module_gap-s__rlajR","gap-m":"ValueField-module_gap-m__JPaKD","gap-l":"ValueField-module_gap-l__9FbWu","position-top":"ValueField-module_position-top__XXDQZ"};
styleInject(css_248z);

var ValueField = function (_a, ref) {
    var title = _a.title, _b = _a.position, position = _b === void 0 ? 'bottom' : _b, _c = _a.mode, mode = _c === void 0 ? 'hint' : _c, _d = _a.gap, gap = _d === void 0 ? 's' : _d, className = _a.className, children = _a.children;
    var classes = classNames$1(styles['value-field'], styles['position-' + position], styles['mode-' + mode], styles['gap-' + gap], className);
    return (jsxs("div", { ref: ref, className: classes, children: [position === 'top' && jsx$1("div", { className: styles.title, children: title }), jsx$1("div", { className: styles.children, children: children }), position === 'bottom' && jsx$1("div", { className: styles.title, children: title })] }));
};
var ValueField$1 = forwardRef(ValueField);

var SvgAbsenceIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M8.39 40.37s-2.21 5.72-2.21 10.57m9.18-10.57s2.21 5.72 2.21 10.57' }), jsx$1("path", { fill: 'currentcolor', d: 'M19.52 33.58c0 .18-.01.35-.03.52-6.26-.44-7.97-2.06-8.87-2.91-1.88 2.19-4.07 3.08-6.32 3.34a7.64 7.64 0 0 1 7.58-8.6 7.65 7.65 0 0 1 7.65 7.65z' }), jsx$1("path", { fill: 'currentcolor', d: 'M15.82 24.92c0 .66-.17 1.3-.46 1.85-1.04-.54-2.22-.84-3.48-.84s-2.43.3-3.47.84a3.937 3.937 0 0 1 3.47-5.78c2.16 0 3.94 1.77 3.94 3.93' }), jsx$1("circle", { cx: 11.88, cy: 33.57, r: 7.64, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M16.62 27.58V13.73c0-.37.3-.67.67-.67h41.8c.37 0 .67.3.67.67V38.3c0 .37-.3.67-.67.67H17.28' }), jsx$1("circle", { cx: 38.2, cy: 26.02, r: 8.44, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'm41.18 23.03-5.97 5.98m5.97 0-5.97-5.98' })] }))); };

var SvgAnnouncementIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M17.99 52.51v-6.89h-3.8c-3.98 0-7.21-3.23-7.21-7.21V17.49c0-3.98 3.23-7.21 7.21-7.21h35.64c3.98 0 7.21 3.23 7.21 7.21v20.92c0 3.98-3.23 7.21-7.21 7.21H31.41L19.88 53.5c-.8.54-1.88-.03-1.88-.99z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M32 33.29q-.51 0-.87-.36c-.36-.36-.36-.53-.36-.87 0-.33.12-.62.36-.86s.53-.36.87-.36.61.12.85.36c.25.24.37.53.37.86 0 .23-.06.43-.18.62-.11.19-.26.33-.45.45-.18.11-.39.17-.6.17zm1.14-10.61-.2 7.34h-1.87l-.2-7.34h2.28z' }), jsx$1("circle", { cx: 32, cy: 27.98, r: 11.18, fill: 'none' })] }))); };

var SvgAttendanceClassIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M8.39 40.37s-2.21 5.72-2.21 10.57m9.18-10.57s2.21 5.72 2.21 10.57' }), jsx$1("path", { fill: 'currentcolor', d: 'M19.52 33.58c0 .18-.01.35-.03.52-6.26-.44-7.97-2.06-8.87-2.91-1.88 2.19-4.07 3.08-6.32 3.34a7.64 7.64 0 0 1 7.58-8.6 7.65 7.65 0 0 1 7.65 7.65z' }), jsx$1("path", { fill: 'currentcolor', d: 'M15.82 24.92c0 .66-.17 1.3-.46 1.85-1.04-.54-2.22-.84-3.48-.84s-2.43.3-3.47.84a3.937 3.937 0 0 1 3.47-5.78c2.16 0 3.94 1.77 3.94 3.93' }), jsx$1("circle", { cx: 11.88, cy: 33.57, r: 7.64, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M16.62 27.58V13.73c0-.37.3-.67.67-.67h41.8c.37 0 .67.3.67.67V38.3c0 .37-.3.67-.67.67H17.28' }), jsx$1("circle", { cx: 38.2, cy: 25.93, r: 8.14, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'm34.19 26.04 2.6 2.59 5.41-5.41' })] }))); };

var SvgClassesIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M9.38 14.67s.04 1.05 0 1.3c.41.04 2.94-1.45 2.94-1.45s0 .63-.24 1.52c2.26-.24 3.87-2 4.82-3.42a5.66 5.66 0 0 0-4.64-2.42c-2.96 0-5.38 2.27-5.64 5.16 1.81.03 2.76-.68 2.76-.68z' }), jsx$1("circle", { cx: 12.26, cy: 15.85, r: 5.66, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M9.68 20.89s-1.64 4.23-1.64 7.83m6.8-7.83s1.64 4.23 1.64 7.83m12.42-7.83s-1.64 4.23-1.64 7.83m6.8-7.83s1.64 4.23 1.64 7.83' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M36.91 14.24H33.3l-.92-.92-.9.9h-5.42a5.661 5.661 0 0 1 10.85.02m-7.13 7.01c-2.13.75-4.29.16-5.05-.55-.85-.79.65-4.75 1.31-6.37-.15.48-.22.99-.22 1.52 0 2.53 1.66 4.68 3.96 5.4m3.41 0c2.13.75 4.29.16 5.05-.55.85-.79-.65-4.75-1.31-6.37.15.48.22.99.22 1.52 0 2.53-1.66 4.68-3.96 5.4' }), jsx$1("circle", { cx: 31.48, cy: 15.85, r: 5.66, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M48.13 20.89s-1.64 4.23-1.64 7.83m6.8-7.83s1.64 4.23 1.64 7.83' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M56.37 15.85c0 .2 0 .41-.03.6-1.25-.85-2.23-1.89-2.23-1.89l-.78 2.13c-.05.14-.25.15-.32.02l-1.17-2.38-.99 1.34c-.07.09-.21.09-.28 0l-.87-1.15-1.91 1.6c-.09.08-.23.04-.27-.07l-.51-1.27-1.93 1.62c-.02-.18-.03-.36-.03-.55 0-2.39 1.46-4.58 3.73-5.34 3.94-1.31 7.59 1.58 7.59 5.34' }), jsx$1("circle", { cx: 50.71, cy: 15.85, r: 5.66, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M9.68 45.98s-1.64 4.23-1.64 7.83m6.8-7.83s1.64 4.23 1.64 7.83' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M17.92 40.95c0 .13 0 .26-.02.38-4.63-.32-5.9-1.52-6.57-2.15-1.39 1.62-3.02 2.28-4.68 2.47-.03-.23-.05-.46-.05-.7 0-3.13 2.53-5.66 5.66-5.66s5.66 2.53 5.66 5.66m-9.31 4.32s-1.46 2.56-4.27 1.09c.22-3.55 2.43-4.05 2.43-4.05.29 1.17.95 2.2 1.84 2.96m7.3 0s1.46 2.56 4.27 1.09c-.22-3.55-2.43-4.05-2.43-4.05-.29 1.17-.95 2.2-1.84 2.96' }), jsx$1("circle", { cx: 12.26, cy: 40.95, r: 5.66, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M28.9 45.98s-1.64 4.23-1.64 7.83m6.8-7.83s1.64 4.23 1.64 7.83' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M29.78 35.47v.08a5.6 5.6 0 0 0-3.29 2.76 2.85 2.85 0 0 1-2.44-2.83 2.866 2.866 0 0 1 5.73 0z' }), jsx$1("path", { fill: 'currentcolor', d: 'M35.24 36.72c-.76 2.99-3.47 5.21-6.7 5.21-.95 0-1.86-.19-2.69-.55-.02-.14-.02-.28-.02-.43 0-3.13 2.53-5.66 5.66-5.66 1.44 0 2.76.54 3.76 1.43z' }), jsx$1("circle", { cx: 31.48, cy: 40.95, r: 5.66, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M46.5 53.81c0-2.83 1.02-6.06 1.45-7.3m5.57.09c.45 1.3 1.41 4.45 1.41 7.21' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M58.94 40.58c.44.38.72.94.72 1.56 0 1.08-.82 1.95-1.88 2.05.02.12.04.25.04.37 0 1.15-.92 2.07-2.07 2.07l-2.24-.04c-.14-.39-.22-.62-.22-.62a5.655 5.655 0 0 0-2.58-10.69c-3.13 0-5.66 2.53-5.66 5.66 0 2.19 1.25 4.1 3.09 5.03 0 0-.08.19-.19.52l-2.52-.05c-1.07 0-1.95-.82-2.05-1.88a2.06 2.06 0 0 1-1.88-2.05c0-.5.18-.95.49-1.32-.42-.38-.7-.92-.7-1.54 0-.93.62-1.71 1.47-1.97-.13-.27-.21-.57-.21-.89 0-1.09.85-1.98 1.92-2.05-.02-.12-.04-.23-.04-.35 0-1.15.93-2.07 2.07-2.07a2 2 0 0 1 .97.25c.34-.68 1.03-1.15 1.85-1.15.52 0 1 .21 1.36.52.37-.32.84-.52 1.36-.52.61 0 1.15.27 1.52.69.95.05 1.72.72 1.92 1.62a2.074 2.074 0 0 1 2.53 2.02c0 .45-.15.86-.39 1.2 1.13.02 2.05.93 2.05 2.07 0 .62-.28 1.18-.72 1.55z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M56.29 40.06c-.21.09-.45.15-.68.15-.96 0-1.75-.79-1.75-1.74 0-.04.02-.08.02-.11-.18.06-.38.11-.59.11-.81 0-1.48-.55-1.68-1.3-.31.27-.7.44-1.14.44-.51 0-.96-.22-1.29-.57-.22.71-.87 1.22-1.65 1.22-.23 0-.45-.05-.65-.12.11.22.17.47.17.73 0 .96-.79 1.74-1.74 1.74-.08 0-.17 0-.25-.02.18-2.96 2.65-5.3 5.65-5.3a5.65 5.65 0 0 1 5.58 4.78z' }), jsx$1("circle", { cx: 50.71, cy: 40.95, r: 5.66, fill: 'none' })] }))); };

var SvgCurriculumIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 39.89, height: 35.17, x: 12.06, y: 16.03, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M17.21 27.64h6.29v6.29h-6.29z' }), jsx$1("path", { fill: 'currentcolor', d: 'M28.86 27.64h6.29v6.29h-6.29z' }), jsx$1("path", { fill: 'none', d: 'M40.5 27.64h6.29v6.29H40.5zm-23.29 10.7h6.29v6.29h-6.29zm11.65 0h6.29v6.29h-6.29z' }), jsx$1("path", { fill: 'currentcolor', d: 'M40.5 38.34h6.29v6.29H40.5z' }), jsx$1("path", { fill: 'none', d: 'M25.38 12.81v6.44m13.24-6.44v6.44' })] }))); };

var SvgDependenciesIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M18.11 47.2C13.19 43.05 10 36.91 9.8 30.02m46.23 4.33a23.4 23.4 0 0 1-10.24 14.66M29.77 6.16c1.11-.16 2.25-.25 3.41-.25 3.52 0 6.85.78 9.85 2.17' }), jsx$1("rect", { width: 7.92, height: 7.92, x: 27.01, y: 38.44, fill: 'currentcolor', rx: 3, ry: 3 }), jsx$1("rect", { width: 8.13, height: 8.13, x: 19.87, y: 52.29, fill: 'none', rx: 3, ry: 3 }), jsx$1("rect", { width: 8.13, height: 8.13, x: 33.93, y: 52.29, fill: 'none', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'M38 52.29v-.69c0-1.23-1-2.22-2.22-2.22h-9.61c-1.23 0-2.22 1-2.22 2.22v.69m7.02-5.93v3.02M22.35 16.3c-.49 4.72-4.58 8.36-9.48 8.16s-8.57-4.08-8.75-8.78c-.19-4.94 3.53-9.05 8.31-9.47.44-.04.82.3.82.74v8.51h8.35c.44 0 .79.39.74.83z' }), jsx$1("path", { fill: 'currentcolor', d: 'M25.22 12.01c.02.37-.26.7-.64.7h-7.72c-.35 0-.64-.29-.64-.64V4.22c0-.38.33-.67.71-.64 4.47.35 8.02 3.94 8.29 8.43' }), jsx$1("path", { fill: 'none', d: 'M39.13 27.31h20.76m-18.28-5.93h1.71c.55 0 1 .45 1 1v4.93h-3.71v-4.93c0-.55.45-1 1-1' }), jsx$1("path", { fill: 'currentcolor', d: 'M54.96 15.95h1.71c.55 0 1 .45 1 1v10.37h-3.71V16.95c0-.55.45-1 1-1' }), jsx$1("path", { fill: 'none', d: 'M48.28 11h1.71c.55 0 1 .45 1 1v15.31h-3.71V12c0-.55.45-1 1-1' })] }))); };

var SvgDivisionIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 22.04, height: 14.65, x: 19.43, y: 4.5, fill: 'currentcolor', rx: 3, ry: 3 }), jsx$1("path", { fill: 'none', d: 'm39.98 35.48.01 5.55-9.51-9.51' }), jsx$1("path", { fill: 'none', d: 'm26.55 41.03-5.57-.01 9.5-9.5v-8.27m-9.49 12.23v5.54m13.44.01h5.56' }), jsx$1("circle", { cx: 49.77, cy: 51.66, r: 7.84, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M7.4 58.83h13.94c.78 0 1.26-.84.88-1.51l-6.97-12.31c-.39-.68-1.37-.68-1.76 0L6.52 57.32c-.38.67.11 1.51.88 1.51' })] }))); };

var SvgEvaluateIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M32.66 42.61H5.39c-.3 0-.54-.24-.54-.54V11.76c0-.3.24-.54.54-.54h42.06c.3 0 .54.24.54.54v14.02' }), jsx$1("path", { fill: 'currentcolor', d: 'M14.55 15.7h4.78v4.78h-4.78z' }), jsx$1("circle", { cx: 16.94, cy: 26.91, r: 2.39, fill: 'none' }), jsx$1("circle", { cx: 26.74, cy: 35.74, r: 2.39, fill: 'currentcolor' }), jsx$1("circle", { cx: 35.91, cy: 18.09, r: 2.39, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M24.42 20.48h4.65c.26 0 .42-.28.29-.5l-2.33-4.11a.338.338 0 0 0-.59 0l-2.33 4.11c-.13.23.04.5.29.5zm13.35 6.99a6.55 6.55 0 0 0-2.59 1.83h-1.59c-.26 0-.42-.28-.3-.51l2.33-4.1c.12-.23.45-.23.58 0zM14.61 38.13h4.65c.26 0 .42-.28.29-.5l-2.33-4.11a.338.338 0 0 0-.59 0l-2.33 4.11c-.13.23.04.5.29.5zm20.85 0h-1.95v-4.78h.09v.22c0 1.78.71 3.39 1.86 4.57zM24.35 24.52h4.78v4.78h-4.78zm25.22 14.09 8.65 8.65c1.24 1.24 1.24 3.3 0 4.59-1.24 1.24-3.3 1.24-4.59 0l-8.65-8.65zm-1.3 1.29-1.89 1.89-2.72-2.72c.76-.49 1.41-1.12 1.91-1.88l2.7 2.7z' }), jsx$1("circle", { cx: 40.14, cy: 33.57, r: 6.54, fill: 'none' })] }))); };

var SvgFactorAlternateIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("rect", { width: 44.61, height: 53.63, x: 9.69, y: 5.18, fill: 'none', rx: 6, ry: 6 }), jsx$1("rect", { width: 30.18, height: 9, x: 16.91, y: 13.87, fill: 'currentcolor', rx: 0.84, ry: 0.84 }), jsx$1("path", { fill: 'none', d: 'M24.39 30.94v7.12m3.56-3.56h-7.13m6.08 10.59-5.03 5.04m5.03 0-5.03-5.04m20.26 0-5.03 5.04m6.08-15.63h-7.13' })] }))); };

var SvgFactorIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("circle", { cx: 53.67, cy: 52.94, r: 5.99, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M5.11 58.42h10.66c.59 0 .97-.64.67-1.15l-5.33-9.41a.78.78 0 0 0-1.35 0l-5.33 9.41a.77.77 0 0 0 .67 1.15zM53.67 42.1v-6.51c0-.38-.31-.69-.69-.69H11.13c-.38 0-.69.31-.69.69v6.51' }), jsx$1("path", { fill: 'none', d: 'm35.06 30.09-3.01-3V42.1m0-15.01-3 3m-2.37 17.47h10.75v10.75H26.68z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M31.4 5.5a.779.779 0 0 1 1.39 0l1.99 3.9c.11.22.33.38.57.42l4.32.69c.63.1.88.87.43 1.33l-3.09 3.1a.78.78 0 0 0-.22.68l.68 4.32c.1.63-.56 1.11-1.13.82l-3.9-1.98a.8.8 0 0 0-.71 0l-3.9 1.98a.784.784 0 0 1-1.13-.82l.68-4.32a.78.78 0 0 0-.22-.68l-3.09-3.1a.787.787 0 0 1 .43-1.33l4.32-.69c.25-.04.46-.19.57-.42l1.99-3.9z' })] }))); };

var SvgGradegroupsIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'currentcolor', d: 'M22.1 13.63c-.37-.68-.84-1.18-1.39-1.52-.57-.33-1.14-.56-1.73-.66-.6-.11-1.13-.17-1.59-.17-.72 0-1.44.1-2.19.3-.73.19-1.42.53-2.04 1.02s-1.12 1.16-1.49 2.02l2.08.74c.24-.52.66-1.01 1.25-1.46q.885-.69 2.46-.69c1.575 0 1.76.25 2.26.75.5.51.75 1.22.75 2.14v.08c0 .36-.13.6-.38.74s-.65.24-1.19.3c-.55.06-1.25.14-2.12.25-.66.08-1.32.21-1.97.35-.66.15-1.25.37-1.79.66q-.81.435-1.29 1.2c-.32.52-.48 1.18-.48 2 0 .93.22 1.72.65 2.37s1.02 1.15 1.75 1.5q1.095.51 2.46.51c.58 0 1.1-.06 1.56-.19l4.98-4.98v-4.56c0-1.13-.19-2.03-.57-2.71zm-1.63 6.95c0 .59-.16 1.16-.46 1.7-.31.54-.76.98-1.36 1.32q-.9.51-2.19.51c-1.29 0-1.59-.19-2.16-.57-.57-.39-.86-.96-.86-1.72 0-.56.15-1 .45-1.33.3-.32.7-.56 1.19-.73.5-.16 1.03-.28 1.6-.36.23-.03.54-.07.92-.11.38-.05.77-.1 1.18-.17s.76-.14 1.07-.23.52-.2.61-.3v2.01zm17.19 14.19c.05-.34.07-.69.07-1.05 0-1-.15-1.9-.46-2.71q-.45-1.215-1.38-1.92c-.61-.48-1.38-.72-2.32-.72s-1.64.23-2.24.68c-.61.44-1.06 1.07-1.38 1.87-.31.8-.46 1.73-.46 2.8s.15 2.02.47 2.84c.31.83.77 1.47 1.38 1.93.53.41 1.18.63 1.92.68l-1.54 1.54-.06-.03c-.55-.3-.97-.65-1.27-1.05-.29-.4-.53-.73-.69-.98h-.25v2.19h-2.12V26.88l2.19-2.19v4.14h.19c.16-.25.39-.57.68-.95.28-.39.7-.73 1.24-1.04s1.27-.46 2.2-.46c1.21 0 2.26.3 3.17.9.92.6 1.63 1.45 2.15 2.55.38.8.61 1.7.72 2.71l-2.2 2.2zm11.1 14.9h2.19c-.13.85-.43 1.6-.92 2.28q-.72 1.005-1.92 1.59c-.79.4-1.71.59-2.77.59-1.34 0-2.49-.32-3.46-.95-.96-.63-1.7-1.5-2.23-2.61-.52-1.11-.77-2.37-.77-3.8 0-1.12.16-2.14.48-3.05l4.1-4.1a6.8 6.8 0 0 1 1.85-.24c1.01 0 1.9.19 2.71.56q1.2.555 1.98 1.56c.78 1.005.83 1.44.96 2.34h-2.19c-.17-.65-.54-1.22-1.11-1.73q-.855-.75-2.31-.75c-.86 0-1.6.22-2.24.66s-1.14 1.06-1.5 1.87c-.36.8-.54 1.73-.54 2.82s.18 2.06.53 2.88c.35.81.85 1.45 1.49 1.9s1.39.68 2.26.68c.57 0 1.08-.1 1.55-.3.46-.2.86-.48 1.18-.86.32-.37.55-.81.69-1.34z' }), jsx$1("path", { fill: 'none', d: 'M10.55 38.34 39.02 9.87M24.98 52.78 53.45 24.3' })] }))); };

var SvgLessonIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M6.06 22.78s.02.59 0 .74c.23.02 1.66-.82 1.66-.82s0 .36-.14.86c1.28-.13 2.18-1.13 2.72-1.93a3.18 3.18 0 0 0-2.62-1.37c-1.67 0-3.03 1.28-3.18 2.91 1.02.01 1.56-.38 1.56-.38z' }), jsx$1("circle", { cx: 7.68, cy: 23.44, r: 3.19, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M6.23 26.28s-.92 2.39-.92 4.41m3.82-4.41s.92 2.39.92 4.41m9.31-19.25s-.92 2.39-.92 4.41m3.83-4.41s.92 2.39.92 4.41' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M24.01 8.6c0 .11 0 .23-.02.34-.7-.48-1.26-1.06-1.26-1.06l-.59 1.28-.69-1.41-.64.86-.57-.75-1.18.99-.34-.84-1.09.91c-.01-.1-.02-.2-.02-.31 0-1.76 1.43-3.19 3.19-3.19s3.19 1.43 3.19 3.19z' }), jsx$1("circle", { cx: 20.82, cy: 8.6, r: 3.19, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M6.23 11.44s-.92 2.39-.92 4.41m3.82-4.41s.92 2.39.92 4.41' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M10.87 8.6c0 .07 0 .15-.01.22-2.61-.18-3.33-.86-3.71-1.21-.79.92-1.7 1.28-2.64 1.39q-.03-.195-.03-.39c0-1.77 1.43-3.19 3.19-3.19s3.19 1.43 3.19 3.19zm-5.25 2.44s-.82 1.44-2.41.62c.12-2 1.37-2.28 1.37-2.28.16.66.53 1.24 1.04 1.67zm4.12 0s.82 1.44 2.41.62c-.12-2-1.37-2.28-1.37-2.28a3.16 3.16 0 0 1-1.04 1.67z' }), jsx$1("circle", { cx: 7.68, cy: 8.6, r: 3.19, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M18.58 30.7c0-1.6.57-3.42.82-4.12m3.13.05c.25.73.79 2.51.79 4.07' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M25.59 23.24c.25.22.41.53.41.88 0 .61-.46 1.1-1.06 1.16.01.07.02.14.02.21 0 .65-.52 1.17-1.17 1.17l-1.26-.02c-.08-.22-.13-.35-.13-.35 1.03-.53 1.74-1.6 1.74-2.84 0-1.77-1.43-3.19-3.19-3.19s-3.19 1.43-3.19 3.19c0 1.24.71 2.31 1.74 2.84l-.11.3-1.42-.03c-.6 0-1.1-.46-1.15-1.06-.59-.05-1.06-.55-1.06-1.15 0-.28.1-.54.27-.74a1.161 1.161 0 0 1 .44-1.98c-.07-.15-.12-.32-.12-.5 0-.62.48-1.12 1.08-1.16-.01-.07-.02-.13-.02-.2 0-.65.53-1.17 1.17-1.17.2 0 .38.05.55.14.19-.38.58-.65 1.04-.65.3 0 .56.12.77.3.21-.18.47-.3.77-.3.34 0 .65.15.86.39.53.03.97.41 1.08.91.08-.02.17-.03.26-.03.64 0 1.17.53 1.17 1.17 0 .25-.08.49-.22.68.64 0 1.15.53 1.15 1.17 0 .35-.16.66-.41.88z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M24.1 22.95a.99.99 0 0 1-1.38-.9v-.06c-.1.03-.21.06-.33.06-.46 0-.83-.31-.95-.73-.17.15-.39.25-.64.25-.29 0-.54-.13-.72-.32a.97.97 0 0 1-.93.69c-.13 0-.26-.03-.37-.07.06.13.1.26.1.41 0 .54-.44.98-.98.98-.05 0-.1 0-.14-.01.1-1.67 1.49-2.99 3.19-2.99 1.59 0 2.91 1.17 3.15 2.69' }), jsx$1("circle", { cx: 20.95, cy: 23.44, r: 3.19, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M50.38 8.82h3.39c.55 0 1 .45 1 1v15.22c0 .55-.45 1-1 1h-2.39c-.55 0-1-.45-1-1z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M50.38 20.46h4.39v2.66h-4.39z' }), jsx$1("rect", { width: 4.39, height: 15.47, x: 55.6, y: 10.41, fill: 'none', rx: 1, ry: 1, transform: 'rotate(-6.53 57.791 18.157)' }), jsx$1("path", { fill: 'none', d: 'm56.55 14.08 1.54-.17m-16.37-2.45h4.27v13.57c0 .55-.45 1-1 1h-2.27c-.55 0-1-.45-1-1zm1.63 2.63h1.02m-1.02 8.42h1.02' }), jsx$1("rect", { width: 4.39, height: 18.33, x: 46, y: 7.7, fill: 'none', rx: 1, ry: 1 }), jsx$1("path", { fill: 'none', d: 'M47.86 10.57v11.94M31.63 48.54s-2.1 5.44-2.1 10.05m8.72-10.05s2.1 5.44 2.1 10.05' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M42.21 42.07c0 .17 0 .34-.03.49-5.95-.42-7.58-1.96-8.44-2.77-1.79 2.09-3.87 2.93-6.01 3.17-.04-.3-.06-.59-.06-.9 0-4.02 3.25-7.28 7.27-7.28s7.28 3.25 7.28 7.28z' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M38.69 33.84c0 .63-.16 1.23-.44 1.76-.99-.51-2.11-.8-3.31-.8s-2.31.29-3.3.8c-.28-.52-.44-1.12-.44-1.76 0-2.06 1.68-3.74 3.74-3.74s3.75 1.68 3.75 3.74' }), jsx$1("circle", { cx: 34.94, cy: 42.07, r: 7.27, fill: 'none' })] }))); };

var SvgManualDeputateIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm47.86 46.19-6.53-6.53c-.75-.74-1.98-.73-2.71.01s-.72 1.95.01 2.69l9.82 9.82-5.54-.04c-1.08 0-1.96.87-1.94 1.95h0c.01 1.06.87 1.91 1.93 1.91h8.47c1.29 0 2.56-.3 3.71-.88h0c1.74-.88 3.19-2.3 3.95-4.1 1.32-3.1.58-6.59-1.71-8.88l-2.41-2.41-1.22-1.22c-.65-.61-1.63-.65-2.25-.14-.71.58-.81 1.75-.11 2.51' }), jsx$1("path", { fill: 'none', d: 'm50.19 44.09-3.33-3.33c-.64-.77-1.74-.84-2.35-.31-.67.58-.7 1.81.13 2.52' }), jsx$1("path", { fill: 'none', d: 'm52.37 41.92-2.52-2.52c-.59-.63-1.59-.65-2.2-.07-.62.6-.62 1.64.03 2.25M32.1 46.99H10.35c-3.32 0-6.01-2.69-6.01-6.01V14.03c0-3.32 2.69-6.01 6.01-6.01H37.3c3.32 0 6.01 2.69 6.01 6.01v19.75M22.14 19.15h14.55m-14.55 4.16h10.4' }), jsx$1("circle", { cx: 13.82, cy: 21.23, r: 2.86, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M22.14 31.7h14.55m-14.55 4.16h10.4' }), jsx$1("circle", { cx: 13.82, cy: 33.78, r: 2.86, fill: 'currentcolor' })] }))); };

var SvgOverviewIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M59.66 33.03c-2.92 5.25-8.09 8.74-13.98 8.74s-11.07-3.49-13.97-8.74c2.92-5.25 8.08-8.73 13.97-8.73s11.07 3.48 13.98 8.73' }), jsx$1("path", { fill: 'none', d: 'M52.98 44.59v4.56c0 .36-.29.65-.65.65H4.99c-.36 0-.65-.29-.65-.65v-34.3c0-.36.29-.65.65-.65h47.34c.36 0 .65.29.65.65v1.69m-38.32 6.2h9.47m-9.47 4.68h15.66M14.66 32.1h7.72m-7.72 4.67h9.47m-9.47 4.68h15.43' }), jsx$1("circle", { cx: 10.8, cy: 22.74, r: 0.65, fill: 'currentcolor', stroke: 'none' }), jsx$1("circle", { cx: 10.8, cy: 27.42, r: 0.65, fill: 'currentcolor', stroke: 'none' }), jsx$1("circle", { cx: 10.8, cy: 32.1, r: 0.65, fill: 'currentcolor', stroke: 'none' }), jsx$1("circle", { cx: 10.8, cy: 36.77, r: 0.65, fill: 'currentcolor', stroke: 'none' }), jsx$1("circle", { cx: 10.8, cy: 41.45, r: 0.65, fill: 'currentcolor', stroke: 'none' }), jsx$1("path", { fill: 'none', d: 'M45.68 17.64v4.02m-4.02.58-2.29-2.3m10.33 2.3 2.3-2.3' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M46.44 31.71a2.768 2.768 0 0 1-5.13 1.43v-.11c0-1.84 1.12-3.41 2.73-4.06 1.36.18 2.41 1.33 2.41 2.74z' }), jsx$1("circle", { cx: 45.68, cy: 33.03, r: 4.38, fill: 'none' })] }))); };

var SvgPlanIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M15.61 33.76v-4.69M48.5 43.05v1.53c0 .27-.22.5-.5.5H16.1c-.27 0-.5-.22-.5-.5V43m.01-23.67v-1.08c0-.27.22-.5.5-.5h31.9c.27 0 .5.22.5.5v16.46' }), jsx$1("circle", { cx: 10.58, cy: 49.2, r: 2.98, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M4.09 17.78h5.79c.32 0 .52-.35.37-.63l-2.89-5.11a.422.422 0 0 0-.73 0l-2.89 5.11c-.16.28.04.63.37.63zM54.38 36.1h5.95v5.95h-5.95z' }), jsx$1("path", { fill: 'currentcolor', d: 'M35.76 24.12h4.37v4.37h-4.37z' }), jsx$1("path", { fill: 'none', d: 'M25 24.12h4.37v4.37H25zm10.76 10.23h4.37v4.37h-4.37zm-10.76 0h4.37v4.37H25zM6.98 20.26v2.21c0 .82.67 1.49 1.49 1.49h7.79m0 14.92h-4.2c-.82 0-1.49.67-1.49 1.49v3.37' }), jsx$1("path", { fill: 'none', d: 'm14.86 25.36 1.4-1.41-1.4-1.4M49.2 37.48l-1.4 1.4 1.4 1.4m-34.34 0 1.4-1.4-1.4-1.4m32.94 1.4h4.1' })] }))); };

var SvgRoomIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm41.12 59.26-26.25-6.87V11.91l26.25-7.17zm-26.25-6.87H4.78m36.34-41.73h7.31V52.2h10.79M35.91 33.15h-3.75' }), jsx$1("path", { fill: 'currentcolor', fillRule: 'evenodd', d: 'm48.43 52.2-7.31.19V10.66h7.31z' })] }))); };

var SvgScenarioIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M36.95 31.45v20.81c0 2.88-2.34 5.22-5.22 5.22H12.44c-2.88 0-5.22-2.34-5.22-5.22v-26.3c0-2.88 2.34-5.22 5.22-5.22h13.8z' }), jsx$1("path", { fill: 'none', d: 'M36.95 31.45H28.8c-1.41 0-2.56-1.15-2.56-2.56v-8.15m2.41 24.92a6.77 6.77 0 0 1-6.57 5.16c-3.73 0-6.76-3.03-6.76-6.76 0-3.15 2.15-5.79 5.07-6.55' }), jsx$1("path", { fill: 'currentcolor', fillRule: 'evenodd', d: 'M30.39 42.5h-6.76v-6.76c3.73 0 6.76 3.03 6.76 6.76' }), jsx$1("path", { fill: 'none', d: 'M19.36 14.58c.85-.6 1.88-.95 3-.95h13.8l10.71 10.71v20.81c0 1.64-.76 3.11-1.94 4.06' }), jsx$1("path", { fill: 'none', d: 'M46.87 24.33h-8.15c-1.41 0-2.56-1.15-2.56-2.56v-8.15' }), jsx$1("path", { fill: 'none', d: 'M29.46 7.34c.81-.52 1.78-.82 2.82-.82h13.8l10.71 10.71v20.81c0 1.52-.65 2.89-1.68 3.84' }), jsx$1("path", { fill: 'none', d: 'M56.78 17.22h-8.15c-1.41 0-2.56-1.15-2.56-2.56V6.52' })] }))); };

var SvgSchoolEventIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'm39.44 51.57-.19-10.01c-.02-.4-.19-.77-.49-1.03l-5.38-4.1c-.47-.41-.62-1.07-.38-1.64l1.41-4.32' }), jsx$1("path", { fill: 'none', d: 'm34.97 51.57-.53-6.75c-.02-.46-.25-.88-.63-1.13l-7.92-5.32c-1-.67-1.44-1.92-1.1-3.07h0l3.65-12.35a2.875 2.875 0 0 1 3.57-1.94l1.59.47c.58.17 1.1.53 1.47 1.01l3.51 5.19c.27.36.7.57 1.15.56l3.85.43c.4.04.77.21 1.06.49l.17.16c.82.76.77 2.07-.1 2.78h0c-.32.26-.72.41-1.14.41l-6.55.08c-.45 0-.88-.2-1.16-.56l-3.74-4.01' }), jsx$1("rect", { width: 7.23, height: 18.31, x: 19.95, y: 17.48, fill: 'none', rx: 3.38, ry: 3.38, transform: 'rotate(16.47 23.572 26.64)' }), jsx$1("path", { fill: 'none', d: 'm24.66 41.75-.39 4.36-4.14 9.32m4.87 0 4.21-7.83.27-2.34m14.09-12.75V50m0-26.23v4.9' }), jsx$1("path", { fill: 'none', d: 'M8.36 58.2h5l5.32-2.77h9.15l3.87-3.86h9.75l4.9-3.63h3.83l4.24-4.23' }), jsx$1("circle", { cx: 35.2, cy: 15.17, r: 3.92, fill: 'none' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M59.66 10.77c0 1.58-1.28 2.86-2.87 2.86h-5.67c-2.16 0-3.92-1.75-3.92-3.92s1.75-3.92 3.92-3.92c1.79 0 3.29 1.2 3.76 2.83.51-.46 1.17-.73 1.91-.73a2.87 2.87 0 0 1 2.87 2.87z' }), jsx$1("circle", { cx: 9.21, cy: 14.34, r: 2.87, fill: 'currentcolor', stroke: 'none' }), jsx$1("path", { fill: 'none', d: 'M9.21 9.72V8.71m-4 3.32-.87-.5m.87 5.12-.87.51m4.87 1.8v1.02m4.01-3.33.87.51m-.87-5.13.87-.5' })] }))); };

var SvgSchoolYearIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M47.11 5h-29.5c-1.14 0-2.16.72-2.54 1.79L6.78 30.27h50.44L49.67 6.86A2.69 2.69 0 0 0 47.11 5M6.78 30.27h50.44v25.72c0 1.66-1.34 3-3 3H9.78c-1.66 0-3-1.34-3-3z' }), jsx$1("rect", { width: 16.39, height: 7.33, x: 23.81, y: 40.97, fill: 'currentcolor', stroke: 'none', rx: 2, ry: 2 }), jsx$1("path", { fill: 'none', d: 'M17.03 22.7h29.95c1.66 0 3 1.34 3 3v4.58H14.03V25.7c0-1.66 1.34-3 3-3m3.22-6.79h23.5c1.66 0 3 1.34 3 3v3.78h-29.5v-3.78c0-1.66 1.34-3 3-3m2.94-6.02h17.62c1.66 0 3 1.34 3 3v3.02H20.19v-3.02c0-1.66 1.34-3 3-3' })] }))); };

var SvgSetupIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M15.02 26.17V9.41c0-.31.25-.55.55-.55h43.1c.31 0 .55.25.55.55v31.06c0 .31-.25.55-.55.55H33.62M22.74 14.99h19.1m-19.1 4.97h19.1m-19.1 4.98H51.5m-19.32 4.97H51.5m-16.16 4.97H51.5M30.19 54.19c-1.27 1.27-3.38 1.27-4.71 0l-8.25-8.25c-1.22-1.22-1.22-3.27 0-4.49l.22-.22c1.22-1.22 3.27-1.22 4.49 0l8.25 8.25c1.33 1.33 1.33 3.43 0 4.71M17.34 41.34l-4.71-4.71m1.17-3.49-.83 2.49c-.22.66-.72 1.16-1.38 1.38l-2.49.83L5 31.69c-.33-.55-.28-1.22.17-1.66L6 29.2c.44-.44 1.16-.5 1.66-.17l6.15 4.1zm-.17 8.37-7.75 7.75c-.66.66-1 1.5-1 2.33s.28 1.66.89 2.27c1.27 1.38 3.54 1.27 4.87-.06l5.32-5.32' }), jsx$1("path", { fill: 'none', d: 'M25.65 41.34c.72-.33 1.38-.78 1.94-1.33 1.72-1.72 2.27-4.15 1.77-6.31-.06-.33-.5-.44-.72-.17l-3.43 3.32-3.16-.78-.72-3.1 3.38-3.38c.22-.22.11-.61-.17-.72-2.22-.55-4.6.06-6.31 1.77s-2.33 4.21-1.72 6.42' }), jsx$1("circle", { cx: 8.65, cy: 51.25, r: 0.55, fill: 'none' }), jsx$1("path", { fill: 'currentcolor', d: 'M46.53 14.99h4.97v4.97h-4.97z' })] }))); };

var SvgSubjectIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M35.17 12.57h9.59c.55 0 1 .45 1 1v39.55c0 .55-.45 1-1 1h-8.59c-.55 0-1-.45-1-1z' }), jsx$1("path", { fill: 'currentcolor', d: 'M35.17 40.66h10.59v6.41H35.17z' }), jsx$1("rect", { width: 10.59, height: 37.32, x: 47.75, y: 16.41, fill: 'none', rx: 1, ry: 1, transform: 'rotate(-6.53 53.042 35.121)' }), jsx$1("path", { fill: 'none', d: 'm49.11 22.4 4.91-.56m-4.57 3.51 4.9-.57m-40.09-5.82h10.31v34.17c0 .55-.45 1-1 1h-8.31c-.55 0-1-.45-1-1zm2.69 6.34h4.94m-4.94 20.32h4.94m-4.94 3.01h4.94' }), jsx$1("rect", { width: 10.59, height: 41.55, x: 3.68, y: 12.57, fill: 'none', rx: 1, ry: 1 }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M3.68 20.31h10.59v6.41H3.68z' }), jsx$1("path", { fill: 'none', d: 'M6.5 48.63h4.94' }), jsx$1("rect", { width: 10.59, height: 44.24, x: 24.58, y: 9.88, fill: 'none', rx: 1, ry: 1 }), jsx$1("path", { fill: 'none', d: 'M28.54 16.8v28.82m2.66-8.57v8.57' })] }))); };

var SvgSubstitutionIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M19.73 19.23 30.55 8.41c2-2 5.23-2 7.23 0l8.31 8.31' }), jsx$1("path", { fill: 'none', d: 'm20.25 10.75-.52 8.47 8.47-.52m17.89 26.07L35.27 55.59c-2 2-5.23 2-7.23 0l-8.31-8.31' }), jsx$1("path", { fill: 'none', d: 'm45.57 53.25.52-8.47-8.47.52M52.1 33.33s-1.51 3.92-1.51 7.25m6.29-7.25s1.51 3.92 1.51 7.25' }), jsx$1("path", { fill: 'currentcolor', d: 'M59.69 27.99c-.54.29-1.15.44-1.81.44-1.47 0-2.74-.84-3.39-2.06-.66 1.21-1.93 2.06-3.4 2.06-.65 0-1.27-.17-1.81-.44.33-2.58 2.54-4.56 5.2-4.56s4.86 1.98 5.19 4.56z' }), jsx$1("circle", { cx: 54.49, cy: 28.67, r: 5.24, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M7.14 33.3s-1.52 3.94-1.52 7.28m6.31-7.28s1.52 3.94 1.52 7.28' }), jsx$1("path", { fill: 'currentcolor', d: 'M14.8 28.61c0 .12 0 .24-.02.36-4.31-.3-5.49-1.42-6.11-2-1.3 1.51-2.81 2.12-4.35 2.3a5.27 5.27 0 0 1 5.22-5.92c2.9 0 5.27 2.36 5.27 5.27z' }), jsx$1("path", { fill: 'currentcolor', d: 'M12.25 22.65c0 .46-.12.89-.32 1.27-.72-.37-1.53-.58-2.4-.58s-1.67.21-2.39.58c-.2-.38-.32-.81-.32-1.27 0-1.49 1.22-2.71 2.71-2.71s2.71 1.22 2.71 2.71z' }), jsx$1("circle", { cx: 9.53, cy: 28.61, r: 5.27, fill: 'none' })] }))); };

var SvgTeacherIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M8.48 40.34s-2.2 5.7-2.2 10.53m9.14-10.53s2.2 5.7 2.2 10.53' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M19.57 33.57c0 .18-.01.35-.03.52-6.23-.43-7.94-2.05-8.84-2.9-1.87 2.18-4.06 3.06-6.3 3.32a7 7 0 0 1-.06-.94c0-4.21 3.41-7.62 7.61-7.62s7.62 3.41 7.62 7.62' }), jsx$1("path", { fill: 'currentcolor', stroke: 'none', d: 'M15.87 24.95c0 .66-.17 1.29-.46 1.84-1.04-.54-2.21-.84-3.47-.84s-2.42.3-3.46.84a3.95 3.95 0 0 1-.46-1.84c0-2.16 1.76-3.92 3.92-3.92s3.93 1.76 3.93 3.92' }), jsx$1("circle", { cx: 11.95, cy: 33.57, r: 7.62, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M16.68 27.6V13.8c0-.37.3-.67.67-.67H59c.37 0 .67.3.67.67v24.49c0 .37-.3.67-.67.67H17.33m4.82-20.94h31.3m-31.3 3.74h31.3m-31.3 3.75h31.3m-31.3 3.74h19.26' })] }))); };

var SvgTimegridIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', fillRule: 'evenodd', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M36.66 49.93H4.72c-.46 0-.83-.37-.83-.83V10.94c0-.46.37-.83.83-.83h46.46c.46 0 .83.37.83.83v22.4M17.99 6.79v6.64m9.96-6.64v6.64m9.95-6.64v6.64m-15.76 8.3h5.81m4.98 5.8h5.8M27.95 39.15h-5.81m-4.98-11.62h-5.8' }), jsx$1("rect", { width: 5.81, height: 5.81, x: 11.36, y: 33.34, fill: 'currentcolor', rx: 1, ry: 1 }), jsx$1("rect", { width: 5.81, height: 5.81, x: 22.14, y: 27.53, fill: 'currentcolor', rx: 1, ry: 1 }), jsx$1("path", { fill: 'currentcolor', d: 'M38.73 34.17v2.61c-.65.72-1.2 1.52-1.67 2.37h-3.31c-.46 0-.83-.37-.83-.83v-4.15c0-.46.37-.83.83-.83h4.15c.46 0 .83.37.83.83' }), jsx$1("circle", { cx: 47.86, cy: 44.95, r: 12.25, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M47.86 36.7v8.23l6.63.02' })] }))); };

var SvgTransferQuotaTableIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'none', d: 'M29.69 47.17H6.22c-.92 0-1.66-.74-1.66-1.66V10.54c0-.92.74-1.66 1.66-1.66h40.11c.92 0 1.66.74 1.66 1.66v17.83' }), jsx$1("path", { fill: 'none', d: 'M10.17 21.53h6.84v6.84h-6.84z' }), jsx$1("path", { fill: 'currentcolor', d: 'M22.85 21.53h6.84v6.84h-6.84z' }), jsx$1("path", { fill: 'none', d: 'M35.53 21.53h6.84v6.84h-6.84z' }), jsx$1("path", { fill: 'currentcolor', d: 'M10.17 33.17h6.84v6.84h-6.84z' }), jsx$1("path", { fill: 'none', d: 'M22.85 33.17h6.84v6.84h-6.84zm-3.79-27.8v7.02m14.42-7.02v7.02' }), jsx$1("circle", { cx: 47.99, cy: 47.17, r: 11.46, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M47.17 49.93h8.02m-3.3-3.3 3.3 3.3-3.29 3.3m-3.1-8.82h-8.02m3.3 3.3-3.3-3.3 3.3-3.3' })] }))); };

var SvgUsersetupIllustration = function (props) { return (jsxs("svg", __assign({ xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentcolor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 3, className: 'svg-illustration', viewBox: '0 0 64 64', width: '1em', height: '1em' }, props, { children: [jsx$1("path", { fill: 'currentcolor', d: 'M10.07 30.81s.05 1.28 0 1.6c.5.05 3.59-1.78 3.59-1.78s0 .77-.3 1.86c2.77-.29 4.73-2.45 5.9-4.19a6.93 6.93 0 0 0-5.68-2.97c-3.62 0-6.58 2.77-6.89 6.31 2.22.03 3.38-.83 3.38-.83' }), jsx$1("circle", { cx: 13.58, cy: 32.25, r: 6.93, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M10.43 38.42s-2 5.18-2 9.58m8.31-9.58s2 5.18 2 9.58m28.3-9.36s-2.03 5.27-2.03 9.74m8.45-9.74s2.03 5.27 2.03 9.74' }), jsx$1("path", { fill: 'currentcolor', fillRule: 'evenodd', d: 'M57.3 32.38c0 .16 0 .33-.03.48-5.77-.4-7.35-1.9-8.18-2.68-1.73 2.02-3.75 2.83-5.82 3.07-.04-.29-.06-.57-.06-.87 0-3.9 3.15-7.05 7.04-7.05s7.05 3.15 7.05 7.05m-11.59 5.38s-1.82 3.18-5.32 1.36c.27-4.41 3.03-5.04 3.03-5.04a7.04 7.04 0 0 0 2.29 3.68m9.09 0s1.82 3.18 5.32 1.36c-.27-4.41-3.03-5.04-3.03-5.04a7.04 7.04 0 0 1-2.29 3.68' }), jsx$1("circle", { cx: 50.25, cy: 32.37, r: 7.04, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M28.79 51.18s-2.04 5.27-2.04 9.74m8.46-9.74s2.04 5.27 2.04 9.74' }), jsx$1("path", { fill: 'currentcolor', fillRule: 'evenodd', d: 'M39.05 44.92c0 .16 0 .33-.03.48-5.77-.4-7.35-1.9-8.18-2.68-1.73 2.02-3.76 2.84-5.83 3.08a6 6 0 0 1-.06-.87c0-3.9 3.15-7.05 7.04-7.05s7.05 3.15 7.05 7.05z' }), jsx$1("path", { fill: 'currentcolor', fillRule: 'evenodd', d: 'M35.63 36.94c0 .61-.15 1.19-.43 1.7-.96-.5-2.05-.77-3.21-.77s-2.24.28-3.2.77c-.27-.51-.43-1.09-.43-1.7 0-2 1.63-3.63 3.63-3.63s3.63 1.63 3.63 3.63z' }), jsx$1("circle", { cx: 32, cy: 44.91, r: 7.05, fill: 'none' }), jsx$1("path", { fill: 'none', d: 'M41.72 23.65c-1.01 1.01-2.69 1.01-3.75 0l-6.57-6.57c-.97-.97-.97-2.6 0-3.57l.18-.18c.97-.97 2.6-.97 3.57 0l6.57 6.57c1.06 1.06 1.06 2.73 0 3.75M31.49 13.42l-3.75-3.75m.93-2.78-.66 1.98a1.7 1.7 0 0 1-1.1 1.1l-1.98.66-3.26-4.9c-.26-.44-.22-.97.13-1.32l.66-.66c.35-.35.93-.4 1.32-.13l4.9 3.26zm-.14 6.66-6.18 6.18c-.53.53-.79 1.19-.79 1.85s.22 1.32.71 1.81c1.01 1.1 2.82 1.01 3.88-.04l4.23-4.23m7.73-5.7c.57-.26 1.1-.62 1.54-1.06 1.37-1.37 1.81-3.31 1.41-5.03-.04-.26-.4-.35-.57-.13l-2.73 2.65-2.51-.62-.57-2.47 2.69-2.69c.18-.18.09-.49-.13-.57-1.76-.44-3.66.04-5.03 1.41s-1.85 3.35-1.37 5.12' }), jsx$1("circle", { cx: 24.56, cy: 21.31, r: 0.44, fill: 'none' })] }))); };

export { SvgAbsenceIllustration as AbsenceIllustration, SvgAccessIcon as AccessIcon, ActionBar, SvgAddIcon as AddIcon, AlertDialog$1 as AlertDialog, SvgAllMeetingsIcon as AllMeetingsIcon, SvgAnnouncementIllustration as AnnouncementIllustration, SvgArrowDownIcon as ArrowDownIcon, SvgArrowHeadDownIcon as ArrowHeadDownIcon, SvgArrowHeadLeftIcon as ArrowHeadLeftIcon, SvgArrowHeadRightIcon as ArrowHeadRightIcon, SvgArrowHeadUpIcon as ArrowHeadUpIcon, SvgArrowLeftIcon as ArrowLeftIcon, SvgArrowRightIcon as ArrowRightIcon, SvgArrowUpIcon as ArrowUpIcon, SvgAttendanceClassIllustration as AttendanceClassIllustration, Autocomplete$1 as Autocomplete, Badge$1 as Badge, BadgeCard$1 as BadgeCard, SvgBasedataIcon as BasedataIcon, SvgBirthdayIcon as BirthdayIcon, Breadcrumbs, SvgBulkEditIcon as BulkEditIcon, Burger$1 as Burger, Button$1 as Button, ButtonGroup$1 as ButtonGroup, SvgCalendarIcon as CalendarIcon, Card, SvgCertificatesIcon as CertificatesIcon, SvgChangeViewIcon as ChangeViewIcon, SvgCheckIcon as CheckIcon, Checkbox$1 as Checkbox, Chip$1 as Chip, SvgClassbookIcon as ClassbookIcon, SvgClassesIllustration as ClassesIllustration, SvgClockIcon as ClockIcon, SvgCloseIcon as CloseIcon, ColorPicker$1 as ColorPicker, ContextMenu, SvgControlIcon as ControlIcon, SvgCoursesIcon as CoursesIcon, SvgCurriculumIllustration as CurriculumIllustration, SvgDashboardIcon as DashboardIcon, DatePicker$1 as DatePicker, SvgDeleteIcon as DeleteIcon, SvgDependenciesIllustration as DependenciesIllustration, SvgDeputateIcon as DeputateIcon, SvgDivisionIllustration as DivisionIllustration, SvgDotsHorizontalIcon as DotsHorizontalIcon, SvgDotsVerticalIcon as DotsVerticalIcon, SvgDoubleExclamationmarkIcon as DoubleExclamationmarkIcon, SvgDoublearrowLeftIcon as DoublearrowLeftIcon, SvgDoublearrowRightIcon as DoublearrowRightIcon, Dropdown$1 as Dropdown, DropdownMenu, SvgDuplicateIcon as DuplicateIcon, SvgEditIcon as EditIcon, EmptyState$1 as EmptyState, SvgEpochenIcon as EpochenIcon, ErrorState, SvgEvaluateIllustration as EvaluateIllustration, SvgExclamationmarkIcon as ExclamationmarkIcon, SvgFachstundenIcon as FachstundenIcon, SvgFactorAlternateIllustration as FactorAlternateIllustration, SvgFactorIcon as FactorIcon, SvgFactorIllustration as FactorIllustration, SvgFileIcon as FileIcon, SvgFilterIcon as FilterIcon, SvgForumIcon as ForumIcon, SvgFullscreenActiveIcon as FullscreenActiveIcon, SvgFullscreenIcon as FullscreenIcon, SvgGenerateIcon as GenerateIcon, SvgGradeGroupsIcon as GradeGroupsIcon, SvgGradegroupsIllustration as GradegroupsIllustration, Grid, GridColumn, SvgGridIcon as GridIcon, GridRow, GroupPicker$1 as GroupPicker, SvgGroupsIcon as GroupsIcon, SvgHandIcon as HandIcon, Header, SvgHomeIcon as HomeIcon, SvgImportIcon as ImportIcon, SvgImportantIcon as ImportantIcon, SvgImproveIcon as ImproveIcon, SvgInfoIcon as InfoIcon, InplaceEdit$1 as InplaceEdit, Input$1 as Input, SvgInvisibleIcon as InvisibleIcon, LazyLoader$1 as LazyLoader, SvgLessonIcon as LessonIcon, SvgLessonIllustration as LessonIllustration, SvgLinkIcon as LinkIcon, SvgListIcon as ListIcon, LoadingBar, SvgLockIcon as LockIcon, SvgLogOutIcon as LogOutIcon, Logo$1 as Logo, SvgMailIcon as MailIcon, SvgMandatoryIcon as MandatoryIcon, SvgManualDeputateIllustration as ManualDeputateIllustration, SvgMaterialArchiveIcon as MaterialArchiveIcon, SvgMediathekIcon as MediathekIcon, SvgMeetingActiveIcon as MeetingActiveIcon, SvgMeetingDefaultIcon as MeetingDefaultIcon, SvgMenuIcon as MenuIcon, SvgMessagesActiveIcon as MessagesActiveIcon, SvgMessagesDefaultIcon as MessagesDefaultIcon, SvgMinusIcon as MinusIcon, SvgMobilePhoneIcon as MobilePhoneIcon, Modal$1 as Modal, ModalBottomButtons, SvgNextcloudIcon as NextcloudIcon, SvgNoDataIcon as NoDataIcon, SvgNoTasksIcon as NoTasksIcon, SvgNotificationActiveIcon as NotificationActiveIcon, SvgNotificationDefaultIcon as NotificationDefaultIcon, SvgOptimizingIcon as OptimizingIcon, SvgOverviewIllustration as OverviewIllustration, Pagination, SvgPdfIcon as PdfIcon, SvgPinIcon as PinIcon, SvgPlanIllustration as PlanIllustration, SvgPrintIcon as PrintIcon, SvgProfileIcon as ProfileIcon, SvgQuestionmarkCircleIcon as QuestionmarkCircleIcon, SvgQuestionmarkIcon as QuestionmarkIcon, RadioGroup$1 as RadioGroup, SvgRoomIcon as RoomIcon, SvgRoomIllustration as RoomIllustration, SvgScenarioIcon as ScenarioIcon, SvgScenarioIllustration as ScenarioIllustration, SvgSchoolEventIllustration as SchoolEventIllustration, SvgSchoolYearIllustration as SchoolYearIllustration, SvgSearchIcon as SearchIcon, SearchInput$1 as SearchInput, Select$1 as Select, SvgSendIcon as SendIcon, SvgSettingsIcon as SettingsIcon, SvgSetupIllustration as SetupIllustration, SvgShareAlternateIcon as ShareAlternateIcon, SvgShareIcon as ShareIcon, Sidebar$1 as Sidebar, Skeleton$1 as Skeleton, SliderModal, SvgSpinnerIcon as SpinnerIcon, SvgSubjectIllustration as SubjectIllustration, SvgSubstitutionIllustration as SubstitutionIllustration, SvgSubstitutionsIcon as SubstitutionsIcon, Switch$1 as Switch, SvgSwitchIcon as SwitchIcon, Tab$1 as Tab, Table, Tag$1 as Tag, SvgTeacherIllustration as TeacherIllustration, SvgTemplatesIcon as TemplatesIcon, SvgTestIcon as TestIcon, TextArea$1 as TextArea, SvgThreemaIcon as ThreemaIcon, SvgTimegridIllustration as TimegridIllustration, SvgTimetableIcon as TimetableIcon, TimetableView$1 as TimetableView, Toast, Tooltip$1 as Tooltip, SvgTransferQuotaTableIllustration as TransferQuotaTableIllustration, SvgUndoIcon as UndoIcon, SvgUnlockIcon as UnlockIcon, SvgUnpinIcon as UnpinIcon, SvgUpdateIcon as UpdateIcon, SvgUploadIcon as UploadIcon, SvgUsersetupIllustration as UsersetupIllustration, ValueField$1 as ValueField, SvgVisibleIcon as VisibleIcon, showToast, useDefaultColumnVisibility, useDefaultSelecting, useDefaultSorting };
//# sourceMappingURL=index.es.js.map
